import { useState, useEffect, useRef, useCallback, useMemo, memo, lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
// chat-md-safety: react-markdown + remark-gfm now live inside the
// shared MarkdownMessage component. App.jsx no longer needs them
// directly.
import WhatsNewPanel from "./WhatsNewPanel";
import { hasUnread as patchNotesHasUnread, classifySeenState as patchNotesClassify, markSeen as patchNotesMarkSeen } from "./patchNotes";
import ThinkingIndicator from "./ThinkingIndicator";
import ToolActivityStrip from "./ToolActivityStrip";
import { CopyMorphButton } from "./CopyMorphButton";
import GlobalTooltip from "./GlobalTooltip";
import CodeBlockView from "./CodeBlockView.jsx";
import WorkflowEditor from "./WorkflowEditor";
import { useStickyBottom } from "./useStickyBottom";
import { planKbUploadBatch } from "./kb_upload_batch.js";
// perf/code-split: HermesPanel pulls in pdfkit/docx/pptxgenjs (via
// hermes_artifact path) + react-markdown's full machinery for the
// transcript renderer + the analytics + KB sub-views + ModelPicker's
// /api/models fetch wiring. Chat users who never click into Hermes
// paid for all of it at first paint. Lazy-load so it splits into its
// own chunk that only ships when section === "hermes".
const HermesPanel = lazy(() => import("./HermesPanel.jsx"));
import HermesResidentTray from "./HermesResidentTray.jsx";
// CoWork: task-first agentic workspace on top of the Hermes engine.
const CoWorkWorkspace = lazy(() => import("./CoWorkWorkspace.jsx"));
// fix/pwa-error-boundary: top-level + per-route error boundary and
// a global banner for stale-chunk recovery after a deploy.
import { ErrorBoundary } from "./ErrorBoundary.jsx";
import ChunkLoadBanner from "./ChunkLoadBanner.jsx";
// Slice H34a: Projects (collaboration workspaces). Lazy-loaded so the
// chunk only ships when section === "projects".
const ProjectsPanel = lazy(() => import("./ProjectsPanel.jsx"));
// Platform guide: lazy-loaded so the full documentation index and
// walkthrough content are only fetched when users open More > Guide.
const PlatformGuide = lazy(() => import("./PlatformGuide.jsx"));
// Slice H34b: Accept-invite landing page. Imported eagerly because it
// can mount BEFORE the user is authed -- it has to render on first
// paint when ?accept_invite=<token> is in the URL.
import AcceptInvitePage from "./AcceptInvitePage.jsx";
// Slice "chat-md-safety": shared safe markdown renderer with URL-scheme
// filter (blocks javascript:/data:/vbscript:/file: in <a> href and <img>
// src). Hermes has shipped through this since H8; chat now matches.
import MarkdownMessage from "./MarkdownMessage.jsx";

const THEMES = {
  // `hoverBg` is distinct from `inputBg` on purpose: inputBg in light mode is
  // near-white to match the card aesthetic, but that made hover feedback
  // invisible (hover = "no change on white background"). hoverBg is a slight
  // darken of the page color so hover is readable without looking boxy.
  light: { bg:"#f8fafc",surface:"#ffffff",surface2:"#f1f5f9",surface3:"#e2e8f0",border:"rgba(91,130,180,0.15)",border2:"rgba(91,130,180,0.25)",border3:"rgba(91,130,180,0.4)",accent:"#3b82c4",accent2:"#5ba3c9",accentDim:"rgba(59,130,196,0.08)",text:"#0f172a",text2:"#475569",text3:"#94a3b8",danger:"#dc2626",success:"#16a34a",grad2:"linear-gradient(135deg,#3b82c4,#5ba3c9)",navBg:"rgba(255,255,255,0.95)",topbarBg:"rgba(255,255,255,0.9)",inputBg:"rgba(255,255,255,0.9)",hoverBg:"rgba(15,23,42,0.06)",cardBg:"rgba(255,255,255,0.85)",modalBg:"#ffffff",toastBg:"rgba(15,23,42,0.92)",codeBlockBg:"#f1f5f9",msgHoverBg:"rgba(59,130,196,0.05)" },
  dark: { bg:"#0c0f1a",surface:"#111827",surface2:"#1a2236",surface3:"#1f2d45",border:"rgba(99,130,185,0.13)",border2:"rgba(99,130,185,0.22)",border3:"rgba(99,130,185,0.38)",accent:"#5ba3c9",accent2:"#7ec8e3",accentDim:"rgba(91,163,201,0.10)",text:"#eef2f8",text2:"#8ea6c4",text3:"#4a6280",danger:"#f87171",success:"#34d399",grad2:"linear-gradient(135deg,#5ba3c9,#7ec8e3)",navBg:"rgba(10,14,24,0.9)",topbarBg:"rgba(8,12,24,0.8)",inputBg:"rgba(255,255,255,0.05)",hoverBg:"rgba(255,255,255,0.07)",cardBg:"rgba(255,255,255,0.03)",modalBg:"#141f32",toastBg:"rgba(17,24,39,0.95)",codeBlockBg:"#0d1117",msgHoverBg:"rgba(255,255,255,0.03)" },
};
let S = THEMES.light;
let BL = null;   // active brandLogo URL/data-URL (set from App state)
let BN = "ChatFortAI"; // brandName (set from App state)
let BC = null;   // brand accent hex (set from App state)
let BF = "'Outfit','Segoe UI',system-ui,sans-serif"; // global brand font stack
let setPreviewCb = null; // set from App so MsgContent can open the preview panel
let SERVER_MODE = false; // true when served from the Node proxy
let AUTH_TOKEN  = null;  // JWT when in server mode

const BRAND_FONTS = [
  { key: "outfit", label: "Outfit", stack: "'Outfit','Segoe UI',system-ui,sans-serif" },
  { key: "inter", label: "Inter", stack: "'Inter','Segoe UI',system-ui,sans-serif" },
  { key: "roboto", label: "Roboto", stack: "'Roboto','Segoe UI',system-ui,sans-serif" },
  { key: "open-sans", label: "Open Sans", stack: "'Open Sans','Segoe UI',system-ui,sans-serif" },
  { key: "lato", label: "Lato", stack: "'Lato','Segoe UI',system-ui,sans-serif" },
  { key: "montserrat", label: "Montserrat", stack: "'Montserrat','Segoe UI',system-ui,sans-serif" },
  { key: "poppins", label: "Poppins", stack: "'Poppins','Segoe UI',system-ui,sans-serif" },
  { key: "source-sans-3", label: "Source Sans 3", stack: "'Source Sans 3','Segoe UI',system-ui,sans-serif" },
  { key: "nunito-sans", label: "Nunito Sans", stack: "'Nunito Sans','Segoe UI',system-ui,sans-serif" },
  { key: "merriweather-sans", label: "Merriweather Sans", stack: "'Merriweather Sans','Segoe UI',system-ui,sans-serif" },
];
const BRAND_FONT_KEYS = new Set(BRAND_FONTS.map(f => f.key));
const BRAND_FONT_IMPORT = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Lato:wght@400;700;900&family=Merriweather+Sans:wght@400;600;700;800&family=Montserrat:wght@400;500;600;700;800&family=Nunito+Sans:wght@400;600;700;800&family=Open+Sans:wght@400;600;700;800&family=Outfit:wght@400;500;600;700;800&family=Poppins:wght@400;500;600;700;800&family=Roboto:wght@400;500;700;900&family=Source+Sans+3:wght@400;500;600;700;800&display=swap";
const PREFERRED_TTS_VOICE = "marin";
// Play a short sample of a TTS voice for the Settings voice picker.
async function cfPreviewVoice(voice, showToast) {
  try {
    const tok = (() => { try { return localStorage.getItem("cf_auth_token") || ""; } catch { return ""; } })();
    const r = await fetch("/api/tts", { method: "POST", headers: { Authorization: "Bearer " + tok, "Content-Type": "application/json" }, body: JSON.stringify({ text: "Hi, this is how I sound on ChatFort AI.", voice }) });
    if (!r.ok) { showToast && showToast("Voice preview unavailable right now"); return; }
    if (r.headers.get("X-ChatFort-TTS-Provider") === "piper") { showToast && showToast("This server uses one built-in voice \u2014 distinct voices need OpenAI TTS (operator sets OPENAI_API_KEY)."); }
    const url = URL.createObjectURL(await r.blob());
    const a = new Audio(url); a.onended = () => { try { URL.revokeObjectURL(url); } catch {} }; a.play().catch(() => {});
  } catch { showToast && showToast("Voice preview failed"); }
}
const GOOGLE_DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.file";
const GOOGLE_GSI_SRC = "https://accounts.google.com/gsi/client";
const GOOGLE_API_SRC = "https://apis.google.com/js/api.js";
function brandFontOption(key) {
  return BRAND_FONTS.find(f => f.key === key) || BRAND_FONTS[0];
}

const EMPTY_TOOL_AUTH = {
  type: "none",
  headerName: "X-API-Key",
  apiKey: "",
  bearerToken: "",
  username: "",
  password: "",
  tokenUrl: "",
  clientId: "",
  clientSecret: "",
  scope: "",
};
function normalizeToolAuth(auth) {
  return { ...EMPTY_TOOL_AUTH, ...(auth && typeof auth === "object" ? auth : {}) };
}
function maskSecret(v) {
  if (!v) return "";
  const s = String(v);
  if (s.length <= 6) return "••••";
  return s.slice(0, 2) + "••••" + s.slice(-4);
}
function buildToolAuthRuntime(authInput) {
  const auth = normalizeToolAuth(authInput);
  const headers = {};
  if (auth.type === "api_key" && auth.apiKey) headers[auth.headerName || "X-API-Key"] = auth.apiKey;
  if (auth.type === "bearer" && auth.bearerToken) headers.Authorization = "Bearer " + auth.bearerToken;
  if (auth.type === "basic" && (auth.username || auth.password)) {
    headers.Authorization = "Basic " + btoa(`${auth.username || ""}:${auth.password || ""}`);
  }
  const secrets = {
    apiKey: auth.apiKey || "",
    bearerToken: auth.bearerToken || "",
    username: auth.username || "",
    password: auth.password || "",
    clientId: auth.clientId || "",
    clientSecret: auth.clientSecret || "",
  };
  return {
    type: auth.type || "none",
    headers,
    headerName: auth.headerName || "X-API-Key",
    tokenUrl: auth.tokenUrl || "",
    scope: auth.scope || "",
    hasSecret: !!(auth.apiKey || auth.bearerToken || auth.password || auth.clientSecret),
    secrets,
    async getAccessToken(extra = {}) {
      if (auth.type !== "client_credentials") throw new Error("Tool auth is not configured for client credentials");
      if (!auth.tokenUrl || !auth.clientId || !auth.clientSecret) throw new Error("Client credentials require token URL, client ID, and client secret");
      const body = new URLSearchParams();
      body.set("grant_type", "client_credentials");
      body.set("client_id", auth.clientId);
      body.set("client_secret", auth.clientSecret);
      if (auth.scope) body.set("scope", auth.scope);
      for (const [k, v] of Object.entries(extra || {})) if (v != null) body.set(k, String(v));
      const r = await fetch(auth.tokenUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error_description || d.error || `Token request failed: HTTP ${r.status}`);
      return d.access_token;
    },
  };
}

// hex color helpers
function hexToRgb(hex){const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);return{r,g,b};}
function lightenHex(hex,amt){const{r,g,b}=hexToRgb(hex);return '#'+[r,g,b].map(v=>Math.min(255,v+amt).toString(16).padStart(2,'0')).join('');}
function hexAlpha(hex,a){const{r,g,b}=hexToRgb(hex);return `rgba(${r},${g},${b},${a})`;}
function applyBrandColor(base,hex){return{...base,accent:hex,accent2:lightenHex(hex,28),accentDim:hexAlpha(hex,0.10),border3:hexAlpha(hex,0.4),grad2:`linear-gradient(135deg,${hex},${lightenHex(hex,28)})`};}

function CFLogo({ size=24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 200 218" fill={S.accent} xmlns="http://www.w3.org/2000/svg">
      {/* Castle battlements — 3 merlons */}
      <rect x="44" y="6" width="24" height="30" rx="5"/>
      <rect x="88" y="0" width="24" height="36" rx="5"/>
      <rect x="132" y="6" width="24" height="30" rx="5"/>

      {/* Ring: outer circle minus inner circle (evenodd = annulus) */}
      <path fillRule="evenodd" d="
        M100,28 A80,80 0 0,1 180,108 A80,80 0 0,1 100,188 A80,80 0 0,1 20,108 A80,80 0 0,1 100,28Z
        M100,48 A60,60 0 0,0 160,108 A60,60 0 0,0 100,168 A60,60 0 0,0 40,108 A60,60 0 0,0 100,48Z
      "/>

      {/* Chat bubble tail — bottom-left */}
      <path d="M36,156 L7,214 L66,183 Z"/>

      {/* C letter — open arc, opening on right
          Outer arc: (110,82)→(110,134) large CCW (sweep=0) r=36 = traces left side of C
          Inner arc: (101,124)→(101,92) large CW  (sweep=1) r=22 = traces back right
      */}
      <path d="M110,82 A36,36 0 1,0 110,134 L101,124 A22,22 0 1,1 101,92 Z"/>

      {/* F letter — vertical stem + top bar + middle bar */}
      <rect x="110" y="72" width="13" height="72" rx="2"/>
      <rect x="110" y="72" width="44" height="13" rx="5"/>
      <rect x="110" y="105" width="34" height="12" rx="4"/>
    </svg>
  );
}

function AppLogo({ size=24 }) {
  if (BL) return <img src={BL} style={{width:size,height:size,objectFit:"contain",borderRadius:4,display:"block"}} alt="Logo"/>;
  return <CFLogo size={size}/>;
}

function brandLogoObjectUrl(logo) {
  if (!logo) return null;
  if (!logo.startsWith("data:")) return logo; // already a URL
  try {
    const [meta, b64] = logo.split(",");
    const mime = (meta.match(/data:([^;]+)/) || [,"image/png"])[1];
    const bin = atob(b64);
    const buf = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
    return URL.createObjectURL(new Blob([buf], { type: mime }));
  } catch { return logo; }
}

// Per-user avatar — uploaded picture if set, else colored initials circle.
// Used everywhere a user's identity is shown next to their name. `square`
// switches from the chat-bubble rounded square (radius 9) to a perfect
// circle (radius 50%) for places that previously used "borderRadius: 50%".
function UserAvatar({ user, size=30, square=false, fontSize }) {
  const radius = square ? 9 : "50%";
  const fs = fontSize || Math.max(8, Math.round(size * 0.36));
  if (user?.avatar) {
    return <img src={user.avatar} alt={user.name || ""} style={{ width: size, height: size, borderRadius: radius, objectFit: "cover", display: "block" }}/>;
  }
  const bg = user?.color || S.accent;
  const initials = user?.initials || (user?.name ? user.name.split(/\s+/).map(w=>w[0]).join("").slice(0,2).toUpperCase() : "U");
  return (
    <div style={{ width: size, height: size, borderRadius: radius, background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: fs, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
      {initials}
    </div>
  );
}

const gid = () => Date.now().toString(36) + Math.random().toString(36).slice(2,5);
const nowt = () => new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"});
function ld(k,d){try{const v=localStorage.getItem(k);return v?JSON.parse(v):d;}catch{return d;}}
function sv(k,v){try{localStorage.setItem(k,JSON.stringify(v));}catch{}}
// Client error telemetry -- best-effort capture of uncaught errors +
// unhandled promise rejections, posted to /api/telemetry/error so ops
// can alert on them. Throttled (20/min) + deduped (30s per message);
// passive (never preventDefault) and wrapped in try/catch so it can
// never itself break the app.
(function installErrorTelemetry(){
  try {
    if (typeof window === "undefined" || window.__cfErrTelemetry) return;
    window.__cfErrTelemetry = true;
    const seen = new Map();
    let sent = 0, windowStart = Date.now();
    function report(rec){
      try {
        const now = Date.now();
        if (now - windowStart > 60000) { windowStart = now; sent = 0; }
        if (sent >= 20) return;
        const key = (rec.message||"") + "|" + (rec.source||"");
        if (seen.get(key) && now - seen.get(key) < 30000) return;
        seen.set(key, now); sent++;
        const body = JSON.stringify(Object.assign({}, rec, { url: location.href }));
        if (navigator.sendBeacon) { navigator.sendBeacon("/api/telemetry/error", new Blob([body], { type: "application/json" })); }
        else { fetch("/api/telemetry/error", { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(()=>{}); }
      } catch(_) {}
    }
    window.addEventListener("error", function(e){
      if (!e || (!e.message && !e.error)) return; // ignore resource-load (img/script 404) noise
      report({ kind: "error", message: e.message || String(e.error || "error"), stack: e.error && e.error.stack, source: e.filename, line: e.lineno, col: e.colno });
    });
    window.addEventListener("unhandledrejection", function(e){
      const r = e && e.reason;
      report({ kind: "unhandledrejection", message: (r && r.message) || String(r || "rejection"), stack: r && r.stack });
    });
  } catch(_) {}
})();

// `tier` orders the cross-provider model list: frontier (flagship/best) →
// balanced (solid general-purpose) → fast (cheapest/quickest). Models are
// also ordered within each provider best → fastest so callers that don't
// sort (agent editor, compare picker) still see the best options first.
const PROVIDERS = {
  anthropic:{name:"Anthropic",icon:"🟣",color:"#cc785c",builtin:true,models:[
    {id:"auto",name:"Auto",tier:"fast",desc:"Picks the right model per message",bestFor:"Let ChatFort route each message: quick questions go to a fast model, complex work to a frontier model. The most cost-effective default."},
    {id:"claude-sonnet-4-6",name:"claude-sonnet-4.6",tier:"frontier",desc:"Newest · best for most tasks",bestFor:"Writing, analysis, coding, Q&A — the best all-rounder for everyday use. Auto-routed through OpenRouter."},
    {id:"claude-opus-4-5",name:"claude-opus-4.5",tier:"frontier",desc:"Best for hard problems",bestFor:"Complex reasoning, lengthy research, multi-step legal or financial analysis"},
    {id:"claude-sonnet-4-20250514",name:"claude-sonnet-4",tier:"frontier",desc:"Best for most tasks",bestFor:"Writing, analysis, coding, Q&A — solid everyday all-rounder"},
    {id:"claude-haiku-4-5-20251001",name:"claude-haiku-4-5",tier:"fast",desc:"Best for fast replies",bestFor:"Quick summaries, simple questions, high-volume tasks where speed matters"}
  ]},
  openai:{name:"OpenAI",icon:"⬛",color:"#10a37f",models:[
    {id:"gpt-4o",name:"GPT-4o",tier:"frontier",desc:"Best for vision & structure",bestFor:"Images, charts, structured JSON output, and general-purpose tasks"},
    {id:"gpt-4o-mini",name:"GPT-4o mini",tier:"fast",desc:"Best for fast & cheap",bestFor:"High-volume, cost-sensitive tasks — fast responses with solid quality"}
  ],ep:"https://api.openai.com/v1/chat/completions"},
  openrouter:{name:"OpenRouter",icon:"🔀",color:"#7c3aed",models:[
    {id:"anthropic/claude-sonnet-4-5",name:"claude-sonnet-4",tier:"frontier",desc:"Best for most tasks",bestFor:"Writing, analysis, coding, Q&A — the best all-rounder for everyday use"},
    {id:"openai/gpt-4o",name:"GPT-4o",tier:"frontier",desc:"Best for vision & structure",bestFor:"Images, charts, structured JSON output, and general-purpose tasks"},
    {id:"deepseek/deepseek-r1",name:"DeepSeek R1",tier:"frontier",desc:"Best for deep reasoning",bestFor:"Math, logic, science, step-by-step problem solving"},
    {id:"nousresearch/hermes-3-llama-3.1-405b",name:"Hermes 3 405B",tier:"frontier",desc:"Best for max reasoning",bestFor:"Complex reasoning tasks requiring the largest open-source model"},
    {id:"mistralai/mistral-large",name:"Mistral Large",tier:"balanced",desc:"Best for EU compliance",bestFor:"European data-residency needs, multilingual tasks, code generation"},
    {id:"meta-llama/llama-3.3-70b-instruct",name:"Llama 3.3 70B",tier:"balanced",desc:"Best for open-source",bestFor:"Open-source tasks, privacy-conscious use, general chat"},
    {id:"x-ai/grok-2",name:"Grok 2",tier:"balanced",desc:"Best for real-time news",bestFor:"Current events, social media context, news-heavy queries"},
    {id:"nousresearch/hermes-3-llama-3.1-70b",name:"Hermes 3 70B",tier:"balanced",desc:"Best for tool use",bestFor:"Function calling, structured outputs, agentic workflows"},
    {id:"qwen/qwen-2.5-72b-instruct",name:"Qwen 2.5 72B",tier:"balanced",desc:"Best for multilingual",bestFor:"Chinese, Japanese, Korean and other multilingual tasks"},
    {id:"google/gemini-2.0-flash-001",name:"Gemini 2.0 Flash",tier:"fast",desc:"Best for speed & long docs",bestFor:"Very long documents, fast responses, Google ecosystem tasks"}
  ],ep:"https://openrouter.ai/api/v1/chat/completions",openrouter:true},
  hermes:{name:"Nous Hermes",icon:"🧠",color:"#f59e0b",models:[
    {id:"hermes-3-llama-3.1-405b",name:"Hermes 3 405B",tier:"frontier",desc:"Best for max reasoning",bestFor:"Complex reasoning tasks requiring the largest open-source model"},
    {id:"hermes-3-llama-3.1-70b",name:"Hermes 3 70B",tier:"balanced",desc:"Best for tool use",bestFor:"Function calling, structured outputs, agentic workflows"},
    {id:"hermes-2-pro-llama-3-8b",name:"Hermes 2 Pro 8B",tier:"fast",desc:"Best for speed",bestFor:"Fast, lightweight tasks where a small model is enough"}
  ],ep:"https://api.nous.systems/v1/chat/completions",hermes:true},
  groq:{name:"Groq",icon:"⚡",color:"#f55036",models:[
    {id:"llama-3.3-70b-versatile",name:"Llama 3.3 70B",tier:"fast",desc:"Best for ultra-fast",bestFor:"Extremely fast open-source inference — ideal for real-time applications"}
  ],ep:"https://api.groq.com/openai/v1/chat/completions"},
  mistral:{name:"Mistral",icon:"🌊",color:"#ff7000",models:[{id:"mistral-large-latest",name:"Mistral Large",tier:"balanced",desc:"Top model"}],ep:"https://api.mistral.ai/v1/chat/completions"},
  google:{name:"Google Gemini",icon:"🔷",color:"#4285f4",byokCard:true,models:[
    {id:"gemini-2.5-pro",name:"Gemini 2.5 Pro",tier:"frontier",desc:"Best Gemini reasoning"},
    {id:"gemini-2.5-flash",name:"Gemini 2.5 Flash",tier:"fast",desc:"Fast + cheap"},
    {id:"gemini-2.0-flash",name:"Gemini 2.0 Flash",tier:"fast",desc:"Low-latency"},
    {id:"gemini-1.5-pro",name:"Gemini 1.5 Pro",tier:"balanced",desc:"Long context"}
  ]},
  azure:{name:"Azure AI Foundry",icon:"☁️",color:"#0078d4",byokCard:true,models:[
    {id:"azure-deployment",name:"Azure deployment",tier:"frontier",desc:"Routes to your configured Azure deployment"}
  ]},
  bedrock:{name:"AWS Bedrock",icon:"🟧",color:"#ff9900",byokCard:true,models:[
    {id:"anthropic.claude-3-5-sonnet-20241022-v2:0",name:"Claude 3.5 Sonnet (Bedrock)",tier:"frontier",desc:"Anthropic on Bedrock"},
    {id:"anthropic.claude-3-5-haiku-20241022-v1:0",name:"Claude 3.5 Haiku (Bedrock)",tier:"fast",desc:"Fast Anthropic on Bedrock"},
    {id:"meta.llama3-1-70b-instruct-v1:0",name:"Llama 3.1 70B (Bedrock)",tier:"balanced",desc:"Meta on Bedrock"}
  ]},
};
const TIER_RANK = { frontier: 0, balanced: 1, fast: 2 };
// OpenRouter is the delivery pipe, not a model family. For customer-facing
// views we re-bucket each OR model into the real owner of the model by
// parsing the ID prefix — "anthropic/claude-sonnet-4.6" → Anthropic, not
// "OpenRouter". Superadmins still see the literal OpenRouter group because
// that's where the routing-config lives.
const OR_REAL_PROVIDER = {
  "anthropic":   { key: "anthropic",  label: "Anthropic",    color: "#cc785c" },
  "openai":      { key: "openai",     label: "OpenAI",       color: "#10a37f" },
  "google":      { key: "google",     label: "Google",       color: "#4285F4" },
  "meta-llama":  { key: "meta",       label: "Meta",         color: "#0866FF" },
  "deepseek":    { key: "deepseek",   label: "DeepSeek",     color: "#4D6BFE" },
  "mistralai":   { key: "mistral",    label: "Mistral",      color: "#ff7000" },
  "x-ai":        { key: "xai",        label: "xAI",          color: "#1d9bf0" },
  "nousresearch":{ key: "hermes",     label: "Nous Hermes",  color: "#f59e0b" },
  "qwen":        { key: "qwen",       label: "Qwen",         color: "#FF6B35" },
  // Round 2: less-common OR upstreams. Brand colors are best-guess
  // from public material; slugs verified against cdn.simpleicons.org
  // (most have no SI entry yet -- those fall back to a colored dot
  // in the brand color rather than OpenRouter purple).
  "perplexity":  { key: "perplexity", label: "Perplexity",   color: "#20B8CD" },
  "cohere":      { key: "cohere",     label: "Cohere",       color: "#39594D" },
  "moonshotai":  { key: "moonshotai", label: "MoonshotAI",   color: "#FFA500" },
  "stepfun":     { key: "stepfun",    label: "StepFun",      color: "#1A75FF" },
  "writer":      { key: "writer",     label: "Writer",       color: "#5436D9" },
  "z-ai":        { key: "zai",        label: "Z.ai",         color: "#10B981" },
  "01-ai":       { key: "zeroone",    label: "01.AI",        color: "#FF6B35" },
  "thudm":       { key: "thudm",      label: "THUDM (GLM)",  color: "#E11D48" },
  "inflection":  { key: "inflection", label: "Inflection",   color: "#9333EA" },
  "ai21":        { key: "ai21",       label: "AI21",         color: "#5B21B6" },
  "nvidia":      { key: "nvidia",     label: "NVIDIA",       color: "#76B900" },
  "microsoft":   { key: "microsoft",  label: "Microsoft",    color: "#00A4EF" },
  "databricks":  { key: "databricks", label: "Databricks",   color: "#FF3621" },
  "sambanova":   { key: "sambanova",  label: "SambaNova",    color: "#FF6B35" },
  "sao10k":      { key: "sao10k",     label: "Sao10k",       color: "#94A3B8" },
  "amazon":      { key: "amazon",     label: "Amazon",       color: "#FF9900" },
  // Round 3 -- the unmapped purple-bullet rows on Model Visibility.
  "xiaomi":      { key: "xiaomi",     label: "Xiaomi",       color: "#FF6700" },
  "minimax":     { key: "minimax",    label: "MiniMax",      color: "#0095E8" },
  "liquid":      { key: "liquid",     label: "LiquidAI",     color: "#1CB1BD" },
  "essentialai": { key: "essentialai",label: "EssentialAI",  color: "#6366F1" },
  "deepcogito":  { key: "deepcogito", label: "Deep Cogito",  color: "#DC2626" },
  "arcee-ai":    { key: "arcee",      label: "Arcee AI",     color: "#F97316" },
  "gryphe":      { key: "gryphe",     label: "Gryphe",       color: "#7C3AED" },
};

// Real-brand simpleicons.org slugs for each provider key. Null
// entries gracefully fall back to the emoji + colored dot.
// Slugs verified against https://simpleicons.org (Nov 2025).
const PROVIDER_SLUGS = {
  anthropic:  "anthropic",
  openai:     "openai",
  openrouter: null,           // no canonical SI slug
  hermes:     null,
  groq:       "groq",
  mistral:    "mistralai",
  google:     "googlegemini", // Gemini badge for the Google bucket
  meta:       "meta",
  deepseek:   "deepseek",
  xai:        "x",            // xAI ships under the X brand
  qwen:       "alibabacloud",
  // Round 2 -- SI verified Nov 2025. Null entries fall back to a
  // colored bullet in the brand color (set on OR_REAL_PROVIDER above).
  perplexity: "perplexity",
  cohere:     null,
  moonshotai: "moonshotai",
  stepfun:    null,
  writer:     null,
  zai:        null,
  zeroone:    null,            // 01.AI
  thudm:      null,
  inflection: null,
  ai21:       null,
  nvidia:     "nvidia",
  microsoft:  null,            // SI has no plain "microsoft" mark
  databricks: "databricks",
  sambanova:  null,
  sao10k:     null,
  amazon:     "amazon",        // SI carries the Amazon smile/wordmark
  // Round 3
  xiaomi:     "xiaomi",        // SI verified (xiaomi slug)
  minimax:    null,
  liquid:     null,
  essentialai:null,
  deepcogito: null,
  arcee:      null,
  gryphe:     null,
};
const OPENROUTER_FALLBACK_MODEL_MAP = {
  "claude-sonnet-4-6": "anthropic/claude-sonnet-4.6",
  "claude-sonnet-4-20250514": "anthropic/claude-sonnet-4-5",
  "claude-haiku-4-5-20251001": "anthropic/claude-haiku-4-5",
  "claude-opus-4-5": "anthropic/claude-opus-4.5",
};
const DEFAULT_CHAT_MAX_TOKENS = 4096;
const CODE_GENERATION_MIN_TOKENS = 8192;

function looksLikeCodeGenerationPrompt(text) {
  const t = String(text || "").toLowerCase();
  return /\b(code|script|html|css|javascript|typescript|python|react|component|app|game|single file|complete file|full file|implementation)\b/.test(t)
    && /\b(make|build|create|write|code|generate|implement|output|put|complete)\b/.test(t);
}

function resolveChatMaxTokens(configured, promptText) {
  const base = Math.max(256, Number(configured) || DEFAULT_CHAT_MAX_TOKENS);
  if (looksLikeCodeGenerationPrompt(promptText) && base < CODE_GENERATION_MIN_TOKENS) {
    return CODE_GENERATION_MIN_TOKENS;
  }
  return base;
}

// Resolve the brand the user actually thinks of for a (provider,
// modelId) pair. For OpenRouter rows the brand is the upstream
// owner, not OR itself -- e.g. "anthropic/claude-sonnet-4-5" is
// the Anthropic brand even though it routes via OR.
function realProviderKey(provider, modelId) {
  if (provider !== "openrouter") return provider || "anthropic";
  const prefix = String(modelId || "").split("/")[0];
  return (OR_REAL_PROVIDER[prefix] && OR_REAL_PROVIDER[prefix].key) || "openrouter";
}

// Inline brand-logo SVGs for providers simpleicons.org has removed
// OR for upstreams it never carried. Two value shapes:
//   * string -- raw <path d="..."/> path data (OpenAI, Groq).
//   * { letter } -- monogram: colored rounded square + white letter.
// ProviderLogo at render dispatches on the type.
const INLINE_PROVIDER_SVGS = {
  // Letter monograms for upstreams without a Simple Icons brand
  // entry. Each renders as a brand-colored rounded square with a
  // white letter centered -- enough to scan the catalog at a glance
  // without leaning on cdn.simpleicons.org for every vendor.
  cohere:     { letter: "C" },
  stepfun:    { letter: "S" },
  writer:     { letter: "W" },
  zai:        { letter: "Z" },
  zeroone:    { letter: "01" },
  thudm:      { letter: "T" },
  inflection: { letter: "I" },
  ai21:       { letter: "A" },
  microsoft:  { letter: "M" },
  sambanova:  { letter: "S" },
  sao10k:     { letter: "S" },
  hermes:     { letter: "H" },
  openrouter: { letter: "OR" },
  // Round 3 -- letter monograms for the new OR upstreams that
  // Simple Icons doesn't yet carry.
  minimax:    { letter: "MM" },
  liquid:     { letter: "L"  },
  essentialai:{ letter: "E"  },
  deepcogito: { letter: "C"  },
  arcee:      { letter: "A"  },
  gryphe:     { letter: "G"  },
  // OpenAI lotus -- the official 6-petal mark.
  openai: "M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z",
  // Groq -- "Q with line through it" mark approximated as a circle
  // + slash. Simplified so it reads as a brand at 14-16px.
  groq: "M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 2c4.418 0 8 3.582 8 8 0 1.85-.629 3.553-1.683 4.91l-2.829-2.83A4 4 0 1 0 12 16h4v3h-4c-3.866 0-7-3.134-7-7s3.134-7 7-7zm0 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6z",
};

// Tiny logo component. Tries (in order): inline SVG -> simpleicons
// CDN -> colored-dot fallback. Used in the topbar model pill +
// model-dropdown row + group header.
function ProviderLogo({ providerKey, color, size = 14 }) {
  const inline = INLINE_PROVIDER_SVGS[providerKey];
  const slug = PROVIDER_SLUGS[providerKey];
  const [failed, setFailed] = useState(false);
  if (inline) {
    // Monogram variant -- colored rounded square + white letter.
    if (typeof inline === "object" && inline.letter) {
      const letter = String(inline.letter);
      const fontSize = letter.length > 1 ? 10 : 14;
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" style={{ flexShrink: 0, display: "inline-block" }} aria-hidden="true">
          <rect x="0" y="0" width="24" height="24" rx="5" fill={color || "currentColor"} />
          <text x="12" y="17" textAnchor="middle" fontSize={fontSize} fontWeight="800" fill="#fff" fontFamily="var(--cf-brand-font, system-ui, -apple-system, 'Segoe UI', sans-serif)">{letter}</text>
        </svg>
      );
    }
    // Path-string variant (OpenAI lotus, Groq).
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill={color || "currentColor"} style={{ flexShrink: 0, display: "inline-block" }} aria-hidden="true">
        <path d={inline} />
      </svg>
    );
  }
  if (!slug || failed) {
    return <span style={{ width: size, height: size, borderRadius: "50%", background: color, display: "inline-block", flexShrink: 0 }} />;
  }
  const hex = (color || "#888").replace("#", "");
  return (
    <img src={`https://cdn.simpleicons.org/${slug}/${hex}`}
         alt=""
         loading="lazy"
         decoding="async"
         style={{ width: size, height: size, objectFit: "contain", flexShrink: 0 }}
         onError={() => setFailed(true)} />
  );
}
// The canonical "default" model new users start on: the first frontier-tier
// Anthropic model that isn't Opus (Opus is the premium tier reserved for
// hard-problem use — too expensive as a blanket default). This is what every
// fresh agent / new org / first-time user chats with, so when we bump to
// Sonnet 4.6 the whole app follows by updating one line in PROVIDERS.
const DEFAULT_PROVIDER = "anthropic";
const DEFAULT_MODEL = (() => {
  const list = PROVIDERS.anthropic?.models || [];
  const pick = list.find(m => m.tier === "frontier" && !String(m.id).includes("opus"))
            || list.find(m => m.tier === "frontier")
            || list[0];
  return pick?.id || "claude-sonnet-4-20250514";
})();

const STARTER_EXTRAS = [
  "What can you help me with today?",
  "Show me an example of your best work",
  "Explain something complex simply",
  "Help me brainstorm ideas",
  "Draft a quick professional reply",
  "Review my writing for tone + clarity",
  "Summarize this for me",
  "Compare two options side-by-side",
  "Help me think through a hard decision",
  "Generate three alternatives",
  "Find the typo in this paragraph",
  "Make this more concise",
  "Translate this to plain English",
  "Turn this into a bulleted summary",
  "Suggest a better subject line",
  "What questions am I not asking?",
];

const DA = [
  {id:"ag-default",name:"ChatFort",emoji:"🏰",color:"#5ba3c9",desc:"Private AI for regulated industries.",model:DEFAULT_MODEL,provider:DEFAULT_PROVIDER,sysprompt:"You are ChatFort, a private AI assistant for regulated industries. Be helpful, precise, and discreet.",temperature:0.7,starters:["Summarize this confidentially","Draft a privileged memo","Explain HIPAA obligations","Review this contract clause"]},
  {id:"ag-legal",name:"Legal Counsel",emoji:"⚖️",color:"#7ab8d4",desc:"ABA 512-aligned. Privilege-safe.",model:DEFAULT_MODEL,provider:DEFAULT_PROVIDER,sysprompt:"You are a private legal AI aligned with ABA Formal Opinion 512.",temperature:0.3,starters:["Draft a privilege log entry","Summarize this deposition","Review this NDA"]},
  {id:"ag-finance",name:"Finance Advisor",emoji:"📊",color:"#4caf87",desc:"FINRA-aware. SEC posture.",model:DEFAULT_MODEL,provider:DEFAULT_PROVIDER,sysprompt:"You are a private financial AI. FINRA-aware and SEC-posture aligned.",temperature:0.4,starters:["Summarize client portfolio","Draft FINRA disclosure","Flag compliance issues"]},
  {id:"ag-health",name:"Clinical Assistant",emoji:"🏥",color:"#e8a87c",desc:"HIPAA-aligned. PHI stays local.",model:DEFAULT_MODEL,provider:DEFAULT_PROVIDER,sysprompt:"You are a HIPAA-aligned clinical AI for healthcare practices.",temperature:0.5,starters:["Summarize patient intake","Draft clinical note template","Explain HIPAA minimum necessary"]},
  {id:"ag-hermes-tools",name:"Hermes Tool Caller",emoji:"🧠",color:"#f59e0b",desc:"Nous Hermes — structured output, tool use, function calling.",model:"hermes-3-llama-3.1-70b",provider:"hermes",sysprompt:"You are Hermes, an expert AI assistant specializing in structured reasoning, tool use, and function calling. Think step by step. When using tools, always validate inputs and handle errors gracefully.",temperature:0.3,starters:["Extract structured data from this text","Call a function to process this","Build a JSON schema for this data","Reason through this step by step"],hermesAgent:true},
  {id:"ag-hermes-reason",name:"Hermes Reasoner",emoji:"🔮",color:"#d97706",desc:"Deep reasoning with Hermes 3 405B. Complex analysis & planning.",model:"hermes-3-llama-3.1-405b",provider:"hermes",sysprompt:"You are Hermes, a powerful reasoning AI. Approach every problem methodically. Break down complex questions, consider multiple perspectives, and provide thorough, well-structured answers.",temperature:0.5,starters:["Analyze this multi-step problem","Create a strategic plan for","Compare and contrast these approaches","What are the second-order effects of"],hermesAgent:true},
];

const DW = [
  {id:"ws-general",name:"General",icon:"🏰",color:"#5ba3c9",desc:"Your private workspace",sysprompt:"",created:Date.now()},
  {id:"ws-legal",name:"Legal",icon:"⚖️",color:"#7ab8d4",desc:"Privilege-safe",sysprompt:"Privileged legal workspace.",created:Date.now()},
  {id:"ws-finance",name:"Finance",icon:"📊",color:"#4caf87",desc:"FINRA/SEC-aware",sysprompt:"Private financial workspace.",created:Date.now()},
];
const DEFAULT_CHAT_WS_ID = "ws-general";

const DCF = [
  {id:"folder-general",name:"General",created:Date.now()},
];

// ── Agent Library ───────────────────────────────────────────────────────────
// Pre-built agent templates across common regulated-industry use cases. Shown
// in the Agents page; one-click install clones a template into the org's
// agents list with a fresh id. Templates are not output from any compliance
// body — treat the sysprompts as reasonable defaults customers can tune.
const AGENT_LIBRARY = [
  // Healthcare
  { cat:"Healthcare", emoji:"📋", color:"#e8a87c", name:"HIPAA Compliance Advisor",
    desc:"Answers questions about HIPAA Privacy, Security, and Breach Notification rules.",
    sysprompt:"You are a HIPAA compliance advisor for a covered entity or business associate. Ground every answer in 45 CFR Parts 160 and 164. Be precise about which rule applies (Privacy, Security, Breach Notification). Cite the specific section when you can. If the question requires legal advice, say so and recommend consulting counsel.",
    temperature:0.3,
    starters:["What are the 18 HIPAA identifiers?","Draft a minimum necessary justification","How long do we have to report a breach?","Is a BAA required with my vendor?"] },
  { cat:"Healthcare", emoji:"🩺", color:"#de7d5b", name:"Clinical Note Summarizer",
    desc:"Turns a long clinical note into a structured SOAP-format summary.",
    sysprompt:"You convert clinical encounter notes into concise SOAP-format summaries (Subjective, Objective, Assessment, Plan). Preserve clinical nuance; do not diagnose beyond what the source supports. Flag any critical findings. Never add information not present in the source.",
    temperature:0.2,
    starters:["Paste a note and I'll summarize it"] },
  { cat:"Healthcare", emoji:"📝", color:"#c6875b", name:"Prior Authorization Drafter",
    desc:"Drafts prior-authorization letters from a case summary.",
    sysprompt:"You draft prior-authorization appeal letters for clinicians. Be factual, cite the clinical justification clearly, reference the relevant medical-necessity criteria when given, and keep the tone professional. Never fabricate clinical facts.",
    temperature:0.4,
    starters:["Draft a PA letter for a specialty medication","Draft an appeal for a denied procedure"] },
  { cat:"Healthcare", emoji:"🏥", color:"#b56d43", name:"Patient Intake Triage",
    desc:"Conducts a structured symptom interview and suggests urgency level.",
    sysprompt:"You interview patients about presenting symptoms in a structured, empathetic way. Ask one clarifying question at a time. At the end, produce a differential-like summary and suggest an urgency level (emergent / urgent / routine / self-care). Always include a disclaimer that this is not medical advice and recommend the patient contact their provider.",
    temperature:0.4,
    starters:["I have a new symptom, help me describe it"] },

  // Legal
  { cat:"Legal", emoji:"⚖️", color:"#7ab8d4", name:"Contract Reviewer",
    desc:"Flags risky clauses, missing terms, and unusual language in a contract.",
    sysprompt:"You are a contract analyst reviewing agreements for a commercial party. Identify risk by clause — indemnification, limitation of liability, IP assignment, termination, auto-renewal, non-compete, choice of law, dispute resolution. For each concern, state the clause, the risk, and a suggested redline. Be direct. Do not give legal advice; suggest the user consult counsel for final decisions.",
    temperature:0.3,
    starters:["Paste a contract and I'll flag risks","Redline this MSA","Explain the indemnification clause"] },
  { cat:"Legal", emoji:"📚", color:"#6ba5c2", name:"Legal Research Assistant",
    desc:"Answers questions about statutes, case law, and regulations.",
    sysprompt:"You are a legal research assistant. Answer research questions grounded in the cited sources provided. When you don't have a source, say so — never invent a case citation. Organize answers by jurisdiction and holding. Always remind the user that research is not legal advice.",
    temperature:0.2,
    starters:["Summarize the current state of X in my jurisdiction","Find controlling precedent on Y"] },
  { cat:"Legal", emoji:"🔒", color:"#5c94b3", name:"Privileged Memo Drafter",
    desc:"Drafts attorney-work-product memos with proper privilege labels.",
    sysprompt:"You draft internal legal memos for attorneys. Mark the top of every memo 'PRIVILEGED & CONFIDENTIAL — ATTORNEY WORK PRODUCT'. Structure: Issue / Short Answer / Facts / Analysis / Conclusion. Cite sources when given. Do not include speculation outside the provided facts.",
    temperature:0.3,
    starters:["Draft a memo on this issue","Outline the analysis for this question"] },

  // HR / Compliance
  { cat:"HR & Compliance", emoji:"👥", color:"#8b5cf6", name:"HR Policy Q&A",
    desc:"Answers employee questions grounded in your HR handbook (attach via KB).",
    sysprompt:"You answer employee questions about HR policy. Only answer from the attached handbook / policy knowledge base — if a question isn't covered, say so and suggest contacting HR. Tone is friendly, neutral, and non-legal.",
    temperature:0.3,
    starters:["How much PTO do I have?","What's our remote work policy?","How do I request a leave of absence?"] },
  { cat:"HR & Compliance", emoji:"🚨", color:"#7c3aed", name:"Incident Report Intake",
    desc:"Conducts a structured intake of a workplace incident.",
    sysprompt:"You interview employees filing an incident report (safety, harassment, or other workplace events). Ask one question at a time. Cover: who was involved, when, where, what happened, witnesses, injuries if any. Produce a written summary suitable for HR review. Be neutral and non-leading. Include a note that the employee's statement was taken as of this session's date.",
    temperature:0.3,
    starters:["I need to report something that happened"] },

  // Finance
  { cat:"Finance", emoji:"📊", color:"#4caf87", name:"SEC Disclosure Drafter",
    desc:"Drafts Regulation S-K / S-X disclosure language from raw facts.",
    sysprompt:"You draft SEC disclosure language for public-company filings (10-K, 10-Q, 8-K). Be careful, precise, and conservative. Hedge appropriately ('may', 'could', 'no assurance'). Flag any claim that would need legal review before filing. Never invent financial figures — only restate what the user provides.",
    temperature:0.2,
    starters:["Draft risk-factor language for X","Draft an MD&A section on our Y"] },
];


// ── Stop generation ──────────────────────────────────────────────────────────
// Every in-flight /api/chat request registers its AbortController here; the
// composer's Stop button aborts them all (parallel compare slots included).
// Controllers are deregistered when their request settles so the set only
// holds live requests.
const _chatAbortControllers = new Set();
function stopActiveGeneration() {
  for (const c of _chatAbortControllers) { try { c.abort(); } catch {} }
  _chatAbortControllers.clear();
}

async function callAI(agent, messages, sysprompt, maxTokens, integrations, webSearch=false, allTools=[], kbId=null, onChunk=null, compareMode=false, onStatus=null, onRedacted=null) {
  const prov  = agent.provider || DEFAULT_PROVIDER;
  const model = agent.model    || DEFAULT_MODEL;

  // ── Server-proxy mode: API keys live on the server, not in the browser ──────
  if (SERVER_MODE && AUTH_TOKEN) {
    const now = new Date();
    const nowStr = now.toLocaleString("en-US",{weekday:"long",year:"numeric",month:"long",day:"numeric",hour:"2-digit",minute:"2-digit",timeZoneName:"short"});
    const isoDate = now.toISOString().split("T")[0];
    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    // Put date at the absolute top + use strong language so the model doesn't
    // fall back to its training cutoff. Some models (esp. Claude variants)
    // will still guess dates from training data unless the override is firm.
    let sp = `CRITICAL — CURRENT DATE: ${nowStr} (ISO: ${isoDate}${userTz?`, timezone ${userTz}`:""}).\nThis is the real current date, provided by the user's actual system clock. DO NOT use your training-data date or guess. When the user mentions "today", "this week", "tonight", "upcoming", or any time-relative term, resolve it against ${isoDate}.\n\n` + (sysprompt ? sysprompt + "\n\n" : "");
    // When web search is on, the server runs a multi-query pipeline BEFORE
    // invoking the model and injects results directly. No "search is enabled"
    // instruction here — that was making Claude hallucinate <search> tags.
    // The server's context block tells the model "don't try to search, results
    // are already below".
    // Defense in depth: also inject the date into the most recent user message
    // so it's inline in the context, not just in system. Some model backends
    // weight inline context more heavily than system role.
    const msgsWithDate = messages.map((m, i) => {
      if (i !== messages.length - 1 || m.role !== "user") return m;
      const prefix = `[Context: today is ${nowStr}]\n\n`;
      if (typeof m.content === "string") return { ...m, content: prefix + m.content };
      return m; // structured content (with files) — date is already in system prompt
    });
    const wantStream = typeof onChunk === "function";
    const _stopCtl = new AbortController();
    _chatAbortControllers.add(_stopCtl);
    const r = await fetch("/api/chat", {
      signal: _stopCtl.signal,
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${AUTH_TOKEN}` },
      body: JSON.stringify({
        provider:    prov,
        model,
        rawMessages: msgsWithDate,
        sysprompt:   sp,
        maxTokens,
        temperature: agent.temperature ?? 0.7,
        webSearch,
        kbId,
        stream:      wantStream,
        compareMode,
      }),
    });
    if (!r.ok) {
      _chatAbortControllers.delete(_stopCtl);
      const d = await r.json().catch(()=>({}));
      throw new Error(d.error || `HTTP ${r.status}`);
    }
    const ct = r.headers.get("content-type") || "";
    // KB citations. Non-streaming responses carry them in a header;
    // streaming responses deliver an `event: kb_sources` frame instead
    // (headers are already flushed by the first status frame).
    let kbSources = null;
    try { const _h = r.headers.get("X-KB-Sources"); if (_h) { const _a = JSON.parse(_h); if (Array.isArray(_a) && _a.length) kbSources = _a; } } catch {}
    if (wantStream && ct.includes("text/event-stream")) {
      // Read SSE stream: lines are `data: {...}` separated by blank lines,
      // optionally prefixed by `event: <type>` lines that set the frame
      // type. We handle two event types:
      //   - default (no `event:` line)  → data is a token frame {t}, a
      //     done frame {done,search,trace}, or an error frame {error}.
      //   - `event: status`             → data is a phase marker
      //     {phase:"searching"|"composing", sources?}. Forwarded to the
      //     onStatus callback so the UI can show "Searching…" during the
      //     retrieval wait (can be 10–20s before the first token when RAG
      //     + multi-query web search both run).
      // The event type resets on blank lines per the SSE spec.
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buf = "", acc = "", meta = null, traceObj = null, streamErr = null;
      let curEvent = "message";
      while (true) {
        let value, done;
        // A Stop click aborts the controller; reader.read() then rejects
        // with AbortError. Treat it as end-of-stream — the accumulated
        // partial answer is the result, not an error.
        try { ({ value, done } = await reader.read()); }
        catch (e) { if ((e && e.name === "AbortError") || _stopCtl.signal.aborted) break; throw e; }
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (line === "") { curEvent = "message"; continue; }
          if (line.startsWith("event:")) { curEvent = line.slice(6).trim(); continue; }
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          try {
            const evt = JSON.parse(payload);
            if (curEvent === "status") {
              try { onStatus && onStatus(evt.phase, evt); } catch {}
              continue;
            }
            if (curEvent === "kb_sources") {
              if (Array.isArray(evt) && evt.length) kbSources = evt;
              continue;
            }
            if (curEvent === "redacted") {
              // Slice 8: server's masked-summary frame. We attach it to the
              // latest user message so the UI can paint the "🔒 Masked: …"
              // chip without waiting for the assistant's first token.
              try { onRedacted && onRedacted(evt); } catch {}
              continue;
            }
            if (evt.error) { streamErr = evt.error; continue; }
            if (evt.done)  { meta = evt.search || null; traceObj = evt.trace || null; continue; }
            if (typeof evt.t === "string") {
              acc += evt.t;
              try { onChunk(acc); } catch {}
            }
          } catch {}
        }
      }
      _chatAbortControllers.delete(_stopCtl);
      if (streamErr) throw new Error(streamErr);
      const finalText = acc || (_stopCtl.signal.aborted ? "(Stopped)" : "(No response)");
      if (meta || traceObj || kbSources) return { __text: finalText, __search: meta, __trace: traceObj, __kbSources: kbSources };
      return finalText;
    }
    _chatAbortControllers.delete(_stopCtl);
    const d = await r.json().catch(()=>({}));
    if (d.search || d.trace || kbSources) { return { __text: d.text || "(No response)", __search: d.search || null, __trace: d.trace || null, __kbSources: kbSources }; }
    return d.text || "(No response)";
  }
  // ── Standalone mode (original logic) continues below ─────────────────────
  // Always inject current date/time so the model knows when "now" is
  const nowStr = new Date().toLocaleString("en-US", {weekday:"long",year:"numeric",month:"long",day:"numeric",hour:"2-digit",minute:"2-digit",timeZoneName:"short"});
  sysprompt = (sysprompt ? sysprompt + "\n\n" : "") + `Today is ${nowStr}.`;
  // Build messages — last user message may have attachments (files array)
  const msgs = messages.filter(m => m.role !== "system").map(m => {
    if (m.role === "user" && m.files && m.files.length > 0) {
      const contentBlocks = [];
      for (const f of m.files) {
        if (f.mediaType === "application/pdf") {
          contentBlocks.push({type:"document", source:{type:"base64", media_type:"application/pdf", data:f.data}});
        } else if (f.mediaType.startsWith("image/")) {
          contentBlocks.push({type:"image", source:{type:"base64", media_type:f.mediaType, data:f.data}});
        } else {
          // Plain text files — inject as text block
          contentBlocks.push({type:"text", text:`File: ${f.name}\n\`\`\`\n${f.text || "(binary file)"}\n\`\`\``});
        }
      }
      contentBlocks.push({type:"text", text:m.content});
      return {role:"user", content:contentBlocks};
    }
    return {role:m.role, content:m.content};
  });
  if (prov === "anthropic") {
    const orKey = integrations.openrouter?.key;
    if (orKey) {
      const orModelMap = {"claude-sonnet-4-6":"anthropic/claude-sonnet-4.6","claude-sonnet-4-20250514":"anthropic/claude-sonnet-4-5","claude-haiku-4-5-20251001":"anthropic/claude-haiku-4-5","claude-opus-4-5":"anthropic/claude-opus-4.5"};
      // When web search is on, use GPT-4o-mini with :online for real-time web results
      const orModel = webSearch ? "openai/gpt-4o-mini:online" : (orModelMap[model] || "anthropic/claude-sonnet-4-5");
      const m2flat = messages.filter(m => m.role !== "system").map(m => ({role:m.role, content:typeof m.content === "string" ? m.content : (m.content?.find?.(b=>b.type==="text")?.text || String(m.content))}));
      const m2 = sysprompt ? [{role:"system",content:sysprompt},...m2flat] : m2flat;
      const r = await fetch("https://openrouter.ai/api/v1/chat/completions", {method:"POST", headers:{"Content-Type":"application/json","Authorization":"Bearer "+orKey,"HTTP-Referer":"https://chatfortai.ai","X-Title":"ChatFort"}, body:JSON.stringify({model:orModel, messages:m2, max_tokens:maxTokens})});
      const d = await r.json();
      if (!r.ok) throw new Error(d.error?.message || "HTTP " + r.status);
      return d.choices?.[0]?.message?.content || "";
    }
    const body = {model, max_tokens:maxTokens, messages:msgs};
    if (sysprompt) body.system = sysprompt;
    if (webSearch) body.tools = [{type:"web_search_20250305",name:"web_search"}];
    const r = await fetch("https://api.anthropic.com/v1/messages", {method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(body)});
    if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.error?.message || `HTTP ${r.status}`); }
    const d = await r.json();
    return (d.content||[]).map(b=>b.text||"").join("").trim() || "(No text response)";
  }
  const key = integrations[prov]?.key;
  if (!key) throw new Error(`No API key for ${PROVIDERS[prov]?.name||prov}. Add in Integrations.`);
  // Non-Anthropic: flatten files to text description
  const m2flat = messages.filter(m => m.role !== "system").map(m => {
    if (m.role === "user" && m.files && m.files.length > 0) {
      const fileDesc = m.files.map(f => `[Attached: ${f.name} (${f.mediaType})]`).join(" ");
      return {role:"user", content: fileDesc + "\n" + m.content};
    }
    return {role:m.role, content:m.content};
  });
  const m2 = sysprompt ? [{role:"system",content:sysprompt},...m2flat] : m2flat;
  const ep = PROVIDERS[prov]?.ep || "";
  const headers = {"Content-Type":"application/json","Authorization":`Bearer ${key}`};
  // Hermes (Nous Research) — OpenAI-compatible with function calling support
  if (prov === "hermes") {
    const hermesCfg = integrations.hermes || {};
    let hermesEp, hermesKey;
    if (hermesCfg.useOpenRouter) {
      hermesEp = "https://openrouter.ai/api/v1/chat/completions";
      hermesKey = integrations.openrouter?.key;
      if (!hermesKey) throw new Error("OpenRouter key required. Add in Integrations.");
      const orModel = model.startsWith("nousresearch/") ? model : "nousresearch/" + model;
      const m2 = sysprompt ? [{role:"system",content:sysprompt},...m2flat] : m2flat;
      const r = await fetch(hermesEp, {method:"POST", headers:{"Content-Type":"application/json","Authorization":"Bearer "+hermesKey,"HTTP-Referer":"https://chatfortai.ai","X-Title":"ChatFort"}, body:JSON.stringify({model:orModel, messages:m2, temperature:agent.temperature??0.3, max_tokens:maxTokens})});
      const d = await r.json();
      if (!r.ok) throw new Error(d.error?.message || "HTTP " + r.status);
      return d.choices?.[0]?.message?.content || "";
    }
    hermesEp = PROVIDERS.hermes.ep;
    hermesKey = hermesCfg.key;
    if (!hermesKey) throw new Error("Nous API key required. Add in Integrations.");
    const m2 = sysprompt ? [{role:"system",content:sysprompt},...m2flat] : m2flat;
    const body = {model, messages:m2, temperature:agent.temperature??0.3, max_tokens:maxTokens};
    if (agent.hermesTools?.length) body.tools = agent.hermesTools;
    const r = await fetch(hermesEp, {method:"POST", headers:{"Content-Type":"application/json","Authorization":"Bearer "+hermesKey}, body:JSON.stringify(body)});
    const d = await r.json();
    if (!r.ok) throw new Error(d.error?.message || "HTTP " + r.status);
    const hmsg = d.choices?.[0]?.message;
    if (hmsg?.tool_calls?.length) {
      const toolResults = hmsg.tool_calls.map(tc => "**" + tc.function.name + "**\n```json\n" + tc.function.arguments + "\n```").join("\n\n");
      return "**Tool calls requested:**\n\n" + toolResults + (hmsg.content ? "\n\n" + hmsg.content : "");
    }
    return hmsg?.content || "";
  }
  // OpenAI with web search — use Responses API with gpt-4o-search-preview
  if (prov === "openai" && webSearch) {
    const input = m2.filter(m => m.role !== "system").map(m => ({role:m.role, content:m.content}));
    const sysMsg = m2.find(m => m.role === "system");
    const reqBody = {model:"gpt-4o-search-preview", tools:[{type:"web_search_preview"}], input};
    if (sysMsg) reqBody.instructions = sysMsg.content;
    const r = await fetch("https://api.openai.com/v1/responses", {method:"POST", headers, body:JSON.stringify(reqBody)});
    const d = await r.json();
    if (!r.ok) throw new Error(d.error?.message || `HTTP ${r.status}`);
    const outputText = (d.output||[]).filter(o=>o.type==="message").flatMap(o=>o.content||[]).filter(c=>c.type==="output_text").map(c=>c.text).join("");
    return outputText || "(No response)";
  }
  // OpenRouter requires extra headers and supports model routing
  if (prov === "openrouter") {
    headers["HTTP-Referer"] = "https://chatfortai.ai";
    headers["X-Title"] = "ChatFort";
  }
  // Build custom tool schemas for function calling
  const agToolIds = agent.toolIds || [];
  const customToolSchemas = agToolIds.map(id => {
    const t = allTools.find(t => t.id === id);
    if (!t || !t.schema) return null;
    return {type:"function", function:{...t.schema}};
  }).filter(Boolean);

  const body = {model, messages:m2, temperature:agent.temperature??0.7, max_tokens:maxTokens};
  // OpenRouter extras: allow fallbacks, provider routing, and web search via :online suffix
  if (prov === "openrouter") {
    const orCfg = integrations.openrouter;
    if (orCfg?.fallbacks?.length) body.models = [model, ...orCfg.fallbacks];
    if (orCfg?.providerOrder?.length) body.provider = {order: orCfg.providerOrder};
    if (orCfg?.fallbackEnabled) body.route = "fallback";
    if (webSearch) body.model = model.includes(":online") ? model : model + ":online";
  }
  // Attach custom tools if any
  if (customToolSchemas.length) body.tools = customToolSchemas;

  const r = await fetch(ep, {method:"POST", headers, body:JSON.stringify(body)});
  const d = await r.json();
  if (!r.ok) throw new Error(d.error?.message || `HTTP ${r.status}`);

  // Handle function/tool calls (OpenAI-compatible format)
  if (d.choices?.[0]?.finish_reason === "tool_calls" && customToolSchemas.length) {
    const toolCalls = d.choices[0].message.tool_calls || [];
    const toolResultMsgs = [];
    for (const tc of toolCalls) {
      const fnName = tc.function?.name;
      const toolDef = allTools.find(t => t.schema?.name === fnName);
      let resultStr = `Tool "${fnName}" not found.`;
      if (toolDef) {
        try {
          const args = JSON.parse(tc.function.arguments || "{}");
          // Extract the async function and execute it safely
          // eslint-disable-next-line no-new-func
          const execFn = new Function("args", "auth", "secrets", `
            ${toolDef.code}
            return (async () => {
              const result = await ${fnName}(args, auth, secrets);
              return result;
            })();
          `);
          const toolAuth = buildToolAuthRuntime(toolDef.auth);
          const result = await execFn(args, toolAuth, toolAuth.secrets);
          resultStr = result !== undefined ? String(result) : "(no result)";
        } catch(e) {
          resultStr = "Error executing tool: " + e.message;
        }
      }
      toolResultMsgs.push({role:"tool", tool_call_id:tc.id, content:resultStr});
    }
    // Continue conversation with tool results
    const m3 = [...m2, d.choices[0].message, ...toolResultMsgs];
    const r2 = await fetch(ep, {method:"POST", headers, body:JSON.stringify({...body, messages:m3, tools:undefined})});
    const d2 = await r2.json();
    if (!r2.ok) throw new Error(d2.error?.message || `HTTP ${r2.status}`);
    return d2.choices?.[0]?.message?.content || "";
  }

  return d.choices?.[0]?.message?.content || "";
}

// Renders the "🔍 Retrieved N sources" panel above AI messages that used
// multi-query web search. Shows the queries as chips and the unique source
// URLs as a collapsible list. Inspired by OpenWebUI's search preview.
// Renders the "📚 Grounded in N knowledge-base sources" panel above AI
// messages that used KB retrieval. The [n] chips match the inline [1] [2]
// citation numbers the model was instructed to use. Sibling of
// SearchSources (web results) below.
function KBSources({ sources }) {
  const [open, setOpen] = useState(false);
  if (!Array.isArray(sources) || sources.length === 0) return null;
  return (
    <div style={{ marginBottom: 10, background: S.inputBg, border: `1px solid ${S.border2}`, borderRadius: 10, overflow: "hidden" }}>
      <div onClick={()=>setOpen(o=>!o)} style={{ padding: "8px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
        <span style={{ fontSize: 14 }}>📚</span>
        <span style={{ flex: 1, color: S.text2, fontWeight: 600 }}>Grounded in {sources.length} knowledge-base source{sources.length===1?"":"s"}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2.5" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}><polyline points="6 9 12 15 18 9"/></svg>
      </div>
      {open && (
        <div style={{ padding: "8px 12px 12px", borderTop: `1px solid ${S.border}`, display: "flex", flexDirection: "column", gap: 4 }}>
          {sources.map((s) => (
            <div key={s.n} style={{ fontSize: 11, padding: "4px 8px", borderRadius: 6, background: S.cardBg || S.surface, border: `1px solid ${S.border2}`, color: S.text2, display: "flex", alignItems: "center", gap: 7 }}>
              <span style={{ fontSize: 9, color: S.accent, fontFamily: "monospace", fontWeight: 700, flexShrink: 0 }}>[{s.n}]</span>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>{s.doc}</span>
              {typeof s.score === "number" && <span style={{ fontSize: 9, color: S.text3, fontFamily: "monospace", flexShrink: 0 }}>{Math.round(s.score * 100)}% match</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SearchSources({ search }) {
  const [open, setOpen] = useState(false);
  if (!search) return null;
  const queries = Array.isArray(search.queries) ? search.queries : [];
  const sources = Array.isArray(search.sources) ? search.sources : [];
  const domain = (u) => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; } };
  return (
    <div style={{ marginBottom: 10, background: S.inputBg, border: `1px solid ${S.border2}`, borderRadius: 10, overflow: "hidden" }}>
      <div onClick={()=>setOpen(o=>!o)} style={{ padding: "8px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
        <span style={{ fontSize: 14 }}>🔍</span>
        <span style={{ flex: 1, color: S.text2, fontWeight: 600 }}>Retrieved {sources.length} source{sources.length===1?"":"s"}</span>
        <span style={{ fontSize: 10, color: S.text3, fontFamily: "monospace" }}>{queries.length} {queries.length===1?"query":"queries"}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2.5" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}><polyline points="6 9 12 15 18 9"/></svg>
      </div>
      {open && (
        <div style={{ padding: "4px 12px 12px", borderTop: `1px solid ${S.border}` }}>
          {queries.length > 0 && (
            <>
              <div style={{ fontSize: 10, color: S.text3, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.05em", margin: "10px 0 6px" }}>Queries run</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {queries.map((q, i) => (
                  <span key={i} style={{ fontSize: 10, padding: "3px 9px", borderRadius: 12, background: S.cardBg || S.surface, border: `1px solid ${S.border2}`, color: S.text2, display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    {q}
                  </span>
                ))}
              </div>
            </>
          )}
          {sources.length > 0 && (
            <>
              <div style={{ fontSize: 10, color: S.text3, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.05em", margin: "12px 0 6px" }}>Sources</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {sources.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer" style={{ fontSize: 11, padding: "4px 8px", borderRadius: 6, background: S.cardBg || S.surface, border: `1px solid ${S.border2}`, color: S.text2, textDecoration: "none", display: "flex", alignItems: "center", gap: 7, overflow: "hidden" }}>
                    <span style={{ fontSize: 9, color: S.accent, fontFamily: "monospace", fontWeight: 700, flexShrink: 0 }}>[{i+1}]</span>
                    <span style={{ color: S.accent, fontWeight: 600, flexShrink: 0 }}>{domain(url)}</span>
                    <span style={{ color: S.text3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "monospace", fontSize: 10 }}>{url}</span>
                  </a>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Markdown renderer for assistant messages — backed by react-markdown +
// remark-gfm. The previous homegrown regex-based renderer didn't support
// GFM tables, didn't render thematic-break dashes as <hr>, and shipped
// flat <div> "headings" instead of real h1/h2/h3, so users on prod (commit
// 123a9dc) saw literal dash lines and pipe-table syntax in plaintext.
//
// Component overrides keep the chat-bubble look-and-feel:
//   - Fenced code blocks get the existing Preview / Copy toolbar via a
//     custom <pre>+<code> pair. (`pre` strips the default react-markdown
//     wrapper so our `code` override can render the full block UI.)
//   - Inline code keeps the prior muted-pill look.
//   - Links open in new tabs with rel="noopener noreferrer".
//   - h1/h2/h3 get explicit sizing so the chat bubble doesn't inherit the
//     browser default (which dwarfs surrounding text on a small bubble).
// Slice 6: zip + single-file download helpers. Used by the artifact-panel
// "Download file" / "Download all" buttons. JSZip is lazy-loaded the first
// time someone hits Download all so the cold-start bundle stays light;
// loadScript caches the load so repeat clicks are instant.
const JSZIP_CDN = "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
async function loadJSZip() {
  if (typeof window === "undefined") throw new Error("no window");
  if (window.JSZip) return window.JSZip;
  await loadScript(JSZIP_CDN);
  if (!window.JSZip) throw new Error("JSZip failed to attach");
  return window.JSZip;
}
function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "chatfort-project";
}
function mimeFor(name) {
  const ext = (String(name || "").split(".").pop() || "").toLowerCase();
  return {
    html: "text/html", htm: "text/html",
    css:  "text/css",
    js:   "application/javascript", mjs: "application/javascript",
    json: "application/json",
    svg:  "image/svg+xml",
    md:   "text/markdown",
    xml:  "application/xml",
    yaml: "text/yaml", yml: "text/yaml",
    csv:  "text/csv",
    txt:  "text/plain",
  }[ext] || "text/plain";
}
// Slice 6: project-name inference for the zip filename + root folder name.
// Priority: (1) explicit File-Structure code block in the assistant's
// message text — root folder name is a token followed by `/` then a tree-
// drawing line; (2) <title> in any sibling HTML; (3) date-stamped fallback.
function inferProjectName(messageText, codeMap) {
  if (typeof messageText === "string" && messageText) {
    const m = /(?:^|\n)\s*([A-Za-z][A-Za-z0-9_.\-]*)\/\s*(?:\r?\n)\s*[├└│─\s]*[├└][─\s]/m.exec(messageText)
          || /(?:^|\n)\s*([A-Za-z][A-Za-z0-9_.\-]*)\/\s*\r?\n\s*[├└│]/m.exec(messageText);
    if (m) return slugify(m[1]);
  }
  if (codeMap) {
    for (const code of Object.values(codeMap)) {
      const tm = /<title[^>]*>([^<]+)<\/title>/i.exec(code || "");
      if (tm && tm[1].trim()) return slugify(tm[1].trim());
    }
  }
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `chatfort-project-${yyyy}${mm}${dd}`;
}
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the browser a beat to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
function imageExtForDownload(contentType, pathname) {
  const ct = String(contentType || "").split(";")[0].trim().toLowerCase();
  if (ct === "image/jpeg" || ct === "image/jpg") return "jpg";
  if (ct === "image/png") return "png";
  if (ct === "image/webp") return "webp";
  if (ct === "image/gif") return "gif";
  const ext = (String(pathname || "").split(".").pop() || "").toLowerCase();
  return ["png", "jpg", "jpeg", "webp", "gif"].includes(ext) ? (ext === "jpeg" ? "jpg" : ext) : "png";
}
function extractMarkdownImages(md) {
  const images = [];
  const re = /!\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;
  let m;
  while ((m = re.exec(String(md || "")))) {
    const alt = String(m[1] || "").trim();
    const url = String(m[2] || "").trim();
    if (url) images.push({ alt, url });
  }
  return images;
}
function isGeneratedChatImageAsset(url) {
  try {
    const base = typeof window !== "undefined" ? window.location.origin : "https://chatfort.local";
    const parsed = new URL(String(url || ""), base);
    return parsed.pathname.startsWith("/api/chat/images/");
  } catch {
    return false;
  }
}

const MD_PREVIEW_LANGS = new Set(["html", "htm", "css", "javascript", "js", "jsx", "tsx", "vue", "svelte", "svg", "xml"]);

// Slice 5: scan a markdown message for fenced code blocks and pair each
// with the filename in the heading immediately preceding it. Used so the
// HTML preview can inline its sibling stylesheet/script and so each inline
// Preview button can be labelled with the file it'll render. Heading match
// is `## name`, `### name`, or `#### name`; any non-empty non-heading line
// in between resets the pairing. Code is the unfenced body, trailing
// newline normalised to match react-markdown's `code` override.
function parseMessageCodeBlocks(md) {
  if (!md || typeof md !== "string") return [];
  const out = [];
  const lines = md.split("\n");
  let lastHeading = null;
  let synthCounter = 0;
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const hm = /^#{2,6}\s+(.+?)\s*#*\s*$/.exec(line);
    if (hm) {
      // Strip wrapping backticks and trailing punctuation that's not part of
      // a filename — we just need the bare token (e.g. `styles.css`).
      const raw = hm[1].replace(/^[`*_"']+|[`*_"':]+$/g, "").trim();
      lastHeading = raw || null;
      i++;
      continue;
    }
    const cm = /^```(\w[\w+-]*)?(?:\s+.*)?$/.exec(line);
    if (cm) {
      const lang = (cm[1] || "").toLowerCase();
      const body = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i])) {
        body.push(lines[i]);
        i++;
      }
      i++; // skip closing fence
      const code = body.join("\n").replace(/\n$/, "");
      let filename = lastHeading;
      if (!filename || !/\.[a-z0-9]{1,8}$/i.test(filename)) {
        // Heading wasn't a real filename — synthesize one from lang.
        synthCounter += 1;
        filename = `untitled-${synthCounter}.${lang || "txt"}`;
      }
      out.push({ filename, lang, code });
      lastHeading = null;
      continue;
    }
    // Any non-empty, non-blank, non-heading line breaks the heading↔block
    // adjacency — e.g. an explanatory paragraph between heading and fence
    // means the heading wasn't a filename label.
    if (line.trim()) lastHeading = null;
    i++;
  }
  return out;
}

// Slice 5: rewrite an HTML source string so that <link rel="stylesheet"
// href="X.css"> and <script src="Y.js"> tags are replaced by inline
// <style>/<script> blocks containing the matching sibling code. External
// URLs (http://, https://, //, data:) are left alone so Google Fonts etc.
// continue to load over the network. Sibling lookup is case-insensitive
// but we use the canonical key for the data-cf-inlined marker.
function inlineSiblings(htmlSrc, siblings) {
  if (!htmlSrc || !siblings) return htmlSrc;
  const keys = Object.keys(siblings);
  if (!keys.length) return htmlSrc;
  const byLower = new Map();
  for (const k of keys) byLower.set(k.toLowerCase(), k);
  const resolve = (rawHref) => {
    if (!rawHref) return null;
    const trimmed = rawHref.trim();
    if (/^(?:https?:|data:|\/\/)/i.test(trimmed)) return null;
    let h = trimmed.replace(/^\.?\//, "").split("?")[0].split("#")[0];
    return byLower.get(h.toLowerCase()) || null;
  };
  // <link rel="stylesheet" ...> in any attribute order. Tolerate single,
  // double, or no quotes around href and rel.
  let out = htmlSrc.replace(/<link\b([^>]*)\/?>/gi, (full, attrs) => {
    if (!/\brel\s*=\s*["']?\s*stylesheet\b/i.test(attrs)) return full;
    const m = /\bhref\s*=\s*"([^"]+)"/i.exec(attrs)
          || /\bhref\s*=\s*'([^']+)'/i.exec(attrs)
          || /\bhref\s*=\s*([^\s>]+)/i.exec(attrs);
    if (!m) return full;
    const sib = resolve(m[1]);
    if (!sib) return full;
    return `<style data-cf-inlined="${sib}">\n${siblings[sib]}\n</style>`;
  });
  // <script src="..."> ... </script> — match paired form first (greedy on
  // body would over-consume across multiple scripts; non-greedy keeps each
  // tag scoped to its own closing).
  out = out.replace(/<script\b([^>]*)>([\s\S]*?)<\/script>/gi, (full, attrs) => {
    const m = /\bsrc\s*=\s*"([^"]+)"/i.exec(attrs)
          || /\bsrc\s*=\s*'([^']+)'/i.exec(attrs)
          || /\bsrc\s*=\s*([^\s>]+)/i.exec(attrs);
    if (!m) return full; // inline script, leave alone
    const sib = resolve(m[1]);
    if (!sib) return full;
    const extras = [];
    if (/\bdefer\b/i.test(attrs))  extras.push("defer");
    if (/\basync\b/i.test(attrs))  extras.push("async");
    const tm = /\btype\s*=\s*["']?(module|[\w/+-]+)["']?/i.exec(attrs);
    if (tm) extras.push(`type="${tm[1]}"`);
    const extra = extras.length ? " " + extras.join(" ") : "";
    return `<script data-cf-inlined="${sib}"${extra}>\n${siblings[sib]}\n</script>`;
  });
  // Self-closing <script src="..." /> — rare but valid in XHTML output.
  out = out.replace(/<script\b([^>]*?)\/>/gi, (full, attrs) => {
    const m = /\bsrc\s*=\s*"([^"]+)"/i.exec(attrs)
          || /\bsrc\s*=\s*'([^']+)'/i.exec(attrs)
          || /\bsrc\s*=\s*([^\s>]+)/i.exec(attrs);
    if (!m) return full;
    const sib = resolve(m[1]);
    if (!sib) return full;
    return `<script data-cf-inlined="${sib}">\n${siblings[sib]}\n</script>`;
  });
  return out;
}

// Slice 5: HTML scaffold for previewing a CSS file standalone. Renders a
// representative set of common elements (nav, headings, paragraph, button,
// input, list, table) so the user actually sees the styles applied.
function buildCssPreviewScaffold(cssCode) {
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>${cssCode}</style></head><body>
  <nav><a href="#">Link</a> <a href="#">Link</a></nav>
  <main>
    <h1>Heading 1</h1><h2>Heading 2</h2><h3>Heading 3</h3>
    <p>Paragraph with <a href="#">a link</a> and <strong>bold</strong>.</p>
    <button>Button</button>
    <input placeholder="Input" /> <input type="checkbox" />
    <ul><li>Item</li><li>Item</li></ul>
    <table><tr><th>Col</th><th>Col</th></tr><tr><td>Cell</td><td>Cell</td></tr></table>
  </main>
</body></html>`;
}

// Slice 5: HTML scaffold for previewing a JS file standalone. Mirrors
// console.log/info/warn/error into a <pre id="cf-console"> so the user can
// see output without opening DevTools (the iframe is sandboxed but still
// can't be inspected from the outer page). Native console calls still flow
// through to the parent DevTools because we save the originals first.
function buildJsPreviewScaffold(jsCode) {
  const consoleShim = `(function(){
    var out = document.getElementById('cf-console');
    var orig = { log: console.log, info: console.info, warn: console.warn, error: console.error };
    ['log','info','warn','error'].forEach(function(level){
      console[level] = function(){
        var args = Array.prototype.slice.call(arguments);
        try { orig[level].apply(console, args); } catch(_){}
        if (out) {
          var line = document.createElement('div');
          line.dataset.level = level;
          line.textContent = '[' + level + '] ' + args.map(function(x){
            try { return typeof x === 'object' ? JSON.stringify(x) : String(x); }
            catch(_) { return String(x); }
          }).join(' ');
          out.appendChild(line);
        }
      };
    });
    window.addEventListener('error', function(e){
      if (!out) return;
      var line = document.createElement('div');
      line.dataset.level = 'error';
      line.textContent = '[error] ' + (e.message || String(e));
      out.appendChild(line);
    });
  })();`;
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<style>
  body{margin:0;font-family:system-ui,-apple-system,sans-serif;background:#0e1116;color:#d8dee9;display:flex;flex-direction:column;height:100vh}
  .cf-hint{padding:8px 14px;font-size:11px;color:#7d8590;border-bottom:1px solid #21262d;background:#161b22}
  #cf-console{flex:1;margin:0;padding:10px 14px;overflow:auto;font:12px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace;background:#0e1116;color:#d8dee9}
  #cf-console > div{padding:1px 0;white-space:pre-wrap;word-break:break-word}
  #cf-console > div[data-level="warn"]{color:#d29922}
  #cf-console > div[data-level="error"]{color:#f85149}
  #cf-console > div[data-level="info"]{color:#58a6ff}
</style>
</head><body>
<div class="cf-hint">JavaScript-only preview · open DevTools or interact with the page below to see output</div>
<pre id="cf-console"></pre>
<script>${consoleShim}<\/script>
<script>${jsCode}<\/script>
</body></html>`;
}
// Build the chat-side code-block renderer the shared `MarkdownMessage`
// will call for every fenced or inline code element. We keep the
// pre-scan + filename-from-heading + Preview/Copy buttons here at the
// chat-call-site because they're chat-specific UX (Hermes has no
// HTML-preview pane). The shared renderer handles every other element
// (links, tables, headings, lists, blockquote, hr) — gaining the
// URL-scheme XSS filter as a side effect, since chat's previous
// inline renderer didn't have one.
function chatCodeBlockOverride({ codeFilename, siblings, text }) {
  return function ChatCode({ className, children, ...props }) {
    const m = /^language-(.+)$/.exec(className || "");
    if (!m) {
      // Inline code — no language class. Theme-neutral background tint
      // (works in both light + dark without coupling to the S palette).
      return (
        <code style={{ background: "rgba(127,127,127,0.15)", padding: "1px 5px", borderRadius: 4, fontFamily: "monospace", fontSize: "0.92em" }} {...props}>
          {children}
        </code>
      );
    }
    const lang = m[1].toLowerCase();
    const content = String(children).replace(/\n$/, "");
    const canPreview = MD_PREVIEW_LANGS.has(lang);
    const filename = codeFilename.get(content) || null;
    const previewLabel = filename
      ? `Preview · ${filename.length > 24 ? filename.slice(0, 23) + "…" : filename}`
      : `Preview · ${lang}`;
    const previewTitleAttr = `Preview ${filename || lang}`;
    return (
      <div className="cf-code-block" style={{ background: S.codeBlockBg, border: `1px solid ${S.border2}`, borderRadius: 8, margin: "8px 0", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", padding: "3px 8px 3px 12px", borderBottom: `1px solid ${S.border}` }}>
          {lang && <span style={{ fontSize: 10, color: S.text3, fontFamily: "monospace", display: "inline-flex", alignItems: "center" }}><span className="cf-lang-dot" data-lang={lang}></span>{filename || lang}</span>}
          <div className="cf-code-actions" style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
            {canPreview && (
              <button onClick={() => setPreviewCb?.({ html: content, lang, filename, siblings, messageText: text })}
                title={previewTitleAttr}
                aria-label={previewTitleAttr}
                style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 5, border: `1px solid ${S.accent}55`, background: S.accentDim, color: S.accent, fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "monospace" }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                {previewLabel}
              </button>
            )}
            <CopyMorphButton text={content} title="Copy" copiedChildren="Copied"
              style={{ padding: "2px 8px", borderRadius: 5, border: `1px solid ${S.border2}`, background: "none", color: S.text3, fontSize: 10, cursor: "pointer", fontFamily: "monospace" }}>
              Copy
            </CopyMorphButton>
          </div>
        </div>
        <CodeBlockView content={content} className={className} lang={lang} theme={chatMarkdownTheme()} />
      </div>
    );
  };
}

// Map chat's `S` palette to the theme-key contract MarkdownMessage
// expects. Done at render time (not import time) because `S` is a
// closure variable that flips with theme + branding accent.
function chatMarkdownTheme() {
  return {
    text:         S.text,
    textMuted:    S.text2,
    accent:       S.accent,
    border:       S.border2,
    borderSubtle: S.border,
    codeBlockBg:  S.codeBlockBg,
    surfaceAlt:   S.surface2 || "rgba(127,127,127,0.08)",
  };
}

const MsgContent = memo(function MsgContent({ text }) {
  // memo() keys on `text` alone, so a dark/light toggle (App re-render,
  // identical props) used to SKIP these components — every existing
  // message kept the palette from its last render and dark text sat
  // unreadable on the light background. The theme effect dispatches
  // "cf_theme_change" after flipping the module-level `S` palette;
  // bump local state so chatMarkdownTheme() re-reads the live colors.
  // ("storage" covers theme flips made in another tab.)
  const [, setThemeTick] = useState(0);
  useEffect(() => {
    const onTheme = () => setThemeTick((t) => t + 1);
    window.addEventListener("cf_theme_change", onTheme);
    window.addEventListener("storage", onTheme);
    return () => {
      window.removeEventListener("cf_theme_change", onTheme);
      window.removeEventListener("storage", onTheme);
    };
  }, []);
  if (!text) return null;
  // Slice 5: pre-scan code blocks once per render so each Preview button
  // can be labelled with its filename (taken from the heading immediately
  // above it) and so the HTML preview can pull in its sibling CSS / JS
  // files. Slice "chat-md-safety": this pre-scan is still chat-specific
  // and stays here — the override factory closes over it.
  const { codeFilename, siblings } = useMemo(() => {
    const blocks = parseMessageCodeBlocks(text);
    const codeFilename = new Map();
    const sibs = {};
    for (const b of blocks) {
      if (!codeFilename.has(b.code)) codeFilename.set(b.code, b.filename);
      sibs[b.filename] = b.code;
    }
    return { codeFilename, siblings: sibs };
  }, [text]);
  // Memoize the code-block override so MarkdownMessage's useMemo on it
  // keeps a stable identity across renders that don't change `text`.
  const codeOverride = useMemo(
    () => chatCodeBlockOverride({ codeFilename, siblings, text }),
    [codeFilename, siblings, text]
  );
  return (
    <MarkdownMessage
      text={text}
      theme={chatMarkdownTheme()}
      codeBlockOverride={codeOverride}
    />
  );
});

const IC = {
  chat:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  ws:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  agent:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>,
  integ:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  settings:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  analytics:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>,
  plus:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  send:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  x:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  edit:<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  archive:<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="4" rx="1"/><path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8"/><path d="M10 13l2 2 2-2"/><path d="M12 11v4"/></svg>,
  unarchive:<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="4" rx="1"/><path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8"/><path d="M10 14l2-2 2 2"/><path d="M12 12v5"/></svg>,
  trash:<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>,
  spin:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{animation:"spin 1s linear infinite"}}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-.49-4.95"/></svg>,
  menu:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
  sun:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>,
  moon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>,
  users:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  invite:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  shield2:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>,
  copy:<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
  retry:<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.95"/></svg>,
  eye:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  lock:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  workflows:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
  cowork:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg>,
  bots:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z"/><circle cx="12" cy="11.5" r="1"/><circle cx="8.5" cy="11.5" r="1"/><circle cx="15.5" cy="11.5" r="1"/></svg>,
  cli:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="16" rx="2"/><polyline points="7 9 10 12 7 15"/><line x1="13" y1="15" x2="17" y2="15"/></svg>,
  mcp:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/><circle cx="12" cy="10" r="3"/><path d="M9 10h.01M15 10h.01"/></svg>,
  plug:<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22V12M7 7V3M17 7V3M7 12H5a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2h-2M7 7h10v5H7z"/></svg>,
  terminal:<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></svg>,
  clip:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>,
  tools:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
  code:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>,
  bolt:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  import_:<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  export_:<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  gear:<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  // Outline icons matched to the rest of the rail so emoji rows (Automations,
  // Marketplace, Knowledge, Audit log, SSO, Billing, Owner console) stop
  // standing out as full-color against the rest of the monochrome nav.
  automation:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" y1="16" x2="8" y2="16"/><line x1="16" y1="16" x2="16" y2="16"/></svg>,
  marketplace:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19.43 12.98a8 8 0 1 0-7.43 4.97M12 8v5l3 2"/><path d="M20 14v7M17 17l3 3 3-3"/></svg>,
  knowledge:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
  guide:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 4.5v15"/><path d="M8 7h8M8 11h8M8 15h5"/></svg>,
  audit:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>,
  sso:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  billing:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><line x1="6" y1="15" x2="10" y2="15"/></svg>,
  crown:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 18h20"/><path d="M3 7l5 5 4-8 4 8 5-5v11H3z"/></svg>,
};

const COLORS=["#5ba3c9","#7ab8d4","#4caf87","#e8a87c","#e06c6c","#a78bfa","#f59e0b","#94a3b8"];
// Common IANA timezones for the Automations schedule picker. Not exhaustive
// — covers the major business centers plus UTC. Server accepts anything
// Intl.DateTimeFormat can round-trip, so users can paste a custom zone
// into the PUT body if they need one that isn't in this list.
const TIMEZONES = [
  "UTC",
  "America/Los_Angeles", "America/Denver", "America/Chicago", "America/New_York", "America/Toronto", "America/Sao_Paulo",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Madrid", "Europe/Amsterdam", "Europe/Warsaw",
  "Africa/Johannesburg", "Africa/Cairo",
  "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Shanghai", "Asia/Tokyo", "Asia/Seoul",
  "Australia/Sydney", "Pacific/Auckland",
];
const EMOJIS=["🏰","💼","🔬","🎨","⚙️","📚","🚀","💡","🔐","🤖","👨‍💻","📊","⚖️","🏥","✍️","🌍"];

function Btn({onClick,children,variant="primary",style:xs,className}) {
  const base = {padding:"8px 16px",borderRadius:8,fontFamily:"inherit",fontSize:13,fontWeight:500,cursor:"pointer",display:"flex",alignItems:"center",gap:6};
  const v = {primary:{...base,background:S.grad2,color:"#fff",border:"none",boxShadow:`0 2px 10px ${S.accent}40`},secondary:{...base,background:S.inputBg,color:S.text2,border:`1px solid ${S.border2}`},danger:{...base,background:"transparent",color:S.danger,border:`1px solid ${S.danger}55`}};
  // Default-include cf-btn-lift so every caller of <Btn ...> gets
  // the hover lift + brightness automatically. Callers can override
  // with their own className when needed.
  return <button onClick={onClick} className={"cf-btn-lift " + (className || "")} style={{...v[variant],...xs}}>{children}</button>;
}
const ISt = () => ({width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"9px 12px",color:S.text,fontFamily:"inherit",fontSize:13,outline:"none",boxSizing:"border-box"});
// Escape-to-close support for stacked modals. A module-level stack
// tracks every mounted Modal in mount order; only the top-most one
// fires onClose when Escape is pressed. Prevents a single keypress
// from closing every layer at once when Run-detail sits on Recent
// runs, for example.
const _modalStack = [];
function useEscapeClose(onClose, enabled) {
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });
  useEffect(() => {
    if (!enabled) return undefined;
    const myId = {};
    _modalStack.push(myId);
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (_modalStack[_modalStack.length - 1] !== myId) return;
      e.stopPropagation();
      const fn = onCloseRef.current;
      if (typeof fn === "function") fn();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const idx = _modalStack.indexOf(myId);
      if (idx >= 0) _modalStack.splice(idx, 1);
    };
  }, [enabled]);
}

function Modal({open,title,onClose,children,footer}) {
  useEscapeClose(onClose, !!open);
  if (!open) return null;
  return (
    <div onClick={onClose} className="cf-modal-backdrop" style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(8px)"}}>
      <div onClick={e=>e.stopPropagation()} className="cf-modal-panel" style={{background:S.modalBg,border:`1px solid ${S.border2}`,borderRadius:16,width:"90%",maxWidth:540,maxHeight:"88vh",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}}>
        <div style={{padding:"18px 22px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <span style={{fontSize:16,fontWeight:700,color:S.text}}>{title}</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:S.text2,cursor:"pointer",padding:4,display:"flex"}}>{IC.x}</button>
        </div>
        <div style={{padding:22}}>{children}</div>
        {footer&&<div style={{padding:"14px 22px",borderTop:`1px solid ${S.border}`,display:"flex",justifyContent:"flex-end",gap:8}}>{footer}</div>}
      </div>
    </div>
  );
}
function FR({label,children}) { return <div style={{marginBottom:16}}><label style={{display:"block",fontSize:11,fontWeight:600,color:S.text3,marginBottom:5,textTransform:"uppercase",letterSpacing:"0.05em"}}>{label}</label>{children}</div>; }
// Card "section" wrapper used inside the agent editor. MUST live at
// module scope -- when it was defined inside AgModal, every keystroke
// re-rendered AgModal, gave Sec a new identity, and remounted the
// section subtree (killing textarea focus + resetting scroll).
function Sec({ title, children, border }) {
  return (
    <div style={{background:S.cardBg,border:`1px solid ${border||S.border2}`,borderRadius:12,overflow:"hidden",marginBottom:12}}>
      <div style={{padding:"10px 16px",borderBottom:`1px solid ${S.border}`,fontSize:11,fontWeight:700,color:S.text3,textTransform:"uppercase",letterSpacing:"0.08em"}}>{title}</div>
      <div style={{padding:16}}>{children}</div>
    </div>
  );
}
function CP({value,onChange}) { return <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>{COLORS.map(c=><button key={c} type="button" aria-label={`Use ${c} accent`} className="cf-color-swatch" onClick={()=>onChange(c)} style={{width:24,height:24,borderRadius:"50%",background:c,cursor:"pointer",border:`2px solid ${value===c?S.surface:S.border2}`,outline:value===c?`2px solid ${c}`:"none",boxShadow:value===c?`0 0 0 4px ${c}22`:"none",transform:value===c?"scale(1.06)":"scale(1)",padding:0}}/> )}</div>; }
function EP({value,onChange}) { return <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(34px,34px))",gap:6,alignItems:"center"}}>{EMOJIS.map(e=><button key={e} type="button" aria-label={`Use ${e} icon`} className="cf-emoji-tile" onClick={()=>onChange(e)} style={{width:34,height:34,borderRadius:8,border:`1px solid ${value===e?S.accent:S.border2}`,background:value===e?S.accentDim:S.inputBg,color:S.text,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,boxShadow:value===e?`0 0 0 2px ${S.accent}22`:"none",padding:0}}>{e}</button>)}</div>; }

// ── CDN library loader ──
const libCache = {};
// Slice 8: masked-summary chip on user messages. Renders a small green pill
// reading e.g. "🔒 Masked: 1 SSN · 2 emails" with a popover showing per-kind
// counts. The chip is only mounted when the redactionSummary metadata is
// present on the message (server-attached after the redacted SSE event).
function kindLabel(k, n) {
  const pairs = {
    SSN:   ["SSN", "SSNs"],
    EMAIL: ["email", "emails"],
    PHONE: ["phone", "phones"],
    CARD:  ["credit card", "credit cards"],
    DOB:   ["DOB", "DOBs"],
    MRN:   ["MRN", "MRNs"],
    IP:    ["IP address", "IP addresses"],
    NAME:  ["name", "names"],
  };
  const p = pairs[k] || [String(k || "").toLowerCase(), String(k || "").toLowerCase()];
  return (n === 1) ? p[0] : p[1];
}
function RedactionChip({ msg }) {
  const [open, setOpen] = useState(false);
  const summary = msg && msg.redactionSummary;
  if (!summary) return null;
  const entries = Object.entries(summary).filter(([, n]) => n > 0);
  if (!entries.length) return null;
  const summaryText = entries.map(([k, n]) => `${n} ${kindLabel(k, n)}`).join(" · ");
  const total = entries.reduce((s, [, n]) => s + n, 0);
  return (
    <div style={{ position: "relative", marginTop: 6, display: "inline-block" }}>
      <button
        onClick={() => setOpen(v => !v)}
        title={`${total} item${total === 1 ? "" : "s"} masked before sending to AI · click for details`}
        aria-expanded={open}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 20, fontSize: 10, fontFamily: "monospace", border: `1px solid ${S.success}40`, background: `${S.success}14`, color: S.success, cursor: "pointer", lineHeight: 1.4 }}
      >
        <span aria-hidden="true">🔒</span>
        Masked: {summaryText}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 29 }} />
          <div role="dialog" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 30, padding: "10px 12px", background: S.modalBg || S.cardBg, border: `1px solid ${S.border2}`, borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.18)", minWidth: 220, maxWidth: 280 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: S.text, marginBottom: 6 }}>Redacted before send</div>
            {entries.map(([k, n]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 11, color: S.text2, padding: "3px 0" }}>
                <span>{kindLabel(k, n)}</span>
                <span style={{ color: S.text3, fontFamily: "monospace" }}>×{n}</span>
              </div>
            ))}
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${S.border}`, fontSize: 10, color: S.text3, lineHeight: 1.5 }}>
              Originals were never sent to the model. Reload the chat to see the masked tokens in place of the originals.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Slice 4: heuristic capability check for image attachments. Used to gate
// the vision payload on send + to surface the "Current model can't see images"
// chip in the composer. Errs on the side of "yes, supports vision" only when
// the model name clearly maps to a known vision-capable family — anything
// else returns false and we strip image bytes before send rather than
// silently shipping them to a model that will discard them.
function modelSupportsVision(provider, model) {
  if (!model) return false;
  const m = String(model).toLowerCase();
  if (provider === "anthropic") {
    return /^claude-(3|sonnet-4|opus-4|haiku-4|opus-3)/.test(m) || m.startsWith("claude-3") || m.includes("claude-sonnet-4") || m.includes("claude-opus-4") || m.includes("claude-haiku-4");
  }
  if (provider === "openai") {
    return m.startsWith("gpt-4o") || m === "gpt-4-vision-preview" || m.startsWith("gpt-4.1") || m.startsWith("o1") && m.includes("vision");
  }
  if (provider === "openrouter") {
    return m.includes("claude-") || m.includes("gpt-4o") || m.includes("gemini") || m.includes("vision") || m.includes("/llava") || m.includes("pixtral");
  }
  if (provider === "google" || provider === "gemini") return m.includes("gemini");
  return false;
}

function loadScript(src) {
  if (libCache[src]) return libCache[src];
  libCache[src] = new Promise((res, rej) => {
    if (document.querySelector('script[src="' + src + '"]')) { res(); return; }
    const s = document.createElement("script");
    s.src = src; s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
  return libCache[src];
}

// Compliance & Security report exporter. Gathers the org's live
// posture once, then emits the requested format:
//   format: "pdf" | "docx" | "xlsx" | "md"
//   ctx:    { authToken, orgInfo, brandName, showToast }
// Reuses the same CDN libs the chat exporter loads (jsPDF / docx /
// SheetJS). Fully client-side -- no new server endpoint.
async function cfExportComplianceReport(format, ctx) {
  const showToast = (ctx && ctx.showToast) || (() => {});
  try {
    const hdr = { headers: (ctx && ctx.authToken) ? { "Authorization": "Bearer " + ctx.authToken } : {} };
    const comp = await fetch("/api/admin/compliance", hdr).then(r => r.ok ? r.json() : null).catch(() => null);
    const red = await fetch("/api/admin/redaction", hdr).then(r => r.ok ? r.json() : null).catch(() => null);
    const now = new Date();
    const orgInfo = (ctx && ctx.orgInfo) || null;
    const orgName = (orgInfo && (orgInfo.name || orgInfo.org_name)) || (ctx && ctx.brandName) || "ChatFort organization";
    const plan = (orgInfo && (orgInfo.plan_details && orgInfo.plan_details.name || orgInfo.plan)) || "—";
    const compMode = (comp && comp.mode) || "off";
    const compLine = compMode === "always"
      ? "Always — every chat is routed through a BAA-covered upstream (Azure OpenAI / AWS Bedrock)."
      : compMode === "auto"
        ? "Auto — requests detected to contain PHI/PII are forced through a BAA-covered upstream; blocked (fail-closed) if none is configured."
        : "Off — compliance routing is not enforced.";
    const redOn = red && red.enabled;
    const redClasses = (red && Array.isArray(red.classes) && red.classes.length) ? red.classes.join(", ") : "(none selected)";
    const redStore = red && red.persistMaskedInStorage;

    // Single structured model every format renders from.
    const sections = [
      { title: "1. Data Handling Posture", items: [
        ["Data retention", "Zero — chat content is not retained beyond the organization's own storage."],
        ["Model training", "Never — organization data is not used to train any model."],
        ["Hosting", "Private deployment — the organization controls where the app and data live."],
        ["Audit log", "Enabled — append-only record of administrative and access events."],
      ]},
      { title: "2. Compliance Routing (BAA)", items: [
        ["Mode", compMode],
        ["Behavior", compLine],
        ["Covered upstreams", "Azure OpenAI (BAA), AWS Bedrock (BAA)."],
      ]},
      { title: "3. PII / PHI Redaction", items: [
        ["Status", redOn ? "Active" : "Off"],
        ["Masked data classes", redClasses],
        ["Store masked version in chat history", redStore ? "Yes — only tokenised placeholders are persisted." : "No"],
        ["Pipeline", "Detected items are replaced with placeholders BEFORE any data reaches the model; the reply is unmasked client-side before display."],
      ]},
      { title: "4. Access Controls", items: [
        ["RBAC", "Role-based access (owner / admin / member)."],
        ["Model allowlists", "Per-organization and per-user."],
        ["SSO", "Single sign-on (OIDC) available."],
        ["Brute-force defense", "Login throttling (IP + email keyed) against credential stuffing."],
        ["Secrets at rest", "Connector secrets encrypted (AES-256-GCM) when ENCRYPTION_KEY is configured."],
      ]},
      { title: "5. Attestation", items: [
        ["Source", "Generated by ChatFort AI on behalf of the organization administrator from the LIVE configuration at the generation timestamp."],
        ["Canonical", "For the real-time posture, consult the live admin console."],
      ]},
    ];
    const fnameBase = "chatfort-compliance-report-" + now.toISOString().slice(0, 10);

    if (format === "md") {
      const L = [];
      L.push("# ChatFort AI — Compliance & Security Report");
      L.push("");
      L.push("Generated: " + now.toISOString() + " (" + now.toLocaleString() + ")");
      L.push("Organization: " + orgName);
      L.push("Plan: " + plan);
      L.push("");
      for (const sec of sections) {
        L.push("## " + sec.title);
        for (const [k, v] of sec.items) L.push("- **" + k + ":** " + v);
        L.push("");
      }
      const blob = new Blob([L.join("\n")], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = fnameBase + ".md"; a.click(); URL.revokeObjectURL(url);
      showToast("Compliance report downloaded (Markdown)");
      return;
    }

    if (format === "pdf") {
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const margin = 18, pageW = 210, maxW = pageW - margin * 2;
      let y = margin;
      const line = (txt, size, bold, color) => {
        doc.setFontSize(size); doc.setFont("helvetica", bold ? "bold" : "normal");
        if (color) doc.setTextColor(color[0], color[1], color[2]); else doc.setTextColor(30, 30, 30);
        for (const ln of doc.splitTextToSize(txt, maxW)) { if (y > 280) { doc.addPage(); y = margin; } doc.text(ln, margin, y); y += size * 0.52; }
      };
      line("ChatFort AI — Compliance & Security Report", 16, true); y += 2;
      doc.setDrawColor(91, 163, 201); doc.line(margin, y, pageW - margin, y); y += 6;
      line("Generated: " + now.toLocaleString(), 9, false, [110,110,110]);
      line("Organization: " + orgName, 9, false, [110,110,110]);
      line("Plan: " + plan, 9, false, [110,110,110]); y += 3;
      for (const sec of sections) {
        y += 2; line(sec.title, 12, true, [40,40,40]); y += 1;
        for (const [k, v] of sec.items) line("• " + k + ": " + v, 10, false);
      }
      doc.setFontSize(8); doc.setTextColor(150);
      doc.text("Generated by ChatFort · " + now.toLocaleDateString(), margin, 290);
      doc.save(fnameBase + ".pdf");
      showToast("Compliance report downloaded (PDF)");
      return;
    }

    if (format === "docx") {
      await loadScript("https://cdn.jsdelivr.net/npm/docx@8.2.2/build/index.umd.min.js");
      const { Document, Paragraph, TextRun, HeadingLevel, Packer } = window.docx;
      const children = [];
      children.push(new Paragraph({ text: "ChatFort AI — Compliance & Security Report", heading: HeadingLevel.HEADING_1 }));
      children.push(new Paragraph({ children: [new TextRun({ text: "Generated: " + now.toLocaleString(), italics: true, color: "777777" })] }));
      children.push(new Paragraph({ children: [new TextRun({ text: "Organization: " + orgName + "  ·  Plan: " + plan, italics: true, color: "777777" })] }));
      children.push(new Paragraph({ text: "" }));
      for (const sec of sections) {
        children.push(new Paragraph({ text: sec.title, heading: HeadingLevel.HEADING_2 }));
        for (const [k, v] of sec.items) {
          children.push(new Paragraph({ children: [new TextRun({ text: k + ": ", bold: true }), new TextRun({ text: v })], bullet: { level: 0 } }));
        }
        children.push(new Paragraph({ text: "" }));
      }
      children.push(new Paragraph({ children: [new TextRun({ text: "Generated by ChatFort · " + now.toLocaleDateString(), italics: true, color: "999999" })] }));
      const blob = await Packer.toBlob(new Document({ sections: [{ children }] }));
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = fnameBase + ".docx"; a.click(); URL.revokeObjectURL(url);
      showToast("Compliance report downloaded (Word)");
      return;
    }

    if (format === "xlsx") {
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js");
      const XLSX = window.XLSX;
      const rows = [["Section", "Item", "Value"]];
      rows.push(["Report", "Organization", orgName]);
      rows.push(["Report", "Plan", plan]);
      rows.push(["Report", "Generated", now.toISOString()]);
      for (const sec of sections) for (const [k, v] of sec.items) rows.push([sec.title, k, v]);
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = [0,1,2].map(ci => ({ wch: Math.min(80, Math.max(12, ...rows.map(r => String(r[ci] || "").length))) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Compliance");
      XLSX.writeFile(wb, fnameBase + ".xlsx");
      showToast("Compliance report downloaded (Excel)");
      return;
    }
  } catch (e) {
    showToast("Compliance export failed: " + e.message);
  }
}

// ── Message-row culler ──
// Wraps each chat message and replaces it with a height-preserving
// placeholder <div> once it scrolls more than ~2000px out of view. Brings
// long conversations back to snappy: DOM weight + React reconciliation cost
// drops proportional to the portion that's off-screen. The placeholder
// keeps the exact height we measured while visible, so scroll position
// never jumps. When the user scrolls back toward a culled row, it re-hydrates.
function MsgRow({ msg, children }) {
  const ref = useRef(null);
  const [culled, setCulled] = useState(false);
  const heightRef = useRef(0);
  useEffect(() => {
    if (!ref.current || typeof IntersectionObserver === "undefined") return;
    const el = ref.current;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        // Capture the real height while we can see it, so the placeholder
        // can stand in for this row later without changing layout.
        const h = entry.target.offsetHeight;
        if (h > 0) heightRef.current = h;
        setCulled(false);
      } else if (heightRef.current > 0) {
        setCulled(true);
      }
    }, { rootMargin: "2000px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  if (culled && heightRef.current > 0) {
    return <div ref={ref} data-msg-id={msg.id} style={{ height: heightRef.current }} />;
  }
  return <div ref={ref} data-msg-id={msg.id}>{children}</div>;
}

// Sidebar chat-list row — extracted out of the App render so each row can be
// memoized independently. The sidebar previously rendered inline inside a
// single mega-map expression, meaning any App re-render (theme toggle, model
// change, section change, streaming commit) reconciled all ~N rows and their
// inline event handlers. With this memo + stable callbacks, rows whose input
// is unchanged skip reconciliation entirely.
//
// Equality: default shallow works here because every prop is either a
// primitive (string/bool) or a stable reference (useCallback'd handlers,
// brandLogoUrl — itself memoized). Theme tokens are passed as primitive
// strings; they only change when the theme actually changes.
const ChatSideRow = memo(function ChatSideRow({
  chatId, agentId, title, active, isGenericAgent, agentEmoji, agentColor,
  brandLogoUrl, text, text2, accent, accentDim, border3, danger, hoverBg,
  xIcon, onPick, onDelete, index = 0
}) {
  // Stagger cap: animation-delay grows by 25ms per row up to 10 rows,
  // then flat. Without the cap, a 200-row list would take 5s to settle.
  const _delayMs = Math.min(index, 10) * 25;
  return (
    <div
      onClick={() => onPick(chatId, agentId)}
      className="cf-sidebar-row"
      data-active={active ? "true" : "false"}
      style={{padding:"6px 8px",borderRadius:8,cursor:"pointer",display:"flex",alignItems:"center",gap:7,color:active?text:text2,background:active?accentDim:"none",border:active?`1px solid ${border3}`:"1px solid transparent",marginBottom:1,transition:"all 0.12s",animationDelay:`${_delayMs}ms`}}
      onMouseEnter={e=>{if(!active)e.currentTarget.style.background=hoverBg;}}
      onMouseLeave={e=>{if(!active)e.currentTarget.style.background="none";}}
    >
      <div className="cf-sidebar-icon" style={{width:18,height:18,borderRadius:5,background:`${agentColor||accent}20`,fontSize:10,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
        {isGenericAgent ? <img src={brandLogoUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : (agentEmoji || "\uD83C\uDFF0")}
      </div>
      <span style={{flex:1,fontSize:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{title}</span>
      <button
        onClick={e=>{e.stopPropagation();onDelete(chatId);}}
        style={{opacity:0,background:"none",border:"none",color:danger,cursor:"pointer",padding:2,display:"flex"}}
        ref={el=>{if(el){const p=el.parentElement;p.onmouseenter=()=>el.style.opacity=1;p.onmouseleave=()=>el.style.opacity=0;}}}
      >{xIcon}</button>
    </div>
  );
});

// ── Chat search helpers ──
// Pure substring search across the in-memory chat list. /api/chats already
// hands back the full corpus (~1.4 MB at 235 chats), so client-side scan is
// instant — measured under 30 ms for the full 235-chat corpus on a cold call.
// Title hits sort first, then content hits; recency desc within each group.
function searchChats(allChats, q) {
  if (!q || !q.trim()) return null;
  const needle = q.trim().toLowerCase();
  const out = [];
  for (const chat of allChats) {
    const title = (chat.title || "").toLowerCase();
    const titleHit = title.includes(needle);
    let bestMsg = null;
    if (!titleHit) {
      for (const m of (chat.messages || [])) {
        const c = typeof m.content === "string" ? m.content.toLowerCase() : "";
        if (c.includes(needle)) { bestMsg = m; break; }
      }
    } else {
      bestMsg = (chat.messages || []).find(m => m.role === "user") || (chat.messages || [])[0] || null;
    }
    if (titleHit || bestMsg) {
      const msgs = chat.messages || [];
      const lastTime = msgs.length ? (msgs[msgs.length - 1].time || 0) : 0;
      out.push({
        chat,
        titleHit,
        snippet: makeSnippet(typeof bestMsg?.content === "string" ? bestMsg.content : "", needle),
        snippetMsgId: bestMsg?.id || null,
        ts: lastTime || chat.created || 0,
      });
    }
  }
  out.sort((a, b) => {
    if (a.titleHit !== b.titleHit) return a.titleHit ? -1 : 1;
    return b.ts - a.ts;
  });
  return out;
}

function makeSnippet(text, needle) {
  if (!text) return "";
  const lower = text.toLowerCase();
  const idx = lower.indexOf(needle);
  if (idx < 0) return text.length > 140 ? text.slice(0, 140) + " …" : text;
  const start = Math.max(0, idx - 50);
  const end = Math.min(text.length, idx + needle.length + 90);
  return (start > 0 ? "… " : "") + text.slice(start, end) + (end < text.length ? " …" : "");
}

// Renders `text` with case-insensitive matches of `needle` wrapped in <mark>.
// Inline so callers can drop it inside any text node — used for both the
// chat title and the message snippet on each search-result row.
function HighlightText({ text, needle, accent }) {
  if (!text) return null;
  if (!needle) return text;
  const ql = needle.toLowerCase();
  if (!ql) return text;
  const lower = text.toLowerCase();
  const out = [];
  let i = 0, k = 0;
  while (i < text.length) {
    const idx = lower.indexOf(ql, i);
    if (idx < 0) { out.push(text.slice(i)); break; }
    if (idx > i) out.push(text.slice(i, idx));
    out.push(
      <mark key={k++} style={{ background: (accent || "#3b82f6") + "44", color: "inherit", padding: "0 1px", borderRadius: 2 }}>
        {text.slice(idx, idx + needle.length)}
      </mark>
    );
    i = idx + needle.length;
  }
  return out;
}

// ── "Show your work" trace modal ──
// Opens when a user clicks the 🔍 button on an AI message. Shows the full
// observability chain for that response: model + upstream, system prompt
// (truncated), KB chunks retrieved with similarity scores, web-search
// queries + sources, token counts, latency. Everything the server collected
// in `trace` for this message. Admin-friendly and auditor-friendly.
// Cmd/Ctrl+K command palette. Pure presentational — every effectful action
// comes in via props so the component stays decoupled from App state.
// With a query: matching chats first, then matching actions. Without one:
// the top actions, then recent chats.
function CommandPalette({ open, onClose, chats, isAdmin, onNewChat, onGo, onPickChat, prompts, onPickPrompt }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  useEffect(() => {
    if (open) { setQ(""); setSel(0); setTimeout(() => inputRef.current?.focus(), 30); }
  }, [open]);
  if (!open) return null;
  const SECTIONS = [
    ["chat", "💬", "Go to Chat"], ["projects", "📁", "Go to Projects"],
    ["agents", "🤖", "Go to Agents"], ["workflows", "🔀", "Go to Workflows"],
    ["knowledge", "📚", "Go to Knowledge"], ["hermes", "🛠️", "Go to Hermes"],
    ["integrations", "🔌", "Go to Integrations"], ["analytics", "📊", "Go to Analytics"],
    ["settings", "⚙️", "Go to Settings"],
  ].concat(isAdmin ? [["billing", "💳", "Go to Billing"], ["bots", "🤖", "Go to Bots"]] : []);
  const ql = q.trim().toLowerCase();
  const actionItems = [{ id: "__new", icon: "✏️", label: "New chat", run: onNewChat }]
    .concat(SECTIONS.map(([id, icon, label]) => ({ id: "__go_" + id, icon, label, run: () => onGo(id) })))
    .filter(a => !ql || a.label.toLowerCase().includes(ql));
  const chatItems = (chats || [])
    .filter(c => c && c.title && (!ql || String(c.title).toLowerCase().includes(ql)))
    .slice(0, 8)
    .map(c => ({ id: c.id, icon: "💬", label: c.title, run: () => onPickChat(c.id) }));
  const promptItems = (prompts || [])
    .filter(p => p && p.title && (!ql || p.title.toLowerCase().includes(ql) || "prompt".includes(ql)))
    .slice(0, ql ? 6 : 3)
    .map(p => ({ id: "prompt_" + p.id, icon: "📄", label: "Prompt: " + p.title, run: () => onPickPrompt && onPickPrompt(p) }));
  const items = ql ? [...chatItems, ...promptItems, ...actionItems] : [...actionItems.slice(0, 4), ...promptItems, ...chatItems];
  const clampSel = Math.min(sel, Math.max(0, items.length - 1));
  const runItem = (it) => { if (!it) return; onClose(); it.run(); };
  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:600,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"flex-start",justifyContent:"center",paddingTop:"14vh"}}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:560,background:S.surface,border:`1px solid ${S.border2}`,borderRadius:14,boxShadow:"0 18px 50px rgba(15,23,42,0.30)",overflow:"hidden",margin:"0 16px"}}>
        <input ref={inputRef} value={q} placeholder="Search chats or type a command…"
          aria-label="Command palette"
          onChange={e=>{setQ(e.target.value);setSel(0);}}
          onKeyDown={e=>{
            if (e.key === "Escape") { e.preventDefault(); onClose(); }
            else if (e.key === "ArrowDown") { e.preventDefault(); setSel(s=>Math.min(s+1, items.length-1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setSel(s=>Math.max(s-1, 0)); }
            else if (e.key === "Enter") { e.preventDefault(); runItem(items[clampSel]); }
          }}
          style={{width:"100%",background:"transparent",border:"none",borderBottom:`1px solid ${S.border2}`,padding:"14px 16px",color:S.text,fontSize:14,outline:"none",fontFamily:"inherit",boxSizing:"border-box"}}/>
        <div style={{maxHeight:320,overflowY:"auto",padding:6}}>
          {items.length === 0 && <div style={{padding:"18px 12px",textAlign:"center",color:S.text3,fontSize:12}}>No matches</div>}
          {items.map((it, i) => (
            <div key={it.id} onClick={()=>runItem(it)} onMouseEnter={()=>setSel(i)}
              style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:8,cursor:"pointer",background:i===clampSel?S.accentDim:"transparent",color:i===clampSel?S.accent:S.text2,fontSize:13}}>
              <span style={{fontSize:14,flexShrink:0}}>{it.icon}</span>
              <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{it.label}</span>
            </div>
          ))}
        </div>
        <div style={{padding:"7px 14px",borderTop:`1px solid ${S.border}`,fontSize:10,color:S.text3,display:"flex",gap:14,fontFamily:"monospace"}}>
          <span>↑↓ navigate</span><span>↵ open</span><span>esc close</span>
        </div>
      </div>
    </div>
  );
}

function TraceModal({ msg, onClose }) {
  const t = msg?.trace || {};
  const s = msg?.search || null;
  // Never expose our routing plumbing here. If the provider is "openrouter",
  // re-derive the real model owner from the ID prefix (qwen/…, anthropic/…,
  // openai/…) and strip the prefix off the model name so the user sees just
  // "Qwen · qwen-2.5-72b-instruct", not "openrouter · qwen/qwen-2.5-72b-instruct".
  const displayProviderModel = (() => {
    const rawProv = t.requestedProvider || "";
    const rawModel = t.requestedModel || "";
    if (rawProv === "openrouter" && rawModel.includes("/")) {
      const [prefix, ...rest] = rawModel.split("/");
      const real = OR_REAL_PROVIDER[prefix];
      if (real) return { provider: real.label, model: rest.join("/") };
      return { provider: "Community", model: rest.join("/") };
    }
    // Canonicalize known providers to their nice name (anthropic → Anthropic).
    const pv = PROVIDERS[rawProv];
    return { provider: pv?.name || rawProv || "—", model: rawModel || "—" };
  })();
  const Row = ({ label, value, mono = false }) => (
    <div style={{ display: "flex", gap: 14, padding: "6px 0", borderBottom: `1px solid ${S.border}`, fontSize: 12 }}>
      <div style={{ width: 140, color: S.text3, flexShrink: 0, fontFamily: "monospace", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", paddingTop: 2 }}>{label}</div>
      <div style={{ flex: 1, color: S.text, fontFamily: mono ? "monospace" : "inherit", wordBreak: "break-word" }}>{value}</div>
    </div>
  );
  const Section = ({ title, children }) => (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: S.text2, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
  const responseWasTruncated = !!(t.truncated || (
    t.tokens?.completion != null &&
    t.maxTokens != null &&
    Number(t.tokens.completion) >= Number(t.maxTokens)
  ));
  return (
    <div onClick={onClose} className="cf-modal-backdrop" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(8px)" }}>
      <div onClick={e => e.stopPropagation()} className="cf-modal-panel" style={{ background: S.modalBg, border: `1px solid ${S.border2}`, borderRadius: 16, width: "min(720px, 92vw)", maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 60px rgba(0,0,0,0.3)", overflow: "hidden" }}>
        <div style={{ padding: "16px 22px", borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: S.text }}>🔍 Show your work</div>
            <div style={{ fontSize: 11, color: S.text3, marginTop: 2 }}>Every detail the server collected about this response</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: S.text2, cursor: "pointer", padding: 4, fontSize: 18 }}>×</button>
        </div>
        <div style={{ padding: "14px 22px", overflowY: "auto" }}>
          {/* Summary row — shows the model the user picked, not our internal
              routing. We deliberately don't surface OpenRouter fallback, Azure,
              Bedrock, or any other upstream plumbing here; buyers shouldn't
              care about our backend supply chain. */}
          <Row label="Provider"     value={displayProviderModel.provider} mono />
          <Row label="Model"        value={displayProviderModel.model} mono />
          <Row label="Temperature"  value={String(t.temperature ?? "—")} mono />
          <Row label="Max tokens"   value={String(t.maxTokens ?? "—")} mono />
          <Row label="Tokens used"  value={t.tokens ? `${(t.tokens.total||0).toLocaleString()}  ·  prompt ${(t.tokens.prompt||0).toLocaleString()} / completion ${(t.tokens.completion||0).toLocaleString()}` : "—"} mono />
          <Row label="Finish reason" value={t.finishReason || "—"} mono />
          {responseWasTruncated && <Row label="Output limit" value={`Response stopped at the configured token limit (${t.truncationReason || "limit reached"}). Use Continue to resume.`} />}
          <Row label="Latency"      value={t.latencyMs != null ? `${t.latencyMs} ms` : "—"} mono />
          <Row label="Messages in context" value={String(t.messageCount ?? "—")} mono />
          <Row label="System prompt" value={`${(t.systemPromptChars || 0).toLocaleString()} chars`} mono />

          {/* System prompt preview */}
          {t.systemPromptPreview && (
            <Section title="System prompt (first 500 chars)">
              <pre style={{ background: S.inputBg, border: `1px solid ${S.border2}`, borderRadius: 8, padding: "10px 12px", fontSize: 11, color: S.text2, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 180, overflowY: "auto", fontFamily: "monospace", margin: 0 }}>{t.systemPromptPreview}</pre>
            </Section>
          )}

          {/* Compliance routing — only present when mode != off */}
          {t.compliance && (
            <Section title="Compliance routing">
              <div style={{ background: S.inputBg, border: `1px solid ${(t.compliance.phiDetected || t.compliance.baaRequired) ? (S.warning || "#f59e0b") : S.border2}`, borderRadius: 8, padding: "10px 12px", fontSize: 11, color: S.text2 }}>
                <div>Mode: <strong style={{color:S.text}}>{t.compliance.mode}</strong>{t.compliance.source && <span style={{color:S.text3,marginLeft:8}}>(set at {t.compliance.source} level)</span>}</div>
                <div style={{ marginTop: 4 }}>
                  PHI detected: <strong style={{color: t.compliance.phiDetected ? (S.warning || "#f59e0b") : S.success}}>
                    {t.compliance.phiDetected ? "yes" : "no"}
                  </strong>
                  {t.compliance.patterns?.length > 0 && (
                    <span style={{ marginLeft: 8 }}>
                      {t.compliance.patterns.map(p => (
                        <span key={p} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: (S.warning || "#f59e0b") + "22", color: S.warning || "#f59e0b", border: `1px solid ${(S.warning || "#f59e0b")}40`, fontFamily: "monospace", marginRight: 4 }}>{p}</span>
                      ))}
                    </span>
                  )}
                </div>
                <div style={{ marginTop: 4 }}>BAA upstream required: <strong style={{color:S.text}}>{t.compliance.baaRequired ? "yes" : "no"}</strong></div>
              </div>
            </Section>
          )}

          {/* KB retrieval */}
          {t.kbHits?.length > 0 && (
            <Section title={`Knowledge base — ${t.kbHits.length} chunk${t.kbHits.length === 1 ? "" : "s"} retrieved`}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {t.kbHits.map(h => (
                  <div key={h.n} style={{ background: S.inputBg, border: `1px solid ${S.border2}`, borderRadius: 8, padding: "8px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: S.accent, fontFamily: "monospace" }}>[{h.n}]</span>
                      <span style={{ fontSize: 11, color: S.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.docName}</span>
                      <span style={{ fontSize: 10, color: S.text3, fontFamily: "monospace" }}>score {h.score}</span>
                    </div>
                    <div style={{ fontSize: 11, color: S.text2, lineHeight: 1.5 }}>{h.textPreview}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Web search */}
          {t.webSearch && (
            <Section title={`Web search — ${t.webSearchSources} unique sources`}>
              {t.webSearchQueries?.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  {t.webSearchQueries.map((q, i) => (
                    <span key={i} style={{ fontSize: 10, padding: "3px 9px", borderRadius: 14, background: S.accentDim, border: `1px solid ${S.accent}40`, color: S.accent, fontFamily: "monospace" }}>"{q}"</span>
                  ))}
                </div>
              )}
              {s?.sources?.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {s.sources.slice(0, 10).map((src, i) => (
                    <a key={i} href={src.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: S.accent, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {src.title || src.url}
                    </a>
                  ))}
                </div>
              )}
            </Section>
          )}
        </div>
        <div style={{ padding: "10px 22px", borderTop: `1px solid ${S.border}`, fontSize: 10, color: S.text3, fontFamily: "monospace" }}>
          Audit-grade trace · {msg.time ? new Date(msg.time).toLocaleString() : ""}
        </div>
      </div>
    </div>
  );
}

// ── Message action toolbar ──
// Proactive cap warning. Reads everything from orgInfo (/api/org/me):
// tokensUsedThisMonth, billing_variant, plan_details.monthlyTokens. Hidden
// for BYOK orgs (no cap applies) and capless plans. Dismissal is per
// month+band ("2026-06:80") so it re-appears when usage crosses the next
// band (80 -> 90 -> 100) or the month rolls over.
// First-time to-do list for young orgs (<21 days). Completion comes from
// real state — chats, agents in use, KB docs, seats — never a stored flag.
// One Go button lives on the FIRST open item and walks down as items
// complete; Skip (persisted in cf_onboarding_skipped) moves it on without
// doing the step. Hides when every step is done or skipped.
function GettingStartedCard({ chats, knowledgeBases, orgInfo, isAdmin, setSection, serverMode, authUser, brandName }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("cf_onboarding_dismissed") === "1"; } catch { return false; }
  });
  const [skippedIds, setSkippedIds] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem("cf_onboarding_skipped") || "[]"); return Array.isArray(v) ? v : []; } catch { return []; }
  });
  if (dismissed || !serverMode || !authUser) return null;
  const createdAt = orgInfo?.created_at ? new Date(orgInfo.created_at).getTime() : null;
  if (!createdAt || !(Date.now() - createdAt < 21 * 86_400_000)) return null;
  const steps = [
    { id: "first-message", icon: "💬", label: "Send your first message", done: (chats || []).some(c => (c.messages || []).some(m => m.role === "assistant" && !m.error && !m.loading && m.content)), go: null },
    { id: "agent", icon: "🤖", label: "Chat with a specialist agent", done: (chats || []).some(c => c.agentId && c.agentId !== "ag-default"), go: () => setSection("agents") },
    { id: "knowledge", icon: "📚", label: "Add company knowledge", done: (knowledgeBases || []).some(kb => Number(kb.docCount) > 0), go: () => setSection("knowledge") },
    ...(isAdmin ? [{ id: "invite", icon: "👥", label: "Invite a teammate (Users page)", done: Number(orgInfo?.seatsUsed) > 1, go: null }] : []),
  ];
  const doneCount = steps.filter(s => s.done).length;
  if (steps.every(s => s.done || skippedIds.includes(s.id))) return null;
  const activeIdx = steps.findIndex(s => !s.done && !skippedIds.includes(s.id));
  const skipStep = (id) => {
    setSkippedIds(prev => {
      const next = prev.includes(id) ? prev : [...prev, id];
      try { localStorage.setItem("cf_onboarding_skipped", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  return (
    <div className="cf-setup-card" style={{ width: "min(100%, 500px)", maxWidth: "calc(100vw - 32px)", margin: "clamp(42px, 7dvh, 82px) auto clamp(10px, 2dvh, 14px)", background: S.cardBg, border: `1px solid ${S.border2}`, borderRadius: 12, padding: "clamp(7px, 1.2dvh, 9px) clamp(8px, 2vw, 10px)", textAlign: "left" }}>
      <div className="cf-setup-head" style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, minWidth: 0 }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={S.accent} strokeWidth="2" style={{ flexShrink: 0 }}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
        <span style={{ fontSize: 11, fontWeight: 800, color: S.text }}>Setup checklist</span>
        <span className="cf-setup-brand" style={{ fontSize: 10, color: S.text3, fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>for {brandName || "ChatFortAI"}</span>
        <span style={{ fontSize: 10, fontFamily: "monospace", color: S.accent, fontWeight: 700, flexShrink: 0 }}>{doneCount}/{steps.length}</span>
        <button onClick={() => { setDismissed(true); try { localStorage.setItem("cf_onboarding_dismissed", "1"); } catch {} }}
          aria-label="Dismiss to-do list" title="Dismiss"
          style={{ marginLeft: "auto", background: "none", border: "none", color: S.text3, cursor: "pointer", fontSize: 13, padding: 2, lineHeight: 1, flexShrink: 0 }}>×</button>
      </div>
      <div className="cf-setup-list" style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {steps.map((s, i) => {
          const skip = !s.done && skippedIds.includes(s.id);
          const active = i === activeIdx;
          return (
            <div key={s.id} className={`cf-setup-step ${s.done ? "cf-setup-step-done" : ""} ${skip ? "cf-setup-step-skip" : ""} ${active ? "cf-setup-step-active" : ""}`} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10.5, color: s.done || skip ? S.text3 : S.text2, opacity: skip ? 0.55 : 1, padding: "1px 4px", margin: "0 -4px", borderRadius: 6, background: active ? S.accentDim : "transparent", border: `1px solid ${active ? S.accent + "35" : "transparent"}`, minWidth: 0 }}>
              <span style={{ width: 14, height: 14, borderRadius: "50%", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 800, background: s.done ? (S.success || "#10b981") + "20" : S.inputBg, color: s.done ? (S.success || "#10b981") : active ? S.accent : S.text3, border: `1px solid ${s.done ? (S.success || "#10b981") + "50" : active ? S.accent + "60" : S.border2}` }}>{s.done ? "✓" : skip ? "–" : i + 1}</span>
              <span style={{ fontSize: 11, flexShrink: 0 }}>{s.icon}</span>
              <span className="cf-setup-label" style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: s.done ? "line-through" : "none" }}>{s.label}{skip ? <em style={{ fontStyle: "normal", fontSize: 10, color: S.text3 }}> (skipped)</em> : null}</span>
              {active && s.go && (
                <button className="cf-setup-action" onClick={s.go} style={{ padding: "1px 7px", borderRadius: 7, background: S.accent, border: "none", color: "#fff", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>Go →</button>
              )}
              {active && (
                <button className="cf-setup-action" onClick={() => skipStep(s.id)} title="Skip this step"
                  style={{ padding: "1px 7px", borderRadius: 7, background: "transparent", border: `1px solid ${S.border2}`, color: S.text3, fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>Skip</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UsageCapBanner({ orgInfo, isAdmin, setSection }) {
  const [dismissedKey, setDismissedKey] = useState(() => {
    try { return localStorage.getItem("cf_usage_banner_dismissed") || ""; } catch { return ""; }
  });
  if (!orgInfo || (orgInfo.billing_variant || "managed") === "byok") return null;
  const cap = orgInfo.plan_details?.monthlyTokens;
  const used = Number(orgInfo.tokensUsedThisMonth) || 0;
  if (!cap || !(cap > 0)) return null;
  const pct = used / cap;
  if (pct < 0.8) return null;
  const band = pct >= 1 ? "100" : pct >= 0.9 ? "90" : "80";
  const key = new Date().toISOString().slice(0, 7) + ":" + band;
  if (dismissedKey === key) return null;
  const over = pct >= 1;
  const planName = orgInfo.plan_details?.name || orgInfo.plan || "current";
  const color = over ? (S.danger || "#ef4444") : (S.warning || "#f59e0b");
  return (
    <div role="status" style={{ width: "100%", maxWidth: 860, margin: "0 auto 10px", display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", borderRadius: 10, background: color + "12", border: `1px solid ${color}45`, fontSize: 12, color: S.text2, boxSizing: "border-box" }}>
      <span style={{ fontSize: 14, flexShrink: 0 }}>{over ? "⛔" : "⚠️"}</span>
      <span style={{ flex: 1, lineHeight: 1.5 }}>
        {over
          ? <>Your {planName} plan's monthly token allowance is used up — responses on metered models are paused until it resets.</>
          : <>You've used <b style={{ color }}>{Math.min(99, Math.floor(pct * 100))}%</b> of your {planName} plan's monthly token allowance.</>}
        {!isAdmin && " Ask your workspace admin about upgrading."}
      </span>
      {isAdmin && (
        <button onClick={() => setSection && setSection("billing")}
          style={{ padding: "5px 12px", borderRadius: 8, border: "none", background: color, color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
          Upgrade
        </button>
      )}
      <button onClick={() => { setDismissedKey(key); try { localStorage.setItem("cf_usage_banner_dismissed", key); } catch {} }}
        aria-label="Dismiss usage warning" title="Dismiss"
        style={{ background: "none", border: "none", color: S.text3, cursor: "pointer", fontSize: 14, padding: 2, lineHeight: 1, flexShrink: 0 }}>×</button>
    </div>
  );
}

function MsgActions({ msg, isAI, curChat, busy, send, showToast, chats, setChats, setChatId, setAgId, wsId, agId, newGid, setSection, onShowTrace }) {
  const [exporting, setExporting] = useState(null);
  // Share-to-project: the whole conversation becomes a markdown doc in the
  // chosen project (Docs tab). Token read from storage — MsgActions doesn't
  // receive authToken as a prop and threading it through both call sites
  // would churn more than it saves.
  const [shareOpen, setShareOpen] = useState(false);
  const [shareProjects, setShareProjects] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);
  const _shareToken = () => { try { return localStorage.getItem("cf_auth_token") || ""; } catch { return ""; } };
  const openSharePicker = async () => {
    const tok = _shareToken();
    if (!tok) { showToast("Sign in to share to a project"); return; }
    setShareOpen(o => !o);
    if (shareProjects !== null) return;
    try {
      const r = await fetch("/api/projects", { headers: { "Authorization": `Bearer ${tok}` } });
      const d = r.ok ? await r.json() : null;
      const list = Array.isArray(d) ? d : (Array.isArray(d?.projects) ? d.projects : []);
      setShareProjects(list);
    } catch { setShareProjects([]); }
  };
  const shareToProject = async (project) => {
    if (shareBusy) return;
    setShareBusy(true);
    try {
      const msgs = (curChat?.messages || []).filter(m => m && (m.role === "user" || m.role === "assistant") && typeof m.content === "string" && m.content.trim());
      const lines = msgs.map(m => `**${m.role === "user" ? "User" : "Assistant"}:**\n\n${m.content.trim()}`);
      const bodyMd = `_Shared from a ChatFort chat on ${new Date().toLocaleDateString()}._\n\n---\n\n${lines.join("\n\n---\n\n")}`;
      const r = await fetch(`/api/projects/${project.id}/docs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${_shareToken()}` },
        body: JSON.stringify({ title: (curChat?.title || "Shared chat").slice(0, 180), kind: "doc", body_markdown: bodyMd }),
      });
      const d = await r.json().catch(()=>({}));
      if (!r.ok) showToast(d.error || "Couldn't share to that project");
      else showToast(`Shared to ${project.name || "project"} ✓ — see its Docs tab`);
    } catch (e) { showToast("Couldn't share: " + e.message); }
    setShareBusy(false);
    setShareOpen(false);
  };
  const content = msg.content || "";
  const title = curChat?.title || "ChatFort Export";
  const generatedChatImages = extractMarkdownImages(content).filter(img => isGeneratedChatImageAsset(img.url));
  const imageAssetMode = isAI && generatedChatImages.length > 0;

  // Strip markdown to plain text
  function toPlain(md) {
    return md
      .replace(/```[\s\S]*?```/g, block => block.replace(/```\w*\n?/g, "").trim())
      .replace(/#{1,6}\s/g, "").replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/\*(.*?)\*/g, "$1").replace(/`(.*?)`/g, "$1")
      .replace(/^[-*]\s/gm, "• ").trim();
  }

  // PDF export via jsPDF
  async function exportPDF() {
    setExporting("pdf");
    try {
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const margin = 18, pageW = 210, maxW = pageW - margin * 2;
      const plain = toPlain(content);
      const lines = doc.splitTextToSize(plain, maxW);
      let y = margin;
      // Title
      doc.setFontSize(16); doc.setFont("helvetica", "bold");
      doc.text(title.slice(0, 80), margin, y); y += 10;
      // Divider
      doc.setDrawColor(91, 163, 201); doc.line(margin, y, pageW - margin, y); y += 6;
      // Body
      doc.setFontSize(10); doc.setFont("helvetica", "normal");
      for (const line of lines) {
        if (y > 280) { doc.addPage(); y = margin; }
        doc.text(line, margin, y); y += 5;
      }
      // Footer
      doc.setFontSize(8); doc.setTextColor(150);
      doc.text("Generated by ChatFort · " + new Date().toLocaleDateString(), margin, 290);
      doc.save(title.replace(/[^a-z0-9]/gi, "_").slice(0, 40) + ".pdf");
      showToast("PDF downloaded!");
    } catch(e) { showToast("PDF export failed: " + e.message); }
    setExporting(null);
  }

  // Word DOCX export via docx.js
  async function exportDOCX() {
    setExporting("docx");
    try {
      await loadScript("https://cdn.jsdelivr.net/npm/docx@8.2.2/build/index.umd.min.js");
      const { Document, Paragraph, TextRun, HeadingLevel, Packer, AlignmentType } = window.docx;
      const lines = content.split("\n");
      const children = [];
      children.push(new Paragraph({ text: title, heading: HeadingLevel.HEADING_1 }));
      children.push(new Paragraph({ text: "" }));
      for (const line of lines) {
        if (line.startsWith("# ")) children.push(new Paragraph({ text: line.slice(2), heading: HeadingLevel.HEADING_1 }));
        else if (line.startsWith("## ")) children.push(new Paragraph({ text: line.slice(3), heading: HeadingLevel.HEADING_2 }));
        else if (line.startsWith("### ")) children.push(new Paragraph({ text: line.slice(4), heading: HeadingLevel.HEADING_3 }));
        else if (line.startsWith("- ") || line.startsWith("* ")) children.push(new Paragraph({ text: "• " + line.slice(2), indent: { left: 360 } }));
        else if (line.match(/^\d+\. /)) children.push(new Paragraph({ text: line, indent: { left: 360 } }));
        else if (line.trim() === "") children.push(new Paragraph({ text: "" }));
        else {
          // Handle bold/italic inline
          const runs = [];
          const parts = line.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
          for (const part of parts) {
            if (part.startsWith("**") && part.endsWith("**")) runs.push(new TextRun({ text: part.slice(2, -2), bold: true }));
            else if (part.startsWith("*") && part.endsWith("*")) runs.push(new TextRun({ text: part.slice(1, -1), italics: true }));
            else runs.push(new TextRun({ text: part }));
          }
          children.push(new Paragraph({ children: runs }));
        }
      }
      children.push(new Paragraph({ text: "" }));
      children.push(new Paragraph({ text: "Generated by ChatFort · " + new Date().toLocaleDateString(), style: "Caption" }));
      const doc = new Document({ sections: [{ children }] });
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = title.replace(/[^a-z0-9]/gi, "_").slice(0, 40) + ".docx";
      a.click(); URL.revokeObjectURL(url);
      showToast("Word doc downloaded!");
    } catch(e) { showToast("DOCX export failed: " + e.message); }
    setExporting(null);
  }

  // Excel XLSX export via SheetJS
  async function exportXLSX() {
    setExporting("xlsx");
    try {
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js");
      const XLSX = window.XLSX;
      // Parse tables from markdown or split lines into rows
      const lines = toPlain(content).split("\n").filter(l => l.trim());
      // Detect markdown table
      const tableLines = lines.filter(l => l.includes("|"));
      let rows;
      if (tableLines.length > 1) {
        rows = tableLines
          .filter(l => !l.match(/^\|[-:| ]+\|$/))
          .map(l => l.split("|").map(c => c.trim()).filter(c => c !== ""));
      } else {
        // No table — export as two-column: line number, content
        rows = [["#", "Content"], ...lines.map((l, i) => [i + 1, l])];
      }
      const ws = XLSX.utils.aoa_to_sheet(rows);
      // Auto column widths
      ws["!cols"] = rows[0].map((_, ci) => ({ wch: Math.min(60, Math.max(10, ...rows.map(r => (r[ci] || "").toString().length))) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Response");
      XLSX.writeFile(wb, title.replace(/[^a-z0-9]/gi, "_").slice(0, 40) + ".xlsx");
      showToast("Excel file downloaded!");
    } catch(e) { showToast("XLSX export failed: " + e.message); }
    setExporting(null);
  }

  // PowerPoint PPTX export via PptxGenJS
  async function exportPPTX() {
    setExporting("pptx");
    try {
      await loadScript("https://cdn.jsdelivr.net/npm/pptxgenjs@3.12.0/dist/pptxgen.bundle.js");
      const PptxGenJS = window.PptxGenJS;
      const pptx = new PptxGenJS();
      pptx.layout = "LAYOUT_WIDE";
      pptx.author = "ChatFort";
      pptx.company = "ChatFort AI";
      // Title slide
      const titleSlide = pptx.addSlide();
      titleSlide.background = { color: "0c0f1a" };
      titleSlide.addText(title, { x: 0.5, y: 1.5, w: 12, h: 1.2, fontSize: 32, bold: true, color: "5ba3c9", align: "center" });
      titleSlide.addText("Generated by ChatFort · " + new Date().toLocaleDateString(), { x: 0.5, y: 4.5, w: 12, h: 0.5, fontSize: 14, color: "8ea6c4", align: "center" });
      titleSlide.addShape(pptx.ShapeType.line, { x: 1.5, y: 2.9, w: 10, h: 0, line: { color: "5ba3c9", width: 1 } });
      // Split content into slides by headers or chunks
      const lines = content.split("\n");
      let slides = [], cur = { heading: "", bullets: [] };
      for (const line of lines) {
        if (line.startsWith("# ") || line.startsWith("## ")) {
          if (cur.heading || cur.bullets.length) slides.push({ ...cur });
          cur = { heading: line.replace(/^#+\s/, ""), bullets: [] };
        } else if (line.startsWith("### ")) {
          cur.bullets.push({ text: line.slice(4), bold: true });
        } else if (line.startsWith("- ") || line.startsWith("* ")) {
          cur.bullets.push({ text: "• " + line.slice(2), bold: false });
        } else if (line.trim() && !line.match(/^[-=]{3,}$/)) {
          // Chunk long paragraphs as bullet points
          if (line.length > 60) cur.bullets.push({ text: line.trim(), bold: false });
        }
        // Auto-split every 6 bullets
        if (cur.bullets.length >= 6) { slides.push({ ...cur }); cur = { heading: cur.heading + " (cont.)", bullets: [] }; }
      }
      if (cur.heading || cur.bullets.length) slides.push(cur);
      // If no structure detected, chunk content into slides of ~500 chars
      if (slides.length === 0) {
        const plain = toPlain(content);
        const chunks = [];
        for (let i = 0; i < plain.length; i += 500) chunks.push(plain.slice(i, i + 500));
        slides = chunks.map((c, i) => ({ heading: title + (chunks.length > 1 ? " (" + (i+1) + "/" + chunks.length + ")" : ""), bullets: [{text: c, bold: false}] }));
      }
      for (const slide of slides.slice(0, 20)) {
        const s = pptx.addSlide();
        s.background = { color: "111827" };
        if (slide.heading) s.addText(slide.heading, { x: 0.5, y: 0.3, w: 12, h: 0.9, fontSize: 22, bold: true, color: "5ba3c9" });
        const bulletText = slide.bullets.map(b => ({ text: b.text, options: { bold: b.bold, color: b.bold ? "eef2f8" : "8ea6c4", fontSize: 14 } }));
        if (bulletText.length) s.addText(bulletText, { x: 0.5, y: 1.4, w: 12, h: 5, fontSize: 14, color: "8ea6c4", valign: "top", wrap: true });
        s.addText("ChatFort", { x: 0.5, y: 6.8, w: 12, h: 0.3, fontSize: 9, color: "4a6280", align: "right" });
      }
      await pptx.writeFile({ fileName: title.replace(/[^a-z0-9]/gi, "_").slice(0, 40) + ".pptx" });
      showToast("PowerPoint downloaded!");
    } catch(e) { showToast("PPTX export failed: " + e.message); }
    setExporting(null);
  }

  async function downloadGeneratedImages() {
    if (!generatedChatImages.length) return;
    setExporting("image");
    const baseName = slugify(title || "chatfort-image");
    try {
      const fetchImage = async (img, index) => {
        const parsed = new URL(img.url, window.location.origin);
        const r = await fetch(parsed.href, { credentials: "include" });
        if (!r.ok) throw new Error(`image ${index + 1} returned ${r.status}`);
        const blob = await r.blob();
        const ext = imageExtForDownload(r.headers.get("content-type"), parsed.pathname);
        return { blob, filename: `${baseName}${generatedChatImages.length > 1 ? `-${index + 1}` : ""}.${ext}` };
      };
      if (generatedChatImages.length === 1) {
        const file = await fetchImage(generatedChatImages[0], 0);
        downloadBlob(file.blob, file.filename);
        showToast("Image downloaded!");
      } else {
        const JSZip = await loadJSZip();
        const zip = new JSZip();
        for (let i = 0; i < generatedChatImages.length; i++) {
          const file = await fetchImage(generatedChatImages[i], i);
          zip.file(file.filename, file.blob);
        }
        const blob = await zip.generateAsync({ type: "blob" });
        downloadBlob(blob, `${baseName}-images.zip`);
        showToast("Images downloaded!");
      }
    } catch(e) {
      showToast("Image download failed: " + (e?.message || e));
    }
    setExporting(null);
  }

  // Branch chat from this message
  function branchChat() {
    const msgs = curChat?.messages || [];
    const msgIdx = msgs.findIndex(m => m.id === msg.id);
    if (msgIdx < 0) return;
    const branchMsgs = msgs.slice(0, msgIdx + 1);
    const newId = newGid();
    const branchTitle = "Branch: " + (curChat?.title || "Chat").slice(0, 30);
    const newChat = { id: newId, title: branchTitle, messages: branchMsgs, wsId, agentId: agId, created: Date.now(), branchedFrom: curChat?.id };
    setChats(p => [newChat, ...p]);
    setChatId(newId);
    setSection("chat");
    showToast("Branched chat created!");
  }

  const btnStyle = {
    padding: "3px 9px", borderRadius: 6, background: S.inputBg,
    border: "1px solid " + S.border2, color: S.text3, fontSize: 10,
    cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
    fontFamily: "inherit", transition: "border-color 0.15s, color 0.15s",
  };
  const expBtnStyle = (color) => ({
    ...btnStyle,
    borderColor: exporting ? S.border2 : S.border2,
    opacity: exporting ? 0.6 : 1,
  });
  const responseWasTruncated = !!(msg.trace?.truncated || (
    msg.trace?.tokens?.completion != null &&
    msg.trace?.maxTokens != null &&
    Number(msg.trace.tokens.completion) >= Number(msg.trace.maxTokens)
  ));

  const ExportBtn = ({ label, icon, onClick, color, busyKey }) => (
    <button onClick={onClick} disabled={!!exporting} title={label.startsWith("Download ") ? label : "Download as " + label}
      style={expBtnStyle(color)}
      onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.color = color; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = S.border2; e.currentTarget.style.color = S.text3; }}>
      {exporting === (busyKey || label.toLowerCase().replace(/\s/g,"")) ? (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{animation:"spin 1s linear infinite"}}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-.49-4.95"/></svg>
      ) : <span style={{fontSize:11}}>{icon}</span>}
      {label}
    </button>
  );

  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
      {/* Primary actions */}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        <CopyMorphButton text={content} style={btnStyle}
          onCopied={() => showToast("Copied!")}
          onMouseEnter={e => { e.currentTarget.style.borderColor = S.accent; e.currentTarget.style.color = S.accent; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = S.border2; e.currentTarget.style.color = S.text3; }}>
          {IC.copy} Copy
        </CopyMorphButton>
        {isAI && (() => {
          const sendFeedback = async (verdict) => {
            if (msg.feedback === verdict) return;
            setChats(p => p.map(c => c.id !== curChat?.id ? c : { ...c, messages: c.messages.map(m => m.id === msg.id ? { ...m, feedback: verdict } : m) }));
            try {
              const tok = _shareToken();
              if (tok) await fetch("/api/feedback", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${tok}` },
                body: JSON.stringify({ messageId: msg.id, chatId: curChat?.id || null, verdict, agentId: curChat?.agentId || null, model: msg.provider || null, preview: String(content).slice(0, 240) }),
              });
            } catch {}
            showToast(verdict === "up" ? "Thanks for the feedback!" : "Thanks — this helps us improve answers");
          };
          const fbStyle = (active, color) => ({ ...btnStyle, padding: "3px 7px", ...(active ? { borderColor: color, color } : {}) });
          return (
            <>
              <button onClick={() => sendFeedback("up")} title="Good answer" aria-label="Good answer" style={fbStyle(msg.feedback === "up", S.success || "#10b981")}
                onMouseEnter={e => { e.currentTarget.style.borderColor = S.success || "#10b981"; e.currentTarget.style.color = S.success || "#10b981"; }}
                onMouseLeave={e => { const a = msg.feedback === "up"; e.currentTarget.style.borderColor = a ? (S.success || "#10b981") : S.border2; e.currentTarget.style.color = a ? (S.success || "#10b981") : S.text3; }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
              </button>
              <button onClick={() => sendFeedback("down")} title="Bad answer" aria-label="Bad answer" style={fbStyle(msg.feedback === "down", S.danger)}
                onMouseEnter={e => { e.currentTarget.style.borderColor = S.danger; e.currentTarget.style.color = S.danger; }}
                onMouseLeave={e => { const a = msg.feedback === "down"; e.currentTarget.style.borderColor = a ? S.danger : S.border2; e.currentTarget.style.color = a ? S.danger : S.text3; }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg>
              </button>
            </>
          );
        })()}
        {isAI && <button onClick={() => { const m = curChat?.messages; if (!m || busy) return; const lu = [...m].reverse().find(x => x.role === "user"); if (lu) send(lu.content); }} style={btnStyle}
          onMouseEnter={e => { e.currentTarget.style.borderColor = S.accent; e.currentTarget.style.color = S.accent; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = S.border2; e.currentTarget.style.color = S.text3; }}>
          {IC.retry} Retry
        </button>}
        {isAI && responseWasTruncated && (
          <button
            onClick={() => {
              if (busy) return;
              send("Continue the previous response from exactly where it stopped. Do not repeat earlier code or prose. Continue until the file or script is complete, including all closing tags, braces, and setup needed to run it.");
            }}
            title="The last response hit the output limit. Continue from the cutoff."
            style={{...btnStyle,borderColor:S.warning || "#f59e0b",color:S.warning || "#f59e0b",fontWeight:700}}
            onMouseEnter={e => { e.currentTarget.style.borderColor = S.accent; e.currentTarget.style.color = S.accent; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = S.warning || "#f59e0b"; e.currentTarget.style.color = S.warning || "#f59e0b"; }}>
            Continue
          </button>
        )}
        <button onClick={branchChat} style={btnStyle}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "#8b5cf6"; e.currentTarget.style.color = "#8b5cf6"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = S.border2; e.currentTarget.style.color = S.text3; }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 0 1-9 9"/></svg>
          Branch chat
        </button>
        {isAI && (
          <span style={{ position: "relative", display: "inline-flex" }}>
            <button onClick={openSharePicker} style={btnStyle}
              title="File this conversation as a doc in a project so your team can read and comment"
              onMouseEnter={e => { e.currentTarget.style.borderColor = S.accent; e.currentTarget.style.color = S.accent; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = S.border2; e.currentTarget.style.color = S.text3; }}>
              📁 Share to project
            </button>
            {shareOpen && (
              <div style={{ position: "absolute", bottom: "calc(100% + 6px)", left: 0, zIndex: 50, minWidth: 220, maxHeight: 240, overflowY: "auto", background: S.surface, border: `1px solid ${S.border2}`, borderRadius: 10, boxShadow: "0 12px 36px rgba(15,23,42,0.25)", padding: 6 }}>
                {shareProjects === null ? (
                  <div style={{ padding: "10px 12px", fontSize: 11, color: S.text3 }}>Loading projects…</div>
                ) : shareProjects.length === 0 ? (
                  <div style={{ padding: "10px 12px", fontSize: 11, color: S.text3 }}>No projects yet — create one in the Projects tab first.</div>
                ) : shareProjects.map(p => (
                  <button key={p.id} onClick={() => shareToProject(p)} disabled={shareBusy}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 7, background: "transparent", border: "none", color: S.text2, fontSize: 12, cursor: "pointer", fontFamily: "inherit", opacity: shareBusy ? 0.6 : 1 }}
                    onMouseEnter={e => { e.currentTarget.style.background = S.accentDim; e.currentTarget.style.color = S.accent; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = S.text2; }}>
                    {p.name || p.id}
                  </button>
                ))}
              </div>
            )}
          </span>
        )}
        {isAI && msg.trace && onShowTrace && (
          <button onClick={onShowTrace} style={btnStyle}
            title="See the model, retrieval, tokens, and latency for this response"
            onMouseEnter={e => { e.currentTarget.style.borderColor = S.accent; e.currentTarget.style.color = S.accent; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = S.border2; e.currentTarget.style.color = S.text3; }}>
            🔍 Show your work
          </button>
        )}
      </div>
      {/* Export row */}
      {isAI && (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
          {imageAssetMode ? (
            <>
              <span style={{ fontSize: 9, color: S.text3, fontFamily: "monospace", letterSpacing: "0.05em", marginRight: 2 }}>IMAGE</span>
              <ExportBtn
                label={generatedChatImages.length > 1 ? "Download images" : "Download image"}
                icon="⬇"
                onClick={downloadGeneratedImages}
                color={S.accent || "#3b82c4"}
                busyKey="image"
              />
            </>
          ) : (
            <>
              <span style={{ fontSize: 9, color: S.text3, fontFamily: "monospace", letterSpacing: "0.05em", marginRight: 2 }}>EXPORT</span>
              <ExportBtn label="PDF" icon="📄" onClick={exportPDF} color="#e53e3e" />
              <ExportBtn label="Word" icon="📝" onClick={exportDOCX} color="#2b579a" />
              <ExportBtn label="Excel" icon="📊" onClick={exportXLSX} color="#217346" />
              <ExportBtn label="PowerPoint" icon="📐" onClick={exportPPTX} color="#d24726" />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MultiModelPicker({idx, multiModels, setMultiModels, setMultiPickerIdx, integrations, chipRefs, modelOverrides = {}, orgAllowedModels = null, liveModelCatalog = null, orgCustomModels = [], authUser = null}) {
  const [search, setSearch] = useState("");
  const ref = chipRefs.current[idx];
  const rect = ref ? ref.getBoundingClientRect() : null;
  const top = rect ? rect.bottom + 6 : 200;
  const left = rect ? Math.min(rect.left, window.innerWidth - 280) : 100;

  // Mirror the main dropdown's selectable-models computation so the
  // slot picker can see every model the user has access to (live OR
  // catalog + org custom models + per-user allowedModels filter), not
  // just the ~15 bundled PROVIDERS[*].models entries.
  const liveORModels = Array.isArray(liveModelCatalog?.models) ? liveModelCatalog.models : [];
  const catalogHasZdr = liveORModels.some(m => m?.zdr);
  const filterByZdr = liveModelCatalog?.zdrEnforced !== false && catalogHasZdr;
  const selectableOR = filterByZdr ? liveORModels.filter(m => m?.zdr?.verified) : liveORModels;
  const liveORById = new Map(selectableOR.map(m => [m.id, m]));
  const liveORAllowedIds = new Set(liveORById.keys());
  const catalogAccessFiltered = liveORModels.length > 0 && liveModelCatalog?.accessFiltered !== false;

  function passesUserFilter(modelId, isCustom) {
    if (modelOverrides?.[modelId]?.enabled === false) return false;
    if (!isCustom && Array.isArray(orgAllowedModels) && orgAllowedModels.length > 0 && !orgAllowedModels.includes(modelId)) return false;
    if (authUser && !(authUser.role === "admin" || authUser.role === "superadmin") && Array.isArray(authUser.allowedModels) && authUser.allowedModels.length > 0 && !authUser.allowedModels.includes(modelId)) return false;
    return true;
  }

  // Per-provider expanded model list. Mirrors the main dropdown:
  // for openrouter, merges pinned + live catalog + bundled, dedup by id.
  const expandedByProvider = {};
  Object.entries(PROVIDERS).filter(([pid, p]) => p.builtin || integrations[pid]?.key).forEach(([pid, p]) => {
    if (pid === "openrouter") {
      const pinned = integrations.openrouter?.pinned?.length ? integrations.openrouter.pinned.map(id => {
        const known = liveORById.get(id) || (p.models || []).find(mm => mm.id === id);
        return known ? { ...known, isPinned: true } : { id, name: id.split("/").pop(), desc: id.split("/")[0] + " via OR", isPinned: true };
      }).filter(m => !catalogAccessFiltered || liveORAllowedIds.has(m.id)) : [];
      const combined = [...pinned, ...selectableOR, ...(p.models || []).filter(m => !catalogAccessFiltered || liveORAllowedIds.has(m.id))];
      const seen = new Set();
      expandedByProvider[pid] = combined.filter(m => { if (!m?.id || seen.has(m.id)) return false; seen.add(m.id); return true; });
    } else {
      expandedByProvider[pid] = (p.models || []).filter(m => {
        if (!catalogAccessFiltered || pid !== "anthropic" || integrations.anthropic?.key || !integrations.openrouter?.key) return true;
        const routedId = OPENROUTER_FALLBACK_MODEL_MAP[m.id];
        return routedId ? liveORAllowedIds.has(routedId) : true;
      });
    }
  });

  const allModels = [
    ...Object.entries(expandedByProvider).flatMap(([pid, models]) => {
      const p = PROVIDERS[pid];
      return models.filter(m => passesUserFilter(m.id, false)).map(m => ({
        model: m.id, provider: pid,
        name: m.name || m.id.split("/").pop(),
        desc: m.desc || pid,
        color: p?.color, icon: p?.icon, provName: p?.name || pid,
        isCustom: false,
      }));
    }),
    ...(orgCustomModels || []).filter(m => passesUserFilter(m.id, true)).map(m => {
      const pv = PROVIDERS[m.provider] || {};
      return {
        model: m.id, provider: m.provider,
        name: m.name || m.id.split("/").pop(),
        desc: m.desc || (m.provider + " (custom)"),
        color: pv.color, icon: pv.icon, provName: pv.name || m.provider,
        isCustom: true,
      };
    }),
  ];

  const filtered = search
    ? allModels.filter(m =>
        m.name.toLowerCase().includes(search.toLowerCase()) ||
        m.model.toLowerCase().includes(search.toLowerCase()) ||
        (m.provName || "").toLowerCase().includes(search.toLowerCase())
      )
    : allModels;

  const cur = multiModels[idx];

  function select(m) {
    setMultiModels(prev => prev.map((mm, i) => i === idx ? {model: m.model, provider: m.provider} : mm));
    setMultiPickerIdx(null);
    setSearch("");
  }

  const S2 = THEMES[document.documentElement.getAttribute("data-theme") || "light"];
  // Use global S since this is inside the module scope. On phones
  // (<=768px viewport) the anchored position can push this off-screen
  // -- snap to a centered sheet near the top instead.
  const isPhone = typeof window !== "undefined" && window.innerWidth <= 768;
  const sheetStyle = isPhone
    ? {
        position: "fixed", zIndex: 500,
        top: "10dvh", left: "3vw", right: "3vw",
        background: S.modalBg, border: "1px solid " + S.border2,
        borderRadius: 14, maxHeight: "80dvh",
        boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
        display: "flex", flexDirection: "column", overflow: "hidden",
      }
    : {
        position: "fixed", zIndex: 500, top, left,
        background: S.modalBg, border: "1px solid " + S.border2,
        borderRadius: 14, width: 280, maxHeight: 380,
        boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        display: "flex", flexDirection: "column", overflow: "hidden",
      };
  return (
    <div style={sheetStyle}>
      {/* Header */}
      <div style={{ padding: "10px 12px 8px", borderBottom: "1px solid " + S.border, flexShrink: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: S.text2, marginBottom: 7 }}>
          Choose model for slot {idx + 1}
          {cur && <span style={{ marginLeft: 6, fontSize: 9, fontFamily: "monospace", color: S.accent }}>
            current: {cur.model.split("/").pop()}
          </span>}
        </div>
        <input
          autoFocus
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search models..."
          style={{
            width: "100%", background: S.surface2, border: "1px solid " + S.border2,
            borderRadius: 8, padding: "6px 10px", color: S.text, fontFamily: "inherit",
            fontSize: 12, outline: "none", boxSizing: "border-box",
          }}
        />
      </div>
      {/* Model list */}
      <div style={{ overflowY: "auto", flex: 1 }}>
        {Object.entries(PROVIDERS)
          .filter(([pid, p]) => p.builtin || integrations[pid]?.key)
          .map(([pid, p]) => {
            const pModels = (expandedByProvider[pid] || []).filter(m => {
              if (!passesUserFilter(m.id, false)) return false;
              return !search || (m.name || "").toLowerCase().includes(search.toLowerCase()) ||
                m.id.toLowerCase().includes(search.toLowerCase());
            });
            if (!pModels.length) return null;
            return (
              <div key={pid}>
                <div style={{ padding: "6px 12px 3px", fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: S.text3, fontFamily: "monospace", background: S.surface2, display: "flex", alignItems: "center", gap: 5 }}>
                  <span>{p.icon}</span> {p.name}
                </div>
                {pModels.map(m => {
                  const isCur = cur?.model === m.id && cur?.provider === pid;
                  const isUsed = multiModels.some((mm, i) => i !== idx && mm.model === m.id && mm.provider === pid);
                  return (
                    <button key={m.id} onClick={() => !isUsed && select({model: m.id, provider: pid, ...m})}
                      style={{
                        width: "100%", padding: "9px 14px", border: "none", textAlign: "left",
                        background: isCur ? S.accentDim : "none",
                        cursor: isUsed ? "not-allowed" : "pointer",
                        opacity: isUsed ? 0.4 : 1,
                        display: "flex", alignItems: "center", gap: 10,
                        borderBottom: "1px solid " + S.border,
                        transition: "background 0.1s",
                      }}
                      onMouseEnter={e => { if (!isUsed && !isCur) e.currentTarget.style.background = S.accentDim + "88"; }}
                      onMouseLeave={e => { if (!isCur) e.currentTarget.style.background = "none"; }}
                    >
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: p.color, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: isCur ? 700 : 500, color: isCur ? S.accent : S.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {m.name}
                        </div>
                        <div style={{ fontSize: 10, color: S.text3, fontFamily: "monospace" }}>{m.desc || m.id.split("/")[0]}</div>
                      </div>
                      {isCur && <span style={{ fontSize: 9, color: S.accent, fontFamily: "monospace", flexShrink: 0 }}>✓</span>}
                      {isUsed && <span style={{ fontSize: 9, color: S.text3, fontFamily: "monospace", flexShrink: 0 }}>in use</span>}
                    </button>
                  );
                })}
              </div>
            );
          })}
        {filtered.length === 0 && (
          <div style={{ padding: "20px", textAlign: "center", color: S.text3, fontSize: 12 }}>
            No models match "{search}"
          </div>
        )}
      </div>
      {/* Footer */}
      <div style={{ padding: "8px 12px", borderTop: "1px solid " + S.border, flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 10, color: S.text3, fontFamily: "monospace" }}>{filtered.length} models</span>
        <button onClick={() => { setMultiPickerIdx(null); setSearch(""); }} style={{ padding: "4px 10px", borderRadius: 6, background: S.inputBg, border: "1px solid " + S.border2, color: S.text3, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
      </div>
    </div>
  );
}

// ── DraftReviewCard ────────────────────────────────────────────────────────
// Rendered above an assistant bubble when the server emitted a tool_result
// with draft_id (create_draft_email tool). Send is intentionally disabled
// in slice 1 — the backend never sends; this card just lets the user
// inspect the draft and Discard if unwanted. Discard calls DELETE
// /api/drafts/:id which hard-deletes the row.
//
// Kept small, stateless, and theme-token-driven so it matches the rest of
// the chat surface without pulling in extra CSS. Parent owns the state
// (pendingDrafts map) — this component just renders + emits callbacks.
function DraftReviewCard({ draft, onDiscard, theme }) {
  if (!draft || !draft.preview) return null;
  const { to, subject, body_excerpt } = draft.preview;
  const t = theme || {};
  const label = { fontSize: 10, color: t.text3, fontFamily: "monospace", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 3 };
  const val = { fontSize: 13, color: t.text, marginBottom: 10, wordBreak: "break-word" };
  return (
    <div style={{
      background: t.cardBg || t.surface,
      border: `1px solid ${t.accent}40`,
      borderLeft: `3px solid ${t.accent}`,
      borderRadius: 12,
      padding: "14px 16px",
      marginBottom: 10,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: t.accent, fontFamily: "monospace", letterSpacing: "0.06em", textTransform: "uppercase" }}>
          ✉️ Draft email — review before sending
        </div>
        <div style={{ fontSize: 9, color: t.text3, fontFamily: "monospace" }}>#{draft.draftId}</div>
      </div>
      <div style={label}>To</div>
      <div style={val}>{to}</div>
      <div style={label}>Subject</div>
      <div style={val}>{subject}</div>
      <div style={label}>Body</div>
      <div style={{ ...val, whiteSpace: "pre-wrap", background: t.inputBg, border: `1px solid ${t.border2}`, borderRadius: 8, padding: "10px 12px", fontSize: 12, lineHeight: 1.6 }}>
        {body_excerpt || "(empty)"}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <button
          disabled
          title="Coming soon"
          style={{
            padding: "7px 16px", borderRadius: 8,
            background: t.accentDim, color: t.accent,
            border: `1px solid ${t.accent}55`,
            fontSize: 12, fontWeight: 700, fontFamily: "inherit",
            cursor: "not-allowed", opacity: 0.55,
          }}
        >
          Send (coming soon)
        </button>
        <button
          onClick={onDiscard}
          style={{
            padding: "7px 14px", borderRadius: 8,
            background: "transparent", color: t.text2,
            border: `1px solid ${t.border2}`,
            fontSize: 12, fontWeight: 600, fontFamily: "inherit",
            cursor: "pointer",
          }}
        >
          Discard
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const defaultRolePerms = () => ({
    admin:{models:[],agents:[],workspaces:[],tools:[],integrations:[],mcp:[],sections:[],adGroups:[]},
    member:{models:[],agents:[],workspaces:[],tools:[],integrations:[],mcp:[],sections:[],adGroups:[]},
    analyst:{models:[],agents:[],workspaces:[],tools:[],integrations:[],mcp:[],sections:[],adGroups:[]},
    viewer:{models:[],agents:[],workspaces:[],tools:[],integrations:[],mcp:[],sections:[],adGroups:[]},
  });
  const [theme,setTheme]=useState(()=>ld("cf_theme","light"));
  S=THEMES[theme]||THEMES.light;
  const [brandLogo,setBrandLogo]=useState(()=>localStorage.getItem("cf_brand_logo")||null);
  const [brandLogoDark,setBrandLogoDark]=useState(()=>localStorage.getItem("cf_brand_logo_dark")||null);
  const [brandName,setBrandName]=useState(()=>ld("cf_brand_name","ChatFortAI"));
  const [brandAccent,setBrandAccent]=useState(()=>ld("cf_brand_color",null));
  const [brandFont,setBrandFont]=useState(()=>BRAND_FONT_KEYS.has(ld("cf_brand_font","outfit")) ? ld("cf_brand_font","outfit") : "outfit");
  const brandLocalEditUntilRef = useRef(0);
  if(brandAccent) S=applyBrandColor(S,brandAccent);
  const brandFontStack = brandFontOption(brandFont).stack;
  const brandLogoActive = theme === "dark" && brandLogoDark ? brandLogoDark : (brandLogo || brandLogoDark || null);
  BN=brandName; BC=brandAccent; BF=brandFontStack;

  useEffect(() => {
    document.documentElement.style.setProperty("--cf-brand-font", brandFontStack);
  }, [brandFontStack]);

  // Blob-URL derived from brandLogo. The sidebar chat list inlines the brand
  // logo into ~180 rows as the agent fallback icon. If `brandLogo` is a raw
  // data-URL (a user-uploaded 1080x1080 PNG -> ~520KB), the DOM ends up with
  // ~96MB of duplicated attribute strings, and every React reconcile during
  // token streaming has to walk that tree. Converting once to a Blob object
  // URL means each row's src is a ~60-byte `blob:...` string; the pixels
  // live in one Blob, reused via the browser's resource cache.
  //
  // ALSO triggers a one-shot migration: if the stored logo is larger than
  // ~60KB we downscale it to 128x128 JPEG 0.85 in-place (and re-save) so
  // the payload shrinks even in localStorage and on the server.
  const brandLogoUrl = useMemo(() => brandLogoObjectUrl(brandLogo), [brandLogo]);
  const brandLogoDarkUrl = useMemo(() => brandLogoObjectUrl(brandLogoDark), [brandLogoDark]);
  const brandLogoActiveUrl = theme === "dark" && brandLogoDark ? brandLogoDarkUrl : (brandLogoUrl || brandLogoDarkUrl);
  BL=brandLogoActiveUrl || brandLogoActive;
  useEffect(() => {
    // revoke prior blob URL when brandLogo changes / on unmount
    return () => { if (brandLogoUrl && brandLogoUrl.startsWith("blob:")) URL.revokeObjectURL(brandLogoUrl); };
  }, [brandLogoUrl]);
  useEffect(() => {
    return () => { if (brandLogoDarkUrl && brandLogoDarkUrl.startsWith("blob:")) URL.revokeObjectURL(brandLogoDarkUrl); };
  }, [brandLogoDarkUrl]);
  useEffect(() => {
    const activeIcon = brandLogoActive || null;
    const fallbackIcon = `/api/icon.png?theme=${theme === "dark" ? "dark" : "light"}&v=chatfort-brand-20260606`;
    for (const link of Array.from(document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]'))) {
      link.href = activeIcon || fallbackIcon;
    }
  }, [brandLogoActive, theme]);
  useEffect(() => {
    // One-shot downscale migration for oversized brand logos. 60KB chosen
    // so a reasonable 256x256 JPEG slides through untouched.
    if (!brandLogo || !brandLogo.startsWith("data:")) return;
    if (brandLogo.length < 60 * 1024) return;
    const img = new Image();
    img.onload = () => {
      const side = Math.min(img.width, img.height);
      const sx = (img.width - side) / 2, sy = (img.height - side) / 2;
      const target = 128;
      const c = document.createElement("canvas");
      c.width = target; c.height = target;
      const ctx = c.getContext("2d");
      ctx.drawImage(img, sx, sy, side, side, 0, 0, target, target);
      const smaller = c.toDataURL("image/jpeg", 0.85);
      if (smaller && smaller.length < brandLogo.length) {
        setBrandLogo(smaller);
        try { localStorage.setItem("cf_brand_logo", smaller); } catch {}
      }
    };
    img.src = brandLogo;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Server-mode auth ──────────────────────────────────────────────────────
  const [serverMode,setServerMode]=useState(false);
  const [authToken,setAuthToken]=useState(()=>{
    // OIDC callback redirects us with the token in a URL hash (#sso_token=...).
    // Consume it once at page load, then clean the URL so it doesn't linger
    // in browser history / screenshots.
    try {
      if (typeof window !== "undefined" && window.location.hash.startsWith("#sso_token=")) {
        const t = decodeURIComponent(window.location.hash.slice("#sso_token=".length));
        if (t) {
          localStorage.setItem("cf_auth_token", t);
          history.replaceState(null, "", window.location.pathname + window.location.search);
          return t;
        }
      }
    } catch {}
    return localStorage.getItem("cf_auth_token") || null;
  });
  const [authUser,setAuthUser]=useState(null);
  const [loginForm,setLoginForm]=useState({email:"",password:"",err:"",loading:false});
  // Self-serve password reset. forgotView: null | "request" | "sent".
  // resetTok holds the ?reset=<jwt> from the email link until submitted.
  const [forgotView,setForgotView]=useState(null);
  const [forgotEmail,setForgotEmail]=useState("");
  const [resetTok,setResetTok]=useState(null);
  const [resetForm,setResetForm]=useState({p1:"",p2:"",err:"",loading:false});
  // Two-step login: holds the server's 5-minute MFA challenge token between
  // the password step and the code step.
  const [mfaChallenge,setMfaChallenge]=useState(null);
  const [mfaForm,setMfaForm]=useState({code:"",err:"",loading:false});
  // Settings 2FA enrollment flow — App-level so Settings-card remounts
  // (toasts, the 10-min org refresh) can't wipe an in-progress setup.
  const [mfaFlow,setMfaFlow]=useState({ stage: null, setup: null, code: "", err: "", recovery: [] });
  // Org-invite accept flow (?org_invite=<token> from the invite email).
  const [orgInviteTok,setOrgInviteTok]=useState(null);
  const [orgInviteInfo,setOrgInviteInfo]=useState(null);
  const [inviteForm,setInviteForm]=useState({name:"",p1:"",p2:"",err:"",loading:false});
  const [authMode,setAuthMode]=useState("login"); // "login" | "signup"
  const [signupForm,setSignupForm]=useState({orgName:"",name:"",email:"",password:"",err:"",loading:false});
  const [orgInfo,setOrgInfo]=useState(null); // { id, name, plan, seatsUsed, plan_details }
  const [paletteOpen,setPaletteOpen]=useState(false); // Cmd/Ctrl+K command palette
  const [orgPrompts,setOrgPrompts]=useState([]); // shared prompt library (palette + settings card)
  const [showPricing,setShowPricing]=useState(()=>typeof window!=="undefined" && window.location.pathname === "/pricing");
  SERVER_MODE=serverMode; AUTH_TOKEN=authToken;

  // ── PWA install prompt ────────────────────────────────────────────────────
  const [canInstall,setCanInstall]=useState(()=>!!window.deferredPrompt);
  const [installTip,setInstallTip]=useState(false);
  useEffect(()=>{
    const onAvail = () => setCanInstall(true);
    const onDone  = () => setCanInstall(false);
    window.addEventListener("pwa-installable", onAvail);
    window.addEventListener("pwa-installed",   onDone);
    return () => { window.removeEventListener("pwa-installable",onAvail); window.removeEventListener("pwa-installed",onDone); };
  },[]);
  async function installApp() {
    if (window.installPWA) await window.installPWA();
    setCanInstall(false);
  }
  const [previewContent,setPreviewContent]=useState(null); // {html,lang}
  setPreviewCb=setPreviewContent;
  const [chats,setChats]=useState(()=>ld("cf_chats",[]));
  const chatsRef = useRef(chats); chatsRef.current = chats;
  const voiceChatIdRef = useRef(null);
  const voiceCtxRef = useRef("");
  const voiceProjectsRef = useRef([]);
  const ttsInterruptRef = useRef(false);
  const ttsFinishRef = useRef(null);
  const voiceTaskNoticeRef = useRef(false);
  const voiceLaunchSectionRef = useRef("chat");
  // Live content of the message currently being streamed. Kept OUT of `chats`
  // so per-frame flushes don't trigger a JSON.stringify(chats) +
  // localStorage.setItem on every delta (that was the main source of stream
  // lag). Commit once into chats on finish, then clear this back to null.
  const [streamingDraft,setStreamingDraft]=useState(null); // { mid } | null — only set on stream start/end, never per chunk
  // Imperative streaming path: the pre-wrap <div> in the render registers
  // itself here, and each onChunk writes textContent directly. Zero React
  // renders between stream start and stream end — crucial for long outputs
  // where even memo'd re-renders compound into visible lag.
  const streamTargetRef = useRef(null);
  const streamTextRef = useRef("");
  // Stable ref callback — React will only invoke it on mount/unmount of the
  // streaming <div>, not on every App re-render. An inline `ref={el => ...}`
  // fires with null then el on EVERY parent render, wiping textContent and
  // forcing a full re-layout mid-stream (visible as streaming "lag").
  const attachStreamTarget = useCallback(el => {
    streamTargetRef.current = el;
    if (el) el.textContent = streamTextRef.current || "";
  }, []);
  // Pending email drafts from the create_draft_email tool — keyed by the
  // assistant message id that emitted them. Cleared by Discard (DELETE
  // /api/drafts/:id) or by the send-real-email flow (slice 2). Lives in
  // App state so a re-render after streaming ends doesn't drop the card.
  // Shape: { [mid]: { draftId, preview: { to, subject, body_excerpt } } }
  const [pendingDrafts, setPendingDrafts] = useState({});
  const dismissDraft = useCallback(async (mid, draftId) => {
    try {
      await fetch(`/api/drafts/${draftId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${authToken}` },
      });
    } catch {}
    setPendingDrafts(p => { const c = { ...p }; delete c[mid]; return c; });
  }, [authToken]);

  // Phased thinking-indicator state. Drives <ThinkingIndicator/> which
  // renders in the assistant slot of the in-flight turn until the first
  // content token arrives, then unmounts. Updated by:
  //   - onStatus (SSE status frames: searching / composing / using_tool /
  //     tool_result / upstream / tool_limit) — see send/sendVoice
  //   - onChunk (first content token only — flips firstTokenReceived
  //     once via setState short-circuit; subsequent chunks are no-ops)
  //   - send/sendVoice turn-start: reset to defaults
  // Replaces the previous imperative streamStatusRef + attachStreamStatus
  // pattern; no DOM ref, no document.head <style> injection at app mount.
  const [thinkingState, setThinkingState] = useState({
    phase: "thinking",
    toolDomain: null,
    firstTokenReceived: false,
  });
  // Reducer-style helper used by both send() and sendVoice(). Pure mapping
  // from SSE frame to next thinkingState — keeps the two call sites in
  // lockstep without copy-pasting the switch.
  const applyStatusFrame = useCallback((phase, data) => {
    setThinkingState(prev => {
      // Once content has begun, status frames don't matter — the indicator
      // is gone. Short-circuit so we don't trigger a re-render.
      if (prev.firstTokenReceived) return prev;
      let nextPhase = prev.phase;
      let nextDomain = prev.toolDomain;
      if (phase === "searching") nextPhase = "searching";
      else if (phase === "composing" || phase === "upstream") nextPhase = "composing";
      else if (phase === "using_tool") {
        const t = data && data.tool;
        if (t === "fetch_url") {
          nextPhase = "reading";
          // Pull a host from args_preview (the truncated JSON of the call
          // args). Best-effort — fall back to null if the URL got cut.
          let host = null;
          try {
            const ap = data && data.args_preview;
            if (typeof ap === "string") {
              const m = ap.match(/"url"\s*:\s*"([^"]+)"/);
              if (m) host = new URL(m[1]).hostname || null;
            }
          } catch { host = null; }
          nextDomain = host;
        } else if (t === "web_search" || t === "search_kb") {
          nextPhase = "searching";
          nextDomain = null;
        }
      }
      else if (phase === "tool_result") nextPhase = "tool_done";
      // tool_limit / unknown: keep prev phase. The next status (composing)
      // or first content token will move it forward.
      if (nextPhase === prev.phase && nextDomain === prev.toolDomain) return prev;
      return { ...prev, phase: nextPhase, toolDomain: nextDomain };
    });
  }, []);
  const markFirstToken = useCallback(() => {
    setThinkingState(prev => prev.firstTokenReceived ? prev : { ...prev, firstTokenReceived: true });
  }, []);
  const resetThinkingState = useCallback(() => {
    setThinkingState({ phase: "thinking", toolDomain: null, firstTokenReceived: false });
  }, []);
  const [workspaces,setWorkspaces]=useState(()=>ld("cf_ws",DW));
  const [chatFolders,setChatFolders]=useState(()=>ld("cf_chat_folders",DCF));
  const [chatFolderId,setChatFolderId]=useState(()=>ld("cf_chat_folder_id","folder-all"));
  const [agents,setAgents]=useState(()=>{
    // Loads from localStorage if present, else seeds the default library.
    // Also does the ag-default → DEFAULT_MODEL migration inline on initial
    // load so the very first render shows the newest model (no flicker).
    const loaded = ld("cf_agents", DA);
    if (!Array.isArray(loaded)) return DA;
    return loaded.map(a => (a?.id === "ag-default" && a?.provider === "anthropic" && a?.model === "claude-sonnet-4-20250514")
      ? { ...a, model: DEFAULT_MODEL }
      : a);
  });
  const [tools,setTools]=useState(()=>ld("cf_tools",[]));
  const [integrations,setIntegrations]=useState(()=>ld("cf_integ",{}));
  const [mcpServers,setMcpServers]=useState(()=>ld("cf_mcp",[]));
  const [cfg,setCfg]=useState(()=>ld("cf_cfg",{temperature:0.7,maxTokens:DEFAULT_CHAT_MAX_TOKENS,timestamps:true,topbarSize:"large"}));
  const [alog,setAlog]=useState(()=>ld("cf_analytics:local",[]));
  const [retentionPolicy,setRetentionPolicy]=useState(()=>ld("cf_retention",{years:7,months:0,days:0,autoDelete:true,customLabel:"7 years (default)"}));
  const [users,setUsers]=useState(()=>ld("cf_users",[{id:"u0",name:"Admin",color:"#5ba3c9",initials:"AD",role:"admin",email:"admin@chatfortai.ai"}]));
  const [invites,setInvites]=useState(()=>ld("cf_invites",[]));
  const [ssoConfig,setSsoConfig]=useState(()=>ld("cf_sso",{provider:"none",clientId:"",clientSecret:"",tenantId:"",domain:"",adUrl:"",enabled:false}));
  const [uid,setUid]=useState(()=>ld("cf_uid","u0"));
  const chatFolderArchiveScope = `${authUser?.orgId || "local"}:${authUser?.id || uid || "local"}`;
  const [archivedChatFolderIdsByScope,setArchivedChatFolderIdsByScope]=useState(()=>ld("cf_chat_folder_archived_ids_by_scope",{}));
  const archivedChatFolderIds=Array.isArray(archivedChatFolderIdsByScope?.[chatFolderArchiveScope])
    ? archivedChatFolderIdsByScope[chatFolderArchiveScope]
    : [];
  const archivedChatFolderIdSet=useMemo(()=>new Set(archivedChatFolderIds),[archivedChatFolderIds]);
  const setArchivedChatFolderIds=useCallback((updater)=>{
    setArchivedChatFolderIdsByScope(prev=>{
      const base=(prev&&typeof prev==="object"&&!Array.isArray(prev))?prev:{};
      const cur=Array.isArray(base[chatFolderArchiveScope])?base[chatFolderArchiveScope]:[];
      const raw=typeof updater==="function"?updater(cur):updater;
      const next=[...new Set((Array.isArray(raw)?raw:[]).map(id=>String(id)).filter(id=>id&&id!=="folder-all"&&id!=="folder-general"))];
      if(next.length===cur.length&&next.every((id,i)=>id===cur[i])) return base;
      return {...base,[chatFolderArchiveScope]:next};
    });
  },[chatFolderArchiveScope]);
  const isKnownActiveChatFolderId=useCallback((id)=>{
    if(!id||id==="folder-all"||archivedChatFolderIdSet.has(id)) return false;
    const folders=(Array.isArray(chatFolders)&&chatFolders.length)?chatFolders:DCF;
    return folders.some(f=>f?.id===id);
  },[chatFolders,archivedChatFolderIdSet]);
  const getNewChatFolderId=useCallback(()=>isKnownActiveChatFolderId(chatFolderId)?chatFolderId:"folder-general",[chatFolderId,isKnownActiveChatFolderId]);
  const [adminPin]=useState(()=>ld("cf_pin","1234"));
  // PIN gate removed — server-side JWT auth handles admin permissions now
  const [adminOk,setAdminOk]=useState(true);
  // Role-based resource permissions. `workspaces` remains in the stored shape
  // for backward compatibility, but this branch no longer exposes it in the UI.
  const [rolePerms,setRolePerms]=useState(()=>ld("cf_role_perms",defaultRolePerms()));
  const [section,setSection]=useState(() => {
    if (typeof window !== "undefined") {
      const h = window.location.hash || "";
      if (h === "#hermes" || h.startsWith("#hermes/")) return "hermes";
      if (h === "#cowork" || h.startsWith("#cowork/")) return "cowork";
      if (h === "#cli" || h.startsWith("#cli/") || h === "#terminal" || h.startsWith("#terminal/")) return "cli";
      if (h === "#projects" || h.startsWith("#projects/")) return "projects";
      if (h === "#guide") return "guide";
    }
    return "chat";
  });
  // Reflect active section in document.title so multitabbed users
  // can find the right ChatFort tab faster.
  useEffect(() => {
    const cap = section ? section.charAt(0).toUpperCase() + section.slice(1) : "";
    try { document.title = (cap ? cap + " · " : "") + (BN || "ChatFort"); } catch {}
  }, [section]);
  const [isMobile,setIsMobile]=useState(()=>typeof window!=="undefined"&&window.innerWidth<=768);
  const [navOpen,setNavOpen]=useState(false); // mobile nav drawer
  const [sidebar,setSidebar]=useState(()=>typeof window!=="undefined"?window.innerWidth>768:true);
  const RAIL_WIDTH_MIN = 180;
  const RAIL_WIDTH_DEFAULT = 200;
  const RAIL_WIDTH_MAX = 320;
  const RAIL_COLLAPSED_WIDTH = 56;
  const CHAT_FOLDER_COLLAPSED_LIMIT = 5;
  const clampRailWidth = (value) => Math.min(RAIL_WIDTH_MAX, Math.max(RAIL_WIDTH_MIN, Number(value) || RAIL_WIDTH_DEFAULT));
  // Rail preferences are persisted across sessions so users do not have to
  // re-tune the navigation width or collapsed state on every load.
  const [railCollapsed,setRailCollapsed]=useState(()=>ld("cf_rail_collapsed", false));
  const [railWidth,setRailWidth]=useState(()=>clampRailWidth(ld("cf_rail_width", RAIL_WIDTH_DEFAULT)));
  const [railResizing,setRailResizing]=useState(false);
  const [railChatOpen,setRailChatOpen]=useState(()=>ld("cf_rail_chat_open", true));
  const [railFoldersExpanded,setRailFoldersExpanded]=useState(()=>ld("cf_rail_folders_expanded", false));
  const [railArchivedFoldersOpen,setRailArchivedFoldersOpen]=useState(()=>ld("cf_rail_archived_folders_open", false));
  useEffect(()=>{sv("cf_rail_collapsed", railCollapsed);},[railCollapsed]);
  useEffect(()=>{sv("cf_rail_width", railWidth);},[railWidth]);
  useEffect(()=>{sv("cf_rail_chat_open", railChatOpen);},[railChatOpen]);
  useEffect(()=>{sv("cf_rail_folders_expanded", railFoldersExpanded);},[railFoldersExpanded]);
  useEffect(()=>{sv("cf_rail_archived_folders_open", railArchivedFoldersOpen);},[railArchivedFoldersOpen]);
  useEffect(()=>{
    if(!railResizing) return undefined;
    const priorCursor = document.body.style.cursor;
    const priorUserSelect = document.body.style.userSelect;
    const onMove = (e)=>setRailWidth(clampRailWidth(e.clientX));
    const onUp = ()=>setRailResizing(false);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return ()=>{
      document.body.style.cursor = priorCursor;
      document.body.style.userSelect = priorUserSelect;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  },[railResizing]);
  useEffect(()=>{
    const onResize=()=>{
      const mob=window.innerWidth<=768;
      setIsMobile(mob);
      if(mob){ setSidebar(false); setNavOpen(false); }
    };
    window.addEventListener("resize",onResize);
    return ()=>window.removeEventListener("resize",onResize);
  },[]);
  const [chatId,setChatId]=useState(null);
  // Chat-search state (Slice 3). debounced is what searchChats actually runs
  // against; the raw query drives the input value. focusIdx is the keyboard
  // cursor, showAll un-caps the 50-result limit.
  const [chatSearch,setChatSearch]=useState("");
  const [chatSearchDebounced,setChatSearchDebounced]=useState("");
  const [chatSearchFocusIdx,setChatSearchFocusIdx]=useState(0);
  const [chatSearchShowAll,setChatSearchShowAll]=useState(false);
  const chatSearchInputRef=useRef(null);
  // After clicking a search result, the target message id is parked here so
  // the open-chat effect (below) can scroll it into view + flash once the
  // chat's messages have rendered. Bumped via scrollNonce so re-clicking the
  // same chat still triggers the scroll.
  const pendingScrollRef=useRef(null);
  const [scrollNonce,setScrollNonce]=useState(0);
  const [wsId,setWsId]=useState(DEFAULT_CHAT_WS_ID);
  const [agId,setAgId]=useState("ag-default");
  const [selModel,setSelModel]=useState(null); // {model, provider} override — null = use agent default
  const [multiModels,setMultiModels]=useState([]); // [{model,provider}] for side-by-side comparison
  const [multiMode,setMultiMode]=useState(false); // comparison mode on/off
  const [multiPickerIdx,setMultiPickerIdx]=useState(null); // which slot's picker is open
  const [busy,setBusy]=useState(false);
  const [toast,setToast]=useState("");
  // Polish: ESC dismisses the active toast (when present + no modal
  // open higher up the stack). The modal-stack approach we added in
  // PR #190 means modals consume ESC first, so we only see this when
  // no modal is open -- exactly the right precedence.
  useEffect(() => {
    if (!toast) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      // If a modal is on the stack, let it handle ESC first.
      if (typeof _modalStack !== "undefined" && _modalStack.length > 0) return;
      setToast("");
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [toast]);
  const [traceModalMsg, setTraceModalMsg] = useState(null); // message object when Show-your-work is open
  const [baaModels, setBaaModels] = useState([]); // model IDs the server can route through a BAA upstream
  const [baaUpstreams, setBaaUpstreams] = useState({ azure: false, bedrock: false });
  // Per-org model config from /api/org/models — allowed-list (null = unrestricted)
  // and custom models the superadmin added for this specific tenant.
  const [orgAllowedModels, setOrgAllowedModels] = useState(null);
  const [orgCustomModels, setOrgCustomModels] = useState([]);
  const [liveModelCatalog, setLiveModelCatalog] = useState({ models: [], count: 0, source: "openrouter" });
  const [liveModelCatalogError, setLiveModelCatalogError] = useState("");
  const [input,setInput]=useState("");
  const [mdOpen,setMdOpen]=useState(false);
  const [mdQuery,setMdQuery]=useState("");
  const [wsModal,setWsModal]=useState(null);
  const [chatFolderModal,setChatFolderModal]=useState(null);
  const [agModal,setAgModal]=useState(null);
  const [libraryOpen,setLibraryOpen]=useState(false);
  // Slice 9: default OFF. Web search adds 1–4s of upstream latency per
  // turn (multi-query fan-out + content fetch) and many turns don't need
  // it. The user's last toggle persists under cf_websearch_default so a
  // user who turned it on stays on across reloads / new chats.
  const [webSearch,setWebSearch]=useState(()=>ld("cf_websearch_default", false));
  const webSearchRef = useRef(webSearch); webSearchRef.current = webSearch;
  useEffect(()=>{sv("cf_websearch_default", webSearch);},[webSearch]);
  // Model overrides — shared across all users, owner-editable
  const [modelOverrides,setModelOverrides]=useState({});
  // Core Memory — per-user persistent facts
  const [memory,setMemory]=useState({enabled:false, entries:[]});
  // Knowledge Bases (RAG) — shared across users, available to all signed-in users
  const [knowledgeBases,setKnowledgeBases]=useState([]);
  const [adminKnowledgeKbId,setAdminKnowledgeKbId]=useState(null);
  const [selectedKbId,setSelectedKbId]=useState(()=>ld("cf_selected_kb",null));
  useEffect(()=>{sv("cf_selected_kb",selectedKbId);},[selectedKbId]);

  // ── "What's New" top-bar panel ───────────────────────────────────────────
  // Reads /CHANGELOG.md on mount to decide whether to show the red-dot
  // indicator on the megaphone icon. Fetch errors are silent — no dot
  // shown and the user can still click through to the panel, which will
  // surface a friendly "couldn't load changelog" message.
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [whatsNewUnread, setWhatsNewUnread] = useState(false);
  const refreshWhatsNewUnread = useCallback(async () => {
    try {
      const r = await fetch("/CHANGELOG.md", { credentials: "omit", cache: "no-store" });
      if (!r.ok) return;
      const txt = await r.text();
      setWhatsNewUnread(patchNotesHasUnread(txt));
    } catch { /* network offline etc — leave dot hidden */ }
  }, []);
  useEffect(() => { refreshWhatsNewUnread(); }, [refreshWhatsNewUnread]);

  // Marketing version shown in the chat empty-state. Derived from the
  // server's APP_VERSION (1.0.xxx) so it tracks deploys without manual
  // bumps. Anchor: SW patch 300 -> v0.8; +0.1 per 50 SW patches. Fails
  // open to "v0.8" so an offline or pre-deploy app still renders cleanly.
  const [marketingVersion, setMarketingVersion] = useState("v0.8");
  useEffect(() => {
    let cancelled = false;
    fetch("/api/version", { cache: "no-store", credentials: "omit" })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (cancelled || !d || typeof d.version !== "string") return;
        const m = d.version.match(/^\d+\.\d+\.(\d+)/);
        if (!m) return;
        const patch = parseInt(m[1], 10);
        if (!Number.isFinite(patch)) return;
        // tenths since anchor: 8 at patch=300, +1 every 50 patches.
        const tenths = 8 + Math.floor((patch - 300) / 50);
        const safeTenths = Math.max(0, tenths);
        const major = Math.floor(safeTenths / 10);
        const minor = safeTenths % 10;
        setMarketingVersion("v" + major + "." + minor);
      })
      .catch(() => { /* offline / older deploy -- keep default */ });
    return () => { cancelled = true; };
  }, []);

  // Auto-open the What's-New panel ONCE per new release for returning
  // users only. Brand-new users (no lastSeenVersion in localStorage) see
  // neither the auto-open nor the dot — we silently mark the current
  // latest as seen so they're not greeted by a panel for features they've
  // never encountered anything else of.
  //
  // sessionStorage gates per-tab idempotence: on any soft re-mount within
  // the same tab (route change, HMR, etc.) we do nothing. Flag is set
  // BEFORE the timer fires so a second tab opened seconds later sees
  // either lastSeenVersion already updated (→ up-to-date) or the session
  // flag (→ skip), never both racing into a double-open.
  //
  // Errors swallowed: a missing/404 changelog, network blip, JSON parse
  // failure — all classify as "no-changelog" or fall into the catch and
  // we just skip the auto-open. Never throws, never logs to prod console.
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    (async () => {
      try {
        if (sessionStorage.getItem("chatfort.patchNotes.autoOpenedThisSession") === "1") {
          // console.debug("[patchNotes] auto-open skipped: already opened this session");
          return;
        }
        const r = await fetch("/CHANGELOG.md", { cache: "no-store", credentials: "omit" });
        if (cancelled || !r.ok) return;
        const md = await r.text();
        if (cancelled) return;
        const state = patchNotesClassify(md);
        if (state === "fresh-install") {
          // First-ever visit: silently absorb the current latest so the
          // dot doesn't display and we don't auto-open. They'll see the
          // panel only when a NEW release lands AFTER this point.
          patchNotesMarkSeen(md);
          // console.debug("[patchNotes] auto-open skipped: fresh-install");
          return;
        }
        if (state !== "new-release") {
          // up-to-date or no-changelog: do nothing.
          return;
        }
        // Mark the session BEFORE arming the timer. If the user closes
        // the tab, opens a new tab, or even if React unmounts us mid-
        // timer, no second auto-open can sneak through this tab's
        // sessionStorage — and other tabs see lastSeenVersion update on
        // close, classifying as up-to-date.
        try { sessionStorage.setItem("chatfort.patchNotes.autoOpenedThisSession", "1"); } catch {}
        timer = setTimeout(() => {
          if (cancelled) return;
          // Functional setter: if the user already clicked the icon
          // manually before our 1500 ms window elapsed, the panel is
          // already open and React will short-circuit (true === true,
          // no re-render). No flicker, no double-open. The pending
          // "manual close → markSeen → dot clears" flow continues to
          // work because the panel's existing onClose path is unchanged.
          setWhatsNewOpen(prev => prev ? prev : true);
        }, 1500);
      } catch {
        // Swallow. Never throw; never log to prod.
      }
    })();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeWhatsNew = useCallback(() => {
    setWhatsNewOpen(false);
    // After the panel marks the latest version as seen, re-check so the
    // dot clears. Short delay to let the panel's markSeen useEffect run.
    setTimeout(refreshWhatsNewUnread, 30);
  }, [refreshWhatsNewUnread]);

  // Stable handlers for the memoized sidebar chat-list row (ChatSideRow).
  // These have to be useCallback'd or the memo would bust on every render —
  // arrow functions inside an App render get a new identity each time.
  const onPickChat = useCallback((id, aid, opts) => {
    setChatId(id);
    setAgId(aid || "ag-default");
    setSection("chat");
    if (isMobile) { setSidebar(false); setNavOpen(false); }
    // Optional: jump straight to a specific message (used by chat search to
    // land the user on the line that matched). Effect below handles the
    // actual scroll + flash once the chat's DOM has settled.
    if (opts && opts.scrollToMessageId) {
      pendingScrollRef.current = { chatId: id, msgId: opts.scrollToMessageId };
      setScrollNonce(n => n + 1);
    }
  }, [isMobile]);

  // Debounce raw search input → 80 ms is the snap-feel-but-no-thrash window
  // called out in the slice spec. Resets the focused-result cursor + the
  // "show more" toggle on every committed query so navigation stays sensible.
  useEffect(() => {
    const t = setTimeout(() => setChatSearchDebounced(chatSearch), 80);
    return () => clearTimeout(t);
  }, [chatSearch]);
  useEffect(() => { setChatSearchFocusIdx(0); setChatSearchShowAll(false); }, [chatSearchDebounced]);

  // Scroll-and-flash: when a search result is opened, after the chat swap
  // we wait two rAFs for messages to render, find [data-msg-id="…"], scroll
  // it to center, and add a 1 s pulse class. Re-runs on scrollNonce so
  // re-clicking inside the same chat still works.
  useEffect(() => {
    const target = pendingScrollRef.current;
    if (!target || target.chatId !== chatId) return;
    let raf1 = 0, raf2 = 0, timer = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const sel = (typeof CSS !== "undefined" && CSS.escape) ? CSS.escape(target.msgId) : target.msgId;
        const el = document.querySelector(`[data-msg-id="${sel}"]`);
        if (el) {
          try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { el.scrollIntoView(); }
          el.classList.add("searchHighlightFlash");
          timer = setTimeout(() => el.classList.remove("searchHighlightFlash"), 1100);
        }
        pendingScrollRef.current = null;
      });
    });
    return () => { if (raf1) cancelAnimationFrame(raf1); if (raf2) cancelAnimationFrame(raf2); if (timer) clearTimeout(timer); };
  }, [chatId, scrollNonce]);

  // Global "/" hotkey to focus the chat search box from anywhere in the app
  // — but not when the user is already typing in another input/textarea/
  // contenteditable. Mirrors the convention used by GitHub, Slack, Linear.
  useEffect(() => {
    function onKey(e) {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const ae = document.activeElement;
      const tag = ae && ae.tagName;
      const isEditable = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (ae && ae.isContentEditable);
      if (isEditable) return;
      const el = chatSearchInputRef.current;
      if (!el) return;
      e.preventDefault();
      el.focus();
      el.select?.();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // Cmd/Ctrl+K — global command palette. Unlike "/", this fires even while
  // typing in an input (the universal convention for this chord). Esc and
  // backdrop close come from the palette itself.
  useEffect(() => {
    function onPaletteKey(e) {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen(p => !p);
      }
    }
    window.addEventListener("keydown", onPaletteKey);
    return () => window.removeEventListener("keydown", onPaletteKey);
  }, []);
  const onDeleteChat = useCallback((id) => {
    setChats(p => p.filter(x => x.id !== id));
    setChatId(cur => cur === id ? null : cur);
  }, []);

  // Online/offline tracking + auto-resend of a message typed while offline.
  useEffect(() => {
    function goOffline(){ setIsOffline(true); }
    function goOnline(){
      setIsOffline(false);
      const t = _offlinePendingRef.current;
      if (t) {
        _offlinePendingRef.current = null;
        showToast("Back online — sending your message");
        // Small delay so the network stack settles before the fetch.
        setTimeout(() => { try { sendRef.current && sendRef.current(t); } catch {} }, 400);
      }
    }
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    const syncOnlineState = () => setIsOffline(typeof navigator !== "undefined" && navigator.onLine === false);
    syncOnlineState();
    const t1 = setTimeout(syncOnlineState, 250);
    const t2 = setTimeout(syncOnlineState, 1500);
    return () => {
      clearTimeout(t1); clearTimeout(t2);
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);
  const fetchKBs = useCallback(async () => {
    if (!serverMode || !authToken) return;
    try {
      const r = await fetch("/api/kb", { headers: { "Authorization": `Bearer ${authToken}` }});
      if (r.ok) setKnowledgeBases(await r.json());
    } catch {}
  }, [serverMode, authToken]);
  useEffect(() => { if (serverMode && authToken && authUser) fetchKBs(); }, [serverMode, authToken, authUser?.id]);
  const fetchMemory = useCallback(async () => {
    if (!serverMode || !authToken) return;
    try {
      const r = await fetch("/api/memory", { headers: { "Authorization": `Bearer ${authToken}` }});
      if (r.ok) setMemory(await r.json());
    } catch {}
  }, [serverMode, authToken]);
  const addMemory = useCallback(async (text) => {
    if (!serverMode || !authToken) return;
    try {
      const r = await fetch("/api/memory", { method:"POST", headers: {"Content-Type":"application/json","Authorization":`Bearer ${authToken}`}, body: JSON.stringify({text}) });
      if (r.ok) setMemory(await r.json());
    } catch {}
  }, [serverMode, authToken]);
  const removeMemory = useCallback(async (id) => {
    if (!serverMode || !authToken) return;
    try {
      const r = await fetch("/api/memory/"+id, { method:"DELETE", headers: { "Authorization": `Bearer ${authToken}` }});
      if (r.ok) setMemory(await r.json());
    } catch {}
  }, [serverMode, authToken]);
  const clearMemory = useCallback(async () => {
    if (!serverMode || !authToken) return;
    try {
      const r = await fetch("/api/memory", { method:"DELETE", headers: { "Authorization": `Bearer ${authToken}` }});
      if (r.ok) setMemory(await r.json());
    } catch {}
  }, [serverMode, authToken]);
  const toggleMemory = useCallback(async (enabled) => {
    if (!serverMode || !authToken) return;
    try {
      const r = await fetch("/api/memory/toggle", { method:"PUT", headers: {"Content-Type":"application/json","Authorization":`Bearer ${authToken}`}, body: JSON.stringify({enabled}) });
      if (r.ok) setMemory(await r.json());
    } catch {}
  }, [serverMode, authToken]);
  // Load memory on login
  useEffect(() => { if (serverMode && authToken && authUser) fetchMemory(); }, [serverMode, authToken, authUser?.id]);

  // Load org invites on login. Server enforces admin (members get 403 →
  // silently ignored). Bails out with the previous state reference when the
  // list is content-identical, so this can never feed a render loop.
  useEffect(() => {
    if (!serverMode || !authToken || !authUser) return;
    fetch("/api/admin/org-invites", { headers: { "Authorization": `Bearer ${authToken}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d || !Array.isArray(d.invites)) return;
        const mapped = d.invites.map(i => ({ id: i.id, email: i.email, role: i.role, created: Date.parse(i.createdAt) || Date.now(), expires: Date.parse(i.expiresAt) || 0, status: i.status, token: i.token }));
        setInvites(prev => JSON.stringify(prev) === JSON.stringify(mapped) ? prev : mapped);
      })
      .catch(()=>{});
  }, [serverMode, authToken, authUser?.id]);
  // Load the shared prompt library on login (palette + settings card).
  useEffect(() => {
    if (!serverMode || !authToken || !authUser) { setOrgPrompts([]); return; }
    fetch("/api/org/prompts", { headers: { "Authorization": `Bearer ${authToken}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && Array.isArray(d.prompts)) setOrgPrompts(d.prompts); })
      .catch(()=>{});
  }, [serverMode, authToken, authUser?.id]);
  // Refresh the org record every 10 minutes while logged in so cap-usage
  // UI (UsageCapBanner) tracks the month's burn without a reload.
  useEffect(() => {
    if (!serverMode || !authToken || !authUser) return;
    const id = setInterval(() => {
      fetch("/api/org/me", { headers: { "Authorization": `Bearer ${authToken}` }})
        .then(r => r.ok ? r.json() : null)
        .then(o => { if (o && o.id) setOrgInfo(o); })
        .catch(()=>{});
    }, 600_000);
    return () => clearInterval(id);
  }, [serverMode, authToken, authUser?.id]);
  // Load the caller's org record (plan, seat usage) on login
  useEffect(() => {
    if (!serverMode || !authToken || !authUser) { setOrgInfo(null); return; }
    fetch("/api/org/me", { headers: { "Authorization": `Bearer ${authToken}` }})
      .then(r => r.ok ? r.json() : null)
      .then(o => { if (o && o.id) setOrgInfo(o); })
      .catch(()=>{});
    // Per-org allowed + custom model config — picks up whatever the superadmin
    // configured for this tenant. Refreshes whenever the user re-auths.
    fetch("/api/org/models", { headers: { "Authorization": `Bearer ${authToken}` }})
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        setOrgAllowedModels(Array.isArray(d.allowed_models) ? d.allowed_models : null);
        setOrgCustomModels(Array.isArray(d.custom_models) ? d.custom_models : []);
      })
      .catch(()=>{});
    fetch("/api/models/catalog", { headers: { "Authorization": `Bearer ${authToken}` }})
      .then(r => r.ok ? r.json() : Promise.reject(new Error("catalog_" + r.status)))
      .then(d => {
        if (!d || !Array.isArray(d.models)) return;
        setLiveModelCatalog(d);
        setLiveModelCatalogError(d.error || "");
      })
      .catch(e => {
        setLiveModelCatalogError(e?.message || "catalog_unavailable");
      });
  }, [serverMode, authToken, authUser?.id]);

  // ── Chat history cross-device sync ──────────────────────────────────────
  // On login: fetch server's chat list. If this is a first-time login on
  // this device (no prior synced user), merge with local chats so the user
  // doesn't lose anything. If switching between users on the same device,
  // replace fully — don't leak chats across users.
  const chatsSyncInit = useRef(false);
  const lastSyncedUserId = useRef(null);
  useEffect(() => {
    if (!serverMode || !authToken || !authUser) return;
    const prevSyncedUser = lastSyncedUserId.current;
    const switchedUser = prevSyncedUser && prevSyncedUser !== authUser.id;
    lastSyncedUserId.current = authUser.id;
    chatsSyncInit.current = false;
    if (switchedUser) setChats([]); // clear old user's chats before fetching new
    (async () => {
      try {
        const r = await fetch("/api/chats", { headers: { "Authorization": `Bearer ${authToken}` }});
        if (!r.ok) return;
        const serverChats = await r.json();
        if (!Array.isArray(serverChats)) return;
        setChats(prev => {
          // If switched user, only take server's chats (prev was cleared)
          if (switchedUser) return serverChats;
          // First login on this device: merge local + server by id
          const byId = new Map();
          for (const c of serverChats) byId.set(c.id, c);
          for (const c of prev) {
            const existing = byId.get(c.id);
            if (!existing) byId.set(c.id, c);
            else if ((c.messages?.length || 0) > (existing.messages?.length || 0)) byId.set(c.id, c);
          }
          return Array.from(byId.values()).sort((a,b)=>(b.created||0)-(a.created||0));
        });
        chatsSyncInit.current = true;
      } catch {}
    })();
  }, [serverMode, authToken, authUser?.id]);

  // Debounced: push chats to server whenever they change (after initial load)
  useEffect(() => {
    if (!serverMode || !authToken || !authUser || !chatsSyncInit.current) return;
    const t = setTimeout(() => {
      // Strip transient UI state and heavy base64 attachments before persisting.
      // Attachments are consumed by the AI at send-time; keeping the filename
      // on reload but dropping the payload is the tradeoff that keeps the
      // free-tier server from OOMing on chat sync.
      const clean = chats.map(c => ({
        ...c,
        messages: (c.messages||[]).map(m => {
          const { loading, error, files, ...rest } = m;
          if (files && files.length) {
            return { ...rest, files: files.map(f => ({ name: f.name, mediaType: f.mediaType, size: f.size })) };
          }
          return rest;
        })
      }));
      fetch("/api/chats", {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
        body: JSON.stringify(clean)
      }).catch(()=>{});
    }, 2000);
    return () => clearTimeout(t);
  }, [chats, serverMode, authToken, authUser?.id]);

  // ── Custom agents cross-device sync ─────────────────────────────────────
  // Agents are shared across all users on this deployment. Load from server
  // on login; debounce pushes back. Only admins can write (server enforces).
	  const agentsSyncInit = useRef(false);
	  const normalizeServerAgents = (serverAgents) => {
	    const rows = Array.isArray(serverAgents) && serverAgents.length ? serverAgents : DA;
	    return rows.map(a => (
	      a.id === "ag-default" && a.provider === "anthropic" && a.model === "claude-sonnet-4-20250514"
	        ? { ...a, model: DEFAULT_MODEL }
	        : a
	    ));
	  };
	  useEffect(() => {
	    if (!serverMode || !authToken || !authUser) return;
	    agentsSyncInit.current = false;
    (async () => {
      try {
        const r = await fetch("/api/agents", { headers: { "Authorization": `Bearer ${authToken}` }});
        if (!r.ok) return;
	        const serverAgents = await r.json();
	        if (!Array.isArray(serverAgents)) return;
	        // Server mode is tenant-scoped. Replace from this org's server row
	        // instead of merging previous-browser state from another tenant.
	        setAgents(normalizeServerAgents(serverAgents));
	        agentsSyncInit.current = true;
	      } catch {}
	    })();
	  }, [serverMode, authToken, authUser?.id]);
  useEffect(() => {
    if (!serverMode || !authToken || !authUser || !agentsSyncInit.current) return;
    const t = setTimeout(() => {
      fetch("/api/agents", {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
        body: JSON.stringify(agents)
      }).catch(()=>{});
    }, 1500);
    return () => clearTimeout(t);
  }, [agents, serverMode, authToken, authUser?.id]);

  // ── Generic shared-config sync ──────────────────────────────────────────
  // Admin-edited config that should follow the user across devices. Server
	  // keys map to client state. Server-side GET is open; PUT is admin-only.
	  const sharedSyncInit = useRef({});
	  const cloneShared = (value) => {
	    try { return JSON.parse(JSON.stringify(value)); } catch { return value; }
	  };
	  const defaultSharedValue = (key) => {
	    if (key === "workspaces") return cloneShared(DW);
	    if (key === "chat_folders") return cloneShared(DCF);
	    if (key === "integrations") return {};
	    if (key === "mcp") return [];
	    if (key === "tools") return [];
	    if (key === "role_perms") return defaultRolePerms();
	    if (key === "invites") return [];
	    if (key === "retention") return { years:7, months:0, days:0, autoDelete:true, customLabel:"7 years (default)" };
	    return null;
	  };
	  const applySharedServerValue = (binding, serverValue) => {
	    const incoming = serverValue == null ? defaultSharedValue(binding.key) : serverValue;
	    if (binding.key === "integrations") {
	      binding.setter(prev => {
	        const next = incoming && typeof incoming === "object" && !Array.isArray(incoming) ? { ...incoming } : {};
	        for (const [pid, cfg] of Object.entries(prev || {})) {
	          if (cfg && cfg.managedByServer && cfg.key === "[server]") next[pid] = { ...(next[pid] || {}), key: "[server]", managedByServer: true };
	        }
	        return next;
	      });
	    } else {
	      binding.setter(cloneShared(incoming));
	    }
	  };
	  const sharedBindings = [
	    { key: "workspaces", value: workspaces, setter: setWorkspaces },
	    { key: "chat_folders", value: chatFolders, setter: setChatFolders },
	    { key: "integrations", value: integrations, setter: setIntegrations },
	    { key: "mcp", value: mcpServers, setter: setMcpServers },
	    { key: "tools", value: tools, setter: setTools },
	    { key: "role_perms", value: rolePerms, setter: setRolePerms },
	    { key: "invites", value: invites, setter: setInvites },
	    { key: "retention", value: retentionPolicy, setter: setRetentionPolicy },
	  ];
  // Load on login
  useEffect(() => {
    if (!serverMode || !authToken || !authUser) return;
    for (const b of sharedBindings) sharedSyncInit.current[b.key] = false;
    (async () => {
      await Promise.all(sharedBindings.map(async b => {
        try {
	          const r = await fetch(`/api/shared/${b.key}`, { headers: { "Authorization": `Bearer ${authToken}` }});
	          if (!r.ok) return;
	          const server = await r.json();
	          applySharedServerValue(b, server);
	          sharedSyncInit.current[b.key] = true;
	        } catch {}
	      }));
	    })();
  }, [serverMode, authToken, authUser?.id]);
  // Push each binding independently so one unchanged state doesn't re-send others
  const pushShared = (key, value) => {
    if (!serverMode || !authToken || !authUser || !sharedSyncInit.current[key]) return;
    fetch(`/api/shared/${key}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify(value)
    }).catch(()=>{});
  };
  useEffect(() => { const t = setTimeout(() => pushShared("workspaces", workspaces), 1500); return () => clearTimeout(t); }, [workspaces, serverMode, authToken, authUser?.id]);
  useEffect(() => { const t = setTimeout(() => pushShared("chat_folders", chatFolders), 1500); return () => clearTimeout(t); }, [chatFolders, serverMode, authToken, authUser?.id]);
  useEffect(() => {
    const t = setTimeout(() => {
      // Strip the runtime "[server]" sentinel before persisting so Postgres
      // doesn't hold a marker that only means "the runtime server had this
      // provider available at the time of the sync."
      const stripped = {};
      for (const [pid, cfg] of Object.entries(integrations || {})) {
        if (!cfg) continue;
        const { managedByServer, ...rest } = cfg;
        if (rest.key === "[server]") delete rest.key;
        stripped[pid] = rest;
      }
      pushShared("integrations", stripped);
    }, 1500);
    return () => clearTimeout(t);
  }, [integrations, serverMode, authToken, authUser?.id]);
  useEffect(() => { const t = setTimeout(() => pushShared("mcp", mcpServers), 1500); return () => clearTimeout(t); }, [mcpServers, serverMode, authToken, authUser?.id]);
  useEffect(() => { const t = setTimeout(() => pushShared("tools", tools), 1500); return () => clearTimeout(t); }, [tools, serverMode, authToken, authUser?.id]);
  useEffect(() => { const t = setTimeout(() => pushShared("role_perms", rolePerms), 1500); return () => clearTimeout(t); }, [rolePerms, serverMode, authToken, authUser?.id]);
  useEffect(() => { const t = setTimeout(() => pushShared("invites", invites), 1500); return () => clearTimeout(t); }, [invites, serverMode, authToken, authUser?.id]);
  useEffect(() => { const t = setTimeout(() => pushShared("retention", retentionPolicy), 1500); return () => clearTimeout(t); }, [retentionPolicy, serverMode, authToken, authUser?.id]);

  const [codeInterpreter,setCodeInterpreter]=useState(()=>ld("cf_code_interpreter",false));
  const [enabledToolIds,setEnabledToolIds]=useState(()=>new Set(ld("cf_enabled_tools",[])));
  const [toolsMenuOpen,setToolsMenuOpen]=useState(false);
  const [toolsSubOpen,setToolsSubOpen]=useState(false);
  const toolsBtnRef = useRef(null);
  const [toolsMenuPos, setToolsMenuPos] = useState({ left: 0, bottom: 0 });
  const openToolsMenu = useCallback(() => {
    if (toolsBtnRef.current) {
      const r = toolsBtnRef.current.getBoundingClientRect();
      setToolsMenuPos({ left: Math.max(8, r.left), bottom: Math.max(8, window.innerHeight - r.top + 6) });
    }
    setToolsMenuOpen(p => !p);
    setToolsSubOpen(false);
  }, []);
  // "+" attach menu — Upload / Capture / Webpage / Notes / Knowledge / Chats / Drive
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [plusSubOpen, setPlusSubOpen] = useState(null); // "knowledge" | "notes" | "chats"
  const plusBtnRef = useRef(null);
  const [plusMenuPos, setPlusMenuPos] = useState({ left: 0, bottom: 0 });
  const openPlusMenu = useCallback(() => {
    if (plusBtnRef.current) {
      const r = plusBtnRef.current.getBoundingClientRect();
      setPlusMenuPos({ left: Math.max(8, r.left), bottom: Math.max(8, window.innerHeight - r.top + 6) });
    }
    setPlusMenuOpen(p => !p);
    setPlusSubOpen(null);
  }, []);
  useEffect(()=>{sv("cf_code_interpreter",codeInterpreter);},[codeInterpreter]);
  useEffect(()=>{sv("cf_enabled_tools",[...enabledToolIds]);},[enabledToolIds]);
  const [voiceMode,setVoiceMode]=useState(false);
  const [dictating,setDictating]=useState(false);
  const [voiceListening,setVoiceListening]=useState(false);
  const [voiceTranscript,setVoiceTranscript]=useState("");
  const [voiceSpeaking,setVoiceSpeaking]=useState(false);
  const [voiceDocked,setVoiceDocked]=useState(false);
  const [projectsReloadKey,setProjectsReloadKey]=useState(0);
  const [workflowsReloadKey,setWorkflowsReloadKey]=useState(0);
  const [voiceThinking,setVoiceThinking]=useState(false);
  const ttsAudioRef = useRef(null);
  const voiceModeRef = useRef(false);
  const ttsQueueRef = useRef([]);
  const ttsPlayingRef = useRef(false);
  const ttsStreamDoneRef = useRef(false);
  const ttsSpokenIdxRef = useRef(0);
  const voiceScanAtRef = useRef(0); // throttle for feedVoiceStream's per-chunk scan
  const ttsAudioElRef = useRef(null);
  const mouthRef = useRef(null);
  const ttsDecodeCtxRef = useRef(null);
  const mouthRafRef = useRef(null);
  const ttsUnavailableNoticeRef = useRef(false);
  // Slice 10 / 10.1 / 10.2: sticky-stream auto-scroll. The hook owns
  // listener binding via a callback ref (containerRef), so the scroller
  // node is wired up the moment React mounts it. Exposes:
  //   stickToBottom() — called per token from the imperative SSE flush
  //   jumpToBottom()  — called on user-send and on pill click; force-sticks
  //   showJumpPill    — drives the "↓ Jump to latest" button visibility
  // Replaces the prior stateless inline-gap-check + showJumpBtn plumbing
  // which used a 64 px threshold (too tight, viewport felt frozen on
  // small scroll-ups) and didn't differentiate "stuck" from "not stuck"
  // explicitly — so a click on the old pill mid-stream could land the
  // user mid-message because more tokens streamed in after the click.
  const { containerRef: areaRef, showJumpPill, stickToBottom, jumpToBottom } = useStickyBottom();

  // Slice 10.5: stable wrapper for ChatPage. ChatPage is declared inside
  // App's body (function declaration at ~L4271), so each App render
  // creates a NEW ChatPage function reference. React's reconciler
  // compares element types by `===` — different references = different
  // types = unmount + remount of the entire ChatPage subtree. That
  // remount tore down wrapper-4354 + the pill + the scroller every
  // time setShowJumpPill fired, leaving the post-click scrollTo to land
  // on a node that was about to be replaced. Slice 10.4 stabilised the
  // wrapper's children-array (pill always-mounted) but couldn't fix
  // ChatPage's own type swap.
  //
  // The fix: a stable-identity wrapper component whose function
  // reference is created ONCE (via useMemo with [] deps) and which
  // delegates to chatPageRef.current(). chatPageRef.current is
  // re-pointed to the freshly-declared ChatPage on every App render
  // (just after the `function ChatPage()` body), so the wrapper always
  // invokes the current closure-bearing version — App-scope state
  // changes still propagate, but React sees a single stable component
  // type and the entire subtree (including wrapper-4354 and the
  // scroller's scrollTop) survives App re-renders.
  const chatPageRef = useRef(null);
  const StableChatPage = useMemo(() => function StableChatPage() {
    return chatPageRef.current ? chatPageRef.current() : null;
  }, []);
  const recogRef=useRef(null);
  const recorderRef=useRef(null);
  const synthRef=useRef(null);
  const fileInputRef=useRef(null);
  const captureInputRef=useRef(null); // camera/mic capture — triggers camera on mobile
  const modelBtnRef=useRef(null);
  const multiChipRefs=useRef({});
  const textareaRef=useRef(null);
  const [attachments,setAttachments]=useState([]);
  // Offline-aware composer: pill state + the message remembered while offline
  // (auto-sent on reconnect via sendRef).
  const [isOffline,setIsOffline]=useState(typeof navigator!=="undefined"&&navigator.onLine===false);
  const _offlinePendingRef=useRef(null);
  const [gdriveConfig,setGdriveConfig]=useState(null);
  const [gdriveApisReady,setGdriveApisReady]=useState(false);
  const [gdriveBusy,setGdriveBusy]=useState(false);
  const gdrivePrepareRef=useRef(null);
  // Slice 4: drag-over visual state for the chat-pane file dropzone. Bumped by
  // dragenter on the main pane, cleared by dragleave / drop / send.
  const [dragOver,setDragOver]=useState(false);

  // Speech recognition setup
  const SpeechRecog = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  // iOS (iPhone + iPadOS, which reports as "Macintosh" with touch) cannot use
  // webkitSpeechRecognition in a standalone/installed PWA, so Talk to Me must
  // use the MediaRecorder -> /api/stt (Whisper) path there.
  const IS_IOS = typeof navigator !== "undefined" && (
    /iPad|iPhone|iPod/.test(navigator.userAgent || "") ||
    (/Macintosh/.test(navigator.userAgent || "") && typeof document !== "undefined" && "ontouchend" in document)
  );
  const IS_STANDALONE_PWA = typeof window !== "undefined" && (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    (typeof navigator !== "undefined" && navigator.standalone === true)
  );
  const FORCE_RECORDER_FOR_IOS_PWA = IS_IOS && IS_STANDALONE_PWA;

  function startDictation() {
    if (!SpeechRecog) { showToast("Speech recognition not supported in this browser"); return; }
    if (recogRef.current) { recogRef.current.stop(); recogRef.current = null; setDictating(false); return; }
    const r = new SpeechRecog();
    r.continuous = true; r.interimResults = true; r.lang = "en-US";
    r.onresult = (e) => {
      let final = "", interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
        else interim += e.results[i][0].transcript;
      }
      if (final) { const ta=textareaRef.current; if(ta){ta.value=(ta.value||"")+final;} setInput(prev=>prev+final); }
    };
    r.onerror = () => { setDictating(false); recogRef.current = null; };
    r.onend = () => { setDictating(false); recogRef.current = null; };
    recogRef.current = r;
    r.start();
    setDictating(true);
  }

  function stopDictation() {
    if (recogRef.current) { recogRef.current.stop(); recogRef.current = null; }
    setDictating(false);
  }

  // Build a short background for Talk to Me from the user's recent chats
  // and active projects so it can pick up where they left off. Fetched once
  // when Talk to Me opens; appended to the voice system prompt.
  async function buildVoiceContext() {
    const parts = [];
    const PAGE = {
      chat: "the Chat workspace",
      projects: "the Projects workspace (shared project spaces with documents, members, and a project chat)",
      agents: "the Agents page, where they create and configure AI agents",
      hermes: "the Hermes run history behind the orb experience",
      cowork: "Hermes Co-work mode, where Hermes handles everyday file automation, documents, app/MCP actions, and revisions",
      cli: "Code mode, the friendly mini IDE lane for developer-style work",
      workflows: "the Workflows page, where they build multi-step automations",
      mcp: "the MCP servers page, where they connect external tools",
      integrations: "the Integrations page",
      knowledge: "the Knowledge Base page",
      guide: "the Platform Guide page, where they learn how to use ChatFortAI end to end",
      workspaces: "the Workspaces page",
      models: "the Models page",
      analytics: "the Analytics page",
    };
    const here = PAGE[voiceLaunchSectionRef.current];
    if (here) {
      let extra = "";
      try {
        if (voiceLaunchSectionRef.current === "agents") { const names = (agents || []).slice(0, 12).map(x => String((x && x.name) || "")).filter(Boolean); if (names.length) extra = " Their agents: " + names.join(", ") + "."; }
        else if (voiceLaunchSectionRef.current === "mcp") { const names = (mcpServers || []).slice(0, 12).map(x => String((x && (x.name || x.url)) || "")).filter(Boolean); if (names.length) extra = " Connected MCP servers: " + names.join(", ") + "."; }
      } catch (e) {}
      parts.push("The user is currently on " + here + " in ChatFort." + extra + " Help them think through and design what they want to build or change here, asking brief clarifying questions as needed. When the plan is ready, remind them they can press 'Turn into action' to hand it to an autonomous builder that implements it.");
    }
    try {
      const recent = (chatsRef.current || []).filter(c => c && c.id !== voiceChatIdRef.current && Array.isArray(c.messages) && c.messages.length > 0).slice(0, 6);
      if (recent.length) {
        const lines = recent.map(c => {
          const msgs = c.messages.filter(m => m && (m.role === "user" || m.role === "assistant") && m.content);
          let lastUser = null;
          for (let i = msgs.length - 1; i >= 0; i--) { if (msgs[i].role === "user") { lastUser = msgs[i]; break; } }
          const t = String(c.title || "Untitled").replace(/\s+/g, " ").slice(0, 70);
          const snip = lastUser ? (" (they last said: \"" + String(lastUser.content).replace(/\s+/g, " ").slice(0, 110) + "\")") : "";
          return "- " + t + snip;
        });
        parts.push("Recent conversations:\n" + lines.join("\n"));
      }
    } catch (e) {}
    try {
      const tok = (() => { try { return localStorage.getItem("cf_auth_token") || ""; } catch (e) { return ""; } })();
      const r = await fetch("/api/projects", { headers: { Authorization: "Bearer " + tok } });
      if (r.ok) {
        const d = await r.json();
        const projs = (d && Array.isArray(d.projects)) ? d.projects : (Array.isArray(d) ? d : []);
        voiceProjectsRef.current = projs;
        if (projs.length) {
          parts.push("Active projects:\n" + projs.slice(0, 8).map(p => "- " + String((p && (p.name || p.title)) || "Untitled").replace(/\s+/g, " ").slice(0, 70)).join("\n"));
        }
      }
    } catch (e) {}
    if (!parts.length) return "";
    return "\n\nBackground on what the user has been working on (use it to pick up where they left off when relevant; reference it naturally, do not recite it):\n" + parts.join("\n\n");
  }

  // On-demand grounding: if the user names one of their projects or chats,
  // pull its full content (project description + docs + recent project chat,
  // or the chat transcript) and hand it to the model for THIS turn so it can
  // answer specifically instead of from the lightweight session summary.
  async function voiceOnDemandContext(text) {
    const q = String(text || "").toLowerCase();
    if (!q) return "";
    const parts = [];
    const tok = (() => { try { return localStorage.getItem("cf_auth_token") || ""; } catch (e) { return ""; } })();
    try {
      const projs = voiceProjectsRef.current || [];
      const proj = projs.find(p => { const n = String((p && (p.name || p.title)) || "").toLowerCase().trim(); return n.length > 2 && q.indexOf(n) !== -1; });
      if (proj && proj.id) {
        const detail = await fetch("/api/projects/" + proj.id, { headers: { Authorization: "Bearer " + tok } }).then(r => r.ok ? r.json() : null).catch(() => null);
        const chatRes = await fetch("/api/projects/" + proj.id + "/chat?limit=20", { headers: { Authorization: "Bearer " + tok } }).then(r => r.ok ? r.json() : null).catch(() => null);
        const seg = [];
        const nm = String((proj.name || proj.title) || "Project").replace(/\s+/g, " ").slice(0, 70);
        if (detail) {
          if (detail.description) seg.push("Description: " + String(detail.description).replace(/\s+/g, " ").slice(0, 400));
          const docs = Array.isArray(detail.docs) ? detail.docs : [];
          if (docs.length) seg.push("Documents: " + docs.slice(0, 12).map(d => String((d && (d.title || d.name)) || "Untitled").replace(/\s+/g, " ").slice(0, 60)).join("; "));
          const mem = Array.isArray(detail.members) ? detail.members : [];
          if (mem.length) seg.push("Members: " + mem.slice(0, 12).map(x => String(x.name || x.email || "member")).join(", "));
        }
        const msgs = (chatRes && Array.isArray(chatRes.messages)) ? chatRes.messages : [];
        if (msgs.length) seg.push("Recent project chat:\n" + msgs.slice(-14).map(x => "- " + String(x.body || "").replace(/\s+/g, " ").slice(0, 160)).join("\n"));
        if (seg.length) parts.push("Full details for the project \"" + nm + "\":\n" + seg.join("\n"));
      }
    } catch (e) {}
    try {
      const chats = (chatsRef.current || []).filter(c => c && c.id !== voiceChatIdRef.current && Array.isArray(c.messages) && c.messages.length > 0);
      const chat = chats.find(c => { const t = String(c.title || "").toLowerCase().trim(); return t.length > 4 && t !== "new chat" && q.indexOf(t) !== -1; });
      if (chat) {
        const ms = chat.messages.filter(x => x && (x.role === "user" || x.role === "assistant") && x.content).slice(-14);
        if (ms.length) {
          const lines = ms.map(x => (x.role === "user" ? "User" : "Assistant") + ": " + String(x.content).replace(/\s+/g, " ").slice(0, 200));
          parts.push("Full transcript of the conversation \"" + String(chat.title).replace(/\s+/g, " ").slice(0, 70) + "\":\n" + lines.join("\n"));
        }
      }
    } catch (e) {}
    if (!parts.length) return "";
    return "\n\nThe user is referring to something specific; here is its full content to ground your answer (use it directly, do not read it back verbatim):\n" + parts.join("\n\n");
  }

  function startVoiceMode() {
    const canRecord = typeof navigator !== "undefined" && navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === "function" && typeof window !== "undefined" && typeof window.MediaRecorder !== "undefined";
    if (!SpeechRecog && !canRecord) { showToast("Voice isn't supported in this browser"); return; }
    // Unlock audio inside this user gesture so the FIRST spoken reply
    // isn't blocked by the browser autoplay policy (the first TTS plays
    // after the async chat round-trip, outside any gesture). We reuse this
    // one element for all TTS so it stays unlocked for the whole session.
    try {
      if (!ttsAudioElRef.current) ttsAudioElRef.current = new Audio();
      ttsAudioElRef.current.muted = false;
      ttsAudioElRef.current.src = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=";
      const _unlock = ttsAudioElRef.current.play(); if (_unlock && _unlock.catch) _unlock.catch(() => {});
    } catch (e) {}
    voiceModeRef.current = true; resetVoiceStream(); voiceChatIdRef.current = chatId; voiceLaunchSectionRef.current = section;
    voiceCtxRef.current = ""; buildVoiceContext().then(c => { voiceCtxRef.current = c || ""; }).catch(() => {});
    setVoiceDocked(false); setVoiceMode(true); setVoiceTranscript("");
    // Opening greeting: say hi, ask how they are (by first name when we have
    // it), then offer help -- speakText's done callback starts listening.
    const _gMe = authUser || curUser;
    const _gFirst = ((_gMe && _gMe.name ? String(_gMe.name) : "").trim().split(/\s+/)[0] || "");
    const _greeting = (_gFirst ? ("Hi " + _gFirst + "! How are you doing today?") : "Hi there! How are you doing today?") + " What can I help you with?";
    speakText(_greeting);
  }

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => { const str = String(fr.result || ""); const ci = str.indexOf(","); resolve(ci >= 0 ? str.slice(ci + 1) : str); };
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
  }

  // Voice-input fallback for browsers without the Web Speech API, plus
  // standalone iOS PWA sessions where Web Speech is present but cannot hear:
  // record a clip, stop on silence/timeout, transcribe via /api/stt, then feed
  // sendVoice -- same hands-free loop.
  async function listenViaRecorder() {
    // Release the gesture-unlocked TTS <audio> element before recording. On
    // iOS an element that just played a reply keeps the audio session in
    // "playback" mode, which makes the next getUserMedia hand back a silent
    // mic track (VAD reads zeros, the recording is empty). Pausing + dropping
    // its src frees the session; the element stays unlocked for later TTS
    // because iOS unlock is per-element (not per-src).
    try {
      const _tts = ttsAudioElRef.current;
      if (_tts) { try { _tts.pause(); } catch (e) {} try { _tts.removeAttribute("src"); _tts.load(); } catch (e) {} }
    } catch (e) {}
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch (e) { setVoiceListening(false); showToast("Microphone permission is needed for voice"); return; }
    let mime = "";
    try {
      if (typeof window.MediaRecorder !== "undefined") {
        if (MediaRecorder.isTypeSupported("audio/webm")) mime = "audio/webm";
        else if (MediaRecorder.isTypeSupported("audio/mp4")) mime = "audio/mp4";
      }
    } catch (e) {}
    let rec;
    try { rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined); }
    catch (e) { try { stream.getTracks().forEach(t => t.stop()); } catch (e2) {} setVoiceListening(false); showToast("Recording isn't supported on this device/browser"); return; }
    recorderRef.current = rec;
    const chunks = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    setVoiceListening(true);
    let spoke = false, silence = 0, last = 0, elapsed = 0, ctx = null, an = null;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctx = new AC();
      // On iOS an AudioContext created after an await (we're past getUserMedia)
      // starts suspended, so the analyser yields only zeros and the voice-
      // activity auto-stop never fires. Resume it so silence detection works.
      try { if (ctx.state === "suspended" && ctx.resume) ctx.resume().catch(() => {}); } catch (e) {}
      const src = ctx.createMediaStreamSource(stream);
      an = ctx.createAnalyser(); an.fftSize = 512; src.connect(an);
      const data = new Uint8Array(an.frequencyBinCount);
      last = (typeof performance !== "undefined" ? performance.now() : Date.now());
      const tick = () => {
        if (!rec || rec.state !== "recording") return;
        an.getByteFrequencyData(data);
        let sum = 0; for (let i = 0; i < data.length; i++) sum += data[i];
        const level = sum / data.length;
        const now = (typeof performance !== "undefined" ? performance.now() : Date.now());
        const dt = now - last; last = now; elapsed += dt;
        if (level > 13) { spoke = true; silence = 0; } else if (spoke) { silence += dt; }
        if ((spoke && silence > 2000) || elapsed > 20000) { try { rec.stop(); } catch (e) {} return; }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    } catch (e) {
      setTimeout(() => { try { if (rec.state === "recording") rec.stop(); } catch (e2) {} }, 8000);
    }
    rec.onstop = async () => {
      setVoiceListening(false);
      try { stream.getTracks().forEach(t => t.stop()); } catch (e) {}
      try { if (ctx) ctx.close(); } catch (e) {}
      recorderRef.current = null;
      const blob = new Blob(chunks, { type: mime || "audio/webm" });
      // Never fail silently: a captured-but-empty mic (the iOS symptom) used to
      // just return with no feedback, which reads as "it can't hear me". Always
      // surface what happened -- with byte sizes -- so the failing stage is
      // obvious (capture vs. transcription).
      if (!chunks.length || blob.size < 1024) { showToast("No audio captured (" + (blob.size || 0) + " B) — check the mic, then tap Speak again"); return; }
      try {
        const b64 = await blobToBase64(blob);
        const tok = (() => { try { return localStorage.getItem("cf_auth_token") || ""; } catch (e) { return ""; } })();
        const r = await fetch("/api/stt", { method: "POST", headers: { Authorization: "Bearer " + tok, "Content-Type": "application/json" }, body: JSON.stringify({ audio: b64, mime: blob.type }) });
        if (!r.ok) { let info = ""; try { const ej = await r.json(); const up = ej && ej.status ? (ej.status + " ") : ""; const det = ej && (ej.detail || ej.error) ? (ej.detail || ej.error) : ""; if (up || det) info = " — " + (up + det).slice(0, 120); } catch (e) {} showToast(r.status === 503 ? "Voice transcription isn't configured on this deployment" : ("Transcription failed (" + r.status + ")" + info)); return; }
        const d = await r.json().catch(() => ({}));
        const text = (d && d.text ? String(d.text).trim() : "");
        if (text) { setVoiceTranscript(text); sendVoice(text); }
        else { showToast("Heard " + Math.round(blob.size / 1024) + "KB but no words — speak a little louder, then tap Speak again"); }
      } catch (e) { showToast("Voice error: " + (e && e.message ? e.message : "network") + " — tap Speak again"); }
    };
    try { rec.start(); } catch (e) { setVoiceListening(false); }
  }

  // Stop the current listening turn on demand. iOS voice-activity detection
  // can be unreliable, so the overlay always offers a "Done" button; whichever
  // capture is active is stopped, and its onstop/onend handler transcribes and
  // sends. Safe to call when nothing is listening.
  function stopListening() {
    try { if (recorderRef.current && recorderRef.current.state === "recording") { recorderRef.current.stop(); return; } } catch (e) {}
    try { if (recogRef.current) { recogRef.current.stop(); return; } } catch (e) {}
  }

  function listenForVoice() {
    const canRecord = typeof navigator !== "undefined" && navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === "function" && typeof window !== "undefined" && typeof window.MediaRecorder !== "undefined";
    // Installed iOS PWAs need recorder -> /api/stt because Web Speech can be
    // present but deaf there. A regular mobile browser should still use Web
    // Speech when available so Talk to Me works without requiring server STT.
    if (FORCE_RECORDER_FOR_IOS_PWA && !canRecord) { setVoiceListening(false); showToast("Voice needs mic access — open in Safari and allow the microphone"); return; }
    if (!SpeechRecog || FORCE_RECORDER_FOR_IOS_PWA) { listenViaRecorder(); return; }
    const r = new SpeechRecog();
    // Continuous + our own silence timer so a brief pause between clauses
    // doesn't end the turn -- the user can finish their thought. We only
    // finalize after ~2s of no new speech (the timer is re-armed on every
    // result, and cleared while speech is actively detected).
    r.continuous = true; r.interimResults = true; r.lang = "en-US";
    let finalText = "", lastInterim = "", silenceTimer = null;
    const SILENCE_MS = 2000;
    const armSilence = () => { if (silenceTimer) clearTimeout(silenceTimer); silenceTimer = setTimeout(() => { try { r.stop(); } catch (e) {} }, SILENCE_MS); };
    const clearSilence = () => { if (silenceTimer) { clearTimeout(silenceTimer); silenceTimer = null; } };
    r.onstart = () => setVoiceListening(true);
    r.onspeechstart = () => clearSilence();
    r.onresult = (e) => {
      finalText = ""; let interim = "";
      for (let i = 0; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalText += e.results[i][0].transcript;
        else interim += e.results[i][0].transcript;
      }
      lastInterim = interim;
      setVoiceTranscript((finalText + " " + interim).trim());
      if (finalText.trim() || interim.trim()) armSilence();
    };
    r.onend = () => {
      clearSilence();
      setVoiceListening(false);
      const said = (finalText.trim() || lastInterim.trim());
      if (said) { setVoiceTranscript(said); sendVoice(said); }
      else setVoiceTranscript("");
    };
    r.onerror = () => { clearSilence(); setVoiceListening(false); setVoiceTranscript(""); };
    recogRef.current = r;
    r.start();
  }

  // Heuristic: does this question need live / web info? If so, Talk to Me
  // flips on web search for the turn (and on the chat screen) so answers
  // like "the weather in Denver" use real-time results instead of guessing.
  function voiceNeedsWeb(text) {
    const q = " " + String(text || "").toLowerCase().replace(/\s+/g, " ").trim() + " ";
    if (!q.trim()) return false;
    const RE = /\b(weather|temperature|forecast|raining|snowing|wind|humidity|news|headline|latest|breaking|today|tonight|right now|currently|current price|stock|share price|crypto|bitcoin|exchange rate|score|who won|standings|election|box office|release date|opening hours|open now|near me|flight status|traffic|gas price|what time is it|this week|this morning|happening now|trending)\b/;
    if (RE.test(q)) return true;
    if (/\b(202[4-9]|203\d)\b/.test(q)) return true;
    return false;
  }

  // Voice navigation: "open the agents page", "go to workflows", etc. Lets
  // Talk to Me move the user around the app hands-free. Returns a section key.
  function voiceNavTarget(text) {
    const q = String(text || "").toLowerCase();
    if (!/\b(open|go to|take me to|show me|navigate to|switch to|bring up|pull up|jump to)\b/.test(q)) return null;
    const M = [["agents", /\bagents?\b/], ["projects", /\bprojects?\b/], ["cowork", /\b(hermes|co[- ]?work)\b/], ["cli", /\b(code mode|code workspace|terminal|command line|cli)\b/], ["workflows", /\b(workflows?|automations?)\b/], ["mcp", /\bmcp\b/], ["integrations", /\bintegrations?\b/], ["knowledge", /\bknowledge( base)?\b/], ["guide", /\b(platform )?(guide|manual|help|docs|documentation)\b/], ["models", /\bmodels?\b/], ["analytics", /\banalytics\b/], ["settings", /\bsettings\b/], ["chat", /\bchats?\b/]];
    for (const pair of M) { if (pair[1].test(q)) return pair[0]; }
    return null;
  }

  // Does the user want to put the idea into action now? Conservative: skip
  // questions ("how would you build it") and only fire on imperative phrases.
  function voiceWantsBuild(text) {
    const raw = String(text || "").toLowerCase().trim();
    if (!raw) return false;
    if (/[?]/.test(raw)) return false;
    if (/^(how|what|why|should|could|would|can|do|does|is|are|when|where|who|explain|tell me)\b/.test(raw)) return false;
    const q = " " + raw.replace(/\s+/g, " ") + " ";
    return /\b(turn it into action|put it into action|put this into action|build it|build this|start building|lets build|let's build|go ahead and build|go build it|make it happen|implement it|implement this|set it up|get started on it|get started on this)\b/.test(q);
  }
  // Fire an autonomous Hermes run from the conversation (the plan we just
  // talked through), then hand off to Hermes Co-work. Ref-safe so it works from the
  // speech-recognition callback.
  async function voiceBuildFromConversation() {
    const cid = voiceChatIdRef.current || chatId;
    const cur = (chatsRef.current || []).find(c => c.id === cid);
    const msgs = (cur && cur.messages) || [];
    const lastAi = [...msgs].reverse().find(m => m && m.role === "assistant" && m.content && !m.error);
    const lastUser = [...msgs].reverse().find(m => m && m.role === "user" && m.content);
    const brief = ((lastAi && lastAi.content) || (lastUser && lastUser.content) || voiceTranscript || "").trim();
    if (!brief) {
      resetVoiceStream();
      ttsStreamDoneRef.current = true;
      enqueueVoiceSentence("Let's talk through the idea first, then I'll put it into action for you.");
      return;
    }
    const goal = ("Take this idea forward and put it into action. Context from a brainstorming conversation:\n\n" + brief).slice(0, 8000);
    const tok = (() => { try { return localStorage.getItem("cf_auth_token") || ""; } catch (e) { return ""; } })();
    try {
      const r = await fetch("/api/hermes/runs", { method: "POST", headers: { Authorization: "Bearer " + tok, "Content-Type": "application/json" }, body: JSON.stringify({ goal, dollar_cap_cents: 500 }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d && d.id && d.enabled !== false) {
        try { await fetch("/api/hermes/runs/" + d.id + "/start", { method: "POST", headers: { Authorization: "Bearer " + tok } }); } catch (e) {}
        exitVoiceMode();
        setSection("cowork");
        showToast("Hermes Co-work is on it \u2713");
        return;
      }
    } catch (e) {}
    exitVoiceMode();
    try { setInput("Here's what we talked through -- please turn it into an action plan and get started:\n\n" + brief); } catch (e) {}
    setSection("chat");
    showToast("Plan added to your message \u2014 review and send.");
  }

  // Voice actions: the model can emit a fenced cf-action JSON block to DO
  // something for the user (create a project/agent, navigate, new chat).
  // We parse + run them here; the block never reaches speech or the chat.
  function executeVoiceActions(txt) {
    const s = String(txt || "");
    const re = /```cf-action\s*([\s\S]*?)```/g;
    let m, ran = 0;
    while ((m = re.exec(s)) !== null) {
      let act = null;
      try { act = JSON.parse(m[1].trim()); } catch (e) { act = null; }
      if (act && typeof act === "object") { try { if (runVoiceAction(act)) ran++; } catch (e) {} }
    }
    return ran;
  }
  function runVoiceAction(act) {
    const kind = String(act.action || act.type || "").toLowerCase().replace(/[^a-z_]/g, "");
    const tok = (() => { try { return localStorage.getItem("cf_auth_token") || ""; } catch (e) { return ""; } })();
    if (kind === "navigate" || kind === "openpage" || kind === "open_page") {
      const map = { agents: "agents", projects: "projects", hermes: "cowork", cowork: "cowork", co_work: "cowork", run_history: "hermes", runs: "hermes", terminal: "cli", code: "cli", cli: "cli", workflows: "workflows", automations: "workflows", mcp: "mcp", integrations: "integrations", knowledge: "knowledge", guide: "guide", help: "guide", docs: "guide", documentation: "guide", models: "models", analytics: "analytics", settings: "settings", chat: "chat" };
      const sec = map[String(act.page || act.section || "").toLowerCase().trim()];
      if (sec) { setSection(sec); voiceLaunchSectionRef.current = sec; showToast("Opened " + sec); return true; }
      return false;
    }
    if (kind === "newchat" || kind === "new_chat") { try { newChat(); setSection("chat"); showToast("Started a new chat"); return true; } catch (e) { return false; } }
    if (kind === "createproject" || kind === "create_project") {
      const name = String(act.name || act.title || "").trim().slice(0, 200);
      if (!name) return false;
      (async () => {
        try {
          const r = await fetch("/api/projects", { method: "POST", headers: { Authorization: "Bearer " + tok, "Content-Type": "application/json" }, body: JSON.stringify({ name }) });
          if (r.ok) { setSection("projects"); voiceLaunchSectionRef.current = "projects"; setProjectsReloadKey(k => k + 1); showToast("Created project: " + name); }
          else showToast("Couldn't create that project");
        } catch (e) { showToast("Couldn't create that project"); }
      })();
      return true;
    }
    if (kind === "createagent" || kind === "create_agent") {
      const name = String(act.name || act.title || "").trim().slice(0, 80);
      if (!name) return false;
      const instructions = String(act.instructions || act.sysprompt || act.description || "").slice(0, 4000);
      const ag = { id: gid(), name, emoji: "\ud83e\udd16", color: "#7c5cff", desc: act.description ? String(act.description).slice(0, 140) : "Created by Talk to Me", model: DEFAULT_MODEL, provider: DEFAULT_PROVIDER, sysprompt: instructions || ("You are " + name + ", a helpful assistant."), temperature: 0.7, starters: [] };
      try { setAgents(p => Array.isArray(p) ? [...p, ag] : [ag]); setSection("agents"); voiceLaunchSectionRef.current = "agents"; showToast("Created agent: " + name); return true; } catch (e) { return false; }
    }
    if (kind === "createmcpserver" || kind === "create_mcp_server" || kind === "addmcpserver" || kind === "add_mcp_server") {
      const name = String(act.name || act.title || "").trim().slice(0, 120);
      const url = String(act.url || act.endpoint || "").trim().slice(0, 500);
      if (!name || !url) return false;
      const srv = { id: "mcp-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 5), name, url, transport: "http", apiKey: "", headers: [], status: "unknown", enabled: true, created: Date.now() };
      try { setMcpServers(p => Array.isArray(p) ? [...p, srv] : [srv]); setSection("mcp"); voiceLaunchSectionRef.current = "mcp"; showToast("Added MCP server: " + name); return true; } catch (e) { return false; }
    }
    if (kind === "createworkflow" || kind === "create_workflow") {
      const name = String(act.name || act.title || "").trim().slice(0, 200);
      if (!name) return false;
      const description = String(act.description || "").slice(0, 500);
      (async () => {
        try {
          const r = await fetch("/api/workflows", { method: "POST", headers: { Authorization: "Bearer " + tok, "Content-Type": "application/json" }, body: JSON.stringify({ name, description }) });
          if (r.ok) { setSection("workflows"); voiceLaunchSectionRef.current = "workflows"; setWorkflowsReloadKey(k => k + 1); showToast("Created workflow: " + name); }
          else showToast("Couldn't create that workflow");
        } catch (e) { showToast("Couldn't create that workflow"); }
      })();
      return true;
    }
    if (kind === "createknowledgebase" || kind === "create_knowledge_base" || kind === "createkb" || kind === "create_kb") {
      const name = String(act.name || act.title || "").trim().slice(0, 120);
      if (!name) return false;
      const description = String(act.description || "").slice(0, 500);
      (async () => {
        try {
          const r = await fetch("/api/kb", { method: "POST", headers: { Authorization: "Bearer " + tok, "Content-Type": "application/json" }, body: JSON.stringify({ name, description }) });
          if (r.ok) { const kb = await r.json().catch(() => null); if (kb && kb.id) setKnowledgeBases(p => Array.isArray(p) ? [...p, kb] : [kb]); setSection("knowledge"); voiceLaunchSectionRef.current = "knowledge"; showToast("Created knowledge base: " + name); }
          else showToast("Couldn't create that (admins only)");
        } catch (e) { showToast("Couldn't create that knowledge base"); }
      })();
      return true;
    }
    if (kind === "starthermesrun" || kind === "start_hermes_run" || kind === "createrun" || kind === "create_run" || kind === "runhermes") {
      const goal = String(act.goal || act.task || act.prompt || act.name || "").trim().slice(0, 8000);
      if (!goal) return false;
      (async () => {
        try {
          const r = await fetch("/api/hermes/runs", { method: "POST", headers: { Authorization: "Bearer " + tok, "Content-Type": "application/json" }, body: JSON.stringify({ goal, dollar_cap_cents: 500 }) });
          const d = await r.json().catch(() => ({}));
          if (r.ok && d && d.id && d.enabled !== false) { try { await fetch("/api/hermes/runs/" + d.id + "/start", { method: "POST", headers: { Authorization: "Bearer " + tok } }); } catch (e) {} setSection("cowork"); voiceLaunchSectionRef.current = "cowork"; showToast("Hermes Co-work started \u2713"); }
          else showToast("Couldn't start that run");
        } catch (e) { showToast("Couldn't start that run"); }
      })();
      return true;
    }
    return false;
  }

  async function sendVoice(text) {
    if (!text || busy) return;
    if (voiceWantsBuild(text)) { voiceBuildFromConversation(); return; }
    resetVoiceStream(); setVoiceThinking(true);
    let cid = voiceChatIdRef.current || chatId; let chat = chatsRef.current.find(c => c.id === cid);
    if (!chat) { cid = gid(); chat = {id:cid,title:"New Chat",messages:[],wsId:DEFAULT_CHAT_WS_ID,folderId:getNewChatFolderId(),agentId:agId,created:Date.now()}; setChats(p=>[chat,...p]); setChatId(cid); }
    voiceChatIdRef.current = cid;
    const agBase = agents.find(a => a.id === chat.agentId) || agents[0]; const ag = selModel ? {...agBase, model:selModel.model, provider:selModel.provider} : agBase;
    const onDemand = await voiceOnDemandContext(text);
    const navTo = voiceNavTarget(text);
    let navNote = "";
    if (navTo) {
      const LBL = { agents: "Agents page", projects: "Projects workspace", hermes: "Hermes run history", cowork: "Hermes Co-work mode", cli: "Code mode", workflows: "Workflows page", mcp: "MCP servers page", integrations: "Integrations page", knowledge: "Knowledge Base page", guide: "Platform Guide page", models: "Models page", analytics: "Analytics page", settings: "Settings page", chat: "Chat workspace" };
      if (navTo !== voiceLaunchSectionRef.current) { try { setSection(navTo); } catch (e) {} voiceLaunchSectionRef.current = navTo; }
      navNote = "\n\n[You have just opened the " + (LBL[navTo] || navTo) + " for the user as they asked. Confirm briefly that it's open and ask what they would like to do there.]";
    }
    const sp = (ag?.sysprompt || "") + "\n\nYou are in a live, spoken voice conversation. Reply the way a person talks out loud: natural, warm, and brief -- usually one to three short sentences. Get straight to the point; no headings or bullet lists. When a request needs code or will take a while, first say a brief spoken line that you're starting and will let them know when it's done (for example: 'Give me a moment while I work on this -- I'll tell you when it's finished.'), then write the result into the chat for them to read and review -- use a fenced code block ONLY when it is actually code, a command, or config; write any prose deliverable such as a story, essay, email, summary, or document as normal markdown (an optional short heading plus plain paragraphs or lists), never wrapped in a code fence -- and finish with a short spoken note that it's complete and ready for them to review, without reading it aloud. Only give a longer answer if explicitly asked. When a question needs current or real-time information such as weather, news, prices, sports, or schedules, web search is turned on automatically -- use the results to answer and never say you cannot browse the web. You can also open any page of the app for the user when they ask, such as 'open the agents page' or 'go to workflows'. When they're happy with a plan and say to build it or put it into action, an autonomous builder takes it from there -- so offer that once an idea is ready. You can also do things for them right away on any page when they clearly ask: briefly say you're doing it, then add a fenced code block tagged cf-action containing JSON, for example ```cf-action {\"action\":\"create_project\",\"name\":\"Q3 Audit\"}```. Supported actions: navigate {\"page\":\"agents\"}, new_chat, create_project {\"name\":\"...\"}, create_agent {\"name\":\"...\",\"instructions\":\"...\"}, create_mcp_server {\"name\":\"...\",\"url\":\"...\"}, create_workflow {\"name\":\"...\"}, create_knowledge_base {\"name\":\"...\"}, start_hermes_run {\"goal\":\"...\"}. If a required detail like a name is missing, ask for it instead of guessing. Only emit one block when they clearly ask you to act, and never read the block aloud." + (voiceCtxRef.current || "") + onDemand + navNote;
    const um = {id:gid(),role:"user",content:text,time:Date.now()};
    const aid2 = gid();
    const am = {id:aid2,role:"assistant",content:"",loading:true,time:Date.now(),provider:ag.provider,model:ag.model};
    setChats(p=>p.map(c=>c.id!==cid?c:{...c,messages:[...c.messages,um,am],title:c.messages.length===0?text.slice(0,40):c.title}));
    setBusy(true);
    scrollBot();
    // `streamEnded` (hoisted so catch block can see it) guards against the
    // late-rAF race: the last onChunk from SSE can schedule a flush that
    // fires AFTER callAI resolves, which would otherwise un-do our final
    // setStreamingDraft(null) and leave the message stuck in pre-wrap mode.
    let streamEnded = false;
    try {
      const allM = [...(chatsRef.current.find(c=>c.id===cid)?.messages||chat.messages).filter(m=>m.role!=="system_inline"),um];
      // Imperative streaming: mount the pre-wrap <div> once via setStreamingDraft,
      // then write text directly to its DOM node per chunk. React only renders
      // twice over the whole response (mount + commit-on-finish) instead of
      // once per flush. For multi-thousand-token replies that's the difference
      // between "smooth" and "noticeably sluggish."
      streamTextRef.current = "";
      setStreamingDraft({ mid: aid2 });
      resetThinkingState();
      let pending = null, scheduled = false;
      const flush = () => {
        scheduled = false;
        if (streamEnded || pending == null) return;
        const prev = streamTextRef.current || "";
        const next = pending;
        const el = streamTargetRef.current;
        // Delta-append when possible: the browser only lays out the NEW
        // characters instead of re-laying-out the entire response every
        // frame. Full text replace is O(n) per flush → O(n²) over a stream.
        // Delta append is O(Δ) per flush → O(n) total.
        if (el) {
          if (next.length >= prev.length && next.startsWith(prev)) {
            const delta = next.slice(prev.length);
            if (delta) el.appendChild(document.createTextNode(delta));
          } else {
            el.textContent = next;
          }
        }
        streamTextRef.current = next;
        pending = null;
        // Slice 10: sticky-bottom auto-scroll. The hook gates this on its
        // own isStuck state — when the user has scrolled up, stickToBottom
        // is a no-op and the viewport stays put.
        stickToBottom();
      };
      const onChunk = (acc) => {
        if (streamEnded) return;
        // First real content token → flip the indicator off via state.
        // setState is short-circuited by the reducer when already true, so
        // this is a single render even though it's called per chunk.
        if (acc) markFirstToken();
        feedVoiceStream(acc);
        pending = acc;
        if (!scheduled) { scheduled = true; requestAnimationFrame(flush); }
      };
      const onStatus = (phase, data) => {
        // Preserve: capture draft_id from tool_result so DraftReviewCard
        // renders above the assistant bubble. Other components depend on
        // pendingDrafts state — must not be removed.
        if (phase === "tool_result" && data && data.tool === "create_draft_email" && data.ok && data.draft_id) {
          setPendingDrafts(p => ({ ...p, [aid2]: { draftId: data.draft_id, preview: data.preview || null } }));
        }
        // feat: tool activity strip. Append a "running" event on
        // using_tool and resolve it to done/error on tool_result.
        if (phase === "using_tool" && data && typeof data.tool === "string") {
          // Parity with send(): cap toolEvents at 50 per message.
          const name = data.tool;
          setChats(prev => prev.map(c => c.id !== cid ? c : { ...c, messages: c.messages.map(m => {
            if (m.id !== aid2) return m;
            const next = [...(m.toolEvents || []), { name, status: "running", startedAt: Date.now() }];
            return { ...m, toolEvents: next.length > 50 ? next.slice(-50) : next };
          }) }));
        } else if (phase === "tool_result" && data && typeof data.tool === "string") {
          const name = data.tool;
          const ok = !!data.ok;
          const err = data.error || null;
          setChats(prev => prev.map(c => c.id !== cid ? c : { ...c, messages: c.messages.map(m => {
            if (m.id !== aid2) return m;
            const events = (m.toolEvents || []).slice();
            let patched = false;
            for (let i = events.length - 1; i >= 0; i--) {
              if (events[i].name === name && events[i].status === "running") {
                events[i] = { ...events[i], status: ok ? "done" : "error", endedAt: Date.now(), error: err };
                patched = true;
                break;
              }
            }
            if (!patched) {
              // Defensive: orphan tool_result -- still emit a chip so
              // the user can see the call happened.
              events.push({ name, status: ok ? "done" : "error", startedAt: Date.now(), endedAt: Date.now(), error: err });
            }
            const capped = events.length > 50 ? events.slice(-50) : events;
            return { ...m, toolEvents: capped };
          }) }));
        }
        applyStatusFrame(phase, data);
      };
      const raw = await callAI(ag, allM, sp, resolveChatMaxTokens(cfg.maxTokens, text), integrations, webSearch, tools, selectedKbId || ag?.kbId || null, onChunk, false, onStatus);
      streamEnded = true;
      const txt = typeof raw === "string" ? raw : raw?.__text || "(No response)";
      const searchMeta = (raw && typeof raw === "object" && raw.__search) ? raw.__search : null;
      const traceMeta = (raw && typeof raw === "object" && raw.__trace) ? raw.__trace : null;
      const kbMeta = (raw && typeof raw === "object" && raw.__kbSources) ? raw.__kbSources : null;
      setChats(p=>p.map(c=>c.id!==cid?c:{...c,messages:c.messages.map(m=>m.id===aid2?{...m,content:txt,loading:false,search:searchMeta,trace:traceMeta,kbSources:kbMeta}:m)}));
      setStreamingDraft(null);
      // Speak the response in Talk to Me through the generated /api/tts voice;
      // speakText re-listens when it finishes.
      try {
        if (executeVoiceActions(txt) > 0) {
          const cleaned = txt.replace(/```cf-action[\s\S]*?```/g, "").replace(/\n{3,}/g, "\n\n").trim();
          setChats(p=>p.map(c=>c.id!==cid?c:{...c,messages:c.messages.map(mm=>mm.id===aid2?{...mm,content:cleaned}:mm)}));
        }
      } catch (e) {}
      if (voiceModeRef.current) finishVoiceStream(txt);
    } catch(err) {
      streamEnded = true;
      setChats(p=>p.map(c=>c.id!==cid?c:{...c,messages:c.messages.map(m=>m.id===aid2?{...m,content:"⚠ "+err.message,loading:false,error:true}:m)}));
      setStreamingDraft(null);
    }
    setBusy(false);
  }

  // Talk to Me: speak a reply in the generated /api/tts voice. The app does
  // not fall back to browser speech synthesis; that path sounds robotic and
  // masks server-side TTS failures.
  function noteTtsUnavailable() {
    if (ttsUnavailableNoticeRef.current) return;
    ttsUnavailableNoticeRef.current = true;
    showToast("Voice audio is temporarily unavailable");
    setTimeout(() => { ttsUnavailableNoticeRef.current = false; }, 6000);
  }
  async function speakText(txt) {
    const clean = String(txt || "").replace(/\[(.*?)\]\((?:.*?)\)/g, "$1").replace(/[#*`>_~]/g, "").trim().slice(0, 4000);
    const done = () => { setVoiceSpeaking(false); setVoiceThinking(false); if (voiceModeRef.current) listenForVoice(); };
    if (!clean) { done(); return; }
    setVoiceThinking(true); setVoiceSpeaking(false);
    try {
      const tok = (() => { try { return localStorage.getItem("cf_auth_token") || ""; } catch (e) { return ""; } })();
      const r = await fetch("/api/tts", { method: "POST", headers: { Authorization: "Bearer " + tok, "Content-Type": "application/json" }, body: JSON.stringify({ text: clean, voice: (cfg.ttsVoice || PREFERRED_TTS_VOICE) }) });
      if (r.ok) {
        const url = URL.createObjectURL(await r.blob());
        // Reuse the gesture-unlocked element so iOS doesn't block playback.
        let audio = ttsAudioElRef.current;
        if (!audio) { audio = new Audio(); ttsAudioElRef.current = audio; }
        try { audio.muted = false; audio.volume = 1; } catch (e) {}
        audio.src = url;
        ttsAudioRef.current = audio;
        audio.onplay = () => { setVoiceThinking(false); setVoiceSpeaking(true); };
        const fin = () => { try { URL.revokeObjectURL(url); } catch (e) {} ttsAudioRef.current = null; done(); };
        audio.onended = fin; audio.onerror = fin;
        await audio.play();
        return;
      }
    } catch (e) {}
    noteTtsUnavailable();
    done();
  }

  // ── Talk to Me: streaming sentence-by-sentence speech ──────────────────
  // Speak the reply AS it streams so audio starts after the first sentence
  // instead of waiting for the whole answer + its full TTS.
  function resetVoiceStream() {
    ttsQueueRef.current = [];
    ttsPlayingRef.current = false;
    ttsStreamDoneRef.current = false;
    ttsSpokenIdxRef.current = 0;
    ttsInterruptRef.current = false;
    voiceTaskNoticeRef.current = false;
    voiceScanAtRef.current = 0;
  }
  function enqueueVoiceSentence(s) {
    const clean = String(s || "").replace(/\[(.*?)\]\((?:.*?)\)/g, "$1").replace(/[#*`>_~]/g, "").trim();
    if (!clean) return;
    // Pipeline: kick off this sentence's TTS fetch NOW so it's ready by the
    // time the player reaches it -- the next sentence plays with no gap.
    ttsQueueRef.current.push({ text: clean, p: fetchClip(clean) });
    if (!ttsPlayingRef.current) runVoiceQueue();
  }
  // Strip code from the SPOKEN text so it's never read aloud (the chat
  // message still shows the full code for review). Splits on the code fence
  // -- even-indexed parts are prose (kept), odd parts are code (dropped) --
  // then removes inline code + leftover markdown.
  function toSpeakable(raw) {
    const F = "\u0060\u0060\u0060";
    const parts = String(raw || "").split(F);
    let out = "";
    for (let i = 0; i < parts.length; i++) { if (i % 2 === 0) out += (out ? " " : "") + parts[i]; }
    return out.replace(/\u0060([^\u0060]*)\u0060/g, "$1").replace(/[#*>_~]/g, "");
  }
  function feedVoiceStream(acc) {
    // Throttle the spoken-sentence scan: this re-scans the whole accumulated
    // reply each call (toSpeakable splits on code fences + regex over all of
    // it), so running it on every SSE chunk is O(n^2) and visibly janks long
    // replies -- worst while writing code -- in voice mode. Cap to ~8x/sec;
    // the next call catches up via ttsSpokenIdxRef and finishVoiceStream
    // flushes the tail at stream end, so no spoken sentence is dropped.
    const _scanNow = (typeof performance !== "undefined" ? performance.now() : Date.now());
    if (_scanNow - voiceScanAtRef.current < 120) return;
    voiceScanAtRef.current = _scanNow;
    const raw = String(acc || "");
    // Up-front notice: if the reply dives straight into code / a long task
    // with no spoken intro, say a brief "give me a moment" once so the user
    // isn't left in silence while it works. Skipped if the model already
    // spoke an intro before the code fence.
    if (!voiceTaskNoticeRef.current) {
      const FENCE = "\u0060\u0060\u0060";
      const fenceAt = raw.indexOf(FENCE);
      if (fenceAt !== -1 && !toSpeakable(raw.slice(0, fenceAt)).trim()) {
        voiceTaskNoticeRef.current = true;
        enqueueVoiceSentence("Give me a moment while I work on this. I'll let you know as soon as it's done.");
      }
    }
    const unspoken = toSpeakable(raw).slice(ttsSpokenIdxRef.current);
    if (!unspoken) return;
    const re = /[.!?][\)\]"'\u2019\u201d]*\s|\n+/g;
    let m, lastEnd = -1;
    while ((m = re.exec(unspoken)) !== null) lastEnd = m.index + m[0].length;
    if (lastEnd > 0) {
      const ready = unspoken.slice(0, lastEnd).trim();
      ttsSpokenIdxRef.current += lastEnd;
      if (ready) enqueueVoiceSentence(ready);
    }
  }
  function finishVoiceStream(fullText) {
    const full = toSpeakable(fullText);
    let rest = full.slice(ttsSpokenIdxRef.current).trim();
    // If the whole reply was code (nothing spoken), say a short note rather
    // than going silent -- the code is in the chat for review.
    if (!rest && /\u0060\u0060\u0060/.test(String(fullText || ""))) {
      rest = "All done. The code is in the chat -- please review it and let me know if you have any questions.";
    }
    if (rest) enqueueVoiceSentence(rest);
    ttsSpokenIdxRef.current = full.length;
    ttsStreamDoneRef.current = true;
    if (!ttsPlayingRef.current) runVoiceQueue();
  }
  // Decode a spoken clip into a per-frame loudness envelope so the mouth
  // can move with the actual words. Decoding is independent of playback,
  // so it never affects whether sound comes out. Returns null on failure.
  function buildVoiceEnvelope(arrayBuf) {
    return new Promise((resolve) => {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC || !arrayBuf || !arrayBuf.byteLength) return resolve(null);
        if (!ttsDecodeCtxRef.current) ttsDecodeCtxRef.current = new AC();
        const onBuf = (buf) => {
          try {
            const ch = buf.getChannelData(0);
            const fps = 60;
            const spf = Math.max(1, Math.floor(buf.sampleRate / fps));
            const frames = Math.ceil(ch.length / spf);
            const env = new Float32Array(frames);
            let max = 0.0001;
            for (let f = 0; f < frames; f++) {
              const s = f * spf, e = Math.min(ch.length, s + spf);
              let sum = 0; for (let i = s; i < e; i++) { const v = ch[i]; sum += v * v; }
              const rms = Math.sqrt(sum / Math.max(1, e - s));
              env[f] = rms; if (rms > max) max = rms;
            }
            for (let f = 0; f < frames; f++) env[f] = Math.min(1, (env[f] / max) * 1.25);
            resolve({ env, fps });
          } catch (e) { resolve(null); }
        };
        const ret = ttsDecodeCtxRef.current.decodeAudioData(arrayBuf, onBuf, () => resolve(null));
        if (ret && typeof ret.then === "function") ret.then(onBuf).catch(() => resolve(null));
      } catch (e) { resolve(null); }
    });
  }
  function startMouthLoop(audio, env) {
    stopMouthLoop();
    const apply = (lvl) => { const m = mouthRef.current; if (m) { m.setAttribute("ry", (2 + lvl * 9).toFixed(2)); m.setAttribute("rx", (10.5 + lvl * 3).toFixed(2)); } };
    if (!env || !env.env || !env.env.length) {
      let t = 0;
      const tick = () => { t += 0.22; const lvl = Math.min(1, 0.38 + 0.5 * Math.abs(Math.sin(t)) * (0.55 + 0.45 * Math.random())); apply(lvl); mouthRafRef.current = requestAnimationFrame(tick); };
      mouthRafRef.current = requestAnimationFrame(tick);
      return;
    }
    const tick = () => {
      const f = Math.floor((audio.currentTime || 0) * env.fps);
      const lvl = env.env[Math.max(0, Math.min(env.env.length - 1, f))] || 0;
      apply(lvl);
      mouthRafRef.current = requestAnimationFrame(tick);
    };
    mouthRafRef.current = requestAnimationFrame(tick);
  }
  function stopMouthLoop() {
    if (mouthRafRef.current) { cancelAnimationFrame(mouthRafRef.current); mouthRafRef.current = null; }
    const m = mouthRef.current; if (m) m.setAttribute("ry", "3");
  }

  // Fetch + decode one clip. Kicked off as soon as a sentence is ready so
  // it's prefetched while earlier sentences play. Returns { url, env } or null.
  function fetchClip(text) {
    const clean = String(text || "").slice(0, 4000);
    return (async () => {
      if (!clean) return null;
      try {
        const tok = (() => { try { return localStorage.getItem("cf_auth_token") || ""; } catch (e) { return ""; } })();
        const r = await fetch("/api/tts", { method: "POST", headers: { Authorization: "Bearer " + tok, "Content-Type": "application/json" }, body: JSON.stringify({ text: clean, voice: (cfg.ttsVoice || PREFERRED_TTS_VOICE) }) });
        if (!r.ok) return null;
        const mime = r.headers.get("content-type") || "audio/mpeg";
        const ab = await r.arrayBuffer();
        const url = URL.createObjectURL(new Blob([ab], { type: mime }));
        const env = await buildVoiceEnvelope(ab.slice(0));
        return { url, env };
      } catch (e) { return null; }
    })();
  }
  // Play one (prefetched) clip; resolves when it ends so the next plays
  // immediately. If generated audio is unavailable, do not use browser speech.
  function playClip(clip, text) {
    return new Promise((resolve) => {
      let settled = false, guard = null;
      const finish = () => { if (settled) return; settled = true; ttsFinishRef.current = null; if (guard) { clearTimeout(guard); guard = null; } resolve(); };
      ttsFinishRef.current = finish;
      guard = setTimeout(finish, Math.min(60000, (text ? String(text).length : 40) * 120 + 6000));
      if (clip && clip.url) {
        // Reuse the ONE element unlocked inside the user gesture (startVoiceMode).
        // On iOS a fresh `new Audio()` is NOT unlocked and its play() is blocked
        // by the autoplay policy -> silent reply even though the transcript shows.
        let audio = ttsAudioElRef.current;
        if (!audio) { audio = new Audio(); ttsAudioElRef.current = audio; }
        try { audio.muted = false; audio.volume = 1; } catch (e) {}
        audio.src = clip.url;
        ttsAudioRef.current = audio;
        audio.onplay = () => { setVoiceThinking(false); setVoiceSpeaking(true); startMouthLoop(audio, clip.env); };
        const fin = () => { stopMouthLoop(); try { URL.revokeObjectURL(clip.url); } catch (e) {} finish(); };
        audio.onended = fin; audio.onerror = fin;
        const _p = audio.play();
        if (_p && _p.catch) _p.catch(() => { /* blocked -> watchdog continues */ });
        return;
      }
      noteTtsUnavailable();
      finish();
    });
  }
  // Barge-in: stop talking immediately and start listening for the user's
  // next turn -- triggered by tapping the avatar (or the button) while it's
  // speaking. Clears the queue + current clip, resolves the in-flight
  // playClip via ttsFinishRef, and re-arms the recognizer.
  function interruptVoice() {
    if (!voiceModeRef.current) return;
    ttsInterruptRef.current = true;
    ttsQueueRef.current = [];
    try { if (ttsAudioRef.current) ttsAudioRef.current.pause(); } catch (e) {}
    ttsAudioRef.current = null;
    if (typeof window !== "undefined" && window.speechSynthesis) { try { window.speechSynthesis.cancel(); } catch (e) {} }
    if (ttsFinishRef.current) { try { ttsFinishRef.current(); } catch (e) {} }
    ttsPlayingRef.current = false;
    stopMouthLoop();
    setVoiceSpeaking(false); setVoiceThinking(false);
    if (recogRef.current) { try { recogRef.current.stop(); } catch (e) {} recogRef.current = null; }
    listenForVoice();
  }
  async function runVoiceQueue() {
    if (ttsPlayingRef.current) return;
    ttsPlayingRef.current = true;
    while (ttsQueueRef.current.length) {
      if (!voiceModeRef.current || ttsInterruptRef.current) { ttsPlayingRef.current = false; return; }
      const item = ttsQueueRef.current.shift();
      let clip = null;
      try { clip = await item.p; } catch (e) {}
      if (!voiceModeRef.current || ttsInterruptRef.current) { ttsPlayingRef.current = false; return; }
      await playClip(clip, item.text);
    }
    ttsPlayingRef.current = false;
    if (ttsInterruptRef.current || !ttsStreamDoneRef.current) return; // interrupted, or more sentences still coming
    setVoiceSpeaking(false);
    if (voiceModeRef.current) { setVoiceThinking(false); listenForVoice(); }
  }

  function exitVoiceMode() {
    if (recogRef.current) { recogRef.current.stop(); recogRef.current = null; }
    if (recorderRef.current) { try { recorderRef.current.stop(); } catch (e) {} recorderRef.current = null; }
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    if (ttsAudioRef.current) { try { ttsAudioRef.current.pause(); } catch (e) {} ttsAudioRef.current = null; }
    voiceModeRef.current = false; ttsQueueRef.current = []; ttsPlayingRef.current = false; stopMouthLoop();
    setVoiceMode(false); setVoiceListening(false); setVoiceTranscript(""); setVoiceSpeaking(false); setVoiceThinking(false); setVoiceDocked(false);
  }

  // Talk to Me: turn what you just talked through into action -- hand the
  // brief to a Hermes Co-work task; if the engine is off, drop the
  // plan into the chat composer instead. The conversation's own content is
  // the brief (no extra LLM call).
  async function turnIntoAction() {
    const cur = chats.find(c => c.id === chatId);
    const msgs = (cur && cur.messages) || [];
    const lastAi = [...msgs].reverse().find(m => m.role === "assistant" && m.content && !m.error);
    const lastUser = [...msgs].reverse().find(m => m.role === "user" && m.content);
    const brief = ((lastAi && lastAi.content) || (lastUser && lastUser.content) || voiceTranscript || "").trim();
    if (!brief) { showToast("Talk through an idea first, then turn it into action."); return; }
    const goal = ("Take this idea forward and put it into action. Context from a brainstorming conversation:\n\n" + brief).slice(0, 8000);
    const tok = (() => { try { return localStorage.getItem("cf_auth_token") || ""; } catch (e) { return ""; } })();
    try {
      const r = await fetch("/api/hermes/runs", { method: "POST", headers: { Authorization: "Bearer " + tok, "Content-Type": "application/json" }, body: JSON.stringify({ goal, dollar_cap_cents: 500 }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d && d.id && d.enabled !== false) {
        try { await fetch("/api/hermes/runs/" + d.id + "/start", { method: "POST", headers: { Authorization: "Bearer " + tok } }); } catch (e) {}
        exitVoiceMode();
        setSection("cowork");
        showToast("Hermes Co-work is on it \u2713");
        return;
      }
    } catch (e) { /* fall through to composer */ }
    exitVoiceMode();
    try { setInput("Here's what we talked through -- please turn it into an action plan and get started:\n\n" + brief); } catch (e) {}
    showToast("Plan added to your message \u2014 review and send.");
  }

  useEffect(()=>{
    sv("cf_theme",theme);
    // H7.1: notify same-window listeners (cf_theme.js subscribers in
    // the Hermes panel). The native `storage` event only fires in OTHER
    // tabs, so panels that read cf_theme need this push to re-render.
    try { window.dispatchEvent(new CustomEvent("cf_theme_change")); } catch {}
  },[theme]);
  useEffect(()=>{sv("cf_chats",chats);},[chats]);
  useEffect(()=>{sv("cf_ws",workspaces);},[workspaces]);
  useEffect(()=>{sv("cf_chat_folders",chatFolders);},[chatFolders]);
  useEffect(()=>{sv("cf_chat_folder_archived_ids_by_scope",archivedChatFolderIdsByScope);},[archivedChatFolderIdsByScope]);
  useEffect(()=>{
    const valid=new Set(((Array.isArray(chatFolders)&&chatFolders.length)?chatFolders:DCF).map(f=>f?.id).filter(Boolean));
    setArchivedChatFolderIds(prev=>prev.filter(id=>valid.has(id)));
  },[chatFolders,setArchivedChatFolderIds]);
  useEffect(()=>{sv("cf_chat_folder_id",chatFolderId);},[chatFolderId]);
  useEffect(()=>{sv("cf_agents",agents);},[agents]);
  useEffect(()=>{sv("cf_tools",tools);},[tools]);
  useEffect(()=>{sv("cf_integ",integrations);},[integrations]);
  useEffect(()=>{sv("cf_mcp",mcpServers);},[mcpServers]);
  useEffect(()=>{sv("cf_cfg",cfg);},[cfg]);
	  useEffect(()=>{
	    // Apply retention policy before saving
	    const cutoff = retentionPolicy.autoDelete ? Date.now() - ((retentionPolicy.years*365 + retentionPolicy.months*30 + retentionPolicy.days) * 86400000) : 0;
	    const kept = cutoff > 0 ? alog.filter(e => e.ts >= cutoff) : alog;
	    const key = serverMode && authUser?.orgId ? `cf_analytics:${authUser.orgId}` : "cf_analytics:local";
	    sv(key, kept);
	  },[alog, retentionPolicy, serverMode, authUser?.orgId]);
  useEffect(()=>{sv("cf_retention",retentionPolicy);},[retentionPolicy]);
  useEffect(()=>{sv("cf_users",users);},[users]);
  useEffect(()=>{sv("cf_invites",invites);},[invites]);
  useEffect(()=>{sv("cf_sso",ssoConfig);},[ssoConfig]);
  useEffect(()=>{sv("cf_role_perms",rolePerms);},[rolePerms]);
  useEffect(()=>{sv("cf_uid",uid);},[uid]);

  // Helper: merge the parallel responses from a multi-model comparison into
  // one synthesized answer. Uses the agent's default model as the merger.
  const mergeCompareResponses = useCallback(async (chatIdArg, msgIdArg) => {
    const chat = chats.find(c => c.id === chatIdArg);
    if (!chat) return;
    const compIdx = chat.messages.findIndex(m => m.id === msgIdArg);
    if (compIdx < 0) return;
    const comp = chat.messages[compIdx];
    if (!comp?.slots || comp.slots.some(s => s.loading)) return;
    // Find the user message immediately before the comparison (the original question)
    let userMsg = null;
    for (let i = compIdx - 1; i >= 0; i--) {
      if (chat.messages[i].role === "user") { userMsg = chat.messages[i]; break; }
    }
    const origQuestion = (userMsg?.content && typeof userMsg.content === "string") ? userMsg.content : (userMsg?.content ? String(userMsg.content) : "");

    // Mark merged as loading
    setChats(p => p.map(c => c.id !== chatIdArg ? c : { ...c, messages: c.messages.map(m => m.id !== msgIdArg ? m : { ...m, merged: { loading: true, content: "" } }) }));

    const slotBlocks = comp.slots.map((s, i) => {
      const pv = PROVIDERS[s.provider];
      const mName = pv?.models?.find(mm => mm.id === s.model)?.name || s.model;
      return `## Response ${i + 1} — ${mName} (${pv?.name || s.provider})\n${s.content || "(empty)"}`;
    }).join("\n\n---\n\n");

    const mergePrompt = `Below are ${comp.slots.length} different AI models' responses to the same user question. Your job is to synthesize them into ONE best answer.

# Original user question
${origQuestion}

${slotBlocks}

# Your task
Synthesize the above responses into a single definitive answer. Follow these rules:
1. Lead with the core answer — whatever all the models agree on, stated confidently.
2. Note any meaningful disagreements explicitly: "Models X and Y said A; Model Z said B because..."
3. Include unique insights any single model added that the others missed.
4. Be concise. Don't pad. Aim for fewer words than any single response.
5. Do NOT mention the merge process itself or say "I've synthesized X responses".

Write your synthesized answer directly:`;

    // For synthesis, pick a model from one of the slots that just succeeded.
    // Rationale: the agent's DEFAULT model may not pass BAA (e.g. no Azure
    // deployment mapping for that ID) even when every slot model already
    // produced output in this same chat. Reusing a slot model guarantees
    // we re-use a path the org's policy + provider config already accepts.
    const agBase = agents.find(a => a.id === chat.agentId) || agents[0];
    const winnerSlot = comp.slots.find(s => !s.error && s.content) || comp.slots[0];
    const mergeAgent = {
      ...agBase,
      model: winnerSlot?.model || agBase.model,
      provider: winnerSlot?.provider || agBase.provider,
      temperature: 0.3,
    };
    const mergeMessages = [{ role: "user", content: mergePrompt }];

    // If the chosen slot model fails (rare -- transient upstream blip),
    // walk the remaining slot models in turn before surfacing the error.
    const fallbackModels = comp.slots
      .filter(s => !s.error && s.content && (s.model !== winnerSlot?.model || s.provider !== winnerSlot?.provider))
      .map(s => ({ model: s.model, provider: s.provider }));

    async function callMerge(useAgent) {
      return await callAI(useAgent, mergeMessages, "You are a careful synthesizer of multiple AI responses. Produce one clean, accurate merged answer.", cfg.maxTokens, integrations, false, [], null);
    }

    try {
      let raw;
      try {
        raw = await callMerge(mergeAgent);
      } catch (firstErr) {
        let lastErr = firstErr;
        let ok = false;
        for (const fb of fallbackModels) {
          try {
            raw = await callMerge({ ...mergeAgent, model: fb.model, provider: fb.provider });
            ok = true;
            break;
          } catch (e) { lastErr = e; }
        }
        if (!ok) throw lastErr;
      }
      const txt = typeof raw === "string" ? raw : raw?.__text || "(No merged response)";
      setChats(p => p.map(c => c.id !== chatIdArg ? c : { ...c, messages: c.messages.map(m => m.id !== msgIdArg ? m : { ...m, merged: { loading: false, content: txt } }) }));
      showToast("Responses merged ✓");
    } catch (err) {
      setChats(p => p.map(c => c.id !== chatIdArg ? c : { ...c, messages: c.messages.map(m => m.id !== msgIdArg ? m : { ...m, merged: { loading: false, error: true, content: "Merge failed: " + err.message } }) }));
    }
  }, [chats, agents, cfg.maxTokens, integrations]);

  // Helper: post one analytics entry to the central server log
	  const postAnalytics = useCallback((entry) => {
	    if (!serverMode || !authToken) return;
	    const scopedEntry = { ...entry, orgId: authUser?.orgId || entry.orgId || "local" };
	    fetch("/api/analytics", {
	      method: "POST",
	      headers: {"Content-Type":"application/json","Authorization":`Bearer ${authToken}`},
	      body: JSON.stringify(scopedEntry)
	    }).catch(()=>{});
	  }, [serverMode, authToken, authUser?.orgId]);

  // Analytics page sync: stamp + helper.
  // The /api/admin/analytics GET was previously only called once at
  // login. This pulls it out into a callable helper so the 60s poller
  // can re-fire it -- and admins on one device see chats from active
  // users on every other device near-real-time.
  const [analyticsLastSyncAt, setAnalyticsLastSyncAt] = useState(0);

  // Analytics view state, LIFTED to App scope. AnalyticsPage is
  // defined inline inside App -- every App re-render gives it a new
  // function reference, so React unmounts/remounts it, dropping any
  // local useState. The 60s sync fires setAlog -> App re-renders ->
  // AnalyticsPage remounts -> drill-in is lost. Lifting these to
  // App scope so they survive the remount.
  const [analyticsTab, setAnalyticsTab] = useState("overview");
  const [analyticsSelE, setAnalyticsSelE] = useState(null);
  const [analyticsFilterUser, setAnalyticsFilterUser] = useState("all");
  const [analyticsFilterModel, setAnalyticsFilterModel] = useState("all");
  const [analyticsFilterWs, setAnalyticsFilterWs] = useState("all");
  const [analyticsFilterStatus, setAnalyticsFilterStatus] = useState("all");
  const [analyticsDateRange, setAnalyticsDateRange] = useState("30d");
  const [analyticsSearchQ, setAnalyticsSearchQ] = useState("");
  const [analyticsSortBy, setAnalyticsSortBy] = useState("newest");
  useEffect(() => {
    if (!serverMode || !authUser?.orgId) return;
    setAlog(ld(`cf_analytics:${authUser.orgId}`, []));
    setAnalyticsLastSyncAt(0);
  }, [serverMode, authUser?.orgId, authUser?.id]);

  // Phase 5: run-detail UI state lifted to App so the 1.5s live-progress
  // polling remount doesn't reset the user's search query / variables
  // panel open-state.
  const [wfDetailSearch, setWfDetailSearch] = useState("");
  const [wfDetailVarsOpen, setWfDetailVarsOpen] = useState(false);
  // Phase 5: card-level UX state.
  const [wfListSearch, setWfListSearch] = useState("");
  const [wfListSort, setWfListSort] = useState("recent"); // 'recent' | 'name' | 'trigger'
  const [wfPinned, setWfPinned] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cf_wf_pinned") || "[]"); } catch { return []; }
  });
  function wfTogglePin(id) {
    setWfPinned(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      try { localStorage.setItem("cf_wf_pinned", JSON.stringify(next)); } catch {}
      return next;
    });
  }
  // Phase 5: Recent runs modal status filter.
  const [wfRunsStatusFilter, setWfRunsStatusFilter] = useState("all");
  // n8n parity: active tag filter on the workflows list.
  const [wfTagFilter, setWfTagFilter] = useState("");
  // Workflow preview modal state (App-scope so it survives any
  // re-renders triggered by other workflows' polling).
  const [wfPreviewing, setWfPreviewing] = useState(null);     // workflow row
  const [wfPreviewStep, setWfPreviewStep] = useState(-1);     // -1 = not started; 0..N-1 = current; N = complete
  const [wfPreviewRun, setWfPreviewRun] = useState(null);     // latest successful run (for final output)
  const [wfPreviewLoading, setWfPreviewLoading] = useState(false);
  // Client-side topo sort matching server _wfTopoSort: Kahn's algorithm.
  function _wfPreviewTopo(nodes, edges) {
    const inDeg = {}, adj = {};
    for (const n of (nodes || [])) { inDeg[n.id] = 0; adj[n.id] = []; }
    for (const e of (edges || [])) {
      if (!(e.source in inDeg) || !(e.target in inDeg)) continue;
      adj[e.source].push(e.target);
      inDeg[e.target] = (inDeg[e.target] || 0) + 1;
    }
    const q = (nodes || []).filter(n => inDeg[n.id] === 0).map(n => n.id);
    const out = [];
    while (q.length) {
      const id = q.shift();
      out.push(id);
      for (const nx of (adj[id] || [])) { if (--inDeg[nx] === 0) q.push(nx); }
    }
    // Append any unreachable nodes at the end so the user sees them.
    for (const n of (nodes || [])) if (!out.includes(n.id)) out.push(n.id);
    return out;
  }
  // Friendly per-node icons for the preview tiles. Mirrors the
  // WorkflowEditor palette but inlined here so app.jsx is self-
  // contained for the modal.
  const _WF_PREVIEW_ICONS = {
    "trigger.manual":      "▶",  "trigger.schedule":    "🕐",
    "trigger.webhook":     "🔗", "action.aiChat":       "🤖",
    "action.httpRequest":  "🌐", "action.mcpCall":      "🔌",
    "action.kbSearch":     "🔍", "logic.if":            "❓",
    "logic.setVar":        "📌", "output.toast":        "💬",
    "output.saveMemory":   "💾", "workflow.call":       "🔁",
  };
  async function wfStartPreview(w) {
    setWfPreviewing(w);
    setWfPreviewStep(-1);
    setWfPreviewRun(null);
    setWfPreviewLoading(true);
    // Fetch the most recent SUCCESSFUL run so we can show the final
    // output at the end of the preview. Best-effort -- if no successful
    // run exists yet, the modal renders the walkthrough + a "no past
    // results yet" hint.
    try {
      const r = await fetch("/api/workflows/" + w.id + "/runs?status=success&limit=1", { headers: { "Authorization": "Bearer " + authToken } });
      if (r.ok) {
        const runs = await r.json();
        const latest = Array.isArray(runs) && runs[0];
        if (latest) {
          const r2 = await fetch("/api/workflow_runs/" + latest.id, { headers: { "Authorization": "Bearer " + authToken } });
          if (r2.ok) setWfPreviewRun(await r2.json());
        }
      }
    } catch {}
    setWfPreviewLoading(false);
    // Kick off the walkthrough on the next tick.
    setWfPreviewStep(0);
  }
  function wfClosePreview() {
    setWfPreviewing(null); setWfPreviewStep(-1); setWfPreviewRun(null);
  }
  // Walkthrough timer: advance step every 750ms while the preview
  // is open and not finished. After the last node, settle 1.5s
  // then mark complete.
  useEffect(() => {
    if (!wfPreviewing) return undefined;
    if (wfPreviewStep < 0) return undefined;
    const nodes = (wfPreviewing.definition && Array.isArray(wfPreviewing.definition.nodes)) ? wfPreviewing.definition.nodes : [];
    const order = _wfPreviewTopo(nodes, (wfPreviewing.definition && wfPreviewing.definition.edges) || []);
    if (wfPreviewStep >= order.length) return undefined;
    const tid = setTimeout(() => setWfPreviewStep(s => s + 1), 750);
    return () => clearTimeout(tid);
  }, [wfPreviewing, wfPreviewStep]);
  const refetchServerAnalytics = useCallback(async (tokenOverride) => {
    const tok = tokenOverride || authToken;
    if (!tok) return;
    try {
      const rA = await fetch("/api/admin/analytics", { headers: { "Authorization": `Bearer ${tok}` }});
      if (!rA.ok) return;
      const serverLog = await rA.json();
      if (!Array.isArray(serverLog)) return;
      const normalize = (e) => ({
        ...e,
        prompt: typeof e.prompt === "string" ? e.prompt : (e.prompt?.__text || ""),
        completion: typeof e.completion === "string" ? e.completion : (e.completion?.__text || String(e.completion || "")),
      });
	      // Server-side rows are already scoped by /api/admin/analytics.
	      // Replace the local cache instead of merging prior-browser entries,
	      // otherwise switching tenants on one machine can show old prompt logs.
	      setAlog(serverLog.map(normalize).sort((a,b)=>(a.ts||0)-(b.ts||0)));
	      setAnalyticsLastSyncAt(Date.now());
	    } catch {}
	  }, [authToken]);

  // Analytics page sync: 60s polling effect, scoped to the
  // Analytics page only. Previously this fired from App scope
  // unconditionally, so every 60s setAlog re-rendered App and
  // remounted EVERY inline-defined page (Workflows, Workspaces,
  // Agents, etc.). Gating on `section === "analytics"` means the
  // poll only runs while the user is actually viewing Analytics --
  // navigate away and the interval clears + no more App churn.
  //
  // Also fire an immediate refetch on entry so the user sees fresh
  // data the moment they click into Analytics, not 60s later.
  useEffect(() => {
    if (!serverMode || !authToken) return undefined;
    if (section !== "analytics") return undefined;
    const role = authUser && authUser.role;
    if (role !== "admin" && role !== "superadmin") return undefined;
    refetchServerAnalytics();
    const tid = setInterval(() => { refetchServerAnalytics(); }, 60_000);
    return () => clearInterval(tid);
  }, [section, serverMode, authToken, authUser && authUser.role, refetchServerAnalytics]);

  // Helper: save model overrides to server (owner only)
  const saveModelOverridesToServer = useCallback(async (fullObj) => {
    if (!serverMode || !authToken || authUser?.role !== "superadmin") return;
    try {
      await fetch("/api/models", {
        method: "PUT",
        headers: {"Content-Type":"application/json","Authorization":`Bearer ${authToken}`},
        body: JSON.stringify(fullObj)
      });
    } catch {}
  }, [serverMode, authToken, authUser]);

  const modelVisibleToNonOwners = useCallback((modelId, opts = {}) => {
    if (!modelId) return false;
    if (modelOverrides?.[modelId]?.enabled === false) return false;
    if (!opts.isCustom && Array.isArray(orgAllowedModels) && orgAllowedModels.length > 0 && !orgAllowedModels.includes(modelId)) return false;
    return true;
  }, [modelOverrides, orgAllowedModels]);

  // Helper: save branding to server (admin only)
	  const saveBrandingToServer = useCallback(async (updates) => {
	    brandLocalEditUntilRef.current = Date.now() + 4000;
	    if (!serverMode || !authToken || !(authUser?.role === "admin" || authUser?.role === "superadmin")) return;
    try {
      await fetch("/api/branding", {
        method: "PUT",
        headers: {"Content-Type":"application/json","Authorization":`Bearer ${authToken}`},
        body: JSON.stringify(updates)
      });
	    } catch {}
	  }, [serverMode, authToken, authUser]);

	  function applyBrandingFromServer(b) {
	    if (!b || typeof b !== "object") return;
	    if (Date.now() < brandLocalEditUntilRef.current) return;
	    const nextLogo = b.logo !== undefined && b.logo !== null ? b.logo : null;
	    const nextLogoDark = b.logoDark !== undefined && b.logoDark !== null ? b.logoDark : null;
	    if (nextLogo) {
	      setBrandLogo(nextLogo);
	      localStorage.setItem("cf_brand_logo", nextLogo);
	    } else {
	      setBrandLogo(null);
	      localStorage.removeItem("cf_brand_logo");
	    }
	    if (nextLogoDark) {
	      setBrandLogoDark(nextLogoDark);
	      localStorage.setItem("cf_brand_logo_dark", nextLogoDark);
	    } else {
	      setBrandLogoDark(null);
	      localStorage.removeItem("cf_brand_logo_dark");
	    }
	    const activeIcon = theme === "dark" && nextLogoDark ? nextLogoDark : (nextLogo || nextLogoDark);
	    const fallbackIcon = `/api/icon.png?theme=${theme === "dark" ? "dark" : "light"}&v=chatfort-brand-20260606`;
	    for (const link of Array.from(document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]'))) {
	      link.href = activeIcon || fallbackIcon;
	    }
	    if (b.name) { setBrandName(b.name); sv("cf_brand_name", b.name); }
	    else { setBrandName("ChatFortAI"); sv("cf_brand_name", "ChatFortAI"); }
	    if (b.color) { setBrandAccent(b.color); sv("cf_brand_color", b.color); }
	    else { setBrandAccent(null); localStorage.removeItem("cf_brand_color"); }
	    if (BRAND_FONT_KEYS.has(b.font)) { setBrandFont(b.font); sv("cf_brand_font", b.font); }
	    else { setBrandFont("outfit"); sv("cf_brand_font", "outfit"); }
	  }

  // ── Email verification callback ─────────────────────────────────────────
  // If the user arrives with ?verify=<jwt> (from the welcome email), POST it
  // to the server, strip the query param, and show a toast. Runs independent
  // of auth state — verification links must work even for a signed-out
  // browser that just clicked through.
  useEffect(() => {
    if (window.location.protocol === "file:") return;
    const qs = new URLSearchParams(window.location.search);
    const verifyTok = qs.get("verify");
    if (!verifyTok) return;
    (async () => {
      try {
        const r = await fetch("/api/auth/verify-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: verifyTok }),
        });
        const d = await r.json().catch(() => ({}));
        if (r.ok) {
          showToast("Email verified — thanks!");
          // Refresh authUser so the banner disappears without a reload.
          const tok = localStorage.getItem("cf_auth_token");
          if (tok) {
            fetch("/api/auth/me", { headers: { "Authorization": `Bearer ${tok}` }})
              .then(r2 => r2.ok ? r2.json() : null)
              .then(u => { if (u) setAuthUser(u); })
              .catch(()=>{});
          }
        } else {
          showToast(d.error || "Verification link invalid");
        }
      } catch {}
      // Scrub the token from the URL so a reload doesn't re-submit it.
      qs.delete("verify");
      const next = window.location.pathname + (qs.toString() ? "?" + qs.toString() : "") + window.location.hash;
      window.history.replaceState({}, "", next);
    })();
  }, []);

  // If the user arrives with ?reset=<jwt> (from the reset email), hold the
  // token in state and show the set-new-password panel. Scrub the param so
  // a reload doesn't re-trigger; the token itself is submitted only when
  // the user saves the new password.
  useEffect(() => {
    if (window.location.protocol === "file:") return;
    const qs = new URLSearchParams(window.location.search);
    const tok = qs.get("reset");
    if (!tok) return;
    setResetTok(tok);
    qs.delete("reset");
    const next = window.location.pathname + (qs.toString() ? "?" + qs.toString() : "") + window.location.hash;
    window.history.replaceState({}, "", next);
  }, []);

  // ?org_invite=<token> — stash the token, look it up, scrub the URL.
  useEffect(() => {
    if (window.location.protocol === "file:") return;
    const qs = new URLSearchParams(window.location.search);
    const tok = qs.get("org_invite");
    if (!tok) return;
    setOrgInviteTok(tok);
    fetch(`/api/org-invites/lookup?token=${encodeURIComponent(tok)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setOrgInviteInfo(d || { valid: false }))
      .catch(() => setOrgInviteInfo({ valid: false }));
    qs.delete("org_invite");
    window.history.replaceState({}, "", window.location.pathname + (qs.toString() ? "?" + qs.toString() : "") + window.location.hash);
  }, []);

  // ── Stripe Checkout post-redirect — show a toast + jump to billing page ───
  useEffect(() => {
    if (window.location.protocol === "file:") return;
    const qs = new URLSearchParams(window.location.search);
    const billing = qs.get("billing");
    if (!billing) return;
    if (billing === "success") { showToast("Subscription updated ✓"); setSection("billing"); }
    else if (billing === "canceled") { showToast("Checkout canceled"); setSection("billing"); }
    qs.delete("billing");
    const next = window.location.pathname + (qs.toString() ? "?" + qs.toString() : "") + window.location.hash;
    window.history.replaceState({}, "", next);
  }, []);

  // ── Slice H5: bidirectional sync between #hermes hash and section state ──
  // Lets users deep-link to /#hermes and bookmark a Hermes session. The hash
  // is the public surface; section is internal state. On mount, mirror the
  // hash to section. On hashchange (back/forward), do the same. When section
  // changes to/from 'hermes' through nav clicks, mirror the other way.
  useEffect(() => {
    if (window.location.protocol === "file:") return;
    const onHash = () => {
      const isHermes = window.location.hash === "#hermes" || window.location.hash.startsWith("#hermes/");
      if (isHermes && section !== "hermes") setSection("hermes");
      const isCoWork = window.location.hash === "#cowork" || window.location.hash.startsWith("#cowork/");
      if (isCoWork && section !== "cowork") setSection("cowork");
      const isCli = window.location.hash === "#cli" || window.location.hash.startsWith("#cli/") || window.location.hash === "#terminal" || window.location.hash.startsWith("#terminal/");
      if (isCli && section !== "cli") setSection("cli");
      // Slice H34a: Projects deep-link routing. #projects + #projects/<id>
      // + #projects/<id>/doc/<docId> all land on the Projects section;
      // ProjectsPanel parses the hash internally to pick selectedId/docId.
      const isProjects = window.location.hash === "#projects" || window.location.hash.startsWith("#projects/");
      if (isProjects && section !== "projects") setSection("projects");
      if (window.location.hash === "#guide" && section !== "guide") setSection("guide");
    };
    onHash();
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (window.location.protocol === "file:") return;
    if (section === "hermes" && !window.location.hash.startsWith("#hermes")) {
      window.history.replaceState({}, "", window.location.pathname + window.location.search + "#hermes");
    } else if (section !== "hermes" && window.location.hash.startsWith("#hermes")) {
      window.history.replaceState({}, "", window.location.pathname + window.location.search);
    }
    // Slice H34a: mirror section <-> #projects hash. Don't clobber a
    // deep-link sub-hash like #projects/<id> -- only set the bare
    // "#projects" when arriving from elsewhere and there's no project
    // segment present yet. Leaving the hash when navigating away.
    if (section === "projects" && !window.location.hash.startsWith("#projects")) {
      window.history.replaceState({}, "", window.location.pathname + window.location.search + "#projects");
    } else if (section !== "projects" && window.location.hash.startsWith("#projects")) {
      window.history.replaceState({}, "", window.location.pathname + window.location.search);
    }
    if (section === "guide" && window.location.hash !== "#guide") {
      window.history.replaceState({}, "", window.location.pathname + window.location.search + "#guide");
    } else if (section !== "guide" && window.location.hash === "#guide") {
      window.history.replaceState({}, "", window.location.pathname + window.location.search);
    }
    if (section === "cowork" && !window.location.hash.startsWith("#cowork")) {
      window.history.replaceState({}, "", window.location.pathname + window.location.search + "#cowork");
    } else if (section !== "cowork" && window.location.hash.startsWith("#cowork")) {
      window.history.replaceState({}, "", window.location.pathname + window.location.search);
    }
    if (section === "cli" && !window.location.hash.startsWith("#cli")) {
      window.history.replaceState({}, "", window.location.pathname + window.location.search + "#cli");
    } else if (section !== "cli" && (window.location.hash.startsWith("#cli") || window.location.hash.startsWith("#terminal"))) {
      window.history.replaceState({}, "", window.location.pathname + window.location.search);
    }
  }, [section]);

  // Slice H34b: parse ?accept_invite=<token> on mount and stash it in
  // state. Also fishes the token back out of sessionStorage after the
  // user logs in via the "Go to login" path on the accept-invite page
  // (we stash it there so the post-login redirect can resume the flow).
  const [inviteToken, setInviteToken] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const fromUrl = sp.get("accept_invite");
      if (fromUrl) return fromUrl;
      const stashed = sessionStorage.getItem("cf_pending_invite_token");
      if (stashed) {
        sessionStorage.removeItem("cf_pending_invite_token");
        // Re-stamp the URL so the AcceptInvitePage's clearUrlAndGoto
        // cleanup works uniformly across both arrival paths.
        try {
          const u = new URL(window.location.href);
          u.searchParams.set("accept_invite", stashed);
          window.history.replaceState(null, "", u.toString());
        } catch {}
        return stashed;
      }
    } catch {}
    return null;
  });

  // ── Server mode detection + token validation on mount ─────────────────────
  useEffect(()=>{
    if (window.location.protocol === "file:") return; // local file — skip
    const token = localStorage.getItem("cf_auth_token");
    const applyServerSession = (tokenValue, user) => {
	      if (!tokenValue || !user) return;
	      setAuthToken(tokenValue);
	      setAuthUser(user);
	      fetch("/api/branding", { headers: { "Authorization": `Bearer ${tokenValue}` }})
	        .then(r2 => r2.ok ? r2.json() : null)
	        .then(applyBrandingFromServer)
	        .catch(()=>{});
	      fetch("/api/models", { headers: { "Authorization": `Bearer ${tokenValue}` }})
	        .then(r2 => r2.ok ? r2.json() : null)
	        .then(m => { if (m && typeof m === "object") setModelOverrides(m); })
	        .catch(()=>{});
	      if (user.role === "admin" || user.role === "superadmin") {
        refetchServerAnalytics(tokenValue);
        fetch("/api/admin/users", { headers: { "Authorization": `Bearer ${tokenValue}` }})
          .then(r3 => r3.ok ? r3.json() : null)
          .then(serverUsers => { if (Array.isArray(serverUsers)) setUsers(serverUsers); })
          .catch(()=>{});
      }
    };
    const tryDevSession = () => {
      fetch("/api/auth/dev-session")
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (!d?.token || !d?.user) return;
          localStorage.setItem("cf_auth_token", d.token);
          applyServerSession(d.token, d.user);
        })
        .catch(()=>{});
    };
    // Pull shared model overrides (public) so everyone sees admin's choices
	    fetch("/api/models", { headers: token ? { "Authorization": `Bearer ${token}` } : {} }).then(r => r.ok ? r.json() : null).then(m => {
      if (m && typeof m === "object") setModelOverrides(m);
    }).catch(()=>{});
    // Pull shared branding from server (public endpoint) so every user sees it
	    const brandingHeaders = token ? { "Authorization": `Bearer ${token}` } : {};
	    fetch("/api/branding", { headers: brandingHeaders }).then(r => r.ok ? r.json() : null).then(applyBrandingFromServer).catch(()=>{});
    // /api/config is public — no auth needed, just tells us this is our server
    fetch("/api/config")
      .then(async r => {
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) return; // static file server returning HTML — not our API
        const d = await r.json().catch(()=>({}));
        if (!d?.serverMode) return; // extra safety: must confirm it's our server
        setServerMode(true);
        // Every user gets the same provider set that the admin has configured
        // on the server. We mark each one with key="[server]" as a sentinel
        // so the existing integrations[pid]?.key checks throughout the app
        // treat these as available without exposing the real API keys.
        if (d.providers && typeof d.providers === "object") {
          setIntegrations(prev => {
            const next = { ...prev };
            for (const pid of Object.keys(d.providers)) {
              if (d.providers[pid]) next[pid] = { ...(next[pid]||{}), key: "[server]", managedByServer: true };
            }
            return next;
          });
        }
        // Which models the server can route through a BAA upstream. Used by
        // the model picker to render a 🛡 badge next to eligible choices.
        if (Array.isArray(d.baaModels)) setBaaModels(d.baaModels);
        if (d.baaUpstreams && typeof d.baaUpstreams === "object") setBaaUpstreams(d.baaUpstreams);
        if (!token) {
          tryDevSession();
          return; // no stored token and no dev session → show login screen
        }
        // Validate stored token separately
        fetch("/api/auth/me", { headers: { "Authorization": `Bearer ${token}` }})
          .then(r2 => r2.ok ? r2.json() : null)
          .then(user => {
            if (user) {
              applyServerSession(token, user);
            } else {
              // Token is invalid/expired — clear it and show login screen
              localStorage.removeItem("cf_auth_token");
              setAuthToken(null);
              tryDevSession();
            }
          }).catch(()=>{});
      })
      .catch(()=>{}); // network error → standalone PWA mode
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  async function doLogin(email, password) {
    setLoginForm(p=>({...p, loading:true, err:""}));
    try {
      const r = await fetch("/api/auth/login", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ email, password })
      });
      const d = await r.json();
      // Slice C1d: 402 with seat_limit_reached gets a friendly message
      // instead of dumping the raw JSON. The user can't act on this
      // themselves; their admin has to upgrade.
      if (r.status === 402 && d?.error === "seat_limit_reached") {
        throw new Error(d.message || "Your organization has reached its seat limit. Ask your admin to upgrade in the Billing tab.");
      }
      if (!r.ok) throw new Error(d.error || "Login failed");
      if (d.mfaRequired && d.mfaToken) {
        setMfaChallenge(d.mfaToken);
        setMfaForm({code:"",err:"",loading:false});
        setLoginForm(p=>({...p, loading:false, err:""}));
        return;
      }
      localStorage.setItem("cf_auth_token", d.token);
      setAuthToken(d.token);
      setAuthUser(d.user);
      setLoginForm({email:"",password:"",err:"",loading:false});
      // Re-fetch branding so freshly-logged-in users on new devices get
      // the admin's logo/name/color immediately (not stuck with defaults)
	      fetch("/api/branding", { headers: { "Authorization": `Bearer ${d.token}` }})
	        .then(r2 => r2.ok ? r2.json() : null)
	        .then(applyBrandingFromServer)
	        .catch(()=>{});
	      fetch("/api/models", { headers: { "Authorization": `Bearer ${d.token}` }})
	        .then(r2 => r2.ok ? r2.json() : null)
	        .then(m => { if (m && typeof m === "object") setModelOverrides(m); })
	        .catch(()=>{});
      // Preload user list if admin
      if (d.user?.role === "admin") {
        fetch("/api/admin/users", { headers: { "Authorization": `Bearer ${d.token}` }})
          .then(r2 => r2.ok ? r2.json() : null)
          .then(serverUsers => { if (Array.isArray(serverUsers)) setUsers(serverUsers); })
          .catch(()=>{});
      }
    } catch(e) {
      setLoginForm(p=>({...p, err:e.message, loading:false}));
    }
  }

  async function doSignup(orgName, name, email, password) {
    setSignupForm(p=>({...p, loading:true, err:""}));
    try {
      const r = await fetch("/api/auth/signup", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ orgName, name, email, password })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Signup failed");
	      localStorage.setItem("cf_auth_token", d.token);
	      setAuthToken(d.token);
	      setAuthUser(d.user);
	      fetch("/api/branding", { headers: { "Authorization": `Bearer ${d.token}` }})
	        .then(r2 => r2.ok ? r2.json() : null)
	        .then(applyBrandingFromServer)
	        .catch(()=>{});
	      fetch("/api/models", { headers: { "Authorization": `Bearer ${d.token}` }})
	        .then(r2 => r2.ok ? r2.json() : null)
	        .then(m => { if (m && typeof m === "object") setModelOverrides(m); })
	        .catch(()=>{});
	      setSignupForm({orgName:"",name:"",email:"",password:"",err:"",loading:false});
      setAuthMode("login");
    } catch(e) {
      setSignupForm(p=>({...p, err:e.message, loading:false}));
    }
  }

  function doLogout() {
    localStorage.removeItem("cf_auth_token");
    setAuthToken(null);
    setAuthUser(null);
    // Clear in-memory chats on logout so the next user on this device
    // starts with a blank slate until their server chats load
    setChats([]);
    setChatId(null);
    chatsSyncInit.current = false;
    lastSyncedUserId.current = null;
  }

  // ── Forced password-change state (for users with temp passwords) ─────────
  const [pwChange,setPwChange]=useState({current:"",next:"",confirm:"",err:"",loading:false});
  async function submitPwChange() {
    if (!pwChange.current || !pwChange.next) { setPwChange(p=>({...p,err:"All fields required"})); return; }
    if (pwChange.next.length < 8) { setPwChange(p=>({...p,err:"New password must be at least 8 characters"})); return; }
    if (pwChange.next !== pwChange.confirm) { setPwChange(p=>({...p,err:"Passwords don't match"})); return; }
    setPwChange(p=>({...p,loading:true,err:""}));
    try {
      const r = await fetch("/api/auth/change-password", {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":`Bearer ${authToken}`},
        body: JSON.stringify({ current: pwChange.current, next: pwChange.next })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Change failed");
      // Server revokes other sessions on password change and hands this one
      // a fresh token — adopt it silently.
      if (d.token) { localStorage.setItem("cf_auth_token", d.token); setAuthToken(d.token); }
      setAuthUser(u => u ? { ...u, mustChangePassword: false } : u);
      setPwChange({current:"",next:"",confirm:"",err:"",loading:false});
    } catch(e) {
      setPwChange(p=>({...p,loading:false,err:e.message}));
    }
  }

  // Slice 7-E: showToast accepts either a plain string (existing behavior)
  // or {text, actionLabel, onAction} for a clickable toast (e.g. "Open chat
  // →" after a manual automation run). Clickable toasts get a longer
  // dismiss timeout because they want a click before disappearing.
  const showToast=useCallback((m)=>{
    if (m && typeof m === "object" && m.onAction) {
      setToast(m);
      setTimeout(()=>setToast(""), 6000);
    } else {
      setToast(m);
      setTimeout(()=>setToast(""), 2200);
    }
  },[]);

  // Slice 4: shared file-intake. Called from file picker, drag-drop, and
  // textarea paste so all three paths produce the same chip + payload shape.
  // Each attachment is {id, name, mediaType, size, kind, status, error?,
  // dataUrl?, data?, text?} — the existing server picks `data` for images
  // and `text` for PDFs/text, so the chips' kind/status/dataUrl are purely
  // UI state and don't affect the server payload.
  const ATT_LIMITS = {
    PER_IMAGE_MB: 5,        // hard cap, rejected with toast
    TOTAL_IMAGE_MB: 20,     // batch cap across all image attachments combined
    PER_FILE_MB: 5,         // raw-file cap for PDFs / text before reading
    EXTRACTED_TEXT_MAX: 50000,
  };
  function classifyKind(file) {
    if ((file.type || "").startsWith("image/")) return "image";
    if (file.type === "application/pdf" || /\.pdf$/i.test(file.name)) return "pdf";
    if ((file.type || "").startsWith("text/") || /\.(txt|md|csv|json|xml|yaml|yml|js|ts|jsx|tsx|py|sh|css|html)$/i.test(file.name)) return "text";
    return null;
  }
  async function readImageAsBase64(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const dataUrl = r.result;
        const data = typeof dataUrl === "string" ? dataUrl.split(",")[1] : "";
        resolve({ dataUrl, data });
      };
      r.onerror = () => reject(r.error || new Error("read failed"));
      r.readAsDataURL(file);
    });
  }
  async function extractPdfText(file) {
    // Lazy-load pdfjs from the same CDN the KB importer already uses (line
    // ~8495). The script + worker get cached by the SW after first hit.
    await loadScript("https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/legacy/build/pdf.min.js");
    if (window.pdfjsLib?.GlobalWorkerOptions) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js";
    }
    const buf = await file.arrayBuffer();
    const pdf = await window.pdfjsLib.getDocument({ data: buf }).promise;
    let text = "";
    let stoppedAtPage = pdf.numPages;
    for (let p = 1; p <= pdf.numPages; p++) {
      const page = await pdf.getPage(p);
      const tc = await page.getTextContent();
      text += tc.items.map(it => it.str).join(" ") + "\n";
      if (text.length >= ATT_LIMITS.EXTRACTED_TEXT_MAX) {
        stoppedAtPage = p;
        break;
      }
    }
    if (text.length > ATT_LIMITS.EXTRACTED_TEXT_MAX) {
      const omitted = text.length - ATT_LIMITS.EXTRACTED_TEXT_MAX;
      const omittedPages = pdf.numPages - stoppedAtPage;
      text = text.slice(0, ATT_LIMITS.EXTRACTED_TEXT_MAX)
        + `\n…[truncated, ${omittedPages} more page${omittedPages === 1 ? "" : "s"}, ${omitted.toLocaleString()} chars omitted]`;
    }
    return { text, pages: pdf.numPages };
  }
  async function intakeFiles(rawList) {
    const files = Array.from(rawList || []);
    if (!files.length) return;
    // Pre-validate and synthesize provisional rows before any async work,
    // so chips appear immediately in `reading` state with a spinner.
    const provisional = [];
    let runningImageBytes = attachments
      .filter(a => a.kind === "image" && a.status !== "error")
      .reduce((s, a) => s + (a.size || 0), 0);
    for (const file of files) {
      const kind = classifyKind(file);
      if (!kind) { showToast(`${file.name}: unsupported file type`); continue; }
      if (kind === "image") {
        if (file.size > ATT_LIMITS.PER_IMAGE_MB * 1024 * 1024) {
          showToast(`${file.name}: image too large (max ${ATT_LIMITS.PER_IMAGE_MB} MB)`);
          continue;
        }
        if (runningImageBytes + file.size > ATT_LIMITS.TOTAL_IMAGE_MB * 1024 * 1024) {
          showToast(`Image batch exceeds ${ATT_LIMITS.TOTAL_IMAGE_MB} MB total — drop fewer or smaller images`);
          continue;
        }
        runningImageBytes += file.size;
      } else {
        if (file.size > ATT_LIMITS.PER_FILE_MB * 1024 * 1024) {
          showToast(`${file.name}: file too large (max ${ATT_LIMITS.PER_FILE_MB} MB)`);
          continue;
        }
      }
      provisional.push({
        id: gid(),
        file,
        name: file.name,
        mediaType: file.type || (kind === "pdf" ? "application/pdf" : kind === "text" ? "text/plain" : "application/octet-stream"),
        size: file.size,
        kind,
        status: "reading",
      });
    }
    if (!provisional.length) return;
    setAttachments(prev => [...prev, ...provisional]);
    // Resolve each provisional row in parallel; on completion mutate that
    // single row in the attachments array (functional setState so we don't
    // race with concurrent intakes).
    await Promise.all(provisional.map(async row => {
      try {
        if (row.kind === "image") {
          const { dataUrl, data } = await readImageAsBase64(row.file);
          setAttachments(prev => prev.map(a => a.id === row.id ? { ...a, status: "ready", dataUrl, data } : a));
        } else if (row.kind === "pdf") {
          const t0 = performance.now();
          const { text, pages } = await extractPdfText(row.file);
          const out = (text || "").slice(0, ATT_LIMITS.EXTRACTED_TEXT_MAX + 200); // already truncated above; small safety
          try { console.log(`[chat-attach] pdf "${row.name}" extracted ${out.length} chars from ${pages} pages in ${(performance.now() - t0).toFixed(0)}ms`); } catch {}
          setAttachments(prev => prev.map(a => a.id === row.id ? { ...a, status: "ready", text: out } : a));
        } else if (row.kind === "text") {
          let text = await row.file.text();
          if (text.length > ATT_LIMITS.EXTRACTED_TEXT_MAX) {
            const omitted = text.length - ATT_LIMITS.EXTRACTED_TEXT_MAX;
            text = text.slice(0, ATT_LIMITS.EXTRACTED_TEXT_MAX) + `\n…[truncated, ${omitted.toLocaleString()} chars omitted]`;
          }
          setAttachments(prev => prev.map(a => a.id === row.id ? { ...a, status: "ready", text } : a));
        }
      } catch (e) {
        setAttachments(prev => prev.map(a => a.id === row.id ? { ...a, status: "error", error: e?.message || "read failed" } : a));
        showToast(`Couldn't read ${row.name}: ${e?.message || "error"}`);
      }
    }));
  }
  async function pickFiles(e) {
    await intakeFiles(e.target.files);
    if (e.target) e.target.value = "";
  }
  useEffect(() => {
    if (!serverMode || !authToken) { setGdriveConfig(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/marketplace/gdrive/config", { headers: { "Authorization": `Bearer ${authToken}` } });
        const d = await r.json().catch(() => ({}));
        if (!cancelled) setGdriveConfig(d);
      } catch {
        if (!cancelled) setGdriveConfig({ enabled: false });
      }
    })();
    return () => { cancelled = true; };
  }, [serverMode, authToken]);
  useEffect(() => {
    if (!gdriveConfig?.enabled) { setGdriveApisReady(false); return; }
    let cancelled = false;
    prepareGoogleDriveApis()
      .then(() => { if (!cancelled) setGdriveApisReady(true); })
      .catch(() => { if (!cancelled) setGdriveApisReady(false); });
    return () => { cancelled = true; };
  }, [gdriveConfig?.enabled, gdriveConfig?.clientId, gdriveConfig?.apiKey, gdriveConfig?.appId]);
  const googleDriveTokenKey = () => `cf_google_drive_token:${authUser?.id || "user"}`;
  function prepareGoogleDriveApis() {
    if (typeof window === "undefined") return Promise.reject(new Error("Google Drive is unavailable in this browser"));
    if (window.google?.accounts?.oauth2 && window.google?.picker) {
      setGdriveApisReady(true);
      return Promise.resolve();
    }
    if (!gdrivePrepareRef.current) {
      gdrivePrepareRef.current = Promise.all([loadScript(GOOGLE_GSI_SRC), loadGooglePicker()])
        .then(() => {
          if (!window.google?.accounts?.oauth2) throw new Error("Google sign-in did not load");
          if (!window.google?.picker) throw new Error("Google Picker did not load");
          setGdriveApisReady(true);
        })
        .catch((e) => {
          gdrivePrepareRef.current = null;
          setGdriveApisReady(false);
          throw e;
        });
    }
    return gdrivePrepareRef.current;
  }
  async function requestGoogleDriveToken(cfg) {
    const key = googleDriveTokenKey();
    const requestedScope = cfg.scope || GOOGLE_DRIVE_SCOPE;
    let hadPriorToken = false;
    try {
      const cached = JSON.parse(localStorage.getItem(key) || "null");
      const sameGrant = cached?.clientId === cfg.clientId && cached?.scope === requestedScope && cached?.accessToken;
      hadPriorToken = !!sameGrant;
      if (sameGrant && cached?.expiresAt > Date.now() + 60_000) {
        return cached.accessToken;
      }
    } catch {}
    if (!window.google?.accounts?.oauth2) throw new Error("Google sign-in did not load");
    return await new Promise((resolve, reject) => {
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: cfg.clientId,
        scope: requestedScope,
        include_granted_scopes: true,
        error_callback: (err) => {
          reject(new Error(err?.type === "popup_failed_to_open"
            ? "Google sign-in popup was blocked. Click Google Drive again after the page finishes loading."
            : (err?.message || err?.type || "Google sign-in failed")));
        },
        callback: (resp) => {
          if (!resp || resp.error || !resp.access_token) {
            reject(new Error(resp?.error_description || resp?.error || "Google sign-in failed"));
            return;
          }
          const expiresAt = Date.now() + Math.max(60, Number(resp.expires_in || 3600)) * 1000;
          try { localStorage.setItem(key, JSON.stringify({ clientId: cfg.clientId, scope: requestedScope, accessToken: resp.access_token, expiresAt })); } catch {}
          resolve(resp.access_token);
        },
      });
      tokenClient.requestAccessToken({ prompt: hadPriorToken ? "" : "consent" });
    });
  }
  async function loadGooglePicker() {
    await loadScript(GOOGLE_API_SRC);
    if (!window.gapi?.load) throw new Error("Google Picker did not load");
    await new Promise((resolve, reject) => {
      try { window.gapi.load("picker", { callback: resolve, onerror: reject, timeout: 15000, ontimeout: () => reject(new Error("Google Picker timed out")) }); }
      catch (e) { reject(e); }
    });
    if (!window.google?.picker) throw new Error("Google Picker is unavailable");
  }
  async function pickGoogleDriveDocs(cfg, token) {
    await loadGooglePicker();
    return await new Promise((resolve, reject) => {
      try {
        const view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
          .setIncludeFolders(true)
          .setSelectFolderEnabled(false);
        const builder = new window.google.picker.PickerBuilder()
          .setDeveloperKey(cfg.apiKey)
          .setOAuthToken(token)
          .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
          .addView(view)
          .setCallback((data) => {
            if (data.action === window.google.picker.Action.PICKED) resolve(data.docs || []);
            if (data.action === window.google.picker.Action.CANCEL) resolve([]);
          });
        const appId = cfg.appId || String(cfg.clientId || "").split("-")[0];
        if (appId && typeof builder.setAppId === "function") builder.setAppId(appId);
        const picker = builder.build();
        picker.setVisible(true);
      } catch (e) { reject(e); }
    });
  }
  function gdriveDownloadPlan(doc) {
    const id = encodeURIComponent(doc.id);
    const mime = doc.mimeType || doc.type || "";
    const name = doc.name || doc.title || "Google Drive file";
    if (mime === "application/vnd.google-apps.folder") return null;
    if (mime === "application/vnd.google-apps.document") {
      return { url: `https://www.googleapis.com/drive/v3/files/${id}/export?mimeType=text/plain`, name: /\.txt$/i.test(name) ? name : `${name}.txt`, mime: "text/plain" };
    }
    if (mime === "application/vnd.google-apps.spreadsheet") {
      return { url: `https://www.googleapis.com/drive/v3/files/${id}/export?mimeType=text/csv`, name: /\.csv$/i.test(name) ? name : `${name}.csv`, mime: "text/csv" };
    }
    if (mime === "application/vnd.google-apps.presentation") {
      return { url: `https://www.googleapis.com/drive/v3/files/${id}/export?mimeType=text/plain`, name: /\.txt$/i.test(name) ? name : `${name}.txt`, mime: "text/plain" };
    }
    return { url: `https://www.googleapis.com/drive/v3/files/${id}?alt=media`, name, mime: mime || "application/octet-stream" };
  }
  async function attachGoogleDriveFiles(docs, token) {
    const files = [];
    for (const doc of docs.slice(0, 8)) {
      const plan = gdriveDownloadPlan(doc);
      if (!plan) { showToast(`${doc.name || doc.title || "Folder"}: folders cannot be attached`); continue; }
      const r = await fetch(plan.url, { headers: { "Authorization": `Bearer ${token}` } });
      if (!r.ok) {
        const msg = r.status === 403 ? "not enough Google Drive permission" : `download failed (${r.status})`;
        showToast(`${plan.name}: ${msg}`);
        continue;
      }
      const blob = await r.blob();
      const file = new File([blob], plan.name, { type: plan.mime || blob.type || "application/octet-stream" });
      files.push(file);
    }
    if (!files.length) return;
    await intakeFiles(files);
    showToast(`Attached ${files.length} Google Drive file${files.length === 1 ? "" : "s"}`);
  }
  async function openGoogleDrivePicker() {
    if (gdriveBusy) return;
    setGdriveBusy(true);
    try {
      let cfg = gdriveConfig;
      if (!cfg) {
        const r = await fetch("/api/marketplace/gdrive/config", { headers: { "Authorization": `Bearer ${authToken}` } });
        cfg = await r.json().catch(() => ({}));
        setGdriveConfig(cfg);
        showToast("Google Drive is preparing. Click Google Drive again in a moment.");
        prepareGoogleDriveApis().catch(() => {});
        return;
      }
      if (!cfg?.enabled || !cfg.clientId || !cfg.apiKey) {
        showToast("Google Drive is not configured on this VPS yet.");
        return;
      }
      if (!gdriveApisReady || !window.google?.accounts?.oauth2 || !window.google?.picker) {
        prepareGoogleDriveApis().catch(() => {});
        showToast("Google Drive is preparing. Click Google Drive again in a moment.");
        return;
      }
      const token = await requestGoogleDriveToken(cfg);
      const docs = await pickGoogleDriveDocs(cfg, token);
      if (!docs.length) return;
      showToast(`Importing ${docs.length} Google Drive file${docs.length === 1 ? "" : "s"}…`);
      await attachGoogleDriveFiles(docs, token);
    } catch (e) {
      showToast("Google Drive failed: " + (e?.message || "unknown error"));
    } finally {
      setGdriveBusy(false);
    }
  }
  // Slice 4: clipboard-paste path. Browsers expose pasted images as
  // dataTransfer items with kind === "file"; native text paste leaves no
  // file items so this is a no-op in that case (text falls through to the
  // textarea normally).
  function onComposerPaste(e) {
    const items = e.clipboardData?.items;
    if (!items || !items.length) return;
    const files = [];
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length) {
      e.preventDefault();
      intakeFiles(files);
    }
  }

  function fmtSize(bytes) { return bytes < 1024*1024 ? `${Math.round(bytes/1024)}KB` : `${(bytes/1024/1024).toFixed(1)}MB`; }
  function fileIcon(mt) { if(mt.startsWith("image/")) return "🖼"; if(mt==="application/pdf") return "📄"; if(mt.startsWith("text/")) return "📝"; return "📎"; }
  // Slice 10: scroll plumbing now lives in useStickyBottom (called
  // above). Pill visibility, per-token sticky writes, and user-scroll-up
  // detach are all hook-owned. `scrollBot` is kept as an alias to
  // `jumpToBottom` so the existing call sites in send() / sendVoice()
  // don't have to be touched — both want force-stick semantics anyway
  // (user-initiated, scroll regardless of current position).
  const scrollBot = jumpToBottom;

  const curChat=chats.find(c=>c.id===chatId)||null;
  const defaultAgent=agents.find(a=>a.id==="ag-default")||agents[0]||DA[0];
  const curAg=agents.find(a=>a.id===agId)||defaultAgent;
  const activeProv = selModel?.provider || curAg?.provider || DEFAULT_PROVIDER;
  const activeModel = selModel?.model || curAg?.model || DEFAULT_MODEL;
  const curMod = PROVIDERS[activeProv]?.models?.find(m=>m.id===activeModel) || {name: activeModel};
  const curUser=users.find(u=>u.id===uid)||users[0];
  const selectableAgents=agents.filter(a=>a&&a.id&&a.id!=="ag-default");
  const selectedAgentToolIds=Array.isArray(curAg?.toolIds)?curAg.toolIds.filter(id=>tools.some(t=>t.id===id)):[];
  const selectedAgentKb=curAg?.kbId?knowledgeBases.find(kb=>kb.id===curAg.kbId):null;
  const agentSelectorValue=curAg?.id||"ag-default";
  const agentSelectorLabel=agentSelectorValue==="ag-default"?"No agent":`${curAg?.emoji?curAg.emoji+" ":""}${curAg?.name||"Selected agent"}`;
  const applyChatAgentSelection=useCallback((nextId)=>{
    const next=nextId||"ag-default";
    setAgId(next);
    setSelModel(null);
    if(chatId) setChats(p=>p.map(c=>c.id===chatId?{...c,agentId:next}:c));
    const nextAg=agents.find(a=>a.id===next);
    showToast(next==="ag-default"?"Using no agent":"Using "+(nextAg?.name||"selected agent"));
  },[agents,chatId]);
  const [projectsHermesContext, setProjectsHermesContext] = useState(null);
  const onProjectsHermesSurfaceContext = useCallback((ctx) => {
    setProjectsHermesContext(ctx && typeof ctx === "object" ? ctx : null);
  }, []);
  const getHermesSurfaceContext = useCallback(() => {
    let url = "";
    try { url = window.location.href; } catch {}
    const surface = section || "platform";
    if (surface === "chat") {
      const msgs = Array.isArray(curChat?.messages) ? curChat.messages : [];
      const lastUser = [...msgs].reverse().find(m => m && m.role === "user");
      return {
        surface: "Chat",
        title: curChat?.title || "New Chat",
        detail: `Agent: ${curAg?.name || "Default"}; model: ${activeModel}; messages: ${msgs.length}`,
        recent: typeof lastUser?.content === "string" ? lastUser.content.slice(0, 600) : "",
        url,
      };
    }
    if (surface === "projects" && projectsHermesContext) {
      return {
        ...projectsHermesContext,
        surface: "Projects",
        url,
      };
    }
    const labels = {
      projects: "Projects",
      automations: "Automations",
      workflows: "Workflows",
      cowork: "Hermes Co-work",
      tools: "Tools",
      mcp: "MCP Servers",
      cli: "Code Mode",
      knowledge: "Knowledge",
      guide: "Platform Guide",
      agents: "Agents",
      models: "Models",
      settings: "Settings",
    };
    return {
      surface: labels[surface] || (surface.charAt(0).toUpperCase() + surface.slice(1)),
      title: labels[surface] || "ChatFortAI",
      detail: `Current section: ${surface}`,
      url,
    };
  }, [section, curChat, curAg, activeModel, projectsHermesContext]);

  function newChat(aid){const id=gid();const a=aid||agId;const folderId=getNewChatFolderId();setChats(p=>[{id,title:"New Chat",messages:[],wsId:DEFAULT_CHAT_WS_ID,folderId,agentId:a,created:Date.now()},...p]);setChatId(id);setAgId(a);setSection("chat");setRailChatOpen(true);}

  async function send(ov, opts){
    // editFromIdx (optional): when set, this is an edit-and-resend turn.
    // Drop chat.messages from that index onward (the original user message
    // and everything after it) BEFORE appending the new user/assistant
    // pair. The truncation lives inside send()'s own setChats updater so
    // there's no race between caller-side truncation and our read of
    // chats here. The edited text comes through the normal `ov` parameter.
    const editFromIdx = (opts && typeof opts.editFromIdx === "number") ? opts.editFromIdx : null;
    const text=(ov||textareaRef.current?.value||input).trim();if((!text&&attachments.length===0)||busy)return;
    // Offline guard: keep the text in the composer instead of burning it
    // into a fetch-error bubble; the online listener auto-sends it.
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      _offlinePendingRef.current = text;
      showToast("You're offline — your message will send when you reconnect");
      return;
    }
    // ── /remember slash command — append to "About you" memory, no LLM call.
    // Matches `/remember <text>` or `/remember "<text>"` (single OR double
    // quotes optional). Short-circuits before any chat/turn setup so it
    // also bypasses the model-access check, the streaming pipe, and
    // FEATURE_TOOLS overhead. Auth is required (we hit /api/user/memory).
    if (text && /^\/remember\b/i.test(text) && serverMode && authToken) {
      const m = text.match(/^\/remember\s+["']?(.+?)["']?\s*$/is);
      const snippet = m ? m[1].trim() : "";
      if (textareaRef.current) textareaRef.current.value = "";
      setInput("");
      if (!snippet) {
        showToast("Usage: /remember <something to remember>");
        return;
      }
      let cid = chatId;
      let chat = chats.find(c => c.id === cid);
      if (!chat) {
        cid = gid();
        chat = { id: cid, title: "New Chat", messages: [], wsId: DEFAULT_CHAT_WS_ID, folderId: getNewChatFolderId(), agentId: agId, created: Date.now() };
        setChats(p => [chat, ...p]);
        setChatId(cid);
      }
      try {
        const cur = await fetch("/api/user/memory", { headers: { "Authorization": `Bearer ${authToken}` } }).then(r => r.ok ? r.json() : { memory: "" });
        const prev = (cur.memory || "").trim();
        const next = (prev ? `${prev}\n- ${snippet}` : `- ${snippet}`).slice(0, ABOUT_YOU_MAX);
        const r = await fetch("/api/user/memory", {
          method: "PUT",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
          body: JSON.stringify({ memory: next }),
        });
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          showToast(d.error || "Couldn't save memory");
          return;
        }
        const confirm = {
          id: gid(),
          role: "system_inline",
          kind: "memory_save",
          content: snippet,
          time: Date.now(),
        };
        setChats(p => p.map(c => c.id !== cid ? c : { ...c, messages: [...c.messages, confirm] }));
      } catch (e) {
        showToast("Couldn't save memory: " + e.message);
      }
      return;
    }
    if (textareaRef.current) textareaRef.current.value = "";
    setInput("");
    // Slice 4: gate the send on attachment readiness, strip image bytes when
    // the active model can't see them, and project to the lean shape the
    // server already expects ({name, mediaType, size, data?, text?}). The
    // chip-state fields (kind, status, dataUrl, file, error) stay client-only.
    if (attachments.some(a => a.status === "reading")) {
      showToast("Still reading attachments — try again in a moment");
      return;
    }
    const visionOk = modelSupportsVision(activeProv, activeModel);
    const curAtts = attachments
      .filter(a => a.status !== "error")
      .filter(a => visionOk || a.kind !== "image")
      .map(a => {
        const out = { id: a.id, name: a.name, mediaType: a.mediaType, size: a.size };
        if (a.data) out.data = a.data;
        if (a.text) out.text = a.text;
        return out;
      });
    if (!visionOk && attachments.some(a => a.kind === "image")) {
      showToast("Image stripped — current model can't see images");
    }
    setAttachments([]);
    // Auto-open website preview if message contains a URL
    const urlMatch = text.match(/(https?:\/\/[^\s,)<>\]"']+)/);
    if (urlMatch) setPreviewContent({ url: urlMatch[1] });
    let cid=chatId;let chat=chats.find(c=>c.id===cid);
    if(!chat){cid=gid();chat={id:cid,title:"New Chat",messages:[],wsId:DEFAULT_CHAT_WS_ID,folderId:getNewChatFolderId(),agentId:agId,created:Date.now()};setChats(p=>[chat,...p]);setChatId(cid);}
    const agBase=agents.find(a=>a.id===chat.agentId)||agents[0];
    let sp=agBase?.sysprompt||"";
    const analyticsFolderName = chatFolders.find(f=>f.id===(chat.folderId||"folder-general"))?.name || "General";
    // Apply model override (admin-set per-model customization) — if the
    // selected model has an override, prepend its system prompt
    const resolvedModel = selModel?.model || agBase?.model;
    const modelOv = modelOverrides[resolvedModel] || {};
    if (modelOv.systemPrompt) sp = modelOv.systemPrompt + (sp ? "\n\n" + sp : "");
    // Inject core memory (if user has it enabled) so the AI sees what it knows
    if (memory.enabled && memory.entries.length > 0) {
      sp += "\n\n# What you remember about the user\nThese are facts the user has shared with you over past conversations. Use them naturally when relevant:\n" +
        memory.entries.map(e => `- ${e.text}`).join("\n") +
        "\n\nIf the user shares a new important fact about themselves (their name, preferences, context, goals, etc.) that's worth remembering for future conversations, save it by emitting on its own line: `[REMEMBER: <short fact>]`. Don't say the tag aloud — it'll be filtered from your reply and stored silently.";
    }
    // Append tool hints to system prompt for any enabled tools, the selected
    // agent's assigned tools, and code interpreter.
    const agentToolIdSet = new Set(Array.isArray(agBase?.toolIds) ? agBase.toolIds : []);
    const activeTools = tools.filter(t => enabledToolIds.has(t.id) || agentToolIdSet.has(t.id));
    if (activeTools.length > 0) {
      sp += "\n\n# Available Tools\nYou have access to the following tools. Mention them naturally when relevant:\n" +
        activeTools.map(t => `- ${t.name}${t.desc?": "+t.desc:""}`).join("\n");
    }
    if (codeInterpreter) {
      sp += "\n\n# Code Interpreter\nYou can write and execute code in Python. When the user asks you to compute, analyze, or verify something, write Python in ```python code blocks. The code is executed in the user's environment.";
    }
    const displayText = text || (curAtts.length ? "[Attached: "+curAtts.map(f=>f.name).join(", ")+"]" : "");
    const um={id:gid(),role:"user",content:displayText,files:curAtts.length?curAtts:undefined,time:Date.now()};
    setBusy(true);

    if(multiMode && multiModels.length > 1){
      const compId=gid();
      const slots=multiModels.map(mm=>({id:gid(),model:mm.model,provider:mm.provider,content:"",loading:true}));
      const compMsg={id:compId,role:"comparison",slots,time:Date.now()};
      setChats(p=>p.map(c=>{if(c.id!==cid)return c;const msgs=[...c.messages,um,compMsg];const title=c.messages.filter(m=>m.role==="user").length===0?displayText.slice(0,40)+(displayText.length>40?"...":""):c.title;return{...c,messages:msgs,title};}));
      scrollBot();
      const allM=[...(chats.find(c=>c.id===cid)?.messages||chat.messages).filter(m=>m.role!=="system_inline"),um];
      const multiT0 = Date.now();
      await Promise.all(multiModels.map(async(mm,idx)=>{
        const ag={...agBase,model:mm.model,provider:mm.provider};
        try{
          // Per-slot rAF-throttled streaming. onChunk receives the
          // accumulated text; we coalesce updates inside a single
          // requestAnimationFrame so a 200-token reply does not produce
          // 200 setChats calls per slot.
          let pendingAcc = null, scheduled = false;
          const flush = () => {
            scheduled = false;
            if (pendingAcc == null) return;
            const txt = pendingAcc;
            pendingAcc = null;
            setChats(p=>p.map(c=>c.id!==cid?c:{...c,messages:c.messages.map(m=>m.id!==compId?m:{...m,slots:m.slots.map((s,i)=>i===idx?{...s,content:txt,streaming:true}:s)})}));
          };
          const onChunkSlot = (acc) => {
            pendingAcc = acc;
            if (!scheduled) { scheduled = true; requestAnimationFrame(flush); }
          };
          // callAI may return a string OR { __text, __search } when web search
          // is on — unwrap so we don't try to render an object as React content.
          const raw = await callAI(ag,allM,sp,resolveChatMaxTokens(cfg.maxTokens, displayText),integrations,webSearch,tools,selectedKbId || ag?.kbId || null,onChunkSlot,true);
          const txt = typeof raw === "string" ? raw : raw?.__text || "(No response)";
          const searchMeta = (raw && typeof raw === "object" && raw.__search) ? raw.__search : null;
          setChats(p=>p.map(c=>c.id!==cid?c:{...c,messages:c.messages.map(m=>m.id!==compId?m:{...m,slots:m.slots.map((s,i)=>i===idx?{...s,content:txt,loading:false,streaming:false,search:searchMeta}:s)})}));
          { const entry={id:gid(),ts:Date.now(),orgId:authUser?.orgId||"local",userId:(authUser||curUser)?.id,userName:(authUser||curUser)?.name,userColor:(authUser||curUser)?.color,model:mm.model,agentName:agBase?.name,workspace:analyticsFolderName,prompt:displayText,completion:txt,promptTokens:Math.round(displayText.length/4),completionTokens:Math.round(txt.length/4),totalTokens:Math.round((displayText.length+txt.length)/4),latencyMs:Date.now()-multiT0,status:"ok"}; setAlog(p=>[...p,entry]); postAnalytics(entry); }
        }catch(err){
          setChats(p=>p.map(c=>c.id!==cid?c:{...c,messages:c.messages.map(m=>m.id!==compId?m:{...m,slots:m.slots.map((s,i)=>i===idx?{...s,content:"Error: "+err.message,loading:false,error:true}:s)})}));
        }
      }));
      setBusy(false);scrollBot();
      return;
    }

    let ag=selModel?{...agBase,model:selModel.model,provider:selModel.provider}:agBase;
    // Apply model override's temperature if set (overrides agent default)
    if (modelOv.temperature !== undefined) ag = { ...ag, temperature: modelOv.temperature };
    const effMaxTokens = modelOv.maxTokens !== undefined ? modelOv.maxTokens : resolveChatMaxTokens(cfg.maxTokens, displayText);
    const aid2=gid();const am={id:aid2,role:"assistant",content:"",loading:true,time:Date.now(),provider:ag.provider,model:ag.model};
    // Atomic truncate-and-append for edit-and-resend. When editFromIdx is
    // set, the base for the new messages array is c.messages.slice(0, idx)
    // — drops the original user message at idx and the assistant reply (+
    // anything else) that came after it.
    setChats(p=>p.map(c=>{if(c.id!==cid)return c;const base=(editFromIdx!==null)?c.messages.slice(0,editFromIdx):c.messages;const msgs=[...base,um,am];const title=base.filter(m=>m.role==="user").length===0?displayText.slice(0,40)+(displayText.length>40?"...":""):c.title;return{...c,messages:msgs,title};}));
    scrollBot();
    const t0=Date.now();
    // Hoisted for the catch block. Blocks the late-rAF flush that would
    // otherwise fire AFTER the stream commits to chats and leave the
    // message stuck in pre-wrap mode forever.
    let streamEnded = false;
    try{
      // Build the upstream messages array. If we're in an edit-and-resend
      // turn, slice the captured chat.messages at editFromIdx (everything
      // before the edited message) so we don't read stale chats state
      // from the closure (the setChats above is async and our local
      // `chats` variable hasn't observed it yet).
      const _baseMsgs = (editFromIdx !== null) ? (chat.messages || []).slice(0, editFromIdx) : (chats.find(c=>c.id===cid)?.messages||chat.messages);
      const allM=[..._baseMsgs.filter(m=>m.role!=="system_inline"),um];
      // Imperative streaming — see sendVoice for the rationale. Mount the
      // pre-wrap <div> once, then write textContent directly per chunk so
      // long responses don't pay a React reconciliation cost per token.
      streamTextRef.current = "";
      setStreamingDraft({ mid: aid2 });
      resetThinkingState();
      let pending = null, scheduled = false;
      const flush = () => {
        scheduled = false;
        if (streamEnded || pending == null) return;
        const prev = streamTextRef.current || "";
        const next = pending;
        const el = streamTargetRef.current;
        // Delta-append — see sendVoice for rationale. O(Δ) per flush vs O(n).
        if (el) {
          if (next.length >= prev.length && next.startsWith(prev)) {
            const delta = next.slice(prev.length);
            if (delta) el.appendChild(document.createTextNode(delta));
          } else {
            el.textContent = next;
          }
        }
        streamTextRef.current = next;
        pending = null;
        // Slice 10: sticky-bottom — see useStickyBottom hook. No-op when
        // the user has scrolled up; the viewport stays where they left it.
        stickToBottom();
      };
      const onChunk = (acc) => {
        if (streamEnded) return;
        // First real content token → flip the indicator off via state.
        // See sendVoice() for the short-circuit rationale.
        if (acc) markFirstToken();
        pending = acc;
        if (!scheduled) { scheduled = true; requestAnimationFrame(flush); }
      };
      const onStatus = (phase, data) => {
        // Preserve: capture draft_id from tool_result so DraftReviewCard
        // renders above the assistant bubble (read by other components
        // via pendingDrafts state).
        if (phase === "tool_result" && data && data.tool === "create_draft_email" && data.ok && data.draft_id) {
          setPendingDrafts(p => ({ ...p, [aid2]: { draftId: data.draft_id, preview: data.preview || null } }));
        }
        // fix(chat-chip): wire tool activity strip into the primary
        // text-chat send() path. PR #120 wired this only into
        // sendVoice() because its anchor matched only one of the two
        // structurally identical onStatus closures. This branch
        // mirrors the sendVoice accumulator + adds:
        //   * 50-cap on toolEvents (defensive memory bound)
        //   * Synthetic append when tool_result arrives without a
        //     matching using_tool (orphan resolution -- still shows
        //     the chip so the user can see what happened).
        if (phase === "using_tool" && data && typeof data.tool === "string") {
          const name = data.tool;
          setChats(prev => prev.map(c => c.id !== cid ? c : { ...c, messages: c.messages.map(m => {
            if (m.id !== aid2) return m;
            const next = [...(m.toolEvents || []), { name, status: "running", startedAt: Date.now() }];
            return { ...m, toolEvents: next.length > 50 ? next.slice(-50) : next };
          }) }));
        } else if (phase === "tool_result" && data && typeof data.tool === "string") {
          const name = data.tool;
          const ok = !!data.ok;
          const err = data.error || null;
          setChats(prev => prev.map(c => c.id !== cid ? c : { ...c, messages: c.messages.map(m => {
            if (m.id !== aid2) return m;
            const events = (m.toolEvents || []).slice();
            let patched = false;
            for (let i = events.length - 1; i >= 0; i--) {
              if (events[i].name === name && events[i].status === "running") {
                events[i] = { ...events[i], status: ok ? "done" : "error", endedAt: Date.now(), error: err };
                patched = true;
                break;
              }
            }
            if (!patched) {
              // Defensive: server emitted tool_result without a prior
              // using_tool (out-of-order SSE, or a server-side bug).
              // startedAt === endedAt means the chip shows no duration.
              events.push({ name, status: ok ? "done" : "error", startedAt: Date.now(), endedAt: Date.now(), error: err });
            }
            const capped = events.length > 50 ? events.slice(-50) : events;
            return { ...m, toolEvents: capped };
          }) }));
        }
        applyStatusFrame(phase, data);
      };
      // Slice 8: server emits an `event: redacted` SSE frame as soon as it
      // has tokenised the request (well before the upstream returns its
      // first content token). We attach the per-message summary so the chip
      // paints immediately and, when persistMasked is on, replace the user
      // message's content with the masked version so a chat reload shows
      // ⟨SSN_1⟩ instead of the original PHI.
      const onRedacted = (evt) => {
        if (!evt) return;
        const perMessage = Array.isArray(evt.perMessage) ? evt.perMessage : [];
        if (!perMessage.length) return;
        const persistMasked = !!evt.persistMasked;
        const byId = new Map();
        for (const pm of perMessage) if (pm && pm.id) byId.set(pm.id, pm);
        setChats(p => p.map(c => {
          if (c.id !== cid) return c;
          return {
            ...c,
            messages: c.messages.map(m => {
              if (m.role !== "user") return m;
              const pm = byId.get(m.id);
              if (!pm) return m;
              const next = { ...m, redactionSummary: pm.summary };
              if (persistMasked && typeof pm.masked === "string") next.content = pm.masked;
              return next;
            }),
          };
        }));
      };
      const voiceWeb = webSearchRef.current || voiceNeedsWeb(text);
      if (voiceWeb && !webSearchRef.current) { webSearchRef.current = true; setWebSearch(true); }
      let raw = await callAI(ag,allM,sp,effMaxTokens,integrations,voiceWeb,tools,selectedKbId || ag?.kbId || null,onChunk,false,onStatus,onRedacted);
      streamEnded = true;
      let txt = typeof raw === "string" ? raw : raw.__text;
      const searchMeta = (raw && typeof raw === "object" && raw.__search) ? raw.__search : null;
      const traceMeta = (raw && typeof raw === "object" && raw.__trace) ? raw.__trace : null;
      // Extract [REMEMBER: ...] tags before display and save them silently
      if (memory.enabled) {
        const memRe = /\[REMEMBER:\s*([^\]]+)\]/gi;
        const found = [];
        let mm; while ((mm = memRe.exec(txt)) !== null) { const f = mm[1].trim(); if (f) found.push(f); }
        if (found.length > 0) {
          txt = txt.replace(memRe, "").replace(/\n{3,}/g, "\n\n").trim();
          for (const f of found) addMemory(f);
        }
      }
      const kbMeta = (raw && typeof raw === "object" && raw.__kbSources) ? raw.__kbSources : null;
      setChats(p=>p.map(c=>c.id!==cid?c:{...c,messages:c.messages.map(m=>m.id===aid2?{...m,content:txt,loading:false,search:searchMeta,trace:traceMeta,kbSources:kbMeta}:m)}));
      setStreamingDraft(null);
      { const entry={id:gid(),ts:Date.now(),orgId:authUser?.orgId||"local",userId:(authUser||curUser)?.id,userName:(authUser||curUser)?.name,userColor:(authUser||curUser)?.color,model:ag?.model,agentName:ag?.name,workspace:analyticsFolderName,prompt:displayText,completion:txt,promptTokens:Math.round(displayText.length/4),completionTokens:Math.round(txt.length/4),totalTokens:Math.round((displayText.length+txt.length)/4),latencyMs:Date.now()-t0,status:"ok"}; setAlog(p=>[...p,entry]); postAnalytics(entry); }
    }catch(err){
      streamEnded = true;
      setChats(p=>p.map(c=>c.id!==cid?c:{...c,messages:c.messages.map(m=>m.id===aid2?{...m,content:"Error: "+err.message,loading:false,error:true}:m)}));
      setStreamingDraft(null);
    }
    setBusy(false);
  }
  // Live ref so the offline-reconnect effect always calls the CURRENT send
  // closure (send is re-declared each App render; a []-dep effect would
  // otherwise capture a stale one). Same pattern as chatPageRef.
  const sendRef = useRef(null);
  sendRef.current = send;


  // ── Preview Panel ──────────────────────────────────────────────────────────
  function PreviewPanel() {
    const [pvTab, setPvTab] = useState("preview");
    const [copied, setCopied] = useState(false);
    // Slice 6: spinner state for the Download all button. JSZip cold-load
    // can take ~150ms on a fast connection, longer on flaky networks; this
    // disables the button so impatient users don't double-click and get
    // two downloads.
    const [zipBusy, setZipBusy] = useState(false);

    // Slice 7-A: resizable artifact panel. Width is persisted to
    // localStorage under cf_artifact_panel_width and clamped to
    // [MIN_W, viewport - 480] so the chat column always has ≥480px to
    // breathe. Default bumped 660 → 720. The panel state lives in
    // PreviewPanel because PreviewPanel mounts/unmounts as previewContent
    // toggles — re-reading from localStorage on each mount is the right
    // shape (cheap, no stale-state-after-resize-event bugs).
    const PANEL_STORAGE_KEY = "cf_artifact_panel_width";
    const PANEL_DEFAULT_W = 720;
    const PANEL_MIN_W = 360;
    const computeMaxW = () => (typeof window !== "undefined")
      ? Math.max(PANEL_MIN_W, Math.min(1200, window.innerWidth - 480))
      : PANEL_DEFAULT_W;
    const [panelWidth, setPanelWidth] = useState(() => {
      try {
        const raw = parseInt(localStorage.getItem(PANEL_STORAGE_KEY) || "", 10);
        if (Number.isFinite(raw) && raw >= PANEL_MIN_W) return Math.min(raw, computeMaxW());
      } catch (_) {}
      return PANEL_DEFAULT_W;
    });
    const [isMaximized, setIsMaximized] = useState(false);
    // Re-clamp on viewport resize so the panel can't out-grow the chat
    // column when the user shrinks the window. We use a tick state so
    // computeMaxW() at render time picks up the new innerWidth.
    const [, setResizeTick] = useState(0);
    useEffect(() => {
      function onResize() { setResizeTick(t => t + 1); }
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }, []);
    const dragStateRef = useRef(null);

    function onHandlePointerDown(e) {
      // Skip on mobile — the panel is fixed-position fullscreen there
      // (the .cf-preview media query overrides flex with !important), so
      // resizing is meaningless.
      if (typeof window !== "undefined" && window.innerWidth <= 768) return;
      e.preventDefault();
      try { e.target.setPointerCapture(e.pointerId); } catch (_) {}
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      // Iframes capture mousemove inside their document — disable pointer
      // events on every preview iframe for the duration of the drag so
      // our onPointerMove on the handle keeps firing across the whole
      // viewport.
      try {
        document.querySelectorAll(".cf-preview iframe").forEach(i => { i.style.pointerEvents = "none"; });
      } catch (_) {}
      dragStateRef.current = { startX: e.clientX, startW: panelWidth };
    }
    function onHandlePointerMove(e) {
      if (!dragStateRef.current) return;
      const next = Math.max(PANEL_MIN_W, Math.min(computeMaxW(), window.innerWidth - e.clientX));
      setPanelWidth(next);
    }
    function onHandlePointerUp(e) {
      if (!dragStateRef.current) return;
      try { e.target.releasePointerCapture?.(e.pointerId); } catch (_) {}
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      try {
        document.querySelectorAll(".cf-preview iframe").forEach(i => { i.style.pointerEvents = ""; });
      } catch (_) {}
      try { localStorage.setItem(PANEL_STORAGE_KEY, String(panelWidth)); } catch (_) {}
      dragStateRef.current = null;
    }
    function onHandleDoubleClick() {
      setPanelWidth(PANEL_DEFAULT_W);
      try { localStorage.removeItem(PANEL_STORAGE_KEY); } catch (_) {}
    }

    const _maxW = computeMaxW();
    const effectiveWidth = isMaximized ? _maxW : Math.max(PANEL_MIN_W, Math.min(panelWidth, _maxW));

    // Resize handle JSX is the same for both URL and code/HTML branches —
    // build it once.
    const resizeHandle = (
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panel"
        title="Drag to resize · Double-click to reset"
        onPointerDown={onHandlePointerDown}
        onPointerMove={onHandlePointerMove}
        onPointerUp={onHandlePointerUp}
        onPointerCancel={onHandlePointerUp}
        onDoubleClick={onHandleDoubleClick}
        onMouseEnter={e => { e.currentTarget.style.background = `${S.accent}40`; }}
        onMouseLeave={e => { if (!dragStateRef.current) e.currentTarget.style.background = "transparent"; }}
        style={{
          position: "absolute",
          left: 0, top: 0, bottom: 0,
          width: 6,
          cursor: "col-resize",
          zIndex: 2,
          background: "transparent",
          transition: "background 0.15s",
          touchAction: "none",
        }}
      />
    );

    const isUrl = !!previewContent.url;

    // ── URL / website preview ──────────────────────────────────────────────
    if (isUrl) {
      const { url } = previewContent;
      const displayUrl = url.replace(/^https?:\/\//, "").replace(/\/$/, "");
      return (
        <div className="cf-preview" style={{flex:`0 0 ${effectiveWidth}px`,minWidth:PANEL_MIN_W,maxWidth:_maxW,borderLeft:`1px solid ${S.border}`,display:"flex",flexDirection:"column",overflow:"hidden",background:S.bg,position:"relative"}}>
          {resizeHandle}
          {/* Header */}
          <div style={{height:46,display:"flex",alignItems:"center",padding:"0 10px 0 14px",borderBottom:`1px solid ${S.border}`,gap:6,flexShrink:0,background:S.surface}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={S.accent} strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            <span style={{fontSize:12,fontWeight:700,color:S.text}}>Preview</span>
            <span style={{fontSize:10,color:S.text3,fontFamily:"monospace",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:200}} title={url}>{displayUrl}</span>
            <div style={{marginLeft:"auto",display:"flex",gap:4}}>
              <a href={url} target="_blank" rel="noreferrer" title="Open in new tab"
                style={{width:26,height:26,borderRadius:6,border:`1px solid ${S.border2}`,background:"transparent",color:S.text3,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",textDecoration:"none"}}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              </a>
              <button onClick={()=>setPreviewContent(null)} title="Close preview"
                style={{width:26,height:26,borderRadius:6,border:`1px solid ${S.border2}`,background:"transparent",color:S.text3,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,lineHeight:1}}>
                ×
              </button>
            </div>
          </div>
          {/* Website iframe */}
          <div style={{flex:1,position:"relative",background:S.surface2}}>
            <iframe
              key={url}
              src={url}
              style={{position:"absolute",inset:0,width:"100%",height:"100%",border:"none"}}
              title="Website Preview"
              referrerPolicy="no-referrer"
            />
            {/* Fallback hint — shown behind the iframe, visible only if iframe fails to load */}
            <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12,pointerEvents:"none"}}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
              <span style={{fontSize:12,color:S.text3,textAlign:"center",maxWidth:240,lineHeight:1.5}}>
                This site blocks previews.<br/>Use the ↗ button to open it in a new tab.
              </span>
            </div>
          </div>
        </div>
      );
    }

    // ── Code / HTML preview ────────────────────────────────────────────────
    const {html, lang, filename, siblings, messageText} = previewContent;

    // Slice 5: when the previewed file is HTML, inline any sibling CSS / JS
    // referenced by relative <link>/<script src>. This is what makes a
    // multi-file project actually render styled inside the iframe — about:
    // srcdoc has no notion of "sibling files", so relative refs would 404.
    // External CDN URLs are skipped by inlineSiblings so Google Fonts etc.
    // still load. The single-file CSS / JS branches use enriched scaffolds
    // so users see styles applied / console output without writing scaffold
    // markup themselves.
    const buildDoc = () => {
      const l = (lang || "").toLowerCase();
      if (l === "css") return buildCssPreviewScaffold(html);
      if (l === "javascript" || l === "js") return buildJsPreviewScaffold(html);
      if (l === "svg") {
        // Center + scale the SVG to the panel. A raw <svg> renders fine
        // inline; the wrapper just makes it responsive and gives a
        // neutral checkerboard-free background.
        return `<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>html,body{margin:0;height:100%;}body{display:flex;align-items:center;justify-content:center;background:#fff;padding:16px;box-sizing:border-box;}svg{max-width:100%;max-height:100%;height:auto;width:auto;display:block;}</style></head><body>${html || ""}</body></html>`;
      }
      // HTML branches — sibling-aware. We rewrite first, then decide whether
      // to wrap; rewrites work the same on full docs and snippets.
      let src = html || "";
      if (siblings && Object.keys(siblings).length) {
        // Don't inline the file currently being previewed into itself.
        const filtered = { ...siblings };
        if (filename) delete filtered[filename];
        src = inlineSiblings(src, filtered);
      }
      if (src.trimStart().startsWith("<!") || src.trimStart().startsWith("<html")) return src;
      // HTML snippet — wrap in scaffold
      return `<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;font-family:system-ui,sans-serif;padding:16px}</style></head><body>${src}</body></html>`;
    };

    const doc = buildDoc();

    const copyCode = () => {
      navigator.clipboard?.writeText(html);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };

    const openNew = () => {
      const blob = new Blob([doc], {type:"text/html"});
      window.open(URL.createObjectURL(blob), "_blank");
    };

    // Slice 6: download the currently-previewed file as-is (un-inlined).
    // Filename comes from the slice-5 heading-paired metadata; lang is the
    // fallback for blocks that didn't have a heading. Mime is per-extension
    // so OSes pick the right associated app on save.
    const downloadFilename = filename || `untitled.${(lang || "txt").toLowerCase()}`;
    const downloadOne = () => {
      try {
        const blob = new Blob([html ?? ""], { type: mimeFor(downloadFilename) });
        downloadBlob(blob, downloadFilename);
      } catch (e) {
        showToast("Download failed: " + (e?.message || "unknown error"));
      }
    };

    // Slice 6: bundle the previewed file + all its siblings into a single
    // zip and trigger download. Lazy-loads JSZip on first use; the loader
    // caches via window.JSZip so subsequent zips skip the network. Errors
    // (offline / blocked CDN) surface as a toast and re-enable the button.
    const hasSiblings = !!(siblings && Object.keys(siblings).length > 0);
    const downloadAll = async () => {
      if (zipBusy) return;
      setZipBusy(true);
      const t0 = performance.now();
      try {
        const JSZipCtor = await loadJSZip();
        // Build the file map: previewed file + all siblings, but de-dup by
        // filename so we don't emit two entries if the previewed file is
        // already in the siblings dict (it shouldn't be — slice-5 excludes
        // self — but defense-in-depth).
        const allFiles = { ...(siblings || {}) };
        if (filename) allFiles[filename] = html ?? "";
        const folderName = inferProjectName(messageText, allFiles);
        const zip = new JSZipCtor();
        const folder = zip.folder(folderName);
        let totalRaw = 0;
        let fileCount = 0;
        for (const [name, code] of Object.entries(allFiles)) {
          const body = code ?? "";
          folder.file(name, body);
          totalRaw += (typeof body === "string") ? body.length : 0;
          fileCount += 1;
        }
        const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
        const compressedKB = (blob.size / 1024).toFixed(1);
        const totalRawKB = (totalRaw / 1024).toFixed(1);
        const elapsed = Math.round(performance.now() - t0);
        const zipName = `${folderName}.zip`;
        downloadBlob(blob, zipName);
        try { console.log(`[chat-zip] "${zipName}" — ${fileCount} files, ${compressedKB} KB (${totalRawKB} KB raw) in ${elapsed}ms`); } catch {}
      } catch (e) {
        showToast("Couldn't load zip library. Try again or check your connection.");
        try { console.warn("[chat-zip] failed:", e?.message || e); } catch {}
      } finally {
        setZipBusy(false);
      }
    };

    const BtnT = ({id, label}) => (
      <button onClick={()=>setPvTab(id)} style={{padding:"4px 12px",borderRadius:6,border:"none",background:pvTab===id?S.accentDim:"transparent",color:pvTab===id?S.accent:S.text3,fontSize:11,fontWeight:pvTab===id?700:400,cursor:"pointer",transition:"all 0.15s"}}>
        {label}
      </button>
    );

    return (
      <div className="cf-preview" style={{flex:`0 0 ${effectiveWidth}px`,minWidth:PANEL_MIN_W,maxWidth:_maxW,borderLeft:`1px solid ${S.border}`,display:"flex",flexDirection:"column",overflow:"hidden",background:S.bg,position:"relative"}}>
        {resizeHandle}
        {/* Header */}
        <div style={{height:46,display:"flex",alignItems:"center",padding:"0 10px 0 14px",borderBottom:`1px solid ${S.border}`,gap:6,flexShrink:0,background:S.surface}}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={S.accent} strokeWidth="2.5"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          <span style={{fontSize:12,fontWeight:700,color:S.text}}>Preview</span>
          <span title={filename || lang} style={{fontSize:9,padding:"1px 7px",borderRadius:20,background:S.accentDim,color:S.accent,fontFamily:"monospace",marginRight:4,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{filename || lang}</span>
          {/* Tabs */}
          <div style={{display:"flex",gap:2,background:S.surface2,borderRadius:7,padding:2}}>
            <BtnT id="preview" label="Preview"/>
            <BtnT id="code" label="Code"/>
          </div>
          {/* Actions */}
          <div style={{marginLeft:"auto",display:"flex",gap:4}}>
            <button onClick={()=>setPreviewContent(null)} title="Exit preview"
              aria-label="Exit preview"
              style={{width:26,height:26,borderRadius:6,border:`1px solid ${S.border2}`,background:"transparent",color:S.text3,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,lineHeight:1}}>
              ×
            </button>
            <button onClick={copyCode} title="Copy code"
              style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${S.border2}`,background:copied?S.accentDim:"transparent",color:copied?S.accent:S.text3,fontSize:10,cursor:"pointer",fontFamily:"monospace",transition:"all 0.15s"}}>
              {copied?"Copied!":"Copy"}
            </button>
            {/* Slice 6: download single file (un-inlined raw source) */}
            <button onClick={downloadOne}
              title={`Download ${downloadFilename}`}
              aria-label={`Download ${downloadFilename}`}
              style={{width:26,height:26,borderRadius:6,border:`1px solid ${S.border2}`,background:"transparent",color:S.text3,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            </button>
            {/* Slice 6: download all (current + siblings) as a zip. Hidden when no siblings. */}
            {hasSiblings && (
              <button onClick={downloadAll}
                disabled={zipBusy}
                title="Download all files as zip"
                aria-label="Download all files as zip"
                style={{width:26,height:26,borderRadius:6,border:`1px solid ${S.border2}`,background:zipBusy?S.accentDim:"transparent",color:zipBusy?S.accent:S.text3,cursor:zipBusy?"wait":"pointer",display:"flex",alignItems:"center",justifyContent:"center",opacity:zipBusy?0.85:1}}>
                {zipBusy
                  ? <span style={{width:11,height:11,border:`2px solid ${S.text3}55`,borderTopColor:S.accent,borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/>
                  : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/><polyline points="9 13 12 16 15 13"/><line x1="12" y1="10" x2="12" y2="16"/></svg>}
              </button>
            )}
            <button onClick={openNew} title="Open in new tab"
              style={{width:26,height:26,borderRadius:6,border:`1px solid ${S.border2}`,background:"transparent",color:S.text3,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            </button>
            {/* Slice 7-A: Maximize / Restore. Maximized state expands to
                Math.min(1200, viewport - 480) without persisting; restoring
                returns to the user's last dragged width. */}
            <button onClick={() => setIsMaximized(v => !v)}
              title={isMaximized ? "Restore panel size" : "Maximize panel"}
              aria-label={isMaximized ? "Restore panel size" : "Maximize panel"}
              style={{width:26,height:26,borderRadius:6,border:`1px solid ${S.border2}`,background:isMaximized?S.accentDim:"transparent",color:isMaximized?S.accent:S.text3,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
              {isMaximized
                ? <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6 2v4H2M10 2v4h4M6 14v-4H2M10 14v-4h4"/></svg>
                : <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4"/></svg>}
            </button>
          </div>
        </div>

        {/* Content */}
        {pvTab === "preview"
          ? <iframe
              key={doc}
              srcDoc={doc}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              style={{flex:1,border:"none",background:"#ffffff"}}
              title="Preview"
            />
          : <pre style={{flex:1,overflow:"auto",padding:"14px 16px",margin:0,fontSize:12,fontFamily:"monospace",color:S.text,lineHeight:1.6,background:S.codeBlockBg}}>
              {html}
            </pre>
        }
      </div>
    );
  }

  // InputBar lives at App scope so both ChatPage and the standalone input can use it
  const InputBar = ({controlled=false}) => (
      <div style={{ width: "100%", maxWidth: 720 }}>
        <input ref={fileInputRef} type="file" multiple
          accept="image/*,.pdf,.txt,.md,.csv,.json,.xml,.yaml,.yml,.js,.ts,.jsx,.tsx,.py,.sh,.css,.html"
          onChange={pickFiles} style={{ display: "none" }} />
        <input ref={captureInputRef} type="file" accept="image/*" capture="environment"
          onChange={pickFiles} style={{ display: "none" }} />
        <div className="cf-input-shell" style={{
          background: S.inputBg,
          border: `1px solid ${dictating || attachments.length > 0 ? S.accent : S.border2}`,
          borderRadius: 20, overflow: "hidden",
          boxShadow: theme === "light" ? "0 2px 16px rgba(0,0,0,0.07)" : "0 2px 16px rgba(0,0,0,0.25)",
          transition: "all 0.2s"
        }}>
          {/* Slice 4: attachment chip row. One chip per file with kind-aware
              icon (image thumb, PDF / text emoji), filename truncated to 28
              chars (full name on hover via title), size label, status spinner
              while reading, red border on error, ✕ remove. Plus a yellow
              warning chip when there's an image attached and the active
              model can't see images. */}
          {attachments.length > 0 && (() => {
            const visionOk = modelSupportsVision(activeProv, activeModel);
            const hasImg = attachments.some(a => a.kind === "image");
            return (
              <div style={{ padding: "10px 14px 0", display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                {attachments.map(f => {
                  const isImg = f.kind === "image" || (f.mediaType || "").startsWith("image/");
                  const isPdf = f.kind === "pdf" || f.mediaType === "application/pdf";
                  const reading = f.status === "reading";
                  const errored = f.status === "error";
                  const shortName = f.name && f.name.length > 28 ? f.name.slice(0, 25) + "…" : (f.name || "");
                  return (
                    <div key={f.id} className="cf-attach-chip" title={errored ? `Error: ${f.error || "read failed"}` : (f.name || "")}
                      style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 8px 3px 4px", borderRadius: 20, background: errored ? `${S.danger}18` : S.accentDim, border: `1px solid ${errored ? S.danger : S.border2}`, fontSize: 11, color: errored ? S.danger : S.text2, maxWidth: 220 }}>
                      {isImg && f.dataUrl
                        ? <img src={f.dataUrl} alt="" style={{ width: 22, height: 22, borderRadius: 5, objectFit: "cover", flexShrink: 0 }}/>
                        : <span style={{ width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>{isImg ? "🖼" : isPdf ? "📄" : "📝"}</span>}
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 120 }}>{shortName}</span>
                      <span style={{ fontSize: 9, color: S.text3, fontFamily: "monospace", flexShrink: 0 }}>{fmtSize(f.size || 0)}</span>
                      {reading && (
                        <span style={{ width: 12, height: 12, border: `2px solid ${S.text3}55`, borderTopColor: S.accent, borderRadius: "50%", animation: "spin 0.7s linear infinite", flexShrink: 0 }}/>
                      )}
                      <button onClick={() => setAttachments(p => p.filter(x => x.id !== f.id))} aria-label={`Remove ${f.name}`}
                        style={{ marginLeft: 1, background: "none", border: "none", color: S.text3, cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                    </div>
                  );
                })}
                {hasImg && !visionOk && (
                  <div title="Switch to a vision-capable model (Claude 4.x, GPT-4o) to send images." style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 20, background: `${(S.warning || "#f59e0b")}18`, border: `1px solid ${(S.warning || "#f59e0b")}55`, fontSize: 11, color: S.warning || "#f59e0b" }}>
                    ⚠ Current model can't see images
                  </div>
                )}
              </div>
            );
          })()}
          {/* Multi-model chips */}
          {multiMode && (
            <div style={{ padding: "8px 14px 0", display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
              {multiModels.map((mm, idx) => {
                const pv = PROVIDERS[mm.provider]; const col = pv?.color || S.accent;
                const name = pv?.models?.find(m => m.id === mm.model)?.name || mm.model.split("/").pop();
                return <div key={idx} ref={el=>multiChipRefs.current[idx]=el} style={{ display:"flex",alignItems:"center",gap:3,padding:"2px 4px 2px 8px",borderRadius:20,fontSize:10,fontFamily:"monospace",border:`1px solid ${col}55`,color:col,background:`${col}12` }}>
                  <span style={{width:5,height:5,borderRadius:"50%",background:col,flexShrink:0}}/><span style={{maxWidth:80,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name}</span>
                  <button onClick={()=>setMultiPickerIdx(multiPickerIdx===idx?null:idx)} style={{background:"none",border:"none",color:col,cursor:"pointer",padding:"0 3px",fontSize:10}}>▾</button>
                  <button onClick={()=>setMultiModels(p=>p.filter((_,i)=>i!==idx))} style={{background:"none",border:"none",color:col,cursor:"pointer",padding:"0 2px",fontSize:13}}>×</button>
                </div>;
              })}
              <button onClick={() => {
                // Same expanded list MultiModelPicker uses -- so "+ model"
                // can pick anything the user has access to (live OR
                // catalog + org custom + bundled), not just bundled.
                const liveOR = Array.isArray(liveModelCatalog?.models) ? liveModelCatalog.models : [];
                const catalogHasZdr2 = liveOR.some(m => m?.zdr);
                const filterByZdr2 = liveModelCatalog?.zdrEnforced !== false && catalogHasZdr2;
                const selectableOR2 = filterByZdr2 ? liveOR.filter(m => m?.zdr?.verified) : liveOR;
                const orIds = new Set(selectableOR2.map(m => m.id));
                const avail = [
                  ...Object.entries(PROVIDERS).filter(([pid,p])=>p.builtin||integrations[pid]?.key).flatMap(([pid,p]) => {
                    if (pid === "openrouter") {
                      return [...selectableOR2.map(m => ({ model: m.id, provider: pid })), ...(p.models||[]).filter(m=>orIds.size===0||orIds.has(m.id)).map(m=>({model:m.id,provider:pid}))];
                    }
                    return p.models.filter(m=>modelVisibleToNonOwners(m.id)).map(m=>({model:m.id,provider:pid}));
                  }),
                  ...(orgCustomModels||[]).map(m=>({model:m.id,provider:m.provider})),
                ];
                const seen = new Set();
                const dedup = avail.filter(a => { const k = a.provider+"|"+a.model; if (seen.has(k)) return false; seen.add(k); return true; });
                const next = dedup.find(a => !multiModels.find(m => m.model === a.model && m.provider === a.provider));
                if (next) { setMultiModels(p => [...p, next]); setMultiPickerIdx(multiModels.length); }
              }} style={{padding:"2px 8px",borderRadius:20,fontSize:10,fontFamily:"monospace",border:`1px solid ${S.border2}`,color:S.text3,background:"none",cursor:"pointer"}}>+ model</button>
            </div>
          )}
          {/* Agent selector: choose whether this chat runs as plain ChatFort
              or as one of the configured agents with that agent's prompt,
              default KB, and assigned tools. */}
          <div data-agent-selector style={{padding:"10px 14px 8px",display:"flex",alignItems:"center",gap:7,flexWrap:"wrap"}}>
            <label title="Choose the agent for this chat" style={{position:"relative",display:"inline-flex",alignItems:"center",gap:7,maxWidth:"100%",minWidth:isMobile?172:194,height:30,padding:"0 28px 0 10px",borderRadius:9,border:`1px solid ${agentSelectorValue==="ag-default"?S.border2:(curAg?.color||S.accent)}`,background:agentSelectorValue==="ag-default"?"transparent":`${curAg?.color||S.accent}14`,color:agentSelectorValue==="ag-default"?S.text3:(curAg?.color||S.accent),fontSize:11,fontWeight:700,cursor:"pointer"}}>
              <span aria-hidden="true" style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:15,height:15,flexShrink:0,pointerEvents:"none"}}>{IC.agent}</span>
              <span aria-hidden="true" style={{flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",pointerEvents:"none"}}>{agentSelectorLabel}</span>
              <select aria-label="Chat agent" value={agentSelectorValue} onChange={e=>applyChatAgentSelection(e.target.value)}
                style={{position:"absolute",inset:0,width:"100%",height:"100%",opacity:0,appearance:"none",WebkitAppearance:"none",border:"none",outline:"none",background:"transparent",color:"inherit",fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                <option value="ag-default">No agent</option>
                {selectableAgents.map(a=><option key={a.id} value={a.id}>{a.emoji ? a.emoji+" " : ""}{a.name}</option>)}
              </select>
              <span aria-hidden="true" style={{position:"absolute",right:9,top:"50%",transform:"translateY(-50%)",fontSize:10,color:"inherit",pointerEvents:"none"}}>▾</span>
            </label>
            {agentSelectorValue!=="ag-default" && (
              <div style={{display:"inline-flex",alignItems:"center",gap:5,flexWrap:"wrap",minWidth:0}}>
                {selectedAgentKb && <span title={"Agent default knowledge: "+selectedAgentKb.name} style={{fontSize:10,color:S.text3,border:`1px solid ${S.border2}`,borderRadius:999,padding:"2px 7px",background:S.cardBg,whiteSpace:"nowrap"}}>KB</span>}
                {selectedAgentToolIds.length>0 && <span title={`${selectedAgentToolIds.length} assigned tool${selectedAgentToolIds.length===1?"":"s"}`} style={{fontSize:10,color:S.text3,border:`1px solid ${S.border2}`,borderRadius:999,padding:"2px 7px",background:S.cardBg,whiteSpace:"nowrap"}}>{selectedAgentToolIds.length} tool{selectedAgentToolIds.length===1?"":"s"}</span>}
              </div>
            )}
          </div>
          {/* Textarea */}
          {controlled
            ? <textarea value={input} onChange={e=>setInput(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
                onPaste={onComposerPaste}
                placeholder={dictating?"Listening…":(isMobile ? "How can I help you?" : (((authUser||curUser)?.name) ? `Hi ${(authUser||curUser).name}, how can I help you today?` : "How can I help you today?"))}
                rows={1} style={{width:"100%",background:"none",border:"none",outline:"none",color:S.text,fontFamily:"inherit",fontSize:15,lineHeight:1.6,padding:"14px 16px 8px",resize:"none",minHeight:54,maxHeight:200,display:"block"}}
                onInput={e=>{e.target.style.height="auto";e.target.style.height=Math.min(e.target.scrollHeight,200)+"px";}}/>
            : <textarea ref={textareaRef} defaultValue=""
                onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
                onPaste={onComposerPaste}
                placeholder={dictating?"Listening…":(isMobile ? "How can I help you?" : (((authUser||curUser)?.name) ? `Hi ${(authUser||curUser).name}, how can I help you today?` : "How can I help you today?"))}
                rows={1} style={{width:"100%",background:"none",border:"none",outline:"none",color:S.text,fontFamily:"inherit",fontSize:15,lineHeight:1.6,padding:"14px 16px 8px",resize:"none",minHeight:54,maxHeight:200,display:"block"}}
                onInput={e=>{e.target.style.height="auto";e.target.style.height=Math.min(e.target.scrollHeight,200)+"px";}}/>
          }
          {/* Bottom toolbar */}
          <div style={{ padding: "6px 10px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 8 }}>
            {/* Left: action icons */}
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              {/* Attach — opens a menu with Upload / Capture / Webpage / Notes / Knowledge / Chats / Drive */}
              <div>
                <button ref={plusBtnRef} onClick={openPlusMenu} title="Attach" className="cf-tile-hover"
                  style={{ width: 32, height: 32, background: attachments.length > 0 ? S.accentDim : "none", border: `1px solid ${attachments.length > 0 ? S.accent : S.border2}`, borderRadius: 9, color: attachments.length > 0 ? S.accent : S.text3, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", fontSize: 18, fontWeight: 300, transition: "all 0.15s" }}>
                  +
                  {attachments.length > 0 && <span style={{ position: "absolute", top: 1, right: 1, width: 14, height: 14, borderRadius: "50%", background: S.accent, color: "#fff", fontSize: 8, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{attachments.length}</span>}
                </button>
                {plusMenuOpen && (<>
                  <div onClick={() => { setPlusMenuOpen(false); setPlusSubOpen(null); }} style={{ position: "fixed", inset: 0, zIndex: 99 }}/>
                  <div style={{ position: "fixed", bottom: plusMenuPos.bottom, left: plusMenuPos.left, minWidth: 240, maxWidth: 300, background: S.modalBg || S.cardBg, border: `1px solid ${S.border3 || S.border2}`, borderRadius: 12, padding: 4, zIndex: 100, boxShadow: theme === "dark" ? "0 12px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)" : "0 12px 48px rgba(0,0,0,0.18)" }}>
                    {/* Upload Files */}
                    <div onClick={() => { fileInputRef.current?.click(); setPlusMenuOpen(false); }}
                      style={{ padding: "9px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: S.text, fontSize: 13 }}
                      onMouseEnter={e=>e.currentTarget.style.background=S.hoverBg} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                      <span style={{ flex: 1 }}>Upload Files</span>
                    </div>
                    {/* Capture (camera) */}
                    <div onClick={() => { captureInputRef.current?.click(); setPlusMenuOpen(false); }}
                      style={{ padding: "9px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: S.text, fontSize: 13 }}
                      onMouseEnter={e=>e.currentTarget.style.background=S.hoverBg} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                      <span style={{ flex: 1 }}>Capture</span>
                    </div>
                    {/* Attach Webpage */}
                    <div onClick={() => {
                      const url = window.prompt("Webpage URL");
                      if (!url) { setPlusMenuOpen(false); return; }
                      const trimmed = url.trim();
                      if (!/^https?:\/\//i.test(trimmed)) { showToast("URL must start with http:// or https://"); return; }
                      // Turn on web search so the AI can actually fetch it, and
                      // append the URL to the input so it lands in the prompt.
                      setWebSearch(true);
                      setPreviewContent({ url: trimmed });
                      if (controlled) setInput(p => (p ? p + " " : "") + trimmed);
                      else if (textareaRef.current) textareaRef.current.value = (textareaRef.current.value ? textareaRef.current.value + " " : "") + trimmed;
                      showToast("Web search enabled — send to analyze the page");
                      setPlusMenuOpen(false);
                    }}
                      style={{ padding: "9px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: S.text, fontSize: 13 }}
                      onMouseEnter={e=>e.currentTarget.style.background=S.hoverBg} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                      <span style={{ flex: 1 }}>Attach Webpage</span>
                    </div>
                    {/* Attach Notes (core memory) */}
                    <div onClick={() => setPlusSubOpen(p => p === "notes" ? null : "notes")}
                      style={{ padding: "9px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: S.text, fontSize: 13, background: plusSubOpen === "notes" ? S.inputBg : "none" }}
                      onMouseEnter={e=>{ if (plusSubOpen !== "notes") e.currentTarget.style.background=S.hoverBg; }} onMouseLeave={e=>{ if (plusSubOpen !== "notes") e.currentTarget.style.background="none"; }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/></svg>
                      <span style={{ flex: 1 }}>Attach Notes</span>
                      <span style={{ fontSize: 11, color: S.text3, fontFamily: "monospace" }}>{memory.entries?.length || 0}</span>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                    </div>
                    {/* Attach Knowledge */}
                    <div onClick={() => setPlusSubOpen(p => p === "knowledge" ? null : "knowledge")}
                      style={{ padding: "9px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: S.text, fontSize: 13, background: plusSubOpen === "knowledge" ? S.inputBg : "none" }}
                      onMouseEnter={e=>{ if (plusSubOpen !== "knowledge") e.currentTarget.style.background=S.hoverBg; }} onMouseLeave={e=>{ if (plusSubOpen !== "knowledge") e.currentTarget.style.background="none"; }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                      <span style={{ flex: 1 }}>Attach Knowledge</span>
                      <span style={{ fontSize: 11, color: S.text3, fontFamily: "monospace" }}>{knowledgeBases.length}</span>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                    </div>
                    {/* Reference Chats */}
                    <div onClick={() => setPlusSubOpen(p => p === "chats" ? null : "chats")}
                      style={{ padding: "9px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: S.text, fontSize: 13, background: plusSubOpen === "chats" ? S.inputBg : "none" }}
                      onMouseEnter={e=>{ if (plusSubOpen !== "chats") e.currentTarget.style.background=S.hoverBg; }} onMouseLeave={e=>{ if (plusSubOpen !== "chats") e.currentTarget.style.background="none"; }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      <span style={{ flex: 1 }}>Reference Chats</span>
                      <span style={{ fontSize: 11, color: S.text3, fontFamily: "monospace" }}>{chats.length}</span>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                    </div>
                    {/* Google Drive */}
                    <button type="button" onClick={() => { setPlusMenuOpen(false); openGoogleDrivePicker(); }}
                      style={{ width: "100%", border: 0, background: "none", padding: "9px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: S.text, fontSize: 13, fontFamily: "inherit", textAlign: "left" }}
                      onMouseEnter={e=>e.currentTarget.style.background=S.hoverBg} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                      <img src="https://cdn.simpleicons.org/googledrive/1A73E8" alt="" style={{ width: 15, height: 15 }} onError={e => { e.target.outerHTML = '<span style="font-size:14px">📂</span>'; }}/>
                      <span style={{ flex: 1 }}>{gdriveBusy ? "Opening Drive…" : "Google Drive"}</span>
                    </button>
                  </div>
                  {/* Sub-menus */}
                  {plusSubOpen === "notes" && (
                    <div style={{ position: "fixed", bottom: plusMenuPos.bottom, left: plusMenuPos.left + 248, minWidth: 260, maxWidth: 320, maxHeight: "min(60vh, 420px)", overflowY: "auto", background: S.modalBg || S.cardBg, border: `1px solid ${S.border3 || S.border2}`, borderRadius: 12, padding: 4, zIndex: 101, boxShadow: theme === "dark" ? "0 12px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)" : "0 12px 48px rgba(0,0,0,0.18)" }}>
                      {(memory.entries || []).length === 0 ? (
                        <div style={{ padding: "16px 14px", textAlign: "center", color: S.text3, fontSize: 12 }}>
                          No notes yet.<br/><span style={{ fontSize: 11 }}>Enable memory in Settings.</span>
                        </div>
                      ) : memory.entries.map(e => (
                        <div key={e.id} onClick={() => {
                          const att = { id: gid(), name: "Note " + new Date(e.ts).toLocaleDateString(), mediaType: "text/plain", text: e.text, size: e.text.length };
                          setAttachments(p => [...p, att]);
                          setPlusMenuOpen(false); setPlusSubOpen(null);
                          showToast("Note attached");
                        }} style={{ padding: "8px 12px", borderRadius: 8, cursor: "pointer", color: S.text2, fontSize: 12, lineHeight: 1.45 }}
                          onMouseEnter={ev=>ev.currentTarget.style.background=S.hoverBg} onMouseLeave={ev=>ev.currentTarget.style.background="none"}>
                          <div style={{ overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{e.text}</div>
                          <div style={{ fontSize: 9, color: S.text3, fontFamily: "monospace", marginTop: 3 }}>{new Date(e.ts).toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {plusSubOpen === "knowledge" && (
                    <div style={{ position: "fixed", bottom: plusMenuPos.bottom, left: plusMenuPos.left + 248, minWidth: 240, maxWidth: 320, maxHeight: "min(60vh, 420px)", overflowY: "auto", background: S.modalBg || S.cardBg, border: `1px solid ${S.border3 || S.border2}`, borderRadius: 12, padding: 4, zIndex: 101, boxShadow: theme === "dark" ? "0 12px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)" : "0 12px 48px rgba(0,0,0,0.18)" }}>
                      {knowledgeBases.length === 0 ? (
                        <div style={{ padding: "16px 14px", textAlign: "center", color: S.text3, fontSize: 12 }}>
                          No knowledge bases.<br/><span style={{ fontSize: 11 }}>Create one in Knowledge.</span>
                        </div>
                      ) : <>
                        <div onClick={() => { setSelectedKbId(null); setPlusMenuOpen(false); setPlusSubOpen(null); showToast("Knowledge base cleared"); }}
                          style={{ padding: "8px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, color: S.text, fontSize: 13, background: !selectedKbId ? S.accentDim : "none" }}>
                          <span style={{ fontSize: 14 }}>⊘</span><span style={{ flex: 1 }}>None</span>{!selectedKbId && <span style={{ color: S.accent }}>✓</span>}
                        </div>
                        {knowledgeBases.map(kb => (
                          <div key={kb.id} onClick={() => { setSelectedKbId(kb.id); setPlusMenuOpen(false); setPlusSubOpen(null); showToast(`Attached: ${kb.name}`); }}
                            style={{ padding: "8px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, color: S.text, fontSize: 13, background: selectedKbId === kb.id ? S.accentDim : "none" }}>
                            <span style={{ fontSize: 14 }}>📚</span>
                            <span style={{ flex: 1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{kb.name}</span>
                            <span style={{ fontSize: 10, color: S.text3, fontFamily: "monospace" }}>{kb.docCount}</span>
                            {selectedKbId === kb.id && <span style={{ color: S.accent }}>✓</span>}
                          </div>
                        ))}
                      </>}
                    </div>
                  )}
                  {plusSubOpen === "chats" && (
                    <div style={{ position: "fixed", bottom: plusMenuPos.bottom, left: plusMenuPos.left + 248, minWidth: 280, maxWidth: 340, maxHeight: "min(60vh, 420px)", overflowY: "auto", background: S.modalBg || S.cardBg, border: `1px solid ${S.border3 || S.border2}`, borderRadius: 12, padding: 4, zIndex: 101, boxShadow: theme === "dark" ? "0 12px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)" : "0 12px 48px rgba(0,0,0,0.18)" }}>
                      {chats.length === 0 ? (
                        <div style={{ padding: "16px 14px", textAlign: "center", color: S.text3, fontSize: 12 }}>No chats yet.</div>
                      ) : chats.slice(0, 30).filter(c => c.id !== chatId).map(c => (
                        <div key={c.id} onClick={() => {
                          // Attach a condensed transcript so the current chat can reference it.
                          const msgs = (c.messages || []).filter(m => m.role === "user" || m.role === "assistant");
                          const transcript = msgs.slice(-20).map(m => `${m.role === "user" ? "User" : "AI"}: ${typeof m.content === "string" ? m.content : ""}`).join("\n\n");
                          if (!transcript.trim()) { showToast("Chat is empty"); return; }
                          const att = { id: gid(), name: "Chat: " + (c.title || "Untitled"), mediaType: "text/plain", text: transcript, size: transcript.length };
                          setAttachments(p => [...p, att]);
                          setPlusMenuOpen(false); setPlusSubOpen(null);
                          showToast("Chat attached");
                        }} style={{ padding: "8px 12px", borderRadius: 8, cursor: "pointer", color: S.text2, fontSize: 12, lineHeight: 1.45 }}
                          onMouseEnter={ev=>ev.currentTarget.style.background=S.hoverBg} onMouseLeave={ev=>ev.currentTarget.style.background="none"}>
                          <div style={{ color: S.text, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.title || "Untitled"}</div>
                          <div style={{ fontSize: 10, color: S.text3, fontFamily: "monospace", marginTop: 2 }}>{(c.messages || []).length} messages · {new Date(c.created || Date.now()).toLocaleDateString()}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </>)}
              </div>
              {/* Direct Web Search toggle — separate from the tools popup so users can flip it with one click */}
              <button onClick={startVoiceMode} className="cf-tile-hover" title="Talk to Me — hands-free voice conversation" aria-label="Talk to Me"
                style={{ height: 32, padding: "0 10px", background: "none", border: "1px solid " + S.border2, borderRadius: 9, color: S.text3, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, fontFamily: "inherit", transition: "all 0.15s", whiteSpace: "nowrap" }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="2 12 5 12 7 7 11 17 13 9 16 12 22 12"/></svg>
                Talk to Me
              </button>
              <button onClick={() => setWebSearch(p => !p)} className="cf-tile-hover" title={webSearch ? "Web search: ON (click to disable)" : "Web search: OFF (click to enable)"}
                style={{ height: 32, padding: "0 10px", background: webSearch ? S.accentDim : "none", border: `1px solid ${webSearch ? S.accent : S.border2}`, borderRadius: 9, color: webSearch ? S.accent : S.text3, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, fontFamily: "inherit", transition: "all 0.15s", whiteSpace: "nowrap" }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                Search
              </button>
              {/* Tools / features popup (wrench icon — click to open menu of other features) */}
              <div>
                <button ref={toolsBtnRef} onClick={openToolsMenu} title="Tools & features" className="cf-tile-hover"
                  style={{ width: 32, height: 32, background: (codeInterpreter||enabledToolIds.size>0||selectedKbId||(mcpServers||[]).some(s=>s.status==="connected"&&s.enabled!==false)) ? S.accentDim : "none", border: `1px solid ${(codeInterpreter||enabledToolIds.size>0||selectedKbId||(mcpServers||[]).some(s=>s.status==="connected"&&s.enabled!==false)) ? S.accent : S.border2}`, borderRadius: 9, color: (codeInterpreter||enabledToolIds.size>0||selectedKbId||(mcpServers||[]).some(s=>s.status==="connected"&&s.enabled!==false)) ? S.accent : S.text3, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s", position: "relative" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                </button>
                {toolsMenuOpen && (
                  <>
                    <div onClick={() => { setToolsMenuOpen(false); setToolsSubOpen(false); }} style={{ position: "fixed", inset: 0, zIndex: 99 }}/>
                    <div style={{ position: "fixed", bottom: toolsMenuPos.bottom, left: toolsMenuPos.left, minWidth: 260, maxWidth: 320, maxHeight: "min(60vh, 520px)", overflowY: "auto", background: S.modalBg || S.cardBg, border: `1px solid ${S.border3 || S.border2}`, borderRadius: 12, padding: 4, zIndex: 100, boxShadow: theme === "dark" ? "0 12px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)" : "0 12px 48px rgba(0,0,0,0.18)" }}>
                      {/* Tools nested item */}
                      <div onClick={() => setToolsSubOpen(p => !p)} style={{ padding: "9px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: S.text, fontSize: 13, transition: "background 0.12s" }} onMouseEnter={e=>e.currentTarget.style.background=S.hoverBg} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                        <span style={{ fontSize: 16 }}>🔧</span>
                        <span style={{ flex: 1 }}>Tools</span>
                        {enabledToolIds.size > 0 && <span style={{ fontSize: 11, color: S.text3, fontFamily: "monospace" }}>{enabledToolIds.size}</span>}
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                      </div>
                      {/* Web Search */}
                      <div onClick={() => setWebSearch(p => !p)} style={{ padding: "9px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: S.text, fontSize: 13, transition: "background 0.12s" }} onMouseEnter={e=>e.currentTarget.style.background=S.hoverBg} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                        <span style={{ fontSize: 16 }}>🌐</span>
                        <span style={{ flex: 1 }}>Web Search</span>
                        <div style={{ width: 32, height: 18, borderRadius: 10, background: webSearch ? S.success : S.surface3, border: `1px solid ${S.border2}`, position: "relative", transition: "background 0.18s", flexShrink: 0 }}>
                          <div style={{ position: "absolute", top: 1, left: webSearch ? 14 : 1, width: 14, height: 14, background: "#fff", borderRadius: "50%", transition: "left 0.18s" }}/>
                        </div>
                      </div>
                      {/* Code Interpreter */}
                      <div onClick={() => setCodeInterpreter(p => !p)} style={{ padding: "9px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: S.text, fontSize: 13, transition: "background 0.12s" }} onMouseEnter={e=>e.currentTarget.style.background=S.hoverBg} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                        <span style={{ fontSize: 16 }}>💻</span>
                        <span style={{ flex: 1 }}>Code Interpreter</span>
                        <div style={{ width: 32, height: 18, borderRadius: 10, background: codeInterpreter ? S.success : S.surface3, border: `1px solid ${S.border2}`, position: "relative", transition: "background 0.18s", flexShrink: 0 }}>
                          <div style={{ position: "absolute", top: 1, left: codeInterpreter ? 14 : 1, width: 14, height: 14, background: "#fff", borderRadius: "50%", transition: "left 0.18s" }}/>
                        </div>
                      </div>
                      {/* Knowledge Base selector */}
                      {knowledgeBases.length > 0 && (
                        <div style={{ borderTop: `1px solid ${S.border}`, margin: "4px 0", paddingTop: 4 }}>
                          <div style={{ padding: "4px 12px", fontSize: 10, color: S.text3, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.05em" }}>Knowledge Base</div>
                          <div onClick={() => setSelectedKbId(null)} style={{ padding: "7px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: S.text, fontSize: 13, background: !selectedKbId ? S.accentDim : "none" }}>
                            <span style={{ fontSize: 16 }}>⊘</span>
                            <span style={{ flex: 1 }}>None</span>
                            {!selectedKbId && <span style={{ color: S.accent }}>✓</span>}
                          </div>
                          {knowledgeBases.map(kb => (
                            <div key={kb.id} onClick={() => setSelectedKbId(kb.id)} style={{ padding: "7px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: S.text, fontSize: 13, background: selectedKbId === kb.id ? S.accentDim : "none" }}>
                              <span style={{ fontSize: 16 }}>📚</span>
                              <span style={{ flex: 1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{kb.name}</span>
                              <span style={{ fontSize: 10, color: S.text3, fontFamily: "monospace" }}>{kb.docCount}</span>
                              {selectedKbId === kb.id && <span style={{ color: S.accent }}>✓</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {/* MCP Servers section -- surfaces every CONNECTED
                          server so the user can see what tools the AI
                          can call, plus per-server ON/OFF toggles. */}
                      {(() => {
                        const connectedMcp = (mcpServers || []).filter(s => s.status === "connected");
                        if (connectedMcp.length === 0) {
                          return (
                            <div style={{ borderTop: `1px solid ${S.border}`, margin: "4px 0", paddingTop: 4 }}>
                              <div style={{ padding: "4px 12px", fontSize: 10, color: S.text3, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.05em" }}>MCP Servers</div>
                              <div onClick={() => { setSection("mcp"); setToolsMenuOpen(false); setToolsSubOpen(false); }} style={{ padding: "9px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: S.text3, fontSize: 12 }}
                                onMouseEnter={e=>e.currentTarget.style.background=S.hoverBg} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                                <span style={{ fontSize: 16 }}>🧩</span>
                                <span style={{ flex: 1, lineHeight: 1.45 }}>No MCP servers connected yet. <span style={{ color: S.accent, textDecoration: "underline" }}>Add one →</span></span>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div style={{ borderTop: `1px solid ${S.border}`, margin: "4px 0", paddingTop: 4 }}>
                            <div style={{ padding: "4px 12px", fontSize: 10, color: S.text3, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: 6 }}>
                              <span>MCP Servers</span>
                              <span style={{ fontSize: 9, opacity: 0.7 }}>· {connectedMcp.length} connected</span>
                            </div>
                            {connectedMcp.map(srv => {
                              const on = srv.enabled !== false;
                              const toolCount = Array.isArray(srv.tools) ? srv.tools.length : 0;
                              return (
                                <div key={srv.id} onClick={() => {
                                  // Flip srv.enabled. pushShared("mcp", ...) syncs to
                                  // the server within ~1.5s; subsequent chat calls
                                  // see the new flag in _mcpAssembleToolsForOrg.
                                  setMcpServers(prev => prev.map(s => s.id === srv.id ? { ...s, enabled: !on } : s));
                                }} style={{ padding: "9px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: S.text, fontSize: 13, transition: "background 0.12s" }}
                                  onMouseEnter={e=>e.currentTarget.style.background=S.hoverBg} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                                  <span style={{ fontSize: 16 }}>🧩</span>
                                  <div style={{ flex: 1, overflow: "hidden" }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}>
                                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{srv.name || srv.id}</span>
                                      <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 4, background: S.success+"22", color: S.success, fontFamily: "monospace", fontWeight: 700, letterSpacing: "0.05em", flexShrink: 0 }}>CONNECTED</span>
                                    </div>
                                    <div style={{ fontSize: 10, color: S.text3, fontFamily: "monospace", marginTop: 1 }}>{toolCount} tool{toolCount === 1 ? "" : "s"}{srv.transport ? " · " + srv.transport : ""}</div>
                                  </div>
                                  <div style={{ width: 32, height: 18, borderRadius: 10, background: on ? S.success : S.surface3, border: `1px solid ${S.border2}`, position: "relative", transition: "background 0.18s", flexShrink: 0 }}>
                                    <div style={{ position: "absolute", top: 1, left: on ? 14 : 1, width: 14, height: 14, background: "#fff", borderRadius: "50%", transition: "left 0.18s" }}/>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                    {/* Tools sub-menu */}
                    {toolsSubOpen && (
                      <div style={{ position: "fixed", bottom: toolsMenuPos.bottom, left: toolsMenuPos.left + 268, minWidth: 260, maxWidth: 320, maxHeight: "min(60vh, 520px)", overflowY: "auto", background: S.modalBg || S.cardBg, border: `1px solid ${S.border3 || S.border2}`, borderRadius: 12, padding: 4, zIndex: 101, boxShadow: theme === "dark" ? "0 12px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)" : "0 12px 48px rgba(0,0,0,0.18)" }}>
                        {tools.length === 0 ? (
                          <div style={{ padding: "16px 14px", textAlign: "center", color: S.text3, fontSize: 12 }}>
                            No tools configured yet.<br/>
                            <span style={{ fontSize: 11 }}>Add tools in the Tools section.</span>
                          </div>
                        ) : tools.map(t => {
                          const on = enabledToolIds.has(t.id);
                          return (
                            <div key={t.id} onClick={() => setEnabledToolIds(prev => { const n = new Set(prev); on ? n.delete(t.id) : n.add(t.id); return n; })}
                              style={{ padding: "9px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: S.text, fontSize: 13, transition: "background 0.12s" }}
                              onMouseEnter={e=>e.currentTarget.style.background=S.hoverBg} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                              <span style={{ fontSize: 16 }}>{t.emoji || "🔧"}</span>
                              <div style={{ flex: 1, overflow: "hidden" }}>
                                <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</div>
                                {t.desc && <div style={{ fontSize: 10, color: S.text3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.desc}</div>}
                              </div>
                              <div style={{ width: 32, height: 18, borderRadius: 10, background: on ? S.success : S.surface3, border: `1px solid ${S.border2}`, position: "relative", transition: "background 0.18s", flexShrink: 0 }}>
                                <div style={{ position: "absolute", top: 1, left: on ? 14 : 1, width: 14, height: 14, background: "#fff", borderRadius: "50%", transition: "left 0.18s" }}/>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
              {/* Compare models — hidden on mobile (side-by-side comparison doesn't fit well on phone width). Also hidden when the org's plan doesn't include multi-model compare (server enforces too). */}
              {!isMobile && (orgInfo?.plan_details?.features?.multiModel !== false) && <button className="cf-tile-hover" onClick={() => { setMultiMode(p => !p); if (!multiMode && multiModels.length < 2) setMultiModels([{model:activeModel,provider:activeProv},{model:"claude-haiku-4-5-20251001",provider:"anthropic"}]); }} title={multiMode ? "Parallel compare is on" : "Parallel compare"} aria-label={multiMode ? "Turn off parallel compare" : "Turn on parallel compare"} data-tooltip={multiMode ? "Parallel compare is on. Your prompt will run through multiple models side-by-side. Click to turn it off." : "Turn on parallel compare to send one prompt to multiple models and review the answers side-by-side."}
                style={{ width: 32, height: 32, background: multiMode ? "#8b5cf620" : "none", border: `1px solid ${multiMode ? "#8b5cf6" : S.border2}`, borderRadius: 9, color: multiMode ? "#8b5cf6" : S.text3, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 12h4" />
                  <path d="M8 12c2.2 0 3.1-5 6-5" />
                  <path d="M8 12c2.2 0 3.1 5 6 5" />
                  <rect x="14" y="4" width="6" height="6" rx="2" />
                  <rect x="14" y="14" width="6" height="6" rx="2" />
                </svg>
              </button>}
            </div>
            {/* Right: brand + mic + send */}
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0, marginLeft: "auto" }}>
              {/* Brand badge — hidden on mobile since it's redundant with the chat header + topbar title */}
              {!isMobile && (
                <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, background: S.accentDim, border: `1px solid ${S.border3}`, fontSize: 10, color: S.accent, fontFamily: "monospace", fontWeight: 600, userSelect: "none" }}>
                  <AppLogo size={10} /> {BN}
                </div>
              )}
              {/* Mic */}
              <button className="cf-mic-btn" data-dictating={dictating ? "true" : "false"} onClick={() => dictating ? stopDictation() : startDictation()} title={dictating ? "Stop" : "Dictate"}
                style={{ width: 36, height: 36, background: dictating ? `${S.danger}15` : S.surface || S.inputBg, border: `1px solid ${dictating ? S.danger : S.border2}`, borderRadius: "50%", color: dictating ? S.danger : S.text2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
              </button>
              {/* Send — dark circle like OpenWebUI */}
              <button className="cf-send-btn cf-cta" data-busy={busy ? "true" : "false"} onClick={() => busy ? stopActiveGeneration() : send()} title={busy ? "Stop generating" : "Send"} aria-label={busy ? "Stop generating" : "Send message"}
                style={{ width: 38, height: 38, background: theme === "dark" ? "#e2e8f0" : "#1e293b", border: "none", borderRadius: "50%", color: theme === "dark" ? "#0f172a" : "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>
                {busy ? <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="5" y="5" width="14" height="14" rx="2.5"/></svg> : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>}
              </button>
            </div>
          </div>
        </div>
      </div>
    );

  const InputBox = () => InputBar({controlled: false});

  function ChatPage() {
    const msgs = curChat?.messages || [];
    const [hov, setHov] = useState(null);
    // Edit-and-resend state. editingId is the message id currently in the
    // textarea morph; editDraft is the unsaved buffer. Both reset when the
    // user submits, cancels, or navigates away (ChatPage unmounts).
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState("");
    const [slotContent, setSlotContent] = useState([0, 1, 2, 3]);
    const nextSlotRef = useRef(0);
    const [starterHover, setStarterHover] = useState(false);
    // Mobile / no-hover-device detection. Read once on mount; the
    // breakpoint between "has hover" and "no hover" doesn't change at
    // runtime in a way users care about. When false, the action bar
    // stays visible (opacity:1) regardless of the hover state — no
    // separate ⋯ menu, just always-on.
    const noHover = useMemo(() => {
      try { return typeof window !== "undefined" && !window.matchMedia("(hover: hover)").matches; }
      catch { return false; }
    }, []);
    useEffect(() => {
      setSlotContent([0, 1, 2, 3]);
      nextSlotRef.current = 0;
    }, [curAg?.id]);
    useEffect(() => {
      if (msgs.length > 0) return;
      if (starterHover) return;
      const pool = [...(curAg?.starters || []), ...STARTER_EXTRAS];
      if (pool.length <= 4) return;
      const id = setInterval(() => {
        setSlotContent(prev => {
          const next = prev.slice();
          const slot = nextSlotRef.current;
          const used = new Set(prev);
          let candidate = (Math.max(...prev) + 1) % pool.length;
          let safety = pool.length;
          while (used.has(candidate) && safety-- > 0) {
            candidate = (candidate + 1) % pool.length;
          }
          next[slot] = candidate;
          nextSlotRef.current = (slot + 1) % 4;
          return next;
        });
      }, 5000);
      return () => clearInterval(id);
    }, [msgs.length, starterHover, curAg?.id]);
    // Last-assistant message id — its action bar always stays visible,
    // matching ChatGPT/Claude.ai. Computed inline; the chat-list is
    // typically short enough that a full pass per render is free.
    const lastAssistantMsgId = useMemo(() => {
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === "assistant" && !msgs[i].loading && !msgs[i].error) return msgs[i].id;
      }
      return null;
    }, [msgs]);
    // Edit submit: drop everything from the edited message onward
    // (its old reply included), then re-fire send() with the edited
    // text. send() handles the truncation atomically inside its
    // setChats updater via the editFromIdx option, so there's no race
    // between our local truncate and send's read of `chats`.
    const submitEdit = useCallback(async (msg) => {
      const trimmed = (editDraft || "").trim();
      if (!trimmed || busy) return;
      const c = chats.find(x => x.id === chatId);
      if (!c) return;
      const idx = c.messages.findIndex(m => m.id === msg.id);
      if (idx < 0) return;
      setEditingId(null);
      setEditDraft("");
      await send(trimmed, { editFromIdx: idx });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editDraft, busy, chats, chatId]);
    const hasMessages = msgs.length > 0;

    if (!hasMessages) {
      const showProvider = !!(authUser?.byok || isSuperAdmin || cfg.showProviderBadges);
      const provName = showProvider ? (PROVIDERS[activeProv]?.name || "Anthropic") : BN;
      const rawModelLabel = showProvider ? (curMod?.name || activeModel) : (curMod?.tierLabel || curAg?.name || "");
      // Suppress the suffix if it'd just repeat the brand (e.g. "ChatFortAI: ChatFort")
      const modelName = (!showProvider && rawModelLabel && provName.toLowerCase().includes(rawModelLabel.toLowerCase())) ? "" : rawModelLabel;
      return (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 32px", overflowY: "auto" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", maxWidth: 720 }}>
            <UsageCapBanner orgInfo={orgInfo} isAdmin={isAdmin} setSection={setSection} />
            <GettingStartedCard chats={chats} knowledgeBases={knowledgeBases} orgInfo={orgInfo} isAdmin={isAdmin} setSection={setSection} serverMode={serverMode} authUser={authUser} brandName={BN} />
            {/* ChatFort logo hero */}
            <div className="cf-hero-logo" style={{ width: 80, height: 80, borderRadius: 22, background: S.accentDim, border: `1px solid ${S.border2}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
              <AppLogo size={50} />
            </div>
            <h1 className="cf-hero-title" style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.03em", color: S.text, marginBottom: 8, textAlign: "center", lineHeight: 1.2 }}>
              {modelName ? `${provName}: ${modelName}` : provName}
            </h1>
            <p className="cf-hero-desc" style={{ color: S.text3, fontSize: 13, textAlign: "center", maxWidth: 420, lineHeight: 1.7, marginBottom: 28 }}>
              {curAg?.desc || "Private AI for regulated industries."}
            </p>
            {/* Starter cards -- one-at-a-time rotation through the
                combined agent + extras pool. slotContent[i] is the
                pool index for slot i; the key includes both so only
                the slot whose content just changed remounts and
                re-plays the flip animation. */}
            {(() => {
              const pool = [...(curAg?.starters || []), ...STARTER_EXTRAS];
              return (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, width: "100%", marginBottom: 24 }}
                  onMouseEnter={() => setStarterHover(true)}
                  onMouseLeave={() => setStarterHover(false)}>
                  {slotContent.map((poolIdx, i) => {
                    const text = pool[poolIdx % pool.length] || "";
                    return (
                      <div key={`slot-${i}-${poolIdx}`} className="cf-hero-starter" onClick={() => send(text)}
                        style={{ background: S.cardBg, border: `1px solid ${S.border2}`, borderRadius: 14, padding: "13px 15px", cursor: "pointer" }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = S.accent; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = S.border2; }}>
                        <div style={{ fontSize: 10, color: S.accent, marginBottom: 5, fontFamily: "monospace" }}>✦</div>
                        <div style={{ fontSize: 12, color: S.text2, lineHeight: 1.55 }}>{text}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            {InputBox()}
            <div style={{ marginTop: 10, fontSize: 10, color: S.text3, fontFamily: "monospace" }}>ChatFort AI · {marketingVersion} · Zero retention</div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ flex: 1, position: "relative", display: "flex", flexDirection: "column", minHeight: 0 }}>
        {/* Slice 10.4: always-mount the pill so toggling showJumpPill
            doesn't change the children-array length of this wrapper.
            Slice 10.3's conditional `{showJumpPill && <button>}` flipped
            children from `[<button>, <div>]` to `[<div>]` on click, and
            React's index-based reconciliation tore down the scroller div
            (type-mismatched at index 0) and recreated it with scrollTop
            = 0 — which is why pill clicks landed scroll on the bottom of
            a new node 30 ms before the new node took its place at the
            top. Always-mounting keeps both children at stable indices;
            opacity + pointer-events drive the visible state instead.
            Slice 10.2 callback-ref binding is unaffected — the scroller
            div's identity is now stable across click-driven state
            transitions. */}
        <button className="cf-jump-pill" data-visible={showJumpPill ? "true" : "false"} onClick={jumpToBottom}
          title="Jump to latest"
          aria-label="Jump to latest message"
          aria-hidden={!showJumpPill}
          tabIndex={showJumpPill ? 0 : -1}
          style={{ position: "absolute", right: 24, bottom: 16, zIndex: 10,
                   display: "flex", alignItems: "center", gap: 6,
                   padding: "6px 12px", borderRadius: 20,
                   background: S.accent, color: "#fff", border: "none",
                   fontSize: 11, fontWeight: 700, fontFamily: "inherit",
                   boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
                   cursor: showJumpPill ? "pointer" : "default",
                   opacity: showJumpPill ? 1 : 0,
                   pointerEvents: showJumpPill ? "auto" : "none",
                   transition: "opacity 0.15s" }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
          Jump to latest
        </button>
      <div ref={areaRef} className="cf-chat-scroller" style={{ flex: 1, overflowY: "auto", padding: "24px 0" }}>
        <div style={{ maxWidth: 980, margin: "0 auto", padding: "0 32px", display: "flex", flexDirection: "column", gap: 2 }}>
          <UsageCapBanner orgInfo={orgInfo} isAdmin={isAdmin} setSection={setSection} />
          {msgs.map(msg => {
            // ── Inline system banner (e.g. /remember confirmation) ──
            // Lightweight subtle row — no avatar, no actions, distinctly
            // not styled like an assistant or user bubble so users see
            // it didn't come from the LLM. The /remember slash command
            // pushes one of these via setChats; nothing else uses this
            // role today.
            if (msg.role === "system_inline") {
              const label = msg.kind === "memory_save"
                ? `\u2705 Saved to memory: \u201c${msg.content}\u201d`
                : msg.content;
              return (
                <MsgRow key={msg.id} msg={msg}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 8,
                    margin: "6px 0", padding: "6px 12px",
                    fontSize: 11, fontFamily: "monospace",
                    color: S.text3, lineHeight: 1.5,
                    borderLeft: `2px solid ${S.border2}`,
                    opacity: 0.85, userSelect: "text",
                  }}>
                    <span>{label}</span>
                  </div>
                </MsgRow>
              );
            }
            // ── Comparison row ──
            if (msg.role === "comparison") {
              const colCount = msg.slots.length;
              return (
                <MsgRow key={msg.id} msg={msg}>
                <div style={{ padding: "12px 0", borderRadius: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, padding: "0 2px" }}>
                    <div style={{ height: 1, flex: 1, background: S.border2 }} />
                    <span style={{ fontSize: 9, fontFamily: "monospace", color: S.text3, padding: "2px 8px", borderRadius: 10, background: S.surface2, border: "1px solid " + S.border2, whiteSpace: "nowrap" }}>⚡ {colCount} models in parallel</span>
                    <div style={{ height: 1, flex: 1, background: S.border2 }} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(" + colCount + ", 1fr)", gap: 10 }}>
                    {msg.slots.map((slot, si) => {
                      const pv = PROVIDERS[slot.provider];
                      const modelName = pv?.models?.find(m => m.id === slot.model)?.name || slot.model.split("/").pop();
                      const provColor = pv?.color || S.accent;
                      return (
                        <div key={slot.id} style={{ background: S.cardBg, border: "1px solid " + S.border2, borderTop: "2px solid " + provColor, borderRadius: 12, overflow: "hidden" }}>
                          <div style={{ padding: "8px 12px", borderBottom: "1px solid " + S.border, display: "flex", alignItems: "center", gap: 6, background: S.surface2 }}>
                            <div style={{ width: 7, height: 7, borderRadius: "50%", background: provColor, flexShrink: 0 }} />
                            <span style={{ fontSize: 10, fontWeight: 700, fontFamily: "monospace", color: S.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{modelName}</span>
                            {pv && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, fontFamily: "monospace", background: provColor + "18", color: provColor, border: "1px solid " + provColor + "30", flexShrink: 0 }}>{pv.icon}</span>}
                            {slot.loading && <div style={{ display: "flex", gap: 3 }}>{[0,1,2].map(i => <span key={i} style={{ width: 4, height: 4, borderRadius: "50%", background: S.accent, opacity: 0.6, display: "inline-block", animation: "blink 0.9s " + (i*0.2) + "s step-end infinite" }} />)}</div>}
                            {!slot.loading && slot.content && <CopyMorphButton text={slot.content} onCopied={() => showToast("Copied!")} style={{ background: "none", border: "none", color: S.text3, cursor: "pointer", padding: 2, display: "flex", alignItems: "center", flexShrink: 0 }}>{IC.copy}</CopyMorphButton>}
                          </div>
                          <div style={{ padding: "10px 12px", minHeight: 60 }}>
                            {slot.error
                              ? <div style={{ color: S.danger, fontSize: 12 }}>{slot.content}</div>
                              : slot.content
                                ? <MsgContent text={slot.content} />
                                : slot.loading
                                  ? <div style={{ color: S.text3, fontSize: 11, fontFamily: "monospace" }}>Generating...</div>
                                  : null
                            }
                          </div>
                          {/* Per-slot action toolbar -- same Copy / Retry /
                              Branch / PDF / Word / Excel / PowerPoint /
                              Show-your-work the normal assistant bubble has.
                              The synthetic msg is shaped from the slot so
                              MsgActions can read .content / .role / .provider
                              / .model. Slot.id is reused as the message id
                              so export filenames stay unique per slot. */}
                          {!slot.loading && !slot.error && slot.content && (
                            <div style={{ padding: "0 12px 10px", borderTop: "1px solid " + S.border }}>
                              <MsgActions
                                msg={{ id: slot.id, role: "assistant", content: slot.content, provider: slot.provider, model: slot.model, time: msg.time }}
                                isAI={true}
                                curChat={curChat}
                                busy={busy}
                                send={send}
                                showToast={showToast}
                                chats={chats}
                                setChats={setChats}
                                setChatId={setChatId}
                                setAgId={setAgId}
                                wsId={wsId}
                                agId={agId}
                                newGid={gid}
                                setSection={setSection}
                                onShowTrace={null}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Merge responses — only shown after all slots are done */}
                  {msg.slots.every(s => !s.loading) && !msg.merged && (
                    <div style={{ marginTop: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <button onClick={() => mergeCompareResponses(curChat.id, msg.id)}
                        style={{ padding: "9px 18px", borderRadius: 20, background: `linear-gradient(135deg, ${S.accent}, ${S.accent2})`, border: "none", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 7, fontFamily: "inherit", boxShadow: `0 4px 12px ${S.accentDim}`, transition: "transform 0.15s" }}
                        onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
                        onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 3v12m0 0l-3-3m3 3l3-3"/><path d="M18 3v12m0 0l-3-3m3 3l3-3"/><path d="M12 15l-6 6"/><path d="M12 15l6 6"/></svg>
                        Merge responses into one answer
                      </button>
                    </div>
                  )}
                  {/* Merged synthesis */}
                  {msg.merged && (
                    <div style={{ marginTop: 16, background: S.cardBg, border: `2px solid ${S.accent}`, borderRadius: 14, overflow: "hidden", boxShadow: `0 8px 24px ${S.accentDim}` }}>
                      <div style={{ padding: "10px 14px", borderBottom: `1px solid ${S.border}`, display: "flex", alignItems: "center", gap: 8, background: `linear-gradient(135deg, ${S.accentDim}, ${S.surface2})` }}>
                        <span style={{ fontSize: 14 }}>✨</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: S.accent, flex: 1 }}>Merged synthesis</span>
                        {!msg.merged.loading && !msg.merged.error && msg.merged.content && (
                          <>
                            <CopyMorphButton text={msg.merged.content} onCopied={() => showToast("Copied!")} style={{ background: "none", border: "none", color: S.text3, cursor: "pointer", padding: 2, display: "flex" }} title="Copy">{IC.copy}</CopyMorphButton>
                            <button onClick={() => setChats(p => p.map(c => c.id !== curChat.id ? c : { ...c, messages: c.messages.map(m => m.id !== msg.id ? m : { ...m, merged: null }) }))} style={{ background: "none", border: "none", color: S.text3, cursor: "pointer", padding: 2, display: "flex" }} title="Remove merged answer">×</button>
                          </>
                        )}
                      </div>
                      <div style={{ padding: "14px 16px" }}>
                        {msg.merged.loading
                          ? <div style={{ display: "flex", alignItems: "center", gap: 8, color: S.text3, fontSize: 12 }}>
                              <div style={{ display: "flex", gap: 3 }}>{[0,1,2].map(i => <span key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: S.accent, display: "inline-block", animation: "blink 0.9s " + (i*0.2) + "s step-end infinite" }} />)}</div>
                              Synthesizing the best answer from {msg.slots.length} responses…
                            </div>
                          : msg.merged.error
                            ? <div style={{ color: S.danger, fontSize: 12 }}>{msg.merged.content}</div>
                            : <MsgContent text={msg.merged.content} />
                        }
                      </div>
                      {/* Full action toolbar on the merged synthesis --
                          same Copy / Retry / Branch / PDF / Word / Excel
                          / PowerPoint / Show-your-work the user gets on a
                          normal assistant bubble. Synthetic msg id suffix
                          keeps export filenames distinct from any single
                          slot. */}
                      {!msg.merged.loading && !msg.merged.error && msg.merged.content && (
                        <div style={{ padding: "0 16px 14px", borderTop: "1px solid " + S.border }}>
                          <MsgActions
                            msg={{ id: msg.id + "-merged", role: "assistant", content: msg.merged.content, time: msg.time }}
                            isAI={true}
                            curChat={curChat}
                            busy={busy}
                            send={send}
                            showToast={showToast}
                            chats={chats}
                            setChats={setChats}
                            setChatId={setChatId}
                            setAgId={setAgId}
                            wsId={wsId}
                            agId={agId}
                            newGid={gid}
                            setSection={setSection}
                            onShowTrace={null}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                </MsgRow>
              );
            }

            const isAI = msg.role === "assistant";
            const mAg = isAI ? (agents.find(a => a.id === curChat?.agentId) || curAg) : null;
            // Prefer the provider recorded on the message itself — that's what it
            // was actually generated with — and fall back to the agent's default
            // for older messages that pre-date this field.
            const pv = isAI ? (PROVIDERS[msg.provider] || (mAg ? PROVIDERS[mAg.provider] : null)) : null;
            return (
              <MsgRow key={msg.id} msg={msg}>
              <div onMouseEnter={() => setHov(msg.id)} onMouseLeave={() => setHov(null)}
                data-cf-msg-enter=""
                style={{ display: "flex", gap: 12, padding: "14px 18px", borderRadius: 14, background: hov === msg.id ? S.msgHoverBg : "none", transition: "background 0.15s" }}>
                <div style={{ flexShrink: 0, marginTop: 1 }}>
                  {isAI
                    ? <div data-cf-avatar-pulse={streamingDraft?.mid === msg.id ? "true" : undefined} style={{ width: 30, height: 30, borderRadius: 9, background: (mAg?.color || S.accent) + "20", border: "1px solid " + (mAg?.color || S.accent) + "35", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, overflow: "hidden" }}>
                        {brandLogoActive && (!mAg?.emoji || mAg?.emoji === "🏰" || mAg?.id === "ag-default")
                          ? <img src={brandLogoActiveUrl} alt={BN} style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
                          : (mAg?.emoji || "🏰")}
                      </div>
                    : <span className="cf-user-avatar"><UserAvatar user={authUser||curUser} size={30} square fontSize={11} /></span>
                  }
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: isAI ? (mAg?.color || S.accent) : S.text }}>
                      {isAI ? ((mAg?.id === "ag-default" || !mAg) ? BN : mAg.name) : ((authUser||curUser)?.name || "You")}
                    </span>
                    {isAI && pv && (authUser?.byok || isSuperAdmin || cfg.showProviderBadges) && <span className="cf-provider-badge" style={{ fontSize: 9, padding: "2px 7px", borderRadius: 10, fontFamily: "monospace", background: pv.color + "15", color: pv.color, border: "1px solid " + pv.color + "25" }} title={`Powered by ${pv.name}`}>{pv.icon} {pv.name}</span>}
                    {cfg.timestamps && <span style={{ fontSize: 9, color: S.text3, fontFamily: "monospace", cursor: "help" }} title={msg.time ? new Date(msg.time).toLocaleString() : ""}>{nowt()}</span>}
                    {/* Edit pencil — user messages only. Hidden until the bubble
                        is hovered (or the device has no hover, in which case
                        always-on). Hidden during streaming + while we're already
                        editing this message. */}
                    {!isAI && msg.role === "user" && !msg.error && !busy && editingId !== msg.id && (() => {
                      const visible = noHover || hov === msg.id;
                      return (
                        <button
                          onClick={() => { setEditingId(msg.id); setEditDraft(typeof msg.content === "string" ? msg.content : ""); }}
                          title="Edit & resend"
                          aria-label="Edit and resend message"
                          style={{
                            marginLeft: "auto",
                            opacity: visible ? 1 : 0,
                            transform: visible ? "translateY(0)" : "translateY(-4px)",
                            pointerEvents: visible ? "auto" : "none",
                            transition: "opacity 120ms ease, transform 160ms cubic-bezier(0.16,1,0.3,1)",
                            background: "none", border: "none", cursor: "pointer",
                            color: S.text3, padding: 4, lineHeight: 0, borderRadius: 6,
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                      );
                    })()}
                  </div>
                  {(() => {
                    const isStreamingThis = streamingDraft && streamingDraft.mid === msg.id;
                    // While streaming, the pre-wrap <div> renders with NO
                    // React-managed children. onChunk writes textContent to
                    // it directly via streamTargetRef — React never
                    // reconciles the text during the stream. On finish,
                    // streamingDraft clears and MsgContent renders with the
                    // final markdown-parsed content.
                    const showDots = msg.loading && !isStreamingThis;
                    return showDots
                      ? <div style={{ display: "flex", gap: 4, padding: "4px 0" }}>{[0, 1, 2].map(i => <span key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: S.accent, opacity: 0.5, display: "inline-block", animation: "blink 0.9s " + (i * 0.2) + "s step-end infinite" }} />)}</div>
                      : msg.error
                        ? <div style={{ color: S.danger, fontSize: 13, padding: "8px 12px", background: S.danger + "10", border: "1px solid " + S.danger + "30", borderRadius: 10 }}>{msg.content}</div>
                        : <div>
                            {msg.files && msg.files.length > 0 && (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                                {msg.files.map(f => (
                                  <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 8, background: S.accentDim, border: "1px solid " + S.border2, fontSize: 11, color: S.text2 }}>
                                    <span>{f.mediaType.startsWith("image/") ? "🖼" : f.mediaType === "application/pdf" ? "📄" : "📝"}</span>
                                    <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {Array.isArray(msg.toolEvents) && msg.toolEvents.length > 0 && (
                              <ToolActivityStrip events={msg.toolEvents} />
                            )}
                            {msg.kbSources && <KBSources sources={msg.kbSources} />}
                            {msg.search && <SearchSources search={msg.search} />}
                            {pendingDrafts[msg.id] && (
                              <DraftReviewCard
                                draft={pendingDrafts[msg.id]}
                                onDiscard={() => dismissDraft(msg.id, pendingDrafts[msg.id].draftId)}
                                theme={S}
                              />
                            )}
                            {isStreamingThis
                              ? <>
                                  {!thinkingState.firstTokenReceived && (
                                    <ThinkingIndicator
                                      phase={thinkingState.phase}
                                      toolDomain={thinkingState.toolDomain}
                                      color={S.text3}
                                    />
                                  )}
                                  <div
                                    ref={attachStreamTarget}
                                    className="cf-stream-accent"
                                    style={{ whiteSpace: "pre-wrap", color: S.text, lineHeight: 1.75, fontSize: 13.5, wordBreak: "break-word", paddingBottom: 6 }}
                                  />
                                </>
                              : (editingId === msg.id && msg.role === "user")
                                ? <div>
                                    <textarea
                                      value={editDraft}
                                      autoFocus
                                      onChange={e => setEditDraft(e.target.value)}
                                      onKeyDown={e => {
                                        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                                          e.preventDefault();
                                          submitEdit(msg);
                                        } else if (e.key === "Escape") {
                                          e.preventDefault();
                                          setEditingId(null); setEditDraft("");
                                        }
                                      }}
                                      style={{
                                        width: "100%", minHeight: 80,
                                        background: S.inputBg, border: `1px solid ${S.accent}55`,
                                        borderRadius: 8, padding: "10px 12px",
                                        color: S.text, fontSize: 13.5, lineHeight: 1.6,
                                        fontFamily: "inherit", resize: "vertical",
                                        outline: "none", boxSizing: "border-box",
                                      }}
                                    />
                                    <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                                      <button
                                        onClick={() => submitEdit(msg)}
                                        disabled={!editDraft.trim() || busy}
                                        style={{
                                          padding: "6px 14px", borderRadius: 8,
                                          background: (!editDraft.trim() || busy) ? S.inputBg : S.accent,
                                          color: (!editDraft.trim() || busy) ? S.text3 : "#fff",
                                          border: `1px solid ${(!editDraft.trim() || busy) ? S.border2 : S.accent}`,
                                          fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                                          cursor: (!editDraft.trim() || busy) ? "default" : "pointer",
                                        }}
                                      >Save & Resend</button>
                                      <button
                                        onClick={() => { setEditingId(null); setEditDraft(""); }}
                                        style={{
                                          padding: "6px 14px", borderRadius: 8,
                                          background: "transparent", color: S.text2,
                                          border: `1px solid ${S.border2}`,
                                          fontSize: 12, fontWeight: 500, fontFamily: "inherit", cursor: "pointer",
                                        }}
                                      >Cancel</button>
                                      <span style={{ fontSize: 10, color: S.text3, fontFamily: "monospace", marginLeft: "auto" }}>
                                        Ctrl+Enter to save · Esc to cancel
                                      </span>
                                    </div>
                                  </div>
                                : <MsgContent text={msg.content} />}
                          </div>;
                  })()}
                  {/* Slice 8: masked-summary chip on user messages with a
                      detail popover. Only renders when the server's redacted
                      SSE event attached a non-empty redactionSummary. */}
                  {!isAI && msg.role === "user" && msg.redactionSummary && Object.keys(msg.redactionSummary).length > 0 && (
                    <RedactionChip msg={msg} />
                  )}
                  {isAI && !msg.loading && !(streamingDraft && streamingDraft.mid === msg.id) && msg.content && (() => {
                    // Hover-reveal gating. Always-on for: no-hover devices
                    // (mobile/touch — no hover state to drive a fade), the
                    // last assistant message (most common interaction
                    // target). Otherwise: fade in/out on bubble hover with
                    // a 120 ms transition. pointerEvents:none when hidden
                    // so the buttons don't intercept clicks meant for the
                    // text behind them.
                    const visible = noHover || hov === msg.id || msg.id === lastAssistantMsgId;
                    return (
                      <div style={{
                        opacity: visible ? 1 : 0,
                        transform: visible ? "translateY(0)" : "translateY(-4px)",
                        pointerEvents: visible ? "auto" : "none",
                        transition: "opacity 120ms ease, transform 160ms cubic-bezier(0.16,1,0.3,1)",
                      }}>
                        <MsgActions msg={msg} isAI={isAI} curChat={curChat} busy={busy} send={send} showToast={showToast} chats={chats} setChats={setChats} setChatId={setChatId} setAgId={setAgId} wsId={wsId} agId={agId} newGid={gid} setSection={setSection} onShowTrace={msg.trace ? () => setTraceModalMsg(msg) : null} />
                      </div>
                    );
                  })()}
                </div>
              </div>
              </MsgRow>
            );
          })}
        </div>
      </div>
      </div>
    );
  }
  // Slice 10.5: keep chatPageRef pointing at the fresh ChatPage on every
  // App render so StableChatPage (declared at the top of App's body)
  // always invokes the current-closure version. Without this re-point,
  // the StableChatPage wrapper would call a stale ChatPage that closes
  // over first-render state.
  chatPageRef.current = ChatPage;

  function ToolsPage() {
    const [toolEdit, setToolEdit] = useState(null); // null=list, {} or {tool}=editor
    const [search, setSearch] = useState("");
    const TMPL = `/**
 * Tool Name
 * Describe what this tool does.
 */

// Schema tells the AI how to call this tool
const schema = {
  name: "tool_name",
  description: "Describe what this tool does",
  parameters: {
    type: "object",
    properties: {
      city: {
        type: "string",
        description: "The city to query"
      }
    },
    required: ["city"]
  }
};

// Main function — receives args object matching schema.parameters
async function tool_name(args, auth, secrets) {
  const { city } = args;
  // Example: fetch weather. For authenticated internal APIs, pass:
  //   fetch(url, { headers: auth.headers })
  // API keys and tokens come from the Auth panel, not hardcoded JavaScript.
  const r = await fetch(\`https://wttr.in/\${encodeURIComponent(city)}?format=3\`, { headers: auth.headers });
  const text = await r.text();
  return text;
}`;

    // Editor state
    const [tName, setTName] = useState("");
    const [tDesc, setTDesc] = useState("");
    const [tCode, setTCode] = useState(TMPL);
    const [tVer, setTVer] = useState("1.0.0");
    const [tId, setTId] = useState("");
    const [tAuth, setTAuth] = useState(()=>normalizeToolAuth());
    const [tSaveErr, setTSaveErr] = useState("");

    const openNew = () => {
      const id = "tool_" + gid();
      setTId(id); setTName(""); setTDesc(""); setTCode(TMPL); setTVer("1.0.0"); setTAuth(normalizeToolAuth()); setTSaveErr("");
      setToolEdit({});
    };

    const openEdit = (t) => {
      setTId(t.id); setTName(t.name); setTDesc(t.desc||""); setTCode(t.code); setTVer(t.version||"1.0.0"); setTAuth(normalizeToolAuth(t.auth)); setTSaveErr("");
      setToolEdit(t);
    };

    const saveTool = () => {
      if (!tName.trim()) { setTSaveErr("Tool name is required"); return; }
      // Extract schema from code
      let schema = null;
      try {
        const schemaMatch = tCode.match(/const\s+schema\s*=\s*(\{[\s\S]*?\});/);
        if (schemaMatch) {
          // eslint-disable-next-line no-new-func
          schema = new Function("return (" + schemaMatch[1] + ")")();
        }
      } catch(e) { /* schema parse failed, store null */ }
      const auth = normalizeToolAuth(tAuth);
      const entry = { id: tId, name: tName.trim(), desc: tDesc.trim(), code: tCode, version: tVer, schema, auth, author: "Admin", created: toolEdit?.created || Date.now() };
      if (toolEdit && toolEdit.id) {
        setTools(p => p.map(t => t.id === tId ? entry : t));
      } else {
        setTools(p => [...p, entry]);
      }
      showToast("Tool saved!");
      setToolEdit(null);
    };

    const deleteTool = (id) => {
      if (!window.confirm("Delete this tool?")) return;
      setTools(p => p.filter(t => t.id !== id));
      showToast("Tool deleted");
    };

    const exportTools = () => {
      const blob = new Blob([JSON.stringify(tools, null, 2)], {type:"application/json"});
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "chatfort-tools.json"; a.click();
    };

    const importTools = () => {
      const inp = document.createElement("input"); inp.type = "file"; inp.accept = ".json";
      inp.onchange = e => {
        const f = e.target.files[0]; if (!f) return;
        const reader = new FileReader();
        reader.onload = ev => {
          try {
            const imported = JSON.parse(ev.target.result);
            const arr = Array.isArray(imported) ? imported : [imported];
            setTools(p => {
              const ids = new Set(p.map(t=>t.id));
              const fresh = arr.filter(t=>t.id&&t.name&&t.code&&!ids.has(t.id));
              return [...p, ...fresh];
            });
            showToast("Imported " + arr.length + " tool(s)");
          } catch { showToast("Invalid JSON file"); }
        };
        reader.readAsText(f);
      };
      inp.click();
    };

    const filtered = tools.filter(t => !search || t.name.toLowerCase().includes(search.toLowerCase()) || (t.desc||"").toLowerCase().includes(search.toLowerCase()));

    // ── Tool Editor View ──
    if (toolEdit !== null) {
      return (
        <div style={{flex:1,overflowY:"auto",background:S.bg,display:"flex",flexDirection:"column"}}>
          {/* Header */}
          <div style={{padding:"14px 24px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:12,background:S.surface,flexShrink:0}}>
            <button onClick={()=>setToolEdit(null)} style={{background:"none",border:"none",color:S.text2,cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontSize:12,padding:0}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg> Tools
            </button>
            <span style={{color:S.border2}}>/</span>
            <span style={{fontSize:13,fontWeight:600,color:S.text}}>{toolEdit.id ? tName||"Edit Tool" : "New Tool"}</span>
            <div style={{marginLeft:"auto",display:"flex",gap:8}}>
              <Btn variant="secondary" onClick={()=>setToolEdit(null)}>Cancel</Btn>
              <Btn onClick={saveTool}>{IC.plus} Save Tool</Btn>
            </div>
          </div>
          {tSaveErr && <div style={{margin:"12px 24px 0",padding:"8px 14px",background:`${S.danger}15`,border:`1px solid ${S.danger}44`,borderRadius:8,fontSize:12,color:S.danger}}>{tSaveErr}</div>}
          <div style={{display:"flex",flex:1,overflow:"hidden"}}>
            {/* Left: metadata */}
            <div style={{width:280,minWidth:280,borderRight:`1px solid ${S.border}`,padding:20,overflowY:"auto",display:"flex",flexDirection:"column",gap:16}}>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:S.text3,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:6}}>Tool Name</label>
                <input value={tName} onChange={e=>setTName(e.target.value)} placeholder="e.g. Weather Lookup" style={{...ISt(),fontSize:13}}/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:S.text3,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:6}}>Tool ID</label>
                <input value={tId} readOnly style={{...ISt(),fontSize:11,fontFamily:"monospace",color:S.text3,background:S.surface2}}/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:S.text3,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:6}}>Version</label>
                <input value={tVer} onChange={e=>setTVer(e.target.value)} placeholder="1.0.0" style={{...ISt(),fontSize:13,fontFamily:"monospace"}}/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:S.text3,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:6}}>Tool Description</label>
                <textarea value={tDesc} onChange={e=>setTDesc(e.target.value)} placeholder="What does this tool do?" rows={4} style={{...ISt(),resize:"vertical",lineHeight:1.5,fontSize:13}}/>
              </div>
              <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:10,padding:12}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:10}}>
                  <label style={{fontSize:11,fontWeight:700,color:S.text3,textTransform:"uppercase",letterSpacing:"0.07em"}}>Authentication</label>
                  {tAuth.type !== "none" && <span style={{fontSize:9,padding:"2px 7px",borderRadius:20,background:S.accentDim,color:S.accent,fontFamily:"monospace",fontWeight:700}}>injected</span>}
                </div>
                <select value={tAuth.type} onChange={e=>setTAuth(a=>({...normalizeToolAuth(a),type:e.target.value}))} style={{...ISt(),fontSize:12,marginBottom:10}}>
                  <option value="none">No authentication</option>
                  <option value="api_key">API key header</option>
                  <option value="bearer">Bearer token</option>
                  <option value="basic">Basic auth</option>
                  <option value="client_credentials">OAuth client credentials</option>
                </select>
                {tAuth.type === "api_key" && (<div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <input value={tAuth.headerName} onChange={e=>setTAuth(a=>({...normalizeToolAuth(a),headerName:e.target.value}))} placeholder="Header name, e.g. X-API-Key" style={{...ISt(),fontSize:12}}/>
                  <input value={tAuth.apiKey} onChange={e=>setTAuth(a=>({...normalizeToolAuth(a),apiKey:e.target.value}))} placeholder="API key value" type="password" autoComplete="off" style={{...ISt(),fontSize:12}}/>
                </div>)}
                {tAuth.type === "bearer" && (<input value={tAuth.bearerToken} onChange={e=>setTAuth(a=>({...normalizeToolAuth(a),bearerToken:e.target.value}))} placeholder="Bearer token" type="password" autoComplete="off" style={{...ISt(),fontSize:12}}/>)}
                {tAuth.type === "basic" && (<div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <input value={tAuth.username} onChange={e=>setTAuth(a=>({...normalizeToolAuth(a),username:e.target.value}))} placeholder="Username / client ID" style={{...ISt(),fontSize:12}}/>
                  <input value={tAuth.password} onChange={e=>setTAuth(a=>({...normalizeToolAuth(a),password:e.target.value}))} placeholder="Password / secret" type="password" autoComplete="off" style={{...ISt(),fontSize:12}}/>
                </div>)}
                {tAuth.type === "client_credentials" && (<div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <input value={tAuth.tokenUrl} onChange={e=>setTAuth(a=>({...normalizeToolAuth(a),tokenUrl:e.target.value}))} placeholder="Token URL" style={{...ISt(),fontSize:12}}/>
                  <input value={tAuth.clientId} onChange={e=>setTAuth(a=>({...normalizeToolAuth(a),clientId:e.target.value}))} placeholder="Client ID" style={{...ISt(),fontSize:12}}/>
                  <input value={tAuth.clientSecret} onChange={e=>setTAuth(a=>({...normalizeToolAuth(a),clientSecret:e.target.value}))} placeholder="Client secret" type="password" autoComplete="off" style={{...ISt(),fontSize:12}}/>
                  <input value={tAuth.scope} onChange={e=>setTAuth(a=>({...normalizeToolAuth(a),scope:e.target.value}))} placeholder="Scope (optional)" style={{...ISt(),fontSize:12}}/>
                </div>)}
                <div style={{fontSize:10,color:S.text3,lineHeight:1.5,marginTop:10}}>
                  Tool code receives <code style={{fontFamily:"monospace",background:S.surface2,padding:"1px 5px",borderRadius:3}}>auth.headers</code>, <code style={{fontFamily:"monospace",background:S.surface2,padding:"1px 5px",borderRadius:3}}>secrets</code>, and <code style={{fontFamily:"monospace",background:S.surface2,padding:"1px 5px",borderRadius:3}}>auth.getAccessToken()</code> for client credentials.
                </div>
              </div>
              <div style={{background:S.accentDim,border:`1px solid ${S.border3}`,borderRadius:10,padding:12,fontSize:11,color:S.text2,lineHeight:1.6}}>
                <div style={{fontWeight:700,color:S.accent,marginBottom:6,fontSize:11}}>💡 How tools work</div>
                <div>Define a <code style={{fontFamily:"monospace",background:S.surface2,padding:"1px 5px",borderRadius:3}}>schema</code> object describing parameters, then an <code style={{fontFamily:"monospace",background:S.surface2,padding:"1px 5px",borderRadius:3}}>async function</code> that runs the logic.</div>
                <div style={{marginTop:8}}>The AI calls your function automatically when it needs the data. Results are passed back to the model.</div>
              </div>
            </div>
            {/* Right: code editor */}
            <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
              <div style={{padding:"8px 16px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:8,background:S.surface,flexShrink:0}}>
                {IC.code}
                <span style={{fontSize:11,color:S.text2,fontFamily:"monospace"}}>JavaScript</span>
                <span style={{fontSize:9,padding:"2px 8px",borderRadius:20,background:S.accentDim,color:S.accent,fontFamily:"monospace",marginLeft:4}}>ESNext</span>
                <span style={{marginLeft:"auto",fontSize:10,color:S.text3}}>Tools are function calling with arbitrary code execution</span>
              </div>
              <textarea
                value={tCode}
                onChange={e=>setTCode(e.target.value)}
                spellCheck={false}
                style={{flex:1,background:S.codeBlockBg,color:S.text,fontFamily:"'Fira Code','Cascadia Code','Consolas',monospace",fontSize:12.5,lineHeight:1.7,padding:"16px 20px",border:"none",resize:"none",outline:"none",overflowY:"auto",whiteSpace:"pre",tabSize:2}}
                onKeyDown={e=>{
                  if(e.key==="Tab"){e.preventDefault();const s=e.target.selectionStart,end=e.target.selectionEnd;const v=e.target.value;e.target.value=v.substring(0,s)+"  "+v.substring(end);e.target.selectionStart=e.target.selectionEnd=s+2;setTCode(e.target.value);}
                }}
              />
            </div>
          </div>
        </div>
      );
    }

    // ── Tool List View ──
    return (
      <div style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
        <div style={{maxWidth:900,margin:"0 auto"}}>
          {/* Header */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
                <h2 style={{fontSize:20,fontWeight:800,color:S.text}}>Tools</h2>
                <span style={{fontSize:11,padding:"2px 10px",borderRadius:20,background:S.accentDim,color:S.accent,fontFamily:"monospace",fontWeight:700}}>{tools.length}</span>
              </div>
              <p style={{fontSize:13,color:S.text2}}>JavaScript functions your agents can call at runtime.</p>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={importTools} style={{display:"flex",alignItems:"center",gap:5,padding:"7px 14px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>{IC.import_} Import</button>
              <button onClick={exportTools} disabled={tools.length===0} style={{display:"flex",alignItems:"center",gap:5,padding:"7px 14px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.inputBg,color:tools.length?S.text2:S.text3,fontSize:12,cursor:tools.length?"pointer":"default",fontFamily:"inherit"}}>{IC.export_} Export</button>
              <Btn onClick={openNew}>{IC.plus} New Tool</Btn>
            </div>
          </div>

          {/* Search */}
          <div style={{position:"relative",marginBottom:20}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2" style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)"}}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search tools…" style={{...ISt(),paddingLeft:32,fontSize:12}}/>
          </div>

          {tools.length === 0 ? (
            <div style={{textAlign:"center",padding:"60px 20px",color:S.text3}}>
              <div style={{fontSize:40,marginBottom:16}}>🔧</div>
              <div style={{fontSize:15,fontWeight:700,color:S.text2,marginBottom:8}}>No tools yet</div>
              <div style={{fontSize:13}}>Create a JavaScript function tool that your agents can call in real-time.</div>
            </div>
          ) : (
            <>
              {/* Tool cards */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:10,marginBottom:32}}>
                {filtered.map(t => (
                  <div key={t.id} style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:13,overflow:"hidden"}}>
                    <div style={{height:3,background:S.grad2}}/>
                    <div style={{padding:"14px 16px"}}>
                      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,marginBottom:8}}>
                        <div>
                          <div style={{fontSize:13,fontWeight:700,color:S.text,marginBottom:3}}>{t.name}</div>
                          <div style={{fontSize:10,fontFamily:"monospace",color:S.text3}}>v{t.version||"1.0.0"} · by {t.author||"Admin"}</div>
                        </div>
                        <div style={{display:"flex",gap:4,flexShrink:0}}>
                          <button onClick={()=>openEdit(t)} style={{width:26,height:26,borderRadius:6,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text3,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}} title="Edit">{IC.gear}</button>
                          <button onClick={()=>deleteTool(t.id)} style={{width:26,height:26,borderRadius:6,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.danger,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}} title="Delete">{IC.trash}</button>
                        </div>
                      </div>
                      {t.desc && <div style={{fontSize:12,color:S.text2,lineHeight:1.55,marginBottom:10}}>{t.desc}</div>}
                      {(t.schema?.name || (t.auth && t.auth.type && t.auth.type !== "none")) && <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>{t.schema?.name && <span style={{fontSize:9,fontFamily:"monospace",padding:"2px 8px",borderRadius:20,background:S.accentDim,color:S.accent,border:`1px solid ${S.border3}`}}>{t.schema.name}()</span>}{t.auth?.type && t.auth.type !== "none" && <span title={t.auth.type === "api_key" ? `Header: ${t.auth.headerName || "X-API-Key"}${t.auth.apiKey ? " · " + maskSecret(t.auth.apiKey) : ""}` : "Credentials are injected into the tool at runtime"} style={{fontSize:9,fontFamily:"monospace",padding:"2px 8px",borderRadius:20,background:S.inputBg,color:S.text3,border:`1px solid ${S.border2}`}}>🔐 {t.auth.type.replace("_"," ")}</span>}</div>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Community placeholder */}
              <div style={{borderTop:`1px solid ${S.border}`,paddingTop:24}}>
                <div style={{fontSize:12,fontWeight:700,color:S.text,marginBottom:4}}>Made by ChatFort Community</div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",borderRadius:10,border:`1px solid ${S.border2}`,background:S.cardBg,cursor:"pointer"}} onClick={()=>showToast("Community tools coming soon!")}>
                  <div>
                    <div style={{fontSize:13,color:S.accent,fontWeight:600}}>Discover a tool</div>
                    <div style={{fontSize:11,color:S.text3}}>Discover, download, and explore custom tools</div>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  function WorkspacesPage(){return(<div style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}><div style={{maxWidth:880,margin:"0 auto"}}><div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24}}><div className="cf-page-header"><h2 style={{fontSize:20,fontWeight:800,color:S.text,marginBottom:4}}>Workspaces</h2><p style={{fontSize:13,color:S.text2}}>Isolate matters by practice area.</p></div><Btn onClick={()=>setWsModal({edit:null})}>{IC.plus} New</Btn></div><div className="cf-grid-stagger" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:12}}>{workspaces.map(ws=>{const cnt=chats.filter(c=>c.wsId===ws.id).length;const isA=ws.id===wsId;return(<div key={ws.id} className="cf-page-card" onClick={()=>{setWsId(ws.id);showToast("Switched to "+ws.name);setSection("chat");}} style={{background:isA?`${S.accent}10`:S.cardBg,border:`1px solid ${isA?S.accent:S.border2}`,borderRadius:14,padding:18,cursor:"pointer",position:"relative",transition:"all 0.2s"}}>{isA&&<div style={{position:"absolute",top:0,left:0,right:0,height:2,background:S.grad2,borderRadius:"14px 14px 0 0"}}/>}<div style={{display:"flex",gap:12,marginBottom:12}}><div style={{width:40,height:40,borderRadius:12,background:`${ws.color}20`,border:`1px solid ${ws.color}30`,fontSize:19,display:"flex",alignItems:"center",justifyContent:"center"}}>{ws.icon}</div><div><div style={{fontSize:14,fontWeight:700,color:S.text,marginBottom:3}}>{ws.name}</div><div style={{fontSize:11,color:S.text2}}>{ws.desc}</div></div></div><div style={{display:"flex",alignItems:"center",justifyContent:"space-between",paddingTop:10,borderTop:`1px solid ${S.border}`}}><span style={{fontSize:10,color:S.text3,fontFamily:"monospace"}}>{cnt} chat{cnt!==1?"s":""}</span><div style={{display:"flex",gap:5,alignItems:"center"}}><button onClick={e=>{e.stopPropagation();setWsModal({edit:ws});}} style={{background:"none",border:"none",color:S.text3,cursor:"pointer",padding:3}}>{IC.edit}</button>{ws.id!=="ws-general"&&<button onClick={e=>{e.stopPropagation();if(window.confirm("Delete workspace?")){setWorkspaces(p=>p.filter(w=>w.id!==ws.id));setChats(p=>p.map(c=>c.wsId===ws.id?{...c,wsId:"ws-general"}:c));if(wsId===ws.id)setWsId("ws-general");}}} style={{background:"none",border:"none",color:S.danger,cursor:"pointer",padding:3}}>{IC.trash}</button>}<span style={{fontSize:9,padding:"3px 9px",borderRadius:20,fontFamily:"monospace",fontWeight:600,background:isA?S.accentDim:S.inputBg,color:isA?S.accent:S.text3,border:`1px solid ${isA?S.border3:S.border2}`}}>{isA?"\u25cf Active":"Switch"}</span></div></div></div>);})} <div onClick={()=>setWsModal({edit:null})} style={{background:S.inputBg,border:`1px dashed ${S.border2}`,borderRadius:14,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8,cursor:"pointer",minHeight:120,color:S.text3}} onMouseEnter={e=>{e.currentTarget.style.borderColor=S.accent;e.currentTarget.style.color=S.accent;}} onMouseLeave={e=>{e.currentTarget.style.borderColor=S.border2;e.currentTarget.style.color=S.text3;}}>{IC.plus}<span style={{fontSize:12}}>New Workspace</span></div></div></div></div>);}

  // Bots — embeddable, KB-grounded public chat widgets, promoted out of
  // the Settings card (#440 shipped it there; this gives it a real home
  // plus the per-bot system prompt the server always supported).
  function BotsPage(){
    const [botsData,setBotsData]=useState(null);
    const [botForm,setBotForm]=useState({name:"",kbId:"",origins:"",sysprompt:""});
    const [botBusy,setBotBusy]=useState(false);
    const loadBots=async()=>{
      try{const r=await fetch("/api/org/embed-bots",{headers:{"Authorization":`Bearer ${authToken}`}});if(r.ok)setBotsData(await r.json());}catch{}
    };
    useEffect(()=>{if(serverMode&&authToken)loadBots();},[]);
    const createBot=async()=>{
      if(!botForm.name.trim())return;
      setBotBusy(true);
      try{
        const r=await fetch("/api/org/embed-bots",{method:"POST",headers:{"Content-Type":"application/json","Authorization":`Bearer ${authToken}`},body:JSON.stringify(botForm)});
        const d=await r.json().catch(()=>({}));
        if(!r.ok)showToast(d.error||"Couldn't create bot");
        else{setBotForm({name:"",kbId:"",origins:"",sysprompt:""});showToast("Bot created ✓ — copy its embed code");loadBots();}
      }catch(e){showToast("Couldn't create bot: "+e.message);}
      setBotBusy(false);
    };
    const deleteBot=async(id)=>{
      if(!window.confirm("Delete this bot? Sites embedding it stop working immediately."))return;
      try{const r=await fetch(`/api/org/embed-bots/${id}`,{method:"DELETE",headers:{"Authorization":`Bearer ${authToken}`}});if(r.ok){showToast("Bot deleted");loadBots();}}catch{}
    };
    const snippet=(b)=>`<iframe src="${window.location.origin}/embed/${b.key}" style="width:380px;height:560px;border:1px solid #e2e8f0;border-radius:14px" title="${b.name}"></iframe>`;
    const inputSt={width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"9px 12px",color:S.text,fontSize:12,outline:"none",fontFamily:"inherit",boxSizing:"border-box"};
    return(
      <div className="cf-page-fade-in" style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
        <div style={{maxWidth:880,margin:"0 auto"}}>
          <div className="cf-page-header" style={{marginBottom:20}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
              <span style={{color:S.accent}}>{IC.bots}</span>
              <h2 style={{fontSize:20,fontWeight:800,color:S.text,margin:0}}>Bots</h2>
            </div>
            <p style={{fontSize:13,color:S.text2,margin:0}}>Public chat widgets for your website — grounded in your knowledge base, domain-locked, rate-limited, and billed against your plan like normal chat.</p>
          </div>
          {!botsData?(
            <div style={{padding:"24px 0",color:S.text3,fontSize:12}}>Loading…</div>
          ):!botsData.available?(
            <div className="cf-page-card" style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:24,display:"flex",alignItems:"center",gap:14}}>
              <div style={{flex:1,fontSize:13,color:S.text2,lineHeight:1.6}}>Put a knowledge-base-grounded assistant on your own website with one iframe tag. Included from the <b>Business</b> plan up.</div>
              <button onClick={()=>setSection("billing")} style={{padding:"9px 18px",borderRadius:9,border:"none",background:`linear-gradient(135deg,${S.accent},${S.accent2||S.accent})`,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Upgrade</button>
            </div>
          ):(
            <>
              {(botsData.bots||[]).length>0&&(
                <div className="cf-grid-stagger" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(360px,1fr))",gap:12,marginBottom:20}}>
                  {botsData.bots.map(b=>(
                    <div key={b.id} className="cf-page-card" style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
                      <div style={{height:3,background:`linear-gradient(90deg,${S.accent},${S.accent}66)`}}/>
                      <div style={{padding:16}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                          <span style={{fontSize:14,fontWeight:700,color:S.text}}>{b.name}</span>
                          <span style={{marginLeft:"auto",fontSize:10,fontFamily:"monospace",color:S.text3}}>{b.msgCount||0} messages</span>
                        </div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:10}}>
                          {b.kbId&&<span style={{fontSize:9,fontFamily:"monospace",color:S.text3,padding:"2px 7px",borderRadius:20,background:S.inputBg,border:`1px solid ${S.border2}`}}>📚 {(knowledgeBases.find(k=>k.id===b.kbId)?.name)||b.kbId}</span>}
                          <span style={{fontSize:9,fontFamily:"monospace",color:S.text3,padding:"2px 7px",borderRadius:20,background:S.inputBg,border:`1px solid ${S.border2}`}}>{(b.origins&&b.origins.length)?`${b.origins.length} allowed origin${b.origins.length===1?"":"s"}`:"any site"}</span>
                          {b.createdAt&&<span style={{fontSize:9,fontFamily:"monospace",color:S.text3,padding:"2px 7px",borderRadius:20,background:S.inputBg,border:`1px solid ${S.border2}`}}>{new Date(b.createdAt).toLocaleDateString()}</span>}
                        </div>
                        <code style={{display:"block",fontSize:9.5,padding:"7px 9px",borderRadius:7,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text2,overflowWrap:"anywhere",marginBottom:10,maxHeight:64,overflowY:"auto"}}>{snippet(b)}</code>
                        <div style={{display:"flex",gap:6}}>
                          <button onClick={()=>{try{navigator.clipboard.writeText(snippet(b));showToast("Embed code copied");}catch{}}} style={{padding:"5px 12px",borderRadius:7,background:S.accentDim,border:`1px solid ${S.accent}40`,color:S.accent,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Copy embed</button>
                          <a href={`/embed/${b.key}`} target="_blank" rel="noreferrer" style={{padding:"5px 12px",borderRadius:7,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text2,fontSize:11,fontWeight:600,textDecoration:"none"}}>Preview</a>
                          <button onClick={()=>deleteBot(b.id)} style={{marginLeft:"auto",padding:"5px 12px",borderRadius:7,background:"transparent",border:`1px solid ${S.danger}30`,color:S.danger,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Delete</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="cf-page-card" style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:18}}>
                <div style={{fontSize:13,fontWeight:700,color:S.text,marginBottom:12}}>Create a bot</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                  <input value={botForm.name} placeholder="Bot name (e.g. Support assistant)" onChange={e=>setBotForm(p=>({...p,name:e.target.value}))} style={inputSt}/>
                  <select value={botForm.kbId} onChange={e=>setBotForm(p=>({...p,kbId:e.target.value}))} style={{...inputSt,cursor:"pointer"}}>
                    <option value="">No knowledge base</option>
                    {knowledgeBases.map(kb=><option key={kb.id} value={kb.id}>{kb.name}</option>)}
                  </select>
                </div>
                <input value={botForm.origins} placeholder="Allowed origins, space-separated (e.g. https://example.com) — empty = any site" onChange={e=>setBotForm(p=>({...p,origins:e.target.value}))} style={{...inputSt,marginBottom:8}}/>
                <textarea value={botForm.sysprompt} rows={3} placeholder={"Optional system prompt — tone, scope, escalation (e.g. \"You are Acme's support assistant. Be concise. For billing issues, direct people to billing@acme.com.\")"} onChange={e=>setBotForm(p=>({...p,sysprompt:e.target.value}))} style={{...inputSt,resize:"vertical",marginBottom:10}}/>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:10,color:S.text3}}>Answers stay grounded in the selected knowledge base; off-KB questions get an honest "I don't know."</span>
                  <button onClick={createBot} disabled={botBusy||!botForm.name.trim()} style={{marginLeft:"auto",padding:"9px 18px",borderRadius:9,border:"none",background:botBusy||!botForm.name.trim()?S.border3:`linear-gradient(135deg,${S.accent},${S.accent2||S.accent})`,color:"#fff",fontSize:12,fontWeight:700,cursor:botBusy||!botForm.name.trim()?"default":"pointer",fontFamily:"inherit",flexShrink:0}}>{botBusy?"Creating…":"Create bot"}</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  function AgentsPage(){return(<div style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}><div style={{maxWidth:880,margin:"0 auto"}}><div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24}}><div className="cf-page-header"><h2 style={{fontSize:20,fontWeight:800,color:S.text,marginBottom:4}}>Agents</h2><p style={{fontSize:13,color:S.text2}}>Custom AI for law, finance, healthcare.</p></div><div style={{display:"flex",gap:8}}><button onClick={()=>setLibraryOpen(true)} style={{padding:"8px 14px",borderRadius:10,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text2,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:6}}>📚 Browse library</button><Btn onClick={()=>setAgModal({edit:null})}>{IC.plus} New Agent</Btn></div></div><div className="cf-grid-stagger" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:12}}>{agents.map(ag=>{const pv=PROVIDERS[ag.provider];const m=pv?.models?.find(m=>m.id===ag.model);const isHermes=ag.provider==="hermes";const hermesReady=isHermes&&(integrations.hermes?.key||integrations.hermes?.useOpenRouter);return(<div key={ag.id} className="cf-page-card" style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden",opacity:isHermes&&!hermesReady?0.75:1}}><div style={{height:3,background:`linear-gradient(90deg,${ag.color},${ag.color}88)`}}/><div style={{padding:16,display:"flex",gap:12}}><div style={{width:44,height:44,borderRadius:13,background:`${ag.color}18`,border:`1px solid ${ag.color}30`,fontSize:22,display:"flex",alignItems:"center",justifyContent:"center"}}>{ag.emoji}</div><div style={{flex:1,minWidth:0}}><div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}><div style={{fontSize:14,fontWeight:700,color:S.text}}>{ag.name}</div>{isHermes&&<span style={{fontSize:8,padding:"1px 5px",borderRadius:3,fontFamily:"monospace",fontWeight:700,background:"#f59e0b18",color:"#f59e0b",border:"1px solid #f59e0b30",flexShrink:0}}>HERMES</span>}</div><div style={{fontSize:11,color:S.text2,lineHeight:1.55}}>{ag.desc}</div>{isHermes&&!hermesReady&&<div style={{fontSize:9,color:S.warning||"#f59e0b",marginTop:4,fontFamily:"monospace"}}>⚠ Connect Hermes in Integrations</div>}</div></div><div style={{padding:"10px 16px 14px",borderTop:`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}><span style={{fontSize:9,fontFamily:"monospace",color:S.text3,padding:"2px 7px",borderRadius:20,background:S.inputBg,border:`1px solid ${S.border2}`}}>{m?.name||ag.model.split("/").pop()}</span>{pv&&<span style={{fontSize:9,fontFamily:"monospace",color:S.text3,padding:"2px 7px",borderRadius:20,background:S.inputBg,border:`1px solid ${S.border2}`}}>{pv.icon} {pv.name}</span>}{ag.hermesAgent&&<span style={{fontSize:9,fontFamily:"monospace",color:"#f59e0b",padding:"2px 7px",borderRadius:20,background:"#f59e0b10",border:"1px solid #f59e0b30"}}>🔧 Tools</span>}<div style={{marginLeft:"auto",display:"flex",gap:5}}>{ag.id!=="ag-default"&&<button onClick={()=>{if(!window.confirm(`Delete the "${ag.name}" agent? Chats that used it keep their history and fall back to the default assistant.`))return;setAgents(p=>p.filter(x=>x.id!==ag.id));if(agId===ag.id)setAgId("ag-default");showToast("Agent deleted");}} title="Delete this agent" style={{padding:"4px 10px",borderRadius:7,fontSize:11,border:`1px solid ${S.danger}30`,background:"transparent",color:S.danger,cursor:"pointer"}}>Delete</button>}<button onClick={()=>setAgModal({edit:ag})} style={{padding:"4px 10px",borderRadius:7,fontSize:11,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,cursor:"pointer"}}>Edit</button><button onClick={()=>{if(isHermes&&!hermesReady){setSection("integrations");showToast("Connect Hermes first!");return;}setAgId(ag.id);newChat(ag.id);showToast("Started!");}} style={{padding:"4px 12px",borderRadius:7,fontSize:11,background:isHermes&&!hermesReady?"none":`linear-gradient(135deg,${ag.color},${ag.color}cc)`,border:isHermes&&!hermesReady?`1px solid ${ag.color}55`:"none",color:isHermes&&!hermesReady?ag.color:"#fff",cursor:"pointer",fontWeight:600}}>{isHermes&&!hermesReady?"Setup":"Chat"}</button></div></div></div>);})} <div onClick={()=>setAgModal({edit:null})} style={{background:S.inputBg,border:`1px dashed ${S.border2}`,borderRadius:14,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8,cursor:"pointer",minHeight:140,color:S.text3}} onMouseEnter={e=>{e.currentTarget.style.borderColor=S.accent;e.currentTarget.style.color=S.accent;}} onMouseLeave={e=>{e.currentTarget.style.borderColor=S.border2;e.currentTarget.style.color=S.text3;}}>{IC.plus}<span style={{fontSize:12}}>New Agent</span></div></div></div></div>);}

  function AuditPage() {
    const [entries, setEntries] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [since, setSince] = useState("");
    const [until, setUntil] = useState("");
    const [filter, setFilter] = useState("");
    const [planErr, setPlanErr] = useState(null); // {error, requiredPlan} when server returns 402

    const load = useCallback(async (cursor) => {
      if (!serverMode || !authToken) return;
      setLoading(true);
      const qs = new URLSearchParams();
      qs.set("limit", "100");
      if (cursor) qs.set("cursor", cursor);
      if (since) qs.set("since", new Date(since).toISOString());
      if (until) qs.set("until", new Date(until).toISOString());
      try {
        const r = await fetch("/api/audit?" + qs.toString(), { headers: { "Authorization": `Bearer ${authToken}` } });
        if (r.status === 402) {
          const d = await r.json().catch(() => ({}));
          setPlanErr(d); setEntries([]); setNextCursor(null);
        } else {
          setPlanErr(null);
          const d = await r.json();
          if (cursor) setEntries(p => [...p, ...(d.entries || [])]);
          else setEntries(d.entries || []);
          setNextCursor(d.nextCursor);
        }
      } catch { /* noop */ }
      setLoading(false);
    }, [since, until]);

    useEffect(() => { load(null); }, [load]);

    const downloadCsv = async () => {
      const qs = new URLSearchParams();
      if (since) qs.set("since", new Date(since).toISOString());
      if (until) qs.set("until", new Date(until).toISOString());
      const r = await fetch("/api/audit/export.csv?" + qs.toString(), { headers: { "Authorization": `Bearer ${authToken}` } });
      if (!r.ok) { showToast("Export failed"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `audit-log-${new Date().toISOString().slice(0,10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
      showToast("CSV downloaded");
    };

    const filtered = filter
      ? entries.filter(e => (e.action + " " + (e.actor_email||"") + " " + (e.target_id||"")).toLowerCase().includes(filter.toLowerCase()))
      : entries;

    const actionColor = (a) => {
      if (a.startsWith("auth.login.failure")) return S.danger;
      if (a.startsWith("auth.")) return "#8b5cf6";
      if (a.startsWith("user.")) return "#f59e0b";
      if (a.startsWith("kb.")) return S.accent;
      if (a.startsWith("config.") || a.startsWith("org.")) return "#d4a017";
      return S.text2;
    };

    return (
      <div className="cf-page-fade-in" style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
            <div>
              <h2 style={{fontSize:20,fontWeight:800,color:S.text,marginBottom:4}}>Audit log</h2>
              <p style={{fontSize:13,color:S.text2}}>Append-only record of every admin action. Hand a CSV to your auditor.</p>
            </div>
            <button onClick={downloadCsv} className="cf-btn-lift" style={{padding:"9px 16px",borderRadius:10,background:S.accentDim,border:`1px solid ${S.accent}44`,color:S.accent,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>⬇ Export CSV</button>
          </div>

          {/* Filters */}
          <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
            <input type="date" value={since} onChange={e=>setSince(e.target.value)} style={{padding:"8px 12px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text,fontFamily:"inherit",fontSize:12}}/>
            <span style={{alignSelf:"center",color:S.text3,fontSize:11}}>to</span>
            <input type="date" value={until} onChange={e=>setUntil(e.target.value)} style={{padding:"8px 12px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text,fontFamily:"inherit",fontSize:12}}/>
            <input type="text" placeholder="Search action, email, target…" value={filter} onChange={e=>setFilter(e.target.value)} style={{flex:1,minWidth:200,padding:"8px 12px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text,fontFamily:"inherit",fontSize:12}}/>
            <button onClick={()=>load(null)} style={{padding:"8px 14px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Refresh</button>
          </div>

          {planErr && (
            <div className="cf-upgrade-banner" style={{marginBottom:16,padding:"16px 18px",borderRadius:12,background:S.accentDim,border:`1px solid ${S.accent}55`,color:S.text,display:"flex",alignItems:"flex-start",gap:14}}>
              <div style={{fontSize:24,lineHeight:1,flexShrink:0}}>🔒</div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:700,marginBottom:4}}>Audit log requires the {({trial:"Trial",starter:"Starter",pro:"Professional",business:"Business",enterprise:"Enterprise"})[planErr.requiredPlan] || "Business"} plan</div>
                <div style={{fontSize:12,color:S.text2,lineHeight:1.55}}>Upgrade to access read-only audit history and CSV export. Events are still being recorded — they'll appear here after you upgrade.</div>
              </div>
            </div>
          )}
          <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:12,overflow:"hidden"}}>
            <div style={{display:"grid",gridTemplateColumns:"160px 180px 1fr 120px 180px",gap:8,padding:"10px 14px",background:S.surface2,borderBottom:`1px solid ${S.border}`,fontSize:9,fontWeight:700,color:S.text3,fontFamily:"monospace",textTransform:"uppercase"}}>
              <div>When</div><div>Actor</div><div>Action · Target</div><div>IP</div><div>Details</div>
            </div>
            {loading && entries.length === 0 && (
              <div style={{padding:30,display:"flex",flexDirection:"column",alignItems:"center",gap:10,color:S.text3,fontSize:12}}>
                <span className="cf-auth-spinner" style={{display:"inline-flex"}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={S.border2} strokeWidth="2.5"/><path d="M21 12a9 9 0 0 0-9-9" stroke={S.accent} strokeWidth="2.5" strokeLinecap="round"/></svg>
                </span>
                <span>Loading…</span>
              </div>
            )}
            {!loading && !planErr && filtered.length === 0 && (
              <div className="cf-empty-state" style={{padding:"50px 30px",textAlign:"center",color:S.text3}}>
                <div style={{fontSize:32,marginBottom:8,opacity:0.6}}>📜</div>
                <div style={{fontSize:13,fontWeight:600,color:S.text2,marginBottom:4}}>No audit entries</div>
                <div style={{fontSize:11,color:S.text3}}>{filter || since || until ? "Try clearing filters above." : "Admin actions will appear here as they happen."}</div>
              </div>
            )}
            {filtered.map(e => (
              <div key={e.id} className="cf-audit-row" style={{display:"grid",gridTemplateColumns:"160px 180px 1fr 120px 180px",gap:8,padding:"10px 14px",borderBottom:`1px solid ${S.border}`,fontSize:11,color:S.text2,alignItems:"center"}}>
                <div style={{fontFamily:"monospace",fontSize:10,color:S.text3}}>{new Date(e.ts).toLocaleString()}</div>
                <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  <div style={{color:S.text,fontSize:11}}>{e.actor_email || "—"}</div>
                  <div style={{fontSize:9,color:S.text3,fontFamily:"monospace"}}>{e.actor_role || "—"}</div>
                </div>
                <div style={{overflow:"hidden"}}>
                  <span style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:actionColor(e.action)}}>{e.action}</span>
                  {e.target_type && <span style={{marginLeft:8,fontSize:10,color:S.text3,fontFamily:"monospace"}}>→ {e.target_type}:{(e.target_id||"—").slice(0,16)}</span>}
                </div>
                <div style={{fontFamily:"monospace",fontSize:10,color:S.text3}}>{e.source_ip || "—"}</div>
                <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={JSON.stringify(e.details)}>
                  {e.details && Object.keys(e.details).length > 0 ? JSON.stringify(e.details) : "—"}
                </div>
              </div>
            ))}
            {nextCursor && (
              <div style={{padding:"12px",textAlign:"center",borderTop:`1px solid ${S.border}`}}>
                <button onClick={()=>load(nextCursor)} disabled={loading} style={{padding:"7px 14px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text2,fontSize:11,cursor:loading?"default":"pointer",fontFamily:"inherit",display:"inline-flex",alignItems:"center",gap:7}}>{loading && (<span className="cf-auth-spinner" style={{display:"inline-flex"}}><svg width="12" height="12" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={S.border2} strokeWidth="3"/><path d="M21 12a9 9 0 0 0-9-9" stroke={S.accent} strokeWidth="3" strokeLinecap="round"/></svg></span>)}{loading?"Loading…":"Load more"}</button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  function OwnerConsolePage() {
    const [orgs, setOrgs] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [expanded, setExpanded] = useState(null); // orgId whose user list is open
    const [expandedUsers, setExpandedUsers] = useState({});
    const [showCreate, setShowCreate] = useState(false);
    const [modelsModalOrg, setModelsModalOrg] = useState(null); // org whose model config is open
    const [createForm, setCreateForm] = useState({ name:"", plan:"trial", trialDays:"", adminName:"", adminEmail:"", adminPassword:"", err:"", busy:false });
    // Slice C1c: per-org current-period usage rollup, lazily fetched on row
    // expansion. orgId -> usage shape (or { error } / { loading: true }).
    const [orgUsage, setOrgUsage] = useState({});

    const load = useCallback(async () => {
      if (!serverMode || !authToken) return;
      setLoading(true);
      try {
        const [oR, sR] = await Promise.all([
          fetch("/api/owner/orgs", { headers: { "Authorization": `Bearer ${authToken}` } }),
          fetch("/api/owner/stats", { headers: { "Authorization": `Bearer ${authToken}` } }),
        ]);
        if (oR.ok) setOrgs(await oR.json());
        if (sR.ok) setStats(await sR.json());
      } catch {}
      setLoading(false);
    }, []);
    useEffect(() => { load(); }, [load]);

    const changePlan = async (orgId, plan) => {
      const r = await fetch(`/api/orgs/${orgId}/plan`, {
        method: "PUT", headers: {"Content-Type":"application/json","Authorization":`Bearer ${authToken}`},
        body: JSON.stringify({ plan })
      });
      if (!r.ok) { const d = await r.json().catch(()=>({})); showToast(d.error || "Plan change failed"); return; }
      showToast(`Plan → ${plan}`);
      load();
    };
    const changeBillingMode = async (orgId, mode) => {
      const r = await fetch(`/api/owner/orgs/${orgId}/billing-mode`, {
        method: "PUT", headers: {"Content-Type":"application/json","Authorization":`Bearer ${authToken}`},
        body: JSON.stringify({ mode })
      });
      if (!r.ok) { const d = await r.json().catch(()=>({})); showToast(d.error || "Update failed"); return; }
      showToast(`Billing mode → ${mode === "custom" ? "custom" : "self-service"}`);
      load();
    };
    const changeVariant = async (orgId, variant) => {
      const r = await fetch(`/api/owner/orgs/${orgId}/billing-variant`, {
        method: "PUT", headers: {"Content-Type":"application/json","Authorization":`Bearer ${authToken}`},
        body: JSON.stringify({ variant })
      });
      if (!r.ok) { const d = await r.json().catch(()=>({})); showToast(d.error || "Update failed"); return; }
      showToast(`Billing → ${variant}`);
      load();
    };
	    const resetTrialTokens = async (org) => {
	      const limit = org.trialUserTokenLimit || 500000;
	      const label = `${Math.round(limit / 1000).toLocaleString()}k`;
	      if (!window.confirm(`Reset trial token allowance for "${org.name}"? Every user in this tenant will get the full ${label}-token trial allowance again from now.`)) return;
      const r = await fetch(`/api/owner/orgs/${org.id}/trial-token-reset`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${authToken}` }
      });
      if (!r.ok) {
        const d = await r.json().catch(()=>({}));
        showToast(d.error || "Trial token reset failed");
        return;
      }
	      showToast(`Trial tokens reset for ${org.name}`);
	      load();
	    };
	    const resetTrialWindow = async (org) => {
	      if (!window.confirm(`Reset the 14-day trial for "${org.name}"? This will set the trial end date to 14 days from now without changing tokens, seats, users, or analytics.`)) return;
	      const r = await fetch(`/api/owner/orgs/${org.id}/trial-reset`, {
	        method: "POST",
	        headers: { "Authorization": `Bearer ${authToken}` }
	      });
	      if (!r.ok) {
	        const d = await r.json().catch(()=>({}));
	        showToast(d.error || "Trial reset failed");
	        return;
	      }
	      showToast(`Trial reset for ${org.name}: +14 days`);
	      load();
	    };
	    const toggleSuspend = async (org) => {
      const next = !org.suspended;
      if (next && !window.confirm(`Suspend "${org.name}"? Users won't be able to log in.`)) return;
      const r = await fetch(`/api/owner/orgs/${org.id}/suspend`, {
        method: "POST", headers: {"Content-Type":"application/json","Authorization":`Bearer ${authToken}`},
        body: JSON.stringify({ suspended: next })
      });
      if (!r.ok) { showToast("Suspend failed"); return; }
      showToast(next ? "Suspended" : "Unsuspended");
      load();
    };
    const deleteOrg = async (org) => {
      const name = window.prompt(`Type the org name "${org.name}" to confirm permanent deletion:`);
      if (name !== org.name) { if (name != null) showToast("Name didn't match — cancelled"); return; }
      const r = await fetch(`/api/owner/orgs/${org.id}?confirm=yes-delete-everything`, {
        method: "DELETE", headers: { "Authorization": `Bearer ${authToken}` }
      });
      if (!r.ok) { const d = await r.json().catch(()=>({})); showToast(d.error || "Delete failed"); return; }
      const d = await r.json();
      showToast(`Deleted ${org.name} (${d.deletedUsers} users)`);
      load();
    };
    const loadUsers = async (orgId) => {
      if (expanded === orgId) { setExpanded(null); return; }
      setExpanded(orgId);
      // Slice C1c: kick off the usage fetch in parallel with the user list.
      loadOrgUsage(orgId);
      if (expandedUsers[orgId]) return;
      const r = await fetch(`/api/owner/orgs/${orgId}/users`, { headers: { "Authorization": `Bearer ${authToken}` } });
      if (!r.ok) return;
      const list = await r.json();
      setExpandedUsers(p => ({ ...p, [orgId]: list }));
    };
    // Slice C1c: load (or refresh) one org's current-period usage.
    const loadOrgUsage = async (orgId) => {
      setOrgUsage(p => ({ ...p, [orgId]: { ...(p[orgId] || {}), loading: true } }));
      try {
        const r = await fetch(`/api/owner/orgs/${orgId}/usage/current`, { headers: { "Authorization": `Bearer ${authToken}` } });
        if (!r.ok) {
          setOrgUsage(p => ({ ...p, [orgId]: { error: r.status === 503 ? "stripe_not_configured" : ("status_" + r.status) } }));
          return;
        }
        const d = await r.json();
        setOrgUsage(p => ({ ...p, [orgId]: d }));
      } catch (e) {
        setOrgUsage(p => ({ ...p, [orgId]: { error: String(e && e.message ? e.message : e) } }));
      }
    };
    const reloadUsers = async (orgId) => {
      const r = await fetch(`/api/owner/orgs/${orgId}/users`, { headers: { "Authorization": `Bearer ${authToken}` } });
      if (!r.ok) return;
      const list = await r.json();
      setExpandedUsers(p => ({ ...p, [orgId]: list }));
    };
    // Toggle a user's role between "admin" (their current) and "superadmin".
    // Server enforces that only a superadmin can grant/revoke the superadmin role.
    const toggleOwnerRole = async (user, orgId) => {
      const makeOwner = user.role !== "superadmin";
      if (makeOwner && !window.confirm(`Promote ${user.email} to owner (superadmin)? They'll be able to manage every tenant and access this console.`)) return;
      if (!makeOwner && user.id === authUser.id) { showToast("You can't remove your own owner role."); return; }
      if (!makeOwner && !window.confirm(`Remove owner role from ${user.email}? They'll revert to org admin.`)) return;
      const r = await fetch(`/api/admin/users/${user.id}`, {
        method: "PUT", headers: {"Content-Type":"application/json","Authorization":`Bearer ${authToken}`},
        body: JSON.stringify({ role: makeOwner ? "superadmin" : "admin" })
      });
      if (!r.ok) { const d = await r.json().catch(()=>({})); showToast(d.error || "Role change failed"); return; }
      showToast(makeOwner ? "Owner granted ✓" : "Owner removed");
      reloadUsers(orgId);
    };

    const submitCreate = async () => {
      const { name, plan, trialDays, adminName, adminEmail, adminPassword } = createForm;
      if (!name.trim() || !adminName.trim() || !adminEmail.trim() || adminPassword.length < 8) {
        setCreateForm(p => ({ ...p, err: "All fields required. Password must be 8+ chars." })); return;
      }
      setCreateForm(p => ({ ...p, busy: true, err: "" }));
      try {
        const body = { name: name.trim(), plan, adminName: adminName.trim(), adminEmail: adminEmail.trim(), adminPassword };
        const days = parseInt(trialDays, 10);
        if (plan !== "trial" && days > 0) body.trialDays = days;
        const r = await fetch("/api/owner/orgs", {
          method: "POST", headers: {"Content-Type":"application/json","Authorization":`Bearer ${authToken}`},
          body: JSON.stringify(body)
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Create failed");
        showToast(`Created ${name.trim()} (${d.org.id})${body.trialDays?` — ${body.trialDays}-day trial`:""}`);
        setShowCreate(false);
        setCreateForm({ name:"", plan:"trial", trialDays:"", adminName:"", adminEmail:"", adminPassword:"", err:"", busy:false });
        load();
      } catch (e) {
        setCreateForm(p => ({ ...p, busy: false, err: e.message }));
      }
    };

    const filtered = filter
      ? orgs.filter(o => (o.name + " " + o.id + " " + o.plan).toLowerCase().includes(filter.toLowerCase()))
      : orgs;

    const planColor = (p) => p === "enterprise" ? "#d4a017" : p === "premium" ? "#f59e0b" : p === "business" ? "#8b5cf6" : p === "pro" ? S.accent : S.text3;

    return (
      <div className="cf-page-fade-in" style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
        <div style={{maxWidth:1200,margin:"0 auto"}}>
          <div style={{marginBottom:20,display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10}}>
            <div>
              <h2 style={{fontSize:20,fontWeight:800,color:S.text,marginBottom:4}}>👑 Owner console</h2>
              <p style={{fontSize:13,color:S.text2}}>All tenants on this deployment. Change plans, suspend, delete.</p>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <button onClick={()=>{
                setCreateForm({ name:"", plan:"enterprise", trialDays:"14", adminName:"", adminEmail:"", adminPassword:"", err:"", busy:false });
                setShowCreate(true);
              }} style={{padding:"8px 14px",borderRadius:8,background:"#d4a01722",border:`1px solid #d4a01755`,color:"#d4a017",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>👑 Enterprise trial (14 days)</button>
              <button onClick={()=>setShowCreate(true)} style={{padding:"8px 14px",borderRadius:8,background:`linear-gradient(135deg,${S.accent},${S.accent2})`,border:"none",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ New organization</button>
              <button onClick={load} disabled={loading} style={{padding:"8px 14px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Refresh</button>
            </div>
          </div>

          {/* Create-org modal */}
          {showCreate && (
            <div onClick={()=>!createForm.busy && setShowCreate(false)} style={{position:"fixed",inset:0,zIndex:300,background:"rgba(0,0,0,0.6)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
              <div onClick={e=>e.stopPropagation()} style={{background:S.surface,border:`1px solid ${S.border2}`,borderRadius:18,width:"min(520px, 100%)",padding:24,boxShadow:"0 24px 80px rgba(0,0,0,0.4)"}}>
                <div style={{fontSize:17,fontWeight:800,color:S.text,marginBottom:4}}>Create organization</div>
                <div style={{fontSize:12,color:S.text3,marginBottom:18}}>Spin up a new tenant + its first admin. They'll be forced to change the password on first login.</div>
                {createForm.err && <div style={{background:S.danger+"18",border:`1px solid ${S.danger}40`,borderRadius:8,padding:"8px 12px",marginBottom:12,fontSize:12,color:S.danger}}>{createForm.err}</div>}
                <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:10,marginBottom:10}}>
                  <input type="text" placeholder="Organization name" value={createForm.name} onChange={e=>setCreateForm(p=>({...p,name:e.target.value,err:""}))}
                    style={{background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"10px 12px",color:S.text,fontFamily:"inherit",fontSize:13}}/>
                  <select value={createForm.plan} onChange={e=>setCreateForm(p=>({...p,plan:e.target.value,err:""}))}
                    style={{background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"10px 12px",color:S.text,fontFamily:"inherit",fontSize:13}}>
                    <option value="trial">Trial</option>
                    <option value="starter">Starter</option>
                    <option value="pro">Professional</option>
                    <option value="business">Business</option>
                    <option value="premium">Premium</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                {createForm.plan !== "trial" && (
                  <div style={{marginBottom:10}}>
                    <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>TRIAL WINDOW (days, blank = no expiry)</div>
                    <input type="number" min="1" max="365" placeholder="e.g. 14" value={createForm.trialDays} onChange={e=>setCreateForm(p=>({...p,trialDays:e.target.value,err:""}))}
                      style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"10px 12px",color:S.text,fontFamily:"monospace",fontSize:13}}/>
                    <div style={{fontSize:10,color:S.text3,marginTop:4,lineHeight:1.5}}>After the trial expires, the org auto-downgrades to the Trial plan. Only issuable from this console — never from self-serve signup.</div>
                  </div>
                )}
                <div style={{fontSize:10,fontWeight:700,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.06em",marginTop:14,marginBottom:8}}>First admin</div>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  <input type="text" placeholder="Admin name" value={createForm.adminName} onChange={e=>setCreateForm(p=>({...p,adminName:e.target.value,err:""}))}
                    style={{background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"10px 12px",color:S.text,fontFamily:"inherit",fontSize:13}}/>
                  <input type="email" placeholder="Admin email" value={createForm.adminEmail} onChange={e=>setCreateForm(p=>({...p,adminEmail:e.target.value,err:""}))}
                    style={{background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"10px 12px",color:S.text,fontFamily:"inherit",fontSize:13}}/>
                  <input type="text" placeholder="Temp password (8+ chars)" value={createForm.adminPassword} onChange={e=>setCreateForm(p=>({...p,adminPassword:e.target.value,err:""}))}
                    style={{background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"10px 12px",color:S.text,fontFamily:"monospace",fontSize:12}}/>
                </div>
                <div style={{display:"flex",gap:10,marginTop:18,justifyContent:"flex-end"}}>
                  <button onClick={()=>setShowCreate(false)} disabled={createForm.busy} style={{padding:"9px 16px",borderRadius:9,background:"transparent",border:`1px solid ${S.border2}`,color:S.text2,fontSize:12,cursor:createForm.busy?"default":"pointer",fontFamily:"inherit"}}>Cancel</button>
                  <button onClick={submitCreate} disabled={createForm.busy} style={{padding:"9px 18px",borderRadius:9,border:"none",background:createForm.busy?S.border3:`linear-gradient(135deg,${S.accent},${S.accent2})`,color:"#fff",fontSize:12,fontWeight:700,cursor:createForm.busy?"default":"pointer",fontFamily:"inherit"}}>
                    {createForm.busy ? "Creating…" : "Create organization"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Stats row */}
          {stats && (() => {
            const mrr = stats.mrrEstimateUsd || 0;
            const tokenCost = stats.tokenCostUsdMonth || 0;
            const infraCost = stats.infraCostUsdMonth || 0;
            const totalCost = tokenCost + infraCost;
            const netMargin = mrr - totalCost;
            const netColor = netMargin >= 0 ? S.success : S.danger;
            const tokenTip = stats.pricedTokensPct != null
              ? `${(stats.platformTokensMonth || 0).toLocaleString()} tokens this month across managed orgs.\n${stats.pricedTokensPct}% priced from the per-model table${stats.pricedTokensPct < 100 ? ` · ${100 - stats.pricedTokensPct}% used default fallback rate` : ""}.\nBYOK orgs excluded (they pay their provider directly).`
              : `Estimate across managed orgs. Excludes BYOK.`;
            const infraTip = `Render Standard ($25) + Postgres ($7). Override with INFRA_COST_USD_MONTH env var.`;
            return (
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10,marginBottom:20}}>
                {[
                  ["Organizations", stats.orgCount, S.accent, null],
                  ["Total users", stats.userCount, S.text, null],
                  ["Suspended", stats.suspendedCount, stats.suspendedCount > 0 ? S.danger : S.text3, null],
                  ["MRR estimate", `$${mrr.toLocaleString()}`, "#d4a017", "Sum of per-seat prices across non-suspended orgs. Real MRR lands with Stripe."],
                  ["Token cost (est)", `$${tokenCost.toLocaleString()}`, S.danger, tokenTip],
                  ["Infra cost", `$${infraCost.toLocaleString()}`, S.text2, infraTip],
                  ["Net margin", `${netMargin >= 0 ? "$" : "-$"}${Math.abs(netMargin).toLocaleString()}`, netColor, `MRR − (tokens + infra)`],
                  ["Audit entries", stats.auditEntries?.toLocaleString() || "0", S.text2, null],
                ].map(([l, v, c, tip]) => (
                  <div key={l} title={tip || undefined} style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:12,padding:"12px 14px",cursor:tip?"help":"default"}}>
                    <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{l}</div>
                    <div style={{fontSize:20,fontWeight:800,color:c,fontFamily:"monospace"}}>{v}</div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Compute health — heap headroom on the Render instance */}
          {stats?.compute && (() => {
            const c = stats.compute;
            const pct = c.heapPct || 0;
            const color = pct > 90 ? S.danger : pct > 75 ? (S.warning || "#f59e0b") : S.success;
            const label = pct > 90 ? "Critical" : pct > 75 ? "High" : "Healthy";
            const advice = pct > 90
              ? "Upgrade Render — at risk of OOM. Consider Pro ($85/mo, 4GB RAM)."
              : pct > 75
              ? "Watch this — a sustained spike here is your early signal to upgrade."
              : "Plenty of room — no action needed.";
            const uptimeH = Math.floor((c.uptimeSeconds || 0) / 3600);
            return (
              <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:12,padding:"14px 16px",marginBottom:20}}>
                <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:8,gap:10,flexWrap:"wrap"}}>
                  <div>
                    <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>Compute headroom</div>
                    <div style={{fontSize:13,color:S.text}}>
                      <span style={{fontWeight:700,color}}>{label}</span>
                      <span style={{color:S.text3}}> · {c.heapUsedMB} / {c.heapLimitMB} MB heap · RSS {c.rssMB} MB · up {uptimeH}h</span>
                    </div>
                  </div>
                  <span style={{fontFamily:"monospace",fontSize:18,fontWeight:800,color}}>{pct}%</span>
                </div>
                <div style={{height:8,borderRadius:4,background:S.inputBg,overflow:"hidden",border:`1px solid ${S.border2}`}}>
                  <div style={{width:`${Math.min(100, pct)}%`,height:"100%",background:color,transition:"width 0.4s ease"}}/>
                </div>
                <div style={{fontSize:11,color:S.text3,marginTop:8,lineHeight:1.5}}>{advice}</div>
              </div>
            );
          })()}

          {/* Per-plan breakdown */}
          {stats?.perPlan && (
            <div style={{display:"flex",gap:8,marginBottom:20,flexWrap:"wrap"}}>
              {Object.entries(stats.perPlan).map(([p, n]) => (
                <span key={p} style={{fontSize:11,padding:"4px 10px",borderRadius:20,background:`${planColor(p)}15`,color:planColor(p),border:`1px solid ${planColor(p)}35`,fontFamily:"monospace",fontWeight:700}}>
                  {p.toUpperCase()} · {n}
                </span>
              ))}
            </div>
          )}

          {/* Platform providers — read-only view of which server env keys
              are live. These back every customer's "built-in" models and are
              deliberately hidden from the customer-facing Integrations page. */}
          <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:12,padding:"14px 18px",marginBottom:20}}>
            <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:10}}>
              <div style={{fontSize:12,fontWeight:700,color:S.text2}}>🔐 Platform providers</div>
              <div style={{fontSize:10,color:S.text3}}>Managed via Render env vars. Not visible to customer admins.</div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:8}}>
              {[
                { id: "anthropic",  label: "Anthropic",  env: "ANTHROPIC_API_KEY" },
                { id: "openai",     label: "OpenAI",     env: "OPENAI_API_KEY" },
                { id: "openrouter", label: "OpenRouter", env: "OPENROUTER_API_KEY" },
                { id: "groq",       label: "Groq",       env: "GROQ_API_KEY" },
                { id: "mistral",    label: "Mistral",    env: "MISTRAL_API_KEY" },
                { id: "hermes",     label: "Nous Hermes", env: "HERMES_API_KEY" },
                { id: "azure",      label: "Azure OpenAI (BAA)", env: "AZURE_OPENAI_API_KEY", baa: true },
                { id: "bedrock",    label: "AWS Bedrock (BAA)",  env: "AWS_ACCESS_KEY_ID",   baa: true },
              ].map(p => {
                const active = p.baa ? !!baaUpstreams[p.id] : !!integrations[p.id]?.managedByServer;
                return (
                  <div key={p.id} style={{padding:"9px 12px",borderRadius:9,border:`1px solid ${active?S.success+"55":S.border2}`,background:active?S.success+"08":"transparent",display:"flex",alignItems:"center",gap:8}}>
                    <span style={{width:8,height:8,borderRadius:"50%",background:active?S.success:S.border3,flexShrink:0}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:11,fontWeight:600,color:S.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.label}</div>
                      <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{active?"active":"not set"}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Filter */}
          <input type="text" placeholder="Search by name, id, or plan…" value={filter} onChange={e=>setFilter(e.target.value)}
            style={{width:"100%",padding:"10px 14px",borderRadius:9,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text,fontFamily:"inherit",fontSize:13,marginBottom:16}}/>

          {/* Org table */}
          <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:12,overflow:"hidden"}}>
            <div style={{display:"grid",gridTemplateColumns:"1.3fr 0.8fr 0.7fr 0.8fr 0.8fr 0.55fr 1.3fr",gap:10,padding:"10px 14px",background:S.surface2,borderBottom:`1px solid ${S.border}`,fontSize:9,fontWeight:700,color:S.text3,fontFamily:"monospace",textTransform:"uppercase"}}>
              <div>Organization</div><div>Plan</div><div>Billing</div><div>Seats</div><div>Tokens</div><div>Status</div><div style={{textAlign:"right"}}>Actions</div>
            </div>
            {loading && orgs.length === 0 && (
              <div style={{padding:"30px",textAlign:"center",color:S.text3,fontSize:12,display:"flex",flexDirection:"column",alignItems:"center",gap:10}}>
                <span className="cf-auth-spinner" style={{display:"inline-flex"}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={S.border2} strokeWidth="2.5"/><path d="M21 12a9 9 0 0 0-9-9" stroke={S.accent} strokeWidth="2.5" strokeLinecap="round"/></svg>
                </span>
                <span>Loading orgs…</span>
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="cf-empty-state cf-owner-empty" style={{padding:"50px 30px",textAlign:"center",color:S.text3}}>
                <div style={{fontSize:32,marginBottom:8,opacity:0.6}}>🏢</div>
                <div style={{fontSize:13,fontWeight:600,color:S.text2,marginBottom:4}}>No organizations match</div>
                <div style={{fontSize:11,color:S.text3}}>{filter ? "Try clearing the filter above." : "Customers haven't signed up yet."}</div>
              </div>
            )}
            {filtered.map(o => {
              const seatPct = o.seatsLimit ? Math.round((o.seatsUsed / o.seatsLimit) * 100) : 0;
              const tokPct = o.tokensLimit ? Math.round((o.tokensUsedThisMonth / o.tokensLimit) * 100) : 0;
              const isExpanded = expanded === o.id;
              const usersInOrg = expandedUsers[o.id] || [];
              return (
                <div key={o.id}>
                  <div className="cf-owner-row" style={{display:"grid",gridTemplateColumns:"1.3fr 0.8fr 0.7fr 0.8fr 0.8fr 0.55fr 1.3fr",gap:10,padding:"12px 14px",borderBottom:`1px solid ${S.border}`,fontSize:12,color:S.text2,alignItems:"center"}}>
                    <div>
                      <div style={{color:S.text,fontWeight:600,fontSize:13}}>{o.name}{o.ssoEnabled && <span title="SSO enabled" style={{marginLeft:6}}>🔐</span>}</div>
                      <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",marginTop:2}}>{o.id}</div>
                    </div>
                    <div>
                      <select value={o.plan} onChange={e=>changePlan(o.id, e.target.value)}
                        style={{background:`${planColor(o.plan)}15`,color:planColor(o.plan),border:`1px solid ${planColor(o.plan)}35`,borderRadius:6,padding:"4px 8px",fontFamily:"monospace",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                        <option value="trial">trial</option>
                        <option value="starter">starter</option>
                        <option value="pro">pro</option>
                        <option value="business">business</option>
                        <option value="premium">premium</option>
                        <option value="enterprise">enterprise</option>
                      </select>
                      <select value={o.billing_mode||"self_service"} onChange={e=>changeBillingMode(o.id, e.target.value)} title="Billing mode shown to this org" style={{marginTop:5,background:S.inputBg,color:S.text2,border:`1px solid ${S.border2}`,borderRadius:6,padding:"3px 7px",fontFamily:"monospace",fontSize:9,fontWeight:700,cursor:"pointer",display:"block"}}>
                        <option value="self_service">self-service billing</option>
                        <option value="custom">custom billing</option>
                      </select>
                      {/* Trial countdown under the plan selector — more
                          prominent than the old tiny-text treatment. */}
                      {o.trial_ends_at && (() => {
                        const ms = new Date(o.trial_ends_at).getTime() - Date.now();
                        const days = Math.ceil(ms / 86400000);
                        const hours = Math.max(0, Math.ceil(ms / 3600000));
                        const expired = days <= 0;
                        const color = expired ? S.danger : days <= 3 ? (S.warning || "#f59e0b") : "#d4a017";
                        const label = expired ? "⏳ EXPIRED" : days === 1 ? `⏳ ${hours}h LEFT` : `⏳ ${days}d LEFT`;
                        return (
                          <div title={`Trial ends ${new Date(o.trial_ends_at).toLocaleString()}`}
                            style={{marginTop:5,fontSize:9,padding:"2px 7px",borderRadius:10,background:color+"22",color,border:`1px solid ${color}55`,fontFamily:"monospace",fontWeight:700,display:"inline-block"}}>
                            {label}
                          </div>
                        );
                      })()}
                    </div>
                    <div>
                      <select value={o.billing_variant || "managed"} onChange={e=>changeVariant(o.id, e.target.value)}
                        style={{background:S.inputBg,color:o.billing_variant==="byok"?"#4caf87":S.text2,border:`1px solid ${o.billing_variant==="byok"?"#4caf8755":S.border2}`,borderRadius:6,padding:"4px 8px",fontFamily:"monospace",fontSize:10,fontWeight:700,cursor:"pointer"}}
                        title="Managed = we supply tokens & enforce cap. BYOK = customer provides provider keys; no cap.">
                        <option value="managed">managed</option>
                        <option value="byok">byok</option>
                      </select>
                    </div>
                    <div style={{fontFamily:"monospace",fontSize:11}}>
                      <div style={{color:seatPct >= 85 ? S.danger : S.text2}}>{o.seatsUsed}{o.seatsLimit == null ? "" : " / " + o.seatsLimit}</div>
                      {o.seatsLimit != null && <div style={{fontSize:9,color:S.text3}}>{seatPct}%</div>}
                    </div>
	                    <div style={{fontFamily:"monospace",fontSize:11}}>
	                      <div style={{color:tokPct >= 85 ? S.danger : S.text2}}>{(o.tokensUsedThisMonth || 0).toLocaleString()}</div>
	                      {o.tokensLimit != null && <div style={{fontSize:9,color:S.text3}}>{tokPct}% of {(o.tokensLimit/1000).toFixed(0)}k</div>}
	                      {o.trialUserTokenLimit != null && <div style={{fontSize:9,color:"#d4a017"}}>{(o.trialUserTokenLimit/1000).toFixed(0)}k/user trial</div>}
	                      {o.trialTokenResetAt && <div style={{fontSize:9,color:S.text3}} title={`Trial token allowance reset ${new Date(o.trialTokenResetAt).toLocaleString()}`}>reset {new Date(o.trialTokenResetAt).toLocaleDateString()}</div>}
	                    </div>
                    <div>
                      {o.suspended
                        ? <span style={{fontSize:9,padding:"2px 7px",borderRadius:10,background:S.danger+"18",color:S.danger,border:`1px solid ${S.danger}35`,fontFamily:"monospace",fontWeight:700}}>SUSPENDED</span>
                        : <span style={{fontSize:9,padding:"2px 7px",borderRadius:10,background:S.success+"18",color:S.success,border:`1px solid ${S.success}35`,fontFamily:"monospace",fontWeight:700}}>ACTIVE</span>}
                    </div>
                    <div style={{display:"flex",gap:5,justifyContent:"flex-end",flexWrap:"wrap"}}>
                      <button onClick={()=>setModelsModalOrg(o)} style={{padding:"4px 9px",borderRadius:6,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text3,fontSize:10,cursor:"pointer"}}>Models</button>
                      <button onClick={()=>loadUsers(o.id)} style={{padding:"4px 9px",borderRadius:6,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text3,fontSize:10,cursor:"pointer"}}>{isExpanded ? "Hide users" : "Users"}</button>
	                      {o.trialUserTokenLimit != null && (
	                        <button
	                          onClick={()=>resetTrialTokens(o)}
	                          title={`Reset every user in ${o.name} back to the full ${(o.trialUserTokenLimit/1000).toFixed(0)}k trial token allowance.`}
	                          style={{padding:"4px 9px",borderRadius:6,background:"#d4a01712",border:"1px solid #d4a01755",color:"#d4a017",fontSize:10,cursor:"pointer"}}
                        >
	                          Reset tokens
	                        </button>
	                      )}
	                      {o.id !== "org-owner" && (o.billing_variant || "managed") !== "byok" && (
	                        <button
	                          onClick={()=>resetTrialWindow(o)}
	                          title={`Set ${o.name}'s trial end date to 14 days from now.`}
	                          style={{padding:"4px 9px",borderRadius:6,background:(S.success || "#22c55e")+"12",border:`1px solid ${(S.success || "#22c55e")}55`,color:S.success || "#22c55e",fontSize:10,cursor:"pointer"}}
	                        >
	                          Reset trial
	                        </button>
	                      )}
	                      <button onClick={()=>toggleSuspend(o)} style={{padding:"4px 9px",borderRadius:6,background:S.inputBg,border:`1px solid ${S.border2}`,color:o.suspended ? S.success : S.text2,fontSize:10,cursor:"pointer"}}>{o.suspended ? "Unsuspend" : "Suspend"}</button>
                      {o.id !== "org-owner" && (
                        <button onClick={()=>deleteOrg(o)} style={{padding:"4px 9px",borderRadius:6,background:S.danger+"0d",border:`1px solid ${S.danger}35`,color:S.danger,fontSize:10,cursor:"pointer"}}>Delete</button>
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="cf-owner-expand" style={{padding:"10px 14px 14px 30px",background:S.surface2,borderBottom:`1px solid ${S.border}`}}>
                      <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",marginBottom:6}}>Users in this org</div>
                      {usersInOrg.length === 0
                        ? <div style={{color:S.text3,fontSize:11}}>Loading…</div>
                        : <div style={{display:"flex",flexDirection:"column",gap:6}}>
                            {usersInOrg.map(u => (
                              <div key={u.id} style={{display:"flex",gap:10,alignItems:"center",fontSize:11,color:S.text2}}>
                                <span style={{color:S.text,minWidth:140}}>{u.name}</span>
                                <span style={{fontFamily:"monospace",color:S.text3,fontSize:10,flex:1}}>{u.email}</span>
                                <span style={{fontFamily:"monospace",fontSize:9,padding:"1px 6px",borderRadius:8,background:u.role==="superadmin"?"#d4a01718":u.role==="admin"?S.danger+"18":S.accent+"18",color:u.role==="superadmin"?"#d4a017":u.role==="admin"?S.danger:S.accent}}>{u.role}</span>
                                {u.ssoOnly && <span style={{fontFamily:"monospace",fontSize:9,color:S.text3}}>SSO</span>}
                                {/* "Make owner" is hard-gated to users in the
                                    home/owner org. Superadmin is cross-tenant
                                    and must never belong to a customer tenant.
                                    The server blocks this too — the UI hide
                                    is just so the button isn't even clickable. */}
                                {u.role !== "member" && u.role !== "viewer" && u.role !== "analyst" && o.id === "org-owner" && (
                                  <button onClick={()=>toggleOwnerRole(u, o.id)}
                                    style={{padding:"3px 9px",borderRadius:6,background:u.role==="superadmin"?"#d4a01710":"transparent",border:`1px solid ${u.role==="superadmin"?"#d4a017":S.border2}`,color:u.role==="superadmin"?"#d4a017":S.text2,fontSize:10,cursor:"pointer",fontFamily:"inherit"}}
                                    title={u.role==="superadmin" ? "Remove owner privileges" : "Promote to owner (superadmin)"}>
                                    {u.role==="superadmin" ? "Remove owner" : "Make owner"}
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>}
                      {/* Slice C1c: Billable usage card (current period) */}
                      {(() => {
                        const u = orgUsage[o.id];
                        return (
                          <div style={{marginTop:14,padding:"10px 12px",background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:10}}>
                            <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>
                              Billable usage — current period
                            </div>
                            {(!u || u.loading) ? (
                              <div style={{color:S.text3,fontSize:11}}>Loading…</div>
                            ) : u.error ? (
                              <div style={{color:S.text3,fontSize:11}}>
                                {u.error === "stripe_not_configured"
                                  ? "Stripe not configured — usage tracked but not billed."
                                  : "Couldn't load: " + u.error}
                              </div>
                            ) : (
                              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10,fontSize:11}}>
                                <div>
                                  <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",marginBottom:2}}>OVERAGE TOKENS</div>
                                  <div style={{color:u.overageTokens>0?(S.warning||"#f59e0b"):S.text2,fontFamily:"monospace",fontWeight:600}}>
                                    {u.overageTokens > 0 ? u.overageTokens.toLocaleString() : "—"}
                                  </div>
                                </div>
                                <div>
                                  <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",marginBottom:2}}>EST. OVERAGE CHARGE</div>
                                  <div style={{color:u.overageTokens>0?(S.warning||"#f59e0b"):S.text2,fontFamily:"monospace",fontWeight:600}}>
                                    {u.overageTokens > 0 && u.overageMeteredConfigured
                                      ? "billable"
                                      : u.overageTokens > 0 && !u.overageMeteredConfigured
                                      ? "set " + u.overageMeteredEnv
                                      : "—"}
                                  </div>
                                </div>
                                <div>
                                  <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",marginBottom:2}}>CAP</div>
                                  <div style={{color:S.text2,fontFamily:"monospace"}}>
                                    {u.cap == null ? "Unlimited" : u.cap.toLocaleString()}
                                  </div>
                                </div>
                                <div>
                                  <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",marginBottom:2}}>LAST REPORTED</div>
                                  <div style={{color:S.text2,fontFamily:"monospace"}}>
                                    {u.reportedToStripeAt
                                      ? new Date(u.reportedToStripeAt).toLocaleString()
                                      : "Not yet reported"}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {modelsModalOrg && <OrgModelsModal org={modelsModalOrg} onClose={()=>setModelsModalOrg(null)} />}
        </div>
      </div>
    );
  }

  // Per-org model-config editor — superadmin sets which platform models this
  // tenant sees + adds custom models (extra OpenRouter IDs, Azure deployments)
  // that don't belong in the global catalog. Pulls the full platform model
  // list from PROVIDERS at render time so it stays in sync with code updates.
  function OrgModelsModal({ org, onClose }) {
    const [allowedSet, setAllowedSet] = useState(null); // null = unrestricted
    const [customs, setCustoms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [newCustom, setNewCustom] = useState({ id: "", name: "", provider: "openrouter", desc: "" });
    useEffect(() => {
      fetch(`/api/owner/orgs/${org.id}/models`, { headers: { "Authorization": `Bearer ${authToken}` }})
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (!d) { setLoading(false); return; }
          setAllowedSet(Array.isArray(d.allowed_models) ? new Set(d.allowed_models) : null);
          setCustoms(Array.isArray(d.custom_models) ? d.custom_models : []);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }, [org.id]);
    const allPlatformModels = Object.entries(PROVIDERS).flatMap(([pid, p]) =>
      p.models.map(m => ({ ...m, provider: pid, providerName: p.name, providerColor: p.color }))
    );
    const unrestricted = allowedSet === null;
    const save = async () => {
      setBusy(true);
      try {
        const body = {
          allowed_models: unrestricted ? null : Array.from(allowedSet || []),
          custom_models: customs,
        };
        const r = await fetch(`/api/owner/orgs/${org.id}/models`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
          body: JSON.stringify(body),
        });
        const d = await r.json();
        if (!r.ok) { showToast(d.error || "Save failed"); setBusy(false); return; }
        showToast(`Models updated for ${org.name}`);
        onClose();
      } catch (e) { showToast("Save failed: " + e.message); }
      setBusy(false);
    };
    const addCustom = () => {
      if (!newCustom.id.trim() || !newCustom.name.trim()) { showToast("Custom model needs an ID and a name"); return; }
      if (customs.find(c => c.id === newCustom.id.trim())) { showToast("That model ID is already added"); return; }
      setCustoms(p => [...p, { id: newCustom.id.trim(), name: newCustom.name.trim(), provider: newCustom.provider, desc: newCustom.desc.trim() || undefined }]);
      setNewCustom({ id: "", name: "", provider: "openrouter", desc: "" });
    };
    const removeCustom = (id) => setCustoms(p => p.filter(c => c.id !== id));
    return (
      <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:350,background:"rgba(0,0,0,0.6)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
        <div onClick={e=>e.stopPropagation()} style={{background:S.surface,border:`1px solid ${S.border2}`,borderRadius:18,width:"min(780px, 100%)",maxHeight:"90vh",display:"flex",flexDirection:"column"}}>
          <div style={{padding:"16px 22px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div>
              <div style={{fontSize:16,fontWeight:800,color:S.text}}>Models — {org.name}</div>
              <div style={{fontSize:11,color:S.text3,fontFamily:"monospace",marginTop:2}}>{org.id}</div>
            </div>
            <button onClick={onClose} style={{background:"none",border:"none",color:S.text2,cursor:"pointer",padding:4,fontSize:18}}>×</button>
          </div>
          <div style={{padding:"14px 22px",overflowY:"auto"}}>
            {loading && (
              <div style={{padding:30,display:"flex",flexDirection:"column",alignItems:"center",gap:10,color:S.text3,fontSize:12}}>
                <span className="cf-auth-spinner" style={{display:"inline-flex"}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={S.border2} strokeWidth="2.5"/><path d="M21 12a9 9 0 0 0-9-9" stroke={S.accent} strokeWidth="2.5" strokeLinecap="round"/></svg>
                </span>
                <span>Loading…</span>
              </div>
            )}
            {!loading && <>
              {/* Platform-model allowlist */}
              <div style={{marginBottom:22}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:8}}>
                  <div style={{fontSize:12,fontWeight:700,color:S.text}}>Platform models</div>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>setAllowedSet(null)} style={{padding:"3px 10px",borderRadius:6,background:unrestricted?S.accentDim:"transparent",border:`1px solid ${unrestricted?S.accent:S.border2}`,color:unrestricted?S.accent:S.text3,fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>All (unrestricted)</button>
                    <button onClick={()=>setAllowedSet(new Set())} style={{padding:"3px 10px",borderRadius:6,background:"transparent",border:`1px solid ${S.border2}`,color:S.text3,fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>None</button>
                  </div>
                </div>
                <div style={{fontSize:11,color:S.text3,marginBottom:10,lineHeight:1.5}}>{unrestricted ? "This org sees every platform model." : `This org sees ${(allowedSet?.size)||0} of ${allPlatformModels.length} models.`}</div>
                {!unrestricted && (
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4,maxHeight:260,overflowY:"auto",padding:6,border:`1px solid ${S.border2}`,borderRadius:9}}>
                    {allPlatformModels.map(m => {
                      const on = allowedSet.has(m.id);
                      return (
                        <label key={m.provider+":"+m.id} style={{display:"flex",alignItems:"center",gap:7,padding:"5px 9px",borderRadius:6,border:`1px solid ${on?S.accent+"55":S.border2}`,cursor:"pointer",background:on?S.accentDim:"none",overflow:"hidden"}}>
                          <input type="checkbox" checked={on} onChange={e=>{
                            const next = new Set(allowedSet); if (e.target.checked) next.add(m.id); else next.delete(m.id);
                            setAllowedSet(next);
                          }}/>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:11,color:S.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.name}</div>
                            <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.providerName}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Custom models */}
              <div>
                <div style={{fontSize:12,fontWeight:700,color:S.text,marginBottom:4}}>Custom models</div>
                <div style={{fontSize:11,color:S.text3,marginBottom:10,lineHeight:1.5}}>Extra models appended to this org's picker. Route through the specified provider (OpenRouter / Azure / Bedrock). They bypass the allowlist — adding one here means the org sees it.</div>
                <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:10}}>
                  {customs.length === 0 && <div style={{padding:"10px 12px",borderRadius:8,background:S.inputBg,border:`1px dashed ${S.border2}`,fontSize:11,color:S.text3,textAlign:"center"}}>No custom models yet.</div>}
                  {customs.map(c => (
                    <div key={c.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 11px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:600,color:S.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</div>
                        <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginTop:2}}>{c.provider} · {c.id}</div>
                        {c.desc && <div style={{fontSize:10,color:S.text3,marginTop:2}}>{c.desc}</div>}
                      </div>
                      <button onClick={()=>removeCustom(c.id)} style={{padding:"3px 9px",borderRadius:6,background:"transparent",border:`1px solid ${S.danger}40`,color:S.danger,fontSize:10,cursor:"pointer"}}>Remove</button>
                    </div>
                  ))}
                </div>
                <div style={{padding:"10px 12px",borderRadius:9,background:S.inputBg,border:`1px solid ${S.border2}`}}>
                  <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:6}}>ADD CUSTOM MODEL</div>
                  <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:6,marginBottom:6}}>
                    <input placeholder="Model ID (e.g. openai/gpt-4o or anthropic/claude-sonnet-4-5)" value={newCustom.id} onChange={e=>setNewCustom(p=>({...p,id:e.target.value}))}
                      style={{background:S.surface,border:`1px solid ${S.border2}`,borderRadius:7,padding:"7px 10px",color:S.text,fontFamily:"monospace",fontSize:11}}/>
                    <select value={newCustom.provider} onChange={e=>setNewCustom(p=>({...p,provider:e.target.value}))}
                      style={{background:S.surface,border:`1px solid ${S.border2}`,borderRadius:7,padding:"7px 10px",color:S.text,fontSize:11}}>
                      <option value="openrouter">OpenRouter</option>
                      <option value="openai">OpenAI (Azure if BAA)</option>
                      <option value="anthropic">Anthropic</option>
                    </select>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:8}}>
                    <input placeholder="Display name (e.g. Llama Special)" value={newCustom.name} onChange={e=>setNewCustom(p=>({...p,name:e.target.value}))}
                      style={{background:S.surface,border:`1px solid ${S.border2}`,borderRadius:7,padding:"7px 10px",color:S.text,fontSize:11}}/>
                    <input placeholder="Description (optional)" value={newCustom.desc} onChange={e=>setNewCustom(p=>({...p,desc:e.target.value}))}
                      style={{background:S.surface,border:`1px solid ${S.border2}`,borderRadius:7,padding:"7px 10px",color:S.text,fontSize:11}}/>
                  </div>
                  <button onClick={addCustom} style={{padding:"6px 14px",borderRadius:7,background:S.accentDim,border:`1px solid ${S.accent}40`,color:S.accent,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ Add model</button>
                </div>
              </div>
            </>}
          </div>
          <div style={{padding:"12px 22px",borderTop:`1px solid ${S.border}`,display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button onClick={onClose} style={{padding:"9px 16px",borderRadius:9,background:"transparent",border:`1px solid ${S.border2}`,color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            <button onClick={save} disabled={busy || loading} style={{padding:"9px 18px",borderRadius:9,border:"none",background:busy?S.border3:`linear-gradient(135deg,${S.accent},${S.accent2})`,color:"#fff",fontSize:12,fontWeight:700,cursor:busy?"default":"pointer",fontFamily:"inherit"}}>{busy ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>
    );
  }

  // Brand icon for a marketplace card. Tries Simple Icons (monochrome SVG
  // rendered in the provider's brand color) and falls back to the emoji on
  // CDN error or when the entry has no slug (verticals like Epic/Clio).
  function MktIcon({ slug, emoji, color, size = 20 }) {
    const [failed, setFailed] = useState(false);
    if (!slug || failed) return <span style={{ fontSize: size - 2 }}>{emoji}</span>;
    const hex = (color || "#888").replace("#", "");
    return (
      <img src={`https://cdn.simpleicons.org/${slug}/${hex}`}
           alt=""
           style={{ width: size, height: size, objectFit: "contain" }}
           onError={() => setFailed(true)} />
    );
  }

  function MarketplacePage() {
    const [data, setData] = useState({ items: [], installed: {}, slackClientConfigured: false, slackSigningConfigured: false, slackBotTokenConfigured: false, slackConfigured: false, slackSetup: null });
    const [loading, setLoading] = useState(true);
    const [zapierUrl, setZapierUrl] = useState("");
    const [zapierOpen, setZapierOpen] = useState(false);
    // Generic paste-URL state used by Discord / Teams / Mattermost /
    // Rocket.Chat / Generic webhook (mirrors the Zapier pattern).
    const [pasteOpen, setPasteOpen] = useState(null); // service id or null
    const [pasteUrl, setPasteUrl] = useState("");
    // Generic credentials modal (driven by item.auth.fields).
    const [credsOpen, setCredsOpen] = useState(null); // item object or null
    const [credsValues, setCredsValues] = useState({}); // { fieldKey: value }

    const load = useCallback(async () => {
      setLoading(true);
      try {
        const r = await fetch("/api/marketplace", { headers: { "Authorization": `Bearer ${authToken}` }});
        if (r.ok) setData(await r.json());
      } catch {}
      setLoading(false);
    }, []);
    useEffect(() => { load(); }, [load]);

    // Surface the ?slack=connected redirect param as a toast then clean the URL
    useEffect(() => {
      if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("slack") === "connected") {
        showToast("Slack connected ✓");
        history.replaceState(null, "", window.location.pathname);
        load();
      }
    }, [load]);

    const connect = async (id) => {
      if (id === "slack") {
        const slackFullyConfigured = data.slackConfigured ?? ((data.slackClientConfigured && data.slackSigningConfigured) || (data.slackBotTokenConfigured && data.slackSigningConfigured));
        if (!slackFullyConfigured) { showToast("Slack setup is missing env vars. Use the setup URLs shown on this page, then set OAuth credentials or SLACK_BOT_TOKEN plus SLACK_SIGNING_SECRET."); return; }
        // Fetch (with auth header) → get the Slack authorize URL → navigate to it.
        // Can't navigate directly to /connect: a top-level link drops the Bearer.
        try {
          const r = await fetch("/api/marketplace/slack/connect", { headers: { "Authorization": `Bearer ${authToken}` }});
          const d = await r.json().catch(()=>({}));
          if (r.ok && d.installed) { showToast("Slack connected ✓"); load(); return; }
          if (!r.ok || !d.url) { showToast(d.error || "Slack connect failed"); return; }
          window.location.href = d.url;
        } catch (e) { showToast("Slack connect failed: " + e.message); }
        return;
      }
      if (id === "gdrive") {
        await openGoogleDrivePicker();
        return;
      }
      if (id === "zapier") { setZapierOpen(true); return; }
      if (["discord","teams","mattermost","rocketchat","webhook"].includes(id)) {
        setPasteOpen(id); setPasteUrl("");
        return;
      }
      // Generic credentials path: open the multi-field modal driven by
      // the integration's auth spec returned by GET /api/marketplace.
      const item = (data.items || []).find(m => m.id === id);
      if (item && item.auth && Array.isArray(item.auth.fields)) {
        setCredsOpen(item);
        setCredsValues({});
        return;
      }
      showToast("Coming soon — ping sales for a preview.");
    };
    const disconnect = async (id) => {
      if (!window.confirm(`Disconnect ${id}?`)) return;
      const r = await fetch(`/api/marketplace/${id}`, { method: "DELETE", headers: { "Authorization": `Bearer ${authToken}` }});
      if (!r.ok) { showToast("Disconnect failed"); return; }
      showToast("Disconnected");
      load();
    };
    const PASTE_MODAL = () => {
      if (!pasteOpen) return null;
      const h = PASTE_HINTS[pasteOpen];
      return (
        <Modal open={!!pasteOpen} title={`Connect ${pasteOpen.charAt(0).toUpperCase() + pasteOpen.slice(1)}`} onClose={()=>{setPasteOpen(null); setPasteUrl("");}} footer={
          <>
            <button onClick={()=>{setPasteOpen(null); setPasteUrl("");}} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${S.border2}`,background:"none",color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            <button onClick={savePaste} disabled={!pasteUrl.trim()} className="cf-btn-lift" style={{padding:"7px 14px",borderRadius:8,border:"none",background:pasteUrl.trim()?S.accent:S.border3,color:"#fff",fontSize:12,fontWeight:700,cursor:pasteUrl.trim()?"pointer":"default",fontFamily:"inherit"}}>Save</button>
          </>
        }>
          <div style={{fontSize:12,color:S.text3,marginBottom:8,lineHeight:1.55}}>{h.help}</div>
          <input value={pasteUrl} onChange={e=>setPasteUrl(e.target.value)} autoFocus placeholder={h.placeholder} style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text,fontSize:12,fontFamily:"monospace"}}/>
        </Modal>
      );
    };
    const saveZapier = async () => {
      const r = await fetch("/api/marketplace/zapier", {
        method: "PUT", headers: {"Content-Type":"application/json","Authorization":`Bearer ${authToken}`},
        body: JSON.stringify({ webhook_url: zapierUrl.trim() })
      });
      if (!r.ok) { const d = await r.json().catch(()=>({})); showToast(d.error || "Save failed"); return; }
      showToast("Zapier connected ✓");
      setZapierOpen(false); setZapierUrl(""); load();
    };
    const saveCreds = async () => {
      if (!credsOpen) return;
      const r = await fetch("/api/marketplace/" + credsOpen.id, {
        method: "PUT",
        headers: { "Content-Type":"application/json", "Authorization": `Bearer ${authToken}` },
        body: JSON.stringify(credsValues),
      });
      if (!r.ok) { const d = await r.json().catch(()=>({})); showToast(d.error || "Save failed"); return; }
      showToast((credsOpen.name || credsOpen.id) + " credentials saved ✓");
      setCredsOpen(null); setCredsValues({}); load();
    };
    const CREDS_MODAL = () => {
      if (!credsOpen) return null;
      const fields = (credsOpen.auth && credsOpen.auth.fields) || [];
      const authType = credsOpen.auth && credsOpen.auth.type;
      const typeLabel = authType ? authType.replace(/_/g, " ") : "credentials";
      const allFilled = fields.every(f => {
        if (f.required === false) return true;
        const v = credsValues[f.key];
        return v != null && String(v).trim();
      });
      return (
        <Modal open={!!credsOpen} title={`Connect ${credsOpen.name || credsOpen.id}`} onClose={()=>{setCredsOpen(null); setCredsValues({});}} footer={
          <>
            <button onClick={()=>{setCredsOpen(null); setCredsValues({});}} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${S.border2}`,background:"none",color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            <button onClick={saveCreds} disabled={!allFilled} className="cf-btn-lift" style={{padding:"7px 14px",borderRadius:8,border:"none",background:allFilled?S.accent:S.border3,color:"#fff",fontSize:12,fontWeight:700,cursor:allFilled?"pointer":"default",fontFamily:"inherit"}}>Save</button>
          </>
        }>
          <div style={{fontSize:11,color:S.text3,marginBottom:10,padding:"7px 10px",borderRadius:7,background:S.inputBg,border:`1px dashed ${S.border2}`,lineHeight:1.55}}>
            <strong style={{color:S.text2,textTransform:"capitalize"}}>{typeLabel}</strong> · Credentials are stored per-organization. Vendor-specific features ship incrementally as we build each API client. Drop credentials now to be first in line.
          </div>
          {fields.map((f) => (
            <FR key={f.key} label={(f.label || f.key).toUpperCase()}>
              {f.type === "textarea" ? (
                <textarea
                  value={credsValues[f.key] || ""}
                  onChange={e=>setCredsValues(p=>({...p, [f.key]: e.target.value}))}
                  placeholder={f.placeholder || ""}
                  spellCheck={false}
                  style={{width:"100%",minHeight:90,padding:"9px 12px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.codeBlockBg,color:S.text,fontSize:12,fontFamily:"monospace"}}
                />
              ) : (
                <input
                  type={f.type === "password" ? "password" : "text"}
                  autoComplete={f.type === "password" ? "new-password" : "off"}
                  value={credsValues[f.key] || ""}
                  onChange={e=>setCredsValues(p=>({...p, [f.key]: e.target.value}))}
                  placeholder={f.placeholder || ""}
                  style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text,fontSize:13,fontFamily:f.type==="password"?"monospace":"inherit"}}
                />
              )}
            </FR>
          ))}
        </Modal>
      );
    };
    const savePaste = async () => {
      if (!pasteOpen) return;
      const r = await fetch("/api/marketplace/" + pasteOpen, {
        method: "PUT", headers: {"Content-Type":"application/json","Authorization":`Bearer ${authToken}`},
        body: JSON.stringify({ webhook_url: pasteUrl.trim() })
      });
      if (!r.ok) { const d = await r.json().catch(()=>({})); showToast(d.error || "Save failed"); return; }
      showToast(pasteOpen.charAt(0).toUpperCase() + pasteOpen.slice(1) + " connected ✓");
      setPasteOpen(null); setPasteUrl(""); load();
    };
    const PASTE_HINTS = {
      discord:    { label: "Discord webhook URL",     placeholder: "https://discord.com/api/webhooks/...",   help: "Server Settings → Integrations → Webhooks → New Webhook → Copy Webhook URL." },
      teams:      { label: "Microsoft Teams webhook URL", placeholder: "https://<tenant>.webhook.office.com/...", help: "In Teams: channel → Connectors → Incoming Webhook → Create → Copy URL." },
      mattermost: { label: "Mattermost webhook URL",  placeholder: "https://your-mattermost/hooks/...",      help: "Mattermost: Integrations → Incoming Webhooks → Add → Copy the URL." },
      rocketchat: { label: "Rocket.Chat webhook URL", placeholder: "https://your-rocket-chat/hooks/...",     help: "Rocket.Chat: Admin → Integrations → New → Incoming → Copy URL." },
      webhook:    { label: "Webhook URL (HTTPS only)",placeholder: "https://example.com/webhook",            help: "Catch-all: any HTTPS endpoint. We POST JSON." },
    };

    // Group cards by category
    const byCat = {};
    for (const m of data.items || []) { (byCat[m.category] = byCat[m.category] || []).push(m); }
    const slackFullyConfigured = data.slackConfigured ?? ((data.slackClientConfigured && data.slackSigningConfigured) || (data.slackBotTokenConfigured && data.slackSigningConfigured));
    const slackSetup = data.slackSetup || {};
    const slackMissing = [
      !data.slackClientConfigured && !data.slackBotTokenConfigured && "SLACK_CLIENT_ID / SLACK_CLIENT_SECRET or SLACK_BOT_TOKEN",
      !data.slackSigningConfigured && "SLACK_SIGNING_SECRET",
    ].filter(Boolean);

    return (
      <div style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <div style={{marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div>
              <h2 style={{fontSize:20,fontWeight:800,color:S.text,marginBottom:4}}>🧩 Marketplace</h2>
              <p style={{fontSize:13,color:S.text2}}>One-click integrations with where your team already works.</p>
            </div>
            <button onClick={load} disabled={loading} style={{padding:"8px 14px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Refresh</button>
          </div>

          {/* Slack config hint when server isn't configured */}
          {!slackFullyConfigured && (
            <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderLeft:`3px solid ${S.warning || "#f59e0b"}`,borderRadius:12,padding:14,marginBottom:16,fontSize:12,color:S.text2,lineHeight:1.65}}>
              <div style={{fontWeight:700,color:S.text,marginBottom:4}}>⚠️ Slack OAuth not configured</div>
              To enable Slack: create a Slack app at <span style={{fontFamily:"monospace",color:S.accent}}>api.slack.com/apps</span>, add <code>chat:write</code>, <code>commands</code>, <code>app_mentions:read</code>, and <code>users:read</code> scopes, then set <code>SLACK_CLIENT_ID</code>, <code>SLACK_CLIENT_SECRET</code>, and <code>SLACK_SIGNING_SECRET</code> in the Docker or Render env vars. Existing Slack apps can also connect with <code>SLACK_BOT_TOKEN</code> plus <code>SLACK_SIGNING_SECRET</code>.
              {slackMissing.length > 0 && <div style={{marginTop:6}}>Missing now: <code>{slackMissing.join(", ")}</code>.</div>}
              <div style={{marginTop:6}}>OAuth redirect URL: <span style={{fontFamily:"monospace",color:S.accent}}>{slackSetup.redirect_uri || (typeof window!=="undefined"?window.location.origin:"<origin>") + "/api/marketplace/slack/callback"}</span></div>
              <div>Slash command URL: <span style={{fontFamily:"monospace",color:S.accent}}>{slackSetup.slash_command_url || (typeof window!=="undefined"?window.location.origin:"<origin>") + "/api/marketplace/slack/commands"}</span></div>
              <div>Events URL: <span style={{fontFamily:"monospace",color:S.accent}}>{slackSetup.events_url || (typeof window!=="undefined"?window.location.origin:"<origin>") + "/api/marketplace/slack/events"}</span></div>
            </div>
          )}

          <PASTE_MODAL />
          <CREDS_MODAL />
          {Object.entries(byCat).map(([cat, items]) => (
            <div key={cat} style={{marginBottom:24}}>
              <div style={{fontSize:10,fontWeight:700,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>{cat}</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:10}}>
                {items.map(m => {
                  const installed = !!data.installed[m.id];
                  const isSoon = m.status === "soon";
                  const isReady = m.status === "ready";
                  return (
                    <div key={m.id} className="cf-tile-hover" style={{background:S.cardBg,border:`1px solid ${installed ? S.success+"55" : S.border2}`,borderLeft:installed?`3px solid ${S.success}88`:`1px solid ${S.border2}`,borderRadius:12,padding:14,display:"flex",flexDirection:"column",gap:10,opacity:isSoon?0.82:1,position:"relative"}}>
                      {isReady && !installed && (
                        <span title="Credentials can be saved now -- vendor features ship incrementally." style={{position:"absolute",top:8,right:8,fontSize:8,padding:"1px 6px",borderRadius:6,background:(S.warning||"#f59e0b")+"22",color:(S.warning||"#f59e0b"),border:`1px solid ${(S.warning||"#f59e0b")}55`,fontFamily:"monospace",fontWeight:700,letterSpacing:"0.05em"}}>BETA</span>
                      )}
                      <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                        <div style={{width:36,height:36,borderRadius:10,background:`${m.color}18`,border:`1px solid ${m.color}30`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}><MktIcon slug={m.slug} emoji={m.emoji} color={m.color} size={20} /></div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <div style={{fontSize:13,fontWeight:700,color:S.text}}>{m.name}</div>
                            {isSoon && <span style={{fontSize:8,padding:"1px 5px",borderRadius:4,background:S.text3+"22",color:S.text3,fontFamily:"monospace",fontWeight:700}}>SOON</span>}
                            {installed && <span style={{fontSize:8,padding:"1px 5px",borderRadius:4,background:S.success+"22",color:S.success,fontFamily:"monospace",fontWeight:700}}>CONNECTED</span>}
                          </div>
                          <div style={{fontSize:11,color:S.text2,lineHeight:1.5,marginTop:3}}>{m.desc}</div>
                        </div>
                      </div>
                      {installed ? (
                        <button onClick={()=>disconnect(m.id)} style={{marginTop:"auto",padding:"6px 12px",borderRadius:8,border:`1px solid ${S.danger}55`,background:"transparent",color:S.danger,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Disconnect</button>
                      ) : (
                        <button onClick={()=>connect(m.id)} disabled={isSoon}
                          style={{marginTop:"auto",padding:"6px 12px",borderRadius:8,border:"none",background:isSoon?S.border3:`linear-gradient(135deg,${m.color},${m.color}cc)`,color:"#fff",fontSize:11,fontWeight:700,cursor:isSoon?"default":"pointer",fontFamily:"inherit"}}>
                          {isSoon ? "Coming soon" : "Connect"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {zapierOpen && (
            <div onClick={()=>setZapierOpen(false)} style={{position:"fixed",inset:0,zIndex:300,background:"rgba(0,0,0,0.6)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
              <div onClick={e=>e.stopPropagation()} style={{background:S.surface,border:`1px solid ${S.border2}`,borderRadius:18,width:"min(520px,100%)",padding:24}}>
                <div style={{fontSize:17,fontWeight:800,color:S.text,marginBottom:4}}>Connect Zapier</div>
                <div style={{fontSize:12,color:S.text3,marginBottom:16,lineHeight:1.6}}>In Zapier, create a new Zap with the "Webhooks by Zapier" trigger, choose "Catch Hook", and paste the URL it gives you below. We'll POST each ChatFort reply to that URL.</div>
                <input type="url" placeholder="https://hooks.zapier.com/hooks/catch/..." value={zapierUrl} onChange={e=>setZapierUrl(e.target.value)}
                  style={{width:"100%",padding:"10px 13px",borderRadius:9,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text,fontFamily:"monospace",fontSize:12,marginBottom:16}}/>
                <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                  <button onClick={()=>setZapierOpen(false)} style={{padding:"9px 16px",borderRadius:9,background:"transparent",border:`1px solid ${S.border2}`,color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                  <button onClick={saveZapier} disabled={!zapierUrl.trim()} style={{padding:"9px 18px",borderRadius:9,border:"none",background:zapierUrl.trim()?`linear-gradient(135deg,${S.accent},${S.accent2})`:S.border3,color:"#fff",fontSize:12,fontWeight:700,cursor:zapierUrl.trim()?"pointer":"default",fontFamily:"inherit"}}>Save</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Recurring AI tasks. User picks a preset schedule + prompt + agent; the
  // server cron-matches every minute and fires due automations, appending
  // the AI's response as a new chat in the owner's history.
  function AutomationsPage() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | {} | automation
    const [runsFor, setRunsFor] = useState(null); // automation id
    const [runs, setRuns] = useState([]);
    const load = useCallback(async () => {
      if (!serverMode || !authToken) { setLoading(false); return; }
      try {
        const r = await fetch("/api/automations", { headers: { "Authorization": `Bearer ${authToken}` }});
        if (r.ok) setList(await r.json());
      } catch {}
      setLoading(false);
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async (form) => {
      const body = { ...form };
      const method = form.id ? "PUT" : "POST";
      const url = form.id ? `/api/automations/${form.id}` : "/api/automations";
      const r = await fetch(url, { method, headers: {"Content-Type":"application/json","Authorization":`Bearer ${authToken}`}, body: JSON.stringify(body) });
      const d = await r.json().catch(()=>({}));
      if (!r.ok) { showToast(d.error || "Save failed"); return; }
      showToast(form.id ? "Automation updated" : "Automation created ✓");
      setEditing(null); load();
    };
    const toggle = async (a) => {
      const r = await fetch(`/api/automations/${a.id}`, { method:"PUT", headers:{"Content-Type":"application/json","Authorization":`Bearer ${authToken}`}, body: JSON.stringify({ enabled: !a.enabled }) });
      if (!r.ok) { showToast("Toggle failed"); return; }
      load();
    };
    const runNow = async (a) => {
      // Slice 7-E: synchronously await the run so we can land the user on
      // the result chat with one click. Server takes ~10s typical for the
      // model call; we accept that wait because the user explicitly asked
      // for it.
      showToast("Running automation…");
      let chatId = null;
      try {
        const r = await fetch(`/api/automations/${a.id}/run`, { method: "POST", headers: { "Authorization": `Bearer ${authToken}` } });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) { showToast(d.error || "Run failed"); load(); return; }
        chatId = d.chatId || null;
      } catch (e) {
        showToast("Run failed: " + (e?.message || "network error"));
        load();
        return;
      }
      // Pull the new chat back from the server then offer to open it via a
      // clickable toast. The chat list sync useEffect runs once on login;
      // we manually merge here so the new automation chat appears without
      // a page reload.
      try {
        const cr = await fetch("/api/chats", { headers: { "Authorization": `Bearer ${authToken}` } });
        if (cr.ok) {
          const serverChats = await cr.json();
          if (Array.isArray(serverChats)) {
            setChats(prev => {
              const byId = new Map();
              for (const c of prev) byId.set(c.id, c);
              for (const c of serverChats) byId.set(c.id, c);
              return Array.from(byId.values()).sort((x, y) => (y.created || 0) - (x.created || 0));
            });
          }
        }
      } catch {}
      load();
      if (chatId) {
        showToast({
          text: "Automation ran",
          actionLabel: "Open chat",
          onAction: () => onPickChat(chatId, a.agent_id || "ag-default"),
        });
      } else {
        showToast("Automation ran — no output recorded");
      }
    };
    const remove = async (a) => {
      if (!window.confirm(`Delete automation "${a.name}"?`)) return;
      const r = await fetch(`/api/automations/${a.id}`, { method:"DELETE", headers:{ "Authorization":`Bearer ${authToken}` } });
      if (!r.ok) { showToast("Delete failed"); return; }
      showToast("Deleted");
      load();
    };
    const openRuns = async (a) => {
      setRunsFor(a.id);
      const r = await fetch(`/api/automations/${a.id}/runs`, { headers:{ "Authorization":`Bearer ${authToken}` } });
      if (r.ok) setRuns(await r.json()); else setRuns([]);
    };

    // Slice 7-E: relative-time pill ("Just now · success", "2h ago · error",
    // "Never run"). Compact thresholds — anything older than ~30 days falls
    // back to a date string so the pill doesn't read "734h ago".
    const timeAgo = (ts) => {
      if (!ts) return "Never run";
      const d = (typeof ts === "string") ? new Date(ts).getTime() : ts;
      if (!Number.isFinite(d)) return "Never run";
      const diff = Date.now() - d;
      if (diff < 60_000) return "Just now";
      if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
      if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
      if (diff < 30 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
      return new Date(d).toLocaleDateString();
    };
    const formatCron = (c, tz) => {
      const parts = (c || "").split(/\s+/);
      if (parts.length !== 5) return c;
      const [mm, hh, dom, mon, dow] = parts;
      const time = `${String(hh).padStart(2,"0")}:${String(mm).padStart(2,"0")} ${tz || "UTC"}`;
      if (dom === "*" && mon === "*" && dow === "*") return `Daily at ${time}`;
      if (dow === "1-5") return `Weekdays at ${time}`;
      if (dom === "*" && /^\d$/.test(dow)) {
        const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
        return `Every ${days[parseInt(dow,10)]} at ${time}`;
      }
      if (dow === "*" && /^\d+$/.test(dom)) return `Day ${dom} of each month at ${time}`;
      return c;
    };

    return (
      <div style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
        <div style={{maxWidth:960,margin:"0 auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24}}>
            <div>
              <h2 style={{fontSize:20,fontWeight:800,color:S.text,marginBottom:4}}>🤖 Automations</h2>
              <p style={{fontSize:13,color:S.text2}}>Run the AI on a recurring schedule — daily summaries, weekly digests, monthly reports. Results appear as new chats in your history.</p>
            </div>
            <Btn onClick={()=>setEditing({ name:"", prompt:"", agentId: agents[0]?.id || "", preset:"daily", time:"09:00", day:1, tz: (typeof Intl !== "undefined" && Intl.DateTimeFormat().resolvedOptions().timeZone) || "UTC", enabled:true })}>{IC.plus} New automation</Btn>
          </div>

          {loading && (
            <div style={{padding:30,display:"flex",flexDirection:"column",alignItems:"center",gap:10,color:S.text3,fontSize:12}}>
                <span className="cf-auth-spinner" style={{display:"inline-flex"}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={S.border2} strokeWidth="2.5"/><path d="M21 12a9 9 0 0 0-9-9" stroke={S.accent} strokeWidth="2.5" strokeLinecap="round"/></svg>
                </span>
                <span>Loading…</span>
              </div>
          )}
          {!loading && list.length === 0 && (
            <div style={{background:S.cardBg,border:`1px dashed ${S.border2}`,borderRadius:14,padding:40,textAlign:"center",color:S.text3}}>
              <div style={{fontSize:34,marginBottom:10}}>🤖</div>
              <div style={{fontSize:14,color:S.text2,marginBottom:6}}>No automations yet.</div>
              <div style={{fontSize:12,color:S.text3,lineHeight:1.6,maxWidth:420,margin:"0 auto"}}>Try: <em>"Every weekday at 9 AM, summarize yesterday's audit log entries."</em></div>
            </div>
          )}

          {!loading && list.map(a => (
            <div key={a.id} style={{background:S.cardBg,border:`1px solid ${a.enabled?S.border2:S.border}`,borderLeft:`3px solid ${a.enabled?S.accent:S.text3}`,borderRadius:12,padding:"14px 18px",marginBottom:10}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:14,flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:220}}>
                  <div style={{fontSize:13,fontWeight:700,color:S.text,marginBottom:3}}>{a.name}</div>
                  <div style={{fontSize:11,color:S.text3,fontFamily:"monospace",marginBottom:6}}>{formatCron(a.cron, a.tz)}</div>
                  <div style={{fontSize:12,color:S.text2,lineHeight:1.5,overflow:"hidden",textOverflow:"ellipsis",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{a.prompt}</div>
                  {/* Slice 7-E: last-run pill with timeAgo + green/red/gray
                      colour. Server stores "ok"/"error"; we display
                      "success"/"error" per the slice spec. */}
                  <div style={{marginTop:8,display:"inline-flex",alignItems:"center"}}>
                    {(() => {
                      const has = !!a.last_run_at;
                      const ok  = has && a.last_run_status === "ok";
                      const err = has && a.last_run_status === "error";
                      const colour = err ? S.danger : ok ? S.success : S.text3;
                      const dim    = err ? `${S.danger}18` : ok ? `${S.success}1a` : "transparent";
                      const label  = !has ? "Never run" : `${timeAgo(a.last_run_at)} · ${ok ? "success" : "error"}`;
                      return (
                        <span title={has ? new Date(a.last_run_at).toLocaleString() : ""}
                          style={{display:"inline-flex",alignItems:"center",gap:5,padding:"2px 8px",borderRadius:20,fontSize:10,fontFamily:"monospace",border:`1px solid ${colour}40`,background:dim,color:colour}}>
                          <span style={{width:5,height:5,borderRadius:"50%",background:colour,display:"inline-block"}}/>
                          {label}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                  <div onClick={()=>toggle(a)} style={{width:36,height:20,background:a.enabled?S.success:S.surface3,borderRadius:10,position:"relative",cursor:"pointer",border:`1px solid ${S.border2}`,transition:"background 0.18s"}} title={a.enabled?"Enabled":"Paused"}>
                    <div style={{position:"absolute",top:1,left:a.enabled?17:1,width:16,height:16,background:"#fff",borderRadius:"50%",transition:"left 0.18s"}}/>
                  </div>
                  <button onClick={()=>runNow(a)} title="Run now" style={{padding:"5px 11px",borderRadius:8,background:S.accentDim,border:`1px solid ${S.accent}40`,color:S.accent,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>▶ Run</button>
                  <button onClick={()=>openRuns(a)} title="Run history" style={{padding:"5px 11px",borderRadius:8,background:"transparent",border:`1px solid ${S.border2}`,color:S.text2,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>History</button>
                  <button onClick={()=>setEditing(a)} title="Edit" style={{padding:"5px 11px",borderRadius:8,background:"transparent",border:`1px solid ${S.border2}`,color:S.text2,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Edit</button>
                  <button onClick={()=>remove(a)} title="Delete" style={{padding:"5px 11px",borderRadius:8,background:"transparent",border:`1px solid ${S.danger}40`,color:S.danger,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Delete</button>
                </div>
              </div>
            </div>
          ))}

          {editing && <AutomationEditor form={editing} onClose={()=>setEditing(null)} onSave={save} />}
          {runsFor && <AutomationRunsModal runs={runs} onClose={()=>{setRunsFor(null);setRuns([]);}} />}
        </div>
      </div>
    );
  }

  function AutomationEditor({ form, onClose, onSave }) {
    const [f, setF] = useState({ ...form });
    const days = [["1","Mon"],["2","Tue"],["3","Wed"],["4","Thu"],["5","Fri"],["6","Sat"],["0","Sun"]];
    // feat/automation-kind-select: fetch the kind allowlist from the
    // server so the dropdown stays in sync without duplicating the
    // list client-side. Seeded with the two V1 kinds so the field
    // works even before the fetch resolves (or if it fails).
    const [kindOptions, setKindOptions] = useState([
      { value: "generic",        label: "Generic",        description: "Send the prompt as-is to the LLM. Default for all existing automations." },
      { value: "audit_summary",  label: "Audit Summary",  description: "Injects the last 24h of audit_log rows (org-scoped) before the prompt." },
    ]);
    useEffect(() => {
      let cancelled = false;
      fetch("/api/automations/kinds", { headers: { "Authorization": `Bearer ${authToken}` } })
        .then((r) => r.ok ? r.json() : null)
        .then((d) => { if (!cancelled && d && Array.isArray(d.kinds) && d.kinds.length) setKindOptions(d.kinds); })
        .catch(() => { /* keep the seeded fallback */ });
      return () => { cancelled = true; };
    // authToken is from outer closure; eslint-disable to keep the
    // effect mount-only (we don't want to refetch on every token bump).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Union the curated list with the user's detected zone and the form's
    // current zone, so a previously-saved or auto-detected zone still shows
    // up selected even if it isn't in the default list.
    const detectedTz = (typeof Intl !== "undefined" && Intl.DateTimeFormat().resolvedOptions().timeZone) || null;
    const tzOptions = [...new Set([detectedTz, f.tz, ...TIMEZONES].filter(Boolean))];
    return (
      <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:350,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(8px)",padding:24}}>
        <div onClick={e=>e.stopPropagation()} style={{background:S.modalBg,border:`1px solid ${S.border2}`,borderRadius:16,width:"min(620px, 100%)",padding:24,maxHeight:"90vh",overflowY:"auto"}}>
          <div style={{fontSize:16,fontWeight:800,color:S.text,marginBottom:4}}>{f.id ? "Edit automation" : "New automation"}</div>
          <div style={{fontSize:12,color:S.text3,marginBottom:18}}>Results land in your chat history.</div>

          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,color:S.text2,marginBottom:5}}>Name</div>
            <input value={f.name} onChange={e=>setF(p=>({...p,name:e.target.value}))} placeholder="Daily audit summary"
              style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"8px 12px",color:S.text,fontSize:13,outline:"none"}}/>
          </div>

          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,color:S.text2,marginBottom:5}}>Prompt the AI should run</div>
            <textarea value={f.prompt} onChange={e=>setF(p=>({...p,prompt:e.target.value}))} rows={4}
              placeholder="Summarize yesterday's audit log entries. Flag anything unusual."
              style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"10px 12px",color:S.text,fontSize:12,outline:"none",fontFamily:"inherit",resize:"vertical"}}/>
          </div>

          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,color:S.text2,marginBottom:5}}>Agent</div>
            <select value={f.agentId||""} onChange={e=>setF(p=>({...p,agentId:e.target.value}))}
              style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"8px 12px",color:S.text,fontSize:13,outline:"none"}}>
              {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>

          {/* feat/automation-kind-select: choose what context the runner
              injects before the prompt. Default 'generic' is the
              backward-compatible behavior (prompt-only). 'audit_summary'
              injects the last 24h of audit_logs -- see PR #87 for the
              runner logic. */}
          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,color:S.text2,marginBottom:5}}>Kind</div>
            <select
              value={f.kind || "generic"}
              onChange={e=>setF(p=>({...p,kind:e.target.value}))}
              aria-label="Automation kind"
              data-testid="automation-kind-select"
              style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"8px 12px",color:S.text,fontSize:13,outline:"none"}}>
              {kindOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <div style={{fontSize:10,color:S.text3,marginTop:5,lineHeight:1.4}}>
              Audit Summary kind injects the last 24h of audit_logs into the prompt automatically.
            </div>
          </div>

          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,color:S.text2,marginBottom:5}}>Schedule</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
              {[["daily","Daily"],["weekdays","Weekdays"],["weekly","Weekly"],["monthly","Monthly"]].map(([v,l])=>(
                <button key={v} onClick={()=>setF(p=>({...p,preset:v}))}
                  style={{padding:"5px 12px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit",background:f.preset===v?S.accentDim:"none",border:`1px solid ${f.preset===v?S.accent:S.border2}`,color:f.preset===v?S.accent:S.text2}}>
                  {l}
                </button>
              ))}
            </div>
            <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
              <div>
                <div style={{fontSize:10,color:S.text3,marginBottom:3}}>Time</div>
                <input type="time" value={f.time} onChange={e=>setF(p=>({...p,time:e.target.value}))}
                  style={{background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"7px 10px",color:S.text,fontSize:13,outline:"none"}}/>
              </div>
              <div>
                <div style={{fontSize:10,color:S.text3,marginBottom:3}}>Timezone</div>
                <select value={f.tz || "UTC"} onChange={e=>setF(p=>({...p,tz:e.target.value}))}
                  style={{background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"7px 10px",color:S.text,fontSize:13,outline:"none",maxWidth:220}}>
                  {tzOptions.map(tz => <option key={tz} value={tz}>{tz}{tz === detectedTz ? " (your timezone)" : ""}</option>)}
                </select>
              </div>
              {f.preset === "weekly" && (
                <div>
                  <div style={{fontSize:10,color:S.text3,marginBottom:3}}>Day of week</div>
                  <select value={String(f.day)} onChange={e=>setF(p=>({...p,day:parseInt(e.target.value,10)}))}
                    style={{background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"7px 10px",color:S.text,fontSize:13,outline:"none"}}>
                    {days.map(([v,l])=><option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              )}
              {f.preset === "monthly" && (
                <div>
                  <div style={{fontSize:10,color:S.text3,marginBottom:3}}>Day of month</div>
                  <input type="number" min="1" max="28" value={f.day} onChange={e=>setF(p=>({...p,day:parseInt(e.target.value,10)||1}))}
                    style={{width:80,background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"7px 10px",color:S.text,fontSize:13,outline:"none"}}/>
                </div>
              )}
            </div>
          </div>

          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:18}}>
            <div onClick={()=>setF(p=>({...p,enabled:!p.enabled}))} style={{width:36,height:20,background:f.enabled?S.success:S.surface3,borderRadius:10,position:"relative",cursor:"pointer",border:`1px solid ${S.border2}`}}>
              <div style={{position:"absolute",top:1,left:f.enabled?17:1,width:16,height:16,background:"#fff",borderRadius:"50%",transition:"left 0.18s"}}/>
            </div>
            <span style={{fontSize:12,color:S.text2}}>{f.enabled?"Enabled":"Paused"}</span>
          </div>

          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button onClick={onClose} style={{padding:"9px 16px",borderRadius:9,background:"transparent",border:`1px solid ${S.border2}`,color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            <button onClick={()=>onSave(f)} disabled={!f.name?.trim() || !f.prompt?.trim()}
              style={{padding:"9px 18px",borderRadius:9,border:"none",background:(!f.name?.trim()||!f.prompt?.trim())?S.border3:`linear-gradient(135deg,${S.accent},${S.accent2})`,color:"#fff",fontSize:12,fontWeight:700,cursor:(!f.name?.trim()||!f.prompt?.trim())?"default":"pointer",fontFamily:"inherit"}}>
              {f.id ? "Save changes" : "Create"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  function AutomationRunsModal({ runs, onClose }) {
    return (
      <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:350,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(8px)",padding:24}}>
        <div onClick={e=>e.stopPropagation()} style={{background:S.modalBg,border:`1px solid ${S.border2}`,borderRadius:16,width:"min(700px, 100%)",maxHeight:"85vh",display:"flex",flexDirection:"column"}}>
          <div style={{padding:"16px 22px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{fontSize:15,fontWeight:700,color:S.text}}>Run history</div>
            <button onClick={onClose} style={{background:"none",border:"none",color:S.text2,cursor:"pointer",fontSize:18,padding:4}}>×</button>
          </div>
          <div style={{padding:"10px 22px",overflowY:"auto"}}>
            {runs.length === 0 && <div style={{padding:30,textAlign:"center",color:S.text3,fontSize:12}}>No runs yet.</div>}
            {runs.map(r => (
              <div key={r.id} style={{padding:"10px 0",borderBottom:`1px solid ${S.border}`}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
                  <span style={{fontSize:10,padding:"2px 7px",borderRadius:4,background:r.status==="ok"?S.success+"22":S.danger+"22",color:r.status==="ok"?S.success:S.danger,fontFamily:"monospace",fontWeight:700}}>{r.status.toUpperCase()}</span>
                  <span style={{fontSize:11,color:S.text3,fontFamily:"monospace"}}>{new Date(r.ts).toLocaleString()}</span>
                  <span style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginLeft:"auto"}}>{r.latency_ms}ms</span>
                </div>
                {r.error && <div style={{fontSize:11,color:S.danger,fontFamily:"monospace",background:S.danger+"10",padding:"6px 10px",borderRadius:6,marginBottom:4}}>{r.error}</div>}
                {r.output_preview && (
                  <div style={{fontSize:11,color:S.text2,background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:6,padding:"8px 10px",whiteSpace:"pre-wrap",maxHeight:120,overflowY:"auto"}}>{r.output_preview}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function BillingPage() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null); // currently-clicked plan key, "portal", or null
    const [interval, setInterval] = useState("monthly");
    const [plans, setPlans] = useState(null); // server PLANS for live per-seat prices

    const [seatsDraft, setSeatsDraft] = useState("");
    const [variant, setVariant] = useState("managed"); // managed | byok — drives price + checkout
    const reloadStatus = () => {
      if (!serverMode || !authToken) { setLoading(false); return; }
      fetch("/api/billing/status", { headers: { "Authorization": `Bearer ${authToken}` }})
        .then(r => r.ok ? r.json() : null)
        .then(d => { setStatus(d); setLoading(false); if (d) { setSeatsDraft(String(d.seatsPurchased ?? d.billedSeats ?? "")); if (d.variant) setVariant(d.variant); } })
        .catch(() => setLoading(false));
    };
    const [invoiceData, setInvoiceData] = useState(null);
    const loadInvoices = () => {
      if (!serverMode || !authToken) return;
      fetch("/api/billing/invoices", { headers: { "Authorization": `Bearer ${authToken}` }})
        .then(r => r.ok ? r.json() : null).then(d => { if (d) setInvoiceData(d); }).catch(() => {});
    };
    const payBill = () => {
      if (invoiceData && invoiceData.openInvoiceUrl) { window.location.href = invoiceData.openInvoiceUrl; return; }
      goPortal();
    };
    useEffect(() => {
      reloadStatus();
      loadInvoices();
      fetch("/api/plans").then(r => r.ok ? r.json() : null).then(d => { if (d) setPlans(d); }).catch(() => {});
    }, []);

    const goCheckout = async (plan) => {
      setBusy(`${plan}-${variant}-${interval}`);
      try {
        const r = await fetch("/api/billing/checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
          body: JSON.stringify({ plan, variant, interval, successUrl: window.location.origin + "/?section=billing", cancelUrl: window.location.origin + "/?section=billing" }),
        });
        const d = await r.json();
        if (!r.ok || !d.url) { showToast(d.error || "Checkout failed"); setBusy(null); return; }
        window.location.href = d.url;
      } catch (e) { showToast("Checkout failed: " + e.message); setBusy(null); }
    };

    const goChangePlan = async (plan) => {
      if (!window.confirm("Switch this org to " + plan.toUpperCase() + " (" + variant + ", " + interval + ")? Stripe applies prorated charges/credits immediately.")) return;
      setBusy(plan + "-switch");
      try {
        const r = await fetch("/api/billing/change-plan", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
          body: JSON.stringify({ plan, variant, interval }),
        });
        const d = await r.json();
        if (!r.ok) { showToast(d.error || "Plan change failed"); setBusy(null); return; }
        showToast("Plan changed to " + plan.toUpperCase() + " \u2713");
        setBusy(null);
        reloadStatus();
      } catch (e) { showToast("Plan change failed: " + e.message); setBusy(null); }
    };
    const goSeats = async () => {
      const n = parseInt(seatsDraft, 10);
      if (!Number.isFinite(n) || n < 1) { showToast("Enter a valid seat count."); return; }
      setBusy("seats");
      try {
        const r = await fetch("/api/billing/seats", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
          body: JSON.stringify({ seats: n }),
        });
        const d = await r.json();
        if (!r.ok) { showToast(d.error || "Seat update failed"); setBusy(null); return; }
        showToast("Seats updated \u2014 " + d.billedSeats + " billed \u2713");
        setBusy(null);
        reloadStatus();
      } catch (e) { showToast("Seat update failed: " + e.message); setBusy(null); }
    };
    const goPortal = async () => {
      setBusy("portal");
      try {
        const r = await fetch("/api/billing/portal", { method: "POST", headers: { "Authorization": `Bearer ${authToken}` }});
        const d = await r.json();
        if (!r.ok || !d.url) { showToast(d.error || "Could not open portal"); setBusy(null); return; }
        window.location.href = d.url;
      } catch (e) { showToast("Portal failed: " + e.message); setBusy(null); }
    };

    if (loading) return <div style={{flex:1,padding:28,background:S.bg,color:S.text3,fontSize:13}}>Loading…</div>;
    const configured = status?.configured;
    const customMode = status?.billingMode === "custom";
    const statusColor = status?.status === "active" || status?.status === "trialing" ? S.success
                      : status?.status === "past_due" ? (S.warning || "#f59e0b")
                      : status?.status === "canceled" ? S.danger : S.text3;

    const offerPlans = [
      { id: "starter", name: "Starter", highlights: ["Up to 15 users", "500K tokens per seat", "Full agent library"] },
      { id: "pro", name: "Professional", highlights: ["Up to 50 users", "1M tokens per seat", "Multi-model compare", "BAA add-on available"] },
      { id: "business", name: "Business", highlights: ["Up to 250 users", "3M tokens per seat", "SSO + audit log + HIPAA BAA", "API access"] },
      { id: "premium", name: "Premium", highlights: ["Everything in Business", "3M premium tokens per seat", "Claude Opus + GPT-5 access", "Priority support"] },
    ];

    return (
      <div style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
        <div style={{maxWidth:960,margin:"0 auto"}}>
          <div style={{marginBottom:24}}>
            <h2 style={{fontSize:20,fontWeight:800,color:S.text,marginBottom:4}}>💳 Billing</h2>
            <p style={{fontSize:13,color:S.text2}}>{customMode ? "Your plan is managed by the ChatFortAI team \u2014 reach out to us to make any changes." : "Manage your subscription. Changes take effect immediately."}</p>
          </div>

          {!configured && (
            <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderLeft:`3px solid ${S.warning || "#f59e0b"}`,borderRadius:12,padding:"14px 18px",marginBottom:20,fontSize:12,color:S.text2,lineHeight:1.6}}>
              <div style={{fontWeight:700,color:S.text,marginBottom:4}}>Billing not yet enabled</div>
              Self-serve billing is staged but not live. Once the ChatFortAI team turns it on, you'll be able to upgrade/downgrade here without a sales call. To get on a paid plan today, email <span style={{fontFamily:"monospace",color:S.accent}}>sales@chatfortai.ai</span>.
            </div>
          )}

          {/* Current state */}
          {!customMode && (<>
          <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:"16px 20px",marginBottom:20,display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:200}}>
              <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>Current plan</div>
              <div style={{fontSize:22,fontWeight:800,color:S.text,fontFamily:"monospace"}}>{(status?.plan || "trial").toUpperCase()}</div>
              <div style={{fontSize:11,color:S.text3,marginTop:4,fontFamily:"monospace"}}>{status?.variant === "byok" ? "BYOK billing" : "Managed billing"}</div>
            </div>
            {/* Slice C1d: seat row from /api/auth/me's seatStatus */}
            {(() => {
              const ss = authUser?.seatStatus;
              const canManageSeats = !!status?.hasSubscription;
              return (
                <div>
                  <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>Seats</div>
                  <div style={{fontSize:13,fontFamily:"monospace",color:S.text2,fontWeight:600}}>
                    {status?.members != null
                      ? status.members + " member" + (status.members === 1 ? "" : "s") + " \u00b7 " + (status.billedSeats ?? status.members) + " billed"
                      : ss ? (ss.unlimited ? "Unlimited (trial)" : `${ss.usedSeats} of ${ss.paidSeats} used`) : "\u2014"}
                  </div>
                  {canManageSeats && (
                    <div style={{display:"flex",gap:6,marginTop:8,alignItems:"center"}}>
                      <input type="number" min={1} max={status?.planSeatCap ?? 10000} value={seatsDraft} onChange={e=>setSeatsDraft(e.target.value)}
                        style={{width:64,padding:"5px 8px",borderRadius:7,border:`1px solid ${S.border2}`,background:S.bg,color:S.text,fontSize:12,fontFamily:"monospace",outline:"none"}}/>
                      <button onClick={goSeats} disabled={busy==="seats"}
                        style={{padding:"6px 12px",borderRadius:7,background:S.accentDim,border:`1px solid ${S.accent}44`,color:S.accent,fontSize:11,fontWeight:700,cursor:busy==="seats"?"default":"pointer",fontFamily:"inherit"}}>
                        {busy==="seats" ? "Saving\u2026" : "Update seats"}
                      </button>
                      {status?.planSeatCap != null && <span style={{fontSize:10,color:S.text3}}>plan max {status.planSeatCap}</span>}
                    </div>
                  )}
                </div>
              );
            })()}
            {status?.status && (
              <div>
                <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>Status</div>
                <span style={{padding:"4px 10px",borderRadius:20,background:statusColor+"18",color:statusColor,border:`1px solid ${statusColor}35`,fontSize:11,fontWeight:700,fontFamily:"monospace"}}>{status.status.toUpperCase()}</span>
              </div>
            )}
            {status?.hasSubscription && (
              <button onClick={goPortal} disabled={busy==="portal"}
                style={{padding:"9px 16px",borderRadius:9,background:S.accentDim,border:`1px solid ${S.accent}44`,color:S.accent,fontSize:12,fontWeight:700,cursor:busy==="portal"?"default":"pointer",fontFamily:"inherit"}}>
                {busy==="portal" ? "Opening…" : "Manage subscription"}
              </button>
            )}
          </div>
          </>)}

          {/* Pay bill + 12-month history */}
          {invoiceData && (
            <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:"16px 20px",marginBottom:20}}>
              <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap",marginBottom:invoiceData.invoices?.length?14:0}}>
                <div style={{flex:1,minWidth:180}}>
                  <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>Amount due</div>
                  <div style={{fontSize:22,fontWeight:800,color:invoiceData.amountDue>0?(S.warning||"#f59e0b"):S.text,fontFamily:"monospace"}}>
                    {invoiceData.amountDue>0 ? "$"+(invoiceData.amountDue/100).toFixed(2) : "$0.00"}
                    <span style={{fontSize:11,color:S.text3,fontWeight:500,marginLeft:6}}>{invoiceData.amountDue>0 ? "outstanding" : "nothing due"}</span>
                  </div>
                </div>
                <button onClick={payBill} disabled={busy==="pay"}
                  style={{padding:"10px 18px",borderRadius:9,border:"none",background:invoiceData.amountDue>0?`linear-gradient(135deg,${S.accent},${S.accent2})`:S.accentDim,color:invoiceData.amountDue>0?"#fff":S.accent,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                  {busy==="pay" ? "Opening…" : invoiceData.amountDue>0 ? "Pay bill now" : "Pay / manage billing"}
                </button>
              </div>
              {invoiceData.invoices?.length>0 && (<>
                <div style={{height:1,background:S.border,margin:"4px 0 12px"}}/>
                <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Billing history · last 12 months</div>
                <div style={{display:"flex",flexDirection:"column",gap:1}}>
                  {invoiceData.invoices.map(inv => {
                    const dt = new Date(inv.created*1000).toLocaleDateString(undefined,{year:"numeric",month:"short",day:"numeric"});
                    const sc = inv.status==="paid"?S.success:(inv.status==="open"||inv.status==="past_due")?(S.warning||"#f59e0b"):inv.status==="void"||inv.status==="uncollectible"?S.danger:S.text3;
                    return (
                      <div key={inv.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 4px",borderBottom:`1px solid ${S.border}55`,fontSize:12,flexWrap:"wrap"}}>
                        <span style={{color:S.text2,fontFamily:"monospace",minWidth:96}}>{dt}</span>
                        <span style={{color:S.text3,fontFamily:"monospace",fontSize:11,flex:1,minWidth:80}}>{inv.number||inv.id.slice(0,14)}</span>
                        <span style={{color:S.text,fontFamily:"monospace",fontWeight:600,minWidth:72,textAlign:"right"}}>${((inv.amountDue||inv.amountPaid||0)/100).toFixed(2)}</span>
                        <span style={{padding:"2px 9px",borderRadius:20,background:sc+"18",color:sc,border:`1px solid ${sc}35`,fontSize:10,fontWeight:700,fontFamily:"monospace",textTransform:"uppercase"}}>{inv.status}</span>
                        <span style={{display:"flex",gap:8}}>
                          {inv.hostedUrl && <a href={inv.hostedUrl} target="_blank" rel="noopener noreferrer" style={{color:S.accent,fontSize:11,textDecoration:"none",fontWeight:600}}>View</a>}
                          {inv.pdf && <a href={inv.pdf} target="_blank" rel="noopener noreferrer" style={{color:S.accent,fontSize:11,textDecoration:"none",fontWeight:600}}>PDF</a>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>)}
            </div>
          )}
          {/* Plan picker — only show when billing is configured */}
          {configured && !customMode && (
            <>
              <div style={{display:"flex",gap:6,marginBottom:14,alignItems:"center"}}>
                <span style={{fontSize:11,color:S.text3}}>Billing cycle:</span>
                {[["monthly","Monthly"],["annual","Annual · save 20%"]].map(([v, l]) => (
                  <button key={v} onClick={() => setInterval(v)}
                    style={{padding:"5px 12px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit",background:interval===v?S.accentDim:"none",border:`1px solid ${interval===v?S.accent:S.border2}`,color:interval===v?S.accent:S.text2}}>
                    {l}
                  </button>
                ))}
              </div>
              <div style={{display:"flex",gap:6,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
                <span style={{fontSize:11,color:S.text3}}>Billing type:</span>
                {[["managed","Managed (we run the models)"],["byok","BYOK (your own API keys)"]].map(([v, l]) => (
                  <button key={v} onClick={() => setVariant(v)}
                    style={{padding:"5px 12px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit",background:variant===v?S.accentDim:"none",border:`1px solid ${variant===v?S.accent:S.border2}`,color:variant===v?S.accent:S.text2}}>
                    {l}
                  </button>
                ))}
              </div>
              <div style={{background:S.cardBg,border:`1px solid ${S.success}40`,borderLeft:`3px solid ${S.success}`,borderRadius:12,padding:"12px 16px",marginBottom:16,fontSize:12,color:S.text2,lineHeight:1.6}}>
                <span style={{fontWeight:700,color:S.text}}>✓ Unlimited base models</span> — the everyday fast &amp; balanced models are free to use without per-message limits on every paid plan, under fair use (~20M tokens per seat each month). Your plan's per-seat token allowance applies to <span style={{fontWeight:600,color:S.text}}>premium frontier models</span> only.
                {variant === "byok"
                  ? <span> On <span style={{fontWeight:600,color:S.accent}}>BYOK</span>, you bring your own provider keys (Anthropic, OpenAI, Google, Azure, Bedrock) and there are no model limits at all — you pay your provider directly.</span>
                  : <span> Prefer your own provider keys? Switch to <span style={{fontWeight:600,color:S.accent}}>BYOK</span> above for lower per-seat pricing and no model limits.</span>}
              </div>
              <div className="cf-grid-stagger" style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",gap:12}}>
                {offerPlans.map(p => {
                  const active = status?.plan === p.id;
                  return (
                    <div key={p.id} style={{background:S.cardBg,border:`1px solid ${active?S.accent:S.border2}`,borderRadius:14,padding:"18px 20px",display:"flex",flexDirection:"column",gap:12}}>
                      <div>
                        <div style={{fontSize:11,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{active ? "Current" : ""}</div>
                        <div style={{fontSize:18,fontWeight:800,color:S.text}}>{p.name}</div>
                      </div>
                      {(() => {
                        const pd = plans?.[p.id]?.[variant];
                        const price = pd ? (interval === "annual" ? pd.annualPrice : pd.price) : null;
                        if (price == null) return null;
                        return (
                          <div style={{display:"flex",alignItems:"baseline",gap:4}}>
                            <span style={{fontSize:26,fontWeight:800,color:S.text,letterSpacing:"-0.02em"}}>${price}</span>
                            <span style={{fontSize:11,color:S.text3}}>{"/ user / mo" + (interval === "annual" ? " · billed annually" : "")}</span>
                          </div>
                        );
                      })()}
                      {(() => {
                        const sp = plans?.[p.id];
                        if (!sp) return (
                          <ul style={{margin:0,padding:0,listStyle:"none",display:"flex",flexDirection:"column",gap:6}}>
                            {p.highlights.map(h => (
                              <li key={h} style={{fontSize:12,color:S.text2,display:"flex",alignItems:"flex-start",gap:6}}>
                                <span style={{color:S.accent,flexShrink:0}}>✓</span><span>{h}</span>
                              </li>
                            ))}
                          </ul>
                        );
                        const f = sp.features || {};
                        const rank = ({ starter: 0, pro: 1, business: 2, premium: 3, enterprise: 4 })[p.id] ?? 0;
                        const tokM = Math.round((sp.monthlyTokens || 0) / 1e6 * 10) / 10;
                        // "What you get" per plan: seats + premium-token allowance, then
                        // the capability + product-surface gates (section gating #481:
                        // Automations/Hermes Co-work = Pro+, Hermes run history/Code mode/Analytics/Bots =
                        // Business+) and the feature flags from the server PLANS map.
                        const rows = [
                          ["Up to " + (sp.seats ?? "—") + " users", true],
                          [tokM + "M premium tokens / seat / mo", true],
                          ["Unlimited base models (fair use)", true],
                          ["Chat, Projects, Agents, Knowledge, Tools, MCP", true],
                          ["Automations & Workflows", rank >= 1],
                          ["Hermes Co-work agent", rank >= 1],
                          ["Hermes run history", rank >= 2],
                          ["Code mode", rank >= 2],
                          ["Analytics dashboard", rank >= 2],
                          ["Embeddable Bots", rank >= 2],
                          ["Multi-model compare", !!f.multiModel],
                          ["Bring your own keys (BYOK)", !!f.byok],
                          ["API access + /v1", !!f.apiAccess],
                          ["SSO (Okta / Entra / Google)", !!f.ssoOidc],
                          ["Audit log + CSV export", !!f.auditLog],
                          ["HIPAA BAA", !!f.baa],
                          ["White-label", !!f.whiteLabel],
                        ];
                        return (
                          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 5 }}>
                            {rows.map(([label, on]) => (
                              <li key={label} style={{ fontSize: 11.5, lineHeight: 1.4, color: on ? S.text2 : S.text3, display: "flex", alignItems: "flex-start", gap: 6, opacity: on ? 1 : 0.5 }}>
                                <span style={{ color: on ? S.success : S.text3, flexShrink: 0, fontWeight: 700 }}>{on ? "✓" : "·"}</span>
                                <span>{label}</span>
                              </li>
                            ))}
                          </ul>
                        );
                      })()}
                      <button onClick={() => (status?.hasSubscription ? goChangePlan(p.id) : goCheckout(p.id))} disabled={active || busy?.startsWith(p.id)}
                        style={{marginTop:"auto",padding:"10px 14px",borderRadius:9,border:"none",background:active?S.border3:`linear-gradient(135deg,${S.accent},${S.accent2})`,color:"#fff",fontSize:12,fontWeight:700,cursor:(active||busy)?"default":"pointer",fontFamily:"inherit"}}>
                        {active ? "Current plan" : busy?.startsWith(p.id) ? (status?.hasSubscription ? "Switching…" : "Redirecting…") : (status?.hasSubscription ? "Switch plan" : "Upgrade")}
                      </button>
                    </div>
                  );
                })}
              </div>
              <div style={{marginTop:16,fontSize:11,color:S.text3,lineHeight:1.6}}>
                Need enterprise pricing or a BAA-signed plan? Email <span style={{fontFamily:"monospace",color:S.accent}}>sales@chatfortai.ai</span>.
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  function SsoPage() {
    const [cfg, setCfg] = useState({ issuer:"", clientId:"", clientSecret:"", scopes:"openid email profile", allowedEmailDomains:[] });
    const [saved, setSaved] = useState(null); // server state as returned
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [domainsInput, setDomainsInput] = useState("");

    const load = useCallback(async () => {
      if (!serverMode || !authToken) return;
      try {
        const r = await fetch("/api/admin/sso-config", { headers: { "Authorization": `Bearer ${authToken}` }});
        if (r.ok) {
          const d = await r.json();
          if (d) {
            setSaved(d);
            setCfg({ issuer: d.issuer || "", clientId: d.clientId || "", clientSecret: "", scopes: d.scopes || "openid email profile", allowedEmailDomains: d.allowedEmailDomains || [] });
            setDomainsInput((d.allowedEmailDomains || []).join(", "));
          }
        }
      } catch {}
      setLoading(false);
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
      if (!cfg.issuer.trim() || !cfg.clientId.trim()) { showToast("Issuer and Client ID required"); return; }
      setBusy(true);
      const body = {
        issuer: cfg.issuer.trim(),
        clientId: cfg.clientId.trim(),
        scopes: cfg.scopes.trim(),
        allowedEmailDomains: domainsInput.split(",").map(s => s.trim()).filter(Boolean),
      };
      if (cfg.clientSecret) body.clientSecret = cfg.clientSecret;
      const r = await fetch("/api/admin/sso-config", { method:"PUT", headers: {"Content-Type":"application/json","Authorization":`Bearer ${authToken}`}, body: JSON.stringify(body) });
      setBusy(false);
      if (!r.ok) { const d = await r.json().catch(()=>({})); showToast(d.error || "Save failed"); return; }
      showToast("SSO configuration saved");
      await load();
    };
    const remove = async () => {
      if (!window.confirm("Remove SSO configuration? Users will have to sign in with password again.")) return;
      setBusy(true);
      await fetch("/api/admin/sso-config", { method:"DELETE", headers: { "Authorization": `Bearer ${authToken}` }});
      setBusy(false);
      setSaved(null);
      setCfg({ issuer:"", clientId:"", clientSecret:"", scopes:"openid email profile", allowedEmailDomains:[] });
      setDomainsInput("");
      showToast("SSO removed");
    };

    const callbackUrl = (typeof window !== "undefined" ? window.location.origin : "https://app.chatfortai.com") + "/api/auth/oidc/callback";

    return (
      <div style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
        <div style={{maxWidth:680,margin:"0 auto"}}>
          <div style={{marginBottom:20}}>
            <h2 style={{fontSize:20,fontWeight:800,color:S.text,marginBottom:4}}>Single sign-on</h2>
            <p style={{fontSize:13,color:S.text2}}>OIDC — works with Okta, Entra ID, Google Workspace, Auth0, and anything OIDC-compliant.</p>
          </div>

          {/* Setup hints */}
          <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderLeft:`3px solid ${S.accent}`,borderRadius:12,padding:16,marginBottom:16,fontSize:12,color:S.text2,lineHeight:1.65}}>
            <div style={{fontWeight:700,color:S.text,marginBottom:8}}>Setup at your identity provider</div>
            1. Register a new OIDC application.<br/>
            2. Set the redirect URI to: <span style={{fontFamily:"monospace",fontSize:11,color:S.accent,background:S.inputBg,padding:"2px 6px",borderRadius:4}}>{callbackUrl}</span><br/>
            3. Copy the Issuer URL, Client ID, and Client Secret below.<br/>
            4. Optional: restrict by email domain (e.g. <code>yourco.com</code>).
          </div>

          <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:20}}>
            {loading ? (
              <div style={{textAlign:"center",padding:"20px",color:S.text3,fontSize:12}}>Loading…</div>
            ) : (
              <>
                <div style={{marginBottom:14}}>
                  <label style={{display:"block",fontSize:11,color:S.text2,marginBottom:5,fontFamily:"monospace",textTransform:"uppercase"}}>Issuer URL</label>
                  <input type="url" value={cfg.issuer} onChange={e=>setCfg(p=>({...p,issuer:e.target.value}))}
                    placeholder="https://login.microsoftonline.com/<tenant>/v2.0"
                    style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"10px 13px",color:S.text,fontFamily:"monospace",fontSize:12}}/>
                </div>
                <div style={{marginBottom:14}}>
                  <label style={{display:"block",fontSize:11,color:S.text2,marginBottom:5,fontFamily:"monospace",textTransform:"uppercase"}}>Client ID</label>
                  <input type="text" value={cfg.clientId} onChange={e=>setCfg(p=>({...p,clientId:e.target.value}))}
                    style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"10px 13px",color:S.text,fontFamily:"monospace",fontSize:12}}/>
                </div>
                <div style={{marginBottom:14}}>
                  <label style={{display:"block",fontSize:11,color:S.text2,marginBottom:5,fontFamily:"monospace",textTransform:"uppercase"}}>Client Secret {saved?.clientSecretSet && <span style={{color:S.success,textTransform:"none"}}>· saved (leave blank to keep)</span>}</label>
                  <input type="password" value={cfg.clientSecret} onChange={e=>setCfg(p=>({...p,clientSecret:e.target.value}))}
                    placeholder={saved?.clientSecretSet ? "••••••••" : "Paste from your IdP"}
                    style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"10px 13px",color:S.text,fontFamily:"monospace",fontSize:12}}/>
                </div>
                <div style={{marginBottom:14}}>
                  <label style={{display:"block",fontSize:11,color:S.text2,marginBottom:5,fontFamily:"monospace",textTransform:"uppercase"}}>Scopes</label>
                  <input type="text" value={cfg.scopes} onChange={e=>setCfg(p=>({...p,scopes:e.target.value}))}
                    style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"10px 13px",color:S.text,fontFamily:"monospace",fontSize:12}}/>
                </div>
                <div style={{marginBottom:18}}>
                  <label style={{display:"block",fontSize:11,color:S.text2,marginBottom:5,fontFamily:"monospace",textTransform:"uppercase"}}>Allowed Email Domains <span style={{textTransform:"none",color:S.text3}}>· comma-separated · leave blank = any</span></label>
                  <input type="text" value={domainsInput} onChange={e=>setDomainsInput(e.target.value)}
                    placeholder="yourcompany.com, subsidiary.com"
                    style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"10px 13px",color:S.text,fontFamily:"monospace",fontSize:12}}/>
                </div>
                <div style={{display:"flex",gap:10,alignItems:"center"}}>
                  <button onClick={save} disabled={busy} style={{padding:"10px 18px",borderRadius:10,border:"none",background:busy?S.border3:`linear-gradient(135deg,${S.accent},${S.accent2})`,color:"#fff",fontSize:12,fontWeight:700,cursor:busy?"default":"pointer",fontFamily:"inherit"}}>
                    {busy ? "Saving…" : saved ? "Update configuration" : "Save & enable SSO"}
                  </button>
                  {saved && <button onClick={remove} disabled={busy} style={{padding:"10px 16px",borderRadius:10,border:`1px solid ${S.danger}55`,background:"transparent",color:S.danger,fontSize:12,fontWeight:600,cursor:busy?"default":"pointer",fontFamily:"inherit"}}>Remove</button>}
                  {saved && orgInfo && (
                    <button onClick={()=>window.open("/api/auth/oidc/start?org=" + encodeURIComponent(orgInfo.id), "_blank")}
                      style={{marginLeft:"auto",padding:"10px 16px",borderRadius:10,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Test login ↗</button>
                  )}
                </div>
              </>
            )}
          </div>

          {orgInfo && (
            <div style={{marginTop:16,fontSize:11,color:S.text3,textAlign:"center"}}>
              Share this org ID with users so they can sign in with SSO:{" "}
              <span style={{fontFamily:"monospace",color:S.text2,background:S.inputBg,padding:"2px 8px",borderRadius:5}}>{orgInfo.id}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Bring-your-own model provider cards (Anthropic / Google / Azure /
  // Bedrock) ─────────────────────────────────────────────────────────────────
  // These four route a customer's OWN credentials through their org's chat
  // inference. Each card may carry extra fields beyond the key (Azure:
  // endpoint + deployment; Bedrock: region). Saved into integrations[pid]
  // (synced to the server's shared-integrations.json), which the chat route
  // now reads as a per-org key override.
  function BYOKProvidersSection({ integrations, setIntegrations, showToast, isConnected }) {
    const DEFS = [
      { pid: "anthropic", name: "Anthropic", icon: "🟣", color: "#cc785c",
        help: "Your own Anthropic key (sk-ant-…). Routes your org's Claude calls through your account.",
        fields: [{ k: "key", label: "API key", pw: true, ph: "sk-ant-…" }] },
      { pid: "google", name: "Google Gemini", icon: "🔷", color: "#4285f4",
        help: "Google AI Studio API key. Unlocks Gemini models for your org.",
        fields: [{ k: "key", label: "API key", pw: true, ph: "AIza…" }] },
      { pid: "azure", name: "Azure AI Foundry", icon: "☁️", color: "#0078d4",
        help: "Azure OpenAI / AI Foundry — key, resource endpoint, and deployment name.",
        fields: [
          { k: "key", label: "API key", pw: true, ph: "Azure key" },
          { k: "endpoint", label: "Endpoint", pw: false, ph: "https://<resource>.openai.azure.com" },
          { k: "deployment", label: "Deployment", pw: false, ph: "gpt-4o" },
        ] },
      { pid: "bedrock", name: "AWS Bedrock", icon: "🟧", color: "#ff9900",
        help: "Amazon Bedrock API key (bearer) plus the AWS region your models live in.",
        fields: [
          { k: "key", label: "Bedrock API key", pw: true, ph: "ABSK…" },
          { k: "region", label: "Region", pw: false, ph: "us-east-1" },
        ] },
    ];
    const [draft, setDraft] = useState(() => {
      const d = {};
      for (const def of DEFS) { d[def.pid] = {}; for (const f of def.fields) d[def.pid][f.k] = (integrations[def.pid] || {})[f.k] || ""; }
      return d;
    });
    const [show, setShow] = useState({});
    const setField = (pid, k, v) => setDraft(p => ({ ...p, [pid]: { ...p[pid], [k]: v } }));
    const connect = (def) => {
      const vals = draft[def.pid] || {};
      for (const f of def.fields) {
        if (!String(vals[f.k] || "").trim()) { showToast("Enter the " + f.label.toLowerCase() + " first"); return; }
      }
      const rec = {};
      for (const f of def.fields) rec[f.k] = String(vals[f.k]).trim();
      setIntegrations(p => ({ ...p, [def.pid]: rec }));
      showToast(def.name + " connected!");
    };
    const disconnect = (def) => {
      setIntegrations(p => { const n = { ...p }; delete n[def.pid]; return n; });
      setDraft(p => { const n = { ...p }; n[def.pid] = {}; for (const f of def.fields) n[def.pid][f.k] = ""; return n; });
      showToast(def.name + " disconnected");
    };
    return (
      <>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: S.text3, marginBottom: 8, marginTop: 24, fontFamily: "monospace", letterSpacing: "0.08em" }}>Model providers · bring your own keys</div>
        {DEFS.map(def => {
          const connected = isConnected(def.pid);
          return (
            <div key={def.pid} className="cf-tile-hover" style={{ background: S.cardBg, border: `1px solid ${connected ? S.success + "55" : S.border2}`, borderLeft: connected ? `3px solid ${S.success}88` : `1px solid ${S.border2}`, borderRadius: 12, padding: 14, marginBottom: 8 }}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: `${def.color}22`, fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{def.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: S.text, marginBottom: 2 }}>{def.name}</div>
                  <div style={{ fontSize: 10, color: connected ? S.success : S.text3, marginBottom: 6 }}>{connected ? "Connected" : "Not connected"}</div>
                  <div style={{ fontSize: 11, color: S.text3, lineHeight: 1.45, marginBottom: 8 }}>{def.help}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {def.fields.map(f => (
                      <div key={f.k} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <input type={f.pw && !show[def.pid + f.k] ? "password" : "text"} placeholder={f.label + " · " + f.ph} value={(draft[def.pid] || {})[f.k] || ""} onChange={e => setField(def.pid, f.k, e.target.value)} style={{ flex: 1, background: S.inputBg, border: `1px solid ${S.border2}`, borderRadius: 8, padding: "8px 10px", color: S.text, fontFamily: "monospace", fontSize: 12, outline: "none" }} />
                        {f.pw && <button onClick={() => setShow(p => ({ ...p, [def.pid + f.k]: !p[def.pid + f.k] }))} style={{ background: "none", border: "none", color: S.text3, cursor: "pointer", padding: 4, display: "flex" }}>{IC.eye}</button>}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    {connected
                      ? <button onClick={() => disconnect(def)} style={{ padding: "6px 14px", borderRadius: 7, background: "transparent", border: `1px solid ${S.danger}55`, color: S.danger, fontSize: 11, cursor: "pointer" }}>Disconnect</button>
                      : <button onClick={() => connect(def)} style={{ padding: "6px 14px", borderRadius: 7, background: S.accentDim, border: `1px solid ${S.accent}44`, color: S.accent, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Connect</button>}
                    {connected && <button onClick={() => connect(def)} style={{ padding: "6px 14px", borderRadius: 7, background: S.inputBg, border: `1px solid ${S.border2}`, color: S.text2, fontSize: 11, cursor: "pointer" }}>Update</button>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </>
    );
  }

  function IntegrationsPage(){
    const [keys,setKeys]=useState(()=>Object.fromEntries(Object.entries(integrations).map(([k,v])=>[k,v?.key||""])));
    const [show,setShow]=useState({});
    // fix/mcp-server-proxy: single source of truth for the "connected"
    // status. Previously the top list and the per-provider detail
    // cards (HermesSection, OpenRouterSection) each computed it
    // locally with slightly different rules, which is how the same
    // provider could show "Not connected" in the list and
    // "Connected · N models" in the detail card. Server endpoint
    // applies one canonical rule (env-managed OR user-key) and both
    // surfaces consume the same flag.
    const [integrationStatus, setIntegrationStatus] = useState(null);
    useEffect(() => {
      let cancelled = false;
      const tick = async () => {
        try {
          const r = await fetch("/api/integrations/status", {
            headers: { "Authorization": "Bearer " + (localStorage.getItem("cf_auth_token") || "") },
          });
          if (!r.ok) return;
          const d = await r.json();
          if (!cancelled) setIntegrationStatus(d);
        } catch { /* network blip; next tick retries */ }
      };
      tick();
      const t = setInterval(tick, 10000);
      return () => { cancelled = true; clearInterval(t); };
    }, []);
    const isConnected = (pid) => {
      // Prefer server-side canonical status when loaded. Falls back to
      // the legacy local rule only during the brief first-paint window
      // before the fetch completes -- after that, server is source of truth.
      if (integrationStatus && integrationStatus.providers && integrationStatus.providers[pid]) {
        return !!integrationStatus.providers[pid].connected;
      }
      return !!integrations[pid]?.key;
    };
    const connect=(pid)=>{const k=keys[pid]||"";if(!k.trim()){showToast("Enter an API key first");return;}setIntegrations(p=>({...p,[pid]:{key:k}}));showToast(PROVIDERS[pid]?.name+" connected!");};const disconnect=(pid)=>{setIntegrations(p=>{const n={...p};delete n[pid];return n;});setKeys(p=>({...p,[pid]:""}));showToast(PROVIDERS[pid]?.name+" disconnected");};return(<div style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}><div style={{maxWidth:680,margin:"0 auto"}}><div style={{marginBottom:24}}><h2 className="cf-page-header" style={{fontSize:20,fontWeight:800,color:S.text,marginBottom:4}}>Integrations</h2><p style={{fontSize:13,color:S.text2}}>{isSuperAdmin ? "Connect AI providers. Keys stay local." : "Models are provided under your ChatFortAI plan. To use your own API keys instead, add a provider below."}</p></div>{isSuperAdmin && <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",color:S.text3,marginBottom:8,fontFamily:"monospace",letterSpacing:"0.08em"}}>Built-in</div>}{isSuperAdmin && Object.entries(PROVIDERS).filter(([,p])=>p.builtin).map(([pid,p])=>(<div key={pid} style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderLeft:`3px solid ${S.success}88`,borderRadius:12,padding:14,marginBottom:8,display:"flex",gap:12,alignItems:"center"}}><div style={{width:36,height:36,borderRadius:8,background:`${p.color}22`,fontSize:18,display:"flex",alignItems:"center",justifyContent:"center"}}>{p.icon}</div><div><div style={{fontSize:13,fontWeight:600,color:S.text}}>{p.name}</div><div style={{fontSize:10,color:S.success,marginTop:2}}>Built-in \u00b7 no key needed \u00b7 {p.models?.length} models</div></div></div>))}<div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",color:S.text3,marginBottom:8,marginTop:20,fontFamily:"monospace",letterSpacing:"0.08em"}}>{isSuperAdmin ? "Third-party" : "Bring your own API keys (optional)"}</div>{Object.entries(PROVIDERS).filter(([pid,p])=>!p.builtin && !p.byokCard && (isSuperAdmin || !integrations[pid]?.managedByServer)).map(([pid,p])=>{const connected=isConnected(pid);return(<div key={pid} className="cf-tile-hover" style={{background:S.cardBg,border:`1px solid ${connected?S.success+"55":S.border2}`,borderLeft:connected?`3px solid ${S.success}88`:`1px solid ${S.border2}`,borderRadius:12,padding:14,marginBottom:8}}><div style={{display:"flex",gap:12,alignItems:"flex-start"}}><div style={{width:36,height:36,borderRadius:8,background:`${p.color}22`,fontSize:18,display:"flex",alignItems:"center",justifyContent:"center"}}>{p.icon}</div><div style={{flex:1}}><div style={{fontSize:13,fontWeight:600,color:S.text,marginBottom:2}}>{p.name}</div><div style={{fontSize:10,color:connected?S.success:S.text3,marginBottom:8}}>{connected?"Connected":"Not connected"}</div><div style={{display:"flex",gap:6,alignItems:"center"}}><input type={show[pid]?"text":"password"} placeholder="API key\u2026" value={keys[pid]||""} onChange={e=>setKeys(p=>({...p,[pid]:e.target.value}))} style={{...ISt(),flex:1,fontFamily:"monospace",fontSize:12}}/><button onClick={()=>setShow(p=>({...p,[pid]:!p[pid]}))} style={{background:"none",border:"none",color:S.text3,cursor:"pointer",padding:4,display:"flex"}}>{IC.eye}</button>{connected?<button onClick={()=>disconnect(pid)} style={{padding:"6px 12px",borderRadius:7,background:"transparent",border:`1px solid ${S.danger}55`,color:S.danger,fontSize:11,cursor:"pointer",whiteSpace:"nowrap"}}>Disconnect</button>:<button onClick={()=>connect(pid)} style={{padding:"6px 12px",borderRadius:7,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text2,fontSize:11,cursor:"pointer",whiteSpace:"nowrap"}}>Connect</button>}</div></div></div></div>);})}
          <BYOKProvidersSection integrations={integrations} setIntegrations={setIntegrations} showToast={showToast} isConnected={isConnected}/>
          {(isSuperAdmin || !integrations.openrouter?.managedByServer) && <>
          <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",color:S.text3,marginBottom:8,marginTop:24,fontFamily:"monospace",letterSpacing:"0.08em"}}>🔀 OpenRouter</div>
          {/* fix/mcp-server-proxy: pass isConnected down so the detail card
              shares the same canonical status source as the top list. */}
          <OpenRouterSection integrations={integrations} setIntegrations={setIntegrations} showToast={showToast} isConnected={isConnected}/>
          </>}
          <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",color:S.text3,marginBottom:8,marginTop:24,fontFamily:"monospace",letterSpacing:"0.08em"}}>🧠 Nous Hermes</div>
          <HermesSection integrations={integrations} setIntegrations={setIntegrations} showToast={showToast} isConnected={isConnected}/>
          <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",color:S.text3,marginBottom:8,marginTop:24,fontFamily:"monospace",letterSpacing:"0.08em"}}>CLI Integration</div>
          <CLISection integrations={integrations} setIntegrations={setIntegrations} showToast={showToast}/>
        </div></div>);}

  function HermesSection({integrations, setIntegrations, showToast, isConnected}) {
    const [apiKey, setApiKey] = useState(integrations.hermes?.key || "");
    const [useOpenRouter, setUseOpenRouter] = useState(integrations.hermes?.useOpenRouter ?? !integrations.hermes?.key);
    const [saved, setSaved] = useState(!!integrations.hermes?.key || !!integrations.hermes?.useOpenRouter);
    const [showKey, setShowKey] = useState(false);
    // fix/mcp-server-proxy: use the canonical isConnected helper passed
    // down from IntegrationsPage. Previously this was
    //   const orConnected = !!integrations.openrouter?.key;
    // which disagreed with the top list (env-managed keys made the top
    // list say "Not connected" while this said "Connected") -- the
    // classic dual-status bug. With isConnected the rule matches.
    const orConnected = typeof isConnected === "function"
      ? isConnected("openrouter")
      : !!integrations.openrouter?.key;

    function save() {
      if (useOpenRouter) {
        if (!orConnected) { showToast("Connect OpenRouter first in the section above"); return; }
        setIntegrations(p => ({...p, hermes:{useOpenRouter:true}}));
        setSaved(true); showToast("Hermes routed via OpenRouter!");
      } else {
        if (!apiKey.trim()) { showToast("Enter your Nous API key"); return; }
        setIntegrations(p => ({...p, hermes:{key:apiKey.trim(), useOpenRouter:false}}));
        setSaved(true); showToast("Hermes connected!");
      }
    }

    const connected = saved && (useOpenRouter ? orConnected : !!integrations.hermes?.key);

    const HERMES_MODELS = [
      {id:"hermes-3-llama-3.1-70b", name:"Hermes 3 70B", badge:"Recommended", desc:"Best balance of speed and capability. Excellent tool calling, structured output, and reasoning."},
      {id:"hermes-3-llama-3.1-405b", name:"Hermes 3 405B", badge:"Flagship", desc:"Maximum reasoning depth. Best for complex multi-step problems and high-stakes tasks."},
      {id:"hermes-2-pro-llama-3-8b", name:"Hermes 2 Pro 8B", badge:"Fast", desc:"Lightweight and efficient. Good for quick structured extraction and simple tool use."},
    ];

    const HERMES_STRENGTHS = [
      {icon:"🔧", label:"Function Calling", desc:"Native tool use with JSON schema validation. Reliable structured outputs."},
      {icon:"🧩", label:"Structured Output", desc:"Guaranteed JSON/XML output conforming to any schema you define."},
      {icon:"🔗", label:"Agentic Chains", desc:"Multi-step reasoning with tool orchestration and memory management."},
      {icon:"📐", label:"ChatML Format", desc:"Native ChatML prompt format for precise system/user/assistant control."},
      {icon:"🔒", label:"On-premise ready", desc:"Open weights — run locally via Ollama or vLLM for full data control."},
      {icon:"⚡", label:"Context Length", desc:"128K token context window on Hermes 3 models for long documents."},
    ];

    return (
      <div style={{background:S.cardBg, border:"1px solid "+(connected?S.success+"55":S.border2), borderLeft:"3px solid "+(connected?S.success:"#f59e0b"), borderRadius:12, overflow:"hidden", marginBottom:8}}>
        {/* Header */}
        <div style={{padding:"14px 16px", borderBottom:"1px solid "+S.border, display:"flex", alignItems:"center", gap:12}}>
          <div style={{width:38, height:38, borderRadius:10, background:"#f59e0b18", border:"1px solid #f59e0b30", fontSize:20, display:"flex", alignItems:"center", justifyContent:"center"}}>🧠</div>
          <div style={{flex:1}}>
            <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:2}}>
              <span style={{fontSize:14, fontWeight:700, color:S.text}}>Nous Hermes</span>
              <span style={{fontSize:9, padding:"1px 6px", borderRadius:4, fontFamily:"monospace", fontWeight:700, background:"#f59e0b18", color:"#f59e0b", border:"1px solid #f59e0b30"}}>Tool Use · Structured Output</span>
              {connected && <span style={{fontSize:9, padding:"1px 6px", borderRadius:4, fontFamily:"monospace", background:S.success+"18", color:S.success}}>Connected</span>}
            </div>
            <div style={{fontSize:11, color:S.text2}}>Open-weights models fine-tuned for function calling, agentic tasks, and structured reasoning.</div>
          </div>
          <a href="https://nousresearch.com/hermes" target="_blank" rel="noreferrer" style={{padding:"5px 10px", borderRadius:7, background:S.inputBg, border:"1px solid "+S.border2, color:S.text3, fontSize:11, textDecoration:"none", fontFamily:"monospace", whiteSpace:"nowrap"}}>About ↗</a>
        </div>

        <div style={{padding:16}}>
          {/* Connection method */}
          <div style={{marginBottom:14}}>
            <div style={{fontSize:10, color:S.text3, fontFamily:"monospace", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.05em"}}>Connection Method</div>
            <div style={{display:"flex", gap:8}}>
              {[
                {val:true, label:"Via OpenRouter", desc:"Use your OpenRouter key", icon:"🔀", avail:orConnected},
                {val:false, label:"Direct API", desc:"Nous API key (beta)", icon:"🧠", avail:true},
              ].map(opt => (
                <div key={String(opt.val)} onClick={()=>setUseOpenRouter(opt.val)}
                  style={{flex:1, padding:"10px 12px", borderRadius:10, border:"1px solid "+(useOpenRouter===opt.val?"#f59e0b":""+S.border2), background:useOpenRouter===opt.val?"#f59e0b10":S.inputBg, cursor:"pointer", opacity:!opt.avail&&opt.val?0.5:1, transition:"all 0.15s"}}>
                  <div style={{display:"flex", alignItems:"center", gap:6, marginBottom:3}}>
                    <span style={{fontSize:14}}>{opt.icon}</span>
                    <span style={{fontSize:12, fontWeight:useOpenRouter===opt.val?700:400, color:useOpenRouter===opt.val?"#f59e0b":S.text}}>{opt.label}</span>
                    {useOpenRouter===opt.val && <div style={{marginLeft:"auto", width:6, height:6, borderRadius:"50%", background:"#f59e0b"}}/>}
                  </div>
                  <div style={{fontSize:10, color:S.text3}}>{opt.desc}</div>
                  {opt.val && !orConnected && <div style={{fontSize:9, color:S.danger, marginTop:3, fontFamily:"monospace"}}>⚠ Connect OpenRouter first</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Key input (only for direct) */}
          {!useOpenRouter && (
            <div style={{marginBottom:14}}>
              <div style={{fontSize:10, color:S.text3, fontFamily:"monospace", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.05em"}}>Nous API Key</div>
              <div style={{display:"flex", gap:6}}>
                <input type={showKey?"text":"password"} value={apiKey} onChange={e=>{setApiKey(e.target.value);setSaved(false);}}
                  placeholder="nous-..." style={{...ISt(), flex:1, fontFamily:"monospace", fontSize:12}}/>
                <button onClick={()=>setShowKey(p=>!p)} style={{background:"none",border:"none",color:S.text3,cursor:"pointer",padding:"0 6px",display:"flex",alignItems:"center"}}>{IC.eye}</button>
              </div>
              <div style={{fontSize:10, color:S.text3, marginTop:5, fontFamily:"monospace"}}>
                Get access at <a href="https://nousresearch.com" target="_blank" rel="noreferrer" style={{color:"#f59e0b", textDecoration:"none"}}>nousresearch.com ↗</a> · Or use via OpenRouter (no separate key needed)
              </div>
            </div>
          )}

          <button onClick={save} style={{padding:"8px 16px", borderRadius:9, background:"linear-gradient(135deg,#f59e0b,#d97706)", border:"none", color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", marginBottom:16}}>
            {saved?(useOpenRouter?"Routing via OpenRouter ✓":"Connected ✓"):"Connect Hermes"}
          </button>

          {/* Models */}
          <div style={{marginBottom:14}}>
            <div style={{fontSize:10, color:S.text3, fontFamily:"monospace", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.05em"}}>Available Models</div>
            <div style={{display:"flex", flexDirection:"column", gap:6}}>
              {HERMES_MODELS.map(m => (
                <div key={m.id} style={{padding:"10px 13px", borderRadius:9, background:S.surface2, border:"1px solid "+S.border, display:"flex", alignItems:"flex-start", gap:10}}>
                  <div style={{flex:1}}>
                    <div style={{display:"flex", alignItems:"center", gap:7, marginBottom:2}}>
                      <span style={{fontSize:12, fontWeight:700, color:S.text, fontFamily:"monospace"}}>{m.name}</span>
                      <span style={{fontSize:8, padding:"1px 5px", borderRadius:3, fontFamily:"monospace", fontWeight:700, background:"#f59e0b18", color:"#f59e0b", border:"1px solid #f59e0b30"}}>{m.badge}</span>
                    </div>
                    <div style={{fontSize:11, color:S.text2}}>{m.desc}</div>
                    <div style={{fontSize:9, color:S.text3, fontFamily:"monospace", marginTop:2}}>{useOpenRouter?"nousresearch/"+m.id+" via OpenRouter":m.id}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Strengths grid */}
          <div style={{marginBottom:14}}>
            <div style={{fontSize:10, color:S.text3, fontFamily:"monospace", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.05em"}}>Why Hermes?</div>
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:7}}>
              {HERMES_STRENGTHS.map(s => (
                <div key={s.label} style={{padding:"9px 11px", borderRadius:8, background:S.surface2, border:"1px solid "+S.border}}>
                  <div style={{display:"flex", alignItems:"center", gap:6, marginBottom:3}}>
                    <span style={{fontSize:14}}>{s.icon}</span>
                    <span style={{fontSize:11, fontWeight:600, color:S.text}}>{s.label}</span>
                  </div>
                  <div style={{fontSize:10, color:S.text3, lineHeight:1.5}}>{s.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Ollama local tip */}
          <div style={{background:"#f59e0b08", border:"1px solid #f59e0b30", borderRadius:8, padding:"10px 13px", fontSize:11, color:S.text2, lineHeight:1.7}}>
            <strong style={{color:"#f59e0b"}}>Run locally with Ollama:</strong> Hermes is open-weights — pull and run on your own hardware for complete privacy. Use OpenRouter as a fallback when local resources are busy.
            <div style={{fontFamily:"monospace", fontSize:10, color:S.text3, marginTop:6, background:S.codeBlockBg, padding:"6px 10px", borderRadius:6}}>ollama pull nous-hermes3:70b && ollama serve</div>
          </div>
        </div>
      </div>
    );
  }

  function OpenRouterSection({integrations, setIntegrations, showToast, isConnected}) {
    const [apiKey, setApiKey] = useState(integrations.openrouter?.key || "");
    const [saved, setSaved] = useState(!!integrations.openrouter?.key);
    const [loadingModels, setLoadingModels] = useState(false);
    const [allModels, setAllModels] = useState(integrations.openrouter?.cachedModels || []);
    const [search, setSearch] = useState("");
    const [showBrowser, setShowBrowser] = useState(false);
    const [fallbackInput, setFallbackInput] = useState((integrations.openrouter?.fallbacks || []).join(", "));
    const [provOrder, setProvOrder] = useState((integrations.openrouter?.providerOrder || []).join(", "));
    const [fallbackEnabled, setFallbackEnabled] = useState(integrations.openrouter?.fallbackEnabled || false);
    const [pinned, setPinned] = useState(integrations.openrouter?.pinned || []);
    const [showKey, setShowKey] = useState(false);

    function saveKey() {
      if (!apiKey.trim()) { showToast("Enter your OpenRouter API key"); return; }
      setIntegrations(p => ({...p, openrouter:{
        ...p.openrouter,
        key: apiKey.trim(),
        fallbacks: fallbackInput.split(",").map(s=>s.trim()).filter(Boolean),
        providerOrder: provOrder.split(",").map(s=>s.trim()).filter(Boolean),
        fallbackEnabled,
        pinned,
        cachedModels: allModels,
      }}));
      setSaved(true);
      showToast("OpenRouter connected!");
    }

    function saveRouting() {
      setIntegrations(p => ({...p, openrouter:{
        ...p.openrouter,
        fallbacks: fallbackInput.split(",").map(s=>s.trim()).filter(Boolean),
        providerOrder: provOrder.split(",").map(s=>s.trim()).filter(Boolean),
        fallbackEnabled,
        pinned,
      }}));
      showToast("Routing config saved");
    }

    async function fetchModels() {
      const key = apiKey.trim() || integrations.openrouter?.key;
      if (!key) { showToast("Save your API key first"); return; }
      setLoadingModels(true);
      try {
        const r = await fetch("https://openrouter.ai/api/v1/models", {
          headers: {"Authorization": "Bearer " + key}
        });
        if (!r.ok) throw new Error("HTTP " + r.status);
        const d = await r.json();
        const models = (d.data || []).sort((a,b) => (b.context_length||0) - (a.context_length||0));
        setAllModels(models);
        setIntegrations(p => ({...p, openrouter:{...p.openrouter, cachedModels: models}}));
        showToast("Loaded " + models.length + " models");
        setShowBrowser(true);
      } catch(e) {
        showToast("Failed to load models: " + e.message);
      }
      setLoadingModels(false);
    }

    function togglePin(modelId) {
      const next = pinned.includes(modelId) ? pinned.filter(id=>id!==modelId) : [...pinned, modelId];
      setPinned(next);
      setIntegrations(p => ({...p, openrouter:{...p.openrouter, pinned: next}}));
    }

    function addToDefaultModels(model) {
      // Add to PROVIDERS.openrouter.models dynamically for the model picker
      const existing = PROVIDERS.openrouter.models.find(m => m.id === model.id);
      if (!existing) {
        PROVIDERS.openrouter.models.push({id: model.id, name: model.name || model.id.split("/").pop(), desc: model.id.split("/")[0]});
      }
      showToast("Added to model picker: " + (model.name || model.id));
    }

    const filtered = allModels.filter(m =>
      !search || m.id.toLowerCase().includes(search.toLowerCase()) || (m.name||"").toLowerCase().includes(search.toLowerCase())
    ).slice(0, 60);

    // fix/mcp-server-proxy: canonical "connected" via isConnected from
    // IntegrationsPage. Falls back to the legacy local rule during the
    // brief first-paint window before the status fetch completes.
    const connected = typeof isConnected === "function"
      ? isConnected("openrouter")
      : !!integrations.openrouter?.key;

    const formatCtx = (n) => n >= 1000000 ? (n/1000000).toFixed(0)+"M" : n >= 1000 ? (n/1000).toFixed(0)+"k" : n || "?";
    const formatCost = (n) => n ? "$" + (n * 1000000).toFixed(2) + "/M" : "free";

    return (
      <div style={{background:S.cardBg, border:"1px solid " + (connected ? S.success+"55" : S.border2), borderLeft:"3px solid " + (connected ? S.success : "#7c3aed"), borderRadius:12, overflow:"hidden", marginBottom:8}}>
        {/* Header */}
        <div style={{padding:"14px 16px", borderBottom:"1px solid "+S.border, display:"flex", alignItems:"center", gap:12}}>
          <div style={{width:38, height:38, borderRadius:10, background:"#7c3aed18", border:"1px solid #7c3aed30", fontSize:20, display:"flex", alignItems:"center", justifyContent:"center"}}>🔀</div>
          <div style={{flex:1}}>
            <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:2}}>
              <span style={{fontSize:14, fontWeight:700, color:S.text}}>OpenRouter</span>
              <span style={{fontSize:9, padding:"1px 6px", borderRadius:4, fontFamily:"monospace", fontWeight:700, background:"#7c3aed18", color:"#7c3aed", border:"1px solid #7c3aed30"}}>300+ models</span>
              {connected && <span style={{fontSize:9, padding:"1px 6px", borderRadius:4, fontFamily:"monospace", background:S.success+"18", color:S.success}}>Connected</span>}
            </div>
            <div style={{fontSize:11, color:S.text2}}>One key to access GPT-4o, Claude, Gemini, Llama, Mistral, DeepSeek, Grok and hundreds more.</div>
          </div>
          <a href="https://openrouter.ai/keys" target="_blank" rel="noreferrer" style={{padding:"5px 10px", borderRadius:7, background:S.inputBg, border:"1px solid "+S.border2, color:S.text3, fontSize:11, textDecoration:"none", fontFamily:"monospace", whiteSpace:"nowrap"}}>Get key ↗</a>
        </div>

        <div style={{padding:16}}>
          {/* API Key */}
          <div style={{marginBottom:14}}>
            <div style={{fontSize:10, color:S.text3, fontFamily:"monospace", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.05em"}}>API Key</div>
            <div style={{display:"flex", gap:6}}>
              <input type={showKey ? "text" : "password"} value={apiKey} onChange={e=>{setApiKey(e.target.value); setSaved(false);}}
                placeholder="sk-or-v1-..." style={{...ISt(), flex:1, fontFamily:"monospace", fontSize:12}}/>
              <button onClick={()=>setShowKey(p=>!p)} style={{background:"none", border:"none", color:S.text3, cursor:"pointer", padding:"0 6px", display:"flex", alignItems:"center"}}>{IC.eye}</button>
              <button onClick={saveKey} style={{padding:"8px 14px", borderRadius:8, background:S.grad2, border:"none", color:"#fff", fontSize:12, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap"}}>
                {saved ? "Saved ✓" : "Connect"}
              </button>
            </div>
          </div>

          {/* Model Browser */}
          <div style={{marginBottom:14}}>
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8}}>
              <div style={{fontSize:10, color:S.text3, fontFamily:"monospace", textTransform:"uppercase", letterSpacing:"0.05em"}}>
                Model Browser {allModels.length > 0 && <span style={{color:S.accent}}>· {allModels.length} loaded</span>}
              </div>
              <div style={{display:"flex", gap:6}}>
                {allModels.length > 0 && <button onClick={()=>setShowBrowser(p=>!p)} style={{padding:"3px 9px", borderRadius:6, background:S.inputBg, border:"1px solid "+S.border2, color:S.text2, fontSize:10, fontFamily:"monospace", cursor:"pointer"}}>{showBrowser ? "Hide" : "Show"}</button>}
                <button onClick={fetchModels} disabled={loadingModels} style={{padding:"3px 9px", borderRadius:6, background:S.accentDim, border:"1px solid "+S.border2, color:S.accent, fontSize:10, fontFamily:"monospace", cursor:"pointer", display:"flex", alignItems:"center", gap:4}}>
                  {loadingModels ? IC.spin : "⬇"} {loadingModels ? "Loading..." : allModels.length ? "Refresh" : "Load models"}
                </button>
              </div>
            </div>

            {showBrowser && allModels.length > 0 && (
              <div style={{border:"1px solid "+S.border2, borderRadius:10, overflow:"hidden"}}>
                <div style={{padding:"8px 10px", borderBottom:"1px solid "+S.border, background:S.surface2}}>
                  <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search models..." style={{...ISt(), fontSize:12, padding:"5px 10px"}}/>
                </div>
                <div style={{maxHeight:320, overflowY:"auto"}}>
                  {filtered.map(m => {
                    const isPinned = pinned.includes(m.id);
                    const inPicker = PROVIDERS.openrouter.models.some(x=>x.id===m.id);
                    return (
                      <div key={m.id} style={{padding:"9px 12px", borderBottom:"1px solid "+S.border, display:"flex", alignItems:"center", gap:10, background:isPinned ? S.accentDim : "none"}}>
                        <div style={{flex:1, minWidth:0}}>
                          <div style={{fontSize:12, fontWeight:600, color:S.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{m.name || m.id.split("/").pop()}</div>
                          <div style={{display:"flex", gap:6, marginTop:2, flexWrap:"wrap"}}>
                            <span style={{fontSize:9, fontFamily:"monospace", color:S.text3}}>{m.id.split("/")[0]}</span>
                            <span style={{fontSize:9, fontFamily:"monospace", color:S.text3, background:S.surface2, padding:"0 4px", borderRadius:3}}>ctx: {formatCtx(m.context_length)}</span>
                            {m.pricing?.prompt && <span style={{fontSize:9, fontFamily:"monospace", color:S.text3}}>{formatCost(parseFloat(m.pricing.prompt))} in</span>}
                          </div>
                        </div>
                        <div style={{display:"flex", gap:4, flexShrink:0}}>
                          <button onClick={()=>togglePin(m.id)} title={isPinned ? "Unpin" : "Pin"} style={{padding:"3px 7px", borderRadius:5, background:isPinned ? S.accentDim : S.inputBg, border:"1px solid "+(isPinned ? S.accent : S.border2), color:isPinned ? S.accent : S.text3, fontSize:10, cursor:"pointer"}}>
                            {isPinned ? "📌" : "📌"}
                          </button>
                          <button onClick={()=>addToDefaultModels(m)} title="Add to model picker" style={{padding:"3px 7px", borderRadius:5, background:inPicker ? S.success+"18" : S.inputBg, border:"1px solid "+(inPicker ? S.success+"44" : S.border2), color:inPicker ? S.success : S.text3, fontSize:10, cursor:"pointer"}}>
                            {inPicker ? "✓" : "+"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {filtered.length === 0 && <div style={{padding:"20px", textAlign:"center", color:S.text3, fontSize:12}}>No models match your search</div>}
                </div>
              </div>
            )}
          </div>

          {/* Routing Config */}
          <details style={{cursor:"pointer"}}>
            <summary style={{fontSize:11, fontWeight:600, color:S.text2, userSelect:"none", listStyle:"none", display:"flex", alignItems:"center", gap:6, marginBottom:8}}>
              <span style={{fontSize:9}}>▸</span> Advanced Routing
            </summary>
            <div style={{paddingTop:10, display:"flex", flexDirection:"column", gap:10}}>
              <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:S.surface2, borderRadius:8, border:"1px solid "+S.border}}>
                <div>
                  <div style={{fontSize:12, fontWeight:600, color:S.text}}>Enable fallback routing</div>
                  <div style={{fontSize:10, color:S.text3}}>Auto-retry with next model if primary fails</div>
                </div>
                <div onClick={()=>setFallbackEnabled(p=>!p)} style={{width:32, height:18, background:fallbackEnabled ? S.success : S.surface3, borderRadius:9, position:"relative", cursor:"pointer", border:"1px solid "+S.border2, transition:"background 0.2s"}}>
                  <div style={{position:"absolute", top:2, left:fallbackEnabled?14:2, width:12, height:12, background:"#fff", borderRadius:"50%", transition:"left 0.18s"}}/>
                </div>
              </div>
              <div>
                <div style={{fontSize:10, color:S.text3, fontFamily:"monospace", marginBottom:4}}>Fallback models (comma-separated model IDs)</div>
                <input value={fallbackInput} onChange={e=>setFallbackInput(e.target.value)} placeholder="openai/gpt-4o, meta-llama/llama-3.3-70b-instruct" style={{...ISt(), fontFamily:"monospace", fontSize:11}}/>
              </div>
              <div>
                <div style={{fontSize:10, color:S.text3, fontFamily:"monospace", marginBottom:4}}>Provider order (comma-separated, e.g. OpenAI, Anthropic)</div>
                <input value={provOrder} onChange={e=>setProvOrder(e.target.value)} placeholder="OpenAI, Anthropic, Together" style={{...ISt(), fontFamily:"monospace", fontSize:11}}/>
              </div>
              <button onClick={saveRouting} style={{padding:"7px 14px", borderRadius:8, background:S.inputBg, border:"1px solid "+S.border2, color:S.text2, fontSize:12, cursor:"pointer", alignSelf:"flex-start"}}>Save routing config</button>
            </div>
          </details>

          {/* Quick guide */}
          <div style={{marginTop:12, background:S.surface2, border:"1px solid "+S.border, borderRadius:8, padding:"10px 13px", fontSize:11, color:S.text2, lineHeight:1.7}}>
            <strong style={{color:S.text}}>How to use:</strong> Connect your key, load models, then click <strong>+</strong> next to any model to add it to the model picker in chat. OpenRouter routes through the cheapest available provider — or pin specific providers in routing config.{" "}
            <a href="https://openrouter.ai/docs" target="_blank" rel="noreferrer" style={{color:S.accent, textDecoration:"none"}}>Docs ↗</a>
          </div>
        </div>
      </div>
    );
  }

  function CLISection({integrations, setIntegrations, showToast}) {
    const [cliTab, setCliTab] = useState("claudecode");
    const [apiKey, setApiKey] = useState(integrations.cli_anthropic?.key || "");
    const [saved, setSaved] = useState(!!integrations.cli_anthropic?.key);
    const [copied, setCopied] = useState(null);

    function saveKey() {
      if (!apiKey.trim()) { showToast("Enter your Anthropic API key"); return; }
      setIntegrations(p => ({...p, cli_anthropic:{key:apiKey.trim()}}));
      setSaved(true); showToast("API key saved!");
    }

    function copyCmd(id, text) {
      navigator.clipboard?.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 1800);
    }

    const CLI_TOOLS = [
      {
        id:"claudecode",
        name:"Claude Code",
        icon:"🤖",
        color:"#cc785c",
        desc:"Anthropic's official agentic coding CLI. Works in your terminal with full file system access.",
        badge:"Official",
        badgeColor:"#cc785c",
        steps:[
          {label:"Install via npm", cmd:"npm install -g @anthropic-ai/claude-code"},
          {label:"Set API key", cmd:`export ANTHROPIC_API_KEY="${apiKey || "your-api-key-here"}"`},
          {label:"Run in your project", cmd:"claude"},
          {label:"Or start with a task", cmd:`claude "review this codebase for security issues"`},
        ],
        envVar:"ANTHROPIC_API_KEY",
        docs:"https://docs.anthropic.com/en/docs/claude-code",
        notes:[
          "Requires Node.js 18+",
          "Supports multi-file editing, git integration, and bash commands",
          "Use --dangerously-skip-permissions for automated pipelines",
          "Add to .env: ANTHROPIC_API_KEY=your-key",
        ]
      },
      {
        id:"opencode",
        name:"OpenCode CLI",
        icon:"⚡",
        color:"#6366f1",
        desc:"Open-source terminal AI coding assistant. Supports Claude, GPT-4o, local models, and more.",
        badge:"Open Source",
        badgeColor:"#6366f1",
        steps:[
          {label:"Install via npm", cmd:"npm install -g opencode-ai"},
          {label:"Or via Homebrew (macOS)", cmd:"brew install opencode"},
          {label:"Configure Claude as provider", cmd:`opencode config set provider anthropic`},
          {label:"Set API key", cmd:`opencode config set api_key "${apiKey || "your-api-key-here"}"`},
          {label:"Run", cmd:"opencode"},
        ],
        envVar:"ANTHROPIC_API_KEY",
        docs:"https://opencode.ai",
        notes:[
          "Supports Anthropic, OpenAI, Gemini, Ollama, and local models",
          "TUI-based — runs in your terminal with a full interface",
          "Config stored at ~/.config/opencode/config.json",
          "Works great with any Claude model including Opus and Haiku",
        ]
      },
      {
        id:"codeenv",
        name:".env Setup",
        icon:"🔐",
        color:"#16a34a",
        desc:"Add your ChatFort API key to any project's .env file for direct Claude API access.",
        badge:"Universal",
        badgeColor:"#16a34a",
        steps:[
          {label:"Create or edit .env in your project root", cmd:`ANTHROPIC_API_KEY="${apiKey || "your-api-key-here"}"`},
          {label:"Load in Node.js", cmd:`import 'dotenv/config'; // npm install dotenv`},
          {label:"Load in Python", cmd:`from dotenv import load_dotenv; load_dotenv()`},
          {label:"Load in shell", cmd:`source .env && echo $ANTHROPIC_API_KEY`},
        ],
        envVar:"ANTHROPIC_API_KEY",
        docs:"https://docs.anthropic.com/en/api/getting-started",
        notes:[
          "Always add .env to .gitignore to avoid leaking keys",
          "Use direnv for automatic per-project env loading",
          "Works with any Claude SDK: Python, TypeScript, Go, Rust",
          "Compatible with any tool that reads ANTHROPIC_API_KEY",
        ]
      },
    ];

    const tool = CLI_TOOLS.find(t => t.id === cliTab) || CLI_TOOLS[0];

    const cmdBlock = (id, cmd, label) => (
      <div key={id} style={{marginBottom:8}}>
        {label && <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4,letterSpacing:"0.03em"}}>{label}</div>}
        <div style={{display:"flex",alignItems:"center",gap:6,background:S.codeBlockBg,border:`1px solid ${S.border2}`,borderRadius:8,overflow:"hidden"}}>
          <pre style={{flex:1,padding:"9px 12px",margin:0,fontSize:12,color:S.text,fontFamily:"monospace",overflowX:"auto",whiteSpace:"pre"}}><code>{cmd}</code></pre>
          <button onClick={() => copyCmd(id, cmd)} style={{padding:"0 12px",height:"100%",minHeight:36,background:"none",border:"none",borderLeft:`1px solid ${S.border}`,color:copied===id?S.success:S.text3,cursor:"pointer",fontSize:10,fontFamily:"monospace",whiteSpace:"nowrap",flexShrink:0,transition:"color 0.15s"}}>
            {copied===id ? "✓ Copied" : "Copy"}
          </button>
        </div>
      </div>
    );

    return (
      <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
        {/* Tool tabs */}
        <div style={{display:"flex",borderBottom:`1px solid ${S.border}`,background:S.surface2}}>
          {CLI_TOOLS.map(t => (
            <button key={t.id} onClick={() => setCliTab(t.id)} style={{flex:1,padding:"11px 8px",background:"none",border:"none",borderBottom:cliTab===t.id?`2px solid ${t.color}`:"2px solid transparent",color:cliTab===t.id?t.color:S.text3,cursor:"pointer",fontSize:12,fontWeight:cliTab===t.id?700:400,transition:"all 0.15s",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
              <span style={{fontSize:15}}>{t.icon}</span>
              <span style={{display:"none"}}></span>
              <span>{t.name}</span>
            </button>
          ))}
        </div>

        <div style={{padding:18}}>
          {/* Tool header */}
          <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:16}}>
            <div style={{width:44,height:44,borderRadius:12,background:`${tool.color}18`,border:`1px solid ${tool.color}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>{tool.icon}</div>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:3}}>
                <span style={{fontSize:15,fontWeight:700,color:S.text}}>{tool.name}</span>
                <span style={{fontSize:9,padding:"2px 7px",borderRadius:10,fontFamily:"monospace",fontWeight:700,background:`${tool.badgeColor}18`,color:tool.badgeColor,border:`1px solid ${tool.badgeColor}30`}}>{tool.badge}</span>
              </div>
              <div style={{fontSize:12,color:S.text2,lineHeight:1.55}}>{tool.desc}</div>
            </div>
            <a href={tool.docs} target="_blank" rel="noreferrer" style={{padding:"5px 11px",borderRadius:7,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text3,fontSize:11,textDecoration:"none",fontFamily:"monospace",whiteSpace:"nowrap",flexShrink:0}}>Docs ↗</a>
          </div>

          {/* API Key row */}
          <div style={{background:`${S.accent}08`,border:`1px solid ${S.border2}`,borderRadius:10,padding:14,marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:600,color:S.text2,marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontSize:14}}>🔑</span> Your Anthropic API Key
              {saved && <span style={{fontSize:9,padding:"2px 7px",borderRadius:10,fontFamily:"monospace",background:`${S.success}18`,color:S.success,border:`1px solid ${S.success}30`}}>Saved</span>}
            </div>
            <div style={{display:"flex",gap:7}}>
              <input type="password" value={apiKey} onChange={e => {setApiKey(e.target.value); setSaved(false);}} placeholder="sk-ant-api03-…" style={{...ISt(),flex:1,fontFamily:"monospace",fontSize:12}}/>
              <button onClick={saveKey} style={{padding:"8px 14px",borderRadius:8,background:S.grad2,border:"none",color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>Save Key</button>
            </div>
            <div style={{fontSize:10,color:S.text3,marginTop:7,fontFamily:"monospace"}}>
              Get your key at{" "}
              <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer" style={{color:S.accent,textDecoration:"none"}}>console.anthropic.com/settings/keys</a>
              {" "}· Stored locally, never transmitted to ChatFort servers
            </div>
          </div>

          {/* Install steps */}
          <div style={{fontSize:11,fontWeight:600,color:S.text2,marginBottom:10,letterSpacing:"0.02em"}}>Quick Setup</div>
          {tool.steps.map((s,i) => cmdBlock(`${tool.id}-${i}`, s.cmd, `${i+1}. ${s.label}`))}

          {/* Notes */}
          <div style={{background:S.surface2,border:`1px solid ${S.border}`,borderRadius:8,padding:"10px 13px",marginTop:12}}>
            <div style={{fontSize:10,fontWeight:700,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:7}}>Notes</div>
            {tool.notes.map((n,i) => (
              <div key={i} style={{display:"flex",gap:6,marginBottom:4,fontSize:11,color:S.text2,lineHeight:1.5}}>
                <span style={{color:S.accent,flexShrink:0,marginTop:1}}>·</span>
                <span>{n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }


  function MCPPage({mcpServers, setMcpServers, showToast}) {
    const [form, setForm] = useState({name:"", url:"", transport:"http", apiKey:"", headers:"", description:""});
    const [editing, setEditing] = useState(null);
    const [testing, setTesting] = useState(null);
    const [testResults, setTestResults] = useState({});
    const [expanded, setExpanded] = useState(null);
    const [activeTools, setActiveTools] = useState({});
    const [copied, setCopied] = useState(null);

    const PRESETS = [
      {name:"Filesystem", icon:"📁", color:"#f59e0b", description:"Read/write local files via MCP filesystem server", transport:"stdio", url:"npx -y @modelcontextprotocol/server-filesystem /path/to/dir", badge:"Official"},
      {name:"GitHub", icon:"🐙", color:"#6366f1", description:"Search repos, read files, manage issues and PRs", transport:"http", url:"https://api.githubcopilot.com/mcp/", apiKey:"ghp_...", badge:"Official"},
      {name:"Brave Search", icon:"🦁", color:"#fb923c", description:"Real-time web search and news via Brave API", transport:"http", url:"https://search.brave.com/mcp", apiKey:"BSA...", badge:"Official"},
      {name:"Postgres", icon:"🐘", color:"#336791", description:"Query and manage PostgreSQL databases", transport:"stdio", url:"npx -y @modelcontextprotocol/server-postgres postgresql://user:pass@localhost/db", badge:"Official"},
      {name:"Slack", icon:"💬", color:"#4a154b", description:"Send messages, read channels, manage workspace", transport:"http", url:"https://mcp.slack.com/", apiKey:"xoxb-...", badge:"Official"},
      {name:"Puppeteer", icon:"🤖", color:"#00d084", description:"Browser automation, screenshots, web scraping", transport:"stdio", url:"npx -y @modelcontextprotocol/server-puppeteer", badge:"Official"},
      {name:"Custom HTTP", icon:"🔌", color:"#5ba3c9", description:"Connect any MCP-compatible HTTP server", transport:"http", url:"https://your-mcp-server.com/mcp", badge:"Custom"},
      {name:"Custom stdio", icon:"⚡", color:"#8b5cf6", description:"Connect any local MCP-compatible stdio process", transport:"stdio", url:"node /path/to/your-server.js", badge:"Custom"},
    ];

    function applyPreset(p) {
      setForm({name:p.name, url:p.url, transport:p.transport, apiKey:p.apiKey||"", headers:"", description:p.description});
    }

    // fix/mcp-server-proxy: ALL MCP transport routes through the server
    // now. Browser never speaks MCP directly. The server reads the org's
    // shared-mcp.json, makes the JSON-RPC call from there, and surfaces
    // a normalized {status, tools, error} response. Stdio servers come
    // back with status="stdio_unsupported_hosted" -- the UI surfaces an
    // explanatory pill + disables the Reconnect button via the lookup
    // table at the row render below.
    async function testConnection(srv) {
      setTesting(srv.id);
      setTestResults(prev => ({...prev, [srv.id]: {status:"testing", msg:"Connecting…"}}));
      try {
        const r = await fetch("/api/mcp/" + encodeURIComponent(srv.id) + "/connect", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": "Bearer " + (localStorage.getItem("cf_auth_token") || "") },
        });
        let data = null;
        try { data = await r.json(); } catch {}
        if (!r.ok) {
          const msg = (data && (data.reason || data.error)) || `HTTP ${r.status}`;
          throw new Error(msg);
        }
        const status = data && data.status;
        const tools  = (data && Array.isArray(data.tools)) ? data.tools : [];
        if (status === "stdio_unsupported_hosted") {
          // Stdio MCP servers can't run in the hosted PWA context. Mark
          // the row with a warn (not an error -- the config itself is
          // valid; it just can't run here) and surface the reason as a
          // tooltip via the testResults map.
          setMcpServers(prev => prev.map(s => s.id===srv.id ? {...s, status:"stdio_unsupported_hosted", tools:[]} : s));
          setTestResults(prev => ({...prev, [srv.id]: {status:"warn", msg: (data && data.error) || "Unavailable in hosted mode -- use this server in Claude Desktop or Hermes Co-work via the exported config."}}));
          return;
        }
        if (status === "connected") {
          setMcpServers(prev => prev.map(s => s.id===srv.id ? {...s, status:"connected", tools} : s));
          setTestResults(prev => ({...prev, [srv.id]: {status:"ok", msg: `Connected · ${tools.length} tool${tools.length!==1?"s":""} available`}}));
          if (tools.length) setExpanded(srv.id);
          return;
        }
        // status === "error" or anything unexpected
        setMcpServers(prev => prev.map(s => s.id===srv.id ? {...s, status:"error"} : s));
        setTestResults(prev => ({...prev, [srv.id]: {status:"error", msg: (data && data.error) || "MCP connection failed"}}));
      } catch(err) {
        setTestResults(prev => ({...prev, [srv.id]: {status:"error", msg: err.message}}));
        setMcpServers(prev => prev.map(s => s.id===srv.id ? {...s, status:"error"} : s));
      }
      setTesting(null);
    }

    function saveServer() {
      if (!form.name.trim()) { showToast("Name required"); return; }
      if (!form.url.trim()) { showToast("URL / command required"); return; }
      if (editing) {
        setMcpServers(prev => prev.map(s => s.id===editing ? {...s, ...form, status:"unknown", tools:undefined} : s));
        setEditing(null); showToast("Server updated");
      } else {
        const srv = {id:"mcp-"+Date.now().toString(36), ...form, status:"unknown", enabled:true, created:Date.now()};
        setMcpServers(prev => [...prev, srv]);
        showToast("MCP server added");
      }
      setForm({name:"", url:"", transport:"http", apiKey:"", headers:"", description:""});
    }

    function editServer(srv) {
      setEditing(srv.id);
      setForm({name:srv.name, url:srv.url, transport:srv.transport||"http", apiKey:srv.apiKey||"", headers:srv.headers||"", description:srv.description||""});
    }

    function removeServer(id) {
      if (!window.confirm("Remove this MCP server?")) return;
      setMcpServers(prev => prev.filter(s => s.id!==id));
      setTestResults(prev => { const n={...prev}; delete n[id]; return n; });
      showToast("Server removed");
    }

    function toggleEnabled(id) {
      setMcpServers(prev => prev.map(s => s.id===id ? {...s, enabled:!s.enabled} : s));
    }

    function copyConfig() {
      const cfg = {mcpServers: Object.fromEntries(mcpServers.map(s => [s.name.toLowerCase().replace(/\s+/g,"-"), s.transport==="stdio" ? {command:s.url.split(" ")[0], args:s.url.split(" ").slice(1)} : {url:s.url, headers:s.apiKey?{Authorization:`Bearer ${s.apiKey}`}:{}}]))};
      navigator.clipboard?.writeText(JSON.stringify(cfg, null, 2));
      setCopied("cfg"); setTimeout(()=>setCopied(null), 1800);
      showToast("Copied claude_desktop_config.json format");
    }

    const connected = mcpServers.filter(s => s.status==="connected");
    const totalTools = connected.reduce((a,s) => a+(s.tools?.length||0), 0);

    // fix/mcp-server-proxy: render the stdio_unsupported_hosted status
    // as a muted warn-style dot (amber) so users can see at a glance
    // that the row isn't broken -- it's intentionally inactive in the
    // hosted deployment.
    const statusDot = (s) => {
      const isStdioHosted = s.status === "stdio_unsupported_hosted";
      const c = s.status==="connected" ? S.success
              : s.status==="error" ? S.danger
              : (s.status==="warn" || isStdioHosted) ? "#f59e0b"
              : S.text3;
      return <div style={{width:7,height:7,borderRadius:"50%",background:c,flexShrink:0,boxShadow:s.status==="connected"?`0 0 6px ${c}`:undefined,opacity:isStdioHosted?0.65:1}}/>;
    };

    const inp2 = () => ({...ISt(), marginBottom:0});

    return (
      <div style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
        <div style={{maxWidth:780,margin:"0 auto"}}>

          {/* Header */}
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:22}}>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                <h2 style={{fontSize:20,fontWeight:800,color:S.text}}>MCP Servers</h2>
                <span style={{fontSize:9,padding:"2px 8px",borderRadius:10,fontFamily:"monospace",fontWeight:700,background:`${S.accent}18`,color:S.accent,border:`1px solid ${S.accent}30`}}>Model Context Protocol</span>
              </div>
              <p style={{fontSize:13,color:S.text2}}>Connect tools, databases, and APIs to Claude via MCP.</p>
            </div>
            <div style={{display:"flex",gap:7,alignItems:"center"}}>
              {mcpServers.length>0 && <button onClick={copyConfig} style={{padding:"7px 12px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text3,fontSize:11,fontFamily:"monospace",cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>{IC.copy} {copied==="cfg"?"Copied!":"Export config"}</button>}
            </div>
          </div>

          {/* Stats bar */}
          {mcpServers.length>0 && (
            <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
              {[
                ["Servers", mcpServers.length, S.text],
                ["Connected", connected.length, S.success],
                ["Tools available", totalTools, S.accent],
                ["Enabled", mcpServers.filter(s=>s.enabled!==false).length, S.text2],
              ].map(([label,val,color])=>(
                <div key={label} style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:12,padding:"12px 16px",flex:1,minWidth:110}}>
                  <div style={{fontSize:20,fontWeight:700,color,fontFamily:"monospace",marginBottom:2}}>{val}</div>
                  <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Add / Edit Form */}
          <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,marginBottom:20,overflow:"hidden"}}>
            <div style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{fontSize:13,fontWeight:700,color:S.text}}>{editing?"Edit Server":"Add MCP Server"}</div>
              {editing && <button onClick={()=>{setEditing(null);setForm({name:"",url:"",transport:"http",apiKey:"",headers:"",description:""}); }} style={{background:"none",border:"none",color:S.text3,cursor:"pointer",fontSize:12,fontFamily:"monospace"}}>Cancel edit</button>}
            </div>
            <div style={{padding:18}}>
              {/* Transport tabs */}
              <div style={{display:"flex",gap:6,marginBottom:16}}>
                {[["http","🌐 HTTP / SSE"],["stdio","⚡ stdio (local)"]].map(([t,label])=>(
                  <button key={t} onClick={()=>setForm(f=>({...f,transport:t}))} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${form.transport===t?S.accent:S.border2}`,background:form.transport===t?S.accentDim:S.inputBg,color:form.transport===t?S.accent:S.text2,fontSize:12,fontWeight:form.transport===t?600:400,cursor:"pointer",transition:"all 0.15s"}}>{label}</button>
                ))}
              </div>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                <div>
                  <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.05em"}}>Server Name *</div>
                  <input style={inp2()} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. My Filesystem"/>
                </div>
                <div>
                  <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.05em"}}>Description</div>
                  <input style={inp2()} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="What does this server do?"/>
                </div>
              </div>

              <div style={{marginBottom:10}}>
                <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.05em"}}>{form.transport==="stdio"?"Command *":"URL *"}</div>
                <input style={{...inp2(),fontFamily:"monospace",fontSize:12}} value={form.url} onChange={e=>setForm(f=>({...f,url:e.target.value}))} placeholder={form.transport==="stdio"?"npx -y @modelcontextprotocol/server-filesystem /path":"https://your-mcp-server.com/mcp"}/>
              </div>

              {form.transport==="http" && (
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                  <div>
                    <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.05em"}}>API Key (optional)</div>
                    <input type="password" style={{...inp2(),fontFamily:"monospace",fontSize:12}} value={form.apiKey} onChange={e=>setForm(f=>({...f,apiKey:e.target.value}))} placeholder="Bearer token or API key"/>
                  </div>
                  <div>
                    <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.05em"}}>Extra Headers (JSON)</div>
                    <input style={{...inp2(),fontFamily:"monospace",fontSize:12}} value={form.headers} onChange={e=>setForm(f=>({...f,headers:e.target.value}))} placeholder='{"X-Custom":"value"}'/>
                  </div>
                </div>
              )}

              {form.transport==="stdio" && (
                <div style={{background:`${S.accent}08`,border:`1px solid ${S.border2}`,borderRadius:8,padding:"10px 13px",marginBottom:10,fontSize:11,color:S.text2,lineHeight:1.6}}>
                  <strong style={{color:S.accent}}>stdio servers</strong> run as local processes. ChatFort generates the config for Claude Desktop / Claude Code. Connection testing is not available in browser — validate your command locally.
                </div>
              )}

              <div style={{display:"flex",gap:8,marginTop:6}}>
                <button onClick={saveServer} style={{padding:"9px 18px",borderRadius:9,background:S.grad2,border:"none",color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer"}}>{editing?"Save Changes":"Add Server"}</button>
                {!editing && form.name && <button onClick={()=>setForm({name:"",url:"",transport:"http",apiKey:"",headers:"",description:""})} style={{padding:"9px 14px",borderRadius:9,background:"none",border:`1px solid ${S.border2}`,color:S.text3,fontSize:13,cursor:"pointer"}}>Clear</button>}
              </div>
            </div>
          </div>

          {/* Preset Templates */}
          <div style={{marginBottom:20}}>
            <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",color:S.text3,marginBottom:10,fontFamily:"monospace",letterSpacing:"0.08em"}}>Quick Templates</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(175px,1fr))",gap:8}}>
              {PRESETS.map(p=>(
                <div key={p.name} onClick={()=>applyPreset(p)} style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:11,padding:"12px 13px",cursor:"pointer",transition:"border-color 0.15s,transform 0.15s"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=p.color;e.currentTarget.style.transform="translateY(-1px)";}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=S.border2;e.currentTarget.style.transform="none";}}>
                  <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:6}}>
                    <span style={{fontSize:18}}>{p.icon}</span>
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:S.text,lineHeight:1.2}}>{p.name}</div>
                      <span style={{fontSize:8,padding:"1px 5px",borderRadius:3,fontFamily:"monospace",fontWeight:600,background:`${p.color}18`,color:p.color}}>{p.badge}</span>
                    </div>
                  </div>
                  <div style={{fontSize:10,color:S.text3,lineHeight:1.5}}>{p.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Server List */}
          {mcpServers.length===0 ? (
            <div style={{textAlign:"center",padding:"48px 32px",color:S.text3,border:`1px dashed ${S.border2}`,borderRadius:14}}>
              <div style={{fontSize:36,marginBottom:12}}>🔌</div>
              <div style={{fontSize:14,fontWeight:600,color:S.text2,marginBottom:6}}>No MCP servers yet</div>
              <div style={{fontSize:12,lineHeight:1.6}}>Add a server above or click a template to get started.<br/>MCP lets Claude use tools, read files, query databases, and more.</div>
            </div>
          ) : (
            <div>
              <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",color:S.text3,marginBottom:10,fontFamily:"monospace",letterSpacing:"0.08em"}}>Your Servers</div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {mcpServers.map(srv=>{
                  const tr=testResults[srv.id];
                  const isExpanded=expanded===srv.id;
                  const enabled=srv.enabled!==false;
                  return (
                    <div key={srv.id} style={{background:S.cardBg,border:`1px solid ${srv.status==="connected"?S.success+"55":srv.status==="error"?S.danger+"44":S.border2}`,borderLeft:`3px solid ${srv.status==="connected"?S.success:srv.status==="error"?S.danger:S.border2}`,borderRadius:12,overflow:"hidden",opacity:enabled?1:0.6,transition:"opacity 0.2s"}}>
                      {/* Server header row */}
                      <div style={{padding:"13px 16px",display:"flex",alignItems:"center",gap:12}}>
                        <div style={{width:36,height:36,borderRadius:10,background:`${S.accent}15`,border:`1px solid ${S.border2}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
                          {srv.name.includes("GitHub")?"🐙":srv.name.includes("File")?"📁":srv.name.includes("Slack")?"💬":srv.name.includes("Postgres")||srv.name.includes("DB")?"🐘":srv.name.includes("Search")||srv.name.includes("Brave")?"🦁":srv.name.includes("Puppet")?"🤖":"🔌"}
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:2}}>
                            {statusDot(srv)}
                            <span style={{fontSize:13,fontWeight:700,color:S.text}}>{srv.name}</span>
                            <span style={{fontSize:9,padding:"1px 6px",borderRadius:4,fontFamily:"monospace",background:srv.transport==="stdio"?`${S.accent}15`:S.surface2,color:srv.transport==="stdio"?S.accent:S.text3,border:`1px solid ${S.border}`}}>{srv.transport}</span>
                            {srv.tools?.length>0 && <span style={{fontSize:9,padding:"1px 6px",borderRadius:4,fontFamily:"monospace",background:`${S.success}15`,color:S.success}}>{srv.tools.length} tools</span>}
                          </div>
                          <div style={{fontSize:11,color:S.text3,fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{srv.url}</div>
                          {srv.description && <div style={{fontSize:11,color:S.text2,marginTop:2}}>{srv.description}</div>}
                        </div>
                        {/* Actions */}
                        <div style={{display:"flex",gap:5,alignItems:"center",flexShrink:0}}>
                          {/* Enable toggle */}
                          <div onClick={()=>toggleEnabled(srv.id)} style={{width:32,height:18,background:enabled?S.success:S.surface3,borderRadius:9,position:"relative",cursor:"pointer",border:`1px solid ${S.border2}`,transition:"background 0.2s",flexShrink:0}}>
                            <div style={{position:"absolute",top:2,left:enabled?14:2,width:12,height:12,background:"#fff",borderRadius:"50%",transition:"left 0.18s"}}/>
                          </div>
                          {/* fix/mcp-server-proxy: disable Reconnect for stdio
                              servers in the hosted PWA -- they have no
                              way to run here. Tooltip explains why. */}
                          <button onClick={()=>testConnection(srv)}
                                  disabled={testing===srv.id || srv.transport==="stdio" || srv.status==="stdio_unsupported_hosted"}
                                  title={(srv.transport==="stdio" || srv.status==="stdio_unsupported_hosted") ? "Stdio MCP servers can't run in the hosted ChatFortAI deployment. Use this server in Claude Desktop or Hermes Co-work via the exported config." : "Reconnect"}
                                  style={{width:30,height:30,borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text2,cursor:(srv.transport==="stdio" || srv.status==="stdio_unsupported_hosted")?"not-allowed":"pointer",opacity:(srv.transport==="stdio" || srv.status==="stdio_unsupported_hosted")?0.45:1,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11}}>
                            {testing===srv.id ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{animation:"spin 1s linear infinite"}}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-.49-4.95"/></svg> : "⟳"}
                          </button>
                          <button onClick={()=>editServer(srv)} title="Edit" style={{width:30,height:30,borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text3,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>{IC.edit}</button>
                          <button onClick={()=>removeServer(srv.id)} title="Remove" style={{width:30,height:30,borderRadius:8,background:`${S.danger}0d`,border:`1px solid ${S.danger}30`,color:S.danger,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>{IC.trash}</button>
                          {srv.tools?.length>0 && <button onClick={()=>setExpanded(isExpanded?null:srv.id)} style={{width:30,height:30,borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text3,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,transform:isExpanded?"rotate(180deg)":"none",transition:"transform 0.2s"}}>▾</button>}
                        </div>
                      </div>

                      {/* Test result banner */}
                      {tr && (
                        <div style={{padding:"7px 16px",borderTop:`1px solid ${S.border}`,background:tr.status==="ok"?`${S.success}0d`:tr.status==="error"?`${S.danger}0d`:tr.status==="warn"?`${S.accent}0d`:S.surface2,fontSize:11,color:tr.status==="ok"?S.success:tr.status==="error"?S.danger:S.text2,fontFamily:"monospace",display:"flex",alignItems:"center",gap:7}}>
                          <span>{tr.status==="ok"?"✓":tr.status==="error"?"✗":tr.status==="warn"?"⚠":"⋯"}</span>
                          <span>{tr.msg}</span>
                        </div>
                      )}

                      {/* Tools list */}
                      {isExpanded && srv.tools && srv.tools.length>0 && (
                        <div style={{borderTop:`1px solid ${S.border}`,padding:"12px 16px"}}>
                          <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",color:S.text3,fontFamily:"monospace",letterSpacing:"0.07em",marginBottom:10}}>Available Tools</div>
                          <div style={{display:"flex",flexDirection:"column",gap:6}}>
                            {srv.tools.map(tool=>{
                              const key = `${srv.id}:${tool.name}`;
                              const toolActive = activeTools[key];
                              return (
                                <div key={tool.name} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"9px 12px",borderRadius:8,background:toolActive?S.accentDim:S.surface2,border:`1px solid ${toolActive?S.accent:S.border}`,transition:"all 0.15s"}}>
                                  <div style={{width:26,height:26,borderRadius:7,background:`${S.accent}18`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,flexShrink:0}}>{IC.terminal}</div>
                                  <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontSize:12,fontWeight:600,color:S.text,fontFamily:"monospace",marginBottom:2}}>{tool.name}</div>
                                    {tool.description && <div style={{fontSize:11,color:S.text2,lineHeight:1.5}}>{tool.description}</div>}
                                    {tool.inputSchema?.properties && (
                                      <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:5}}>
                                        {Object.entries(tool.inputSchema.properties).map(([param,schema])=>(
                                          <span key={param} style={{fontSize:9,padding:"1px 6px",borderRadius:3,fontFamily:"monospace",background:S.surface3,color:S.text3,border:`1px solid ${S.border}`}}>{param}{tool.inputSchema.required?.includes(param)?"*":""}</span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div onClick={()=>setActiveTools(prev=>({...prev,[key]:!toolActive}))} style={{width:32,height:18,background:toolActive?S.success:S.surface3,borderRadius:9,position:"relative",cursor:"pointer",border:`1px solid ${S.border2}`,transition:"background 0.2s",flexShrink:0,marginTop:3}}>
                                    <div style={{position:"absolute",top:2,left:toolActive?14:2,width:12,height:12,background:"#fff",borderRadius:"50%",transition:"left 0.18s"}}/>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Config export section */}
          {mcpServers.length>0 && (
            <div style={{marginTop:24,background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
              <div style={{padding:"13px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>Claude Desktop & Claude Code Config</div>
              <div style={{padding:16}}>
                <div style={{fontSize:11,color:S.text2,marginBottom:12,lineHeight:1.6}}>
                  Paste this into your <code style={{fontFamily:"monospace",background:S.surface2,padding:"1px 5px",borderRadius:3}}>claude_desktop_config.json</code> or <code style={{fontFamily:"monospace",background:S.surface2,padding:"1px 5px",borderRadius:3}}>~/.claude/settings.json</code> to use these servers in Claude Code.
                </div>
                <div style={{position:"relative"}}>
                  <pre style={{background:S.codeBlockBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"12px 14px",fontSize:11,color:S.text,fontFamily:"monospace",overflowX:"auto",margin:0,lineHeight:1.6}}>
                    <code>{JSON.stringify({mcpServers: Object.fromEntries(mcpServers.map(s=>[s.name.toLowerCase().replace(/\s+/g,"-"), s.transport==="stdio"?{command:s.url.split(" ")[0],args:s.url.split(" ").slice(1)}:{url:s.url,headers:s.apiKey?{Authorization:`Bearer ${s.apiKey}`}:{}}]))}, null, 2)}</code>
                  </pre>
                  <button onClick={copyConfig} style={{position:"absolute",top:8,right:8,padding:"4px 10px",borderRadius:6,background:S.inputBg,border:`1px solid ${S.border2}`,color:copied==="cfg"?S.success:S.text3,fontSize:10,fontFamily:"monospace",cursor:"pointer"}}>{copied==="cfg"?"✓ Copied":"Copy"}</button>
                </div>
              </div>
            </div>
          )}

          {/* Help */}
          <div style={{marginTop:18,background:`${S.accent}08`,border:`1px solid ${S.border2}`,borderRadius:12,padding:"14px 18px"}}>
            <div style={{fontSize:11,fontWeight:700,color:S.accent,marginBottom:8}}>What is MCP?</div>
            <div style={{fontSize:11,color:S.text2,lineHeight:1.8}}>
              <strong>Model Context Protocol</strong> is an open standard that lets Claude connect to external tools and data sources. Once connected, Claude can read files, query databases, search the web, run code, and more — all securely through your configuration.{" "}
              <a href="https://modelcontextprotocol.io" target="_blank" rel="noreferrer" style={{color:S.accent,textDecoration:"none"}}>Learn more ↗</a>
            </div>
          </div>

        </div>
      </div>
    );
  }



  function WfWebhookUrlPanel({ id, showToast, authToken }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);
    const [rotating, setRotating] = useState(false);
    const reload = useCallback(async () => {
      setLoading(true); setError(null);
      try {
        const r = await fetch("/api/workflows/" + id + "/webhook-url", { headers: { "Authorization": "Bearer " + authToken } });
        const d = await r.json().catch(() => ({}));
        if (r.ok) setData(d); else setError(d.error || ("HTTP " + r.status));
      } catch (e) { setError(e.message); }
      setLoading(false);
    }, [id, authToken]);
    useEffect(() => { reload(); }, [reload]);
    async function copy() {
      if (!data || !data.url) return;
      try { await navigator.clipboard.writeText(data.url); setCopied(true); setTimeout(()=>setCopied(false), 1500); showToast("Webhook URL copied"); }
      catch { showToast("Copy failed"); }
    }
    async function rotate() {
      if (rotating) return;
      if (!window.confirm("Rotate the webhook token? The current URL will stop working immediately.")) return;
      setRotating(true);
      try {
        const r = await fetch("/api/workflows/" + id + "/rotate-webhook-token", { method: "POST", headers: { "Authorization": "Bearer " + authToken } });
        if (r.ok) { showToast("Token rotated — new URL below"); await reload(); }
        else { const d = await r.json().catch(() => ({})); showToast(d.error || "Rotate failed"); }
      } catch (e) { showToast("Rotate failed: " + e.message); }
      setRotating(false);
    }
    const [testing, setTesting] = useState(false);
    async function test() {
      if (testing || !data || !data.url) return;
      setTesting(true);
      try {
        // Phase 4: POST a sample payload to the user's OWN webhook
        // URL. This DOES fire the workflow (per design -- it's the
        // same path an external caller would hit) so the user sees
        // a real run in Runs history.
        const sample = { source: "chatfort-test", at: new Date().toISOString(), message: "Sample webhook test payload" };
        const r = await fetch(data.url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(sample) });
        if (r.ok) showToast("✓ Test fired (HTTP " + r.status + ") — check Runs");
        else showToast("Test returned HTTP " + r.status);
      } catch (e) { showToast("Test failed: " + e.message); }
      setTesting(false);
    }
    if (loading) return <div style={{fontSize:11,color:S.text3,fontFamily:"monospace"}}>Loading webhook URL...</div>;
    if (error) return <div style={{fontSize:11,color:S.danger,fontFamily:"monospace",padding:"8px 10px",borderRadius:7,background:S.danger+"12",border:`1px solid ${S.danger}40`}}>Error: {error}</div>;
    if (!data || !data.url) return null;
    return (
      <div style={{display:"flex",flexDirection:"column",gap:6}}>
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <code style={{flex:"1 1 240px",padding:"7px 10px",borderRadius:7,border:`1px solid ${S.border2}`,background:S.codeBlockBg,color:S.text2,fontSize:11,fontFamily:"monospace",overflow:"auto",whiteSpace:"nowrap"}}>{data.url}</code>
          <button onClick={copy} style={{padding:"6px 12px",borderRadius:7,border:`1px solid ${copied?S.success:S.border2}`,background:copied?S.success+"22":S.inputBg,color:copied?S.success:S.text2,fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600,whiteSpace:"nowrap"}}>{copied?"✓ Copied":"📋 Copy"}</button>
          <button onClick={rotate} disabled={rotating} style={{padding:"6px 12px",borderRadius:7,border:`1px solid ${S.border2}`,background:S.inputBg,color:rotating?S.text3:S.text2,fontSize:11,cursor:rotating?"default":"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>{rotating?"...":"⟳ Rotate"}</button>
          <button onClick={test} disabled={testing} style={{padding:"6px 12px",borderRadius:7,border:`1px solid ${S.accent}55`,background:S.accentDim,color:testing?S.text3:S.accent,fontSize:11,cursor:testing?"default":"pointer",fontFamily:"inherit",fontWeight:600,whiteSpace:"nowrap"}}>{testing?"...":"⚡ Test"}</button>
        </div>
        <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",lineHeight:1.55}}>
          Send {data.method} requests with {data.body} body. The body is exposed to trigger.webhook nodes as the run payload.
        </div>
      </div>
    );
  }

  function WorkflowsPage({showToast}) {
    // Default to the built-in builder so users land on something
    // functional, not the external-platforms docs.
    const [tab, setTab] = useState("builtin");
    const [activePlat, setActivePlat] = useState("n8n");
    const [copied, setCopied] = useState(null);

    // Built-in builder state (Phase 2b).
    const [wfItems, setWfItems] = useState([]);
    const [wfLoading, setWfLoading] = useState(false);
    const [wfEditing, setWfEditing] = useState(null); // { id?, name, description, definition, trigger_type, enabled } | null
    const [wfDefStr, setWfDefStr] = useState("");
    const [wfRunsFor, setWfRunsFor] = useState(null); // workflow id whose run list is open
    const [wfRuns, setWfRuns] = useState([]);
    const [wfRunDetail, setWfRunDetail] = useState(null); // full run object
    // Phase 4: templates browse + cancel/retry/live-polling state.
    const [wfTemplatesOpen, setWfTemplatesOpen] = useState(false);
    const [wfTemplates, setWfTemplates] = useState([]);
    const [wfTemplatesLoading, setWfTemplatesLoading] = useState(false);
    const [wfActionBusy, setWfActionBusy] = useState(false); // Cancel/Retry in-flight guard
    const [wfShowEditor, setWfShowEditor] = useState(false);

    function copy(id, text) {
      navigator.clipboard?.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 1800);
    }

    // ── Workflow CRUD + run helpers (Phase 2b) ─────────────────────────────
    const wfAuthHdrs = () => ({ "Authorization": "Bearer " + authToken, "Content-Type": "application/json" });
    async function wfReload() {
      if (!authToken || !serverMode) return;
      setWfLoading(true);
      try {
        const r = await fetch("/api/workflows", { headers: { "Authorization": "Bearer " + authToken } });
        if (r.ok) setWfItems(await r.json());
      } catch {}
      setWfLoading(false);
    }
    useEffect(() => { if (tab === "builtin") wfReload(); /* eslint-disable-next-line */ }, [authToken, serverMode, tab]);

    function wfStartNew() {
      setWfEditing({ id: null, name: "New Workflow", description: "", trigger_type: "manual", trigger_config: {}, enabled: true, tags: [] });
      setWfDefStr(JSON.stringify({ nodes: [], edges: [] }, null, 2));
    }
    function wfStartEdit(w) {
      setWfEditing({ id: w.id, name: w.name, description: w.description || "", trigger_type: w.trigger_type || "manual", trigger_config: (w.trigger_config && typeof w.trigger_config === "object") ? w.trigger_config : {}, enabled: w.enabled !== false, tags: Array.isArray(w.tags) ? w.tags : [] });
      setWfDefStr(JSON.stringify(w.definition || { nodes: [], edges: [] }, null, 2));
    }
    // defOverride: the visual editor passes its definition directly —
    // setWfDefStr hasn't flushed yet within the same tick, so reading the
    // textarea state here would save the OLD definition.
    async function wfSave(defOverride) {
      if (!wfEditing) return;
      let def;
      if (defOverride && typeof defOverride === "object") def = defOverride;
      else {
        try { def = JSON.parse(wfDefStr); }
        catch (e) { showToast("Invalid JSON: " + e.message); return; }
      }
      if (!def || typeof def !== "object" || !Array.isArray(def.nodes) || !Array.isArray(def.edges)) {
        showToast("Definition must have nodes:[] and edges:[]"); return;
      }
      const payload = { name: wfEditing.name, description: wfEditing.description, definition: def, trigger_type: wfEditing.trigger_type, trigger_config: wfEditing.trigger_config || {}, enabled: wfEditing.enabled, tags: Array.isArray(wfEditing.tags) ? wfEditing.tags : [] };
      try {
        const url = wfEditing.id ? "/api/workflows/" + wfEditing.id : "/api/workflows";
        const method = wfEditing.id ? "PUT" : "POST";
        const r = await fetch(url, { method, headers: wfAuthHdrs(), body: JSON.stringify(payload) });
        if (r.ok) { showToast(wfEditing.id ? "Workflow saved" : "Workflow created"); setWfEditing(null); wfReload(); }
        else { const d = await r.json().catch(() => ({})); showToast(d.error || "Save failed"); }
      } catch (e) { showToast("Save failed: " + e.message); }
    }
    async function wfRun(id) {
      showToast("Running workflow...");
      try {
        const r = await fetch("/api/workflows/" + id + "/run", { method: "POST", headers: { "Authorization": "Bearer " + authToken } });
        const d = await r.json().catch(() => ({}));
        // Diagnostic probe (also catchable from DevTools as window.__cfLastWfRun).
        try { window.__cfLastWfRun = { ok: r.ok, status: r.status, body: d, at: new Date().toISOString() }; } catch {}
        try { console.log("[wfRun] resolved", { ok: r.ok, status: r.status, runId: d && d.runId, runStatus: d && d.status }); } catch {}
        if (r.ok) {
          // Build the detail object FIRST and open the modal BEFORE the
          // staggered toast loop. Two reasons: (a) the user sees the
          // run-detail UI immediately, (b) any future refactor that
          // awaits inside the toast loop can't accidentally delay the
          // modal. setTimeout(0) backup re-asserts the state on the
          // next tick to defeat any batching anomaly.
          const detail = {
            id: d.runId,
            status: d.status,
            latency_ms: d.latencyMs,
            trigger: "manual",
            error: d.error,
            log: d.log,
            outputs: d.outputs,
          };
          try { console.log("[wfRun] auto-open setWfRunDetail", detail.id); } catch {}
          setWfRunDetail(detail);
          setTimeout(() => setWfRunDetail(detail), 0);
          showToast("✓ Opened Run #" + (d.runId == null ? "?" : d.runId));

          // fix/workflow-output-toast-rendering: surface output.toast node
          // payloads as separate cf-toasts in execution order, then the
          // run-summary. 2400ms stagger.
          const nodeToasts = Array.isArray(d.toasts) ? d.toasts : [];
          let delay = 0;
          for (const t of nodeToasts) {
            const raw = (t && typeof t.message === "string") ? t.message : "";
            if (!raw) continue;
            const display = raw.length > 200 ? raw.slice(0, 197) + "..." : raw;
            setTimeout(() => showToast(display), delay);
            delay += 2400;
          }
          setTimeout(() => showToast("Run " + d.status + " in " + d.latencyMs + "ms"), delay);
        } else {
          try { console.warn("[wfRun] non-ok response", r.status, d); } catch {}
          showToast(d.error || ("Run failed (HTTP " + r.status + ")"));
        }
        wfReload();
      } catch (e) {
        try { console.error("[wfRun] threw", e); } catch {}
        showToast("Run failed: " + e.message);
      }
    }
    // Phase 5: quick enable/disable from the card without opening Edit.
    // Duplicate a workflow via POST with " (copy)" appended to name.
    async function wfDuplicate(w) {
      try {
        const def = (w.definition && typeof w.definition === "object") ? w.definition : { nodes: [], edges: [] };
        const cfg = (w.trigger_config && typeof w.trigger_config === "object") ? Object.assign({}, w.trigger_config) : {};
        // Don't carry the original webhook token over -- server will mint a fresh one.
        delete cfg.token;
        const r = await fetch("/api/workflows", { method: "POST", headers: wfAuthHdrs(), body: JSON.stringify({
          name: (w.name || "Workflow") + " (copy)",
          description: w.description || "",
          definition: def,
          trigger_type: w.trigger_type || "manual",
          trigger_config: cfg,
          enabled: false,           // start the duplicate disabled
          tags: Array.isArray(w.tags) ? w.tags : [],
        })});
        if (r.ok) { showToast("✓ Duplicated (disabled)"); wfReload(); }
        else { const d = await r.json().catch(()=>({})); showToast(d.error || "Duplicate failed"); }
      } catch (e) { showToast("Duplicate failed: " + e.message); }
    }
    async function wfQuickToggleEnabled(w) {
      try {
        const r = await fetch("/api/workflows/" + w.id, {
          method: "PUT",
          headers: { "Authorization": "Bearer " + authToken, "Content-Type": "application/json" },
          body: JSON.stringify({ enabled: !w.enabled }),
        });
        if (r.ok) { showToast(w.enabled ? "Paused" : "Enabled"); wfReload(); }
        else { const d = await r.json().catch(()=>({})); showToast(d.error || "Toggle failed"); }
      } catch (e) { showToast("Toggle failed: " + e.message); }
    }
    async function wfDelete(id, name) {
      if (!window.confirm("Delete workflow \"" + name + "\"? This also deletes its run history.")) return;
      try {
        const r = await fetch("/api/workflows/" + id, { method: "DELETE", headers: { "Authorization": "Bearer " + authToken } });
        if (r.ok) { showToast("Deleted"); wfReload(); }
        else showToast("Delete failed");
      } catch (e) { showToast("Delete failed: " + e.message); }
    }
    async function wfOpenRuns(id) {
      setWfRunsFor(id);
      try {
        const r = await fetch("/api/workflows/" + id + "/runs", { headers: { "Authorization": "Bearer " + authToken } });
        if (r.ok) setWfRuns(await r.json());
      } catch {}
    }
    async function wfOpenRunDetail(runId) {
      try {
        const r = await fetch("/api/workflow_runs/" + runId, { headers: { "Authorization": "Bearer " + authToken } });
        if (r.ok) setWfRunDetail(await r.json());
      } catch {}
    }
    // Phase 4-A: cancel + retry the current run-detail.
    async function wfCancelRun() {
      if (!wfRunDetail || wfActionBusy) return;
      setWfActionBusy(true);
      try {
        const r = await fetch("/api/workflow_runs/" + wfRunDetail.id + "/cancel", { method: "POST", headers: { "Authorization": "Bearer " + authToken } });
        const d = await r.json().catch(() => ({}));
        if (r.ok) showToast("Cancel requested -- run will abort at the next step");
        else showToast(d.error || ("Cancel failed (HTTP " + r.status + ")"));
      } catch (e) { showToast("Cancel failed: " + e.message); }
      setWfActionBusy(false);
    }
    async function wfRetryRun() {
      if (!wfRunDetail || wfActionBusy) return;
      setWfActionBusy(true);
      try {
        const r = await fetch("/api/workflow_runs/" + wfRunDetail.id + "/retry", { method: "POST", headers: { "Authorization": "Bearer " + authToken } });
        const d = await r.json().catch(() => ({}));
        if (r.ok) {
          showToast("Retry started -- Run #" + d.runId);
          setWfRunDetail({
            id: d.runId, status: d.status, latency_ms: d.latencyMs,
            trigger: "retry", error: d.error, log: d.log, outputs: d.outputs,
          });
          wfReload();
        } else showToast(d.error || ("Retry failed (HTTP " + r.status + ")"));
      } catch (e) { showToast("Retry failed: " + e.message); }
      setWfActionBusy(false);
    }
    // Phase 4-A: live-progress polling. When an open run is 'running',
    // re-fetch /api/workflow_runs/:id every 1.5s and replace
    // wfRunDetail with the fresh row (which has the per-step log
    // updates written by _runWorkflow's per-step UPDATE). Stops the
    // moment status flips to anything other than 'running'.
    useEffect(() => {
      if (!wfRunDetail || wfRunDetail.status !== "running") return undefined;
      let cancelled = false;
      const tick = async () => {
        try {
          const r = await fetch("/api/workflow_runs/" + wfRunDetail.id, { headers: { "Authorization": "Bearer " + authToken } });
          if (!r.ok || cancelled) return;
          const d = await r.json().catch(() => null);
          if (!d || cancelled) return;
          setWfRunDetail(d);
        } catch {}
      };
      const tid = setInterval(tick, 1000);
      return () => { cancelled = true; clearInterval(tid); };
    }, [wfRunDetail && wfRunDetail.id, wfRunDetail && wfRunDetail.status, authToken]);
    // Phase 4-B: load template library lazily when the browse modal opens.
    useEffect(() => {
      if (!wfTemplatesOpen) return;
      setWfTemplatesLoading(true);
      fetch("/api/workflow_templates", { headers: { "Authorization": "Bearer " + authToken } })
        .then(r => r.ok ? r.json() : [])
        .then(d => { setWfTemplates(Array.isArray(d) ? d : []); })
        .catch(() => {})
        .finally(() => setWfTemplatesLoading(false));
    }, [wfTemplatesOpen, authToken]);
    // n8n-style: export a workflow as a portable JSON file.
    function wfExportJson(w) {
      const payload = {
        _kind: "chatfort_workflow_v1",
        name: w.name,
        description: w.description || "",
        trigger_type: w.trigger_type || "manual",
        trigger_config: w.trigger_config || {},
        enabled: w.enabled !== false,
        tags: Array.isArray(w.tags) ? w.tags : [],
        definition: w.definition || { nodes: [], edges: [] },
      };
      // Strip the webhook token from the export -- regenerated on import.
      if (payload.trigger_config && typeof payload.trigger_config === "object" && payload.trigger_config.token) {
        payload.trigger_config = Object.assign({}, payload.trigger_config);
        delete payload.trigger_config.token;
      }
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = (w.name || "workflow").replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 60) + ".chatfort.json";
      a.click();
      URL.revokeObjectURL(url);
      showToast("Exported");
    }
    // Import: paste JSON in a modal, POST to /api/workflows.
    const [wfImportOpen, setWfImportOpenLocal] = useState(false);
    const [wfImportText, setWfImportText] = useState("");
    async function wfImportSubmit() {
      let parsed;
      try { parsed = JSON.parse(wfImportText); }
      catch (e) { showToast("Invalid JSON: " + e.message); return; }
      if (!parsed || typeof parsed !== "object" || !parsed.name || !parsed.definition) {
        showToast("Missing name or definition"); return;
      }
      try {
        const r = await fetch("/api/workflows", { method: "POST", headers: wfAuthHdrs(), body: JSON.stringify({
          name: String(parsed.name).slice(0, 200),
          description: String(parsed.description || "").slice(0, 1000),
          trigger_type: parsed.trigger_type || "manual",
          trigger_config: parsed.trigger_config || {},
          definition: parsed.definition,
          enabled: parsed.enabled !== false,
          tags: Array.isArray(parsed.tags) ? parsed.tags : [],
        })});
        if (r.ok) { showToast("✓ Imported"); setWfImportOpenLocal(false); setWfImportText(""); wfReload(); }
        else { const d = await r.json().catch(()=>({})); showToast(d.error || "Import failed"); }
      } catch (e) { showToast("Import failed: " + e.message); }
    }

    async function wfInstallTemplate(tplId) {
      try {
        const r = await fetch("/api/workflow_templates/" + tplId + "/install", { method: "POST", headers: { "Authorization": "Bearer " + authToken } });
        const d = await r.json().catch(() => ({}));
        if (r.ok) { showToast("✓ Installed: " + (d.name || tplId)); setWfTemplatesOpen(false); wfReload(); }
        else showToast(d.error || ("Install failed (HTTP " + r.status + ")"));
      } catch (e) { showToast("Install failed: " + e.message); }
    }

    const CmdBlock = ({id, code, label, lang="bash"}) => (
      <div style={{marginBottom:12}}>
        {label && <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4,letterSpacing:"0.03em"}}>{label}</div>}
        <div style={{display:"flex",alignItems:"stretch",background:S.codeBlockBg,border:`1px solid ${S.border2}`,borderRadius:9,overflow:"hidden"}}>
          <pre style={{flex:1,padding:"11px 14px",margin:0,fontSize:11.5,color:S.text,fontFamily:"monospace",overflowX:"auto",whiteSpace:"pre",lineHeight:1.6}}><code>{code}</code></pre>
          <button onClick={()=>copy(id,code)} style={{padding:"0 13px",background:"none",border:"none",borderLeft:`1px solid ${S.border}`,color:copied===id?S.success:S.text3,cursor:"pointer",fontSize:10,fontFamily:"monospace",whiteSpace:"nowrap",flexShrink:0,minWidth:64,transition:"color 0.15s"}}>
            {copied===id?"✓ Done":"Copy"}
          </button>
        </div>
      </div>
    );

    const PLATFORMS = {
      n8n: {
        name:"n8n",icon:"⚡",color:"#ea4b71",
        badge:"Open Source · Self-hostable",
        desc:"Open-source workflow automation. Self-host on-prem for full data control — ideal for regulated industries. Has a native Claude / Anthropic node.",
        useCases:["Auto-triage inbound emails → categorize + route","Document intake → extract fields → write to database","Legal matter updates → summarize → notify Slack","Patient intake PDF → parse → update EHR system","Monitor RSS / news → summarize → post to internal wiki"],
        steps:[
          {title:"Install n8n (self-hosted)", code:`# Docker — recommended for regulated environments
docker run -it --rm \
  --name n8n \
  -p 5678:5678 \
  -v ~/.n8n:/home/node/.n8n \
  -e N8N_BASIC_AUTH_ACTIVE=true \
  -e N8N_BASIC_AUTH_USER=admin \
  -e N8N_BASIC_AUTH_PASSWORD=yourpassword \
  docker.n8n.io/n8nio/n8n`},
          {title:"Set your Anthropic API key", code:`# In n8n UI:
# Settings → Credentials → New Credential → "Anthropic"
# Paste your key: sk-ant-api03-…

# Or via environment variable:
N8N_ANTHROPIC_API_KEY=sk-ant-api03-…`},
          {title:"Add the Claude node", code:`# In any workflow:
# + Add node → search "Claude" or "Anthropic"
# Select model: claude-sonnet-4-20250514
# Add your system prompt
# Connect to upstream trigger (Webhook, Email, etc.)`},
          {title:"Example: Email triage workflow", code:`Trigger: Email (IMAP)
  ↓
Node: Claude (Anthropic)
  System: "You are a legal matter intake AI. Classify emails as:
           URGENT / ROUTINE / SPAM. Extract: sender, matter, deadline."
  Input: {{ $json.body }}
  ↓
Branch: If URGENT
  → Slack notification to partner
  → Create task in project tool
Branch: ROUTINE
  → Add to matter log spreadsheet`},
        ],
        docs:"https://docs.n8n.io/integrations/builtin/app-nodes/n8n-nodes-langchain.lmanthropiccloud/",
        selfHost:true,
      },
      retool: {
        name:"Retool",icon:"🔧",color:"#3c6ef5",
        badge:"Low-code · Internal tools",
        desc:"Build internal apps that call Claude via API. Ideal for building custom dashboards, review queues, and data tools powered by Claude — with fine-grained access control.",
        useCases:["Document review queue — intake PDF, Claude summarizes, human approves","Contract redline assistant — paste contract, Claude flags risk clauses","Client intake form → Claude extracts fields → writes to Salesforce / Airtable","Internal knowledge base Q&A powered by Claude","Compliance checklist generator from uploaded documents"],
        steps:[
          {title:"Add Anthropic as a Resource", code:`# In Retool:
# Resources → + New → REST API
# Name: Anthropic Claude
# Base URL: https://api.anthropic.com
# Headers:
#   x-api-key: {{ your_anthropic_key }}  (use Retool secrets)
#   anthropic-version: 2023-06-01
#   Content-Type: application/json`},
          {title:"Create a Query to call Claude", code:`// Retool Query (REST API → POST /v1/messages)
{
  "model": "claude-sonnet-4-20250514",
  "max_tokens": 1024,
  "system": "You are a legal document analyst. Extract key clauses.",
  "messages": [
    {
      "role": "user",
      "content": {{ documentInput.value }}
    }
  ]
}`},
          {title:"Wire to a UI component", code:`// In Retool component (e.g. Text component):
// Value: {{ claudeQuery.data.content[0].text }}

// Trigger on button click:
// Button onClick → Run Query → claudeQuery.trigger()

// Show loading state:
// isLoading: {{ claudeQuery.isFetching }}`},
          {title:"Use Retool secrets for the key", code:`# Never hardcode the API key.
# In Retool: Settings → Secrets → Add Secret
# Name: ANTHROPIC_API_KEY
# Value: sk-ant-api03-…

# Reference in queries:
# {{ secrets.ANTHROPIC_API_KEY }}`},
        ],
        docs:"https://docs.retool.com/docs/connect-to-claude",
        selfHost:false,
      },
      zapier: {
        name:"Zapier",icon:"⚡",color:"#ff4a00",
        badge:"No-code · 6000+ apps",
        desc:"Connect Claude to 6000+ apps with zero code. Best for fast prototyping and connecting Claude to SaaS tools your team already uses.",
        useCases:["New email → Claude summary → Notion database","Form submission → Claude analyzes → routes to right team","New Slack message → Claude drafts reply → send for approval","Document upload → Claude extracts → writes to Airtable","Calendar invite → Claude drafts agenda → sends to attendees"],
        steps:[
          {title:"Add Claude to a Zap", code:`1. Create a New Zap
2. Set your Trigger (e.g. "New Email in Gmail")
3. + Add Action → search "Claude AI by Anthropic" (official)
4. Choose action: "Send Message"
5. Connect your Anthropic account (enter API key)
6. Configure:
   - Model: claude-sonnet-4-20250514
   - System Prompt: your instructions
   - User Message: map from trigger data`},
          {title:"Example: Document intake Zap", code:`Trigger: New file in Google Drive folder
  ↓
Action: Get File Content (Google Drive)
  ↓
Action: Claude AI → Send Message
  System: "Extract: document type, parties, dates, key terms."
  Message: {{ File Content }}
  ↓
Action: Create Row in Airtable
  Fields: map Claude output fields`},
          {title:"Handle structured output", code:`# Prompt Claude to return JSON for easy mapping:
System: "Always respond with valid JSON only:
{
  'type': 'contract|memo|invoice|other',
  'parties': ['...'],
  'date': 'YYYY-MM-DD',
  'summary': '...',
  'flags': ['...']
}"

# Then use Zapier's "Code by Zapier" step:
const data = JSON.parse(inputData.claudeOutput);
return data;`},
        ],
        docs:"https://zapier.com/apps/claude-ai-by-anthropic/integrations",
        selfHost:false,
      },
      make: {
        name:"Make (Integromat)",icon:"🔄",color:"#6d4aff",
        badge:"Visual · Advanced logic",
        desc:"Visual workflow builder with advanced branching, iterators, and error handling. Great for complex multi-step pipelines with conditional logic.",
        useCases:["Batch document processing with error handling","Multi-step approval workflows with human-in-the-loop","Scheduled reports generated by Claude","CRM enrichment — Claude researches and fills contact fields","Multi-language document translation pipeline"],
        steps:[
          {title:"Add an HTTP module for Claude", code:`Module: HTTP → Make a Request
URL: https://api.anthropic.com/v1/messages
Method: POST
Headers:
  x-api-key: [your key from Make keychain]
  anthropic-version: 2023-06-01
  Content-Type: application/json
Body (JSON):
{
  "model": "claude-sonnet-4-20250514",
  "max_tokens": 1024,
  "messages": [{"role": "user", "content": "{{1.document}}"}]
}`},
          {title:"Parse Claude's response", code:`# After the HTTP module, add:
Module: JSON → Parse JSON
String: {{http.data}}

# Then map:
# Content: {{json.content[].text}}
# (Use the Array Iterator for multiple content blocks)`},
          {title:"Add human-in-the-loop approval", code:`# After Claude processes:
Module: Email → Send an Email
  To: reviewer@yourfirm.com
  Subject: Review Required: {{document.name}}
  Body: Claude summary + approve/reject links

# Use Make's "Wait for Webhook" to pause
# until the reviewer clicks approve`},
        ],
        docs:"https://www.make.com/en/integrations/anthropic-claude",
        selfHost:false,
      },
      langchain: {
        name:"LangChain / LangGraph",icon:"🦜",color:"#1c7c54",
        badge:"Code-first · Python / JS",
        desc:"Build custom AI pipelines in code. Full control over prompting, memory, tool use, and multi-agent orchestration using the official Anthropic SDK.",
        useCases:["Multi-agent research pipelines","RAG over private document stores","Long-running agentic tasks with checkpoints","Custom tool-calling workflows with your internal APIs","Fine-grained prompt chaining with fallbacks"],
        steps:[
          {title:"Install dependencies", code:`pip install langchain langchain-anthropic langgraph

# or for JS:
npm install @langchain/anthropic @langchain/langgraph`},
          {title:"Basic Claude chain (Python)", code:`from langchain_anthropic import ChatAnthropic
from langchain_core.messages import HumanMessage, SystemMessage

llm = ChatAnthropic(
    model="claude-sonnet-4-20250514",
    api_key="sk-ant-api03-…",  # or set ANTHROPIC_API_KEY
    max_tokens=1024,
)

messages = [
    SystemMessage(content="You are a legal document analyst."),
    HumanMessage(content="Summarize this contract: " + doc_text),
]

response = llm.invoke(messages)
print(response.content)`},
          {title:"Multi-agent graph (LangGraph)", code:`from langgraph.graph import StateGraph, END
from langchain_anthropic import ChatAnthropic

llm = ChatAnthropic(model="claude-sonnet-4-20250514")

def intake_agent(state):
    result = llm.invoke([("system", "Extract fields from document"),
                         ("human", state["document"])])
    return {"extracted": result.content}

def review_agent(state):
    result = llm.invoke([("system", "Flag compliance issues"),
                         ("human", state["extracted"])])
    return {"flags": result.content}

graph = StateGraph(dict)
graph.add_node("intake", intake_agent)
graph.add_node("review", review_agent)
graph.add_edge("intake", "review")
graph.add_edge("review", END)
graph.set_entry_point("intake")

app = graph.compile()
result = app.invoke({"document": doc_text})`},
          {title:"Set API key", code:`# Python — .env or environment
export ANTHROPIC_API_KEY="sk-ant-api03-…"

# In code:
import os
os.environ["ANTHROPIC_API_KEY"] = "sk-ant-api03-…"

# JS — .env
ANTHROPIC_API_KEY=sk-ant-api03-…`},
        ],
        docs:"https://python.langchain.com/docs/integrations/chat/anthropic/",
        selfHost:true,
      },
      activepieces: {
        name:"Activepieces",icon:"🧩",color:"#6e41e2",
        badge:"Open Source · Self-hostable",
        desc:"Open-source n8n alternative with a clean UI and growing library of pieces. Self-hostable for data-sensitive workflows, with a native Claude AI piece.",
        useCases:["Email auto-responder powered by Claude","Document pipeline: upload → extract → store","Webhook receiver → Claude classifies → routes","Scheduled content generation for internal teams","Multi-step approval with Claude summarization at each step"],
        steps:[
          {title:"Self-host with Docker", code:`docker run -d \
  --name activepieces \
  -p 8080:80 \
  -e AP_ENCRYPTION_KEY=$(openssl rand -hex 16) \
  -e AP_JWT_SECRET=$(openssl rand -hex 16) \
  activepieces/activepieces:latest`},
          {title:"Add Claude AI Piece", code:`# In Activepieces:
# + New Flow → Add Trigger (e.g. Webhook)
# + Add Step → Search "Claude AI" or "Anthropic"
# Configure:
#   API Key: your sk-ant-api03-… key
#   Model: claude-sonnet-4-20250514
#   System Prompt: your instructions
#   User Message: {{ trigger.body.text }}`},
          {title:"Example: Webhook → Claude → Slack", code:`Trigger: Webhook (POST /intake)
  body: { "document": "..." }
  ↓
Step: Claude AI
  Prompt: "Summarize and flag issues in: {{ trigger.body.document }}"
  ↓
Step: Slack → Send Message
  Channel: #reviews
  Message: {{ claude.output }}`},
        ],
        docs:"https://www.activepieces.com/pieces/claude-ai",
        selfHost:true,
      },
    };

    const plat = PLATFORMS[activePlat];

    const WORKFLOW_PATTERNS = [
      {icon:"📥", title:"Document Intake Pipeline", desc:"File arrives → Claude extracts structured fields → writes to database → notifies reviewer", tools:["n8n","Make","Activepieces"]},
      {icon:"📧", title:"Email Triage & Routing", desc:"Incoming email → Claude classifies priority + category → routes to right person/queue", tools:["Zapier","n8n","Make"]},
      {icon:"🔍", title:"Research & Enrichment", desc:"New contact/lead → Claude researches and fills fields → updates CRM", tools:["Zapier","Retool","LangChain"]},
      {icon:"📋", title:"Report Generation", desc:"Scheduled trigger → Claude pulls data + writes narrative → delivers via email/Slack", tools:["n8n","Make","LangGraph"]},
      {icon:"✅", title:"Human-in-the-Loop Review", desc:"Claude processes → flags uncertainty → pauses for human approval → continues", tools:["Make","LangGraph","Retool"]},
      {icon:"🔄", title:"Multi-Step Data Transform", desc:"Raw input → Claude normalizes → validates → reformats → delivers to destination", tools:["LangChain","n8n","Activepieces"]},
    ];

    const tabs2 = [
      {id:"builtin", label:"Built-in"},
      {id:"platforms", label:"Platforms"},
      {id:"patterns", label:"Workflow Patterns"},
      {id:"api", label:"Direct API"},
    ];

    return (
      <div className="cf-page-fade-in" style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
        <div style={{maxWidth:860,margin:"0 auto"}}>

          {/* Header */}
          <div className="cf-section-enter" style={{marginBottom:22}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
              <h2 style={{fontSize:22,fontWeight:800,color:S.text,letterSpacing:"-0.03em"}}>Agentic Workflows</h2>
              <span style={{fontSize:9,padding:"3px 9px",borderRadius:10,fontFamily:"monospace",fontWeight:700,background:`${S.accent}18`,color:S.accent,border:`1px solid ${S.accent}30`}}>Claude API</span>
            </div>
            <p style={{fontSize:13,color:S.text2,lineHeight:1.65,maxWidth:620}}>Connect Claude to your automation platforms. Run multi-step pipelines, process documents at scale, and build agentic workflows that act on your behalf — all with zero data retention.</p>
          </div>

          {/* Tab bar */}
          <div style={{display:"flex",gap:4,marginBottom:22,background:S.surface2,borderRadius:10,padding:3}}>
            {tabs2.map(t=>(
              <button key={t.id} onClick={()=>setTab(t.id)} style={{flex:1,padding:"7px 10px",borderRadius:8,border:"none",background:tab===t.id?S.cardBg:"none",color:tab===t.id?S.text:S.text3,fontSize:12,fontWeight:tab===t.id?600:400,cursor:"pointer",transition:"all 0.15s",boxShadow:tab===t.id?"0 1px 4px rgba(0,0,0,0.1)":"none"}}>
                {t.label}
              </button>
            ))}
          </div>

          {/* ── BUILT-IN BUILDER TAB (Phase 2b) ── */}
          {tab==="builtin" && (
            <div>
              {/* Quick-stats row -- enabled / paused / by-trigger counts. */}
              {wfItems.length > 0 && (() => {
                const enabled = wfItems.filter(w => w.enabled !== false).length;
                const paused  = wfItems.length - enabled;
                const sched   = wfItems.filter(w => w.trigger_type === "schedule").length;
                const wh      = wfItems.filter(w => w.trigger_type === "webhook").length;
                return (
                  <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
                    {[
                      ["Total",   wfItems.length, S.text],
                      ["Enabled", enabled,        S.success],
                      ["Paused",  paused,         S.text3],
                      ["🕐 Sched", sched,         S.accent],
                      ["🔗 Hook",  wh,            S.accent],
                    ].map(([label, val, color]) => (
                      <div key={label} style={{padding:"7px 14px",borderRadius:10,background:S.cardBg,border:`1px solid ${S.border2}`,minWidth:84}}>
                        <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:2}}>{label}</div>
                        <div style={{fontSize:18,fontWeight:800,color}}>{val}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,gap:10,flexWrap:"wrap"}}>
                <div style={{display:"flex",gap:8,alignItems:"center",flex:1,minWidth:240}}>
                  <input value={wfListSearch} onChange={e=>setWfListSearch(e.target.value)} placeholder="🔍 Search workflows…" style={{flex:1,minWidth:160,padding:"6px 11px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text,fontSize:12,fontFamily:"inherit"}}/>
                  <select value={wfListSort} onChange={e=>setWfListSort(e.target.value)} style={{padding:"6px 10px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:12,fontFamily:"inherit"}}>
                    <option value="recent">Recent</option>
                    <option value="name">Name</option>
                    <option value="trigger">Trigger</option>
                  </select>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setWfImportOpenLocal(true)} className="cf-btn-lift" title="Paste workflow JSON to import" style={{padding:"7px 14px",borderRadius:9,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontFamily:"inherit"}}>⬆ Import</button>
                  <button onClick={()=>setWfTemplatesOpen(true)} className="cf-btn-lift" style={{padding:"7px 14px",borderRadius:9,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontFamily:"inherit"}}>📋 Browse Templates</button>
                  <button onClick={wfStartNew} className="cf-cta cf-btn-lift" style={{padding:"7px 14px",borderRadius:9,border:`1px solid ${S.accent}55`,background:S.accent,color:S.accent_fg||"#fff",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontFamily:"inherit"}}>{IC.plus} New Workflow</button>
                </div>
              </div>
              {/* Tag filter row -- collected from every workflow's tags. */}
              {(() => {
                const allTags = Array.from(new Set(wfItems.flatMap(w => Array.isArray(w.tags) ? w.tags : []))).sort();
                if (allTags.length === 0) return null;
                return (
                  <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap",alignItems:"center"}}>
                    <span style={{fontSize:9,color:S.text3,fontFamily:"monospace",fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase",marginRight:4}}>Tags:</span>
                    <button onClick={()=>setWfTagFilter("")} style={{padding:"3px 10px",borderRadius:10,border:`1px solid ${wfTagFilter===""?S.accent:S.border2}`,background:wfTagFilter===""?S.accentDim:S.inputBg,color:wfTagFilter===""?S.accent:S.text2,fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:wfTagFilter===""?700:500}}>All</button>
                    {allTags.map(t => (
                      <button key={t} onClick={()=>setWfTagFilter(wfTagFilter===t?"":t)} style={{padding:"3px 10px",borderRadius:10,border:`1px solid ${wfTagFilter===t?S.accent:S.border2}`,background:wfTagFilter===t?S.accentDim:S.inputBg,color:wfTagFilter===t?S.accent:S.text2,fontSize:11,cursor:"pointer",fontFamily:"inherit",fontFamily:"monospace",fontWeight:wfTagFilter===t?700:500}}>#{t}</button>
                    ))}
                  </div>
                );
              })()}
              {wfItems.length === 0 ? (
                <div className="cf-empty-state" style={{padding:"50px 30px",textAlign:"center",color:S.text3,fontSize:13,background:S.cardBg,borderRadius:14,border:`1px dashed ${S.border2}`}}>
                  <div className="cf-empty-icon-pop" style={{fontSize:42,marginBottom:10}}>⚡</div>
                  <div style={{fontSize:14,color:S.text2,fontWeight:600,marginBottom:6}}>No workflows yet</div>
                  <div style={{fontSize:12,color:S.text3,lineHeight:1.6,maxWidth:420,margin:"0 auto"}}>Click <strong style={{color:S.accent}}>+ New Workflow</strong> to build one from scratch, or <strong style={{color:S.accent}}>📋 Browse Templates</strong> to start from a pre-built example.</div>
                </div>
              ) : (() => {
                // Sort + filter pipeline. Pinned workflows always sort first.
                const q = (wfListSearch || "").toLowerCase();
                const filtered = wfItems.filter(w => {
                  if (wfTagFilter) {
                    const tags = Array.isArray(w.tags) ? w.tags : [];
                    if (!tags.includes(wfTagFilter)) return false;
                  }
                  if (!q) return true;
                  return ((w.name||"") + " " + (w.description||"") + " " + (w.id||"")).toLowerCase().includes(q);
                });
                const sorted = [...filtered].sort((a, b) => {
                  const aPinned = wfPinned.includes(a.id), bPinned = wfPinned.includes(b.id);
                  if (aPinned !== bPinned) return aPinned ? -1 : 1;
                  if (wfListSort === "name") return (a.name || "").localeCompare(b.name || "");
                  if (wfListSort === "trigger") return (a.trigger_type || "").localeCompare(b.trigger_type || "");
                  // 'recent': last_run_at desc, then updated_at desc.
                  const aT = new Date(a.last_run_at || a.updated_at || 0).getTime();
                  const bT = new Date(b.last_run_at || b.updated_at || 0).getTime();
                  return bT - aT;
                });
                const sinceLabel = (iso) => {
                  if (!iso) return null;
                  const ms = Date.now() - new Date(iso).getTime();
                  if (ms < 60_000) return "just now";
                  if (ms < 3600_000) return Math.floor(ms/60_000) + "m ago";
                  if (ms < 86400_000) return Math.floor(ms/3600_000) + "h ago";
                  return Math.floor(ms/86400_000) + "d ago";
                };
                return (
                <div className="cf-grid-stagger" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
                  {sorted.map((w, idx) => {
                    const status = w.last_run_status || "never";
                    const statusColor = status === "success" ? S.success : status === "error" ? S.danger : S.text3;
                    const nodeCount = (w.definition && Array.isArray(w.definition.nodes)) ? w.definition.nodes.length : 0;
                    const isPinned = wfPinned.includes(w.id);
                    const isRunning = status === "running";
                    const ago = sinceLabel(w.last_run_at);
                    return (
                      <div key={w.id} className={"cf-wf-card" + (isRunning ? " cf-wf-card-pulse" : "")} style={{padding:16,background:S.cardBg,border:`1px solid ${S.border2}`,borderLeft:`3px solid ${isPinned ? "#f59e0b" : statusColor + "55"}`,borderRadius:14,display:"flex",flexDirection:"column",gap:8,animationDelay:(Math.min(idx, 8) * 30) + "ms"}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                          <div style={{display:"flex",alignItems:"center",gap:6,flex:1,minWidth:0}}>
                            <button onClick={()=>wfTogglePin(w.id)} className="cf-wf-star" data-pinned={isPinned} title={isPinned ? "Unpin" : "Pin to top"} style={{background:"none",border:"none",color:isPinned?"#f59e0b":S.text3,fontSize:14,cursor:"pointer",padding:0,lineHeight:1,flexShrink:0}}>{isPinned ? "★" : "☆"}</button>
                            <div style={{fontSize:14,fontWeight:700,color:S.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{w.name}</div>
                          </div>
                          <span style={{fontSize:9,padding:"2px 7px",borderRadius:10,fontFamily:"monospace",fontWeight:700,background:statusColor+"22",color:statusColor,border:`1px solid ${statusColor}55`,flexShrink:0}}>{status.toUpperCase()}</span>
                        </div>
                        {w.description && <div style={{fontSize:11,color:S.text3,lineHeight:1.55}}>{w.description}</div>}
                        <div style={{display:"flex",gap:6,flexWrap:"wrap",fontSize:10,color:S.text3,fontFamily:"monospace",alignItems:"center"}}>
                          <span style={{padding:"1px 7px",borderRadius:10,background:S.inputBg,border:`1px solid ${S.border2}`}}>{(w.trigger_type==="schedule"?"🕐 Schedule":w.trigger_type==="webhook"?"🔗 Webhook":"▶ Manual")}</span>
                          <span style={{padding:"1px 7px",borderRadius:10,background:S.inputBg,border:`1px solid ${S.border2}`}}>{nodeCount} node{nodeCount===1?"":"s"}</span>
                          {ago && <span style={{padding:"1px 7px",borderRadius:10,background:S.inputBg,border:`1px solid ${S.border2}`}}>⏱ {ago}</span>}
                          {!w.enabled && <span style={{padding:"1px 7px",borderRadius:10,background:S.danger+"15",color:S.danger,border:`1px solid ${S.danger}40`}}>paused</span>}
                          <span style={{marginLeft:"auto"}}>
                            <button onClick={()=>wfQuickToggleEnabled(w)} title={w.enabled?"Pause":"Enable"} style={{padding:"1px 8px",borderRadius:10,background:w.enabled?S.success+"15":S.text3+"15",color:w.enabled?S.success:S.text3,border:`1px solid ${w.enabled?S.success+"40":S.border2}`,fontFamily:"monospace",fontWeight:700,fontSize:9,cursor:"pointer"}}>{w.enabled ? "ON" : "OFF"}</button>
                          </span>
                        </div>
                        {Array.isArray(w.tags) && w.tags.length > 0 && (
                          <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                            {w.tags.map(t => (
                              <span key={t} onClick={(e)=>{e.stopPropagation();setWfTagFilter(t);}} style={{fontSize:9,padding:"1px 7px",borderRadius:10,fontFamily:"monospace",fontWeight:700,background:S.accent+"15",color:S.accent,border:`1px solid ${S.accent}30`,cursor:"pointer"}}>#{t}</span>
                            ))}
                          </div>
                        )}
                        <div style={{display:"flex",gap:6,marginTop:4,flexWrap:"wrap"}}>
                          <button onClick={()=>wfRun(w.id)} className="cf-btn-lift" style={{padding:"5px 11px",borderRadius:7,border:`1px solid ${S.accent}55`,background:S.accentDim,color:S.accent,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>▶ Run</button>
                          <button onClick={()=>wfStartPreview(w)} title="Animate the workflow's steps + show the latest result (no engine fire)" style={{padding:"5px 11px",borderRadius:7,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>👁 Preview</button>
                          <button onClick={()=>wfStartEdit(w)} style={{padding:"5px 11px",borderRadius:7,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Edit</button>
                          <button onClick={()=>wfOpenRuns(w.id)} style={{padding:"5px 11px",borderRadius:7,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Runs</button>
                          <button onClick={()=>wfExportJson(w)} title="Download workflow JSON" style={{padding:"5px 11px",borderRadius:7,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>⬇ Export</button>
                          <button onClick={()=>wfDuplicate(w)} title="Create a disabled copy of this workflow" style={{padding:"5px 11px",borderRadius:7,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>⎘ Duplicate</button>
                          <button onClick={()=>wfDelete(w.id,w.name)} style={{padding:"5px 11px",borderRadius:7,border:`1px solid ${S.danger}40`,background:"none",color:S.danger,fontSize:11,cursor:"pointer",fontFamily:"inherit",marginLeft:"auto"}}>Delete</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                );
              })()}
            </div>
          )}

          {/* Create/edit workflow modal */}
          {wfEditing && (
            <Modal open={!!wfEditing} title={wfEditing.id ? "Edit workflow" : "New workflow"} onClose={()=>setWfEditing(null)} footer={
              <>
                <button onClick={()=>setWfEditing(null)} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${S.border2}`,background:"none",color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                <button onClick={wfSave} style={{padding:"7px 14px",borderRadius:8,border:"none",background:S.accent,color:S.accent_fg||"#fff",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Save</button>
              </>
            }>
              <FR label="NAME"><input value={wfEditing.name} onChange={e=>setWfEditing(p=>({...p,name:e.target.value}))} style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text,fontSize:13,fontFamily:"inherit"}}/></FR>
              <FR label="DESCRIPTION"><input value={wfEditing.description} onChange={e=>setWfEditing(p=>({...p,description:e.target.value}))} placeholder="Optional" style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text,fontSize:13,fontFamily:"inherit"}}/></FR>
              <FR label="TRIGGER"><select value={wfEditing.trigger_type} onChange={e=>setWfEditing(p=>({...p,trigger_type:e.target.value}))} style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text,fontSize:13,fontFamily:"inherit"}}>
                <option value="manual">▶ Manual (click Run)</option>
                <option value="schedule">🕐 Schedule (cron)</option>
                <option value="webhook">🔗 Webhook (POST to URL)</option>
              </select></FR>
              {wfEditing.trigger_type === "schedule" && (
                <>
                  <FR label="QUICK PRESET">
                    <select value="" onChange={e=>{if(!e.target.value)return;setWfEditing(p=>({...p,trigger_config:{...(p.trigger_config||{}),cron:e.target.value}}));}} style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text,fontSize:13,fontFamily:"inherit"}}>
                      <option value="">-- pick a preset (or write your own below) --</option>
                      <option value="*/5 * * * *">Every 5 minutes</option>
                      <option value="*/15 * * * *">Every 15 minutes</option>
                      <option value="*/30 * * * *">Every 30 minutes</option>
                      <option value="0 * * * *">Every hour, on the hour</option>
                      <option value="0 9 * * *">Daily at 9 AM</option>
                      <option value="0 18 * * *">Daily at 6 PM</option>
                      <option value="0 9 * * 1-5">Weekdays at 9 AM</option>
                      <option value="0 0 * * 0">Sunday at midnight</option>
                      <option value="0 0 1 * *">First of each month</option>
                    </select>
                  </FR>
                  <FR label="CRON"><input value={(wfEditing.trigger_config||{}).cron||""} onChange={e=>setWfEditing(p=>({...p,trigger_config:{...(p.trigger_config||{}),cron:e.target.value}}))} placeholder="*/15 * * * *" spellCheck={false} style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text,fontSize:12,fontFamily:"monospace"}}/></FR>
                  <FR label="TIMEZONE"><input value={(wfEditing.trigger_config||{}).tz||""} onChange={e=>setWfEditing(p=>({...p,trigger_config:{...(p.trigger_config||{}),tz:e.target.value}}))} placeholder="UTC (e.g. America/New_York)" style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text,fontSize:12,fontFamily:"inherit"}}/></FR>
                  <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",margin:"-8px 0 14px",lineHeight:1.6}}>
                    5-field cron (min hour dom mon dow). Examples: <code>*/15 * * * *</code> every 15 min, <code>0 9 * * 1-5</code> 9 AM weekdays. Polled every 60s.
                  </div>
                </>
              )}
              {wfEditing.trigger_type === "webhook" && wfEditing.id && (
                <FR label="WEBHOOK URL">
                  <WfWebhookUrlPanel id={wfEditing.id} showToast={showToast} authToken={authToken}/>
                </FR>
              )}
              {wfEditing.trigger_type === "webhook" && !wfEditing.id && (
                <div style={{fontSize:11,color:S.text3,padding:"8px 10px",borderRadius:7,background:S.inputBg,border:`1px dashed ${S.border2}`,margin:"0 0 14px",lineHeight:1.55}}>
                  Save the workflow first — the webhook URL is generated on create.
                </div>
              )}
              <FR label="DEFINITION">
                <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:8}}>
                  <button onClick={()=>setWfShowEditor(true)} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${S.accent}55`,background:S.accentDim,color:S.accent,fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6,fontFamily:"inherit"}}>🎨 Visual editor</button>
                  <span style={{fontSize:10,color:S.text3,fontFamily:"monospace"}}>or edit JSON directly below</span>
                </div>
                <textarea value={wfDefStr} onChange={e=>setWfDefStr(e.target.value)} spellCheck={false} style={{width:"100%",minHeight:200,padding:"10px 12px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.codeBlockBg,color:S.text,fontSize:11,fontFamily:"monospace",lineHeight:1.55}}/>
                <div style={{fontSize:10,color:S.text3,marginTop:6,fontFamily:"monospace",lineHeight:1.6}}>
                  Shape: {"{nodes:[{id,type,position,data}], edges:[{id,source,target}]}"}. Types: trigger.manual / action.aiChat / action.kbSearch / action.mcpCall / output.toast / logic.setVar / logic.if / workflow.call. Interp: {"{{ $node.<id>.output }}"} or {"{{ $var.<name> }}"}.
                  <br/>action.aiChat `model` accepts a full ID (e.g. <code>claude-haiku-4-5-20251001</code>) OR a unique short prefix (e.g. <code>claude-haiku-4-5</code>); the engine resolves it against your enabled models at run time.
                  <br/><br/><strong style={{color:S.text2}}>Node data shapes:</strong>
                  <br/>logic.if      data: {"{ left, op (==|!=|>|<|>=|<=|contains), right }"}
                  <br/>logic.setVar  data: {"{ name, value }"}
                  <br/>action.aiChat data: {"{ prompt, systemPrompt?, agentId?, model?, maxTokens? }"}
                  <br/>output.toast  data: {"{ message }"}
                  <br/>action.kbSearch  data: {"{ kbId, query, topK? }"}
                  <br/>action.mcpCall   data: {"{ serverId, toolName, args? }"}
                  <br/>action.httpRequest data: {"{ url, method?, headers?, body?, timeoutMs? }"}
                  <br/>output.saveMemory  data: {"{ text }"}
                  <br/>workflow.call      data: {"{ workflowId, vars? (object) }"}
                  <br/>action.sendEmail   data: {"{ to (string|array), subject, body, from?, replyTo?, html? }"}
                  <br/>action.fetchFeeds  data: {"{ urls: string[], sinceHours?, max? }"}
                  <br/>action.wait        data: {"{ ms (number, 0..60_000) }"}
                  <br/>action.code        data: {"{ code (JS body; inputs/vars/payload/console available; must return) }"}
                  <br/>output.note        data: {"{ text (interpolated) }"}
                  <br/>action.jsonParse   data: {"{ input (string, interpolated) }"}
                  <br/>action.regex       data: {"{ input, pattern, flags?, mode? (test|match|matchAll) }"}
                  <br/>action.slack       data: {"{ webhookUrl? (falls back to Marketplace Slack), text, channel? (required when using Slack OAuth bot) }"}
                  <br/>action.mattermost  data: {"{ webhookUrl? (falls back to Marketplace Mattermost), text }"}
                  <br/>action.rocketchat  data: {"{ webhookUrl? (falls back to Marketplace Rocket.Chat), text }"}
                  <br/>logic.switch       data: {"{ expression, cases: { caseValue: branchName }, default? }"}
                  <br/>logic.filter       data: {"{ items, pattern, flags? }"}
                  <br/>logic.map          data: {"{ items, template ({{ $item }} or {{ $item.field }}) }"}
                  <br/>logic.sort         data: {"{ items, by?, order? (asc|desc) }"}
                  <br/>logic.dedupe       data: {"{ items, by? }"}
                  <br/>action.discord     data: {"{ webhookUrl? (falls back to Marketplace Discord), content }"}
                  <br/>action.template    data: {"{ template (interpolated) }"}
                  <br/>action.csv         data: {"{ items, columns? }"}
                  <br/>action.base64      data: {"{ input, mode? (encode|decode) }"}
                  <br/>logic.iif          data: {"{ condition, thenValue, elseValue }"}
                  <br/>logic.length       data: {"{ input }"}
                  <br/>logic.slice        data: {"{ input, start, end? }"}
                  <br/>logic.join         data: {"{ items, separator? }"}
                  <br/>action.uuid        data: {}
                  <br/>action.now         data: {"{ tz? (IANA) }"}
                  <br/>action.replace     data: {"{ input, search, replace, all?, regex? }"}
                  <br/>action.markdownToHtml data: {"{ input }"}
                  <br/>output.webhook     data: {"{ url, headers?, body? }"}
                  <br/>action.urlEncode   data: {"{ input }"}
                  <br/>action.urlDecode   data: {"{ input }"}
                  <br/>action.range       data: {"{ start?, end, step? }"}
                  <br/>action.lowercase   data: {"{ input }"}
                  <br/>action.uppercase   data: {"{ input }"}
                  <br/><br/><strong style={{color:S.text2}}>Trigger outputs ($node.&lt;triggerId&gt;.output):</strong>
                  <br/>trigger.manual    {"{ triggered, by (userId), firedAt }"}
                  <br/>trigger.schedule  {"{ triggered, by, firedAt, cron, tz }"}
                  <br/>trigger.webhook   {"{ triggered, by, payload, headers, query, method, firedAt }"}
                  <br/><span style={{opacity:0.75}}>Webhook `payload` is the parsed request body (NOT `body`). Schedule `cron`/`tz` are null when fired manually via Run.</span>
                </div>
              </FR>
              <FR label="ENABLED">
                <label style={{display:"inline-flex",alignItems:"center",gap:8,fontSize:12,color:S.text2,cursor:"pointer"}}>
                  <input type="checkbox" checked={wfEditing.enabled} onChange={e=>setWfEditing(p=>({...p,enabled:e.target.checked}))}/>
                  Enabled
                </label>
              </FR>
              <FR label="TAGS">
                <input
                  value={(Array.isArray(wfEditing.tags) ? wfEditing.tags : []).join(", ")}
                  onChange={e=>setWfEditing(p=>({...p,tags:e.target.value.split(",").map(s=>s.trim()).filter(Boolean).slice(0, 10)}))}
                  placeholder="e.g. daily, marketing, alerts (comma-separated, max 10)"
                  style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text,fontSize:13,fontFamily:"inherit"}}
                />
                <div style={{fontSize:10,color:S.text3,marginTop:6,fontFamily:"monospace",lineHeight:1.55}}>Normalized server-side: lowercase, a-z 0-9 _ -, max 20 chars each. Click a tag chip on a card to filter.</div>
              </FR>
            </Modal>
          )}

          {wfShowEditor && wfEditing && (() => {
            let parsedDef;
            try { parsedDef = JSON.parse(wfDefStr); }
            catch { parsedDef = { nodes: [], edges: [] }; }
            if (!parsedDef || typeof parsedDef !== "object") parsedDef = { nodes: [], edges: [] };
            return (
              <WorkflowEditor
                initialValue={parsedDef}
                theme={{ bg: S.bg, text: S.text }}
                onCancel={()=>setWfShowEditor(false)}
                onSave={(def)=>{
                  setWfDefStr(JSON.stringify(def, null, 2));
                  setWfShowEditor(false);
                  // Existing workflow: persist NOW — one Save means saved.
                  // New workflow: there's no server row yet; point at Create.
                  if (wfEditing?.id) wfSave(def);
                  else showToast("Definition updated — click Create below to save the new workflow");
                }}
              />
            );
          })()}

          {/* Run history modal */}
          {/* Workflow preview modal -- animated step walkthrough +
              latest successful run's final output. No engine fire. */}
          {wfPreviewing && (() => {
            const nodes = (wfPreviewing.definition && Array.isArray(wfPreviewing.definition.nodes)) ? wfPreviewing.definition.nodes : [];
            const edges = (wfPreviewing.definition && wfPreviewing.definition.edges) || [];
            const order = _wfPreviewTopo(nodes, edges);
            const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));
            const complete = wfPreviewStep >= order.length;
            // Final output: the last successful step's output from the
            // latest successful run. Unwraps common handler shapes so
            // the preview reads as the finished product the workflow
            // actually produced, not the raw JSON wrapper. Markdown
            // in the extracted text renders via MsgContent.
            const _extractRenderable = (out) => {
              if (out == null) return "";
              if (typeof out === "string") return out;
              if (typeof out === "object" && !Array.isArray(out)) {
                // output.toast handler returns { toast: <string> }
                if (typeof out.toast === "string") return out.toast;
                // action.aiChat / agent calls -- assistant text variants
                if (typeof out.text === "string") return out.text;
                if (typeof out.message === "string") return out.message;
                if (typeof out.content === "string") return out.content;
                // Generic { output: <string> } wrapper.
                if (typeof out.output === "string") return out.output;
              }
              return JSON.stringify(out, null, 2);
            };
            let finalText = "";
            let finalNode = null;
            if (wfPreviewRun) {
              const log = Array.isArray(wfPreviewRun.log) ? wfPreviewRun.log : [];
              const outs = wfPreviewRun.outputs || {};
              for (let i = log.length - 1; i >= 0; i--) {
                if (log[i].status === "ok") { finalNode = log[i]; break; }
              }
              if (finalNode) {
                finalText = _extractRenderable(outs[finalNode.node_id]);
              }
            }
            return (
              <Modal open={!!wfPreviewing} title={"Preview: " + (wfPreviewing.name || "")} onClose={wfClosePreview} footer={
                <>
                  <button onClick={()=>{setWfPreviewStep(-1); setTimeout(()=>setWfPreviewStep(0), 50);}} disabled={wfPreviewStep < 0} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>↻ Replay</button>
                  <button onClick={()=>{wfClosePreview(); wfRun(wfPreviewing.id);}} className="cf-btn-lift" style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${S.accent}55`,background:S.accentDim,color:S.accent,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>▶ Run for real</button>
                  <button onClick={wfClosePreview} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${S.border2}`,background:"none",color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
                </>
              }>
                <div style={{fontSize:11,color:S.text3,marginBottom:10,fontFamily:"monospace"}}>
                  {complete ? "✓ Walkthrough complete" : (wfPreviewStep < 0 ? "Loading…" : ("Step " + (wfPreviewStep + 1) + " of " + order.length + " · " + (nodeMap[order[wfPreviewStep]]?.type || "?")))}
                </div>
                {/* Horizontal animated flow of step tiles */}
                <div style={{display:"flex",alignItems:"stretch",gap:0,overflowX:"auto",padding:"4px 0 12px",marginBottom:12}}>
                  {order.map((nid, i) => {
                    const node = nodeMap[nid] || { id: nid, type: "?" };
                    const isDone = i < wfPreviewStep || complete;
                    const isActive = i === wfPreviewStep && !complete;
                    const c = isActive ? S.accent : isDone ? S.success : S.text3;
                    const icon = _WF_PREVIEW_ICONS[node.type] || "•";
                    return (
                      <div key={nid} style={{display:"flex",alignItems:"center",flexShrink:0}}>
                        <div style={{minWidth:104,padding:"10px 8px",borderRadius:10,background:isActive?S.accentDim:(isDone?S.success+"12":S.inputBg),border:`1.5px solid ${c}55`,display:"flex",flexDirection:"column",alignItems:"center",gap:4,transition:"transform 320ms cubic-bezier(0.16, 1, 0.3, 1), background 320ms ease, border-color 320ms ease, box-shadow 320ms ease",transform: isActive ? "scale(1.06)" : "scale(1)",boxShadow: isActive ? `0 0 0 4px ${S.accent}22, 0 8px 18px rgba(0,0,0,0.08)` : (isDone ? `0 2px 6px ${S.success}18` : "none")}}>
                          <div style={{fontSize:22,lineHeight:1}}>{icon}</div>
                          <div style={{fontSize:9,fontFamily:"monospace",color:c,fontWeight:700,letterSpacing:"0.04em",textAlign:"center",lineHeight:1.3}}>{node.type || "?"}</div>
                          <div style={{fontSize:9,fontFamily:"monospace",color:S.text3,opacity:0.7,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:90}}>{nid}</div>
                          <div style={{fontSize:9,fontFamily:"monospace",fontWeight:700,color:c}}>{isDone ? "✓" : isActive ? "●" : "○"}</div>
                        </div>
                        {i < order.length - 1 && (
                          <div style={{width:24,height:2,background:isDone?S.success:S.border2,margin:"0 -1px",position:"relative",transition:"background 240ms ease"}}>
                            <div style={{position:"absolute",right:-4,top:-3,width:0,height:0,borderLeft:`6px solid ${isDone?S.success:S.border2}`,borderTop:"4px solid transparent",borderBottom:"4px solid transparent",transition:"border-color 240ms ease"}}/>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Final output card -- shown when walkthrough is complete. */}
                {complete && wfPreviewLoading && (
                  <div style={{padding:"14px 16px",borderRadius:10,background:S.inputBg,border:`1px dashed ${S.border2}`,fontSize:12,color:S.text3,textAlign:"center"}}>Loading latest result…</div>
                )}
                {complete && !wfPreviewLoading && !finalNode && (
                  <div style={{padding:"14px 16px",borderRadius:10,background:S.inputBg,border:`1px dashed ${S.border2}`,fontSize:12,color:S.text3,textAlign:"center",lineHeight:1.55}}>
                    <div style={{fontSize:24,marginBottom:6,opacity:0.7}}>📭</div>
                    No past successful runs yet -- click <strong style={{color:S.accent}}>▶ Run for real</strong> below to see live output.
                  </div>
                )}
                {complete && !wfPreviewLoading && finalNode && (
                  <div style={{padding:"12px 14px",borderRadius:10,background:`${S.accent}10`,border:`1.5px solid ${S.accent}55`}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                      <div style={{fontSize:10,color:S.accent,fontFamily:"monospace",fontWeight:700,letterSpacing:"0.05em"}}>LATEST RESULT · {finalNode.node_id} · {finalNode.type}</div>
                      <CopyMorphButton text={finalText} title="Copy latest result" copiedChildren="✓ Copied!" failedChildren="✗ Failed" style={{padding:"3px 10px",borderRadius:6,border:`1px solid ${S.accent}55`,background:S.accentDim,color:S.accent,fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"monospace"}}>📋 Copy</CopyMorphButton>
                    </div>
                    <div style={{margin:0,fontSize:13,color:S.text,lineHeight:1.65,maxHeight:280,overflow:"auto"}}>
                      {finalText ? <MsgContent text={finalText} /> : <span style={{color:S.text3,fontStyle:"italic"}}>(empty)</span>}
                    </div>
                    <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",marginTop:6}}>From run #{wfPreviewRun?.id} · {wfPreviewRun?.started_at ? new Date(wfPreviewRun.started_at).toLocaleString() : ""}</div>
                  </div>
                )}
              </Modal>
            );
          })()}

          {wfRunsFor && (
            <Modal open={!!wfRunsFor} title="Recent runs" onClose={()=>{setWfRunsFor(null);setWfRuns([]);setWfRunsStatusFilter("all");}} footer={<button onClick={()=>{setWfRunsFor(null);setWfRuns([]);setWfRunsStatusFilter("all");}} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${S.border2}`,background:"none",color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Close</button>}>
              {/* Phase 5: status filter pills. Counts per-status from the
                  loaded runs so the user can quickly drill into failures. */}
              {wfRuns.length > 0 && (() => {
                const counts = { all: wfRuns.length, success: 0, error: 0, cancelled: 0, running: 0 };
                for (const r of wfRuns) counts[r.status] = (counts[r.status] || 0) + 1;
                return (
                  <div style={{display:"flex",gap:5,marginBottom:10,flexWrap:"wrap"}}>
                    {[["all","All"],["success","✓ Success"],["error","✗ Error"],["cancelled","⛔ Cancelled"],["running","● Running"]].map(([k,label]) => (
                      <button key={k} onClick={()=>setWfRunsStatusFilter(k)} style={{padding:"4px 11px",borderRadius:10,border:`1px solid ${wfRunsStatusFilter===k?S.accent:S.border2}`,background:wfRunsStatusFilter===k?S.accentDim:S.inputBg,color:wfRunsStatusFilter===k?S.accent:S.text2,fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:wfRunsStatusFilter===k?700:500}}>{label} <span style={{opacity:0.7,marginLeft:3}}>{counts[k]||0}</span></button>
                    ))}
                  </div>
                );
              })()}
              {(() => {
                const filtered = wfRunsStatusFilter === "all" ? wfRuns : wfRuns.filter(r => r.status === wfRunsStatusFilter);
                if (wfRuns.length === 0) return (
                  <div style={{padding:30,textAlign:"center",color:S.text3,fontSize:12}}>No runs yet.</div>
                );
                if (filtered.length === 0) return (
                  <div style={{padding:30,textAlign:"center",color:S.text3,fontSize:12}}>No {wfRunsStatusFilter} runs.</div>
                );
                return (
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {filtered.map(r => {
                    const c = r.status === "success" ? S.success : r.status === "error" ? S.danger : S.text3;
                    return (
                      <button key={r.id} onClick={()=>wfOpenRunDetail(r.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                        <span style={{fontSize:9,padding:"2px 7px",borderRadius:10,fontFamily:"monospace",fontWeight:700,background:c+"22",color:c,border:`1px solid ${c}55`}}>{r.status.toUpperCase()}</span>
                        <span style={{flex:1,fontSize:11,fontFamily:"monospace",color:S.text2}}>{new Date(r.started_at).toLocaleString()}</span>
                        <span style={{fontSize:10,color:S.text3,fontFamily:"monospace"}}>{r.latency_ms||0}ms</span>
                        <span style={{fontSize:10,color:S.text3,fontFamily:"monospace"}}>{r.trigger}</span>
                      </button>
                    );
                  })}
                </div>
                );
              })()}
            </Modal>
          )}

          {/* Single-run detail modal */}
          {/* n8n-parity: import workflow JSON */}
          {wfImportOpen && (
            <Modal open={wfImportOpen} title="Import workflow JSON" onClose={()=>{setWfImportOpenLocal(false); setWfImportText("");}} footer={
              <>
                <button onClick={()=>{setWfImportOpenLocal(false); setWfImportText("");}} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${S.border2}`,background:"none",color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                <button onClick={wfImportSubmit} disabled={!wfImportText.trim()} className="cf-btn-lift" style={{padding:"7px 14px",borderRadius:8,border:"none",background:wfImportText.trim()?S.accent:S.border3,color:"#fff",fontSize:12,fontWeight:700,cursor:wfImportText.trim()?"pointer":"default",fontFamily:"inherit"}}>Import</button>
              </>
            }>
              <div style={{fontSize:12,color:S.text3,marginBottom:8,lineHeight:1.55}}>Paste a workflow JSON previously exported from any ChatFort workspace (chatfort_workflow_v1 format). Webhook tokens are NOT included in exports -- a fresh token is generated on import.</div>
              <textarea value={wfImportText} onChange={e=>setWfImportText(e.target.value)} spellCheck={false} placeholder={'{\n  "_kind": "chatfort_workflow_v1",\n  "name": "My Workflow",\n  "trigger_type": "manual",\n  "trigger_config": {},\n  "definition": { "nodes": [...], "edges": [...] }\n}'} style={{width:"100%",minHeight:240,padding:"10px 12px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.codeBlockBg,color:S.text,fontSize:11,fontFamily:"monospace",lineHeight:1.55}}/>
            </Modal>
          )}

          {/* Phase 4-B: starter template browse modal */}
          {wfTemplatesOpen && (
            <Modal open={wfTemplatesOpen} title="Workflow Templates" onClose={()=>setWfTemplatesOpen(false)} footer={
              <button onClick={()=>setWfTemplatesOpen(false)} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${S.border2}`,background:"none",color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
            }>
              {wfTemplatesLoading ? (
                <div style={{padding:"30px 0",textAlign:"center",color:S.text3,fontSize:12}}>Loading templates...</div>
              ) : wfTemplates.length === 0 ? (
                <div style={{padding:"30px 0",textAlign:"center",color:S.text3,fontSize:12}}>No templates available.</div>
              ) : (
                <div className="cf-grid-stagger" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:10}}>
                  {wfTemplates.map(tpl => (
                    <div key={tpl.id} style={{padding:14,background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:12,display:"flex",flexDirection:"column",gap:6}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:20}}>{tpl.icon || "📋"}</span>
                        <strong style={{fontSize:13,color:S.text}}>{tpl.name}</strong>
                      </div>
                      <div style={{fontSize:11,color:S.text3,lineHeight:1.5}}>{tpl.description}</div>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap",fontSize:10,color:S.text3,fontFamily:"monospace",marginTop:4}}>
                        {tpl.category && <span style={{padding:"1px 7px",borderRadius:10,background:S.inputBg,border:`1px solid ${S.border2}`}}>{tpl.category}</span>}
                        <span style={{padding:"1px 7px",borderRadius:10,background:S.inputBg,border:`1px solid ${S.border2}`}}>{(tpl.trigger_type==="schedule"?"🕐":tpl.trigger_type==="webhook"?"🔗":"▶")} {tpl.trigger_type}</span>
                        <span style={{padding:"1px 7px",borderRadius:10,background:S.inputBg,border:`1px solid ${S.border2}`}}>{tpl.node_count} node{tpl.node_count===1?"":"s"}</span>
                      </div>
                      <button onClick={()=>wfInstallTemplate(tpl.id)} style={{marginTop:6,padding:"6px 12px",borderRadius:7,border:`1px solid ${S.accent}55`,background:S.accentDim,color:S.accent,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ Install</button>
                    </div>
                  ))}
                </div>
              )}
            </Modal>
          )}

          {wfRunDetail && (
            <Modal open={!!wfRunDetail} title={"Run #" + wfRunDetail.id} onClose={()=>setWfRunDetail(null)} footer={
              <>
                {wfRunDetail.status === "running" && (
                  <button onClick={wfCancelRun} disabled={wfActionBusy} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${S.danger}55`,background:S.danger+"15",color:wfActionBusy?S.text3:S.danger,fontSize:12,fontWeight:600,cursor:wfActionBusy?"default":"pointer",fontFamily:"inherit"}}>{wfActionBusy?"...":"⛔ Cancel"}</button>
                )}
                {wfRunDetail.status && wfRunDetail.status !== "running" && (
                  <button onClick={wfRetryRun} disabled={wfActionBusy} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${S.accent}55`,background:S.accentDim,color:wfActionBusy?S.text3:S.accent,fontSize:12,fontWeight:600,cursor:wfActionBusy?"default":"pointer",fontFamily:"inherit"}}>{wfActionBusy?"...":"↻ Retry"}</button>
                )}
                <button onClick={()=>setWfRunDetail(null)} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${S.border2}`,background:"none",color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
              </>
            }>
              <div style={{display:"flex",gap:12,marginBottom:8,fontSize:11,color:S.text2,fontFamily:"monospace",flexWrap:"wrap",alignItems:"center"}}>
                <span>status: <strong style={{color:wfRunDetail.status==="success"?S.success:wfRunDetail.status==="error"?S.danger:wfRunDetail.status==="cancelled"?S.warning||"#f59e0b":S.text}}>{wfRunDetail.status}</strong></span>
                <span>latency: {wfRunDetail.latency_ms||0}ms</span>
                <span>trigger: {wfRunDetail.trigger}</span>
                <span style={{marginLeft:"auto"}}>
                  <a href={"/api/workflow_runs/" + wfRunDetail.id + "/export.json"} target="_blank" rel="noreferrer" style={{padding:"3px 9px",borderRadius:6,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:10,textDecoration:"none",fontFamily:"monospace",fontWeight:600}}>⬇ Export run</a>
                </span>
              </div>
              {/* Phase 5: progress bar. Counts completed steps (ok or error)
                  vs total. For a 'running' run with 2 of 5 steps done it
                  fills 40%. For terminal runs (success/error/cancelled) it
                  shows the final percentage, capped at 100%. */}
              {(() => {
                const log = Array.isArray(wfRunDetail.log) ? wfRunDetail.log : [];
                const completed = log.filter(s => s.status === "ok" || s.status === "error").length;
                const total = Math.max(completed + (wfRunDetail.status === "running" ? 1 : 0), log.length);
                if (total === 0) return null;
                const pct = Math.min(100, Math.round((completed / total) * 100));
                const barColor = wfRunDetail.status === "error" ? S.danger : wfRunDetail.status === "cancelled" ? (S.warning || "#f59e0b") : S.success;
                return (
                  <div style={{marginBottom:12}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>
                      <span>{completed} / {total} steps complete</span>
                      <span>{pct}%</span>
                    </div>
                    <div className="cf-wf-progress-bar" style={{height:6,borderRadius:4,background:S.inputBg,overflow:"hidden",border:`1px solid ${S.border2}`}}>
                      <div style={{width:pct+"%",height:"100%",background:barColor}}/>
                    </div>
                  </div>
                );
              })()}
              {wfRunDetail.error && <div style={{padding:"8px 12px",borderRadius:8,background:S.danger+"15",color:S.danger,fontSize:11,marginBottom:10,fontFamily:"monospace"}}>{wfRunDetail.error}</div>}
              {/* Phase 5: toast feed. Lists output.toast messages in
                  chronological order so the user can see what the run
                  surfaced even after the modal opened. Only rendered when
                  there are toasts to show. */}
              {Array.isArray(wfRunDetail.toasts) && wfRunDetail.toasts.length > 0 && (
                <div style={{marginBottom:12,padding:"8px 10px",borderRadius:8,background:S.inputBg,border:`1px dashed ${S.border2}`}}>
                  <div style={{fontSize:9,fontWeight:700,color:S.text3,fontFamily:"monospace",letterSpacing:"0.05em",marginBottom:6,textTransform:"uppercase"}}>Toasts emitted ({wfRunDetail.toasts.length})</div>
                  <div style={{display:"flex",flexDirection:"column",gap:4}}>
                    {wfRunDetail.toasts.map((t, i) => (
                      <div key={i} style={{display:"flex",gap:8,fontSize:11,color:S.text2,fontFamily:"monospace",alignItems:"flex-start"}}>
                        <span style={{color:S.text3,flexShrink:0}}>{t.nodeId||"-"}</span>
                        <span style={{flex:1,wordBreak:"break-word"}}>{t.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Phase 5: collapsible variable inspector. Renders
                  ctx.variables (set by logic.setVar nodes) so the user
                  can confirm a variable was set before the next node ran. */}
              {wfRunDetail.variables && typeof wfRunDetail.variables === "object" && Object.keys(wfRunDetail.variables).length > 0 && (
                <div style={{marginBottom:12,padding:"8px 10px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`}}>
                  <div onClick={()=>setWfDetailVarsOpen(v=>!v)} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:10,fontWeight:700,color:S.text3,fontFamily:"monospace",letterSpacing:"0.05em",textTransform:"uppercase"}}>
                    <span style={{display:"inline-block",transform:wfDetailVarsOpen?"rotate(90deg)":"rotate(0deg)",transition:"transform 180ms ease"}}>▶</span>
                    Variables ({Object.keys(wfRunDetail.variables).length})
                  </div>
                  {wfDetailVarsOpen && (
                    <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:4}}>
                      {Object.entries(wfRunDetail.variables).map(([k, v]) => (
                        <div key={k} style={{display:"flex",gap:8,fontSize:11,color:S.text2,fontFamily:"monospace",alignItems:"flex-start"}}>
                          <span style={{color:S.accent,fontWeight:700,minWidth:80}}>{k}</span>
                          <span style={{flex:1,wordBreak:"break-word",whiteSpace:"pre-wrap"}}>{typeof v === "string" ? v : JSON.stringify(v)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {(() => {
                // FINAL OUTPUT callout. Distinct card above STEPS that
                // shows the last successful step's full output, with a
                // Copy button. If NO successful steps exist (all errored),
                // render in muted state with the last error message
                // instead. Always renders -- never silently absent.
                const allSteps = Array.isArray(wfRunDetail.log) ? wfRunDetail.log : [];
                const outputs = wfRunDetail.outputs || {};
                let lastOk = null;
                for (let i = allSteps.length - 1; i >= 0; i--) {
                  if (allSteps[i].status === "ok") { lastOk = allSteps[i]; break; }
                }
                if (lastOk) {
                  const out = outputs[lastOk.node_id];
                  const text = (out == null) ? "" :
                    (typeof out === "string" ? out : JSON.stringify(out, null, 2));
                  return (
                    <div style={{marginBottom:14,padding:"12px 14px",borderRadius:10,background:`${S.accent}10`,border:`1.5px solid ${S.accent}55`}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                        <div style={{fontSize:10,color:S.accent,fontFamily:"monospace",fontWeight:700,letterSpacing:"0.05em"}}>FINAL OUTPUT · {lastOk.node_id} · {lastOk.type}</div>
                        <CopyMorphButton text={text} title="Copy final output" copiedChildren="✓ Copied!" failedChildren="✗ Failed" style={{padding:"3px 10px",borderRadius:6,border:`1px solid ${S.accent}55`,background:S.accentDim,color:S.accent,fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"monospace"}}>📋 Copy</CopyMorphButton>
                      </div>
                      <pre style={{margin:0,fontSize:11.5,color:S.text,lineHeight:1.6,fontFamily:"monospace",whiteSpace:"pre-wrap",wordBreak:"break-word",maxHeight:384,overflow:"auto"}}>{text || "(empty)"}</pre>
                    </div>
                  );
                }
                // Muted "NO FINAL OUTPUT" state -- no successful step.
                const lastErr = wfRunDetail.error || (allSteps.length ? (allSteps[allSteps.length-1].error || "(no error message)") : "(no steps executed)");
                return (
                  <div style={{marginBottom:14,padding:"12px 14px",borderRadius:10,background:S.surface||S.cardBg,border:`1.5px dashed ${S.border2}`,opacity:0.85}}>
                    <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",fontWeight:700,letterSpacing:"0.05em",marginBottom:6}}>NO FINAL OUTPUT</div>
                    <div style={{fontSize:11.5,color:S.danger,fontFamily:"monospace",whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{lastErr}</div>
                  </div>
                );
              })()}
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",flex:1}}>STEPS</div>
                <input value={wfDetailSearch} onChange={e=>setWfDetailSearch(e.target.value)} placeholder="🔍 Search outputs…" style={{flex:"0 0 220px",padding:"5px 10px",borderRadius:7,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text,fontSize:11,fontFamily:"inherit"}}/>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {(Array.isArray(wfRunDetail.log) ? wfRunDetail.log : []).map((step, i, logArr) => {
                  const c = step.status === "ok" ? S.success : step.status === "error" ? S.danger : S.text3;
                  const outs = wfRunDetail.outputs || {};
                  const hasOutput = (step.node_id in outs);
                  let displayText = "";
                  if (hasOutput) {
                    const out = outs[step.node_id];
                    displayText = (out == null) ? "" :
                      (typeof out === "string" ? out : JSON.stringify(out, null, 2));
                  }
                  // Phase 5: search filter -- matches output, node_id, type, error.
                  if (wfDetailSearch) {
                    const q = wfDetailSearch.toLowerCase();
                    const hay = ((step.node_id||"") + " " + (step.type||"") + " " + (step.error||"") + " " + displayText).toLowerCase();
                    if (!hay.includes(q)) return null;
                  }
                  // Phase 5: is this the currently-running step? It's the
                  // LAST entry in the log AND the overall run is 'running'
                  // AND this step's own status hasn't transitioned yet.
                  const isRunning = wfRunDetail.status === "running" && step.status === "running" && i === logArr.length - 1;
                  // Phase 5: output_type badge (server-tagged).
                  const typeBadge = step.output_type || (typeof outs[step.node_id]);
                  // Phase 5: latency. Server stamps step.latency_ms on
                  // completion; fall back to 0 for running / legacy.
                  const lat = step.latency_ms || 0;
                  return (
                    <div key={i} className={"cf-wf-step-enter" + (isRunning ? " cf-wf-step-pulse" : "")} style={{padding:"8px 10px",borderRadius:8,background:S.inputBg,border:`1px solid ${isRunning ? S.accent + "55" : S.border2}`,animationDelay: (Math.min(i, 6) * 30) + "ms"}}>
                      <div style={{display:"flex",gap:8,alignItems:"center",fontSize:11,fontFamily:"monospace",flexWrap:"wrap"}}>
                        <span style={{fontSize:9,padding:"1px 6px",borderRadius:8,fontWeight:700,background:c+"22",color:c,flexShrink:0}}>{(step.status||"?").toUpperCase()}</span>
                        <span style={{color:S.text3}}>{step.node_id||"-"}</span>
                        <span style={{color:S.text}}>{step.type||"?"}</span>
                        {step.status === "ok" && (
                          <>
                            <span style={{fontSize:9,padding:"1px 6px",borderRadius:8,background:S.text3+"15",color:S.text3,fontFamily:"monospace",marginLeft:4}}>{typeBadge}</span>
                            {lat > 0 && <span style={{fontSize:9,padding:"1px 6px",borderRadius:8,background:S.text3+"15",color:S.text3,fontFamily:"monospace"}}>{lat}ms</span>}
                          </>
                        )}
                        {displayText && (
                          <span style={{marginLeft:"auto"}}>
                            <CopyMorphButton text={displayText} title="Copy step output" copiedChildren="✓" failedChildren="✗" style={{padding:"2px 7px",borderRadius:5,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text3,fontSize:9,cursor:"pointer",fontFamily:"monospace",fontWeight:700}}>📋</CopyMorphButton>
                          </span>
                        )}
                      </div>
                      {step.error && (
                        <div style={{marginTop:5,padding:"6px 8px",borderRadius:5,background:S.danger+"15",color:S.danger,fontSize:11,fontFamily:"monospace",wordBreak:"break-word"}}>
                          ✗ {step.error}
                        </div>
                      )}
                      {displayText && (
                        <pre style={{margin:"5px 0 0",padding:"6px 9px",borderRadius:5,background:S.codeBlockBg,border:`1px solid ${S.border}`,color:S.text,fontSize:10.5,lineHeight:1.5,fontFamily:"monospace",whiteSpace:"pre-wrap",wordBreak:"break-word",maxHeight:384,overflow:"auto"}}>{displayText}</pre>
                      )}
                    </div>
                  );
                })}
              </div>
            </Modal>
          )}

          {/* ── PLATFORMS TAB ── */}
          {tab==="platforms" && (
            <div>
              {/* Platform picker */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:20}}>
                {Object.entries(PLATFORMS).map(([id,p])=>(
                  <div key={id} onClick={()=>setActivePlat(id)}
                    style={{background:activePlat===id?`${p.color}14`:S.cardBg,border:`1px solid ${activePlat===id?p.color+"55":S.border2}`,borderRadius:12,padding:"12px 14px",cursor:"pointer",transition:"all 0.15s",borderLeft:activePlat===id?`3px solid ${p.color}`:undefined}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                      <span style={{fontSize:18}}>{p.icon}</span>
                      <span style={{fontSize:13,fontWeight:700,color:activePlat===id?p.color:S.text}}>{p.name}</span>
                      {p.selfHost&&<span style={{fontSize:8,padding:"1px 5px",borderRadius:3,fontFamily:"monospace",fontWeight:700,background:`${S.success}18`,color:S.success,border:`1px solid ${S.success}30`,marginLeft:"auto"}}>Self-host</span>}
                    </div>
                    <div style={{fontSize:10,color:S.text3,lineHeight:1.5}}>{p.badge}</div>
                  </div>
                ))}
              </div>

              {/* Platform detail */}
              <div style={{background:S.cardBg,border:`1px solid ${plat.color}44`,borderLeft:`4px solid ${plat.color}`,borderRadius:14,overflow:"hidden",marginBottom:16}}>
                <div style={{padding:"16px 20px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"flex-start",gap:14}}>
                  <div style={{width:48,height:48,borderRadius:14,background:`${plat.color}18`,border:`1px solid ${plat.color}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>{plat.icon}</div>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <span style={{fontSize:16,fontWeight:800,color:S.text}}>{plat.name}</span>
                      <span style={{fontSize:9,padding:"2px 8px",borderRadius:10,fontFamily:"monospace",fontWeight:700,background:`${plat.color}15`,color:plat.color,border:`1px solid ${plat.color}30`}}>{plat.badge}</span>
                    </div>
                    <p style={{fontSize:13,color:S.text2,lineHeight:1.65,margin:0}}>{plat.desc}</p>
                  </div>
                  <a href={plat.docs} target="_blank" rel="noreferrer" style={{padding:"7px 13px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text3,fontSize:11,textDecoration:"none",fontFamily:"monospace",whiteSpace:"nowrap",flexShrink:0}}>Docs ↗</a>
                </div>

                {/* Use cases */}
                <div style={{padding:"14px 20px",borderBottom:`1px solid ${S.border}`,background:S.surface2}}>
                  <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",color:S.text3,fontFamily:"monospace",letterSpacing:"0.07em",marginBottom:8}}>Common Use Cases</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {plat.useCases.map((u,i)=>(
                      <div key={i} style={{fontSize:11,padding:"4px 10px",borderRadius:20,background:S.cardBg,border:`1px solid ${S.border2}`,color:S.text2,display:"flex",alignItems:"center",gap:5}}>
                        <span style={{color:plat.color,fontSize:10}}>▸</span>{u}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Setup steps */}
                <div style={{padding:"16px 20px"}}>
                  <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",color:S.text3,fontFamily:"monospace",letterSpacing:"0.07em",marginBottom:12}}>Setup Guide</div>
                  {plat.steps.map((step,i)=>(
                    <div key={i} style={{marginBottom:i<plat.steps.length-1?18:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                        <div style={{width:20,height:20,borderRadius:"50%",background:`${plat.color}18`,border:`1px solid ${plat.color}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:plat.color,flexShrink:0}}>{i+1}</div>
                        <span style={{fontSize:12,fontWeight:600,color:S.text}}>{step.title}</span>
                      </div>
                      <CmdBlock id={`${activePlat}-step-${i}`} code={step.code}/>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick compare table */}
              <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
                <div style={{padding:"13px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>Platform Comparison</div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                    <thead>
                      <tr style={{background:S.surface2}}>
                        {["Platform","Type","Self-host","Skill level","Best for"].map(h=>(
                          <th key={h} style={{padding:"9px 14px",textAlign:"left",fontSize:9,fontWeight:700,textTransform:"uppercase",color:S.text3,fontFamily:"monospace",letterSpacing:"0.06em",whiteSpace:"nowrap"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["⚡ n8n","Visual automation","✓ Yes","Low","Self-hosted pipelines, regulated data"],
                        ["🔧 Retool","Internal tools","Partial","Low–Med","Custom dashboards, review queues"],
                        ["⚡ Zapier","No-code","✗ Cloud","None","Fast prototyping, SaaS integrations"],
                        ["🔄 Make","Visual + logic","Partial","Medium","Complex branching, batch workflows"],
                        ["🦜 LangChain","Code (Python/JS)","✓ Yes","High","Custom agents, RAG, full control"],
                        ["🧩 Activepieces","Visual automation","✓ Yes","Low","n8n alternative, open source"],
                      ].map(([name,...cells],i,arr)=>(
                        <tr key={name} style={{borderTop:`1px solid ${S.border}`,background:i%2===0?"none":S.surface2+"66"}}>
                          <td style={{padding:"9px 14px",fontWeight:600,color:S.text,whiteSpace:"nowrap"}}>{name}</td>
                          {cells.map((c,j)=>(
                            <td key={j} style={{padding:"9px 14px",color:c==="✓ Yes"?S.success:c==="✗ Cloud"?S.text3:S.text2,fontFamily:c==="✓ Yes"||c==="✗ Cloud"?"monospace":"inherit",fontWeight:c==="✓ Yes"||c==="✗ Cloud"?600:400}}>{c}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── PATTERNS TAB ── */}
          {tab==="patterns" && (
            <div>
              <div style={{background:`${S.accent}08`,border:`1px solid ${S.border2}`,borderRadius:12,padding:"12px 16px",marginBottom:18,fontSize:12,color:S.text2,lineHeight:1.7}}>
                These patterns work across all platforms. Pick the one that fits your use case and map it to your chosen tool.
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
                {WORKFLOW_PATTERNS.map(p=>(
                  <div key={p.title} style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:13,padding:"16px 18px"}}>
                    <div style={{fontSize:22,marginBottom:10}}>{p.icon}</div>
                    <div style={{fontSize:13,fontWeight:700,color:S.text,marginBottom:5}}>{p.title}</div>
                    <div style={{fontSize:12,color:S.text2,lineHeight:1.65,marginBottom:10}}>{p.desc}</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                      {p.tools.map(t=>(
                        <span key={t} onClick={()=>{setActivePlat(Object.keys(PLATFORMS).find(k=>PLATFORMS[k].name===t||k===t.toLowerCase().replace(/[^a-z]/g,""))||"n8n");setTab("platforms");}} style={{fontSize:9,padding:"2px 7px",borderRadius:3,fontFamily:"monospace",background:S.surface2,border:`1px solid ${S.border2}`,color:S.accent,cursor:"pointer",fontWeight:600}}>{t}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Sample workflow diagram */}
              <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
                <div style={{padding:"13px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>Sample: Legal Document Intake Pipeline</div>
                <div style={{padding:20}}>
                  <div style={{display:"flex",alignItems:"center",gap:0,overflowX:"auto",paddingBottom:8}}>
                    {[
                      {icon:"📧",label:"Email / Upload",sub:"Trigger"},
                      {icon:"📄",label:"Extract Text",sub:"PDF / DOCX"},
                      {icon:"🤖",label:"Claude",sub:"Classify + Extract"},
                      {icon:"🔀",label:"Branch",sub:"By doc type"},
                      {icon:"📋",label:"Log to DB",sub:"Structured fields"},
                      {icon:"🔔",label:"Notify",sub:"Slack / Email"},
                    ].map((step,i,arr)=>(
                      <div key={i} style={{display:"flex",alignItems:"center",flexShrink:0}}>
                        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                          <div style={{width:52,height:52,borderRadius:14,background:S.accentDim,border:`1px solid ${S.border2}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>{step.icon}</div>
                          <div style={{fontSize:11,fontWeight:600,color:S.text,textAlign:"center",whiteSpace:"nowrap"}}>{step.label}</div>
                          <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",textAlign:"center"}}>{step.sub}</div>
                        </div>
                        {i<arr.length-1&&<div style={{display:"flex",alignItems:"center",padding:"0 6px",marginBottom:16}}><div style={{width:20,height:1.5,background:S.border3}}/><svg width="8" height="8" viewBox="0 0 24 24" fill={S.border3}><polygon points="12,0 24,12 12,24 12,16 0,16 0,8 12,8"/></svg></div>}
                      </div>
                    ))}
                  </div>
                  <div style={{marginTop:16,fontSize:11,color:S.text2,lineHeight:1.7,background:S.surface2,borderRadius:9,padding:"10px 13px"}}>
                    <strong style={{color:S.text}}>Claude's role:</strong> Receives extracted text, classifies document type (contract / brief / correspondence / other), extracts parties, dates, and key terms, returns structured JSON. Downstream nodes branch on doc type and write to the appropriate matter record.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── DIRECT API TAB ── */}
          {tab==="api" && (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div style={{background:`${S.accent}08`,border:`1px solid ${S.border2}`,borderRadius:12,padding:"13px 17px",fontSize:12,color:S.text2,lineHeight:1.7}}>
                Don't need a platform? Call the Claude API directly from your own code, cron job, or serverless function. Full control, no third-party dependency.
              </div>

              <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
                <div style={{padding:"13px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>🐍 Python — Direct API call</div>
                <div style={{padding:18}}>
                  <CmdBlock id="py-install" code="pip install anthropic" label="Install"/>
                  <CmdBlock id="py-basic" label="Basic call" code={[
                    "import anthropic",
                    "",
                    'client = anthropic.Anthropic(api_key="sk-ant-api03-…")',
                    "",
                    "message = client.messages.create(",
                    '    model="claude-sonnet-4-20250514",',
                    "    max_tokens=1024,",
                    '    system="You are a legal document analyst.",',
                    '    messages=[{"role": "user", "content": document_text}]',
                    ")",
                    "",
                    "print(message.content[0].text)",
                  ].join("\n")}/>
                  <CmdBlock id="py-batch" label="Batch processing" code={[
                    "import anthropic, json",
                    "",
                    "client = anthropic.Anthropic()",
                    "results = []",
                    "",
                    "for doc in documents:",
                    "    resp = client.messages.create(",
                    '        model="claude-sonnet-4-20250514",',
                    "        max_tokens=512,",
                    '        system="Extract: type, parties, date. Return JSON only.",',
                    '        messages=[{"role": "user", "content": doc["text"]}]',
                    "    )",
                    "    data = json.loads(resp.content[0].text)",
                    '    results.append({"id": doc["id"], **data})',
                    "",
                    "# Write to your database, spreadsheet, etc.",
                  ].join("\n")}/>
                </div>
              </div>

              <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
                <div style={{padding:"13px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>🟨 JavaScript / Node.js</div>
                <div style={{padding:18}}>
                  <CmdBlock id="js-install" code={"npm install @anthropic-" + "ai/sdk"} label="Install"/>
                  <CmdBlock id="js-basic" label="Basic async call" code={
                    ["const { Anthropic } = require(\"@anthropic-\" + \"ai/sdk\");",
                     "// or: import Anthropic ... (ESM)",
                     "",
                     "const client = new Anthropic({ apiKey: process.env.ANTHROPIC_API_KEY });",
                     "",
                     "async function processDocument(text) {",
                     "  const msg = await client.messages.create({",
                     "    model: \"claude-sonnet-4-20250514\",",
                     "    max_tokens: 1024,",
                     "    system: \"You are a compliance analyst.\",",
                     "    messages: [{ role: \"user\", content: text }],",
                     "  });",
                     "  return msg.content[0].text;",
                     "}",
                     "",
                     "// Use in Express, Next.js API route, Lambda, etc."].join("\n")
                  }/>
                </div>
              </div>

              <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
                <div style={{padding:"13px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>🌐 cURL — Test any workflow step</div>
                <div style={{padding:18}}>
                  <CmdBlock id="curl-basic" label="Basic request" code={[
                    "curl https://api.anthropic.com/v1/messages \\",
                    '  -H "x-api-key: $ANTHROPIC_API_KEY" \\',
                    '  -H "anthropic-version: 2023-06-01" \\',
                    '  -H "content-type: application/json" \\',
                    "  -d '{",
                    '    "model": "claude-sonnet-4-20250514",',
                    '    "max_tokens": 1024,',
                    '    "system": "You are a document analyst.",',
                    '    "messages": [{"role":"user","content":"Summarize this..."}]',
                    "  }'",
                  ].join("\n")}/>
                </div>
              </div>

              <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
                <div style={{padding:"13px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>🔗 Webhook receiver pattern</div>
                <div style={{padding:18}}>
                  <div style={{fontSize:12,color:S.text2,lineHeight:1.7,marginBottom:12}}>Expose a POST endpoint. Your automation platform (n8n, Zapier, Make) calls it with a payload. Claude processes and returns structured data.</div>
                  <CmdBlock id="webhook" label="Node.js webhook receiver" code={[
                     "// server.js",
                     'const express = require("express");',
                     'const Anthropic = require("@anthropic-" + "ai/sdk");',
                     "",
                     "const app = express();",
                     "const client = new Anthropic();",
                     "app.use(express.json());",
                     "",
                     'app.post("/process", async (req, res) => {',
                     "  const { document, type } = req.body;",
                     "  const msg = await client.messages.create({",
                     '    model: "claude-sonnet-4-20250514",',
                     "    max_tokens: 1024,",
                     '    system: "You process " + type + " documents. Return JSON only.",',
                     '    messages: [{ role: "user", content: document }],',
                     "  });",
                     "  const result = JSON.parse(msg.content[0].text);",
                     "  res.json({ success: true, data: result });",
                     "});",
                     "",
                     'app.listen(3000);',
                   ].join("\n")}/>
                </div>
              </div>

              <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
                <div style={{padding:"13px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>📚 Resources</div>
                <div style={{padding:"12px 18px",display:"flex",flexDirection:"column"}}>
                  {[
                    ["Anthropic API reference","https://docs.anthropic.com/en/api","📖"],
                    ["Python SDK","https://github.com/anthropics/anthropic-sdk-python","🐍"],
                    ["TypeScript SDK","https://github.com/anthropics/anthropic-sdk-typescript","🟨"],
                    ["Prompt engineering guide","https://docs.anthropic.com/en/docs/build-with-claude/prompt-engineering/overview","✏️"],
                    ["Tool use / function calling","https://docs.anthropic.com/en/docs/build-with-claude/tool-use","🔧"],
                    ["Batch API (async processing)","https://docs.anthropic.com/en/docs/build-with-claude/batch-processing","⚡"],
                  ].map(([label,url,icon],i,arr)=>(
                    <a key={label} href={url} target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",gap:10,padding:"11px 0",borderBottom:i<arr.length-1?`1px solid ${S.border}`:"none",textDecoration:"none",color:S.text,transition:"color 0.15s"}}
                      onMouseEnter={e=>e.currentTarget.style.color=S.accent}
                      onMouseLeave={e=>e.currentTarget.style.color=S.text}>
                      <span style={{fontSize:14,flexShrink:0}}>{icon}</span>
                      <span style={{fontSize:13,fontWeight:500,flex:1}}>{label}</span>
                      <span style={{color:S.text3,fontSize:11}}>↗</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Code mode page ───────────────────────────────────────────────
  // Polished in-app code workspace -- VS Code + Codex inspired.
  // Features: multi-tab editor, line numbers, status bar, find-in-file,
  // quick file switcher, command palette, rename, markdown preview,
  // JSON format, workspace download, drag-drop import, file icons,
  // 3 themes, word-wrap, fullscreen editor, auto-save.
  function TerminalPage({showToast, setSection}) {
    const STORAGE_KEY = "cf_terminal_session_v1";
    const [files, setFiles] = useState(() => {
      try { return JSON.parse(localStorage.getItem(STORAGE_KEY+"_files") || "[]"); } catch { return []; }
    });
    const [activeFileId, setActiveFileId] = useState(null);
    const [openTabs, setOpenTabs] = useState(() => {
      try { return JSON.parse(localStorage.getItem(STORAGE_KEY+"_tabs") || "[]"); } catch { return []; }
    });
    const [showFiles, setShowFiles] = useState(true);
    const [showTranscript, setShowTranscript] = useState(true);
    const [showEditor, setShowEditor] = useState(false);
    // Compact Code mode: show one of Prompt / Files / Editor at a time
    // behind a tab bar so medium screens, high zoom, and mobile do not squeeze
    // the editor into an unreadable strip beside the workspace rail/transcript.
    const [termIsMobile, setTermIsMobile] = useState(() => { try { return typeof window !== "undefined" && window.matchMedia("(max-width: 1100px)").matches; } catch { return false; } });
    const [termTab, setTermTab] = useState("terminal"); // terminal | files | editor
    useEffect(() => {
      if (typeof window === "undefined" || !window.matchMedia) return;
      const mq = window.matchMedia("(max-width: 1100px)");
      const on = () => setTermIsMobile(mq.matches);
      on();
      try { mq.addEventListener("change", on); return () => mq.removeEventListener("change", on); }
      catch { mq.addListener(on); return () => mq.removeListener(on); }
    }, []);
    const [messages, setMessages] = useState(() => {
      try { return JSON.parse(localStorage.getItem(STORAGE_KEY+"_messages") || "[]"); } catch { return []; }
    });
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [showCmdPalette, setShowCmdPalette] = useState(false);
    const [showQuickSwitcher, setShowQuickSwitcher] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [findOpen, setFindOpen] = useState(false);
    const [findQuery, setFindQuery] = useState("");
    const [quickQuery, setQuickQuery] = useState("");
    const [editorTheme, setEditorTheme] = useState(() => { try { return localStorage.getItem("cf_terminal_theme") || "auto"; } catch { return "auto"; } });
    const [wordWrap, setWordWrap] = useState(true);
    const [mdPreview, setMdPreview] = useState(false);
    const [preview, setPreview] = useState(null); // HTML/SVG string to render in the preview overlay
    // True when a code block (or file) should offer a live preview --
    // matches by language tag or by sniffing the content for an HTML/
    // SVG root.
    function isPreviewable(lang, code) {
      const l = String(lang || "").toLowerCase();
      if (["html", "htm", "svg", "xml", "vue", "svelte"].includes(l)) return true;
      const c = String(code || "").trim().toLowerCase();
      return c.startsWith("<!doctype") || c.startsWith("<html") || c.includes("<body") || c.startsWith("<svg") || (c.startsWith("<") && c.includes("</") && /<(div|section|main|header|h1|p|button|table|ul)\b/.test(c));
    }
    const [cursorPos, setCursorPos] = useState({line:1,col:1});
    const [savedAt, setSavedAt] = useState(null);
    const [renaming, setRenaming] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const [isDragging, setIsDragging] = useState(false);
    const [termModelOpen, setTermModelOpen] = useState(false);
    const [termModel, setTermModel] = useState(() => {
      try { return localStorage.getItem("cf_terminal_model") || localStorage.getItem("cf_hermes_model") || activeModel; } catch { return activeModel; }
    });
    // ── Agent mode (Terminal v2) ──────────────────────────────────
    // ON: the model drives the workspace itself via the cf-tool action
    // protocol and loops until done. OFF: classic single-shot chat with
    // manual /apply.
    const [agentMode, setAgentMode] = useState(() => { try { return localStorage.getItem("cf_terminal_agent_mode") !== "0"; } catch { return true; } });
    const stopRef = useRef(false);
    const filesRef = useRef(files);
    // Slice 2: change review + checkpoints. pendingReview persists so a
    // mid-review App re-render (toast -> in-App remount) can't eat it.
    const [pendingReview, setPendingReview] = useState(() => { try { return JSON.parse(localStorage.getItem("cf_terminal_pending_review") || "null"); } catch { return null; } });
    const [diffOpen, setDiffOpen] = useState({});
    const [showCheckpoints, setShowCheckpoints] = useState(false);
    // Slice 4: editor syntax highlighting (tokenized <pre> underlay).
    const [hlOn, setHlOn] = useState(() => { try { return localStorage.getItem("cf_terminal_highlight") !== "0"; } catch { return true; } });
    const hlRef = useRef(null);
    // Rail menu state (PR for the four-options sidebar).
    const [routines, setRoutines] = useState(() => { try { return JSON.parse(localStorage.getItem("cf_terminal_routines") || "[]"); } catch { return []; } });
    const [showRoutinesPanel, setShowRoutinesPanel] = useState(false);
    const [routineDraft, setRoutineDraft] = useState({ name: "", prompt: "" });
    const [routineEditId, setRoutineEditId] = useState(null);
    const [showCustomize, setShowCustomize] = useState(false);
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const [fontSize, setFontSize] = useState(() => { try { return parseInt(localStorage.getItem("cf_terminal_font_size") || "12", 10) || 12; } catch { return 12; } });
    const [tabSize, setTabSize] = useState(() => { try { return parseInt(localStorage.getItem("cf_terminal_tab_size") || "2", 10) || 2; } catch { return 2; } });
    const importFileRef = useRef(null);
    useEffect(() => { try { localStorage.setItem("cf_terminal_routines", JSON.stringify(routines)); } catch {} }, [routines]);
    useEffect(() => { try { localStorage.setItem("cf_terminal_font_size", String(fontSize)); } catch {} }, [fontSize]);
    useEffect(() => { try { localStorage.setItem("cf_terminal_tab_size", String(tabSize)); } catch {} }, [tabSize]);
    const scrollerRef = useRef(null);
    const taRef = useRef(null);
    const editorRef = useRef(null);
    const findInputRef = useRef(null);
    const lineNumbersRef = useRef(null);

    useEffect(() => { try { localStorage.setItem(STORAGE_KEY+"_files", JSON.stringify(files)); setSavedAt(Date.now()); } catch {} }, [files]);
    useEffect(() => { try { localStorage.setItem(STORAGE_KEY+"_messages", JSON.stringify(messages.slice(-100))); } catch {} }, [messages]);
    useEffect(() => { try { localStorage.setItem(STORAGE_KEY+"_tabs", JSON.stringify(openTabs)); } catch {} }, [openTabs]);
    useEffect(() => { try { localStorage.setItem("cf_terminal_model", termModel); } catch {} }, [termModel]);
    useEffect(() => { try { localStorage.setItem("cf_terminal_agent_mode", agentMode ? "1" : "0"); } catch {} }, [agentMode]);
    useEffect(() => { filesRef.current = files; }, [files]);
    useEffect(() => { try { if (pendingReview) localStorage.setItem("cf_terminal_pending_review", JSON.stringify(pendingReview)); else localStorage.removeItem("cf_terminal_pending_review"); } catch {} }, [pendingReview]);
    useEffect(() => { try { localStorage.setItem("cf_terminal_highlight", hlOn ? "1" : "0"); } catch {} }, [hlOn]);
    useEffect(() => { try { localStorage.setItem("cf_terminal_theme", editorTheme); } catch {} }, [editorTheme]);
    useEffect(() => { const el = scrollerRef.current; if (el) el.scrollTop = el.scrollHeight; }, [messages]);

    const activeFile = files.find(f => f.id === activeFileId) || null;
    const BT = String.fromCharCode(96);
    const FENCE = BT + BT + BT;
    const monoStack = "JetBrains Mono, Fira Code, Cascadia Mono, Consolas, monospace";

    // ── Theme palettes ─────────────────────────────────────────────
    const THEMES = {
      midnight: { bg:"#0c0f14", chrome:"#070a0f", panel:"#0a0d12", text:"#e6edf3", text2:"#9ba9b9", dim:"#1c2230", active:"#142233", accent:S.accent, gutter:"#5a6573", gutterBg:"#0a0d12" },
      ocean:    { bg:"#0d1b2a", chrome:"#06121e", panel:"#0c1928", text:"#e0ecf7", text2:"#8da4bc", dim:"#1e3349", active:"#163554", accent:"#5ed3f3", gutter:"#5d7a93", gutterBg:"#0c1928" },
      paper:    { bg:"#f8f6ef", chrome:"#efece2", panel:"#f0ede0", text:"#1d1d1d", text2:"#5a5a5a", dim:"#d9d5c4", active:"#e8e3d2", accent:"#0066cc", gutter:"#9e958a", gutterBg:"#f0ede0" },
    };
    // "auto" derives directly from the app theme tokens (S.bg etc.)
    // so Code mode matches every other page exactly when the user
    // flips the app to light mode -- no second theme to keep in sync.
    const T = (() => {
      if (editorTheme === "auto") {
        if (theme === "light") {
          return {
            bg: S.bg,
            chrome: S.surface2 || S.inputBg || S.cardBg,
            panel: S.cardBg,
            text: S.text,
            text2: S.text2,
            dim: S.border2,
            active: S.accentDim,
            accent: S.accent,
            gutter: S.text3,
            gutterBg: S.surface2 || S.inputBg || S.cardBg,
          };
        }
        // Dark "auto" reuses midnight -- already tuned to feel native
        // on dark, and the app's dark palette already matches it.
        return THEMES.midnight;
      }
      return THEMES[editorTheme] || THEMES.midnight;
    })();
    // Token palette selector for the syntax highlighter.
    const isLightEditor = editorTheme === "paper" || (editorTheme === "auto" && theme === "light");

    // ── Helpers ────────────────────────────────────────────────────
    function detectLang(name) {
      const ext = String(name||"").split(".").pop().toLowerCase();
      const map = { js:"javascript", jsx:"jsx", ts:"typescript", tsx:"tsx", py:"python", rb:"ruby", go:"go", rs:"rust", java:"java", kt:"kotlin", swift:"swift", c:"c", cpp:"cpp", h:"c", hpp:"cpp", cs:"csharp", php:"php", sh:"bash", yml:"yaml", yaml:"yaml", json:"json", html:"html", css:"css", scss:"scss", md:"markdown", sql:"sql", xml:"xml", toml:"toml", env:"bash" };
      return map[ext] || "text";
    }
    function langExt(lang) {
      const map = { javascript:"js", jsx:"jsx", typescript:"ts", tsx:"tsx", python:"py", ruby:"rb", go:"go", rust:"rs", java:"java", kotlin:"kt", swift:"swift", c:"c", cpp:"cpp", csharp:"cs", php:"php", bash:"sh", shell:"sh", yaml:"yml", json:"json", html:"html", css:"css", scss:"scss", markdown:"md", sql:"sql" };
      return map[String(lang||"").toLowerCase()] || "txt";
    }
    function makeId() { return "f-" + Math.random().toString(36).slice(2, 9); }
    function pushMsg(m) { setMessages(p => [...p, Object.assign({ id: "m-"+Math.random().toString(36).slice(2,9), time: Date.now() }, m)]); }
    function validateFilename(name) {
      const n = String(name || "").trim();
      if (!n) return "Filename required.";
      if (n.length > 60) return "Invalid filename. Use letters, numbers, dot, dash, underscore. Max 60 chars.";
      if (n.includes("..") || n.includes("/") || n.includes("\\")) return "Invalid filename. Use letters, numbers, dot, dash, underscore. Max 60 chars.";
      if (n.startsWith(".")) return "Invalid filename. Use letters, numbers, dot, dash, underscore. Max 60 chars.";
      for (let i = 0; i < n.length; i++) if (n.charCodeAt(i) < 32) return "Invalid filename. Use letters, numbers, dot, dash, underscore. Max 60 chars.";
      if (!/^[a-zA-Z0-9_.-]+$/.test(n)) return "Invalid filename. Use letters, numbers, dot, dash, underscore. Max 60 chars.";
      const base = n.split(".")[0].toUpperCase();
      if (/^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/.test(base)) return "Invalid filename. Use letters, numbers, dot, dash, underscore. Max 60 chars.";
      return null;
    }
    function fileIconFor(name) {
      const ext = String(name||"").split(".").pop().toLowerCase();
      const colors = { js:"#f7df1e", jsx:"#61dafb", ts:"#3178c6", tsx:"#3178c6", py:"#3776ab", rb:"#cc342d", go:"#00add8", rs:"#ce422b", java:"#ed8b00", html:"#e34c26", css:"#1572b6", md:"#519aba", json:"#cbcb41", sh:"#89e051", yml:"#cb171e", yaml:"#cb171e", xml:"#005a9c", sql:"#dad8d8", txt:"#9ba9b9" };
      const label = ext.length <= 4 ? ext.toUpperCase() : ext.slice(0,4).toUpperCase();
      return { color: colors[ext] || "#7a8597", label: label || "FILE" };
    }
    function providerForTerminalModel(mid) {
      for (const [pid, p] of Object.entries(PROVIDERS)) { if ((p.models||[]).some(m => m.id === mid)) return pid; }
      if (typeof mid === "string" && mid.includes("/")) return "openrouter";
      return activeProv;
    }
    function buildTerminalModelList() {
      const out = [];
      const liveOR = Array.isArray(liveModelCatalog?.models) ? liveModelCatalog.models : [];
      const orIds = new Set(liveOR.map(m => m.id));
      for (const [pid, p] of Object.entries(PROVIDERS)) {
        if (!p.builtin && !integrations[pid]?.key) continue;
        const bundled = (p.models || []).filter(m => modelOverrides?.[m.id]?.enabled !== false);
        if (pid === "openrouter") {
          for (const m of liveOR) { if (modelOverrides?.[m.id]?.enabled === false) continue; out.push({ id: m.id, name: m.name || m.id.split("/").pop(), provider: pid, providerName: p.name, providerColor: p.color }); }
        }
        for (const m of bundled) { if (pid === "openrouter" && orIds.has(m.id)) continue; out.push({ id: m.id, name: m.name || m.id.split("/").pop(), provider: pid, providerName: p.name, providerColor: p.color }); }
      }
      for (const m of (orgCustomModels || [])) { const pv = PROVIDERS[m.provider] || {}; out.push({ id: m.id, name: m.name || m.id, provider: m.provider, providerName: pv.name || m.provider, providerColor: pv.color || T.accent }); }
      const seen = new Set();
      return out.filter(m => { if (seen.has(m.id)) return false; seen.add(m.id); return true; });
    }

    // ── Tab / file ops ─────────────────────────────────────────────
    function openInTab(fileId) {
      setOpenTabs(p => p.includes(fileId) ? p : [...p, fileId]);
      setActiveFileId(fileId);
      setShowEditor(true);
      setTermTab("editor"); // mobile: surface the editor pane
    }
    function closeTab(fileId) {
      const idx = openTabs.indexOf(fileId);
      const remaining = openTabs.filter(t => t !== fileId);
      setOpenTabs(remaining);
      if (activeFileId === fileId) {
        const next = remaining[idx] || remaining[idx-1] || null;
        setActiveFileId(next);
        if (!next) setShowEditor(false);
      }
    }
    function deleteFile(fileId) {
      const f = files.find(ff => ff.id === fileId);
      if (!f) return;
      if (!window.confirm("Delete " + f.name + "?")) return;
      closeTab(fileId);
      setFiles(p => p.filter(x => x.id !== fileId));
    }
    function startRename(fileId) {
      const f = files.find(ff => ff.id === fileId); if (!f) return;
      setRenaming(fileId); setRenameValue(f.name);
    }
    function commitRename() {
      if (!renaming) return;
      const newName = renameValue.trim();
      const err = validateFilename(newName);
      if (err) { showToast && showToast(err); return; }
      if (files.find(f => f.id !== renaming && f.name === newName)) { showToast && showToast("File exists: " + newName); return; }
      setFiles(p => p.map(f => f.id === renaming ? Object.assign({}, f, { name: newName, language: detectLang(newName) }) : f));
      setRenaming(null); showToast && showToast("Renamed");
    }

    // ── Drag-drop import ──────────────────────────────────────────
    function onDrop(e) {
      e.preventDefault(); setIsDragging(false);
      const dropped = Array.from(e.dataTransfer?.files || []);
      if (!dropped.length) return;
      for (const f of dropped) {
        const reader = new FileReader();
        reader.onload = () => {
          const name = f.name;
          const err = validateFilename(name);
          if (err) { pushMsg({role:"system", content:"Skipped " + name + ": " + err}); return; }
          const text = String(reader.result);
          const existing = files.find(ff => ff.name === name);
          if (existing) { setFiles(p => p.map(ff => ff.id === existing.id ? Object.assign({}, ff, { content: text }) : ff)); openInTab(existing.id); }
          else { const id = makeId(); setFiles(p => [...p, { id, name, content: text, language: detectLang(name) }]); openInTab(id); }
          pushMsg({role:"system", content:"Imported " + name + " (" + text.length + " bytes)"});
        };
        reader.readAsText(f);
      }
    }

    // ── Download workspace ────────────────────────────────────────
    function downloadWorkspace() {
      if (!files.length) { showToast && showToast("Workspace is empty"); return; }
      const data = { files, exportedAt: new Date().toISOString(), session: STORAGE_KEY };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "chatfort-workspace-" + Date.now() + ".json"; a.click();
      URL.revokeObjectURL(url);
      showToast && showToast("Downloaded " + files.length + " file" + (files.length===1?"":"s"));
    }

    // ── JSON format ───────────────────────────────────────────────
    function formatJsonActive() {
      if (!activeFile) { pushMsg({role:"system", content:"No active file."}); return; }
      if (!activeFile.name.endsWith(".json")) { pushMsg({role:"system", content:"Not a JSON file."}); return; }
      try {
        const parsed = JSON.parse(activeFile.content||"");
        const formatted = JSON.stringify(parsed, null, 2);
        setFiles(p => p.map(f => f.id === activeFile.id ? Object.assign({}, f, { content: formatted }) : f));
        pushMsg({role:"system", content:"Formatted " + activeFile.name});
      } catch (e) { pushMsg({role:"system", content:"JSON error: " + e.message}); }
    }

    // ── Rail menu helpers ────────────────────────────────────────
    function newSession() {
      if (messages.length === 0 && openTabs.length === 0) { pushMsg({role:"system",content:"Session already empty."}); return; }
      if (!window.confirm("Start a new session? Workspace files will be kept; transcript and open tabs will be cleared.")) return;
      setMessages([]); setOpenTabs([]); setActiveFileId(null); setShowEditor(false);
      pushMsg({role:"system",content:"New session started."});
    }
    function saveRoutine() {
      const name = routineDraft.name.trim();
      const prompt = routineDraft.prompt.trim();
      if (!name || !prompt) { showToast && showToast("Name and prompt are required."); return; }
      if (routineEditId) {
        setRoutines(p => p.map(r => r.id === routineEditId ? Object.assign({}, r, { name, prompt }) : r));
        showToast && showToast("Routine updated");
      } else {
        if (routines.find(r => r.name === name)) { showToast && showToast("A routine with that name already exists."); return; }
        const id = "r-" + Math.random().toString(36).slice(2, 9);
        setRoutines(p => [...p, { id, name, prompt, created: Date.now() }]);
        showToast && showToast("Routine saved");
      }
      setRoutineDraft({ name: "", prompt: "" }); setRoutineEditId(null);
    }
    function runRoutine(r) {
      setInput(r.prompt); setShowRoutinesPanel(false);
      setTimeout(() => taRef.current?.focus(), 0);
    }
    function deleteRoutine(id) {
      const r = routines.find(x => x.id === id);
      if (!r) return;
      if (!window.confirm("Delete routine \"" + r.name + "\"?")) return;
      setRoutines(p => p.filter(x => x.id !== id));
      if (routineEditId === id) { setRoutineEditId(null); setRoutineDraft({name:"",prompt:""}); }
    }
    function editRoutine(r) { setRoutineEditId(r.id); setRoutineDraft({ name: r.name, prompt: r.prompt }); }
    // Pre-built routine templates. Each is a one-click starter that
    // either runs immediately (RUN) or seeds the save form (SAVE) so
    // the user can tweak before keeping it.
    const TEMPLATES = [
      { icon: "🔍", name: "Code review",            desc: "Audit the current file for bugs, security issues, code smells, and style problems with specific fixes.", prompt: "Review the active file. List concrete issues by severity (security > correctness > performance > style). For each, quote the line and propose the exact fix." },
      { icon: "🧪", name: "Add tests",              desc: "Write unit tests covering happy paths, edge cases, and error conditions for the current file.",            prompt: "Write comprehensive unit tests for every public function in the active file. Cover the happy path, at least two edge cases, and one error condition per function. Use whatever test framework is idiomatic for the language. Emit the full test file in a single fenced code block so I can /apply it." },
      { icon: "♻️", name: "Refactor for clarity",    desc: "Improve naming, extract helpers, remove duplication, keep behavior identical.",                            prompt: "Refactor the active file for clarity. Improve names, extract helpers, remove duplication. Behavior must stay identical. Emit the full refactored file." },
      { icon: "📝", name: "Add documentation",      desc: "Add docstrings or JSDoc to every public function and class.",                                              prompt: "Add idiomatic documentation comments (docstrings, JSDoc, etc. for the language) to every public function and class in the active file. Don't paraphrase obvious code -- document parameters, return values, and side effects. Emit the full annotated file." },
      { icon: "🐛", name: "Find bugs",              desc: "Hunt for race conditions, null-pointer issues, and unhandled errors in the current file.",                   prompt: "Analyze the active file for potential bugs: race conditions, null-pointer dereferences, off-by-one errors, unhandled exceptions, resource leaks. Quote the line and explain the bug before proposing a fix." },
      { icon: "⚡", name: "Performance audit",      desc: "Identify hot paths, unnecessary work, and concrete optimizations.",                                        prompt: "Review the active file for performance problems. Identify hot paths and inefficiencies (O(n^2) loops, redundant allocations, blocking I/O). For each, quote the line and propose an optimization with the expected complexity improvement." },
      { icon: "💡", name: "Explain this code",      desc: "Walk through what the current file does, section by section.",                                            prompt: "Explain the active file section by section. Assume the reader is unfamiliar with the codebase. Cover what it does, how it fits in, and any non-obvious decisions." },
      { icon: "🔄", name: "Convert to TypeScript",  desc: "Migrate the current JS file to TypeScript with proper types.",                                              prompt: "Convert the active file to TypeScript. Add explicit types for parameters, return values, and exported symbols. Preserve runtime behavior exactly. Emit the full .ts file." },
      { icon: "🏗️", name: "Scaffold a starter",     desc: "Generate a starter project layout: entry point, config, and tests.",                                       prompt: "Scaffold a starter project layout for [LANGUAGE/FRAMEWORK]: entry point, config file, a sample module, and a smoke test. Emit each file in its own fenced code block. Replace [LANGUAGE/FRAMEWORK] with what you want." },
      { icon: "📦", name: "Suggest a library",      desc: "Recommend a library for the task, with install command and one usage example.",                            prompt: "Recommend a library for [TASK]. Cover: 1) the top option with install command + one minimal usage example, 2) two alternatives with the trade-offs that differentiate them, 3) any deprecated options to avoid. Replace [TASK] with what you need." },
      { icon: "🔒", name: "Security pass",          desc: "Check for OWASP-class issues: injection, XSS, secrets in code, weak crypto.",                              prompt: "Security review of the active file. Check for OWASP-class issues: injection (SQL/command), XSS, auth bypass, hardcoded secrets, weak crypto, unsafe deserialization, path traversal, missing input validation. Quote the line and propose the fix." },
      { icon: "🧹", name: "Lint + format",          desc: "Apply idiomatic style and fix lint warnings without changing behavior.",                                   prompt: "Apply idiomatic formatting and fix lint-class issues in the active file. Don't change behavior. Emit the full cleaned file." },
    ];
    function useTemplate(t, mode) {
      // mode: "run" -> drop the prompt into the input + close the
      // modal so the user can send / tweak. "save" -> seed the
      // add-routine form so they can keep it.
      if (mode === "save") {
        setRoutineEditId(null);
        setRoutineDraft({ name: t.name, prompt: t.prompt });
        return;
      }
      setInput(t.prompt);
      setShowRoutinesPanel(false);
      setTimeout(() => taRef.current?.focus(), 0);
    }

    function importWorkspaceFromFile(file) {
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const data = JSON.parse(String(reader.result));
          if (!Array.isArray(data?.files)) { showToast && showToast("Invalid workspace file."); return; }
          // Merge: existing files of the same name get overwritten,
          // new ones get a fresh id.
          let added = 0, replaced = 0;
          for (const f of data.files) {
            if (!f?.name || typeof f.content !== "string") continue;
            const err = validateFilename(f.name);
            if (err) continue;
            const existing = files.find(x => x.name === f.name);
            if (existing) {
              setFiles(p => p.map(x => x.id === existing.id ? Object.assign({}, x, { content: f.content }) : x));
              replaced++;
            } else {
              const id = makeId();
              setFiles(p => [...p, { id, name: f.name, content: f.content, language: detectLang(f.name) }]);
              added++;
            }
          }
          pushMsg({role:"system",content:"Imported workspace: " + added + " added, " + replaced + " replaced."});
          setShowMoreMenu(false);
        } catch (e) { showToast && showToast("Parse error: " + e.message); }
      };
      reader.readAsText(file);
    }

    // ── Slash commands ────────────────────────────────────────────
    const SLASH = [
      { cmd: "/new", args: "<filename>", desc: "Create a new file" },
      { cmd: "/open", args: "<filename>", desc: "Open a file in a tab" },
      { cmd: "/close", args: "", desc: "Close active tab" },
      { cmd: "/rename", args: "<new name>", desc: "Rename active file" },
      { cmd: "/apply", args: "<filename>", desc: "Apply last code block" },
      { cmd: "/agent", args: "<on|off>", desc: "Toggle agent mode" },
      { cmd: "/undo", args: "", desc: "Restore last checkpoint" },
      { cmd: "/run", args: "[filename]", desc: "Run a JS file in the sandbox" },
      { cmd: "/files", args: "", desc: "List files" },
      { cmd: "/format", args: "", desc: "Format active file (JSON)" },
      { cmd: "/preview", args: "", desc: "Toggle markdown preview" },
      { cmd: "/wrap", args: "", desc: "Toggle word wrap" },
      { cmd: "/theme", args: "<midnight|ocean|paper>", desc: "Set editor theme" },
      { cmd: "/download", args: "", desc: "Download workspace as JSON" },
      { cmd: "/clear", args: "", desc: "Clear transcript" },
      { cmd: "/reset", args: "", desc: "Wipe transcript + files" },
      { cmd: "/help", args: "", desc: "Show command palette" },
    ];

    function handleSlash(raw) {
      const parts = raw.trim().split(/\s+/);
      const cmd = parts[0];
      const arg = parts.slice(1).join(" ").trim();
      if (cmd === "/help") { setShowHelp(true); return; }
      if (cmd === "/files") {
        const list = files.length ? files.map((f,i) => { const c = f.content || ""; const n = c === "" ? 0 : c.split("\n").length; return "  " + String(i+1).padStart(2," ") + ". " + f.name + " (" + n + " line" + (n === 1 ? "" : "s") + ")"; }).join("\n") : "  (no files yet -- use /new <filename> to create one)";
        pushMsg({ role:"system", content:"Workspace:\n\n" + list }); return;
      }
      if (cmd === "/clear") { setMessages([]); return; }
      if (cmd === "/reset") { if (!window.confirm("Clear transcript and remove all files?")) return; setMessages([]); setFiles([]); setOpenTabs([]); setActiveFileId(null); setShowEditor(false); return; }
      if (cmd === "/new") {
        if (!arg) { pushMsg({role:"system", content:"Filename required."}); return; }
        const fnErr = validateFilename(arg);
        if (fnErr) { pushMsg({role:"system", content:fnErr}); return; }
        if (files.find(f => f.name === arg)) { pushMsg({role:"system", content:"File already exists: " + arg}); return; }
        const id = makeId();
        setFiles(p => [...p, { id, name:arg, content:"", language:detectLang(arg) }]);
        openInTab(id);
        pushMsg({role:"system", content:"Created " + arg});
        return;
      }
      if (cmd === "/open") {
        if (!arg) { pushMsg({role:"system", content:"Filename required."}); return; }
        const fnErr = validateFilename(arg);
        if (fnErr) { pushMsg({role:"system", content:fnErr}); return; }
        const f = files.find(ff => ff.name === arg);
        if (!f) { pushMsg({role:"system", content:"No such file: " + arg}); return; }
        openInTab(f.id);
        pushMsg({role:"system", content:"Opened " + arg});
        return;
      }
      if (cmd === "/close") { if (activeFileId) closeTab(activeFileId); return; }
      if (cmd === "/rename") {
        if (!activeFile) { pushMsg({role:"system", content:"No active file."}); return; }
        if (!arg) { startRename(activeFile.id); return; }
        const fnErr = validateFilename(arg);
        if (fnErr) { pushMsg({role:"system", content:fnErr}); return; }
        if (files.find(f => f.id !== activeFile.id && f.name === arg)) { pushMsg({role:"system", content:"File exists: " + arg}); return; }
        setFiles(p => p.map(f => f.id === activeFile.id ? Object.assign({}, f, { name: arg, language: detectLang(arg) }) : f));
        pushMsg({role:"system", content:"Renamed to " + arg});
        return;
      }
      if (cmd === "/format") { formatJsonActive(); return; }
      if (cmd === "/run") {
        const f = arg ? files.find(ff => ff.name === arg) : activeFile;
        if (!f) { pushMsg({role:"system", content: arg ? "No such file: " + arg : "No active file — /run <filename>."}); return; }
        runFileInSandbox(f);
        return;
      }
      if (cmd === "/undo") {
        const cps = getCheckpoints();
        if (!cps.length) { pushMsg({role:"system", content:"No checkpoints yet — one is saved before every agent change and /apply."}); return; }
        restoreCheckpoint(cps[0].ts);
        return;
      }
      if (cmd === "/agent") {
        const next = arg === "on" ? true : arg === "off" ? false : !agentMode;
        setAgentMode(next);
        pushMsg({role:"system", content:"Agent mode " + (next ? "ON — the model reads, writes, and edits workspace files itself." : "OFF — classic chat; apply code blocks manually with /apply.")});
        return;
      }
      if (cmd === "/preview") { setMdPreview(p => !p); pushMsg({role:"system", content:"Markdown preview " + (!mdPreview ? "on" : "off")}); return; }
      if (cmd === "/wrap") { setWordWrap(p => !p); pushMsg({role:"system", content:"Word wrap " + (!wordWrap ? "on" : "off")}); return; }
      if (cmd === "/theme") {
        const allowed = ["auto","midnight","ocean","paper"];
        if (!arg) { pushMsg({role:"system", content:"Available themes: " + allowed.join(", ") + ". Current: " + editorTheme}); return; }
        if (!allowed.includes(arg)) { pushMsg({role:"system", content:"Unknown theme. Available: " + allowed.join(", ")}); return; }
        setEditorTheme(arg); pushMsg({role:"system", content:"Theme set to " + arg});
        return;
      }
      if (cmd === "/download") { downloadWorkspace(); return; }
      if (cmd === "/apply") {
        const target = arg || activeFile?.name;
        if (!target) { pushMsg({role:"system", content:"Usage: /apply <filename>"}); return; }
        const lastAst = [...messages].reverse().find(m => m.role === "assistant" && m.streaming !== true);
        if (!lastAst) { pushMsg({role:"system", content:"No assistant code to apply yet."}); return; }
        const re = new RegExp(FENCE + "\\s*([a-zA-Z0-9_+-]*)\\s*\\n([\\s\\S]*?)\\n" + FENCE, "g");
        let lastMatch = null; let _m;
        while ((_m = re.exec(lastAst.content)) !== null) { lastMatch = _m; }
        if (!lastMatch) { pushMsg({role:"system", content:"Last assistant message has no code block."}); return; }
        const code = lastMatch[2];
        saveCheckpoint("before /apply " + target);
        const existing = files.find(f => f.name === target);
        if (existing) { setFiles(p => p.map(f => f.id === existing.id ? Object.assign({}, f, { content: code }) : f)); openInTab(existing.id); }
        else { const id = makeId(); setFiles(p => [...p, { id, name:target, content:code, language:detectLang(target) }]); openInTab(id); }
        pushMsg({role:"system", content:"Applied " + code.length + " bytes to " + target});
        return;
      }
      pushMsg({role:"system", content:"Unknown command: " + cmd + "  (try /help)"});
    }

    // ── Agent mode: tool protocol ─────────────────────────────────
    // The model acts through ONE fenced `cf-tool` JSON block per
    // message (always last). write_file bodies come from the fenced
    // code block immediately BEFORE the cf-tool block, so file
    // contents never need JSON escaping. Failed actions are fed back
    // verbatim — the loop self-corrects within the step budget.
    const AGENT_MAX_STEPS = 14;
    const AGENT_READ_CAP = 120000;
    // Output budget: start at the ceiling and LEARN each model's real
    // cap from provider rejections (halve + persist per model), so big
    // models get their full 32k while small ones settle where they fit.
    const AGENT_OUT_CEILING = 32768;
    const AGENT_OUT_FLOOR = 8192;
    function getOutCap(model) {
      try { const v = parseInt(localStorage.getItem("cf_terminal_outcap_" + model) || "", 10); if (Number.isFinite(v) && v >= AGENT_OUT_FLOOR) return Math.min(v, AGENT_OUT_CEILING); } catch {}
      return AGENT_OUT_CEILING;
    }
    function lowerOutCap(model, cur) {
      const next = Math.max(AGENT_OUT_FLOOR, Math.floor(cur / 2));
      try { localStorage.setItem("cf_terminal_outcap_" + model, String(next)); } catch {}
      return next;
    }
    function isTokenLimitError(msg) {
      return /max[_ -]?(output[_ -]?)?tokens|maximum (output|completion|context)|output limit|too (long|large)|context.?length/i.test(String(msg || ""));
    }

    function agentFileList(work) {
      if (!work.length) return "(empty workspace)";
      return work.map(f => {
        const c = f.content || "";
        const n = c === "" ? 0 : c.split("\n").length;
        return "- " + f.name + " (" + n + " line" + (n === 1 ? "" : "s") + ", " + (f.language || "text") + ")";
      }).join("\n");
    }

    function agentSysprompt(work) {
      return (
        "You are Hermes Code mode in AGENT mode -- an autonomous coding agent operating on the user's virtual workspace (browser-local text files, no folders).\n\n" +
        "You work in steps. Each message may contain AT MOST ONE action: a fenced code block tagged cf-tool holding a single JSON object, as the LAST thing in the message. One short line of narration before it is fine.\n\n" +
        "Actions:\n" +
        '- {"tool":"list_files"}\n' +
        '- {"tool":"read_file","name":"file.ext"}\n' +
        '- {"tool":"write_file","name":"file.ext"} — put the FULL new file contents in a separate fenced code block IMMEDIATELY BEFORE the cf-tool block (tag it with the language). Creates or overwrites.\n' +
        '- {"tool":"edit_file","name":"file.ext","find":"exact text","replace":"new text","all":false} — exact string replacement (first match unless "all" is true). Escape newlines as \\n inside the JSON.\n' +
        '- {"tool":"delete_file","name":"file.ext"}\n' +
        '- {"tool":"run_js","name":"file.js"} — execute a JS file (or inline code: put it in a fenced block before the cf-tool block and omit "name") in a sandboxed Web Worker. No DOM, no network; console output is captured; ~5s limit. USE THIS to test your work.\n' +
        '- {"tool":"preview","name":"page.html"} — open an HTML/SVG file in the live preview the user can see.\n\n' +
        "Example write:\n" +
        FENCE + "python\nprint('hello')\n" + FENCE + "\n" +
        FENCE + "cf-tool\n{\"tool\":\"write_file\",\"name\":\"hello.py\"}\n" + FENCE + "\n\n" +
        "Rules:\n" +
        "- Read a file before you edit it (within this run).\n" +
        "- Prefer edit_file for small changes; write_file for new files or full rewrites.\n" +
        "- LARGE FILES: your output budget is large (usually ~32k tokens), so complete files up to ~1500 lines are fine in one write_file. Bigger than that: write the core, then extend with edit_file steps. If a message is ever cut off you will be told — redo that step in smaller pieces.\n" +
        "- Filenames: letters, digits, dot, dash, underscore only.\n" +
        "- When the task is complete, reply WITHOUT any cf-tool block: a terse summary of what changed. That ends the run.\n" +
        "- If an action errors, fix the problem and try again.\n" +
        "- After writing non-trivial JS, verify it with run_js before finishing. After building an HTML page, preview it.\n" +
        "- Be terse. No filler, no apologies.\n\n" +
        "Workspace files (names only — use read_file to see contents):\n" + agentFileList(work) +
        (activeFile ? "\n\nThe user currently has " + activeFile.name + " open." : "")
      );
    }

    // Pull the LAST cf-tool block out of an assistant message, plus the
    // nearest preceding code block (the write_file body). Returns null
    // when the message has no action — that is the final answer.
    function parseAgentAction(text) {
      const blocks = parseBlocks(text);
      let toolIdx = -1;
      for (let i = blocks.length - 1; i >= 0; i--) {
        if (blocks[i].type === "code" && String(blocks[i].lang).toLowerCase() === "cf-tool") { toolIdx = i; break; }
      }
      if (toolIdx === -1) return null;
      let body;
      try { body = JSON.parse(blocks[toolIdx].code); }
      catch (e) { return { parseError: "Invalid JSON in cf-tool block: " + e.message }; }
      if (!body || typeof body.tool !== "string") return { parseError: 'cf-tool JSON must be an object with a "tool" field.' };
      for (let i = toolIdx - 1; i >= 0; i--) {
        if (blocks[i].type === "code" && String(blocks[i].lang).toLowerCase() !== "cf-tool") { body.__content = blocks[i].code; break; }
      }
      return body;
    }

    // Execute one action against the loop-local working copy. Mutating
    // tools push to React state immediately so the user watches files
    // change live. Returns the result string fed back to the model.
    function execAgentTool(act, work) {
      const findFile = (n) => work.find(f => f.name === n);
      const sync = () => { setFiles(work.slice()); };
      const t = act.tool;
      if (t === "list_files") return { result: agentFileList(work) };
      if (t === "read_file") {
        const f = findFile(act.name);
        if (!f) return { result: "ERROR: no file named " + JSON.stringify(act.name) + ". Files:\n" + agentFileList(work) };
        const c = f.content || "";
        const capped = c.length > AGENT_READ_CAP ? c.slice(0, AGENT_READ_CAP) + "\n…(truncated at " + AGENT_READ_CAP + " of " + c.length + " chars)" : c;
        return { result: f.name + " (" + (c ? c.split("\n").length : 0) + " lines):\n" + FENCE + (f.language || "") + "\n" + capped + "\n" + FENCE };
      }
      if (t === "write_file") {
        const err = validateFilename(act.name);
        if (err) return { result: "ERROR: " + err };
        if (typeof act.__content !== "string") return { result: "ERROR: no content found. Put the FULL file contents in a fenced code block immediately BEFORE the cf-tool block." };
        const existing = findFile(act.name);
        if (existing) { existing.content = act.__content; }
        else { work.push({ id: makeId(), name: act.name, content: act.__content, language: detectLang(act.name) }); }
        sync();
        return { result: "Wrote " + act.name + " (" + act.__content.length + " chars).", mutated: true, touched: act.name };
      }
      if (t === "edit_file") {
        const f = findFile(act.name);
        if (!f) return { result: "ERROR: no file named " + JSON.stringify(act.name) + ". Files:\n" + agentFileList(work) };
        if (typeof act.find !== "string" || !act.find) return { result: 'ERROR: edit_file needs a non-empty "find" string.' };
        const c = f.content || "";
        const count = c.split(act.find).length - 1;
        if (count === 0) return { result: "ERROR: find text not found in " + f.name + " — it must match EXACTLY, including whitespace and indentation. read_file again to check." };
        const rep = typeof act.replace === "string" ? act.replace : "";
        // split/join + index splice, NOT String.replace — replace() expands
        // $-patterns ($&, $1…) in the replacement and would corrupt code.
        if (act.all) f.content = c.split(act.find).join(rep);
        else { const at = c.indexOf(act.find); f.content = c.slice(0, at) + rep + c.slice(at + act.find.length); }
        sync();
        return { result: "Edited " + f.name + ": replaced " + (act.all ? count : 1) + " of " + count + " occurrence" + (count === 1 ? "" : "s") + ".", mutated: true, touched: f.name };
      }
      if (t === "delete_file") {
        const f = findFile(act.name);
        if (!f) return { result: "ERROR: no file named " + JSON.stringify(act.name) + "." };
        work.splice(work.indexOf(f), 1);
        sync();
        closeTab(f.id);
        return { result: "Deleted " + f.name + ".", mutated: true };
      }
      return { result: "ERROR: unknown tool " + JSON.stringify(t) + ". Valid: list_files, read_file, write_file, edit_file, delete_file." };
    }

    // ── Slice 2: checkpoints + change review ──────────────────────
    // A workspace snapshot is saved automatically before the agent's
    // first mutation of a run (and before /apply). After a run, every
    // touched file shows up in a review panel with a line diff and
    // keep / revert per file — Cursor-style trust UX.
    const CHECKPOINT_KEY = "cf_terminal_checkpoints_v1";
    function getCheckpoints() {
      try { const a = JSON.parse(localStorage.getItem(CHECKPOINT_KEY) || "[]"); return Array.isArray(a) ? a : []; } catch { return []; }
    }
    function saveCheckpoint(label) {
      try {
        const snap = { ts: Date.now(), label: String(label || "checkpoint"), files: filesRef.current.map(f => ({ id: f.id, name: f.name, content: f.content, language: f.language })) };
        let arr = [snap, ...getCheckpoints()].slice(0, 8);
        // Quota guard: drop oldest snapshots until the set fits.
        for (;;) {
          try { localStorage.setItem(CHECKPOINT_KEY, JSON.stringify(arr)); break; }
          catch (e) { if (arr.length <= 1) break; arr = arr.slice(0, arr.length - 1); }
        }
      } catch {}
    }
    function restoreCheckpoint(ts) {
      const snap = getCheckpoints().find(c => c.ts === ts);
      if (!snap) { pushMsg({ role: "system", content: "Checkpoint not found." }); return; }
      saveCheckpoint("before restore"); // restore is itself undoable
      setFiles(snap.files.map(f => Object.assign({}, f)));
      setPendingReview(null);
      setShowCheckpoints(false);
      pushMsg({ role: "system", content: "Restored workspace checkpoint from " + new Date(snap.ts).toLocaleTimeString() + " (" + snap.files.length + " file" + (snap.files.length === 1 ? "" : "s") + ")." });
    }

    // Line-level LCS diff for the review panel. Returns null when either
    // side is too large to diff comfortably on the UI thread.
    function lineDiff(beforeText, afterText) {
      const a = String(beforeText == null ? "" : beforeText).split("\n");
      const b = String(afterText == null ? "" : afterText).split("\n");
      if (a.length > 600 || b.length > 600) return null;
      const n = a.length, m = b.length;
      const dp = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
      for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) {
        dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
      const out = [];
      let i = 0, j = 0;
      while (i < n && j < m) {
        if (a[i] === b[j]) { out.push({ t: " ", line: a[i] }); i++; j++; }
        else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ t: "-", line: a[i] }); i++; }
        else { out.push({ t: "+", line: b[j] }); j++; }
      }
      while (i < n) { out.push({ t: "-", line: a[i++] }); }
      while (j < m) { out.push({ t: "+", line: b[j++] }); }
      return out;
    }
    function diffStats(beforeText, afterText) {
      const d = lineDiff(beforeText, afterText);
      if (!d) {
        const a = beforeText == null ? 0 : String(beforeText).split("\n").length;
        const b = afterText == null ? 0 : String(afterText).split("\n").length;
        return { adds: Math.max(0, b - a), dels: Math.max(0, a - b), big: true };
      }
      return { adds: d.filter(x => x.t === "+").length, dels: d.filter(x => x.t === "-").length, big: false };
    }

    function reviewKeep(name) {
      setPendingReview(p => { if (!p) return null; const items = p.items.filter(it => it.name !== name); return items.length ? Object.assign({}, p, { items }) : null; });
    }
    function reviewRevert(name) {
      const item = ((pendingReview && pendingReview.items) || []).find(it => it.name === name);
      if (!item) return;
      if (item.before === null) {
        const f = filesRef.current.find(ff => ff.name === name);
        if (f) { closeTab(f.id); setFiles(p => p.filter(x => x.name !== name)); }
        pushMsg({ role: "system", content: "Reverted: removed " + name + "." });
      } else {
        setFiles(p => {
          const ex = p.find(x => x.name === name);
          if (ex) return p.map(x => x.name === name ? Object.assign({}, x, { content: item.before }) : x);
          return [...p, { id: makeId(), name, content: item.before, language: detectLang(name) }];
        });
        pushMsg({ role: "system", content: "Reverted " + name + " to its pre-run contents." });
      }
      reviewKeep(name);
    }

    // ── Slice 3: sandboxed JS runner ──────────────────────────────
    // Executes code in a Blob Web Worker: no DOM, no app state, no
    // auth token in scope — console.* is captured, output is capped,
    // and a hard timeout terminates the worker. Drives the ▶ run
    // button, /run, and the agent's run_js tool.
    const RUN_TIMEOUT_MS = 5000;
    const RUN_OUTPUT_CAP = 32000;
    function runJsInWorker(code) {
      return new Promise((resolve) => {
        const out = [];
        let outLen = 0;
        let settled = false;
        const addLine = (k, t) => {
          if (outLen >= RUN_OUTPUT_CAP) return;
          const s = (k === "log" || k === "result" ? (k === "result" ? "← " : "") : "[" + k + "] ") + t;
          outLen += s.length;
          out.push(outLen >= RUN_OUTPUT_CAP ? s.slice(0, Math.max(0, s.length - (outLen - RUN_OUTPUT_CAP))) + "…(output capped)" : s);
        };
        const src =
          'function fmt(v){try{if(typeof v==="string")return v;if(v instanceof Error)return v.stack||String(v);if(typeof v==="function")return String(v);return JSON.stringify(v)}catch(e){return String(v)}}' +
          'function post(k,a){self.postMessage({k:k,t:Array.prototype.map.call(a,fmt).join(" ")})}' +
          'self.console={log:function(){post("log",arguments)},error:function(){post("error",arguments)},warn:function(){post("warn",arguments)},info:function(){post("info",arguments)},debug:function(){post("log",arguments)}};' +
          'self.onmessage=function(e){Promise.resolve().then(function(){return (0,eval)(e.data)}).then(function(r){if(r!==undefined)post("result",[r]);self.postMessage({done:1})}).catch(function(err){post("error",[err]);self.postMessage({done:1})})};';
        let worker, url;
        try {
          url = URL.createObjectURL(new Blob([src], { type: "application/javascript" }));
          worker = new Worker(url);
        } catch (e) {
          resolve({ ok: false, output: "Runner unavailable in this browser: " + e.message });
          return;
        }
        const finish = (ok, note) => {
          if (settled) return;
          settled = true;
          try { worker.terminate(); } catch {}
          try { URL.revokeObjectURL(url); } catch {}
          resolve({ ok, output: out.join("\n") + (note ? (out.length ? "\n" : "") + note : "") });
        };
        const timer = setTimeout(() => finish(false, "⏱ Timed out after " + (RUN_TIMEOUT_MS / 1000) + "s — infinite loop? The worker was killed."), RUN_TIMEOUT_MS);
        worker.onmessage = (e) => {
          if (e.data && e.data.done) {
            clearTimeout(timer);
            // Grace window so queued setTimeout(…, 0)-ish output lands.
            setTimeout(() => finish(!out.some(l => l.startsWith("[error]"))), 200);
            return;
          }
          if (e.data && e.data.k) addLine(e.data.k, e.data.t);
        };
        worker.onerror = (e) => { clearTimeout(timer); addLine("error", (e && e.message) || "worker error"); finish(false); };
        worker.postMessage(String(code));
      });
    }

    async function runFileInSandbox(file) {
      const f = file || activeFile;
      if (!f) return;
      pushMsg({ role: "system", content: "▶ Running " + f.name + " in the sandbox…" });
      const r = await runJsInWorker(f.content || "");
      pushMsg({ role: "system", content: (r.ok ? "✓ " : "✗ ") + f.name + ":\n" + (r.output || "(no output)") });
    }

    // ── Slice 4: lightweight syntax highlighting ──────────────────
    // A tokenized <pre> underlay sits beneath the (text-transparent)
    // editor textarea — same font metrics, scroll-synced. One regex
    // pass per family; no parser, no dependency. Auto-disabled for
    // very large files so typing stays instant.
    function escHl(s) { return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); }
    function hlFamily(lang) {
      if (["javascript", "jsx", "typescript", "tsx", "json"].includes(lang)) return "js";
      if (lang === "python") return "py";
      if (["css", "scss"].includes(lang)) return "css";
      if (["html", "xml", "svg"].includes(lang)) return "xml";
      if (lang === "markdown") return "md";
      if (["bash", "shell"].includes(lang)) return "sh";
      if (lang === "sql") return "sql";
      return null;
    }
    function highlightToHtml(code, lang, isLight) {
      const fam = hlFamily(lang);
      if (!fam) return null;
      const C = isLight
        ? { com: "#8a939e", str: "#0a7d4d", num: "#aa5d00", kw: "#0550ae", tag: "#0550ae", extra: "#6f42c1" }
        : { com: "#697586", str: "#7ee2a8", num: "#f0b36b", kw: "#7cb7ff", tag: "#7cb7ff", extra: "#c5a3ff" };
      let re, groups;
      if (fam === "js") {
        re = /(\/\/[^\n]*|\/\*[\s\S]*?\*\/)|("(?:[^"\\\n]|\\.)*"|'(?:[^'\\\n]|\\.)*'|`(?:[^`\\]|\\[\s\S])*`)|(\b0x[0-9a-fA-F]+\b|\b\d[\w.]*\b)|(\b(?:abstract|async|await|break|case|catch|class|const|continue|debugger|default|delete|do|else|enum|export|extends|false|finally|for|from|function|get|if|implements|import|in|instanceof|interface|let|new|null|of|private|protected|public|return|set|static|super|switch|this|throw|true|try|type|typeof|undefined|var|void|while|with|yield)\b)/g;
        groups = ["com", "str", "num", "kw"];
      } else if (fam === "py") {
        re = /('''[\s\S]*?'''|"""[\s\S]*?"""|"(?:[^"\\\n]|\\.)*"|'(?:[^'\\\n]|\\.)*')|(#[^\n]*)|(\b\d[\w.]*\b)|(\b(?:and|as|assert|async|await|break|class|continue|def|del|elif|else|except|False|finally|for|from|global|if|import|in|is|lambda|None|nonlocal|not|or|pass|raise|return|self|True|try|while|with|yield)\b)/g;
        groups = ["str", "com", "num", "kw"];
      } else if (fam === "css") {
        re = /(\/\*[\s\S]*?\*\/)|("(?:[^"\\\n]|\\.)*"|'(?:[^'\\\n]|\\.)*')|(#[0-9a-fA-F]{3,8}\b|\b\d[\w.%]*)|([A-Za-z-]+(?=\s*:))/g;
        groups = ["com", "str", "num", "kw"];
      } else if (fam === "xml") {
        re = /(<!--[\s\S]*?-->)|("[^"]*"|'[^']*')|(<\/?[A-Za-z][\w:.-]*|\/?>)|(\b[A-Za-z_:][\w:.-]*(?==))/g;
        groups = ["com", "str", "tag", "extra"];
      } else if (fam === "md") {
        re = /(^#{1,6}[^\n]*$)|(```[\s\S]*?```|`[^`\n]+`)|(\*\*[^*\n]+\*\*|__[^_\n]+__)|(^[-*+] |^\d+\. )/gm;
        groups = ["kw", "str", "extra", "num"];
      } else if (fam === "sh") {
        re = /(#[^\n]*)|("(?:[^"\\]|\\.)*"|'[^']*')|(\$\{?\w+\}?)|(\b(?:if|then|else|elif|fi|for|while|do|done|case|esac|function|in|echo|exit|return|local|export|readonly|shift|set|cd|source)\b)/g;
        groups = ["com", "str", "extra", "kw"];
      } else {
        re = /(--[^\n]*|\/\*[\s\S]*?\*\/)|('(?:[^']|'')*')|(\b\d[\w.]*\b)|(\b(?:select|from|where|insert|into|update|delete|set|values|create|table|drop|alter|index|join|left|right|inner|outer|on|as|and|or|not|null|primary|key|foreign|references|group|by|order|having|limit|offset|distinct|union|all|exists|between|like|in|is)\b)/gi;
        groups = ["com", "str", "num", "kw"];
      }
      let out = "";
      let last = 0;
      let m;
      while ((m = re.exec(code)) !== null) {
        out += escHl(code.slice(last, m.index));
        let gi = 0;
        for (let g = 1; g < m.length; g++) if (m[g] !== undefined) { gi = g - 1; break; }
        out += '<span style="color:' + (C[groups[gi]] || C.kw) + '">' + escHl(m[0]) + "</span>";
        last = m.index + m[0].length;
        if (m[0].length === 0) re.lastIndex++;
      }
      out += escHl(code.slice(last));
      // Trailing newline keeps the underlay's scrollHeight in lockstep
      // with the textarea when the caret sits on a fresh last line.
      return out + "\n";
    }

    async function sendAgent(text) {
      pushMsg({ role: "user", content: text });
      setStreaming(true);
      stopRef.current = false;
      setPendingReview(null); // a new run supersedes any unreviewed changes (= keep all)
      const work = filesRef.current.map(f => Object.assign({}, f));
      // Prior transcript gives cross-run continuity; tool RESULTS only
      // live inside this run's convo (they would bloat localStorage and
      // every future prompt).
      const convo = messages
        .filter(m => m.role === "user" || (m.role === "assistant" && !m.error))
        .slice(-20)
        .map(m => ({ role: m.role, content: m.content }));
      convo.push({ role: "user", content: text });
      const ag = { provider: providerForTerminalModel(termModel), model: termModel, temperature: 0.2, name: "Hermes Code Agent" };
      let maxT = getOutCap(termModel);
      let lastTouched = null;
      let checkpointed = false;
      const changes = new Map(); // name -> content before this run's first touch (null = file didn't exist)
      try {
        for (let step = 1; step <= AGENT_MAX_STEPS; step++) {
          const aid = "m-" + Math.random().toString(36).slice(2, 9);
          setMessages(p => [...p, { id: aid, role: "assistant", content: "", streaming: true, time: Date.now() }]);
          let acc = "";
          const onChunk = (next) => { acc = next; setMessages(p => p.map(m => m.id === aid ? Object.assign({}, m, { content: next }) : m)); };
          let raw;
          try {
            for (;;) {
              try {
                raw = await callAI(ag, convo, agentSysprompt(work), maxT, integrations, false, [], null, onChunk, false, null);
                break;
              } catch (err) {
                if (isTokenLimitError(err.message) && maxT > AGENT_OUT_FLOOR) {
                  maxT = lowerOutCap(termModel, maxT);
                  pushMsg({ role: "system", content: "↘ Model rejected the output budget — retrying at " + maxT + " tokens (remembered for this model)." });
                  continue;
                }
                throw err;
              }
            }
          } catch (err) {
            setMessages(p => p.map(m => m.id === aid ? Object.assign({}, m, { content: "Error: " + err.message, streaming: false, error: true }) : m));
            break;
          }
          const finalText = typeof raw === "string" ? raw : (raw && raw.__text) || acc || "(empty)";
          setMessages(p => p.map(m => m.id === aid ? Object.assign({}, m, { content: finalText, streaming: false }) : m));
          if (stopRef.current) { pushMsg({ role: "system", content: "Run stopped." }); break; }
          const act = parseAgentAction(finalText);
          if (!act) {
            // Truncation guard: an odd number of fences means the stream
            // was cut mid-code-block by the output token cap — that is
            // NOT a final answer. Make the model redo the step smaller.
            const fenceCount = finalText.split(FENCE).length - 1;
            if (fenceCount % 2 === 1 && step < AGENT_MAX_STEPS) {
              pushMsg({ role: "system", content: "⚠ Output hit the token cap mid-code — asking the agent to redo this step in smaller pieces." });
              convo.push({ role: "assistant", content: finalText });
              convo.push({ role: "user", content: "Your last message was CUT OFF mid-code-block by the output token limit, so NOTHING was applied. Redo this step in smaller pieces: write_file a compact working version first (well under the limit), then grow it with edit_file calls. Never emit more than ~150 lines in a single message." });
              continue;
            }
            break; // final answer, run complete
          }
          const isMut = !act.parseError && (act.tool === "write_file" || act.tool === "edit_file" || act.tool === "delete_file");
          if (isMut && !checkpointed) { saveCheckpoint("before agent run"); checkpointed = true; }
          if (isMut && typeof act.name === "string" && !changes.has(act.name)) { const prev = work.find(ff => ff.name === act.name); changes.set(act.name, prev ? prev.content : null); }
          let out;
          if (!act.parseError && act.tool === "run_js") {
            const target = typeof act.name === "string" && act.name ? work.find(ff => ff.name === act.name) : null;
            const codeToRun = target ? (target.content || "") : (typeof act.__content === "string" ? act.__content : null);
            if (typeof act.name === "string" && act.name && !target) out = { result: "ERROR: no file named " + JSON.stringify(act.name) + ". Files:\n" + agentFileList(work) };
            else if (codeToRun === null) out = { result: 'ERROR: run_js needs {"name":"file.js"} or inline code in a fenced block before the cf-tool block.' };
            else {
              const r = await runJsInWorker(codeToRun);
              out = { result: (r.ok ? "Run OK." : "Run FAILED.") + "\nOutput:\n" + (r.output || "(no output)") };
            }
          } else if (!act.parseError && act.tool === "preview") {
            const pf = work.find(ff => ff.name === act.name);
            if (!pf) out = { result: "ERROR: no file named " + JSON.stringify(act.name) + "." };
            else { setPreview(pf.content || ""); out = { result: "Preview opened for the user. Continue, or finish with your summary." }; }
          } else {
            out = act.parseError ? { result: "ERROR: " + act.parseError } : execAgentTool(act, work);
          }
          if (out.touched) lastTouched = out.touched;
          const isErr = out.result.startsWith("ERROR:");
          const firstLine = out.result.split("\n")[0];
          pushMsg({ role: "system", content: (isErr ? "✗ " : "→ ") + (act.tool || "action") + (act.name ? " " + act.name : "") + " · " + (firstLine.length > 90 ? firstLine.slice(0, 90) + "…" : firstLine) });
          convo.push({ role: "assistant", content: finalText });
          convo.push({ role: "user", content: "[" + (act.tool || "action") + " result]\n" + out.result + "\n\nContinue with the next cf-tool action — or, if the task is complete, reply with a terse final summary and NO cf-tool block." });
          if (step === AGENT_MAX_STEPS) pushMsg({ role: "system", content: "Step limit (" + AGENT_MAX_STEPS + ") reached — send a follow-up to continue." });
        }
        const reviewItems = [];
        for (const [name, before] of changes) {
          const cur = work.find(ff => ff.name === name);
          const after = cur ? cur.content : null;
          if (before !== after) reviewItems.push({ name, before, after });
        }
        if (reviewItems.length) { setDiffOpen({}); setPendingReview({ ts: Date.now(), items: reviewItems }); }
        if (lastTouched) { const f = filesRef.current.find(ff => ff.name === lastTouched) || work.find(ff => ff.name === lastTouched); if (f) openInTab(f.id); }
      } finally { setStreaming(false); stopRef.current = false; }
    }

    async function send() {
      const text = input.trim();
      if (!text || streaming) return;
      if (text.startsWith("/")) { pushMsg({role:"user", content:text}); setInput(""); handleSlash(text); return; }
      if (agentMode) { setInput(""); sendAgent(text); return; }
      const userMsg = { role:"user", content:text };
      const assistantId = "m-"+Math.random().toString(36).slice(2,9);
      setMessages(p => [...p, Object.assign({}, userMsg, { id:"m-"+Math.random().toString(36).slice(2,9), time:Date.now() }), { id:assistantId, role:"assistant", content:"", streaming:true, time:Date.now() }]);
      setInput(""); setStreaming(true);
      const filesBlock = files.length
        ? "\n\n# Current workspace files\n\n" + files.map(f => "## " + f.name + "\n" + FENCE + (f.language||"") + "\n" + (f.content||"(empty)") + "\n" + FENCE).join("\n\n")
        : "";
      const sysprompt = "You are Hermes Code mode, a focused in-app code agent. The user works in a virtual workspace and asks you to read, write, refactor, debug, or explain code.\n\nRules:\n- Be terse. Default to code over prose. No motivational filler.\n- When changing a file, emit the FULL new file contents in a single fenced code block tagged with the language. The user has a one-click /apply <filename> that takes the FIRST code block from your message and writes it.\n- For multi-file work, do ONE file per message and tell the user to /apply <name> before moving on.\n- If the answer is short prose, keep it under 3 sentences.\n- Never apologize. Never restate the request.\n\nWorkspace state and recent transcript follow." + filesBlock;
      const allM = [...messages.filter(m => m.role !== "system"), userMsg].map(m => ({ role: m.role === "system" ? "user" : m.role, content: m.content }));
      try {
        let acc = "";
        const onChunk = (next) => { acc = next; setMessages(p => p.map(m => m.id === assistantId ? Object.assign({}, m, { content: acc }) : m)); };
        const ag = { provider: providerForTerminalModel(termModel), model: termModel, temperature: 0.3, name: "Hermes Code" };
        let raw;
        let chatMax = getOutCap(termModel);
        for (;;) {
          try { raw = await callAI(ag, allM, sysprompt, chatMax, integrations, false, [], null, onChunk, false, null); break; }
          catch (err) { if (isTokenLimitError(err.message) && chatMax > AGENT_OUT_FLOOR) { chatMax = lowerOutCap(termModel, chatMax); continue; } throw err; }
        }
        const finalText = typeof raw === "string" ? raw : (raw?.__text || acc || "(empty)");
        setMessages(p => p.map(m => m.id === assistantId ? Object.assign({}, m, { content: finalText, streaming: false }) : m));
      } catch (err) {
        setMessages(p => p.map(m => m.id === assistantId ? Object.assign({}, m, { content: "Error: " + err.message, streaming: false, error: true }) : m));
      } finally { setStreaming(false); }
    }

    // ── Global keyboard shortcuts ─────────────────────────────────
    useEffect(() => {
      const onKey = (e) => {
        const mod = e.metaKey || e.ctrlKey;
        const tag = (e.target?.tagName || "").toLowerCase();
        const inField = tag === "input" || tag === "textarea";
        if (e.key === "Escape") { setFindOpen(false); setShowQuickSwitcher(false); setShowCmdPalette(false); setShowHelp(false); setRenaming(null); setTermModelOpen(false); return; }
        if (mod && e.key.toLowerCase() === "f" && !e.shiftKey) { if (activeFile) { e.preventDefault(); setFindOpen(true); setTimeout(()=>findInputRef.current?.focus(),0); } }
        if (mod && e.key.toLowerCase() === "p" && !e.shiftKey) { e.preventDefault(); setShowQuickSwitcher(true); setQuickQuery(""); }
        if (mod && e.key.toLowerCase() === "p" && e.shiftKey) { e.preventDefault(); setShowCmdPalette(true); }
        if (e.key === "F2" && activeFile && !inField) { e.preventDefault(); startRename(activeFile.id); }
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }, [activeFileId, files]);

    // ── Cursor pos tracker ────────────────────────────────────────
    function trackCursor(el) {
      if (!el) return;
      const pos = el.selectionStart || 0;
      const before = el.value.slice(0, pos);
      const lines = before.split("\n");
      setCursorPos({ line: lines.length, col: (lines[lines.length-1] || "").length + 1 });
    }

    // ── Suggestion list ──────────────────────────────────────────
    const showSuggestions = input.startsWith("/") && !input.includes(" ");
    const suggestions = showSuggestions ? SLASH.filter(s => s.cmd.startsWith(input)) : [];

    function parseBlocks(text) {
      if (!text) return [{type:"text",value:""}];
      const out = [];
      const re = new RegExp(FENCE + "\\s*([a-zA-Z0-9_+-]*)\\s*\\n([\\s\\S]*?)(\\n" + FENCE + "|$)", "g");
      let last = 0; let m;
      while ((m = re.exec(text)) !== null) {
        if (m.index > last) out.push({ type:"text", value:text.slice(last, m.index) });
        out.push({ type:"code", lang:m[1]||"", code:m[2] });
        last = m.index + m[0].length;
      }
      if (last < text.length) out.push({ type:"text", value:text.slice(last) });
      if (out.length === 0) out.push({ type:"text", value:text });
      return out;
    }
    function applyBlock(b) {
      const guess = b.lang ? "snippet." + langExt(b.lang) : "snippet.txt";
      const name = window.prompt("Apply to which file?", guess);
      if (!name) return;
      const fnErr = validateFilename(name);
      if (fnErr) { showToast && showToast(fnErr); return; }
      saveCheckpoint("before apply " + name);
      const existing = files.find(f => f.name === name);
      if (existing) { setFiles(p => p.map(f => f.id === existing.id ? Object.assign({}, f, { content: b.code }) : f)); openInTab(existing.id); }
      else { const id = makeId(); setFiles(p => [...p, { id, name, content: b.code, language: detectLang(name) }]); openInTab(id); }
      showToast && showToast("Applied to " + name);
    }

    // ── Editor helpers ────────────────────────────────────────────
    const editorContent = activeFile?.content || "";
    const editorLines = editorContent.split("\n");
    const editorByteSize = new Blob([editorContent]).size;
    // Find-in-file: count matches in active file
    const findMatches = findQuery && activeFile ? (editorContent.match(new RegExp(findQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi")) || []).length : 0;

    function syncScroll(e) {
      if (lineNumbersRef.current) lineNumbersRef.current.scrollTop = e.target.scrollTop;
      if (hlRef.current) { hlRef.current.scrollTop = e.target.scrollTop; hlRef.current.scrollLeft = e.target.scrollLeft; }
    }
    const editorLang = detectLang(activeFile?.name);
    const hlActive = hlOn && !!activeFile && editorContent.length <= 120000 && editorLines.length <= 3000 && hlFamily(editorLang) !== null;
    const hlHtml = useMemo(() => (hlActive ? highlightToHtml(editorContent, editorLang, isLightEditor) : null), [hlActive, editorContent, editorLang, isLightEditor]);
    function setEditorContent(next) {
      if (!activeFile) return;
      setFiles(p => p.map(f => f.id === activeFile.id ? Object.assign({}, f, { content: next }) : f));
    }

    // ── Tiny markdown renderer for preview ───────────────────────
    function mdToHtml(md) {
      let s = String(md || "");
      s = s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      // Code blocks (preserve before inline rules)
      s = s.replace(new RegExp(FENCE + "([a-zA-Z0-9_+-]*)\\n([\\s\\S]*?)" + FENCE, "g"), (_, lang, code) => "<pre style=\"background:" + T.panel + ";border:1px solid " + T.dim + ";border-radius:6px;padding:10px 12px;overflow-x:auto;font-family:" + monoStack + ";font-size:12px;\"><code>" + code + "</code></pre>");
      s = s.replace(/^### (.*)$/gm, "<h3 style=\"font-size:14px;font-weight:700;margin:14px 0 6px;color:" + T.text + ";\">$1</h3>");
      s = s.replace(/^## (.*)$/gm, "<h2 style=\"font-size:16px;font-weight:700;margin:16px 0 8px;color:" + T.text + ";\">$1</h2>");
      s = s.replace(/^# (.*)$/gm, "<h1 style=\"font-size:20px;font-weight:800;margin:18px 0 10px;color:" + T.text + ";\">$1</h1>");
      s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
      s = s.replace(/(?<![*_])\*([^*\n]+?)\*(?![*_])/g, "<em>$1</em>");
      s = s.replace(/`([^`]+)`/g, "<code style=\"background:" + T.panel + ";padding:1px 5px;border-radius:3px;font-family:" + monoStack + ";font-size:0.9em;\">$1</code>");
      s = s.replace(/^[-*] (.*)$/gm, "<li style=\"margin-left:18px;\">$1</li>");
      s = s.replace(/(<li[^>]*>[\s\S]*?<\/li>\n?)+/g, m => "<ul style=\"margin:6px 0;\">" + m + "</ul>");
      s = s.replace(/\n{2,}/g, "</p><p style=\"margin:6px 0;\">");
      s = "<p style=\"margin:6px 0;\">" + s + "</p>";
      s = s.replace(/<p[^>]*><(h[1-3]|ul|pre)/g, "<$1").replace(/<\/(h[1-3]|ul|pre)><\/p>/g, "</$1>");
      return s;
    }

    const quickSwitchFiltered = quickQuery
      ? files.filter(f => f.name.toLowerCase().includes(quickQuery.toLowerCase()))
      : files.slice(0, 12);

    // ── Render ──────────────────────────────────────────────────
    const isMd = activeFile?.name?.endsWith(".md");
    const showSplit = !!activeFile && showEditor;
    // Effective mobile pane: if "editor" is selected but nothing is open, show
    // the terminal instead of a blank pane.
    const termPane = (termTab === "editor" && !showSplit) ? "terminal" : termTab;

    return (
      <div className="cf-page-fade-in"
        onDragOver={e=>{e.preventDefault(); if(!isDragging) setIsDragging(true);}}
        onDragLeave={e=>{ if(e.target===e.currentTarget) setIsDragging(false); }}
        onDrop={onDrop}
        style={{flex:1,display:"flex",flexDirection:"column",background:T.bg,color:T.text,fontFamily:monoStack,overflow:"hidden",minHeight:0,position:"relative"}}>
        {isDragging && (
          <div style={{position:"absolute",inset:0,background:T.accent+"15",border:"3px dashed "+T.accent,zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none"}}>
            <div style={{padding:"24px 36px",background:T.chrome,border:"1px solid "+T.dim,borderRadius:14,fontSize:14,color:T.text,fontWeight:700,letterSpacing:"0.05em"}}>↓ Drop files to import</div>
          </div>
        )}
        {/* Title bar */}
        <div style={{padding:"10px 16px",borderBottom:"1px solid "+T.dim,display:"flex",alignItems:"center",gap:10,flexShrink:0,background:T.chrome,flexWrap:"wrap",rowGap:6}}>
          <span style={{display:"inline-flex",gap:6}}>
            <span style={{width:11,height:11,borderRadius:"50%",background:"#ff5f56"}}/>
            <span style={{width:11,height:11,borderRadius:"50%",background:"#ffbd2e"}}/>
            <span style={{width:11,height:11,borderRadius:"50%",background:"#27c93f"}}/>
          </span>
          <span style={{fontSize:11,color:T.text2,letterSpacing:"0.04em"}}>chatfort@terminal</span>
          <span style={{fontSize:10,color:T.text2,opacity:0.6}}>~/{activeFile?.name || "workspace"}</span>
          <div style={{marginLeft:"auto",display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",rowGap:6,justifyContent:"flex-end"}}>
            {!termIsMobile && (<>
            <button onClick={()=>setShowFiles(p=>!p)} title="Toggle file rail" style={btnSm(T)}>{showFiles?"hide files":"show files"}</button>
            <button onClick={()=>setShowTranscript(p=>!p)} title="Toggle terminal panel" style={btnSm(T)}>{showTranscript?"hide terminal":"show terminal"}</button>
            </>)}
            <button onClick={downloadWorkspace} title="Download workspace as JSON" style={btnSm(T)}>↓ workspace</button>
            <select value={editorTheme} onChange={e=>setEditorTheme(e.target.value)} title="Editor theme (auto follows app light/dark)" style={Object.assign({}, btnSm(T), {paddingRight:18, appearance:"none"})}>
              <option value="auto">auto (follow app)</option>
              <option value="midnight">midnight</option>
              <option value="ocean">ocean</option>
              <option value="paper">paper</option>
            </select>
            <button onClick={()=>setShowHelp(true)} title="Help (slash commands + shortcuts)" style={btnSm(T)}>? help</button>
          </div>
        </div>
        <div style={{borderBottom:"1px solid "+T.dim,background:T.panel,padding:"12px 16px",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap",flexShrink:0}}>
          <div aria-hidden="true" style={{width:34,height:34,borderRadius:"50%",background:`radial-gradient(circle at 34% 25%, #fff 0%, ${T.accent} 62%, ${T.dim} 100%)`,boxShadow:`0 10px 26px ${T.accent}33`,position:"relative",flexShrink:0}}>
            <span style={{position:"absolute",right:6,top:7,width:7,height:7,borderRadius:"50%",background:"#fff"}}/>
          </div>
          <div style={{flex:"1 1 320px",minWidth:0}}>
            <div style={{fontSize:12,fontWeight:800,color:T.text,letterSpacing:"0.02em"}}>Hermes Code mode</div>
            <div style={{fontSize:11,color:T.text2,lineHeight:1.45,marginTop:2,fontFamily:"var(--cf-brand-font, inherit)"}}>
              Use this friendly IDE lane when a Hermes Co-work or orb request turns into code. For documents, research, spreadsheets, and slides, stay in Co-work and let Hermes return downloadable files there.
            </div>
          </div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"flex-end"}}>
            <button type="button" onClick={()=>setSection && setSection("cowork")} style={Object.assign({}, btnSm(T), {color:T.accent,borderColor:T.accent,fontWeight:700})}>Co-work</button>
            <button type="button" onClick={()=>setSection && setSection("hermes")} style={btnSm(T)}>Runs</button>
          </div>
        </div>
        {/* Compact pane switcher: one of Prompt / Files / Editor at a time. */}
        {termIsMobile && (
          <div style={{display:"flex",borderBottom:"1px solid "+T.dim,background:T.chrome,flexShrink:0}}>
            {[["terminal","Prompt"],["files","Files"],...(showSplit?[["editor","Editor"]]:[])].map(([k,label]) => (
              <button key={k} onClick={()=>setTermTab(k)} style={{flex:1,padding:"9px 0",background:termPane===k?T.accent+"22":"transparent",border:"none",borderBottom:"2px solid "+(termPane===k?T.accent:"transparent"),color:termPane===k?T.accent:T.text2,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{label}</button>
            ))}
          </div>
        )}
        {/* Body */}
        <div style={{flex:1,display:"flex",flexDirection:termIsMobile?"column":"row",minHeight:0,overflow:"hidden"}}>
          {/* File rail */}
          {(termIsMobile ? termPane === "files" : showFiles) && (
            <div style={{width:termIsMobile?"100%":212,minWidth:termIsMobile?0:212,flex:termIsMobile?"1 1 auto":"0 0 auto",borderRight:termIsMobile?"none":"1px solid "+T.dim,display:"flex",flexDirection:"column",background:T.panel,overflow:"hidden"}}>
              {/* Rail menu: New session / Routines / Customize / More */}
              <div style={{padding:"6px 4px",borderBottom:"1px solid "+T.dim,display:"flex",flexDirection:"column",gap:1}}>
                {[
                  { id:"new",       label:"New session", icon:"+",  onClick:newSession },
                  { id:"routines",  label:"Routines",    icon:"⚡", onClick:()=>{setShowRoutinesPanel(true);} },
                  { id:"customize", label:"Customize",   icon:"⚙", onClick:()=>{setShowCustomize(true);} },
                  { id:"more",      label:"More",        icon:"˅", onClick:()=>{setShowMoreMenu(p=>!p);} },
                ].map(it => (
                  <button key={it.id} onClick={it.onClick} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 10px",background:"none",border:"none",color:T.text,fontSize:12,cursor:"pointer",fontFamily:"inherit",borderRadius:6,textAlign:"left",transition:"background .1s ease"}}
                    onMouseEnter={e=>e.currentTarget.style.background=T.active}
                    onMouseLeave={e=>e.currentTarget.style.background="none"}>
                    <span style={{width:18,textAlign:"center",color:T.accent,fontSize:14,lineHeight:1,fontWeight:600}}>{it.icon}</span>
                    <span>{it.label}</span>
                  </button>
                ))}
                {showMoreMenu && (<>
                  <div onClick={()=>setShowMoreMenu(false)} style={{position:"fixed",inset:0,zIndex:996}}/>
                  <div style={{position:"absolute",left:44,marginTop:2,background:T.panel,border:"1px solid "+T.dim,borderRadius:10,minWidth:220,boxShadow:"0 12px 32px rgba(0,0,0,0.4)",zIndex:997,fontFamily:monoStack,fontSize:12,padding:"6px 0"}}>
                    <button onClick={()=>{downloadWorkspace(); setShowMoreMenu(false);}} style={moreItemStyle(T)}>↓ Download workspace</button>
                    <button onClick={()=>{importFileRef.current?.click();}} style={moreItemStyle(T)}>↑ Import workspace…</button>
                    <input ref={importFileRef} type="file" accept=".json,application/json" style={{display:"none"}} onChange={e=>{importWorkspaceFromFile(e.target.files?.[0]); e.target.value="";}}/>
                    <button onClick={()=>{setShowCheckpoints(true); setShowMoreMenu(false);}} style={moreItemStyle(T)}>⏪ Checkpoints…</button>
                    <button onClick={()=>{setShowHelp(true); setShowMoreMenu(false);}} style={moreItemStyle(T)}>? Keyboard shortcuts</button>
                    <div style={{borderTop:"1px solid "+T.dim,margin:"4px 0"}}/>
                    <button onClick={()=>{handleSlash("/reset"); setShowMoreMenu(false);}} style={Object.assign({}, moreItemStyle(T), {color:"#e35d5d"})}>✕ Reset everything</button>
                  </div>
                </>)}
              </div>
              <div style={{padding:"10px 12px",borderBottom:"1px solid "+T.dim,fontSize:10,color:T.text2,letterSpacing:"0.06em",textTransform:"uppercase",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <span>workspace</span>
                <button onClick={()=>{const n=window.prompt("New file name (e.g. main.py):"); if(n)handleSlash("/new "+n);}} title="New file" style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:14,padding:0,lineHeight:1}}>+</button>
              </div>
              <div style={{flex:1,overflowY:"auto",padding:"4px 0"}}>
                {files.length === 0 ? (
                  <div style={{padding:"14px 12px",fontSize:10,color:T.text2,opacity:0.7,lineHeight:1.55}}>Empty workspace. <span style={{color:T.accent}}>/new file.py</span>, drop files here, or paste code into the prompt.</div>
                ) : files.map(f => {
                  const isActive = f.id === activeFileId;
                  const ic = fileIconFor(f.name);
                  const isRenaming = renaming === f.id;
                  return (
                    <div key={f.id} onClick={()=>{ if(!isRenaming) openInTab(f.id); }} onDoubleClick={()=>startRename(f.id)} style={{padding:"6px 10px 6px 12px",cursor:"pointer",fontSize:11,display:"flex",alignItems:"center",gap:7,background:isActive?T.active:"none",borderLeft:isActive?"2px solid "+T.accent:"2px solid transparent",color:isActive?T.text:T.text2}}>
                      <span title={ic.label} style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:18,height:14,borderRadius:3,background:ic.color+"22",border:"1px solid "+ic.color+"55",color:ic.color,fontSize:7,fontWeight:800,flexShrink:0,letterSpacing:"0.02em"}}>{ic.label.slice(0,3)}</span>
                      {isRenaming ? (
                        <input autoFocus value={renameValue} onChange={e=>setRenameValue(e.target.value)} onBlur={commitRename} onKeyDown={e=>{if(e.key==="Enter")commitRename(); if(e.key==="Escape"){setRenaming(null);}}} onClick={e=>e.stopPropagation()} style={{flex:1,background:T.chrome,border:"1px solid "+T.accent,borderRadius:4,padding:"2px 6px",color:T.text,fontSize:11,fontFamily:"inherit",outline:"none",minWidth:0}}/>
                      ) : (
                        <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</span>
                      )}
                      {!isRenaming && (
                        <button onClick={(e)=>{e.stopPropagation(); deleteFile(f.id);}} style={{background:"none",border:"none",color:T.text2,cursor:"pointer",fontSize:11,padding:0,opacity:0.5}}>×</button>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{padding:"8px 12px",borderTop:"1px solid "+T.dim,fontSize:9,color:T.text2,opacity:0.6,display:"flex",justifyContent:"space-between"}}>
                <span>{files.length} file{files.length===1?"":"s"}</span>
                <span>{savedAt ? "saved " + new Date(savedAt).toLocaleTimeString() : ""}</span>
              </div>
            </div>
          )}
          {/* Editor area (tabs + line numbers + editor + status bar) */}
          {(termIsMobile ? termPane === "editor" : showSplit) && (
            <div style={{flex:1,display:"flex",flexDirection:"column",minWidth:0,borderRight:(!termIsMobile&&showTranscript)?("1px solid "+T.dim):"none",background:T.panel}}>
              {/* Tabs */}
              <div style={{display:"flex",alignItems:"stretch",borderBottom:"1px solid "+T.dim,background:T.chrome,overflowX:"auto"}}>
                {openTabs.map(tid => {
                  const f = files.find(ff => ff.id === tid);
                  if (!f) return null;
                  const isCur = tid === activeFileId;
                  const ic = fileIconFor(f.name);
                  return (
                    <div key={tid} onClick={()=>setActiveFileId(tid)} style={{padding:"7px 10px 7px 12px",display:"flex",alignItems:"center",gap:6,cursor:"pointer",borderRight:"1px solid "+T.dim,background:isCur?T.panel:"transparent",borderBottom:isCur?"2px solid "+T.accent:"2px solid transparent",fontSize:11,color:isCur?T.text:T.text2,whiteSpace:"nowrap",flexShrink:0}}>
                      <span style={{display:"inline-block",width:6,height:6,borderRadius:"50%",background:ic.color,flexShrink:0}}/>
                      <span>{f.name}</span>
                      <button onClick={(e)=>{e.stopPropagation(); closeTab(tid);}} title="Close" style={{background:"none",border:"none",color:T.text2,cursor:"pointer",fontSize:13,padding:0,lineHeight:1,opacity:0.6}}>×</button>
                    </div>
                  );
                })}
                <div style={{flex:1}}/>
                {isMd && (<button onClick={()=>setMdPreview(p=>!p)} title="Toggle markdown preview" style={Object.assign({}, btnSm(T), {margin:"4px 6px"})}>{mdPreview ? "edit" : "preview"}</button>)}
                {activeFile?.name?.endsWith(".json") && (<button onClick={formatJsonActive} title="Format JSON" style={Object.assign({}, btnSm(T), {margin:"4px 6px"})}>{ }format</button>)}
                {(activeFile && /\.(m?js)$/i.test(activeFile.name)) && (<button onClick={()=>runFileInSandbox(activeFile)} title="Run in a sandboxed Web Worker — console output goes to the terminal" style={Object.assign({}, btnSm(T), {margin:"4px 6px",color:"#34c98e",borderColor:"#34c98e"})}>▶ run</button>)}
                {(activeFile && /\.(html?|svg)$/i.test(activeFile.name)) && (<button onClick={()=>setPreview(activeFile.content)} title="Render this file in a live preview" style={Object.assign({}, btnSm(T), {margin:"4px 6px",color:T.accent,borderColor:T.accent})}>▶ preview</button>)}
              </div>
              {/* Find bar */}
              {findOpen && (
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",borderBottom:"1px solid "+T.dim,background:T.chrome,fontSize:11}}>
                  <span style={{color:T.text2}}>Find:</span>
                  <input ref={findInputRef} value={findQuery} onChange={e=>setFindQuery(e.target.value)} placeholder="search current file…" style={{flex:1,background:T.panel,border:"1px solid "+T.dim,borderRadius:5,padding:"4px 10px",color:T.text,fontSize:11,fontFamily:"inherit",outline:"none"}} onKeyDown={e=>{if(e.key==="Escape")setFindOpen(false);}}/>
                  <span style={{color:T.text2,fontFamily:"monospace",minWidth:60,textAlign:"right"}}>{findQuery ? findMatches + " match" + (findMatches===1?"":"es") : ""}</span>
                  <button onClick={()=>setFindOpen(false)} style={{background:"none",border:"none",color:T.text2,cursor:"pointer",fontSize:13,padding:0}}>×</button>
                </div>
              )}
              {/* Editor / preview */}
              <div style={{flex:1,display:"flex",minHeight:0,position:"relative"}}>
                {isMd && mdPreview ? (
                  <div style={{flex:1,overflowY:"auto",padding:"18px 22px",fontFamily:"var(--cf-brand-font, inherit)",fontSize:14,lineHeight:1.65,color:T.text,background:T.bg}} dangerouslySetInnerHTML={{__html: mdToHtml(editorContent)}}/>
                ) : (<>
                  {/* Line numbers gutter */}
                  <div ref={lineNumbersRef} style={{width:48,minWidth:48,padding:"14px 6px 14px 8px",background:T.gutterBg,borderRight:"1px solid "+T.dim,color:T.gutter,fontSize:Math.max(10,fontSize-1),fontFamily:monoStack,lineHeight:1.55,overflow:"hidden",textAlign:"right",userSelect:"none",whiteSpace:"pre"}}>
                    {editorLines.map((_, i) => (i+1)).join("\n")}
                  </div>
                  <div style={{flex:1,position:"relative",minWidth:0,background:T.bg}}>
                    {hlActive && hlHtml !== null && (
                      <pre ref={hlRef} aria-hidden="true" dangerouslySetInnerHTML={{__html: hlHtml}} style={{position:"absolute",inset:0,margin:0,padding:"14px 16px",overflow:"auto",scrollbarWidth:"none",scrollbarGutter:"stable",boxSizing:"border-box",color:T.text,fontFamily:monoStack,fontSize:fontSize,lineHeight:1.55,whiteSpace:wordWrap?"pre-wrap":"pre",overflowWrap:wordWrap?"break-word":"normal",tabSize:tabSize,pointerEvents:"none"}}/>
                    )}
                    <textarea ref={editorRef} value={editorContent} onChange={e=>{setEditorContent(e.target.value); trackCursor(e.target);}} onSelect={e=>trackCursor(e.target)} onKeyUp={e=>trackCursor(e.target)} onScroll={syncScroll} spellCheck={false} style={{position:"absolute",inset:0,width:"100%",height:"100%",boxSizing:"border-box",scrollbarGutter:"stable",background:"transparent",border:"none",outline:"none",color:hlActive&&hlHtml!==null?"transparent":T.text,caretColor:T.text,fontFamily:monoStack,fontSize:fontSize,lineHeight:1.55,padding:"14px 16px",resize:"none",whiteSpace:wordWrap?"pre-wrap":"pre",overflowWrap:wordWrap?"break-word":"normal",tabSize:tabSize}}/>
                  </div>
                </>)}
              </div>
              {/* Status bar */}
              <div style={{display:"flex",alignItems:"center",gap:14,padding:"4px 14px",borderTop:"1px solid "+T.dim,background:T.chrome,fontSize:10,color:T.text2,fontFamily:monoStack,letterSpacing:"0.03em",flexShrink:0}}>
                <span>Ln {cursorPos.line}, Col {cursorPos.col}</span>
                <span>{detectLang(activeFile?.name)}</span>
                <span>{editorByteSize}B</span>
                <span>{editorLines.length} line{editorLines.length===1?"":"s"}</span>
                <button onClick={()=>setWordWrap(p=>!p)} title="Toggle word wrap" style={{background:"none",border:"none",color:wordWrap?T.accent:T.text2,cursor:"pointer",fontSize:10,padding:0,fontFamily:"inherit",letterSpacing:"inherit"}}>{wordWrap?"wrap on":"wrap off"}</button>
                <button onClick={()=>setHlOn(p=>!p)} title="Toggle syntax highlighting (auto-off for very large files)" style={{background:"none",border:"none",color:hlActive?T.accent:T.text2,cursor:"pointer",fontSize:10,padding:0,fontFamily:"inherit",letterSpacing:"inherit"}}>{hlOn?"hl on":"hl off"}</button>
                <button onClick={()=>setFindOpen(p=>!p)} title="Find (Ctrl/Cmd+F)" style={{background:"none",border:"none",color:findOpen?T.accent:T.text2,cursor:"pointer",fontSize:10,padding:0,fontFamily:"inherit"}}>find</button>
                <div style={{flex:1}}/>
                {savedAt && <span style={{opacity:0.7}}>saved {new Date(savedAt).toLocaleTimeString()}</span>}
              </div>
            </div>
          )}
          {/* Transcript panel */}
          {(termIsMobile ? termPane === "terminal" : showTranscript) && (
            <div style={{flex:termIsMobile?1:(showSplit?"0 0 38%":1),minWidth:termIsMobile?0:(showSplit?320:0),display:"flex",flexDirection:"column",background:T.bg,minHeight:0}}>
              <div ref={scrollerRef} style={{flex:1,overflowY:"auto",padding:"18px 22px",fontSize:13,lineHeight:1.55}}>
                {messages.length === 0 ? (
                  <div style={{color:T.text2,fontSize:12,maxWidth:580,lineHeight:1.7}}>
                    <div style={{color:T.accent,fontSize:14,fontWeight:700,marginBottom:8}}>Hermes Code mode</div>
                    <div style={{marginBottom:14}}>Describe a coding task and Hermes reads, writes, and edits files in a lightweight workspace. Multi-tab editor, find-in-file, markdown preview, themes, drag-drop import, checkpoints, and workspace download keep it useful without making it the default path for everyday knowledge work.</div>
                    <div style={{color:T.text2,marginBottom:6}}>Try:</div>
                    <ul style={{listStyle:"none",padding:0,marginBottom:14}}>
                      {["build a tic-tac-toe game in game.html","write utils.js with debounce + throttle, then test them","read game.html and make the computer player unbeatable","/agent off — switch back to classic chat + /apply"].map((c,i)=>(<li key={i} style={{padding:"3px 0",color:T.text}}>  <span style={{color:T.accent}}>$</span> {c}</li>))}
                    </ul>
                    <div style={{color:T.text2,fontSize:11}}>Type <span style={{color:T.accent}}>/help</span> for commands · <span style={{color:T.accent}}>Ctrl/Cmd+P</span> quick switch · <span style={{color:T.accent}}>Ctrl/Cmd+Shift+P</span> command palette · <span style={{color:T.accent}}>Ctrl/Cmd+F</span> find · <span style={{color:T.accent}}>F2</span> rename.</div>
                  </div>
                ) : messages.map(m => {
                  const lineCommon = {marginBottom:14,display:"flex",gap:12,alignItems:"flex-start"};
                  if (m.role === "user") return (<div key={m.id} style={lineCommon}><span style={{color:T.accent,fontWeight:700,flexShrink:0,paddingTop:1}}>$</span><div style={{flex:1,minWidth:0,color:T.text,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{m.content}</div></div>);
                  if (m.role === "system") return (<div key={m.id} style={Object.assign({}, lineCommon, {opacity:0.85})}><span style={{color:T.text2,flexShrink:0,paddingTop:1,fontSize:11}}>›</span><div style={{flex:1,minWidth:0,color:T.text2,whiteSpace:"pre-wrap",wordBreak:"break-word",fontSize:11.5}}>{m.content}</div></div>);
                  const blocks = parseBlocks(m.content);
                  return (
                    <div key={m.id} style={Object.assign({}, lineCommon, {marginBottom:18})}>
                      <span style={{color:T.accent,fontWeight:700,flexShrink:0,paddingTop:1}}>✦</span>
                      <div style={{flex:1,minWidth:0,color:T.text}}>
                        {blocks.map((b,i) => b.type === "text" ? (
                          <div key={i} style={{whiteSpace:"pre-wrap",wordBreak:"break-word",marginBottom:b.value.trim()?8:0,fontFamily:"var(--cf-brand-font, inherit)",fontSize:13,lineHeight:1.55}}>{b.value}</div>
                        ) : b.lang === "cf-tool" ? (
                          <div key={i} style={{margin:"6px 0",display:"inline-flex",alignItems:"center",gap:7,padding:"4px 10px",background:T.accent+"14",border:"1px solid "+T.accent+"44",borderRadius:7,fontSize:10.5,color:T.accent,fontWeight:700,letterSpacing:"0.04em"}}>
                            <span>⚙</span>
                            <span>{(() => { try { const a = JSON.parse(b.code); return a.tool + (a.name ? " → " + a.name : ""); } catch { return "action"; } })()}</span>
                          </div>
                        ) : (
                          <div key={i} style={{margin:"8px 0",background:T.panel,border:"1px solid "+T.dim,borderRadius:8,overflow:"hidden"}}>
                            <div style={{padding:"6px 12px",borderBottom:"1px solid "+T.dim,display:"flex",alignItems:"center",gap:8,fontSize:10,color:T.text2,letterSpacing:"0.05em"}}>
                              <span style={{textTransform:"uppercase"}}>{b.lang || "code"}</span>
                              <span style={{flex:1}}/>
                              {isPreviewable(b.lang, b.code) && <button onClick={()=>setPreview(b.code)} title="Render this in a live preview" style={Object.assign({}, btnSm(T), {color:T.accent,borderColor:T.accent})}>▶ preview</button>}
                              <button onClick={()=>{navigator.clipboard?.writeText(b.code); showToast && showToast("Copied!");}} style={btnSm(T)}>copy</button>
                              <button onClick={()=>applyBlock(b)} style={Object.assign({}, btnSm(T), {background:T.accent,color:"#fff",borderColor:T.accent,fontWeight:700})}>APPLY</button>
                            </div>
                            <pre style={{margin:0,padding:"12px 14px",overflowX:"auto",color:T.text,fontSize:12,lineHeight:1.55,fontFamily:monoStack,whiteSpace:"pre"}}><code>{b.code}</code></pre>
                          </div>
                        ))}
                        {m.streaming && <span style={{display:"inline-block",width:8,height:13,background:T.accent,marginLeft:2,animation:"blink 0.9s step-end infinite",verticalAlign:"middle"}}/>}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Agent change review */}
              {pendingReview && pendingReview.items && pendingReview.items.length > 0 && (
                <div style={{borderTop:"1px solid "+T.dim,background:T.chrome,flexShrink:0,maxHeight:"44%",overflowY:"auto"}}>
                  <div style={{padding:"8px 16px",display:"flex",alignItems:"center",gap:10,fontSize:11,fontWeight:700,color:T.text,position:"sticky",top:0,background:T.chrome,borderBottom:"1px solid "+T.dim+"66",zIndex:2}}>
                    <span style={{color:T.accent}}>✎ {pendingReview.items.length} file{pendingReview.items.length===1?"":"s"} changed</span>
                    <span style={{flex:1}}/>
                    <button onClick={()=>setPendingReview(null)} style={Object.assign({}, btnSm(T), {fontWeight:700})}>keep all</button>
                    <button onClick={()=>{ for (const it of pendingReview.items.slice()) reviewRevert(it.name); }} style={Object.assign({}, btnSm(T), {color:"#e35d5d",borderColor:"#e35d5d55",fontWeight:700})}>revert all</button>
                  </div>
                  {pendingReview.items.map(it => {
                    const st = diffStats(it.before, it.after);
                    const open = !!diffOpen[it.name];
                    return (
                      <div key={it.name} style={{borderBottom:"1px solid "+T.dim+"44"}}>
                        <div style={{padding:"6px 16px",display:"flex",alignItems:"center",gap:9,fontSize:11}}>
                          <button onClick={()=>setDiffOpen(p=>Object.assign({}, p, {[it.name]: !open}))} style={{background:"none",border:"none",color:T.text2,cursor:"pointer",fontSize:10,padding:0,width:12}}>{open?"▾":"▸"}</button>
                          <span style={{color:T.text,fontWeight:600,cursor:"pointer"}} onClick={()=>{const f=files.find(ff=>ff.name===it.name); if(f) openInTab(f.id);}}>{it.name}</span>
                          {it.before === null && <span style={{fontSize:9,color:T.accent,border:"1px solid "+T.accent+"55",borderRadius:4,padding:"0 5px"}}>new</span>}
                          {it.after === null && <span style={{fontSize:9,color:"#e35d5d",border:"1px solid #e35d5d55",borderRadius:4,padding:"0 5px"}}>deleted</span>}
                          <span style={{color:"#34c98e",fontFamily:"monospace",fontSize:10}}>+{st.adds}</span>
                          <span style={{color:"#e35d5d",fontFamily:"monospace",fontSize:10}}>−{st.dels}</span>
                          <span style={{flex:1}}/>
                          <button onClick={()=>reviewKeep(it.name)} style={btnSm(T)}>keep</button>
                          <button onClick={()=>reviewRevert(it.name)} style={Object.assign({}, btnSm(T), {color:"#e35d5d",borderColor:"#e35d5d55"})}>revert</button>
                        </div>
                        {open && (() => {
                          const d = lineDiff(it.before, it.after);
                          if (!d) return <div style={{padding:"6px 16px 10px 37px",fontSize:10,color:T.text2}}>File too large to diff inline — use keep/revert.</div>;
                          const keep = new Set();
                          d.forEach((x, idx) => { if (x.t !== " ") for (let k = idx - 2; k <= idx + 2; k++) if (k >= 0 && k < d.length) keep.add(k); });
                          const rows = [];
                          let skipping = 0;
                          d.forEach((x, idx) => {
                            if (!keep.has(idx)) { skipping++; return; }
                            if (skipping > 0) { rows.push({ fold: skipping }); skipping = 0; }
                            rows.push(x);
                          });
                          if (skipping > 0) rows.push({ fold: skipping });
                          return (
                            <div style={{margin:"0 16px 10px 37px",border:"1px solid "+T.dim,borderRadius:7,overflow:"auto",maxHeight:240,fontFamily:monoStack,fontSize:11,lineHeight:1.5,background:T.bg}}>
                              {rows.map((r, ri) => r.fold ? (
                                <div key={ri} style={{padding:"1px 10px",color:T.text2,opacity:0.6,fontSize:9,background:T.chrome}}>··· {r.fold} unchanged line{r.fold===1?"":"s"}</div>
                              ) : (
                                <div key={ri} style={{padding:"0 10px",whiteSpace:"pre",color:r.t==="+"?"#34c98e":r.t==="-"?"#e35d5d":T.text2,background:r.t==="+"?"#34c98e14":r.t==="-"?"#e35d5d14":"transparent"}}>{(r.t==="+"?"+ ":r.t==="-"?"− ":"  ") + r.line}</div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Input */}
              <div style={{position:"relative",borderTop:"1px solid "+T.dim,background:T.chrome,padding:"10px 16px"}}>
                {suggestions.length > 0 && (
                  <div style={{position:"absolute",bottom:"100%",left:16,right:16,background:T.panel,border:"1px solid "+T.dim,borderRadius:8,padding:6,marginBottom:6,boxShadow:"0 -8px 24px rgba(0,0,0,0.4)"}}>
                    {suggestions.map(s => (
                      <div key={s.cmd} onClick={()=>{setInput(s.cmd+" "); taRef.current?.focus();}} style={{padding:"6px 10px",borderRadius:5,cursor:"pointer",display:"flex",gap:10,alignItems:"baseline",fontSize:11}}>
                        <span style={{color:T.accent,fontWeight:700,minWidth:62}}>{s.cmd}</span>
                        <span style={{color:T.text2,minWidth:110}}>{s.args}</span>
                        <span style={{color:T.text2,opacity:0.7}}>{s.desc}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{display:"flex",alignItems:"flex-start",gap:8}}>
                  <span style={{color:T.accent,fontWeight:700,fontSize:14,paddingTop:6,flexShrink:0}}>$</span>
                  <textarea ref={taRef} value={input} onChange={e=>{setInput(e.target.value); e.target.style.height="auto"; e.target.style.height=Math.min(e.target.scrollHeight,200)+"px";}} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();} if(e.key==="Tab"&&suggestions.length>0){e.preventDefault();setInput(suggestions[0].cmd+" ");}}} placeholder={streaming?(agentMode?"agent working — STOP to halt":"…"):(agentMode?"describe a task — the agent reads, edits & creates files (/ for commands)":"ask anything, or type / for commands")} disabled={streaming} rows={1} style={{flex:1,background:"none",border:"none",outline:"none",color:T.text,fontFamily:"inherit",fontSize:13,lineHeight:1.55,resize:"none",minHeight:22,maxHeight:200,padding:"3px 0"}}/>
                  {streaming ? (
                    <button onClick={()=>{stopRef.current=true; stopActiveGeneration();}} title="Stop the run" style={{padding:"5px 14px",borderRadius:7,background:"#e35d5d",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",border:"none",fontFamily:"inherit",flexShrink:0,letterSpacing:"0.04em"}}>STOP</button>
                  ) : (
                    <button onClick={send} disabled={!input.trim()} style={{padding:"5px 14px",borderRadius:7,background:input.trim()?T.accent:T.dim,color:input.trim()?"#fff":T.text2,fontSize:11,fontWeight:700,cursor:input.trim()?"pointer":"default",border:"none",fontFamily:"inherit",flexShrink:0,letterSpacing:"0.04em"}}>SEND</button>
                  )}
                </div>
                <div style={{marginTop:6,fontSize:9,color:T.text2,opacity:0.55,letterSpacing:"0.05em",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <span>enter to send · shift+enter newline · mode:</span>
                  <button onClick={()=>handleSlash("/agent")} title="Toggle agent mode (the model edits workspace files itself)" style={{background:agentMode?T.accent+"22":"none",border:"1px solid "+(agentMode?T.accent:T.dim),color:agentMode?T.accent:T.text2,fontSize:9,padding:"1px 8px",borderRadius:5,cursor:"pointer",fontFamily:"inherit",letterSpacing:"0.04em",fontWeight:700}}>{agentMode?"⚙ AGENT":"CHAT"}</button>
                  <span>· model:</span>
                  <button onClick={()=>setTermModelOpen(p=>!p)} style={{background:"none",border:"1px solid "+T.dim,color:T.accent,fontSize:9,padding:"1px 8px",borderRadius:5,cursor:"pointer",fontFamily:"inherit",letterSpacing:"0.04em"}}>{termModel} ▾</button>
                </div>
                {termModelOpen && (<>
                  <div onClick={()=>setTermModelOpen(false)} style={{position:"fixed",inset:0,zIndex:998}}/>
                  <div style={{position:"absolute",bottom:"calc(100% + 6px)",right:16,background:T.panel,border:"1px solid "+T.dim,borderRadius:10,width:"min(360px,92vw)",maxHeight:"50dvh",overflowY:"auto",zIndex:999,boxShadow:"0 -8px 32px rgba(0,0,0,0.5)",fontFamily:monoStack}}>
                    <div style={{padding:"10px 14px",borderBottom:"1px solid "+T.dim,fontSize:10,color:T.accent,letterSpacing:"0.06em",fontWeight:700}}>SELECT MODEL</div>
                    {(() => {
                      // Group flat model list by provider. OpenRouter
                      // rows sub-group by their REAL upstream (Anthropic
                      // via OR / OpenAI via OR / ...) so e.g. 50
                      // OR-routed Anthropic rows do not pile into one
                      // generic OpenRouter section.
                      const flat = buildTerminalModelList();
                      const groups = new Map();
                      for (const m of flat) {
                        let key = m.provider;
                        let label = m.providerName || m.provider;
                        let color = m.providerColor;
                        if (m.provider === "openrouter") {
                          const prefix = String(m.id).split("/")[0];
                          const upstream = OR_REAL_PROVIDER[prefix];
                          if (upstream) { key = "or:" + upstream.key; label = upstream.label + " (via OpenRouter)"; color = upstream.color; }
                        }
                        if (!groups.has(key)) groups.set(key, { label, color, rows: [], pid: m.provider });
                        groups.get(key).rows.push(m);
                      }
                      // Sort groups alphabetically by label; within each
                      // group sort rows by name. The active model bubbles
                      // to the very top of its group for one-glance "you
                      // are here" feedback.
                      const ordered = Array.from(groups.values()).sort((a,b) => a.label.localeCompare(b.label));
                      for (const g of ordered) {
                        g.rows.sort((a,b) => (a.id === termModel ? -1 : b.id === termModel ? 1 : (a.name || "").localeCompare(b.name || "")));
                      }
                      return ordered.map(g => (<div key={g.label}>
                        <div style={{padding:"7px 14px 5px",fontSize:9,letterSpacing:"0.07em",textTransform:"uppercase",color:T.text2,background:T.chrome,borderBottom:"1px solid "+T.dim+"55",display:"flex",alignItems:"center",gap:7,position:"sticky",top:0,zIndex:1}}>
                          {(() => {
                            // Resolve a logo for the group. For "or:X"
                            // groups we pass the upstream key so the
                            // logo shows the real brand (Anthropic /
                            // OpenAI / etc.), not generic OpenRouter.
                            const upstreamKey = g.label.includes("via OpenRouter") ? (Array.from(Object.entries(OR_REAL_PROVIDER)).find(([_,v]) => v.label === g.label.replace(" (via OpenRouter)",""))?.[1]?.key || "openrouter") : g.pid;
                            return <ProviderLogo providerKey={upstreamKey} color={g.color || T.accent} size={11} />;
                          })()}
                          <span style={{flex:1,fontWeight:700}}>{g.label}</span>
                          <span style={{color:T.text2,opacity:0.6,fontFamily:"monospace"}}>{g.rows.length}</span>
                        </div>
                        {g.rows.map(m => {
                          const isCur = m.id === termModel;
                          const rk = realProviderKey(m.provider, m.id);
                          const rc = (m.provider === "openrouter" && OR_REAL_PROVIDER[String(m.id).split("/")[0]])
                            ? OR_REAL_PROVIDER[String(m.id).split("/")[0]].color
                            : (m.providerColor || T.accent);
                          return (<div key={m.provider+"/"+m.id} onClick={()=>{setTermModel(m.id); setTermModelOpen(false);}} style={{padding:"8px 14px",cursor:"pointer",fontSize:11,display:"flex",alignItems:"center",gap:9,background:isCur?T.active:"none",borderBottom:"1px solid "+T.dim+"33"}}>
                            <ProviderLogo providerKey={rk} color={rc} size={13} />
                            <span style={{flex:1,color:T.text,fontWeight:isCur?700:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.name}</span>
                            {isCur && <span style={{color:T.accent,fontSize:9}}>✓</span>}
                          </div>);
                        })}
                      </div>));
                    })()}
                  </div>
                </>)}
              </div>
            </div>
          )}
        </div>
        {/* Checkpoints modal */}
        {showCheckpoints && (
          <div onClick={()=>setShowCheckpoints(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:1000,display:"flex",alignItems:"flex-start",justifyContent:"center",paddingTop:"14dvh",backdropFilter:"blur(4px)"}}>
            <div onClick={e=>e.stopPropagation()} style={{background:T.panel,border:"1px solid "+T.dim,borderRadius:14,width:"min(460px,94vw)",maxHeight:"70dvh",overflow:"hidden",boxShadow:"0 24px 80px rgba(0,0,0,0.6)",fontFamily:monoStack,color:T.text,display:"flex",flexDirection:"column"}}>
              <div style={{padding:"14px 18px",borderBottom:"1px solid "+T.dim,fontSize:12,fontWeight:700,letterSpacing:"0.05em",color:T.accent,display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
                <span>⏪ CHECKPOINTS</span>
                <span style={{fontSize:10,color:T.text2,opacity:0.7}}>auto-saved before changes</span>
              </div>
              <div style={{flex:1,overflowY:"auto",padding:"6px 0",minHeight:60}}>
                {getCheckpoints().length === 0 ? (
                  <div style={{padding:"22px 18px",fontSize:12,color:T.text2,textAlign:"center"}}>No checkpoints yet. One is saved automatically before every agent change and /apply.</div>
                ) : getCheckpoints().map(c => (
                  <div key={c.ts} style={{padding:"10px 18px",borderBottom:"1px solid "+T.dim+"33",display:"flex",alignItems:"center",gap:10}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:600,color:T.text}}>{c.label}</div>
                      <div style={{fontSize:10,color:T.text2}}>{new Date(c.ts).toLocaleString()} · {c.files.length} file{c.files.length===1?"":"s"}</div>
                    </div>
                    <button onClick={()=>restoreCheckpoint(c.ts)} style={Object.assign({}, btnSm(T), {background:T.accent,color:"#fff",borderColor:T.accent,fontWeight:700})}>RESTORE</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {/* Routines panel */}
        {showRoutinesPanel && (
          <div onClick={()=>{setShowRoutinesPanel(false); setRoutineEditId(null); setRoutineDraft({name:"",prompt:""});}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:1000,display:"flex",alignItems:"flex-start",justifyContent:"center",paddingTop:"10dvh",backdropFilter:"blur(4px)"}}>
            <div onClick={e=>e.stopPropagation()} style={{background:T.panel,border:"1px solid "+T.dim,borderRadius:14,width:"min(580px,94vw)",maxHeight:"80dvh",overflow:"hidden",boxShadow:"0 24px 80px rgba(0,0,0,0.6)",fontFamily:monoStack,color:T.text,display:"flex",flexDirection:"column"}}>
              <div style={{padding:"14px 18px",borderBottom:"1px solid "+T.dim,fontSize:12,fontWeight:700,letterSpacing:"0.05em",color:T.accent,display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
                <span>⚡ ROUTINES</span>
                <span style={{fontSize:10,color:T.text2,opacity:0.7}}>{routines.length} saved</span>
              </div>
              {/* List */}
              <div style={{flex:1,overflowY:"auto",padding:"6px 0",minHeight:80}}>
                {routines.length === 0 ? (<div style={{padding:"24px 18px",fontSize:12,color:T.text2,textAlign:"center"}}>No saved routines yet. Pick a template below to get started, or write your own at the bottom.</div>) : routines.map(r => (
                  <div key={r.id} style={{padding:"10px 18px",borderBottom:"1px solid "+T.dim+"33",display:"flex",alignItems:"flex-start",gap:10}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:3}}>{r.name}</div>
                      <div style={{fontSize:11,color:T.text2,lineHeight:1.45,maxHeight:54,overflow:"hidden",textOverflow:"ellipsis",fontFamily:"var(--cf-brand-font, inherit)"}}>{r.prompt.length > 220 ? r.prompt.slice(0, 220) + "…" : r.prompt}</div>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:4,flexShrink:0}}>
                      <button onClick={()=>runRoutine(r)} style={Object.assign({}, btnSm(T), {background:T.accent,color:"#fff",borderColor:T.accent,fontWeight:700})}>RUN</button>
                      <button onClick={()=>editRoutine(r)} style={btnSm(T)}>edit</button>
                      <button onClick={()=>deleteRoutine(r.id)} style={Object.assign({}, btnSm(T), {color:"#e35d5d",borderColor:"#e35d5d44"})}>delete</button>
                    </div>
                  </div>
                ))}
              </div>
              {/* Templates grid */}
              <div style={{borderTop:"1px solid "+T.dim,padding:"12px 18px 4px",background:T.chrome}}>
                <div style={{fontSize:10,letterSpacing:"0.06em",color:T.text2,fontWeight:700,marginBottom:8,textTransform:"uppercase",display:"flex",alignItems:"baseline",justifyContent:"space-between"}}>
                  <span>{routines.length ? "Or start from a template" : "Start from a template"}</span>
                  <span style={{fontSize:9,opacity:0.6}}>RUN sends now · SAVE adds to your routines</span>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,paddingBottom:8}}>
                  {TEMPLATES.map(t => (
                    <div key={t.name} className="cf-card-hover" style={{background:T.panel,border:"1px solid "+T.dim,borderRadius:10,padding:"10px 12px",display:"flex",flexDirection:"column",gap:4,position:"relative"}}>
                      <div style={{display:"flex",alignItems:"center",gap:7}}>
                        <span style={{fontSize:15,lineHeight:1}}>{t.icon}</span>
                        <span style={{fontSize:12,fontWeight:700,color:T.text,letterSpacing:"-0.01em"}}>{t.name}</span>
                      </div>
                      <div style={{fontSize:11,color:T.text2,lineHeight:1.45,minHeight:32,fontFamily:"var(--cf-brand-font, inherit)"}}>{t.desc}</div>
                      <div style={{display:"flex",gap:5,marginTop:4}}>
                        <button onClick={()=>useTemplate(t,"run")} title="Drop into the prompt input" style={Object.assign({}, btnSm(T), {background:T.accent,color:"#fff",borderColor:T.accent,fontWeight:700,padding:"3px 10px",fontSize:10,flex:1})}>USE</button>
                        <button onClick={()=>useTemplate(t,"save")} title="Add to your saved routines" style={Object.assign({}, btnSm(T), {padding:"3px 10px",fontSize:10,flex:1})}>SAVE</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Add / edit form */}
              <div style={{borderTop:"1px solid "+T.dim,padding:"12px 18px",background:T.chrome}}>
                <div style={{fontSize:10,letterSpacing:"0.06em",color:T.text2,fontWeight:700,marginBottom:6,textTransform:"uppercase"}}>{routineEditId ? "Edit routine" : "Save a new routine"}</div>
                <input value={routineDraft.name} onChange={e=>setRoutineDraft(p=>Object.assign({}, p, {name:e.target.value}))} placeholder={"Name (e.g. weekly status report)"} style={{width:"100%",background:T.panel,border:"1px solid "+T.dim,borderRadius:7,padding:"7px 10px",color:T.text,fontSize:12,fontFamily:"inherit",outline:"none",boxSizing:"border-box",marginBottom:6}}/>
                <textarea value={routineDraft.prompt} onChange={e=>setRoutineDraft(p=>Object.assign({}, p, {prompt:e.target.value}))} placeholder="The prompt to send when this routine runs…" rows={3} style={{width:"100%",background:T.panel,border:"1px solid "+T.dim,borderRadius:7,padding:"7px 10px",color:T.text,fontSize:12,fontFamily:"inherit",outline:"none",boxSizing:"border-box",resize:"vertical",minHeight:60}}/>
                <div style={{display:"flex",gap:8,marginTop:8,justifyContent:"flex-end"}}>
                  {routineEditId && <button onClick={()=>{setRoutineEditId(null);setRoutineDraft({name:"",prompt:""});}} style={btnSm(T)}>cancel</button>}
                  <button onClick={saveRoutine} disabled={!routineDraft.name.trim()||!routineDraft.prompt.trim()} style={Object.assign({}, btnSm(T), {background:routineDraft.name.trim()&&routineDraft.prompt.trim()?T.accent:T.dim,color:routineDraft.name.trim()&&routineDraft.prompt.trim()?"#fff":T.text2,borderColor:routineDraft.name.trim()&&routineDraft.prompt.trim()?T.accent:T.dim,fontWeight:700,padding:"4px 14px",fontSize:11})}>{routineEditId ? "SAVE CHANGES" : "SAVE ROUTINE"}</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Customize modal */}
        {showCustomize && (
          <div onClick={()=>setShowCustomize(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:1000,display:"flex",alignItems:"flex-start",justifyContent:"center",paddingTop:"10dvh",backdropFilter:"blur(4px)"}}>
            <div onClick={e=>e.stopPropagation()} style={{background:T.panel,border:"1px solid "+T.dim,borderRadius:14,width:"min(480px,94vw)",maxHeight:"80dvh",overflow:"hidden",boxShadow:"0 24px 80px rgba(0,0,0,0.6)",fontFamily:monoStack,color:T.text,display:"flex",flexDirection:"column"}}>
              <div style={{padding:"14px 18px",borderBottom:"1px solid "+T.dim,fontSize:12,fontWeight:700,letterSpacing:"0.05em",color:T.accent,display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
                <span>⚙ CUSTOMIZE</span>
                <span style={{fontSize:10,color:T.text2,opacity:0.7}}>Code mode preferences</span>
              </div>
              <div style={{flex:1,overflowY:"auto",padding:"16px 18px",display:"flex",flexDirection:"column",gap:16}}>
                <div>
                  <div style={{fontSize:11,color:T.text2,marginBottom:5,letterSpacing:"0.05em",textTransform:"uppercase"}}>Theme</div>
                  <select value={editorTheme} onChange={e=>setEditorTheme(e.target.value)} style={{width:"100%",background:T.chrome,border:"1px solid "+T.dim,borderRadius:7,padding:"7px 10px",color:T.text,fontSize:12,fontFamily:"inherit",outline:"none"}}>
                    <option value="auto">auto (follow app)</option>
                    <option value="midnight">midnight</option>
                    <option value="ocean">ocean</option>
                    <option value="paper">paper</option>
                  </select>
                </div>
                <div>
                  <div style={{fontSize:11,color:T.text2,marginBottom:5,letterSpacing:"0.05em",textTransform:"uppercase"}}>Font size <span style={{color:T.accent,fontFamily:"monospace"}}>{fontSize}px</span></div>
                  <input type="range" min="11" max="18" value={fontSize} onChange={e=>setFontSize(parseInt(e.target.value,10))} style={{width:"100%",accentColor:T.accent}}/>
                </div>
                <div>
                  <div style={{fontSize:11,color:T.text2,marginBottom:5,letterSpacing:"0.05em",textTransform:"uppercase"}}>Tab size</div>
                  <div style={{display:"flex",gap:6}}>
                    {[2,4,8].map(n => <button key={n} onClick={()=>setTabSize(n)} style={Object.assign({}, btnSm(T), {flex:1,padding:"6px 0",fontSize:11,background:tabSize===n?T.accent:"none",color:tabSize===n?"#fff":T.text,borderColor:tabSize===n?T.accent:T.dim,fontWeight:tabSize===n?700:500})}>{n} spaces</button>)}
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div>
                    <div style={{fontSize:12,color:T.text,fontWeight:600,marginBottom:2}}>Word wrap</div>
                    <div style={{fontSize:10,color:T.text2}}>Long lines wrap instead of scrolling horizontally.</div>
                  </div>
                  <div onClick={()=>setWordWrap(p=>!p)} style={{width:36,height:20,background:wordWrap?T.accent:T.dim,borderRadius:10,position:"relative",cursor:"pointer",border:"1px solid "+T.dim,flexShrink:0,transition:"background 0.18s"}}>
                    <div style={{position:"absolute",top:2,left:wordWrap?16:2,width:14,height:14,background:"#fff",borderRadius:"50%",transition:"left 0.18s"}}/>
                  </div>
                </div>
                <div>
                  <div style={{fontSize:11,color:T.text2,marginBottom:5,letterSpacing:"0.05em",textTransform:"uppercase"}}>Default model</div>
                  <select value={termModel} onChange={e=>setTermModel(e.target.value)} style={{width:"100%",background:T.chrome,border:"1px solid "+T.dim,borderRadius:7,padding:"7px 10px",color:T.text,fontSize:12,fontFamily:"monospace",outline:"none"}}>
                    {buildTerminalModelList().map(m => <option key={m.provider+"/"+m.id} value={m.id}>{m.name} — {m.providerName}</option>)}
                  </select>
                </div>
              </div>
              <div style={{padding:"10px 18px",borderTop:"1px solid "+T.dim,display:"flex",justifyContent:"flex-end",gap:8}}>
                <button onClick={()=>setShowCustomize(false)} style={Object.assign({}, btnSm(T), {fontSize:11,padding:"5px 14px"})}>Done</button>
              </div>
            </div>
          </div>
        )}
        {/* Live preview overlay -- sandboxed iframe rendering HTML/SVG. */}
        {preview !== null && (
          <div style={{position:"fixed",top:0,right:0,bottom:0,width:termIsMobile?"100vw":"min(48vw, 760px)",minWidth:termIsMobile?0:320,zIndex:1100,background:T.chrome,borderLeft:termIsMobile?"none":"1px solid "+T.dim,boxShadow:termIsMobile?"none":"-16px 0 48px rgba(0,0,0,0.45)",display:"flex",flexDirection:"column",animation:"cfPreviewIn .22s cubic-bezier(0.16,1,0.3,1)"}}>
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",background:T.chrome,borderBottom:"1px solid "+T.dim,flexShrink:0,fontFamily:monoStack}}>
              <span style={{fontSize:12,fontWeight:700,color:T.accent}}>▶ Live preview</span>
              <span style={{fontSize:10,color:T.text2,opacity:0.6,display:termIsMobile?"none":"inline"}}>sandboxed · runs locally in your browser</span>
              <div style={{flex:1}}/>
              <button onClick={()=>{ try { const w = window.open("", "_blank"); if (w) { w.document.open(); w.document.write(preview); w.document.close(); } } catch {} }} style={btnSm(T)}>open in new tab</button>
              <button onClick={()=>setPreview(null)} style={Object.assign({}, btnSm(T), {color:T.accent,borderColor:T.accent,fontWeight:700})}>✕ close</button>
            </div>
            <iframe
              title="Hermes Code mode preview"
              srcDoc={preview}
              sandbox="allow-scripts allow-forms allow-popups allow-modals"
              style={{flex:1,border:"none",background:"#fff",width:"100%"}}
            />
          </div>
        )}
        {/* Command palette modal */}
        {(showCmdPalette || showHelp) && (
          <div onClick={()=>{setShowCmdPalette(false);setShowHelp(false);}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:1000,display:"flex",alignItems:"flex-start",justifyContent:"center",paddingTop:"15dvh",backdropFilter:"blur(4px)"}}>
            <div onClick={e=>e.stopPropagation()} style={{background:T.panel,border:"1px solid "+T.dim,borderRadius:14,width:"min(560px,92vw)",maxHeight:"70dvh",overflow:"hidden",boxShadow:"0 24px 80px rgba(0,0,0,0.6)",fontFamily:monoStack,color:T.text}}>
              <div style={{padding:"14px 18px",borderBottom:"1px solid "+T.dim,fontSize:12,fontWeight:700,letterSpacing:"0.05em",color:T.accent,display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
                <span>COMMAND PALETTE</span>
                <span style={{fontSize:10,color:T.text2,opacity:0.7}}>Ctrl/Cmd+Shift+P · Esc to close</span>
              </div>
              <div style={{padding:"10px 0",maxHeight:"55dvh",overflowY:"auto"}}>
                {SLASH.map(s => (
                  <div key={s.cmd} onClick={()=>{setInput(s.cmd+" ");setShowCmdPalette(false);setShowHelp(false);taRef.current?.focus();}} style={{padding:"10px 18px",cursor:"pointer",display:"flex",gap:14,alignItems:"baseline",fontSize:12}}>
                    <span style={{color:T.accent,fontWeight:700,minWidth:80}}>{s.cmd}</span>
                    <span style={{color:T.text2,minWidth:130,fontSize:11}}>{s.args}</span>
                    <span style={{color:T.text,opacity:0.85,fontSize:11}}>{s.desc}</span>
                  </div>
                ))}
              </div>
              <div style={{padding:"12px 18px",borderTop:"1px solid "+T.dim,fontSize:10,color:T.text2,opacity:0.85,display:"flex",gap:18,flexWrap:"wrap"}}>
                <span>↩ click to insert</span>
                <span>Ctrl/Cmd+P quick switch</span>
                <span>Ctrl/Cmd+F find</span>
                <span>F2 rename</span>
              </div>
            </div>
          </div>
        )}
        {/* Quick file switcher */}
        {showQuickSwitcher && (
          <div onClick={()=>setShowQuickSwitcher(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:1000,display:"flex",alignItems:"flex-start",justifyContent:"center",paddingTop:"15dvh",backdropFilter:"blur(4px)"}}>
            <div onClick={e=>e.stopPropagation()} style={{background:T.panel,border:"1px solid "+T.dim,borderRadius:14,width:"min(440px,92vw)",maxHeight:"60dvh",overflow:"hidden",boxShadow:"0 24px 80px rgba(0,0,0,0.6)",fontFamily:monoStack,color:T.text}}>
              <div style={{padding:"12px 14px",borderBottom:"1px solid "+T.dim}}>
                <input autoFocus value={quickQuery} onChange={e=>setQuickQuery(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&quickSwitchFiltered[0]){openInTab(quickSwitchFiltered[0].id);setShowQuickSwitcher(false);} if(e.key==="Escape")setShowQuickSwitcher(false);}} placeholder="Type a file name…" style={{width:"100%",background:T.chrome,border:"1px solid "+T.dim,borderRadius:7,padding:"7px 12px",color:T.text,fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}/>
              </div>
              <div style={{padding:"6px 0",maxHeight:"45dvh",overflowY:"auto"}}>
                {quickSwitchFiltered.length === 0 ? (<div style={{padding:"14px 16px",fontSize:11,color:T.text2,opacity:0.7}}>No files match.</div>) : quickSwitchFiltered.map(f => {
                  const ic = fileIconFor(f.name);
                  return (<div key={f.id} onClick={()=>{openInTab(f.id);setShowQuickSwitcher(false);}} style={{padding:"7px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:10,fontSize:12}}>
                    <span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:ic.color,flexShrink:0}}/>
                    <span style={{flex:1,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</span>
                    <span style={{color:T.text2,fontSize:9,opacity:0.7}}>{(f.content||"").split("\n").length}L</span>
                  </div>);
                })}
              </div>
              <div style={{padding:"8px 14px",borderTop:"1px solid "+T.dim,fontSize:10,color:T.text2,opacity:0.7}}>↩ open · Esc cancel</div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Small button style helper shared across the Terminal UI.
  function btnSm(T) { return {background:"none",border:"1px solid "+T.dim,color:T.text2,fontSize:10,padding:"3px 9px",borderRadius:6,cursor:"pointer",fontFamily:"inherit",letterSpacing:"0.03em"}; }
  function moreItemStyle(T) { return {display:"block",width:"100%",textAlign:"left",padding:"7px 14px",background:"none",border:"none",color:T.text,fontSize:11.5,cursor:"pointer",fontFamily:"inherit"}; }

  // ── CLI page ──────────────────────────────────────────────────────
  // Replaces the old CoWork marketing tab. Lists the install commands
  // for ChatFort's own CLI plus the major coding-agent CLIs we know
  // customers ask about: Claude Code (Anthropic), aider (open source),
  // codex (OpenAI), gemini-cli (Google), ollama (local). Each tile gets
  // copy-to-clipboard install + ChatFort-key wiring snippets.
  function CLIPage({showToast}) {
    const [tab, setTab] = useState("chatfort");
    const [copied, setCopied] = useState(null);
    function copy(id, text) {
      navigator.clipboard?.writeText(text);
      setCopied(id);
      showToast && showToast("Copied!");
      setTimeout(() => setCopied(null), 1600);
    }
    const Cmd = ({id, cmd, label}) => (
      <div style={{marginBottom:10}}>
        {label && <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4,letterSpacing:"0.04em",textTransform:"uppercase"}}>{label}</div>}
        <div style={{display:"flex",alignItems:"stretch",background:S.codeBlockBg,border:`1px solid ${S.border2}`,borderRadius:9,overflow:"hidden"}}>
          <pre style={{flex:1,padding:"10px 13px",margin:0,fontSize:12,color:S.text,fontFamily:"monospace",overflowX:"auto",whiteSpace:"pre"}}><code>{cmd}</code></pre>
          <button onClick={()=>copy(id,cmd)} style={{padding:"0 13px",background:"none",border:"none",borderLeft:`1px solid ${S.border}`,color:copied===id?S.success:S.text3,cursor:"pointer",fontSize:10,fontFamily:"monospace",whiteSpace:"nowrap",flexShrink:0,transition:"color 0.15s",minWidth:64}}>{copied===id?"✓ Done":"Copy"}</button>
        </div>
      </div>
    );
    const Card = ({title, badge, children}) => (
      <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden",marginBottom:14}}>
        <div style={{padding:"13px 18px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:13,fontWeight:700,color:S.text}}>{title}</span>
          {badge && <span style={{fontSize:9,padding:"2px 8px",borderRadius:10,fontFamily:"monospace",fontWeight:700,background:S.accentDim,color:S.accent,border:`1px solid ${S.accent}40`}}>{badge}</span>}
        </div>
        <div style={{padding:18}}>{children}</div>
      </div>
    );
    const Tab = ({id, label, icon}) => (
      <button onClick={()=>setTab(id)} className="cf-btn-lift" style={{padding:"8px 14px",borderRadius:9,border:`1px solid ${tab===id?S.accent:S.border2}`,background:tab===id?S.accentDim:S.inputBg,color:tab===id?S.accent:S.text2,fontSize:12,fontWeight:tab===id?700:600,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:7}}>{icon}{label}</button>
    );
    return (
      <div className="cf-page-fade-in" style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
        <div style={{maxWidth:820,margin:"0 auto"}}>
          <div className="cf-page-header" style={{marginBottom:18}}>
            <h2 style={{fontSize:22,fontWeight:800,color:S.text,marginBottom:4,letterSpacing:"-0.02em"}}>Command Line</h2>
            <p style={{fontSize:13,color:S.text2,lineHeight:1.6,maxWidth:580}}>Use ChatFort from your terminal -- or wire your existing ChatFort key into Claude Code, aider, codex, gemini-cli, or Ollama. One key, every workflow.</p>
          </div>
          {/* Tabs */}
          <div style={{display:"flex",gap:8,marginBottom:18,flexWrap:"wrap"}}>
            <Tab id="chatfort" label="ChatFort CLI" icon="⚡"/>
            <Tab id="claude-code" label="Claude Code" icon="🐻"/>
            <Tab id="aider" label="aider" icon="🔧"/>
            <Tab id="codex" label="OpenAI Codex" icon="📝"/>
            <Tab id="gemini" label="gemini-cli" icon="✨"/>
            <Tab id="ollama" label="Ollama" icon="🦙"/>
          </div>
          {/* ChatFort CLI */}
          {tab==="chatfort" && (<>
            <Card title="Install ChatFort CLI" badge="OFFICIAL">
              <p style={{fontSize:12,color:S.text2,marginBottom:12,lineHeight:1.6}}>One global command -- supports streaming, agents, knowledge bases, and the full multi-model parallel-compare. Same models, same workspace, no second login.</p>
              <Cmd id="chatfort-npm" cmd="npm install -g @chatfort/cli" label="npm (Node 18+)"/>
              <Cmd id="chatfort-brew" cmd="brew install chatfort/tap/chatfort" label="macOS Homebrew"/>
              <Cmd id="chatfort-curl" cmd="curl -fsSL https://chatfort.ai/install.sh | sh" label="Linux / WSL"/>
            </Card>
            <Card title="Authenticate">
              <p style={{fontSize:12,color:S.text2,marginBottom:10}}>Opens a browser to your ChatFort org and stores a refresh token in your OS keychain.</p>
              <Cmd id="chatfort-login" cmd="chatfort login"/>
              <p style={{fontSize:11,color:S.text3,marginTop:6,lineHeight:1.5}}>Or set <code style={{fontFamily:"monospace",background:S.surface2,padding:"1px 5px",borderRadius:3}}>CHATFORT_API_KEY</code> in your shell profile to skip the browser flow.</p>
            </Card>
            <Card title="Common commands">
              <Cmd id="cf-chat" cmd="chatfort chat 'summarize my last meeting'" label="One-shot prompt"/>
              <Cmd id="cf-stream" cmd="chatfort chat --stream --model claude-sonnet-4-6 'write a release note'" label="Streaming + model override"/>
              <Cmd id="cf-agent" cmd="chatfort agent run legal-reviewer < contract.md" label="Run a saved agent"/>
              <Cmd id="cf-pipe" cmd="git diff | chatfort chat 'explain this diff'" label="Pipe stdin"/>
              <Cmd id="cf-kb" cmd="chatfort kb ingest ./policies/*.pdf --tag policy" label="Push docs to your knowledge base"/>
            </Card>
          </>)}
          {/* Claude Code */}
          {tab==="claude-code" && (<>
            <Card title="Claude Code" badge="ANTHROPIC OFFICIAL">
              <p style={{fontSize:12,color:S.text2,marginBottom:12,lineHeight:1.6}}>Anthropic's official terminal-native coding agent. Reads + writes files, runs commands, and explains code. Your ChatFort key works directly when you set <code style={{fontFamily:"monospace",background:S.surface2,padding:"1px 5px",borderRadius:3}}>ANTHROPIC_BASE_URL</code> to route through the ChatFort proxy (BAA + audit log applied).</p>
              <Cmd id="cc-install" cmd="npm install -g @anthropic-ai/claude-code" label="Install"/>
              <Cmd id="cc-route" cmd={"export ANTHROPIC_BASE_URL=https://api.chatfort.ai/v1/proxy\nexport ANTHROPIC_API_KEY=cf_xxx_your_chatfort_key"} label="Route through ChatFort"/>
              <Cmd id="cc-run" cmd="claude" label="Launch"/>
              <p style={{fontSize:11,color:S.text3,marginTop:8,lineHeight:1.5}}>Everything Claude Code does will show up in your ChatFort Audit log + Analytics, and BAA routing applies when your org has it enabled.</p>
            </Card>
          </>)}
          {/* aider */}
          {tab==="aider" && (<>
            <Card title="aider" badge="OPEN SOURCE">
              <p style={{fontSize:12,color:S.text2,marginBottom:12,lineHeight:1.6}}>AI pair-programming in your terminal. Supports any OpenAI-compatible endpoint -- aim it at ChatFort's proxy and your usage flows through your org's BAA + audit.</p>
              <Cmd id="aider-install" cmd="python -m pip install aider-install && aider-install" label="Install"/>
              <Cmd id="aider-route" cmd={"export OPENAI_API_BASE=https://api.chatfort.ai/v1\nexport OPENAI_API_KEY=cf_xxx_your_chatfort_key"} label="Point at ChatFort"/>
              <Cmd id="aider-run" cmd="aider --model claude-sonnet-4-6 path/to/file.py" label="Launch"/>
            </Card>
          </>)}
          {/* OpenAI Codex CLI */}
          {tab==="codex" && (<>
            <Card title="OpenAI Codex" badge="OPENAI OFFICIAL">
              <p style={{fontSize:12,color:S.text2,marginBottom:12,lineHeight:1.6}}>OpenAI's terminal coding agent. Routes through any OpenAI-compatible endpoint, so ChatFort's proxy works directly.</p>
              <Cmd id="codex-install" cmd="npm install -g @openai/codex" label="Install"/>
              <Cmd id="codex-route" cmd={"export OPENAI_BASE_URL=https://api.chatfort.ai/v1\nexport OPENAI_API_KEY=cf_xxx_your_chatfort_key"} label="Point at ChatFort"/>
              <Cmd id="codex-run" cmd="codex" label="Launch"/>
            </Card>
          </>)}
          {/* gemini-cli */}
          {tab==="gemini" && (<>
            <Card title="gemini-cli" badge="GOOGLE OFFICIAL">
              <p style={{fontSize:12,color:S.text2,marginBottom:12,lineHeight:1.6}}>Google's official Gemini terminal client. Use it with your own Gemini API key; for ChatFort routing, prefer the OpenAI-compatible CLIs above with model=<code style={{fontFamily:"monospace",background:S.surface2,padding:"1px 5px",borderRadius:3}}>google/gemini-2.5-pro</code>.</p>
              <Cmd id="gem-install" cmd="npm install -g @google/gemini-cli" label="Install"/>
              <Cmd id="gem-auth" cmd="gemini auth login" label="Sign in"/>
              <Cmd id="gem-run" cmd="gemini chat 'refactor this file'" label="Launch"/>
            </Card>
          </>)}
          {/* Ollama */}
          {tab==="ollama" && (<>
            <Card title="Ollama" badge="LOCAL">
              <p style={{fontSize:12,color:S.text2,marginBottom:12,lineHeight:1.6}}>Run open-source models entirely on your laptop -- zero cloud, zero retention. Pair with ChatFort by adding Ollama as a custom upstream in <strong>AI Providers</strong>.</p>
              <Cmd id="ollama-mac" cmd="brew install ollama" label="macOS"/>
              <Cmd id="ollama-linux" cmd="curl -fsSL https://ollama.com/install.sh | sh" label="Linux"/>
              <Cmd id="ollama-pull" cmd="ollama pull llama3.2" label="Pull a model"/>
              <Cmd id="ollama-run" cmd="ollama serve" label="Start daemon"/>
            </Card>
          </>)}
          <div style={{padding:"14px 18px",borderRadius:12,background:S.accentDim,border:`1px solid ${S.accent}30`,fontSize:12,color:S.text2,lineHeight:1.6,marginTop:8}}>
            <strong style={{color:S.accent}}>One key, every workflow.</strong> When you route a third-party CLI through ChatFort, it inherits your org's BAA enforcement, audit log, model visibility, and rate limits -- so a security incident in a coding agent doesn't bypass the controls you set here.
          </div>
        </div>
      </div>
    );
  }

  function CoWorkPage({showToast}) {
    const [tab, setTab] = useState("overview");
    const [copied, setCopied] = useState(null);

    function copy(id, text) {
      navigator.clipboard?.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 1800);
    }

    const CmdBlock = ({id, cmd, label}) => (
      <div style={{marginBottom:10}}>
        {label && <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4,letterSpacing:"0.03em"}}>{label}</div>}
        <div style={{display:"flex",alignItems:"stretch",background:S.codeBlockBg,border:`1px solid ${S.border2}`,borderRadius:9,overflow:"hidden"}}>
          <pre style={{flex:1,padding:"10px 13px",margin:0,fontSize:12,color:S.text,fontFamily:"monospace",overflowX:"auto",whiteSpace:"pre"}}><code>{cmd}</code></pre>
          <button onClick={()=>copy(id,cmd)} style={{padding:"0 13px",background:"none",border:"none",borderLeft:`1px solid ${S.border}`,color:copied===id?S.success:S.text3,cursor:"pointer",fontSize:10,fontFamily:"monospace",whiteSpace:"nowrap",flexShrink:0,transition:"color 0.15s",minWidth:64}}>
            {copied===id?"✓ Done":"Copy"}
          </button>
        </div>
      </div>
    );

    const Card = ({children, style}) => (
      <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden",...style}}>{children}</div>
    );

    const CardHeader = ({children, icon}) => (
      <div style={{padding:"13px 18px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:8}}>
        {icon && <span style={{fontSize:16}}>{icon}</span>}
        <span style={{fontSize:13,fontWeight:700,color:S.text2}}>{children}</span>
      </div>
    );

    const FEATURES = [
      {icon:"🗂", title:"File & Task Automation", desc:"CoWork reads your desktop files, organizes folders, renames batches, moves documents, and runs multi-step workflows — all by describing what you want in plain language."},
      {icon:"🔄", title:"App-to-App Workflows", desc:"Chain actions across apps. Export from Notion, reformat in Excel, drop into Slack. CoWork bridges the gaps between your tools without writing a single script."},
      {icon:"📋", title:"Clipboard & Context", desc:"CoWork sees what's on your screen and in your clipboard. Paste anything and ask Claude to transform, summarize, translate, or act on it."},
      {icon:"🔐", title:"Private by Default", desc:"CoWork runs entirely on your machine. No cloud upload, no data retention. Your files and context stay local — fully aligned with ChatFort's zero-retention posture."},
      {icon:"🤖", title:"Claude-Powered", desc:"Powered by the same Claude models you use in ChatFort. Switch models, apply your custom agents, and keep your system prompts consistent across chat and desktop."},
      {icon:"🧩", title:"MCP-Ready", desc:"CoWork supports MCP servers you've already configured here. The same tools Claude uses in chat are available in CoWork desktop automations."},
    ];

    const USECASES = [
      {role:"⚖️ Legal", title:"Matter file organization", desc:"Drag a folder of discovery docs. Ask CoWork to rename by Bates number, sort by date, and create an index CSV. Done in seconds, not hours."},
      {role:"📊 Finance", title:"Report assembly", desc:"Pull last month's P&L from Excel, merge with commentary from Word, export a clean PDF. CoWork handles the pipeline — you just review."},
      {role:"🏥 Healthcare", title:"Chart triage", desc:"Drop a folder of intake PDFs. CoWork reads each, extracts key fields into a spreadsheet, and flags anything needing follow-up — no PHI leaves your machine."},
      {role:"💼 Operations", title:"Email → Task sync", desc:"Forward a batch of client emails. CoWork extracts action items, creates a structured task list, and optionally drops them into your project tool."},
    ];

    const tabs = [
      {id:"overview", label:"Overview"},
      {id:"install", label:"Install"},
      {id:"usecases", label:"Use Cases"},
      {id:"config", label:"Config"},
    ];

    return (
      <div style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
        <div style={{maxWidth:800,margin:"0 auto"}}>

          {/* Hero */}
          <div style={{background:`linear-gradient(135deg,${S.accent}14,${S.accent}06)`,border:`1px solid ${S.accent}30`,borderRadius:20,padding:"32px 36px",marginBottom:24,position:"relative",overflow:"hidden"}}>
            <div style={{position:"absolute",top:-20,right:-20,width:180,height:180,borderRadius:"50%",background:`${S.accent}08`,pointerEvents:"none"}}/>
            <div style={{position:"absolute",bottom:-40,right:60,width:120,height:120,borderRadius:"50%",background:`${S.accent}06`,pointerEvents:"none"}}/>
            <div style={{display:"flex",alignItems:"flex-start",gap:20,position:"relative"}}>
              <div style={{width:60,height:60,borderRadius:18,background:`linear-gradient(135deg,${S.accent},${S.accent2})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,flexShrink:0,boxShadow:`0 8px 24px ${S.accent}40`}}>🖥</div>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                  <h2 style={{fontSize:24,fontWeight:800,color:S.text,letterSpacing:"-0.03em",lineHeight:1}}>Claude CoWork</h2>
                  <span style={{fontSize:9,padding:"3px 9px",borderRadius:10,fontFamily:"monospace",fontWeight:700,background:`${S.success}18`,color:S.success,border:`1px solid ${S.success}30`}}>Beta</span>
                </div>
                <p style={{fontSize:14,color:S.text2,lineHeight:1.65,marginBottom:16,maxWidth:520}}>A desktop automation tool that brings Claude to your files, apps, and workflows. Describe a task — CoWork executes it across your entire desktop environment.</p>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <a href="https://claude.ai/download" target="_blank" rel="noreferrer" style={{padding:"9px 18px",borderRadius:10,background:`linear-gradient(135deg,${S.accent},${S.accent2})`,color:"#fff",fontSize:13,fontWeight:700,textDecoration:"none",display:"flex",alignItems:"center",gap:6,boxShadow:`0 4px 14px ${S.accent}40`}}>
                    ⬇ Download CoWork
                  </a>
                  <a href="https://support.claude.ai/hc/en-us/articles/37074459208471" target="_blank" rel="noreferrer" style={{padding:"9px 16px",borderRadius:10,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text2,fontSize:13,fontWeight:500,textDecoration:"none",display:"flex",alignItems:"center",gap:6}}>
                    Docs ↗
                  </a>
                </div>
              </div>
            </div>
            <div style={{display:"flex",gap:10,marginTop:20,paddingTop:18,borderTop:`1px solid ${S.accent}20`,flexWrap:"wrap"}}>
              {[["macOS","✓"],["Windows","✓"],["On-device","✓"],["Zero upload","✓"],["MCP support","✓"]].map(([label,val])=>(
                <div key={label} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:S.text2}}>
                  <span style={{color:S.success,fontWeight:700}}>{val}</span>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div style={{display:"flex",gap:4,marginBottom:20,background:S.surface2,borderRadius:10,padding:3}}>
            {tabs.map(t=>(
              <button key={t.id} onClick={()=>setTab(t.id)} style={{flex:1,padding:"7px 10px",borderRadius:8,border:"none",background:tab===t.id?S.cardBg:"none",color:tab===t.id?S.text:S.text3,fontSize:12,fontWeight:tab===t.id?600:400,cursor:"pointer",transition:"all 0.15s",boxShadow:tab===t.id?`0 1px 4px rgba(0,0,0,0.1)`:"none"}}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {tab==="overview" && (
            <div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
                {FEATURES.map(f=>(
                  <div key={f.title} style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:13,padding:"16px 18px"}}>
                    <div style={{fontSize:22,marginBottom:10}}>{f.icon}</div>
                    <div style={{fontSize:13,fontWeight:700,color:S.text,marginBottom:5}}>{f.title}</div>
                    <div style={{fontSize:12,color:S.text2,lineHeight:1.65}}>{f.desc}</div>
                  </div>
                ))}
              </div>

              {/* How it works */}
              <Card>
                <CardHeader icon="⚡">How CoWork fits into ChatFort</CardHeader>
                <div style={{padding:18}}>
                  <div style={{display:"flex",flexDirection:"column",gap:0}}>
                    {[
                      ["1","Chat in ChatFort","Use Claude in the browser for research, drafting, and analysis.","🌐"],
                      ["2","Switch to CoWork","When you need Claude to act on your desktop — files, apps, system — open CoWork.","🖥"],
                      ["3","Same models, same agents","CoWork uses the same Claude models and can reference your ChatFort agent prompts.","🤖"],
                      ["4","Results stay local","Outputs land on your machine. Nothing is uploaded or retained.","🔐"],
                    ].map(([num,title,desc,icon],i,arr)=>(
                      <div key={num} style={{display:"flex",gap:14,paddingBottom:i<arr.length-1?16:0,marginBottom:i<arr.length-1?16:0,borderBottom:i<arr.length-1?`1px solid ${S.border}`:"none"}}>
                        <div style={{width:30,height:30,borderRadius:"50%",background:S.accentDim,border:`1px solid ${S.border2}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:S.accent,flexShrink:0}}>{num}</div>
                        <div style={{flex:1}}>
                          <div style={{fontSize:13,fontWeight:700,color:S.text,marginBottom:3,display:"flex",alignItems:"center",gap:7}}>{title} <span style={{fontSize:15}}>{icon}</span></div>
                          <div style={{fontSize:12,color:S.text2,lineHeight:1.6}}>{desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Install Tab */}
          {tab==="install" && (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <Card>
                <CardHeader icon="⬇">Download & Install</CardHeader>
                <div style={{padding:18}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
                    {[
                      {os:"macOS",icon:"🍎",arch:"Apple Silicon + Intel",size:"~85 MB",link:"https://claude.ai/download"},
                      {os:"Windows",icon:"🪟",arch:"x64",size:"~90 MB",link:"https://claude.ai/download"},
                    ].map(p=>(
                      <a key={p.os} href={p.link} target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",borderRadius:12,background:S.surface2,border:`1px solid ${S.border2}`,textDecoration:"none",transition:"border-color 0.15s"}}
                        onMouseEnter={e=>e.currentTarget.style.borderColor=S.accent}
                        onMouseLeave={e=>e.currentTarget.style.borderColor=S.border2}>
                        <span style={{fontSize:28}}>{p.icon}</span>
                        <div>
                          <div style={{fontSize:13,fontWeight:700,color:S.text,marginBottom:2}}>Download for {p.os}</div>
                          <div style={{fontSize:10,color:S.text3,fontFamily:"monospace"}}>{p.arch} · {p.size}</div>
                        </div>
                        <span style={{marginLeft:"auto",fontSize:18,color:S.accent}}>↗</span>
                      </a>
                    ))}
                  </div>
                  <div style={{fontSize:11,color:S.text3,lineHeight:1.7}}>
                    CoWork is installed as a desktop app alongside Claude. You'll need a Claude Pro or Team subscription. Once installed, it runs as a background process accessible from your menu bar / system tray.
                  </div>
                </div>
              </Card>

              <Card>
                <CardHeader icon="🔑">Connect Your Anthropic Key</CardHeader>
                <div style={{padding:18}}>
                  <div style={{fontSize:12,color:S.text2,lineHeight:1.7,marginBottom:12}}>
                    CoWork reads your <code style={{fontFamily:"monospace",background:S.surface2,padding:"1px 5px",borderRadius:3}}>ANTHROPIC_API_KEY</code> environment variable. Set it once and CoWork picks it up automatically.
                  </div>
                  <CmdBlock id="env-mac" cmd={'export ANTHROPIC_API_KEY="sk-ant-api03-…"'} label="macOS / Linux — add to ~/.zshrc or ~/.bashrc"/>
                  <CmdBlock id="env-win" cmd={'setx ANTHROPIC_API_KEY "sk-ant-api03-…"'} label="Windows — Command Prompt (run as Administrator)"/>
                  <div style={{marginTop:12,background:`${S.accent}08`,border:`1px solid ${S.border2}`,borderRadius:8,padding:"10px 13px",fontSize:11,color:S.text2,lineHeight:1.6}}>
                    💡 Already set your key in the <button onClick={()=>{}} style={{background:"none",border:"none",padding:0,color:S.accent,cursor:"pointer",fontSize:11,fontFamily:"inherit",fontWeight:600}}>CLI Integration</button> section? CoWork will use the same key.
                  </div>
                </div>
              </Card>

              <Card>
                <CardHeader icon="🧩">Connect MCP Servers</CardHeader>
                <div style={{padding:18}}>
                  <div style={{fontSize:12,color:S.text2,lineHeight:1.7,marginBottom:14}}>
                    Any MCP servers you configured in ChatFort can be used in CoWork. Export your config and drop it into CoWork's settings folder.
                  </div>
                  <CmdBlock id="cowork-config-mac" cmd={"~/Library/Application Support/Claude/claude_desktop_config.json"} label="macOS config path"/>
                  <CmdBlock id="cowork-config-win" cmd={"%APPDATA%\\Claude\\claude_desktop_config.json"} label="Windows config path"/>
                  <div style={{fontSize:11,color:S.text2,lineHeight:1.6,marginTop:10}}>
                    Paste your MCP config from the <strong>MCP Servers</strong> section. CoWork and Claude Desktop share the same config file.
                  </div>
                </div>
              </Card>

              <Card>
                <CardHeader icon="✅">Verify Installation</CardHeader>
                <div style={{padding:18}}>
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {[
                      ["CoWork appears in menu bar / system tray","Look for the Claude icon after launch"],
                      ["Right-click → 'Ask Claude' on any file","Select a file, right-click, and Claude should appear in the context menu"],
                      ["CoWork responds to desktop prompts","Open CoWork and type: 'List the files on my Desktop'"],
                      ["MCP tools are available","Open CoWork and type: 'What tools do you have access to?'"],
                    ].map(([check,hint],i)=>(
                      <div key={i} style={{display:"flex",gap:10,padding:"10px 12px",borderRadius:9,background:S.surface2,border:`1px solid ${S.border}`}}>
                        <div style={{width:20,height:20,borderRadius:"50%",border:`1.5px solid ${S.border2}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1,color:S.text3,fontSize:11}}>○</div>
                        <div>
                          <div style={{fontSize:12,fontWeight:600,color:S.text,marginBottom:2}}>{check}</div>
                          <div style={{fontSize:11,color:S.text3}}>{hint}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Use Cases Tab */}
          {tab==="usecases" && (
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{background:`${S.accent}08`,border:`1px solid ${S.border2}`,borderRadius:12,padding:"13px 17px",fontSize:12,color:S.text2,lineHeight:1.7}}>
                These are example prompts you can type directly into CoWork. No setup required beyond installation — CoWork handles the execution.
              </div>
              {USECASES.map(u=>(
                <Card key={u.title}>
                  <div style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:18}}>{u.role.split(" ")[0]}</span>
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:S.text}}>{u.title}</div>
                      <div style={{fontSize:10,color:S.text3}}>{u.role.split(" ").slice(1).join(" ")}</div>
                    </div>
                  </div>
                  <div style={{padding:18}}>
                    <div style={{fontSize:12,color:S.text2,lineHeight:1.7,marginBottom:14}}>{u.desc}</div>
                  </div>
                </Card>
              ))}

              {/* Prompt examples */}
              <Card>
                <CardHeader icon="💬">Example CoWork Prompts</CardHeader>
                <div style={{padding:18}}>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {[
                      "Rename all PDFs in my Downloads folder to include today's date",
                      "Find all Word docs modified this week and copy them to a folder called 'Recent'",
                      "Read the Excel file on my Desktop and summarize the Q3 column",
                      "Watch my Downloads folder and alert me when a new PDF arrives",
                      "Compress all images in this folder larger than 2MB",
                      "Merge these three CSV files into one and remove duplicate rows",
                      "Create a folder structure for a new legal matter: Client / Matter / Pleadings / Discovery / Correspondence",
                      "Read my clipboard and reformat it as a numbered list",
                    ].map((p,i)=>(
                      <div key={i} onClick={()=>copy(`prompt-${i}`,p)} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:8,background:S.surface2,border:`1px solid ${S.border}`,cursor:"pointer",transition:"border-color 0.15s"}}
                        onMouseEnter={e=>e.currentTarget.style.borderColor=S.accent}
                        onMouseLeave={e=>e.currentTarget.style.borderColor=S.border}>
                        <span style={{fontSize:11,color:S.text3,fontFamily:"monospace",flexShrink:0,width:16,textAlign:"right"}}>{i+1}.</span>
                        <span style={{fontSize:12,color:S.text,flex:1}}>{p}</span>
                        <span style={{fontSize:10,color:copied===`prompt-${i}`?S.success:S.text3,fontFamily:"monospace",flexShrink:0}}>{copied===`prompt-${i}`?"✓":"⎘"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Config Tab */}
          {tab==="config" && (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <Card>
                <CardHeader icon="📁">Config File Locations</CardHeader>
                <div style={{padding:18}}>
                  <CmdBlock id="cfg-mac" cmd={"~/Library/Application Support/Claude/"} label="macOS — Claude config directory"/>
                  <CmdBlock id="cfg-win" cmd={"%APPDATA%\\Claude\\"} label="Windows — Claude config directory"/>
                  <CmdBlock id="cfg-linux" cmd={"~/.config/claude/"} label="Linux — Claude config directory"/>
                </div>
              </Card>

              <Card>
                <CardHeader icon="🔧">claude_desktop_config.json</CardHeader>
                <div style={{padding:18}}>
                  <div style={{fontSize:12,color:S.text2,lineHeight:1.7,marginBottom:14}}>
                    Full example config with MCP servers and permissions:
                  </div>
                  <div style={{position:"relative"}}>
                    <pre style={{background:S.codeBlockBg,border:`1px solid ${S.border2}`,borderRadius:10,padding:"14px 16px",fontSize:11,color:S.text,fontFamily:"monospace",overflowX:"auto",margin:0,lineHeight:1.7}}>
{`{
  "mcpServers": {
    "filesystem": {
      "command": "npx",
      "args": ["-y", "@modelcontextprotocol/server-filesystem", "/Users/you/Documents"]
    },
    "github": {
      "url": "https://api.githubcopilot.com/mcp/",
      "headers": { "Authorization": "Bearer ghp_..." }
    }
  },
  "permissions": {
    "allow": ["read_file", "write_file", "list_directory"],
    "deny": ["delete_file"]
  }
}`}
                    </pre>
                    <button onClick={()=>copy("json-cfg",`{\n  "mcpServers": {\n    "filesystem": {\n      "command": "npx",\n      "args": ["-y", "@modelcontextprotocol/server-filesystem", "/Users/you/Documents"]\n    }\n  }\n}`)} style={{position:"absolute",top:8,right:8,padding:"4px 10px",borderRadius:6,background:S.inputBg,border:`1px solid ${S.border2}`,color:copied==="json-cfg"?S.success:S.text3,fontSize:10,fontFamily:"monospace",cursor:"pointer"}}>{copied==="json-cfg"?"✓ Copied":"Copy"}</button>
                  </div>
                </div>
              </Card>

              <Card>
                <CardHeader icon="🛡">Privacy & Permissions</CardHeader>
                <div style={{padding:18}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
                    {[
                      ["File access","Only paths you explicitly grant"],
                      ["Network","Only via MCP servers you configure"],
                      ["Data retention","Zero — nothing leaves your machine"],
                      ["Model training","Never used for training"],
                      ["Telemetry","Opt-in only"],
                      ["Encryption","Local data encrypted at rest"],
                    ].map(([k,v])=>(
                      <div key={k} style={{padding:"10px 13px",borderRadius:9,background:S.surface2,border:`1px solid ${S.border}`}}>
                        <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:3}}>{k}</div>
                        <div style={{fontSize:12,fontWeight:600,color:S.success}}>{v}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{fontSize:11,color:S.text3,lineHeight:1.7}}>
                    CoWork requests macOS Accessibility permissions and Automation permissions only when needed. You can review and revoke all permissions in System Settings → Privacy & Security.
                  </div>
                </div>
              </Card>

              <Card>
                <CardHeader icon="🔗">Resources</CardHeader>
                <div style={{padding:"12px 18px",display:"flex",flexDirection:"column",gap:0}}>
                  {[
                    ["Claude CoWork documentation","https://support.claude.ai/hc/en-us/articles/37074459208471","📖"],
                    ["Download CoWork","https://claude.ai/download","⬇"],
                    ["MCP server registry","https://modelcontextprotocol.io/servers","🔌"],
                    ["Claude API console","https://console.anthropic.com","🎛"],
                    ["Report CoWork issues","https://support.anthropic.com","🐛"],
                  ].map(([label,url,icon],i,arr)=>(
                    <a key={label} href={url} target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",gap:10,padding:"11px 0",borderBottom:i<arr.length-1?`1px solid ${S.border}`:"none",textDecoration:"none",transition:"color 0.15s"}}
                      onMouseEnter={e=>e.currentTarget.style.color=S.accent}
                      onMouseLeave={e=>e.currentTarget.style.color=S.text}>
                      <span style={{fontSize:14,flexShrink:0}}>{icon}</span>
                      <span style={{fontSize:13,color:"inherit",fontWeight:500}}>{label}</span>
                      <span style={{marginLeft:"auto",fontSize:11,color:S.text3}}>↗</span>
                    </a>
                  ))}
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    );
  }

  function PermissionsTab({rolePerms, setRolePerms, users, setUsers, allAgents, allTools, allMcpServers, setTab}) {
    const [overrideUid, setOverrideUid] = useState(null);

    const allModels = [];
    for (const [pid, p] of Object.entries(PROVIDERS)) {
      for (const m of (p.models||[])) allModels.push({id:m.id, label:`${p.icon} ${p.name} — ${m.name}`, provider:pid});
    }

    // All integration providers (non-builtin need keys)
    const allIntegrations = Object.entries(PROVIDERS).map(([pid,p])=>({id:pid, label:`${p.icon} ${p.name}`, builtin:!!p.builtin}));

    // App sections that can be toggled per role
    const APP_SECTIONS = [
      {id:"integrations", label:"🔗 Integrations", desc:"Connect AI providers & API keys"},
      {id:"mcp",          label:"🖥 MCP Servers",   desc:"Tool & data connectors"},
      {id:"cowork",       label:"🤝 CoWork",         desc:"Real-time collaboration"},
      {id:"bots",         label:"💬 Bots",           desc:"Embeddable site widgets"},
      {id:"workflows",    label:"⚙️ Workflows",      desc:"Automate with AI agents"},
      {id:"analytics",    label:"📊 Analytics",      desc:"Usage & audit logs"},
    ];

    const RP_ROLES = [
      {id:"admin",   label:"Admin",   icon:"👑", color:"#dc2626"},
      {id:"member",  label:"Member",  icon:"👤", color:S.accent},
      {id:"analyst", label:"Analyst", icon:"📊", color:"#8b5cf6"},
      {id:"viewer",  label:"Viewer",  icon:"👁", color:S.text3},
    ];

    const toggle = (roleId, resource, itemId) => {
      setRolePerms(p => {
        const cur = [...(p[roleId]?.[resource]||[])];
        const idx = cur.indexOf(itemId);
        return {...p, [roleId]: {...(p[roleId]||{}), [resource]: idx>=0 ? cur.filter(x=>x!==itemId) : [...cur,itemId]}};
      });
    };

    const Chip = ({roleId, resource, itemId, label, color}) => {
      const sel = (rolePerms[roleId]?.[resource]||[]).includes(itemId);
      return (
        <button onClick={()=>toggle(roleId,resource,itemId)}
          style={{display:"inline-flex",alignItems:"center",gap:5,padding:"4px 10px",borderRadius:20,border:`1px solid ${sel?(color||S.accent):S.border2}`,background:sel?(color||S.accent)+"18":"transparent",color:sel?(color||S.accent):S.text3,fontSize:11,cursor:"pointer",transition:"all 0.15s",fontFamily:"inherit"}}>
          {sel && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
          {label}
        </button>
      );
    };

    const ADGroupInput = ({roleId}) => {
      const [val,setVal] = useState("");
      const groups = rolePerms[roleId]?.adGroups||[];
      return (
        <div>
          <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:6}}>
            {groups.map(g=>(
              <span key={g} style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 9px",borderRadius:20,background:S.accentDim,border:`1px solid ${S.border3}`,fontSize:10,fontFamily:"monospace",color:S.accent}}>
                {g}
                <button onClick={()=>setRolePerms(p=>({...p,[roleId]:{...p[roleId],adGroups:groups.filter(x=>x!==g)}}))} style={{background:"none",border:"none",color:S.accent,cursor:"pointer",padding:0,lineHeight:1,fontSize:14}}>×</button>
              </span>
            ))}
            {groups.length===0 && <span style={{fontSize:11,color:S.text3}}>No AD groups mapped</span>}
          </div>
          <div style={{display:"flex",gap:6}}>
            <input value={val} onChange={e=>setVal(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"&&val.trim()){setRolePerms(p=>({...p,[roleId]:{...p[roleId],adGroups:[...(p[roleId]?.adGroups||[]),val.trim()]}}));setVal("");}}}
              placeholder="CN=Group,OU=Groups,DC=firm,DC=com" style={{...ISt(),fontSize:11,fontFamily:"monospace",flex:1}}/>
            <button onClick={()=>{if(val.trim()){setRolePerms(p=>({...p,[roleId]:{...p[roleId],adGroups:[...(p[roleId]?.adGroups||[]),val.trim()]}}));setVal("");}}}
              style={{padding:"7px 12px",borderRadius:7,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:11,cursor:"pointer",whiteSpace:"nowrap",fontFamily:"inherit"}}>+ Add</button>
          </div>
        </div>
      );
    };

    const toggleUser = (uid, resource, itemId) => {
      setUsers(p => p.map(u => {
        if (u.id !== uid) return u;
        const ov = {...(u.overrides||{})};
        const cur = [...(ov[resource]||[])];
        const idx = cur.indexOf(itemId);
        ov[resource] = idx>=0 ? cur.filter(x=>x!==itemId) : [...cur,itemId];
        return {...u, overrides:ov};
      }));
    };

    const ChipUser = ({uid, resource, itemId, label}) => {
      const u = users.find(x=>x.id===uid);
      const sel = (u?.overrides?.[resource]||[]).includes(itemId);
      return (
        <button onClick={()=>toggleUser(uid,resource,itemId)}
          style={{display:"inline-flex",alignItems:"center",gap:5,padding:"4px 10px",borderRadius:20,border:`1px solid ${sel?S.accent:S.border2}`,background:sel?S.accentDim:"transparent",color:sel?S.accent:S.text3,fontSize:11,cursor:"pointer",transition:"all 0.15s",fontFamily:"inherit"}}>
          {sel && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
          {label}
        </button>
      );
    };

    const SecP = ({title, hint, children}) => (
      <div style={{marginBottom:20}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
          <span style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em",color:S.text3,fontFamily:"monospace"}}>{title}</span>
          {hint && <span style={{fontSize:9,color:S.text3,background:S.surface2,padding:"1px 7px",borderRadius:10,border:`1px solid ${S.border}`}}>{hint}</span>}
        </div>
        {children}
      </div>
    );

    const overUser = users.find(u=>u.id===overrideUid);

    return (
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{background:S.accent+"08",border:"1px solid "+S.border2,borderRadius:10,padding:"10px 14px",fontSize:12,color:S.text2,lineHeight:1.6}}>
          <strong style={{color:S.text}}>Resource Permissions</strong> — Control which LLM models, agents, tools, integrations, and MCP servers each role can access. <em>Empty = all allowed.</em> Checked items are explicitly allowed. Per-user overrides take precedence over role settings. AD groups auto-assign roles when SSO is active.
        </div>

        {RP_ROLES.map(role => (
          <div key={role.id} style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
            <div style={{padding:"12px 18px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:10,background:S.surface}}>
              <span style={{fontSize:18}}>{role.icon}</span>
              <span style={{fontSize:13,fontWeight:700,color:S.text}}>{role.label}</span>
              <span style={{fontSize:9,padding:"1px 7px",borderRadius:10,background:role.color+"18",color:role.color,border:`1px solid ${role.color}30`,fontFamily:"monospace",fontWeight:700}}>
                {users.filter(u=>u.role===role.id).length} user{users.filter(u=>u.role===role.id).length!==1?"s":""}
              </span>
              {role.id==="admin" && <span style={{fontSize:10,color:S.text3,marginLeft:"auto"}}>Admins always have full access · cannot be restricted</span>}
            </div>
            {role.id === "admin" ? (
              <div style={{padding:"12px 18px",fontSize:11,color:S.text3}}>All LLM models, agents, tools, integrations, and MCP servers are unrestricted for admins.</div>
            ) : (
              <div style={{padding:"16px 18px"}}>
                <SecP title="LLM Models" hint="empty = all models allowed">
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {allModels.map(m=><Chip key={m.id} roleId={role.id} resource="models" itemId={m.id} label={m.label} color={role.color}/>)}
                  </div>
                </SecP>
                <SecP title="Agents" hint="empty = all agents allowed">
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {allAgents.length===0
                      ? <span style={{fontSize:11,color:S.text3}}>No agents created yet</span>
                      : allAgents.map(a=><Chip key={a.id} roleId={role.id} resource="agents" itemId={a.id} label={a.emoji+" "+a.name} color={role.color}/>)}
                  </div>
                </SecP>
                <SecP title="Tools" hint="empty = all tools allowed">
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {allTools.length===0
                      ? <span style={{fontSize:11,color:S.text3}}>No tools created yet</span>
                      : allTools.map(t=><Chip key={t.id} roleId={role.id} resource="tools" itemId={t.id} label={"🔧 "+t.name} color={role.color}/>)}
                  </div>
                </SecP>
                <SecP title="Integrations" hint="empty = all integrations allowed">
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {allIntegrations.map(i=><Chip key={i.id} roleId={role.id} resource="integrations" itemId={i.id} label={i.label+(i.builtin?" (built-in)":"")} color={role.color}/>)}
                  </div>
                </SecP>
                <SecP title="MCP Servers" hint="empty = all MCP servers allowed">
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {allMcpServers.length===0
                      ? <span style={{fontSize:11,color:S.text3}}>No MCP servers configured yet</span>
                      : allMcpServers.map(s=><Chip key={s.id||s.name} roleId={role.id} resource="mcp" itemId={s.id||s.name} label={"🖥 "+(s.name||s.id)} color={role.color}/>)}
                  </div>
                </SecP>
                <SecP title="App Sections" hint="checked = this role can access that section · empty = all sections allowed">
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {APP_SECTIONS.map(sec=>(
                      <div key={sec.id} style={{display:"flex",flexDirection:"column",alignItems:"flex-start"}}>
                        <Chip roleId={role.id} resource="sections" itemId={sec.id} label={sec.label} color={role.color}/>
                      </div>
                    ))}
                  </div>
                  <div style={{marginTop:8,fontSize:10,color:S.text3,lineHeight:1.5}}>When any section is checked, only those sections are accessible. Leave all unchecked to allow all sections.</div>
                </SecP>
                <SecP title="AD / LDAP Groups" hint="users in these groups get this role automatically">
                  <ADGroupInput roleId={role.id}/>
                </SecP>
              </div>
            )}
          </div>
        ))}

        {/* Per-user overrides */}
        <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
          <div style={{padding:"12px 18px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:10,background:S.surface}}>
            <span style={{fontSize:13,fontWeight:700,color:S.text}}>Per-User Overrides</span>
            <span style={{fontSize:10,color:S.text3}}>Grant or restrict specific resources for individual users, independent of their role</span>
          </div>
          <div style={{padding:"12px 18px",borderBottom:`1px solid ${S.border}`,display:"flex",flexWrap:"wrap",gap:6}}>
            {users.map(u=>{
              const hasOv = Object.values(u.overrides||{}).some(v=>v?.length>0);
              return (
                <button key={u.id} onClick={()=>setOverrideUid(overrideUid===u.id?null:u.id)}
                  style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 12px",borderRadius:20,border:`1px solid ${overrideUid===u.id?S.accent:S.border2}`,background:overrideUid===u.id?S.accentDim:"transparent",color:overrideUid===u.id?S.accent:S.text2,fontSize:11,cursor:"pointer",transition:"all 0.15s",fontFamily:"inherit"}}>
                  <UserAvatar user={u} size={18} fontSize={8} />
                  {u.name}
                  {hasOv && <span style={{width:6,height:6,borderRadius:"50%",background:S.accent,display:"inline-block",flexShrink:0}}/>}
                </button>
              );
            })}
          </div>
          {overUser ? (
            <div style={{padding:"16px 18px"}}>
              <div style={{fontSize:11,color:S.text2,marginBottom:14,lineHeight:1.6,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <span>Overrides for <strong>{overUser.name}</strong> — checked items are explicitly allowed, overriding their <strong>{overUser.role}</strong> role. Leave empty to inherit from role.</span>
                {Object.values(overUser.overrides||{}).some(v=>v?.length>0) && (
                  <button onClick={()=>setUsers(p=>p.map(u=>u.id===overUser.id?{...u,overrides:{}}:u))}
                    style={{background:"none",border:`1px solid ${S.danger}55`,borderRadius:6,color:S.danger,cursor:"pointer",fontSize:11,padding:"3px 10px",flexShrink:0,fontFamily:"inherit"}}>Clear all</button>
                )}
              </div>
              <SecP title="LLM Models">
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {allModels.map(m=><ChipUser key={m.id} uid={overUser.id} resource="models" itemId={m.id} label={m.label}/>)}
                </div>
              </SecP>
              <SecP title="Agents">
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {allAgents.length===0 ? <span style={{fontSize:11,color:S.text3}}>No agents yet</span>
                    : allAgents.map(a=><ChipUser key={a.id} uid={overUser.id} resource="agents" itemId={a.id} label={a.emoji+" "+a.name}/>)}
                </div>
              </SecP>
              <SecP title="Tools">
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {allTools.length===0 ? <span style={{fontSize:11,color:S.text3}}>No tools yet</span>
                    : allTools.map(t=><ChipUser key={t.id} uid={overUser.id} resource="tools" itemId={t.id} label={"🔧 "+t.name}/>)}
                </div>
              </SecP>
              <SecP title="Integrations">
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {allIntegrations.map(i=><ChipUser key={i.id} uid={overUser.id} resource="integrations" itemId={i.id} label={i.label}/>)}
                </div>
              </SecP>
              <SecP title="MCP Servers">
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {allMcpServers.length===0 ? <span style={{fontSize:11,color:S.text3}}>No MCP servers yet</span>
                    : allMcpServers.map(s=><ChipUser key={s.id||s.name} uid={overUser.id} resource="mcp" itemId={s.id||s.name} label={"🖥 "+(s.name||s.id)}/>)}
                </div>
              </SecP>
              <SecP title="App Sections">
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {APP_SECTIONS.map(sec=><ChipUser key={sec.id} uid={overUser.id} resource="sections" itemId={sec.id} label={sec.label}/>)}
                </div>
              </SecP>
            </div>
          ) : (
            <div style={{padding:"20px 18px",fontSize:11,color:S.text3,textAlign:"center"}}>Select a user above to configure their individual resource overrides.</div>
          )}
        </div>
      </div>
    );
  }

  function UserManagementPage({users, setUsers, invites, setInvites, ssoConfig, setSsoConfig, adminOk, setAdminOk, adminPin, showToast, gid, rolePerms, setRolePerms, allAgents, allTools, mcpServers, serverMode, authToken, isSuperAdmin}) {
    const [pin, setPin] = useState("");
    const [pinErr, setPinErr] = useState(false);
    const [tab, setTab] = useState("users");
    // Diagnostic: log when this component mounts/re-renders and tab changes
    console.log("[UserMgmt] render — tab=", tab, "serverMode=", serverMode);
    const [editUser, setEditUser] = useState(null);
    const [newUser, setNewUser] = useState({name:"", email:"", role:"member", color:"#5ba3c9", password:""});
    // Shows the temp password that was just set so admin can copy & share
    const [lastTempPw, setLastTempPw] = useState(null); // {userName, email, password}
    const genTempPassword = () => Math.random().toString(36).slice(2,6) + "-" + Math.random().toString(36).slice(2,6) + Math.floor(Math.random()*90+10);
    // NOTE: user-loading from server was moved up to App level to prevent
    // remount-loop that reset tab state on every click
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteRole, setInviteRole] = useState("member");
    const [inviteExpiry, setInviteExpiry] = useState("7");
    const [sso, setSso] = useState({...ssoConfig});
    const [adConfig, setAdConfig] = useState({url: ssoConfig.adUrl||"", baseDN:"", bindDN:"", bindPass:"", userAttr:"sAMAccountName", groupAttr:"memberOf", tlsEnabled:true});
    const [copied, setCopied] = useState(null);
    const [showSecret, setShowSecret] = useState(false);

    const ROLES = [
      ...(isSuperAdmin ? [{id:"superadmin", label:"Owner (super admin)", desc:"Account owner — protected from deletion and demotion by other admins", color:"#d4a017", icon:"👑"}] : []),
      {id:"admin", label:"Admin", desc:"Full access including user management", color:"#dc2626", icon:"🛡"},
      {id:"member", label:"Member", desc:"Can chat and use agents", color:S.accent, icon:"👤"},
      {id:"viewer", label:"Viewer", desc:"Read-only chat access", color:S.text3, icon:"👁"},
      {id:"analyst", label:"Analyst", desc:"Member + access to analytics dashboard", color:"#8b5cf6", icon:"📊"},
    ];

    const SSO_PROVIDERS = [
      {id:"none", label:"None / Local", icon:"🔐", desc:"PIN-based local auth"},
      {id:"microsoft", label:"Microsoft / Azure AD", icon:"🪟", desc:"OAuth2 + SAML · Entra ID"},
      {id:"google", label:"Google Workspace", icon:"🔵", desc:"OAuth2 · GSuite SSO"},
      {id:"okta", label:"Okta", icon:"🔷", desc:"SAML 2.0 / OIDC"},
      {id:"onelogin", label:"OneLogin", icon:"🔑", desc:"SAML 2.0 / OIDC"},
      {id:"saml", label:"Custom SAML", icon:"🏢", desc:"Any SAML 2.0 IdP"},
      {id:"oidc", label:"Custom OIDC", icon:"🔗", desc:"OpenID Connect"},
      {id:"ldap", label:"LDAP / Active Directory", icon:"🖧", desc:"On-premise AD / LDAP"},
    ];

    function copy(id, text) {
      navigator.clipboard?.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 1800);
    }

    async function addUser() {
      if (!newUser.name.trim() || !newUser.email.trim()) { showToast("Name and email required"); return; }
      const password = newUser.password.trim() || genTempPassword();
      if (serverMode && authToken) {
        try {
          const r = await fetch("/api/admin/users", {
            method:"POST",
            headers:{"Content-Type":"application/json","Authorization":`Bearer ${authToken}`},
            body: JSON.stringify({
              name: newUser.name.trim(), email: newUser.email.trim(),
              password, role: newUser.role, color: newUser.color,
              mustChangePassword: true,
            })
          });
          const d = await r.json();
          // Slice C1d: 402 with seat_limit_reached gets a friendlier toast +
          // a click-through to the Billing tab. The plain `error` field on
          // server response is now a code ("seat_limit_reached") rather
          // than a sentence, so prefer `message` when present.
          if (r.status === 402 && d?.error === "seat_limit_reached") {
            showToast(d.message || `Seat limit reached on ${d.plan} plan (${d.usedSeats}/${d.paidSeats}). Upgrade in Billing.`);
            // Auto-navigate after a beat so the admin can act on it.
            setTimeout(() => { try { setSection("billing"); } catch {} }, 1200);
            return;
          }
          if (!r.ok) { showToast(d.message || d.error || "Failed to add user"); return; }
          setUsers(p => [...p, d]);
          setLastTempPw({ userName: d.name, email: d.email, password });
          setNewUser({name:"", email:"", role:"member", color:"#5ba3c9", password:""});
          showToast("User added!");
        } catch(e) { showToast("Network error: " + e.message); }
        return;
      }
      // Local-only mode (standalone PWA)
      const initials = newUser.name.trim().split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase();
      setUsers(p => [...p, {id: "u-"+gid(), ...newUser, name: newUser.name.trim(), email: newUser.email.trim(), initials, created: Date.now(), status:"active"}]);
      setNewUser({name:"", email:"", role:"member", color:"#5ba3c9", password:""});
      showToast("User added!");
    }

    async function saveUser() {
      if (serverMode && authToken) {
        try {
          const r = await fetch("/api/admin/users/"+editUser.id, {
            method:"PUT",
            headers:{"Content-Type":"application/json","Authorization":`Bearer ${authToken}`},
            body: JSON.stringify({ name: editUser.name, email: editUser.email, role: editUser.role, color: editUser.color, allowedModels: editUser.allowedModels ?? null, compliance_mode: editUser.compliance_mode ?? null, emailVerified: editUser.emailVerified })
          });
          const d = await r.json();
          if (!r.ok) { showToast(d.error || "Failed to save"); return; }
          setUsers(p => p.map(u => u.id !== editUser.id ? u : d));
          setEditUser(null);
          showToast("User updated!");
        } catch(e) { showToast("Network error: " + e.message); }
        return;
      }
      setUsers(p => p.map(u => u.id !== editUser.id ? u : {...u, ...editUser, initials: editUser.name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()}));
      setEditUser(null);
      showToast("User updated!");
    }

    async function removeUser(id) {
      if (!window.confirm("Remove this user?")) return;
      if (serverMode && authToken) {
        try {
          const r = await fetch("/api/admin/users/"+id, {
            method:"DELETE",
            headers:{"Authorization":`Bearer ${authToken}`}
          });
          const d = await r.json().catch(()=>({}));
          if (!r.ok) { showToast(d.error || "Failed to remove"); return; }
          setUsers(p => p.filter(u => u.id !== id));
          showToast("User removed");
        } catch(e) { showToast("Network error: " + e.message); }
        return;
      }
      setUsers(p => p.filter(u => u.id !== id));
      showToast("User removed");
    }

    async function resetUserPassword(u) {
      if (!serverMode || !authToken) { showToast("Only available in server mode"); return; }
      const password = genTempPassword();
      try {
        const r = await fetch("/api/admin/users/"+u.id+"/reset-password", {
          method:"POST",
          headers:{"Content-Type":"application/json","Authorization":`Bearer ${authToken}`},
          body: JSON.stringify({ password })
        });
        const d = await r.json();
        if (!r.ok) { showToast(d.error || "Reset failed"); return; }
        setLastTempPw({ userName: u.name, email: u.email, password });
        showToast("Password reset — share the new temp password with the user");
      } catch(e) { showToast("Network error: " + e.message); }
    }

    // Server mode: invites are real (org_invites.json + email + accept flow).
    // Standalone keeps the old local-demo behavior.
    const _mapServerInvite = (i) => ({ id: i.id, email: i.email, role: i.role, created: Date.parse(i.createdAt) || Date.now(), expires: Date.parse(i.expiresAt) || 0, status: i.status, token: i.token });
    async function _serverCreateInvite(sendEmail) {
      const r = await fetch("/api/admin/org-invites", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` }, body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole, expiresDays: parseInt(inviteExpiry), sendEmail }) });
      const d = await r.json().catch(()=>({}));
      if (!r.ok) { showToast(d.error || "Couldn't create invite"); return null; }
      setInvites(p => [...p.filter(x => x.id !== d.invite.id && !(x.email === d.invite.email && x.status === "pending")), _mapServerInvite(d.invite)]);
      setInviteEmail("");
      return d;
    }
    async function createInvite() {
      if (!inviteEmail.trim()) { showToast("Email required"); return; }
      if (serverMode && authToken) {
        const d = await _serverCreateInvite(false);
        if (d) {
          try { await navigator.clipboard.writeText(d.link); showToast("Link copied — no email sent. Use ✉ Email Invite to send one."); }
          catch { showToast("Invite created — no email sent. Copy the link below or use ✉ Email Invite."); }
        }
        return;
      }
      const expMs = parseInt(inviteExpiry) * 86400000;
      const inv = {id: "inv-"+gid(), email: inviteEmail.trim(), role: inviteRole, created: Date.now(), expires: Date.now()+expMs, status:"pending", token: Math.random().toString(36).slice(2,18).toUpperCase()};
      setInvites(p => [...p, inv]);
      setInviteEmail("");
      showToast("Invite created — no email sent. Share the link below.");
    }
    async function emailInvite() {
      if (!inviteEmail.trim()) { showToast("Email required"); return; }
      if (!(serverMode && authToken)) { showToast("Email invites need server mode — use the invite link instead"); return; }
      const d = await _serverCreateInvite(true);
      if (d) showToast(d.emailed
        ? `Invite emailed to ${d.invite.email} ✓`
        : ("Invite created, but the email failed" + (d.emailedError ? ` — ${d.emailedError}` : " — copy the link instead")));
    }

    function revokeInvite(id) {
      if (serverMode && authToken) {
        fetch(`/api/admin/org-invites/${id}`, { method: "DELETE", headers: { "Authorization": `Bearer ${authToken}` } }).catch(()=>{});
      }
      setInvites(p => p.map(i => i.id===id ? {...i, status:"revoked"} : i));
      showToast("Invite revoked");
    }

    function saveSso() {
      setSsoConfig({...sso, adUrl: adConfig.url});
      showToast("SSO config saved!");
    }

    const inviteLink = (token) => window.location.origin + ((serverMode && authToken) ? "?org_invite=" : "?invite=") + token;
    const isExpired = (inv) => Date.now() > inv.expires;

    // In server mode, JWT auth already verified admin role — skip the local PIN gate.
    // Also mark adminOk so the sidebar green-dot indicator reflects access.
    useEffect(() => { if (serverMode && !adminOk) setAdminOk(true); }, [serverMode, adminOk]);
    // NOTE: do NOT load invites with an effect inside this component — it
    // remounts on every App render (function identity, see the ChatPage
    // slice-10.5 note), so a mount-effect that setInvites() loops remounts
    // and resets the tab state. The load lives at App level instead.

    if (!serverMode && !adminOk) return (
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:32,background:S.bg}}>
        <div style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:16,padding:32,width:"100%",maxWidth:360,textAlign:"center"}}>
          <div style={{fontSize:32,marginBottom:16}}>👑</div>
          <div style={{fontSize:16,fontWeight:700,color:S.text,marginBottom:6}}>Admin Access Required</div>
          <div style={{fontSize:12,color:S.text2,marginBottom:20,lineHeight:1.6}}>Enter your admin PIN to manage users.</div>
          <div style={{display:"flex",gap:8,justifyContent:"center",marginBottom:14}}>{[0,1,2,3].map(i=><div key={i} style={{width:14,height:14,borderRadius:"50%",background:pin.length>i?S.accent:S.surface3,border:"1px solid "+S.border2}}/>)}</div>
          <input type="password" inputMode="numeric" pattern="[0-9]*" maxLength={4} value={pin} autoFocus onChange={e=>{setPin(e.target.value);setPinErr(false);if(e.target.value.length===4){if(e.target.value===adminPin){setAdminOk(true);}else{setPinErr(true);setTimeout(()=>setPin(""),600);}}}} style={{...ISt(),fontSize:20,textAlign:"center",letterSpacing:10,marginBottom:12}}/>
          {pinErr&&<div style={{color:S.danger,fontSize:11,marginBottom:10}}>Incorrect PIN</div>}
          <Btn onClick={()=>{if(pin===adminPin)setAdminOk(true);else{setPinErr(true);setPin("");}}} style={{width:"100%",justifyContent:"center"}}>Unlock</Btn>
          <div style={{marginTop:12,fontSize:10,color:S.text3}}>Default PIN: 1234</div>
        </div>
      </div>
    );

    const tabs2 = [{id:"users",label:"Users"},{id:"invites",label:"Invites"},{id:"roles",label:"Roles"},{id:"permissions",label:"Permissions"},{id:"sso",label:"SSO / AD"},{id:"audit",label:"Audit Log"}];

    return (
      <div style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
        <div style={{maxWidth:860,margin:"0 auto"}}>
          {/* Header */}
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:20}}>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                <h2 style={{fontSize:20,fontWeight:800,color:S.text}}>User Management</h2>
                <span style={{fontSize:9,padding:"2px 8px",borderRadius:10,fontFamily:"monospace",fontWeight:700,background:S.danger+"18",color:S.danger,border:"1px solid "+S.danger+"30"}}>ADMIN</span>
                {/* Slice C1d: seat pill. Pulls seatStatus from /api/auth/me
                    (shipped on App boot), so renders without a second fetch.
                    Never NaN/undefined — falls back to "Trial — unlimited"
                    when status is missing or billing isn't live yet. */}
                {(() => {
                  const ss = authUser?.seatStatus;
                  if (!ss || ss.unlimited) {
                    return (
                      <span style={{fontSize:9,padding:"2px 8px",borderRadius:10,fontFamily:"monospace",fontWeight:700,background:S.text3+"18",color:S.text3,border:"1px solid "+S.text3+"30"}}>
                        Trial — unlimited
                      </span>
                    );
                  }
                  const overflow = ss.usedSeats > ss.paidSeats;
                  const tight = !overflow && ss.available <= 0;
                  const color = overflow ? S.danger : tight ? (S.warning || "#f59e0b") : S.success;
                  return (
                    <span title={`${ss.usedSeats} active users on a ${ss.paidSeats}-seat plan`}
                      style={{fontSize:9,padding:"2px 8px",borderRadius:10,fontFamily:"monospace",fontWeight:700,background:color+"18",color,border:"1px solid "+color+"30"}}>
                      Seats: {ss.usedSeats} / {ss.paidSeats}
                    </span>
                  );
                })()}
              </div>
              <p style={{fontSize:13,color:S.text2}}>{users.length} users · {invites.filter(i=>i.status==="pending"&&!isExpired(i)).length} pending invites · SSO: {ssoConfig.provider==="none"?"Off":ssoConfig.provider}</p>
            </div>
            <button onClick={()=>{setAdminOk(false);}} style={{padding:"6px 12px",borderRadius:8,background:"none",border:"1px solid "+S.border2,color:S.text3,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Lock</button>
          </div>

          {/* Tab bar */}
          <div style={{display:"flex",gap:4,marginBottom:20,background:S.surface2,borderRadius:10,padding:3}}>
            {tabs2.map(t=>(
              <button key={t.id} type="button" onClick={()=>{console.log("[UserMgmt] tab clicked:", t.id);setTab(t.id);}} style={{flex:1,padding:"7px 8px",borderRadius:8,border:"none",background:tab===t.id?S.cardBg:"none",color:tab===t.id?S.text:S.text3,fontSize:11,fontWeight:tab===t.id?600:400,cursor:"pointer",transition:"all 0.15s",boxShadow:tab===t.id?"0 1px 4px rgba(0,0,0,0.1)":"none",position:"relative",zIndex:1,pointerEvents:"auto"}}>
                {t.label}
                {t.id==="invites"&&invites.filter(i=>i.status==="pending"&&!isExpired(i)).length>0&&<span style={{marginLeft:4,fontSize:9,padding:"1px 5px",borderRadius:10,background:S.accent,color:"#fff"}}>{invites.filter(i=>i.status==="pending"&&!isExpired(i)).length}</span>}
              </button>
            ))}
          </div>

          {/* ── USERS TAB ── */}
          {tab==="users" && (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {/* Temp password confirmation banner — shown right after admin adds user or resets password */}
              {lastTempPw && (
                <div style={{background:`${S.accent}14`,border:`1px solid ${S.accent}50`,borderRadius:12,padding:16,position:"relative"}}>
                  <button onClick={()=>setLastTempPw(null)} style={{position:"absolute",top:10,right:12,background:"none",border:"none",color:S.text3,cursor:"pointer",fontSize:18,lineHeight:1}}>×</button>
                  <div style={{fontSize:13,fontWeight:700,color:S.text,marginBottom:6,display:"flex",alignItems:"center",gap:7}}>
                    🔑 Share this temporary password with <span style={{color:S.accent}}>{lastTempPw.userName}</span>
                  </div>
                  <div style={{fontSize:11,color:S.text3,marginBottom:10}}>They'll be forced to set their own password on first login.</div>
                  <div style={{display:"flex",gap:8,alignItems:"center",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"10px 12px"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:2}}>EMAIL</div>
                      <div style={{fontSize:12,color:S.text,fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis"}}>{lastTempPw.email}</div>
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:2}}>TEMP PASSWORD</div>
                      <div style={{fontSize:13,color:S.accent,fontFamily:"monospace",fontWeight:700,overflow:"hidden",textOverflow:"ellipsis"}}>{lastTempPw.password}</div>
                    </div>
                    <button onClick={()=>{navigator.clipboard?.writeText(`Email: ${lastTempPw.email}\nTemp password: ${lastTempPw.password}`);showToast("Copied!");}}
                      style={{padding:"8px 12px",borderRadius:8,background:`linear-gradient(135deg,${S.accent},${S.accent2})`,border:"none",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>Copy</button>
                  </div>
                </div>
              )}

              {/* Add user form */}
              <div style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:14,overflow:"hidden"}}>
                <div style={{padding:"13px 18px",borderBottom:"1px solid "+S.border,fontSize:12,fontWeight:700,color:S.text2}}>Add User {serverMode && <span style={{fontSize:9,padding:"1px 7px",borderRadius:10,background:`${S.accent}18`,color:S.accent,fontFamily:"monospace",marginLeft:6}}>SERVER</span>}</div>
                <div style={{padding:16}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                    <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>DISPLAY NAME</div><input style={ISt()} value={newUser.name} onChange={e=>setNewUser(p=>({...p,name:e.target.value}))} placeholder="Jane Smith"/></div>
                    <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>EMAIL</div><input style={ISt()} value={newUser.email} onChange={e=>setNewUser(p=>({...p,email:e.target.value}))} placeholder="jane@firm.com" type="email"/></div>
                  </div>
                  {serverMode && (
                    <div style={{marginBottom:10}}>
                      <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>TEMP PASSWORD <span style={{color:S.text3+"99"}}>(leave blank to auto-generate)</span></div>
                      <div style={{display:"flex",gap:6}}>
                        <input style={{...ISt(),fontFamily:"monospace"}} value={newUser.password} onChange={e=>setNewUser(p=>({...p,password:e.target.value}))} placeholder="auto-generated"/>
                        <button onClick={()=>setNewUser(p=>({...p,password:genTempPassword()}))} style={{padding:"0 14px",borderRadius:9,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text2,fontSize:11,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>🎲 Generate</button>
                      </div>
                    </div>
                  )}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
                    <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>ROLE</div>
                      <select style={{...ISt(),fontFamily:"monospace",fontSize:12}} value={newUser.role} onChange={e=>setNewUser(p=>({...p,role:e.target.value}))}>
                        {ROLES.map(r=><option key={r.id} value={r.id}>{r.icon} {r.label}</option>)}
                      </select>
                    </div>
                    <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>COLOR</div>
                      <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>{["#5ba3c9","#7ab8d4","#4caf87","#e8a87c","#f87171","#a78bfa","#f59e0b","#94a3b8"].map(c=><div key={c} onClick={()=>setNewUser(p=>({...p,color:c}))} style={{width:24,height:24,borderRadius:"50%",background:c,cursor:"pointer",border:"2px solid "+(newUser.color===c?"#000":"transparent"),transform:newUser.color===c?"scale(1.2)":"scale(1)"}}/>)}</div>
                    </div>
                  </div>
                  <Btn onClick={addUser}>{IC.plus} Add User</Btn>
                </div>
              </div>

              {/* User list */}
              <div style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:14,overflow:"hidden"}}>
                <div style={{padding:"13px 18px",borderBottom:"1px solid "+S.border,fontSize:12,fontWeight:700,color:S.text2}}>All Users · {users.length}</div>
                {users.map(u => {
                  const role = ROLES.find(r=>r.id===u.role)||ROLES[1];
                  const isEditing = editUser?.id === u.id;
                  return (
                    <div key={u.id} style={{borderBottom:"1px solid "+S.border}}>
                      <div style={{padding:"12px 18px",display:"flex",alignItems:"center",gap:12}}>
                        <UserAvatar user={u} size={36} fontSize={13} />
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:1,flexWrap:"wrap"}}>
                            <span style={{fontSize:13,fontWeight:600,color:S.text}}>{u.name}</span>
                            <span style={{fontSize:9,padding:"1px 6px",borderRadius:4,fontFamily:"monospace",fontWeight:700,background:role.color+"18",color:role.color,border:"1px solid "+role.color+"30"}}>{role.icon} {role.label}</span>
                            {u.ssoLinked&&<span style={{fontSize:9,padding:"1px 6px",borderRadius:4,fontFamily:"monospace",background:S.success+"18",color:S.success}}>SSO</span>}
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginTop:2,flexWrap:"wrap"}}>
                            <span style={{fontSize:11,color:S.text3,fontFamily:"monospace"}}>{u.email||"No email"}</span>
                            {u.role==="superadmin" && <span style={{fontSize:9,padding:"1px 6px",borderRadius:10,background:"#d4a01718",color:"#d4a017",border:"1px solid #d4a01730",fontFamily:"monospace",fontWeight:700}}>👑 OWNER</span>}
                            {Object.values(u.overrides||{}).some(v=>v?.length>0) && <span style={{fontSize:9,padding:"1px 6px",borderRadius:10,background:S.accent+"18",color:S.accent,border:`1px solid ${S.accent}30`,fontFamily:"monospace"}}>custom permissions</span>}
                            {(u.adGroups||[]).length>0 && <span style={{fontSize:9,padding:"1px 6px",borderRadius:10,background:"#8b5cf618",color:"#8b5cf6",border:"1px solid #8b5cf630",fontFamily:"monospace"}}>AD group</span>}
                          </div>
                        </div>
                        <div style={{display:"flex",gap:5,alignItems:"center",flexShrink:0,flexWrap:"wrap",justifyContent:"flex-end"}}>
                          {(u.role!=="superadmin" || isSuperAdmin) && <button onClick={()=>setEditUser(isEditing?null:{...u})} style={{padding:"4px 9px",borderRadius:6,background:isEditing?S.accentDim:S.inputBg,border:"1px solid "+(isEditing?S.accent:S.border2),color:isEditing?S.accent:S.text2,fontSize:10,cursor:"pointer"}}>{isEditing?"Cancel":"Edit"}</button>}
                          {serverMode && (u.role!=="superadmin" || isSuperAdmin) && <button onClick={()=>resetUserPassword(u)} style={{padding:"4px 9px",borderRadius:6,background:S.inputBg,border:"1px solid "+S.border2,color:S.text2,fontSize:10,cursor:"pointer"}} title="Reset password & force change on next login">🔑 Reset</button>}
                          <button onClick={()=>{setTab("permissions");}} style={{padding:"4px 9px",borderRadius:6,background:S.inputBg,border:"1px solid "+S.border2,color:S.text3,fontSize:10,cursor:"pointer"}} title="Edit permissions">🔒</button>
                          {users.length>1 && (u.role!=="superadmin" || isSuperAdmin) && <button onClick={()=>removeUser(u.id)} style={{padding:"4px 9px",borderRadius:6,background:S.danger+"0d",border:"1px solid "+S.danger+"30",color:S.danger,fontSize:10,cursor:"pointer"}}>Remove</button>}
                        </div>
                      </div>
                      {isEditing && (
                        <div style={{padding:"12px 18px",background:S.surface2,borderTop:"1px solid "+S.border}}>
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
                            <div><div style={{fontSize:9,color:S.text3,fontFamily:"monospace",marginBottom:3}}>NAME</div><input style={ISt()} value={editUser.name} onChange={e=>setEditUser(p=>({...p,name:e.target.value}))}/></div>
                            <div><div style={{fontSize:9,color:S.text3,fontFamily:"monospace",marginBottom:3}}>EMAIL</div><input style={ISt()} value={editUser.email||""} onChange={e=>setEditUser(p=>({...p,email:e.target.value}))} type="email"/></div>
                            <div><div style={{fontSize:9,color:S.text3,fontFamily:"monospace",marginBottom:3}}>ROLE</div>
                              <select style={{...ISt(),fontFamily:"monospace",fontSize:11}} value={editUser.role} onChange={e=>setEditUser(p=>({...p,role:e.target.value}))}>
                                {ROLES.map(r=><option key={r.id} value={r.id}>{r.icon} {r.label}</option>)}
                              </select>
                            </div>
                          </div>
                          {/* Model access control */}
                          {(() => {
                            const allModels = Object.entries(PROVIDERS).flatMap(([pid,p]) => (!p.builtin && !integrations[pid]?.key) ? [] : p.models.filter(m => modelVisibleToNonOwners(m.id)).map(m => ({...m, provider: pid, providerName: p.name, providerColor: p.color})));
                            const allowedSet = new Set(editUser.allowedModels || []);
                            const unrestricted = !editUser.allowedModels || editUser.allowedModels.length === 0;
                            return (
                              <div style={{marginBottom:12,padding:"10px 12px",borderRadius:9,background:S.cardBg,border:`1px solid ${S.border2}`}}>
                                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                                  <div>
                                    <div style={{fontSize:11,fontWeight:700,color:S.text,marginBottom:2}}>Model Access</div>
                                    <div style={{fontSize:10,color:S.text3}}>{unrestricted?`All ${allModels.length} models available`:`${allowedSet.size} of ${allModels.length} models allowed`}</div>
                                  </div>
                                  <div style={{display:"flex",gap:5}}>
                                    <button onClick={()=>setEditUser(p=>({...p,allowedModels:null}))} style={{padding:"3px 9px",borderRadius:6,background:unrestricted?S.accentDim:"transparent",border:`1px solid ${unrestricted?S.accent:S.border2}`,color:unrestricted?S.accent:S.text3,fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>All</button>
                                    <button onClick={()=>setEditUser(p=>({...p,allowedModels:[]}))} style={{padding:"3px 9px",borderRadius:6,background:"transparent",border:`1px solid ${S.border2}`,color:S.text3,fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>None</button>
                                  </div>
                                </div>
                                {!unrestricted && (
                                  <div style={{maxHeight:180,overflowY:"auto",display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
                                    {allModels.map(m => {
                                      const on = allowedSet.has(m.id);
                                      return (
                                        <label key={m.id} style={{display:"flex",alignItems:"center",gap:7,padding:"5px 8px",borderRadius:6,border:`1px solid ${S.border2}`,cursor:"pointer",background:on?S.accentDim:"none",overflow:"hidden"}}>
                                          <input type="checkbox" checked={on} onChange={e=>{
                                            const list = new Set(editUser.allowedModels||[]);
                                            if (e.target.checked) list.add(m.id); else list.delete(m.id);
                                            setEditUser(p=>({...p,allowedModels:[...list]}));
                                          }} style={{accentColor:S.accent,flexShrink:0}}/>
                                          <span style={{fontSize:10,width:7,height:7,borderRadius:"50%",background:m.providerColor,display:"inline-block",flexShrink:0}}/>
                                          <span style={{fontSize:11,color:S.text2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.name}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                          {/* Email verification status — admin can force-verify for
                              owner-provisioned trial users so they don't see the nag
                              banner on first login. */}
                          <div style={{marginBottom:12,padding:"10px 12px",borderRadius:9,background:S.cardBg,border:`1px solid ${S.border2}`,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                            <div style={{flex:1,minWidth:180}}>
                              <div style={{fontSize:11,fontWeight:700,color:S.text,marginBottom:2}}>✉️ Email verified</div>
                              <div style={{fontSize:10,color:S.text3}}>{editUser.emailVerified ? "Confirmed — no banner shown." : "Unverified — user sees the verify banner on login."}</div>
                            </div>
                            <button onClick={()=>setEditUser(p=>({...p, emailVerified: !p.emailVerified}))}
                              style={{padding:"5px 12px",borderRadius:7,fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600,background:editUser.emailVerified?S.success+"22":S.accentDim,border:`1px solid ${editUser.emailVerified?S.success+"55":S.accent+"55"}`,color:editUser.emailVerified?S.success:S.accent}}>
                              {editUser.emailVerified ? "✓ Verified — mark unverified" : "Mark as verified"}
                            </button>
                          </div>
                          {/* Per-user compliance routing override (admin-set). Value here
                              ratchets UP org-level compliance for this user; it cannot
                              relax below the org's policy. "Off" means "use org default." */}
                          <div style={{marginBottom:12,padding:"10px 12px",borderRadius:9,background:S.cardBg,border:`1px solid ${S.border2}`}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                              <div>
                                <div style={{fontSize:11,fontWeight:700,color:S.text,marginBottom:2}}>🛡 Compliance routing</div>
                                <div style={{fontSize:10,color:S.text3,lineHeight:1.5}}>Force BAA-covered upstream for this user. Org policy still applies — this only ratchets UP.</div>
                              </div>
                            </div>
                            <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                              {[["","Use org default"],["off","Off"],["auto","Auto-detect PHI"],["always","Always BAA"]].map(([v,l]) => {
                                const current = editUser.compliance_mode || "";
                                const active = current === v;
                                return (
                                  <button key={v||"default"} onClick={()=>setEditUser(p=>({...p, compliance_mode: v || null}))}
                                    style={{padding:"5px 11px",borderRadius:7,background:active?S.accentDim:"transparent",border:`1px solid ${active?S.accent:S.border2}`,color:active?S.accent:S.text3,fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:active?600:400}}>
                                    {l}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <Btn onClick={saveUser}>Save Changes</Btn>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── INVITES TAB ── */}
          {tab==="invites" && (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:14,overflow:"hidden"}}>
                <div style={{padding:"13px 18px",borderBottom:"1px solid "+S.border,fontSize:12,fontWeight:700,color:S.text2}}>Send Invite</div>
                <div style={{padding:16}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:12}}>
                    <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>EMAIL</div><input style={ISt()} value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} placeholder="colleague@firm.com" type="email"/></div>
                    <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>ROLE</div>
                      <select style={{...ISt(),fontFamily:"monospace",fontSize:12}} value={inviteRole} onChange={e=>setInviteRole(e.target.value)}>
                        {ROLES.map(r=><option key={r.id} value={r.id}>{r.icon} {r.label}</option>)}
                      </select>
                    </div>
                    <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>EXPIRES IN</div>
                      <select style={{...ISt(),fontFamily:"monospace",fontSize:12}} value={inviteExpiry} onChange={e=>setInviteExpiry(e.target.value)}>
                        {[["1","1 day"],["3","3 days"],["7","7 days"],["14","14 days"],["30","30 days"]].map(([v,l])=><option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <Btn onClick={emailInvite}>✉ Email Invite</Btn>
                    <Btn variant="secondary" onClick={createInvite}>{IC.invite} Copy invite link</Btn>
                    {serverMode && <Btn variant="secondary" onClick={async()=>{
                      try {
                        const r = await fetch("/api/admin/mail-test", { method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${authToken}`}, body: JSON.stringify({}) });
                        const d = await r.json().catch(()=>({}));
                        showToast(r.ok ? `Test email sent via ${d.provider} ✓ — check your inbox` : `Email delivery is broken: ${d.error || "unknown error"}`);
                      } catch(e) { showToast("Email delivery test failed: " + e.message); }
                    }}>🧪 Test email delivery</Btn>}
                  </div>
                </div>
              </div>

              {/* Invite list */}
              {invites.length > 0 && (
                <div style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:14,overflow:"hidden"}}>
                  <div style={{padding:"13px 18px",borderBottom:"1px solid "+S.border,fontSize:12,fontWeight:700,color:S.text2}}>Invites · {invites.length}</div>
                  {[...invites].reverse().map(inv => {
                    const role = ROLES.find(r=>r.id===inv.role)||ROLES[1];
                    const expired = isExpired(inv);
                    const daysLeft = Math.max(0, Math.ceil((inv.expires - Date.now()) / 86400000));
                    const statusColor = inv.status==="pending"&&!expired ? S.accent : inv.status==="accepted" ? S.success : S.danger;
                    const statusLabel = inv.status==="accepted" ? "Accepted" : expired ? "Expired" : "Pending";
                    const link = inviteLink(inv.token);
                    return (
                      <div key={inv.id} style={{padding:"12px 18px",borderBottom:"1px solid "+S.border}}>
                        <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:3}}>
                              <span style={{fontSize:12,fontWeight:600,color:S.text}}>{inv.email}</span>
                              <span style={{fontSize:9,padding:"1px 6px",borderRadius:4,fontFamily:"monospace",fontWeight:700,background:role.color+"18",color:role.color,border:"1px solid "+role.color+"30"}}>{role.icon} {role.label}</span>
                              <span style={{fontSize:9,padding:"1px 6px",borderRadius:4,fontFamily:"monospace",background:statusColor+"18",color:statusColor}}>{statusLabel}{inv.status==="pending"&&!expired?" · "+daysLeft+"d left":""}</span>
                            </div>
                            {inv.status==="pending"&&!expired && (
                              <div style={{display:"flex",alignItems:"center",gap:6,background:S.surface2,borderRadius:7,padding:"5px 9px",marginTop:4}}>
                                <span style={{fontSize:10,fontFamily:"monospace",color:S.text3,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{link}</span>
                                <button onClick={()=>copy(inv.id, link)} style={{background:"none",border:"none",color:copied===inv.id?S.success:S.text3,cursor:"pointer",fontSize:10,fontFamily:"monospace",flexShrink:0,padding:"0 4px"}}>{copied===inv.id?"✓ Copied":"Copy"}</button>
                              </div>
                            )}
                          </div>
                          {inv.status==="pending"&&!expired && (
                            <button onClick={()=>revokeInvite(inv.id)} style={{padding:"4px 9px",borderRadius:6,background:S.danger+"0d",border:"1px solid "+S.danger+"30",color:S.danger,fontSize:10,cursor:"pointer",flexShrink:0}}>Revoke</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {invites.length===0 && (
                <div style={{textAlign:"center",padding:"40px 32px",color:S.text3,border:"1px dashed "+S.border2,borderRadius:14}}>
                  <div style={{fontSize:28,marginBottom:10}}>✉</div>
                  <div style={{fontSize:13,fontWeight:500,color:S.text2,marginBottom:6}}>No invites yet</div>
                  <div style={{fontSize:12}}>Create an invite link above to onboard teammates.</div>
                </div>
              )}
            </div>
          )}

          {/* ── ROLES TAB ── */}
          {tab==="roles" && (
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:14,overflow:"hidden"}}>
                <div style={{padding:"13px 18px",borderBottom:"1px solid "+S.border,fontSize:12,fontWeight:700,color:S.text2}}>Role Definitions</div>
                {ROLES.map(role => {
                  const roleUsers = users.filter(u=>u.role===role.id);
                  const PERMS = {
                    admin: ["Chat","Agents","Integrations","MCP","Settings","User Management","Analytics","All permissions"],
                    analyst: ["Chat","Agents","Analytics"],
                    member: ["Chat","Agents"],
                    viewer: ["Chat (read shared)"],
                  };
                  return (
                    <div key={role.id} style={{padding:"14px 18px",borderBottom:"1px solid "+S.border,display:"flex",gap:14,alignItems:"flex-start"}}>
                      <div style={{width:42,height:42,borderRadius:12,background:role.color+"18",border:"1px solid "+role.color+"30",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>{role.icon}</div>
                      <div style={{flex:1}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                          <span style={{fontSize:14,fontWeight:700,color:S.text}}>{role.label}</span>
                          <span style={{fontSize:9,padding:"1px 6px",borderRadius:10,fontFamily:"monospace",background:role.color+"18",color:role.color,border:"1px solid "+role.color+"30"}}>{roleUsers.length} user{roleUsers.length!==1?"s":""}</span>
                        </div>
                        <div style={{fontSize:12,color:S.text2,marginBottom:8}}>{role.desc}</div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                          {(PERMS[role.id]||[]).map(p=>(
                            <span key={p} style={{fontSize:9,padding:"2px 7px",borderRadius:3,fontFamily:"monospace",background:S.surface2,border:"1px solid "+S.border,color:S.text2}}>{p}</span>
                          ))}
                        </div>
                        {roleUsers.length>0 && (
                          <div style={{display:"flex",gap:4,marginTop:8,flexWrap:"wrap"}}>
                            {roleUsers.map(u=><div key={u.id} title={u.name}><UserAvatar user={u} size={22} fontSize={9} /></div>)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{background:S.accent+"08",border:"1px solid "+S.border2,borderRadius:12,padding:"12px 16px",fontSize:11,color:S.text2,lineHeight:1.7}}>
                <strong style={{color:S.text}}>Role-based access</strong> controls which sections each user can see. Roles are enforced at the UI level. For production deployments with server-side enforcement, integrate with your identity provider using the SSO tab.
              </div>
            </div>
          )}

          {/* ── PERMISSIONS TAB ── */}
          {tab==="permissions" && <PermissionsTab rolePerms={rolePerms} setRolePerms={setRolePerms} users={users} setUsers={setUsers} allAgents={allAgents} allTools={allTools} allMcpServers={mcpServers||[]} setTab={setTab}/>}
          {false && (() => {
            // All available LLM models across all providers
            const allModels = [];
            for (const [pid, p] of Object.entries(PROVIDERS)) {
              for (const m of (p.models||[])) allModels.push({id:m.id, label:`${p.icon} ${p.name} — ${m.name}`, provider:pid});
            }

            const RP_ROLES = [
              {id:"admin",   label:"Admin",   icon:"👑", color:"#dc2626"},
              {id:"member",  label:"Member",  icon:"👤", color:S.accent},
              {id:"analyst", label:"Analyst", icon:"📊", color:"#8b5cf6"},
              {id:"viewer",  label:"Viewer",  icon:"👁", color:S.text3},
            ];

            // Toggle a resource in a role's allowlist (empty = all allowed)
            const toggle = (roleId, resource, itemId) => {
              setRolePerms(p => {
                const cur = [...(p[roleId]?.[resource]||[])];
                const idx = cur.indexOf(itemId);
                return {...p, [roleId]: {...(p[roleId]||{}), [resource]: idx>=0 ? cur.filter(x=>x!==itemId) : [...cur,itemId]}};
              });
            };

            // Chip toggle button
            const Chip = ({roleId, resource, itemId, label, color}) => {
              const sel = (rolePerms[roleId]?.[resource]||[]).includes(itemId);
              return (
                <button onClick={()=>toggle(roleId,resource,itemId)}
                  style={{display:"inline-flex",alignItems:"center",gap:5,padding:"4px 10px",borderRadius:20,border:`1px solid ${sel?(color||S.accent):S.border2}`,background:sel?(color||S.accent)+"18":"transparent",color:sel?(color||S.accent):S.text3,fontSize:11,cursor:"pointer",transition:"all 0.15s",fontFamily:"inherit"}}>
                  {sel && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                  {label}
                </button>
              );
            };

            // AD group input for a role
            const ADGroupInput = ({roleId}) => {
              const [val,setVal] = useState("");
              const groups = rolePerms[roleId]?.adGroups||[];
              return (
                <div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:6}}>
                    {groups.map(g=>(
                      <span key={g} style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 9px",borderRadius:20,background:S.accentDim,border:`1px solid ${S.border3}`,fontSize:10,fontFamily:"monospace",color:S.accent}}>
                        {g}
                        <button onClick={()=>setRolePerms(p=>({...p,[roleId]:{...p[roleId],adGroups:groups.filter(x=>x!==g)}}))} style={{background:"none",border:"none",color:S.accent,cursor:"pointer",padding:0,lineHeight:1,fontSize:13}}>×</button>
                      </span>
                    ))}
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <input value={val} onChange={e=>setVal(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&val.trim()){setRolePerms(p=>({...p,[roleId]:{...p[roleId],adGroups:[...(p[roleId]?.adGroups||[]),val.trim()]}}));setVal("");}}} placeholder="CN=Group,OU=Groups,DC=firm,DC=com" style={{...ISt(),fontSize:11,fontFamily:"monospace",flex:1}}/>
                    <button onClick={()=>{if(val.trim()){setRolePerms(p=>({...p,[roleId]:{...p[roleId],adGroups:[...(p[roleId]?.adGroups||[]),val.trim()]}}));setVal("");}}} style={{padding:"7px 12px",borderRadius:7,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:11,cursor:"pointer",whiteSpace:"nowrap",fontFamily:"inherit"}}>+ Add</button>
                  </div>
                </div>
              );
            };

            // Per-user override panel
            const [overrideUid, setOverrideUid] = useState(null);
            const overUser = users.find(u=>u.id===overrideUid);

            const toggleUser = (uid, resource, itemId) => {
              setUsers(p => p.map(u => {
                if (u.id !== uid) return u;
                const ov = {...(u.overrides||{})};
                const cur = [...(ov[resource]||[])];
                const idx = cur.indexOf(itemId);
                ov[resource] = idx>=0 ? cur.filter(x=>x!==itemId) : [...cur,itemId];
                return {...u, overrides:ov};
              }));
            };

            const ChipUser = ({uid, resource, itemId, label}) => {
              const u = users.find(x=>x.id===uid);
              const sel = (u?.overrides?.[resource]||[]).includes(itemId);
              return (
                <button onClick={()=>toggleUser(uid,resource,itemId)}
                  style={{display:"inline-flex",alignItems:"center",gap:5,padding:"4px 10px",borderRadius:20,border:`1px solid ${sel?S.accent:S.border2}`,background:sel?S.accentDim:"transparent",color:sel?S.accent:S.text3,fontSize:11,cursor:"pointer",transition:"all 0.15s",fontFamily:"inherit"}}>
                  {sel && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                  {label}
                </button>
              );
            };

            const SecP = ({title, hint, children}) => (
              <div style={{marginBottom:20}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                  <span style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em",color:S.text3,fontFamily:"monospace"}}>{title}</span>
                  {hint && <span style={{fontSize:9,color:S.text3,background:S.surface2,padding:"1px 7px",borderRadius:10,border:`1px solid ${S.border}`}}>{hint}</span>}
                </div>
                {children}
              </div>
            );

            return (
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <div style={{background:S.accent+"08",border:"1px solid "+S.border2,borderRadius:10,padding:"10px 14px",fontSize:12,color:S.text2,lineHeight:1.6}}>
                  <strong style={{color:S.text}}>Resource Permissions</strong> — Control which LLM models, agents, and tools each role can access. Empty = all allowed. Checked items are <em>explicitly allowed</em>. Per-user overrides take precedence over role settings.
                </div>

                {/* ─ Role permission cards ─ */}
                {RP_ROLES.map(role => (
                  <div key={role.id} style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
                    <div style={{padding:"12px 18px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:10,background:S.surface}}>
                      <span style={{fontSize:18}}>{role.icon}</span>
                      <span style={{fontSize:13,fontWeight:700,color:S.text}}>{role.label}</span>
                      <span style={{fontSize:9,padding:"1px 7px",borderRadius:10,background:role.color+"18",color:role.color,border:`1px solid ${role.color}30`,fontFamily:"monospace",fontWeight:700}}>{users.filter(u=>u.role===role.id).length} user{users.filter(u=>u.role===role.id).length!==1?"s":""}</span>
                      {role.id==="admin" && <span style={{fontSize:10,color:S.text3,marginLeft:"auto"}}>Admins always have full access</span>}
                    </div>
                    {role.id === "admin" ? (
                      <div style={{padding:"12px 18px",fontSize:11,color:S.text3}}>All resources unrestricted — admin role bypasses all permission filters.</div>
                    ) : (
                      <div style={{padding:"16px 18px"}}>
                        <SecP title="LLM Models" hint="empty = all models allowed">
                          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                            {allModels.map(m=><Chip key={m.id} roleId={role.id} resource="models" itemId={m.id} label={m.label} color={role.color}/>)}
                          </div>
                        </SecP>
                        <SecP title="Agents" hint="empty = all agents allowed">
                          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                            {allAgents.length===0 ? <span style={{fontSize:11,color:S.text3}}>No agents created yet</span>
                              : allAgents.map(a=><Chip key={a.id} roleId={role.id} resource="agents" itemId={a.id} label={a.emoji+" "+a.name} color={role.color}/>)}
                          </div>
                        </SecP>
                        <SecP title="Tools" hint="empty = all tools allowed">
                          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                            {allTools.length===0 ? <span style={{fontSize:11,color:S.text3}}>No tools created yet</span>
                              : allTools.map(t=><Chip key={t.id} roleId={role.id} resource="tools" itemId={t.id} label={"🔧 "+t.name} color={role.color}/>)}
                          </div>
                        </SecP>
                        <SecP title="AD Groups" hint="users in these groups get this role">
                          <ADGroupInput roleId={role.id}/>
                        </SecP>
                      </div>
                    )}
                  </div>
                ))}

                {/* ─ Per-user overrides ─ */}
                <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
                  <div style={{padding:"12px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2,background:S.surface}}>Per-User Overrides</div>
                  <div style={{padding:"10px 18px",fontSize:11,color:S.text3,borderBottom:`1px solid ${S.border}`}}>Select a user to grant or restrict specific resources independent of their role.</div>
                  {/* User selector */}
                  <div style={{padding:"12px 18px",borderBottom:`1px solid ${S.border}`,display:"flex",flexWrap:"wrap",gap:6}}>
                    {users.map(u=>{
                      const hasOv = Object.values(u.overrides||{}).some(v=>v?.length>0);
                      return (
                        <button key={u.id} onClick={()=>setOverrideUid(overrideUid===u.id?null:u.id)}
                          style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 12px",borderRadius:20,border:`1px solid ${overrideUid===u.id?S.accent:S.border2}`,background:overrideUid===u.id?S.accentDim:"transparent",color:overrideUid===u.id?S.accent:S.text2,fontSize:11,cursor:"pointer",transition:"all 0.15s",fontFamily:"inherit"}}>
                          <UserAvatar user={u} size={18} fontSize={8} />
                          {u.name}
                          {hasOv && <span style={{width:6,height:6,borderRadius:"50%",background:S.accent,flexShrink:0,display:"inline-block"}}/>}
                        </button>
                      );
                    })}
                  </div>
                  {overUser && (
                    <div style={{padding:"16px 18px"}}>
                      <div style={{fontSize:11,color:S.text2,marginBottom:14,lineHeight:1.6}}>
                        Overrides for <strong>{overUser.name}</strong> — checked items are <em>explicitly allowed</em> for this user only, overriding their <strong>{overUser.role}</strong> role settings. Leave empty to inherit from role.
                        {Object.values(overUser.overrides||{}).some(v=>v?.length>0) && (
                          <button onClick={()=>setUsers(p=>p.map(u=>u.id===overUser.id?{...u,overrides:{}}:u))} style={{marginLeft:10,background:"none",border:"none",color:S.danger,cursor:"pointer",fontSize:11,padding:0,textDecoration:"underline"}}>Clear all overrides</button>
                        )}
                      </div>
                      <SecP title="LLM Models">
                        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                          {allModels.map(m=><ChipUser key={m.id} uid={overUser.id} resource="models" itemId={m.id} label={m.label}/>)}
                        </div>
                      </SecP>
                      <SecP title="Agents">
                        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                          {allAgents.length===0 ? <span style={{fontSize:11,color:S.text3}}>No agents created yet</span>
                            : allAgents.map(a=><ChipUser key={a.id} uid={overUser.id} resource="agents" itemId={a.id} label={a.emoji+" "+a.name}/>)}
                        </div>
                      </SecP>
                      <SecP title="Tools">
                        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                          {allTools.length===0 ? <span style={{fontSize:11,color:S.text3}}>No tools created yet</span>
                            : allTools.map(t=><ChipUser key={t.id} uid={overUser.id} resource="tools" itemId={t.id} label={"🔧 "+t.name}/>)}
                        </div>
                      </SecP>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          {/* end dead code */}

          {/* ── SSO TAB ── */}
          {tab==="sso" && (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {/* Provider picker */}
              <div style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:14,overflow:"hidden"}}>
                <div style={{padding:"13px 18px",borderBottom:"1px solid "+S.border,fontSize:12,fontWeight:700,color:S.text2}}>Identity Provider</div>
                <div style={{padding:16}}>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:7,marginBottom:14}}>
                    {SSO_PROVIDERS.map(p=>(
                      <div key={p.id} onClick={()=>setSso(prev=>({...prev,provider:p.id}))}
                        style={{padding:"10px 8px",borderRadius:10,border:"1px solid "+(sso.provider===p.id?S.accent:S.border2),background:sso.provider===p.id?S.accentDim:S.inputBg,cursor:"pointer",textAlign:"center",transition:"all 0.15s"}}>
                        <div style={{fontSize:20,marginBottom:4}}>{p.icon}</div>
                        <div style={{fontSize:10,fontWeight:sso.provider===p.id?700:500,color:sso.provider===p.id?S.accent:S.text,lineHeight:1.3}}>{p.label}</div>
                        <div style={{fontSize:8,color:S.text3,fontFamily:"monospace",marginTop:2}}>{p.desc}</div>
                      </div>
                    ))}
                  </div>

                  {/* Config fields based on provider */}
                  {sso.provider==="none" && (
                    <div style={{background:S.surface2,borderRadius:9,padding:"12px 14px",fontSize:12,color:S.text2,lineHeight:1.7}}>Local PIN authentication is active. Select a provider above to configure SSO.</div>
                  )}

                  {["microsoft","google","okta","onelogin","oidc"].includes(sso.provider) && (
                    <div style={{display:"flex",flexDirection:"column",gap:10}}>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                        <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>CLIENT ID</div><input style={ISt()} value={sso.clientId||""} onChange={e=>setSso(p=>({...p,clientId:e.target.value}))} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"/></div>
                        <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>CLIENT SECRET</div>
                          <div style={{display:"flex",gap:4}}>
                            <input type={showSecret?"text":"password"} style={{...ISt(),flex:1}} value={sso.clientSecret||""} onChange={e=>setSso(p=>({...p,clientSecret:e.target.value}))} placeholder="••••••••••••••••"/>
                            <button onClick={()=>setShowSecret(p=>!p)} style={{background:"none",border:"none",color:S.text3,cursor:"pointer",padding:"0 6px",display:"flex",alignItems:"center"}}>{IC.eye}</button>
                          </div>
                        </div>
                      </div>
                      {sso.provider==="microsoft" && (
                        <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>TENANT ID (Azure)</div><input style={ISt()} value={sso.tenantId||""} onChange={e=>setSso(p=>({...p,tenantId:e.target.value}))} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"/></div>
                      )}
                      {["okta","onelogin","oidc"].includes(sso.provider) && (
                        <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>ISSUER / DOMAIN URL</div><input style={ISt()} value={sso.domain||""} onChange={e=>setSso(p=>({...p,domain:e.target.value}))} placeholder={"https://"+sso.provider+".example.com"}/></div>
                      )}
                      <div style={{background:S.surface2,borderRadius:8,padding:"10px 12px"}}>
                        <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>CALLBACK / REDIRECT URI (add to your IdP)</div>
                        <div style={{display:"flex",alignItems:"center",gap:6,background:S.codeBlockBg,borderRadius:6,padding:"6px 10px",border:"1px solid "+S.border}}>
                          <span style={{fontSize:11,fontFamily:"monospace",color:S.text,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{window.location.origin+"/auth/callback"}</span>
                          <button onClick={()=>copy("cb",window.location.origin+"/auth/callback")} style={{background:"none",border:"none",color:copied==="cb"?S.success:S.text3,cursor:"pointer",fontSize:10,fontFamily:"monospace",flexShrink:0}}>{copied==="cb"?"✓":"Copy"}</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {sso.provider==="saml" && (
                    <div style={{display:"flex",flexDirection:"column",gap:10}}>
                      <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>IdP METADATA URL</div><input style={ISt()} value={sso.domain||""} onChange={e=>setSso(p=>({...p,domain:e.target.value}))} placeholder="https://idp.example.com/metadata"/></div>
                      <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>SP ENTITY ID</div><input style={ISt()} value={sso.clientId||""} onChange={e=>setSso(p=>({...p,clientId:e.target.value}))} placeholder={"https://chatfortai.ai"}/></div>
                      <div style={{background:S.surface2,borderRadius:8,padding:"10px 12px"}}>
                        <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>ACS URL (add to your IdP)</div>
                        <div style={{display:"flex",alignItems:"center",gap:6,background:S.codeBlockBg,borderRadius:6,padding:"6px 10px",border:"1px solid "+S.border}}>
                          <span style={{fontSize:11,fontFamily:"monospace",color:S.text,flex:1}}>{window.location.origin+"/auth/saml/callback"}</span>
                          <button onClick={()=>copy("acs",window.location.origin+"/auth/saml/callback")} style={{background:"none",border:"none",color:copied==="acs"?S.success:S.text3,cursor:"pointer",fontSize:10,fontFamily:"monospace",flexShrink:0}}>{copied==="acs"?"✓":"Copy"}</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {sso.provider==="ldap" && (
                    <div style={{display:"flex",flexDirection:"column",gap:10}}>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                        <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>LDAP SERVER URL</div><input style={ISt()} value={adConfig.url} onChange={e=>setAdConfig(p=>({...p,url:e.target.value}))} placeholder="ldaps://ad.yourfirm.com:636"/></div>
                        <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>BASE DN</div><input style={ISt()} value={adConfig.baseDN} onChange={e=>setAdConfig(p=>({...p,baseDN:e.target.value}))} placeholder="DC=yourfirm,DC=com"/></div>
                        <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>BIND DN (service account)</div><input style={ISt()} value={adConfig.bindDN} onChange={e=>setAdConfig(p=>({...p,bindDN:e.target.value}))} placeholder="CN=svc-chatfort,OU=Service,DC=firm,DC=com"/></div>
                        <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>BIND PASSWORD</div><input type="password" style={ISt()} value={adConfig.bindPass} onChange={e=>setAdConfig(p=>({...p,bindPass:e.target.value}))} placeholder="••••••••••"/></div>
                        <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>USER ATTRIBUTE</div><input style={ISt()} value={adConfig.userAttr} onChange={e=>setAdConfig(p=>({...p,userAttr:e.target.value}))} placeholder="sAMAccountName"/></div>
                        <div><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>GROUP ATTRIBUTE</div><input style={ISt()} value={adConfig.groupAttr} onChange={e=>setAdConfig(p=>({...p,groupAttr:e.target.value}))} placeholder="memberOf"/></div>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",background:S.surface2,borderRadius:8}}>
                        <div onClick={()=>setAdConfig(p=>({...p,tlsEnabled:!p.tlsEnabled}))} style={{width:32,height:18,background:adConfig.tlsEnabled?S.success:S.surface3,borderRadius:9,position:"relative",cursor:"pointer",border:"1px solid "+S.border2,transition:"background 0.2s"}}>
                          <div style={{position:"absolute",top:2,left:adConfig.tlsEnabled?14:2,width:12,height:12,background:"#fff",borderRadius:"50%",transition:"left 0.18s"}}/>
                        </div>
                        <span style={{fontSize:12,color:S.text2}}>Use TLS/LDAPS (recommended)</span>
                      </div>
                      <div style={{background:"#f59e0b08",border:"1px solid #f59e0b30",borderRadius:8,padding:"10px 13px",fontSize:11,color:S.text2,lineHeight:1.7}}>
                        <strong style={{color:"#f59e0b"}}>AD Group → Role mapping:</strong> Configure group mappings after connecting. Users in <code style={{fontFamily:"monospace",background:S.surface2,padding:"1px 4px",borderRadius:3}}>ChatFort-Admins</code> get Admin role, <code style={{fontFamily:"monospace",background:S.surface2,padding:"1px 4px",borderRadius:3}}>ChatFort-Users</code> get Member, <code style={{fontFamily:"monospace",background:S.surface2,padding:"1px 4px",borderRadius:3}}>ChatFort-Viewers</code> get Viewer.
                      </div>
                    </div>
                  )}

                  {sso.provider!=="none" && (
                    <div style={{marginTop:14,display:"flex",gap:8,alignItems:"center"}}>
                      <Btn onClick={saveSso}>{IC.shield2} Save SSO Config</Btn>
                      <button onClick={()=>showToast("Test connection requires a running backend. Deploy ChatFort server to enable.")} style={{padding:"8px 14px",borderRadius:9,background:"none",border:"1px solid "+S.border2,color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Test Connection</button>
                      <div style={{display:"flex",alignItems:"center",gap:7,marginLeft:"auto"}}>
                        <span style={{fontSize:12,color:S.text2}}>Enable SSO</span>
                        <div onClick={()=>setSso(p=>({...p,enabled:!p.enabled}))} style={{width:36,height:20,background:sso.enabled?S.success:S.surface3,borderRadius:10,position:"relative",cursor:"pointer",border:"1px solid "+S.border2,transition:"background 0.2s"}}>
                          <div style={{position:"absolute",top:2,left:sso.enabled?16:2,width:14,height:14,background:"#fff",borderRadius:"50%",transition:"left 0.18s"}}/>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* SSO deployment note */}
              <div style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:12,overflow:"hidden"}}>
                <div style={{padding:"13px 18px",borderBottom:"1px solid "+S.border,fontSize:12,fontWeight:700,color:S.text2}}>Deployment Notes</div>
                <div style={{padding:16,display:"flex",flexDirection:"column",gap:8}}>
                  {[
                    ["🌐","ChatFort Cloud","SSO works out of the box. Config above syncs to your tenant."],
                    ["🖥","Self-hosted (Docker)","Set environment variables: SSO_PROVIDER, SSO_CLIENT_ID, SSO_CLIENT_SECRET, SSO_TENANT_ID"],
                    ["🏢","On-premise AD","LDAP connector runs as a sidecar. Configure bind DN and service account above."],
                    ["🔐","Session management","JWT tokens with configurable expiry. Supports refresh tokens and concurrent sessions."],
                  ].map(([icon,label,desc])=>(
                    <div key={label} style={{display:"flex",gap:10,padding:"9px 12px",background:S.surface2,borderRadius:8}}>
                      <span style={{fontSize:16,flexShrink:0}}>{icon}</span>
                      <div><div style={{fontSize:12,fontWeight:600,color:S.text,marginBottom:2}}>{label}</div><div style={{fontSize:11,color:S.text2}}>{desc}</div></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── AUDIT LOG TAB ── */}
          {tab==="audit" && (
            <div>
              <div style={{background:S.accent+"08",border:"1px solid "+S.border2,borderRadius:12,padding:"12px 16px",marginBottom:14,fontSize:12,color:S.text2,lineHeight:1.7}}>
                User activity audit log. All user actions, logins, and admin changes are recorded locally.
              </div>
              <div style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:14,overflow:"hidden"}}>
                <div style={{padding:"13px 18px",borderBottom:"1px solid "+S.border,fontSize:12,fontWeight:700,color:S.text2}}>Recent Activity</div>
                {[
                  {icon:"👑",action:"Admin unlocked User Management",user:"Admin",time:"Just now",color:S.danger},
                  ...users.filter(u=>u.id!=="u0").map(u=>({icon:"👤",action:"User added: "+u.name+" ("+u.role+")",user:"Admin",time:u.created?new Date(u.created).toLocaleString():"Recently",color:S.accent})),
                  ...invites.map(inv=>({icon:"✉",action:"Invite sent to "+inv.email+" ("+inv.role+")",user:"Admin",time:new Date(inv.created).toLocaleString(),color:inv.status==="accepted"?S.success:S.text3})),
                  {icon:"🔐",action:"ChatFort initialized · Admin account created",user:"System",time:new Date(ld("cf_users",[{}])[0]?.created||Date.now()-86400000).toLocaleString(),color:S.text3},
                ].map((entry,i)=>(
                  <div key={i} style={{padding:"10px 18px",borderBottom:"1px solid "+S.border,display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:16,flexShrink:0}}>{entry.icon}</span>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12,color:S.text}}>{entry.action}</div>
                      <div style={{fontSize:10,color:S.text3,fontFamily:"monospace"}}>{entry.user} · {entry.time}</div>
                    </div>
                    <div style={{width:7,height:7,borderRadius:"50%",background:entry.color,flexShrink:0}}/>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Knowledge page — shared RAG knowledge base manager ──────────────────
  // Web sources card on the KB detail view: attach URLs that re-sync every
  // 12h through the SSRF-hardened fetch pipeline. Admin-gated server-side.
  function KbWebSources({ kb, onChanged }) {
    const [sources, setSources] = useState(null);
    const [srcUrl, setSrcUrl] = useState("");
    const [busyAdd, setBusyAdd] = useState(false);
    const [syncing, setSyncing] = useState(null);
    const loadSources = async () => {
      try {
        const r = await fetch(`/api/kb/${kb.id}/sources`, { headers: { "Authorization": `Bearer ${authToken}` } });
        if (r.ok) setSources((await r.json()).sources || []);
      } catch {}
    };
    useEffect(() => { loadSources(); }, [kb.id]);
    const addSource = async () => {
      const u = srcUrl.trim();
      if (!u) return;
      setBusyAdd(true);
      try {
        const r = await fetch(`/api/kb/${kb.id}/sources`, { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` }, body: JSON.stringify({ url: u }) });
        const d = await r.json().catch(()=>({}));
        if (!r.ok) showToast(d.error || "Couldn't add source");
        else { setSrcUrl(""); showToast("Source synced ✓"); await loadSources(); onChanged && onChanged(); }
      } catch (e) { showToast("Couldn't add source: " + e.message); }
      setBusyAdd(false);
    };
    const syncSource = async (id) => {
      setSyncing(id);
      try {
        const r = await fetch(`/api/kb/${kb.id}/sources/${id}/sync`, { method: "POST", headers: { "Authorization": `Bearer ${authToken}` } });
        const d = await r.json().catch(()=>({}));
        showToast(r.ok ? (d.status === "unchanged" ? "No changes since last sync" : "Re-synced ✓") : (d.error || "Sync failed"));
        await loadSources(); onChanged && onChanged();
      } catch {}
      setSyncing(null);
    };
    const removeSource = async (id) => {
      if (!window.confirm("Remove this web source and its indexed content?")) return;
      try {
        const r = await fetch(`/api/kb/${kb.id}/sources/${id}`, { method: "DELETE", headers: { "Authorization": `Bearer ${authToken}` } });
        if (r.ok) { await loadSources(); onChanged && onChanged(); showToast("Source removed"); }
      } catch {}
    };
    if (!isAdmin && (!sources || sources.length === 0)) return null;
    return (
      <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:18,marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={S.accent} strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
          <span style={{fontSize:13,fontWeight:700,color:S.text}}>Web sources</span>
          <span style={{fontSize:9,padding:"2px 7px",borderRadius:10,background:S.accentDim,color:S.accent,fontFamily:"monospace",fontWeight:700}}>auto-syncs every 12h</span>
        </div>
        <div style={{fontSize:11,color:S.text3,lineHeight:1.55,marginBottom:12}}>Attach public pages (docs, wikis, policies). ChatFort keeps them indexed and re-syncs on a schedule — answers stay grounded in the live page, with citations.</div>
        {sources === null ? <div style={{fontSize:11,color:S.text3}}>Loading…</div> : (
          <>
            {sources.length > 0 && (
              <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:12}}>
                {sources.map(s => (
                  <div key={s.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`}}>
                    <span style={{fontSize:11,fontWeight:600,color:S.text,flexShrink:0,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name || (()=>{try{return new URL(s.url).hostname;}catch{return s.url;}})()}</span>
                    <a href={s.url} target="_blank" rel="noreferrer" style={{fontSize:10,color:S.text3,fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1,textDecoration:"none"}}>{s.url}</a>
                    <span title={s.lastError || ""} style={{fontSize:9,fontFamily:"monospace",flexShrink:0,padding:"2px 7px",borderRadius:8,background:s.lastStatus==="error"?(S.danger+"15"):(S.success||"#10b981")+"15",color:s.lastStatus==="error"?S.danger:(S.success||"#10b981"),border:`1px solid ${s.lastStatus==="error"?S.danger:(S.success||"#10b981")}30`}}>
                      {s.lastStatus === "error" ? "error" : `${s.chunkCount || 0} chunks`}
                    </span>
                    {isAdmin && <button onClick={()=>syncSource(s.id)} disabled={syncing===s.id} style={{padding:"3px 10px",borderRadius:6,background:S.accentDim,border:`1px solid ${S.accent}40`,color:S.accent,fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:"inherit",flexShrink:0,opacity:syncing===s.id?0.6:1}}>{syncing===s.id?"Syncing…":"Sync now"}</button>}
                    {isAdmin && <button onClick={()=>removeSource(s.id)} style={{padding:"3px 10px",borderRadius:6,background:"transparent",border:`1px solid ${S.danger}30`,color:S.danger,fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Remove</button>}
                  </div>
                ))}
              </div>
            )}
            {isAdmin && (
              <div style={{display:"flex",gap:6}}>
                <input value={srcUrl} placeholder="https://docs.example.com/handbook" onChange={e=>setSrcUrl(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")addSource();}}
                  style={{flex:1,background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"8px 12px",color:S.text,fontSize:12,outline:"none",fontFamily:"inherit"}}/>
                <button onClick={addSource} disabled={busyAdd||!srcUrl.trim()} style={{padding:"8px 14px",borderRadius:8,background:S.accentDim,border:`1px solid ${S.accent}40`,color:S.accent,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",opacity:busyAdd||!srcUrl.trim()?0.6:1}}>{busyAdd?"Fetching…":"Add source"}</button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  function KnowledgePage() {
    const [newKb, setNewKb] = useState({ name: "", description: "" });
    const [docs, setDocs] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [kbUploadSummary, setKbUploadSummary] = useState(null);
    const [kbOcrMode, setKbOcrMode] = useState("auto");
    const kbFileInputRef = useRef(null);
    const kbUploadBusyRef = useRef(false);
    const KB_DIRECT_UPLOAD_LIMIT = 45 * 1024 * 1024;
    const KB_DIRECT_OCR_PDF_LIMIT = 34 * 1024 * 1024;
    const KB_TARGET_UPLOAD_LIMIT = 1024 * 1024 * 1024;
    const KB_MIN_PDF_TEXT_PER_PAGE = 24;
    const KB_UPLOAD_EXTENSIONS = [
      ".pdf",
      ".doc", ".docx", ".rtf", ".odt",
      ".xls", ".xlsx", ".xlsm", ".xlsb", ".ods",
      ".csv", ".tsv", ".json", ".jsonl", ".ndjson",
      ".txt", ".text", ".md", ".markdown", ".log",
      ".xml", ".yaml", ".yml", ".toml", ".ini", ".conf",
      ".sql", ".js", ".jsx", ".ts", ".tsx", ".mjs", ".cjs",
      ".py", ".sh", ".bash", ".zsh", ".ps1", ".rb", ".php",
      ".java", ".go", ".rs", ".c", ".cpp", ".h", ".hpp",
      ".cs", ".css", ".scss", ".html", ".htm", ".vue", ".svelte"
    ];
    const KB_UPLOAD_ACCEPT = KB_UPLOAD_EXTENSIONS.join(",");
    const KB_TEXT_EXTENSIONS = new Set([
      "txt", "text", "md", "markdown", "csv", "tsv", "json", "jsonl", "ndjson",
      "xml", "yaml", "yml", "toml", "ini", "conf", "log", "sql",
      "js", "jsx", "ts", "tsx", "mjs", "cjs", "py", "sh", "bash", "zsh",
      "ps1", "rb", "php", "java", "go", "rs", "c", "cpp", "h", "hpp", "cs",
      "css", "scss", "html", "htm", "vue", "svelte"
    ]);
    const KB_SPREADSHEET_EXTENSIONS = new Set(["xls", "xlsx", "xlsm", "xlsb", "ods"]);
    const formatBytes = (bytes) => {
      if (!Number.isFinite(bytes)) return "";
      if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(bytes % (1024 * 1024 * 1024) ? 1 : 0)}GB`;
      if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(bytes % (1024 * 1024) ? 1 : 0)}MB`;
      return `${Math.max(1, Math.round(bytes / 1024))}KB`;
    };
    const fileExt = (file) => (String(file?.name || "").split(".").pop() || "").toLowerCase();
    const xmlText = (xml) => String(xml || "")
      .replace(/<w:tab\/>/g, "\t")
      .replace(/<\/w:p>/g, "\n")
      .replace(/<\/text:p>/g, "\n")
      .replace(/<\/text:h>/g, "\n")
      .replace(/<[^>]+>/g, " ")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&")
      .replace(/&quot;/g, '"')
      .replace(/&#39;|&apos;/g, "'")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/[ \t]{2,}/g, " ")
      .trim();
    const arrayBufferToBase64 = (buf) => {
      const bytes = new Uint8Array(buf);
      let binary = "";
      for (let i = 0; i < bytes.length; i += 0x8000) {
        binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      }
      return btoa(binary);
    };
    const binaryReadableText = async (file) => {
      const raw = new TextDecoder("utf-8", { fatal: false }).decode(await file.arrayBuffer());
      return raw
        .replace(/\u0000/g, " ")
        .replace(/[^\x09\x0a\x0d\x20-\x7e]+/g, " ")
        .replace(/[ \t]{2,}/g, " ")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
    };
    const extractDocxText = async (file) => {
      const JSZipCtor = await loadJSZip();
      const zip = await JSZipCtor.loadAsync(await file.arrayBuffer());
      const paths = [
        "word/document.xml",
        ...Object.keys(zip.files).filter(p => /^word\/(header|footer|footnotes|endnotes)\d*\.xml$/i.test(p))
      ];
      const parts = [];
      for (const p of paths) {
        const entry = zip.file(p);
        if (entry) parts.push(xmlText(await entry.async("text")));
      }
      return parts.filter(Boolean).join("\n\n");
    };
    const extractOdtText = async (file) => {
      const JSZipCtor = await loadJSZip();
      const zip = await JSZipCtor.loadAsync(await file.arrayBuffer());
      const entry = zip.file("content.xml");
      return entry ? xmlText(await entry.async("text")) : "";
    };
    const extractSpreadsheetText = async (file) => {
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js");
      const XLSX = window.XLSX;
      if (!XLSX) throw new Error("Spreadsheet parser failed to load");
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: true });
      const parts = [];
      for (const sheetName of wb.SheetNames || []) {
        const ws = wb.Sheets[sheetName];
        if (!ws) continue;
        const csv = XLSX.utils.sheet_to_csv(ws, { FS: "\t", RS: "\n", blankrows: false });
        if (csv.trim()) parts.push(`# Sheet: ${sheetName}\n${csv.trim()}`);
      }
      return parts.join("\n\n");
    };
    const extractKbFileForUpload = async (file, ocrMode) => {
      const ext = fileExt(file);
      const body = { name: file.name, originalSize: file.size, ocrMode };
      let extractedPdfPages = 0;
      if (ext === "pdf" || file.type === "application/pdf") {
        const buf = await file.arrayBuffer();
        try {
          await loadScript("https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/legacy/build/pdf.min.js");
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js";
          const pdf = await window.pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
          extractedPdfPages = pdf.numPages || 0;
          let text = "";
          for (let p = 1; p <= pdf.numPages; p++) {
            const page = await pdf.getPage(p);
            const tc = await page.getTextContent();
            text += tc.items.map(it => it.str).join(" ") + "\n";
          }
          body.text = text;
          if (ocrMode === "auto" && extractedPdfPages > 0 && text.trim().length < extractedPdfPages * KB_MIN_PDF_TEXT_PER_PAGE) {
            if (buf.byteLength > KB_DIRECT_OCR_PDF_LIMIT) {
              throw new Error(`scan-heavy PDFs over ${formatBytes(KB_DIRECT_OCR_PDF_LIMIT)} need the background large-file OCR pipeline`);
            }
            body.ocrRequested = true;
            body.pdfBase64 = arrayBufferToBase64(buf);
          }
        } catch (err) {
          if (ocrMode === "auto") {
            if (buf.byteLength > KB_DIRECT_OCR_PDF_LIMIT) {
              throw new Error(`scan-heavy PDFs over ${formatBytes(KB_DIRECT_OCR_PDF_LIMIT)} need the background large-file OCR pipeline`);
            }
            body.text = "";
            body.ocrRequested = true;
            body.pdfBase64 = arrayBufferToBase64(buf);
          } else {
            throw err;
          }
        }
      } else if (ext === "docx") {
        body.text = await extractDocxText(file);
      } else if (ext === "odt") {
        body.text = await extractOdtText(file);
      } else if (KB_SPREADSHEET_EXTENSIONS.has(ext)) {
        body.text = await extractSpreadsheetText(file);
      } else if (ext === "rtf") {
        body.text = (await file.text())
          .replace(/\\par[d]?/g, "\n")
          .replace(/\\'[0-9a-fA-F]{2}/g, " ")
          .replace(/[{}]/g, " ")
          .replace(/\\[a-zA-Z]+\d* ?/g, " ")
          .replace(/[ \t]{2,}/g, " ")
          .trim();
      } else if (ext === "doc") {
        body.text = await binaryReadableText(file);
      } else if (KB_TEXT_EXTENSIONS.has(ext) || (file.type || "").startsWith("text/") || /json|xml|yaml|csv|javascript/i.test(file.type || "")) {
        body.text = await file.text();
      } else {
        throw new Error(`Unsupported file type .${ext || "unknown"}`);
      }
      return { body, extractedPdfPages };
    };

    const refreshList = async () => {
      const r = await fetch("/api/kb", { headers: { "Authorization": `Bearer ${authToken}` }});
      if (r.ok) setKnowledgeBases(await r.json());
    };
    const loadDocs = async (kbId) => {
      const r = await fetch(`/api/kb/${kbId}/docs`, { headers: { "Authorization": `Bearer ${authToken}` }});
      if (r.ok) setDocs(await r.json());
    };
    const selectedKb = adminKnowledgeKbId ? knowledgeBases.find(kb => kb.id === adminKnowledgeKbId) : null;
    useEffect(() => { if (selectedKb) loadDocs(selectedKb.id); }, [selectedKb?.id]);

    const createKb = async () => {
      if (!newKb.name.trim()) { showToast("Name required"); return; }
      const r = await fetch("/api/kb", { method: "POST", headers: {"Content-Type":"application/json","Authorization":`Bearer ${authToken}`}, body: JSON.stringify(newKb) });
      const d = await r.json().catch(()=>({}));
      if (!r.ok) { showToast(d.error||"Failed"); return; }
      setNewKb({ name:"", description:"" });
      await refreshList();
      setAdminKnowledgeKbId(d.id);
      showToast("Knowledge base created ✓");
    };
    const deleteKb = async (id) => {
      if (!window.confirm("Delete this knowledge base and all its documents?")) return;
      const r = await fetch(`/api/kb/${id}`, { method: "DELETE", headers: { "Authorization": `Bearer ${authToken}` }});
      if (!r.ok) {
        const d = await r.json().catch(()=>({}));
        showToast(d.error || "Not allowed");
        return;
      }
      await refreshList();
      if (selectedKb?.id === id) setAdminKnowledgeKbId(null);
      showToast("Deleted");
    };
    const uploadFiles = async (fileList) => {
      const files = Array.from(fileList || []).filter(Boolean);
      const kbId = selectedKb?.id;
      if (!kbId || !files.length) return;
      if (kbUploadBusyRef.current) {
        showToast("Knowledge upload already in progress");
        return;
      }
      const { filesToUpload, skippedForCap, docSlots, kbDocCap, planName } = planKbUploadBatch(files, knowledgeBases, orgInfo);
      if (docSlots <= 0) {
        const message = `Your ${planName} plan is limited to ${kbDocCap} knowledge documents. Delete a document or upgrade.`;
        setKbUploadSummary({ status: "blocked", added: 0, failed: 0, skipped: files.length, details: [message] });
        showToast(message);
        return;
      }
      kbUploadBusyRef.current = true;
      setUploading(true);
      try {
        if (filesToUpload.length > 1) showToast(`Uploading ${filesToUpload.length} documents...`);
        const results = { added: 0, failed: 0, skipped: skippedForCap };
        const failures = skippedForCap ? [`${skippedForCap} file${skippedForCap === 1 ? "" : "s"} skipped because this plan has ${docSlots} document slot${docSlots === 1 ? "" : "s"} remaining.`] : [];
        setKbUploadSummary({ status: "running", added: 0, failed: 0, skipped: skippedForCap, total: files.length, details: failures });
        for (const file of filesToUpload) {
          try {
            if (file.size > KB_TARGET_UPLOAD_LIMIT) {
              results.skipped += 1;
              failures.push(`${file.name}: max planned file size is 1GB`);
              setKbUploadSummary(prev => ({ ...(prev || {}), status: "running", added: results.added, failed: results.failed, skipped: results.skipped, details: failures.slice(0, 8) }));
              continue;
            }
            if (file.size > KB_DIRECT_UPLOAD_LIMIT) {
              results.skipped += 1;
              failures.push(`${file.name}: ${formatBytes(file.size)} needs background large-file processing; direct upload is ${formatBytes(KB_DIRECT_UPLOAD_LIMIT)} today.`);
              setKbUploadSummary(prev => ({ ...(prev || {}), status: "running", added: results.added, failed: results.failed, skipped: results.skipped, details: failures.slice(0, 8) }));
              continue;
            }
            const { body } = await extractKbFileForUpload(file, kbOcrMode);
            const r = await fetch(`/api/kb/${kbId}/docs`, { method: "POST", headers: {"Content-Type":"application/json","Authorization":`Bearer ${authToken}`}, body: JSON.stringify(body) });
            const d = await r.json().catch(()=>({}));
            if (!r.ok) {
              results.failed += 1;
              failures.push(`${file.name}: ${d.error||"Failed"}`);
              setKbUploadSummary(prev => ({ ...(prev || {}), status: "running", added: results.added, failed: results.failed, skipped: results.skipped, details: failures.slice(0, 8) }));
              continue;
            }
            results.added += 1;
            setDocs(prev => prev.some(doc => doc.docId === d.docId)
              ? prev
              : [...prev, { docId: d.docId, docName: d.docName || file.name, chunkCount: Number(d.chunkCount) || 0, canDelete: true }]);
            setKbUploadSummary(prev => ({ ...(prev || {}), status: "running", added: results.added, failed: results.failed, skipped: results.skipped }));
          } catch (e) {
            results.failed += 1;
            failures.push(`${file.name}: ${e.message}`);
            setKbUploadSummary(prev => ({ ...(prev || {}), status: "running", added: results.added, failed: results.failed, skipped: results.skipped, details: failures.slice(0, 8) }));
          }
        }
        await loadDocs(kbId);
        await refreshList();
        setKbUploadSummary({ status: "done", added: results.added, failed: results.failed, skipped: results.skipped, total: files.length, details: failures.slice(0, 8), hiddenDetailCount: Math.max(0, failures.length - 8) });
        const parts = [];
        if (results.added) parts.push(`${results.added} added`);
        if (results.skipped) parts.push(`${results.skipped} skipped`);
        if (results.failed) parts.push(`${results.failed} failed`);
        if (parts.length) {
          const detail = failures.length ? `: ${failures.slice(0, 2).join("; ")}${failures.length > 2 ? `; +${failures.length - 2} more` : ""}` : "";
          showToast(`Knowledge upload complete: ${parts.join(", ")}${detail}`);
        }
      } catch (e) {
        setKbUploadSummary({ status: "error", added: 0, failed: 1, skipped: 0, details: [e.message || "Knowledge upload refresh failed"] });
        showToast(`Knowledge upload refresh failed: ${e.message || "try again"}`);
      } finally {
        kbUploadBusyRef.current = false;
        setUploading(false);
      }
    };
    const handleKbFileInputChange = async (e) => {
      const input = e.currentTarget;
      const files = Array.from(input.files || []);
      input.value = "";
      await uploadFiles(files);
    };
    const deleteDoc = async (docId) => {
      if (!window.confirm("Remove this document from the knowledge base?")) return;
      const r = await fetch(`/api/kb/${selectedKb.id}/docs/${docId}`, { method: "DELETE", headers: { "Authorization": `Bearer ${authToken}` }});
      if (!r.ok) {
        const d = await r.json().catch(()=>({}));
        showToast(d.error || "Not allowed");
        return;
      }
      await loadDocs(selectedKb.id);
      await refreshList();
    };

    // Detail view
    if (selectedKb) {
      return (
        <div style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
          <div style={{maxWidth:820,margin:"0 auto"}}>
            <button onClick={()=>setAdminKnowledgeKbId(null)} style={{background:"none",border:"none",color:S.text3,fontSize:13,cursor:"pointer",marginBottom:14,padding:0,fontFamily:"inherit"}}>← Back to all knowledge bases</button>
            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:20}}>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <h2 style={{fontSize:22,fontWeight:800,color:S.text}}>{selectedKb.name}</h2>
                  <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:S.accent+"18",color:S.accent,fontFamily:"monospace",fontWeight:700}}>{selectedKb.docCount} docs · {selectedKb.chunkCount} chunks</span>
                </div>
                {selectedKb.description && <p style={{fontSize:13,color:S.text2}}>{selectedKb.description}</p>}
              </div>
            </div>

            <KbWebSources kb={selectedKb} onChanged={()=>{loadDocs(selectedKb.id);refreshList();}} />

            {/* Upload */}
            <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:20,marginBottom:16,textAlign:"center"}}
              onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor=S.accent;}}
              onDragLeave={e=>{e.currentTarget.style.borderColor=S.border2;}}
              onDrop={e=>{e.preventDefault();e.currentTarget.style.borderColor=S.border2;uploadFiles(Array.from(e.dataTransfer.files||[]));}}>
              <input ref={kbFileInputRef} type="file" multiple accept={KB_UPLOAD_ACCEPT} style={{display:"none"}} onChange={handleKbFileInputChange}/>
              <div style={{fontSize:32,marginBottom:8}}>📥</div>
              <div style={{fontSize:14,fontWeight:600,color:S.text,marginBottom:4}}>Drop files or click to upload</div>
              <div style={{fontSize:11,color:S.text3,marginBottom:14}}>Today: PDFs, Word docs, Excel tables, CSV/JSON/data files, text, markdown, and code up to {formatBytes(KB_DIRECT_UPLOAD_LIMIT)} and 10MB extracted text.</div>
              <div style={{display:"flex",justifyContent:"center",gap:8,flexWrap:"wrap",marginBottom:14}}>
                {[
                  ["auto", "OCR Auto"],
                  ["off", "OCR Off"],
                ].map(([mode,label])=>(
                  <button key={mode} type="button" onClick={()=>setKbOcrMode(mode)} style={{padding:"7px 11px",borderRadius:999,background:kbOcrMode===mode?S.accentDim:S.inputBg,border:`1px solid ${kbOcrMode===mode?S.accent:S.border2}`,color:kbOcrMode===mode?S.accent:S.text2,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{label}</button>
                ))}
              </div>
              <div style={{maxWidth:600,margin:"0 auto 14px",padding:12,borderRadius:10,background:S.inputBg,border:`1px solid ${S.border}`,fontSize:11,color:S.text2,lineHeight:1.55,textAlign:"left"}}>
                Large-file mode can be one pane of glass: users drop a file here, ChatFort processes it in the background, runs OCR automatically when text is not extractable, indexes the chunks, then discards the raw file after indexing when retention policy allows.
              </div>
              <button type="button" onClick={()=>kbFileInputRef.current?.click()} disabled={uploading} style={{padding:"9px 20px",borderRadius:10,background:uploading?S.border3:`linear-gradient(135deg,${S.accent},${S.accent2})`,border:"none",color:"#fff",fontSize:13,fontWeight:700,cursor:uploading?"default":"pointer",fontFamily:"inherit"}}>{uploading?"Embedding…":"Choose Files"}</button>
              {kbUploadSummary && (
                <div role="status" aria-live="polite" style={{maxWidth:600,margin:"12px auto 0",padding:"9px 11px",borderRadius:9,background:S.inputBg,border:`1px solid ${S.border2}`,fontSize:11,color:S.text2,lineHeight:1.55,textAlign:"left"}}>
                  <div style={{fontWeight:700,color:S.text,marginBottom:kbUploadSummary.details?.length?4:0}}>
                    Upload {kbUploadSummary.status === "running" ? "in progress" : "summary"}: {Number(kbUploadSummary.added)||0} added, {Number(kbUploadSummary.failed)||0} failed, {Number(kbUploadSummary.skipped)||0} skipped
                  </div>
                  {Array.isArray(kbUploadSummary.details) && kbUploadSummary.details.slice(0, 8).map((detail, i) => (
                    <div key={i} style={{fontFamily:"monospace",color:S.text3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{detail}</div>
                  ))}
                  {kbUploadSummary.hiddenDetailCount > 0 && <div style={{fontFamily:"monospace",color:S.text3}}>+{kbUploadSummary.hiddenDetailCount} more</div>}
                </div>
              )}
            </div>

            {/* Docs list */}
            <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
              <div style={{padding:"13px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>Documents · {docs.length}</div>
              {docs.length===0 ? (
                <div style={{padding:"24px 18px",textAlign:"center",color:S.text3,fontSize:12}}>No documents yet. Upload some above.</div>
              ) : docs.map(d => (
                <div key={d.docId} style={{padding:"12px 18px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:12}}>
                  <div style={{fontSize:20}}>{d.docName.toLowerCase().endsWith(".pdf")?"📄":d.docName.toLowerCase().endsWith(".md")?"📝":"📃"}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600,color:S.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.docName}</div>
                    <div style={{fontSize:11,color:S.text3}}>{d.chunkCount} chunks</div>
                  </div>
                  {d.canDelete && <button onClick={()=>deleteDoc(d.docId)} style={{padding:"5px 11px",borderRadius:7,background:S.danger+"0d",border:`1px solid ${S.danger}30`,color:S.danger,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Remove</button>}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // List view
    return (
      <div style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
        <div style={{maxWidth:820,margin:"0 auto"}}>
          <div style={{marginBottom:20}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
              <h2 style={{fontSize:20,fontWeight:800,color:S.text}}>Knowledge</h2>
            </div>
            <p style={{fontSize:13,color:S.text2}}>Create knowledge bases, upload docs, and let users query them with RAG.</p>
          </div>
          <div style={{marginBottom:16,padding:14,background:S.accent+"0a",border:`1px solid ${S.accent}30`,borderRadius:10,fontSize:12,color:S.text2,lineHeight:1.6}}>
            <strong style={{color:S.text}}>What this is:</strong> Knowledge is ChatFort's built-in RAG store, not MCP. In production it uses Postgres plus OpenAI embeddings for semantic retrieval. In local dev without <code style={{fontFamily:"monospace",fontSize:11}}>DATABASE_URL</code>, it stores files under the local server data folder and uses keyword retrieval so the workflow can still be tested.
          </div>

          {/* New KB form */}
          <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:16,marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:700,color:S.text2,marginBottom:10}}>Create Knowledge Base</div>
            <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 2fr",gap:10,marginBottom:10}}>
              <input value={newKb.name} onChange={e=>setNewKb(p=>({...p,name:e.target.value}))} onKeyDown={e=>{if(e.key==="Enter")createKb();}} placeholder="Name (e.g., Company Handbook)" style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"8px 12px",color:S.text,fontSize:12,outline:"none",fontFamily:"inherit"}}/>
              <input value={newKb.description} onChange={e=>setNewKb(p=>({...p,description:e.target.value}))} onKeyDown={e=>{if(e.key==="Enter")createKb();}} placeholder="Description (optional)" style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"8px 12px",color:S.text,fontSize:12,outline:"none",fontFamily:"inherit"}}/>
            </div>
            <button onClick={createKb} style={{padding:"8px 18px",borderRadius:9,background:S.accentDim,border:`1px solid ${S.accent}40`,color:S.accent,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ Create</button>
          </div>

          {/* KB list */}
          <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
            <div style={{padding:"13px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>All Knowledge Bases · {knowledgeBases.length}</div>
            {knowledgeBases.length===0 ? (
              <div style={{padding:"24px 18px",textAlign:"center",color:S.text3,fontSize:12}}>None yet. Create one above to start uploading docs.</div>
            ) : knowledgeBases.map(kb => (
              <div key={kb.id} style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:14,cursor:"pointer",transition:"background 0.12s"}}
                onClick={()=>setAdminKnowledgeKbId(kb.id)}
                onMouseEnter={e=>e.currentTarget.style.background=S.hoverBg} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                <div style={{fontSize:26}}>📚</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:S.text,marginBottom:2}}>{kb.name}</div>
                  <div style={{fontSize:11,color:S.text3}}>{kb.docCount} docs · {kb.chunkCount} chunks{kb.description?" · "+kb.description:""}</div>
                </div>
                {kb.canDelete && <button onClick={e=>{e.stopPropagation();deleteKb(kb.id);}} style={{padding:"5px 11px",borderRadius:7,background:S.danger+"0d",border:`1px solid ${S.danger}30`,color:S.danger,fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>Remove</button>}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              </div>
            ))}
          </div>

          <div style={{marginTop:20,padding:14,background:S.accent+"0a",border:`1px solid ${S.accent}30`,borderRadius:10,fontSize:12,color:S.text2,lineHeight:1.6}}>
            💡 <strong>How users attach a KB to chat:</strong> anyone can click the 🌐 tools button in the chat input and pick a knowledge base from there. Their question is then embedded and the top 5 relevant chunks are injected into the AI's context automatically, with citations [1][2] in the response.
          </div>
        </div>
      </div>
    );
  }

  // ── Models page — owner-only editor for global model visibility/defaults ─
  function ModelsPage() {
    const [selectedId, setSelectedId] = useState(null);
    const liveOwnerCatalogModels = Array.isArray(liveModelCatalog?.models) ? liveModelCatalog.models : [];
    const ownerCatalogHasZdrData = liveOwnerCatalogModels.some(m => m?.zdr);
    const ownerCatalogShouldFilterByZdr = liveModelCatalog?.zdrEnforced !== false && ownerCatalogHasZdrData;
    const ownerOpenRouterModels = ownerCatalogShouldFilterByZdr
      ? liveOwnerCatalogModels.filter(m => m?.zdr?.verified)
      : liveOwnerCatalogModels;
    const ownerOpenRouterById = new Map(ownerOpenRouterModels.map(m => [m.id, m]));
    const ownerOpenRouterAllowedIds = new Set(ownerOpenRouterById.keys());
    const ownerCatalogIsAccessFiltered = liveOwnerCatalogModels.length > 0 && liveModelCatalog?.accessFiltered !== false;
    // Flat list of every owner-manageable model from enabled providers,
    // including live OpenRouter rows so visibility toggles stay current with
    // the configured upstream account instead of only the embedded defaults.
    const allModels = [...Object.entries(PROVIDERS).flatMap(([pid, p]) => {
      if (!p.builtin && !integrations[pid]?.key) return [];
      const models = pid === "openrouter"
        ? [...ownerOpenRouterModels, ...(p.models || []).filter(m => !ownerCatalogIsAccessFiltered || ownerOpenRouterAllowedIds.has(m.id))]
        : (p.models || []).filter(m => {
            if (!ownerCatalogIsAccessFiltered || pid !== "anthropic" || integrations.anthropic?.key || !integrations.openrouter?.key) return true;
            const routedId = OPENROUTER_FALLBACK_MODEL_MAP[m.id];
            return routedId ? ownerOpenRouterAllowedIds.has(routedId) : true;
          });
      return models.map(m => ({ ...m, provider: pid, providerName: p.name, providerColor: p.color, isLiveCatalog: m.source === "openrouter" || !!m.zdr }));
    }), ...(orgCustomModels || []).map(m => {
      const pv = PROVIDERS[m.provider] || {};
      return { ...m, providerName: pv.name || m.provider, providerColor: pv.color || S.accent, isCustom: true };
    })].reduce((acc, m) => {
      if (!m?.id || acc.some(x => x.provider === m.provider && x.id === m.id)) return acc;
      acc.push(m);
      return acc;
    }, []);

    const current = selectedId ? allModels.find(m => m.id === selectedId) : null;
    const currentOverride = current ? (modelOverrides[current.id] || {}) : {};
    const [draft, setDraft] = useState(currentOverride);
    useEffect(() => { setDraft(current ? (modelOverrides[current.id] || {}) : {}); }, [selectedId]);

    const saveDraft = () => {
      if (!current) return;
      const next = { ...modelOverrides, [current.id]: draft };
      // Clean: if draft is empty object, remove the key entirely
      if (Object.keys(draft).length === 0) delete next[current.id];
      setModelOverrides(next);
      saveModelOverridesToServer(next);
      showToast("Model updated ✓");
    };
    const resetDraft = () => {
      const next = { ...modelOverrides };
      delete next[current.id];
      setModelOverrides(next);
      setDraft({});
      saveModelOverridesToServer(next);
      showToast("Reset to defaults");
    };

    // Detail view for one model
    if (current) {
      const caps = draft.capabilities || {};
      const setCap = (k, v) => setDraft(p => ({ ...p, capabilities: { ...(p.capabilities||{}), [k]: v } }));
      const ISt = () => ({ width:"100%", background: S.inputBg, border: `1px solid ${S.border2}`, borderRadius: 8, padding: "8px 12px", color: S.text, fontSize: 13, fontFamily: "inherit", outline: "none" });
      return (
        <div style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
          <div style={{maxWidth:820,margin:"0 auto"}}>
            <button onClick={()=>setSelectedId(null)} style={{background:"none",border:"none",color:S.text3,fontSize:13,cursor:"pointer",marginBottom:14,display:"flex",alignItems:"center",gap:6,padding:0,fontFamily:"inherit"}}>← Back to all models</button>
            <div style={{display:"flex",alignItems:"flex-start",gap:16,marginBottom:24}}>
              <div style={{width:72,height:72,borderRadius:16,background:current.providerColor+"22",border:`1px solid ${S.border2}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {(() => {
                  const rk = realProviderKey(current.provider, current.id);
                  const rc = (current.provider === "openrouter" && OR_REAL_PROVIDER[String(current.id).split("/")[0]])
                    ? OR_REAL_PROVIDER[String(current.id).split("/")[0]].color
                    : (current.providerColor || S.accent);
                  return <ProviderLogo providerKey={rk} color={rc} size={40} />;
                })()}
              </div>
              <div style={{flex:1}}>
                <h2 style={{fontSize:22,fontWeight:800,color:S.text,marginBottom:4}}>{draft.name || current.name}</h2>
                <div style={{fontSize:12,color:S.text3,fontFamily:"monospace",marginBottom:6}}>{current.provider}/{current.id}</div>
                <div style={{fontSize:12,color:S.text2}}>{draft.description || current.desc}</div>
              </div>
            </div>

            {/* Basic info */}
            <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:18,marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:S.text2,marginBottom:12}}>Display</div>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>DISPLAY NAME</div>
                <input style={ISt()} value={draft.name||""} placeholder={current.name} onChange={e=>setDraft(p=>({...p,name:e.target.value||undefined}))}/>
              </div>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>DESCRIPTION</div>
                <input style={ISt()} value={draft.description||""} placeholder={current.desc} onChange={e=>setDraft(p=>({...p,description:e.target.value||undefined}))}/>
              </div>
              <div>
                <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>TAGS <span style={{color:S.text3+"99"}}>(comma-separated)</span></div>
                <input style={ISt()} value={(draft.tags||[]).join(", ")} placeholder="fast, cheap, coding" onChange={e=>setDraft(p=>({...p,tags:e.target.value.split(",").map(s=>s.trim()).filter(Boolean)}))}/>
              </div>
            </div>

            {/* System prompt */}
            <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:18,marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:S.text2,marginBottom:4}}>System Prompt</div>
              <div style={{fontSize:11,color:S.text3,marginBottom:10}}>Prepended to the agent's prompt when this model is selected. Leave blank for no override.</div>
              <textarea value={draft.systemPrompt||""} onChange={e=>setDraft(p=>({...p,systemPrompt:e.target.value||undefined}))}
                placeholder="e.g. You are a senior engineer. Prefer concise, production-ready code."
                style={{...ISt(),minHeight:100,resize:"vertical",fontFamily:"monospace",fontSize:12,lineHeight:1.6}}/>
            </div>

            {/* Params */}
            <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:18,marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:S.text2,marginBottom:12}}>Advanced Params</div>
              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,color:S.text2,marginBottom:5,display:"flex",justifyContent:"space-between"}}>Temperature <em style={{fontStyle:"normal",color:S.accent,fontFamily:"monospace"}}>{draft.temperature ?? "default"}</em></div>
                <input type="range" min="0" max="2" step="0.1" value={draft.temperature ?? 0.7} onChange={e=>setDraft(p=>({...p,temperature:parseFloat(e.target.value)}))} style={{width:"100%",accentColor:S.accent}}/>
              </div>
              <div>
                <div style={{fontSize:11,color:S.text2,marginBottom:5,display:"flex",justifyContent:"space-between"}}>Max Tokens <em style={{fontStyle:"normal",color:S.accent,fontFamily:"monospace"}}>{draft.maxTokens ?? "default"}</em></div>
                <input type="range" min="256" max="128000" step="256" value={draft.maxTokens ?? 4096} onChange={e=>setDraft(p=>({...p,maxTokens:parseInt(e.target.value)}))} style={{width:"100%",accentColor:S.accent}}/>
              </div>
            </div>

            {/* Capabilities */}
            <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:18,marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:S.text2,marginBottom:12}}>Capabilities</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(2, 1fr)",gap:8}}>
                {[["vision","Vision"],["fileUpload","File Upload"],["webSearch","Web Search"],["codeInterpreter","Code Interpreter"],["functionCalling","Function Calling"],["citations","Citations"]].map(([k,l])=>(
                  <label key={k} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:8,border:`1px solid ${S.border2}`,cursor:"pointer",background:caps[k]?S.accentDim:"none"}}>
                    <input type="checkbox" checked={!!caps[k]} onChange={e=>setCap(k,e.target.checked)} style={{accentColor:S.accent,cursor:"pointer"}}/>
                    <span style={{fontSize:12,color:S.text2}}>{l}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Visibility */}
            <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:18,marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:S.text,marginBottom:3}}>Show in model picker</div>
                  <div style={{fontSize:11,color:S.text3}}>When off, this model is hidden from users and admins in model selectors.</div>
                </div>
                <div onClick={()=>setDraft(p=>({...p,enabled:p.enabled===false}))} style={{width:40,height:22,background:draft.enabled===false?S.surface3:S.success,borderRadius:12,position:"relative",cursor:"pointer",border:`1px solid ${S.border2}`,flexShrink:0,transition:"background 0.18s"}}>
                  <div style={{position:"absolute",top:2,left:draft.enabled===false?2:18,width:16,height:16,background:"#fff",borderRadius:"50%",transition:"left 0.18s"}}/>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{display:"flex",gap:10}}>
              <button onClick={saveDraft} style={{padding:"12px 24px",borderRadius:10,border:"none",background:`linear-gradient(135deg,${S.accent},${S.accent2})`,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Save & Update</button>
              <button onClick={resetDraft} style={{padding:"12px 16px",borderRadius:10,border:`1px solid ${S.border2}`,background:"transparent",color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Reset to defaults</button>
              <button onClick={()=>setSelectedId(null)} style={{padding:"12px 16px",borderRadius:10,border:`1px solid ${S.border2}`,background:"transparent",color:S.text3,fontSize:12,cursor:"pointer",fontFamily:"inherit",marginLeft:"auto"}}>Cancel</button>
            </div>
          </div>
        </div>
      );
    }

    // List view -- grouped by provider + search.
    return <ModelsListView allModels={allModels} setSelectedId={setSelectedId}/>;
  }

  // Extracted list view so the search-state hook order stays stable
  // (the parent ModelsPage swaps between list + detail views, which
  // would otherwise cause "conditional hook" warnings if useState
  // moved inside a branch).
  function ModelsListView({allModels, setSelectedId}) {
    const [search, setSearch] = useState("");
    const [collapsed, setCollapsed] = useState({});
    const q = search.trim().toLowerCase();
    const filtered = q
      ? allModels.filter(m => {
          const ov = modelOverrides[m.id] || {};
          const name = (ov.name || m.name || "").toLowerCase();
          const desc = (ov.description || m.desc || "").toLowerCase();
          const prov = (m.providerName || m.provider || "").toLowerCase();
          return name.includes(q) || desc.includes(q) || prov.includes(q) || m.id.toLowerCase().includes(q);
        })
      : allModels;
    // Group by provider. Insertion order = display order; OpenRouter
    // rows are sub-grouped by their UPSTREAM (anthropic/openai/google/
    // ...) via OR_REAL_PROVIDER so e.g. 50 OR-routed Anthropic rows
    // don't pile into one giant "OpenRouter" section.
    const groups = new Map();
    for (const m of filtered) {
      let groupKey = m.provider;
      let groupName = m.providerName || m.provider;
      let groupColor = m.providerColor;
      if (m.provider === "openrouter") {
        const prefix = String(m.id).split("/")[0];
        const upstream = OR_REAL_PROVIDER[prefix];
        if (upstream) {
          groupKey = "or:" + upstream.key;
          groupName = upstream.label + " (via OpenRouter)";
          groupColor = upstream.color;
        }
      }
      if (!groups.has(groupKey)) groups.set(groupKey, { name: groupName, color: groupColor, rows: [], pid: m.provider });
      groups.get(groupKey).rows.push(m);
    }
    const groupList = Array.from(groups.entries());
    return (
      <div style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
        <div style={{maxWidth:860,margin:"0 auto"}}>
          <div style={{marginBottom:20}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
              <h2 style={{fontSize:20,fontWeight:800,color:S.text}}>Models</h2>
              <span style={{fontSize:9,padding:"2px 8px",borderRadius:10,fontFamily:"monospace",fontWeight:700,background:"#d4a01718",color:"#d4a017",border:"1px solid #d4a01730"}}>OWNER</span>
            </div>
            <p style={{fontSize:13,color:S.text2,marginBottom:12}}>{allModels.length} owner-manageable models grouped by provider. Disabled models disappear from user and admin selectors.</p>
            <div className="cf-input-shell" style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:10}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search models, providers, descriptions…" style={{flex:1,background:"none",border:"none",outline:"none",color:S.text,fontSize:13,fontFamily:"inherit"}}/>
              {search && <button onClick={()=>setSearch("")} style={{background:"none",border:"none",color:S.text3,cursor:"pointer",fontSize:14,padding:0,lineHeight:1}}>×</button>}
              <span style={{fontSize:10,color:S.text3,fontFamily:"monospace"}}>{filtered.length}/{allModels.length}</span>
            </div>
          </div>
          {groupList.length === 0 && (
            <div className="cf-empty-state" style={{padding:"40px 30px",textAlign:"center",color:S.text3,fontSize:13,background:S.cardBg,borderRadius:14,border:`1px dashed ${S.border2}`}}>No models match "{search}".</div>
          )}
          {groupList.map(([gKey, g]) => {
            const isCollapsed = collapsed[gKey];
            return (
              <div key={gKey} style={{marginBottom:14,background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
                <div onClick={()=>setCollapsed(p=>({...p,[gKey]:!isCollapsed}))} style={{padding:"12px 18px",borderBottom:isCollapsed?"none":`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:10,cursor:"pointer",background:S.surface2,userSelect:"none"}}>
                  <span style={{display:"inline-block",width:10,height:10,borderRadius:"50%",background:g.color||S.accent,flexShrink:0}}/>
                  <span style={{fontSize:13,fontWeight:700,color:S.text}}>{g.name}</span>
                  <span style={{fontSize:10,fontFamily:"monospace",color:S.text3,padding:"2px 8px",borderRadius:10,background:S.inputBg,border:`1px solid ${S.border}`}}>{g.rows.length} model{g.rows.length===1?"":"s"}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2" style={{marginLeft:"auto",transition:"transform .15s ease",transform:isCollapsed?"rotate(-90deg)":"rotate(0deg)"}}><polyline points="6 9 12 15 18 9"/></svg>
                </div>
                {!isCollapsed && g.rows.map(m => {
                  const ov = modelOverrides[m.id] || {};
                  const hasOverrides = Object.keys(ov).length > 0;
                  const displayName = ov.name || m.name;
                  const displayDesc = ov.description || m.desc;
                  return (
                    <div key={m.provider+"/"+m.id} onClick={()=>setSelectedId(m.id)}
                      style={{padding:"12px 18px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:14,cursor:"pointer",transition:"background 0.12s"}}
                      onMouseEnter={e=>e.currentTarget.style.background=S.hoverBg} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                      <div style={{width:36,height:36,borderRadius:9,background:m.providerColor+"22",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        {(() => {
                          const rk = realProviderKey(m.provider, m.id);
                          const rc = (m.provider === "openrouter" && OR_REAL_PROVIDER[String(m.id).split("/")[0]])
                            ? OR_REAL_PROVIDER[String(m.id).split("/")[0]].color
                            : (m.providerColor || S.accent);
                          return <ProviderLogo providerKey={rk} color={rc} size={20} />;
                        })()}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:2}}>
                          <span style={{fontSize:13,fontWeight:600,color:S.text}}>{displayName}</span>
                          {hasOverrides && <span style={{fontSize:9,padding:"1px 7px",borderRadius:10,background:S.accent+"18",color:S.accent,fontFamily:"monospace",fontWeight:700}}>CUSTOMIZED</span>}
                          {ov.enabled===false && <span style={{fontSize:9,padding:"1px 7px",borderRadius:10,background:S.danger+"18",color:S.danger,fontFamily:"monospace",fontWeight:700}}>HIDDEN</span>}
                        </div>
                        <div style={{fontSize:11,color:S.text3,fontFamily:"monospace",marginBottom:2}}>{m.provider}/{m.id}</div>
                        <div style={{fontSize:12,color:S.text2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{displayDesc}</div>
                      </div>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function _UNUSED_originalModelsListView() {
    return (
      <div style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
        <div style={{maxWidth:860,margin:"0 auto"}}>
          <div style={{marginBottom:20}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
              <h2 style={{fontSize:20,fontWeight:800,color:S.text}}>Models</h2>
              <span style={{fontSize:9,padding:"2px 8px",borderRadius:10,fontFamily:"monospace",fontWeight:700,background:"#d4a01718",color:"#d4a017",border:"1px solid #d4a01730"}}>OWNER</span>
            </div>
            <p style={{fontSize:13,color:S.text2}}>0 owner-manageable models.</p>
          </div>
          <div>
            {[].map(m => {
              const ov = modelOverrides[m.id] || {};
              const hasOverrides = Object.keys(ov).length > 0;
              const displayName = ov.name || m.name;
              const displayDesc = ov.description || m.desc;
              return (
                <div key={m.provider+"/"+m.id} onClick={()=>setSelectedId(m.id)}
                  style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:14,cursor:"pointer",transition:"background 0.12s"}}
                  onMouseEnter={e=>e.currentTarget.style.background=S.hoverBg} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                  <div style={{width:40,height:40,borderRadius:10,background:m.providerColor+"22",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {(() => {
                      const rk = realProviderKey(m.provider, m.id);
                      const rc = (m.provider === "openrouter" && OR_REAL_PROVIDER[String(m.id).split("/")[0]])
                        ? OR_REAL_PROVIDER[String(m.id).split("/")[0]].color
                        : (m.providerColor || S.accent);
                      return <ProviderLogo providerKey={rk} color={rc} size={22} />;
                    })()}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:2}}>
                      <span style={{fontSize:13,fontWeight:600,color:S.text}}>{displayName}</span>
                      {hasOverrides && <span style={{fontSize:9,padding:"1px 7px",borderRadius:10,background:S.accent+"18",color:S.accent,fontFamily:"monospace",fontWeight:700}}>CUSTOMIZED</span>}
                      {ov.enabled===false && <span style={{fontSize:9,padding:"1px 7px",borderRadius:10,background:S.danger+"18",color:S.danger,fontFamily:"monospace",fontWeight:700}}>HIDDEN</span>}
                    </div>
                    <div style={{fontSize:11,color:S.text3,fontFamily:"monospace",marginBottom:2}}>{m.provider}/{m.id}</div>
                    <div style={{fontSize:12,color:S.text2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{displayDesc}</div>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Admin-only: compliance-routing mode. Off / Auto (detect PHI & force BAA) /
  // Always (every chat goes through BAA). Server gates on the `baa` plan
  // feature, so the Save will 402 for plans that don't include BAA.
  function ComplianceCard() {
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
      if (!serverMode || !authToken) return;
      fetch("/api/admin/compliance", { headers: { "Authorization": `Bearer ${authToken}` }})
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setState(d); })
        .catch(()=>{});
    }, []);
    const setMode = async (mode) => {
      setBusy(true);
      try {
        const r = await fetch("/api/admin/compliance", {
          method: "PUT",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
          body: JSON.stringify({ mode }),
        });
        const d = await r.json();
        if (!r.ok) { showToast(d.error || "Save failed"); setBusy(false); return; }
        setState(d);
        showToast("Compliance mode updated");
      } catch (e) { showToast("Save failed: " + e.message); }
      setBusy(false);
    };
    if (!state) return null;
    const modes = [
      { v: "off",    label: "Off",         desc: "No PHI scanning. Chats route normally." },
      { v: "auto",   label: "Auto-detect", desc: "Scan each message for PHI/PII. If detected, force a BAA-covered upstream. Non-PHI chats route normally." },
      { v: "always", label: "Always BAA",  desc: "Every chat must go through a BAA-covered upstream. Safest — blocks any chat if no BAA provider is configured." },
    ];
    return (
      <div style={{ background: S.cardBg, border: `1px solid ${S.border2}`, borderLeft: `3px solid ${S.warning || "#f59e0b"}`, borderRadius: 14, overflow: "hidden", marginBottom: 14 }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${S.border}`, fontSize: 12, fontWeight: 700, color: S.text2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>🛡 Compliance routing</span>
          <span style={{ fontSize: 10, fontFamily: "monospace", padding: "2px 8px", borderRadius: 10, background: state.baaUpstreamAvailable ? S.success+"18" : (S.warning||"#f59e0b")+"18", color: state.baaUpstreamAvailable ? S.success : (S.warning||"#f59e0b"), border: `1px solid ${state.baaUpstreamAvailable ? S.success : (S.warning||"#f59e0b")}35` }}>
            BAA upstream: {state.baaUpstreamAvailable ? "available" : "not configured"}
          </span>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 11, color: S.text3, lineHeight: 1.6, marginBottom: 12 }}>
            Keep regulated data (PHI/PII) from reaching non-BAA-covered providers. When triggered, the server requires the request to go through Azure OpenAI or AWS Bedrock under your BAA. If those aren't configured, the chat is blocked — failing closed by design.
            {!state.baaUpstreamAvailable && <span style={{ color: S.warning || "#f59e0b" }}> &nbsp;Until you wire a BAA upstream, "Auto" or "Always" will block every triggered request.</span>}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {modes.map(m => (
              <div key={m.v} onClick={() => !busy && state.mode !== m.v && setMode(m.v)}
                style={{ padding: "10px 12px", borderRadius: 10, border: `1px solid ${state.mode === m.v ? S.accent : S.border2}`, background: state.mode === m.v ? S.accentDim : "transparent", cursor: busy ? "default" : "pointer", display: "flex", alignItems: "flex-start", gap: 10, opacity: busy ? 0.6 : 1 }}>
                <div style={{ width: 14, height: 14, borderRadius: "50%", border: `2px solid ${state.mode === m.v ? S.accent : S.border3}`, background: state.mode === m.v ? S.accent : "transparent", flexShrink: 0, marginTop: 2 }}/>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: state.mode === m.v ? S.accent : S.text }}>{m.label}</div>
                  <div style={{ fontSize: 11, color: S.text3, lineHeight: 1.5, marginTop: 2 }}>{m.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Slice 8: Privacy & Redaction (admin only). Sits directly below the
  // Compliance routing card so an admin sees both privacy guardrails in
  // one place — compliance routing decides which UPSTREAM the message can
  // hit, redaction decides what TEXT the upstream sees regardless of which
  // upstream that ends up being.
  function RedactionCard() {
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
      if (!serverMode || !authToken) return;
      fetch("/api/admin/redaction", { headers: { "Authorization": `Bearer ${authToken}` } })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setState(d); })
        .catch(() => {});
    }, []);
    const save = async (patch) => {
      if (!state) return;
      setBusy(true);
      const next = { ...state, ...patch };
      try {
        const r = await fetch("/api/admin/redaction", {
          method: "PUT",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
          body: JSON.stringify({ enabled: next.enabled, classes: next.classes, persistMaskedInStorage: next.persistMaskedInStorage }),
        });
        const d = await r.json();
        if (!r.ok) { showToast(d.error || "Save failed"); setBusy(false); return; }
        setState(d);
        showToast("Redaction settings updated");
      } catch (e) { showToast("Save failed: " + e.message); }
      setBusy(false);
    };
    if (!state) return null;
    const toggleClass = (k) => {
      const has = state.classes.includes(k);
      const next = has ? state.classes.filter(c => c !== k) : [...state.classes, k];
      save({ classes: next });
    };
    const KIND_DESC = {
      SSN:   { label: "SSN",          desc: "U.S. Social Security numbers (XXX-XX-XXXX)" },
      EMAIL: { label: "Email",        desc: "Email addresses (you@example.com)" },
      PHONE: { label: "Phone",        desc: "North-American phone numbers (10-digit)" },
      CARD:  { label: "Credit card",  desc: "13–19 digit runs that pass Luhn" },
      DOB:   { label: "DOB",          desc: "Dates of birth in MM/DD/YYYY, ISO, or DOB:-prefix forms" },
      MRN:   { label: "MRN",          desc: "Medical record numbers prefixed by MRN:" },
      IP:    { label: "IP address",   desc: "IPv4 addresses (a.b.c.d)" },
      NAME:  { label: "Names (heuristic)", desc: "Title-Case bigrams. Off by default — high false-positive rate.", warn: true },
    };
    const available = Array.isArray(state.available) && state.available.length ? state.available : Object.keys(KIND_DESC);
    return (
      <div style={{ background: S.cardBg, border: `1px solid ${S.border2}`, borderLeft: `3px solid ${S.success}`, borderRadius: 14, overflow: "hidden", marginBottom: 14 }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${S.border}`, fontSize: 12, fontWeight: 700, color: S.text2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>🔒 Privacy &amp; Redaction</span>
          <span style={{ fontSize: 10, fontFamily: "monospace", padding: "2px 8px", borderRadius: 10, background: state.enabled ? S.success + "18" : S.text3 + "18", color: state.enabled ? S.success : S.text3, border: `1px solid ${state.enabled ? S.success : S.text3}35` }}>
            {state.enabled ? "active" : "off"}
          </span>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 11, color: S.text3, lineHeight: 1.6, marginBottom: 14 }}>
            When on, your messages are scanned client-and-server-side. Detected items are replaced with placeholders before reaching the model. The model's reply is unmasked back to your original values before display. Placeholders are never stored when this toggle is on.
          </div>
          {/* Master toggle */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: 10, border: `1px solid ${state.enabled ? S.accent : S.border2}`, background: state.enabled ? S.accentDim : "transparent", marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: state.enabled ? S.accent : S.text }}>Mask sensitive data before sending to AI</div>
              <div style={{ fontSize: 11, color: S.text3, marginTop: 2 }}>{state.enabled ? "On — listed items below will be tokenised." : "Off — messages go to the model verbatim."}</div>
            </div>
            <div onClick={() => !busy && save({ enabled: !state.enabled })} style={{ width: 40, height: 22, background: state.enabled ? S.success : S.surface3, borderRadius: 12, position: "relative", cursor: busy ? "default" : "pointer", border: `1px solid ${S.border2}`, flexShrink: 0, marginLeft: 14, transition: "background 0.18s", opacity: busy ? 0.6 : 1 }}>
              <div style={{ position: "absolute", top: 2, left: state.enabled ? 20 : 2, width: 16, height: 16, background: "#fff", borderRadius: "50%", transition: "left 0.18s" }} />
            </div>
          </div>
          {/* Per-class checkboxes */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 6, opacity: state.enabled ? 1 : 0.55, pointerEvents: state.enabled ? "auto" : "none" }}>
            {available.map(k => {
              const cur = state.classes.includes(k);
              const desc = KIND_DESC[k] || { label: k, desc: "" };
              return (
                <div key={k} onClick={() => !busy && toggleClass(k)} title={desc.desc}
                  style={{ padding: "8px 10px", borderRadius: 8, border: `1px solid ${cur ? S.accent : S.border2}`, background: cur ? S.accentDim : "transparent", cursor: busy ? "default" : "pointer", display: "flex", alignItems: "flex-start", gap: 8, minWidth: 0 }}>
                  <div style={{ width: 14, height: 14, borderRadius: 3, border: `2px solid ${cur ? S.accent : S.border3}`, background: cur ? S.accent : "transparent", flexShrink: 0, marginTop: 2, position: "relative" }}>
                    {cur && <span style={{ position: "absolute", left: 1, top: -2, color: "#fff", fontSize: 10, fontWeight: 800 }}>✓</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: cur ? S.accent : S.text, display: "flex", alignItems: "center", gap: 6 }}>
                      {desc.label}
                      {desc.warn && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 4, background: (S.warning || "#f59e0b") + "20", color: S.warning || "#f59e0b" }}>heuristic</span>}
                    </div>
                    <div style={{ fontSize: 10, color: S.text3, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{desc.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Storage sub-toggle */}
          <div style={{ marginTop: 14, padding: "10px 12px", borderRadius: 10, border: `1px solid ${S.border2}`, background: S.inputBg, display: "flex", alignItems: "center", justifyContent: "space-between", opacity: state.enabled ? 1 : 0.55, pointerEvents: state.enabled ? "auto" : "none" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: S.text }}>Store masked version in chat history</div>
              <div style={{ fontSize: 10, color: S.text3, marginTop: 2, lineHeight: 1.5 }}>Recommended. When on, your chat record persists ⟨SSN_1⟩ instead of the original — an exfil of the chats table can't leak PII.</div>
            </div>
            <div onClick={() => !busy && save({ persistMaskedInStorage: !state.persistMaskedInStorage })} style={{ width: 36, height: 20, background: state.persistMaskedInStorage ? S.success : S.surface3, borderRadius: 10, position: "relative", cursor: busy ? "default" : "pointer", border: `1px solid ${S.border2}`, flexShrink: 0, marginLeft: 14, transition: "background 0.18s" }}>
              <div style={{ position: "absolute", top: 1, left: state.persistMaskedInStorage ? 17 : 1, width: 16, height: 16, background: "#fff", borderRadius: "50%", transition: "left 0.18s" }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Self-service profile editor — every user can change their name, accent
  // color, and avatar. Avatar gets cropped to a square + downscaled to 256px
  // client-side so a 4MB phone photo doesn't bloat users.json.
  // Settings → Two-factor authentication. Enable: fetch a pending secret,
  // add it to any authenticator (otpauth link for mobile + manual key),
  // confirm with the first code, then show the 8 one-shot recovery codes
  // exactly once. Disable requires a valid code. authUser.mfaEnabled comes
  // from /api/auth/me//login.
  function MfaCard() {
    // Flow state lives at App level (mfaFlow): this card remounts on every
    // App render (in-App identity — slice-10.5 trap), so local state was
    // being wiped mid-enrollment by any toast ("Copy collapses the card",
    // and re-clicking Turn on rotated the pending secret).
    const stage = mfaFlow.stage, setup = mfaFlow.setup, err = mfaFlow.err, recovery = mfaFlow.recovery;
    // Bail-on-equal so a no-op write (e.g. the per-keystroke setErr(""))
    // can't trigger an App render — App renders REMOUNT this in-App card.
    const setStage = (v) => setMfaFlow(p => p.stage === v ? p : { ...p, stage: v });
    const setSetup = (v) => setMfaFlow(p => p.setup === v ? p : { ...p, setup: v });
    const setErr = (v) => setMfaFlow(p => p.err === v ? p : { ...p, err: v });
    const setRecovery = (v) => setMfaFlow(p => p.recovery === v ? p : { ...p, recovery: v });
    // The code being TYPED is deliberately local: lifting it meant every
    // keystroke re-rendered App -> remount -> new input node -> focus lost
    // after each digit. Local typing causes no App render, so focus
    // survives; a rare background refresh mid-typing costs at most a
    // retyped code — never the pending secret (that stays lifted).
    const [code, setCode] = useState("");
    const [busyMfa, setBusyMfa] = useState(false);
    // QR of the otpauth URI — desktop browsers don't handle otpauth://
    // links, so the phone scans this instead. Rendered fully client-side
    // (same CDN loadScript pattern as the PDF/Word export buttons); the
    // secret never leaves the page.
    const qrRef = useRef(null);
    useEffect(() => {
      if (stage !== "setup" || !setup || !setup.otpauth || !qrRef.current) return;
      let cancelled = false;
      (async () => {
        try {
          await loadScript("https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js");
          if (cancelled || !qrRef.current || !window.QRCode) return;
          qrRef.current.innerHTML = "";
          new window.QRCode(qrRef.current, { text: setup.otpauth, width: 164, height: 164, colorDark: "#000000", colorLight: "#ffffff", correctLevel: window.QRCode.CorrectLevel.M });
        } catch {}
      })();
      return () => { cancelled = true; };
    }, [stage, setup && setup.otpauth]);
    if (!serverMode || !authToken || !authUser) return null;
    const on = !!authUser.mfaEnabled;
    const post = async (path, body) => {
      const r = await fetch(path, { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` }, body: JSON.stringify(body || {}) });
      const d = await r.json().catch(()=>({}));
      if (!r.ok) throw new Error(d.error || "Request failed");
      return d;
    };
    const beginSetup = async () => {
      setBusyMfa(true); setErr("");
      try { const d = await post("/api/auth/mfa/setup"); setSetup(d); setStage("setup"); setCode(""); }
      catch (e) { setErr(e.message); }
      setBusyMfa(false);
    };
    const confirmEnable = async () => {
      setBusyMfa(true); setErr("");
      try {
        const d = await post("/api/auth/mfa/enable", { code: code.trim() });
        setRecovery(Array.isArray(d.recoveryCodes) ? d.recoveryCodes : []);
        setStage("recovery"); setCode("");
        setAuthUser(p => p ? { ...p, mfaEnabled: true } : p);
      } catch (e) { setErr(e.message); }
      setBusyMfa(false);
    };
    const confirmDisable = async () => {
      setBusyMfa(true); setErr("");
      try {
        await post("/api/auth/mfa/disable", { code: code.trim() });
        setStage(null); setCode(""); setSetup(null);
        setAuthUser(p => p ? { ...p, mfaEnabled: false } : p);
        showToast("Two-factor authentication turned off");
      } catch (e) { setErr(e.message); }
      setBusyMfa(false);
    };
    const inputStyle = {width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"9px 12px",color:S.text,fontSize:13,outline:"none",fontFamily:"monospace",boxSizing:"border-box"};
    const primary = {padding:"7px 14px",borderRadius:8,background:S.accentDim,border:`1px solid ${S.accent}40`,color:S.accent,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"};
    return (
      <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden",marginBottom:14}}>
        <div style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2,display:"flex",alignItems:"center",gap:8}}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          Two-Factor Authentication
          <span style={{marginLeft:"auto",fontSize:9,fontFamily:"monospace",padding:"2px 8px",borderRadius:10,background:on?(S.success||"#10b981")+"18":S.inputBg,color:on?(S.success||"#10b981"):S.text3,border:`1px solid ${on?(S.success||"#10b981")+"40":S.border2}`}}>{on?"ON":"OFF"}</span>
        </div>
        <div style={{padding:16}}>
          {err && <div style={{background:S.danger+"15",border:`1px solid ${S.danger}35`,borderRadius:8,padding:"7px 12px",marginBottom:10,fontSize:11,color:S.danger}}>{err}</div>}
          {stage === "setup" && setup ? (
            <div>
              <div style={{fontSize:11,color:S.text2,lineHeight:1.6,marginBottom:10}}>Add this key to your authenticator app (Google Authenticator, 1Password, Authy…), then enter the 6-digit code it shows.</div>
              <div style={{display:"flex",justifyContent:"center",marginBottom:10}}>
                <div ref={qrRef} style={{padding:10,background:"#ffffff",borderRadius:10,minWidth:184,minHeight:184,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <span style={{fontSize:10,color:"#94a3b8"}}>Loading QR…</span>
                </div>
              </div>
              <a href={setup.otpauth} style={{display:"inline-block",marginBottom:8,fontSize:11,color:S.accent,fontWeight:600}}>📱 On your phone? Open in authenticator</a>
              <div style={{display:"flex",gap:6,marginBottom:10,alignItems:"center"}}>
                <code style={{flex:1,fontSize:11,padding:"8px 10px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text,letterSpacing:"0.08em",overflowWrap:"anywhere"}}>{setup.secret}</code>
                <button onClick={()=>{try{navigator.clipboard.writeText(setup.secret);showToast("Key copied");}catch{}}} style={primary}>Copy</button>
              </div>
              <div style={{display:"flex",gap:6}}>
                <input inputMode="numeric" placeholder="123456" value={code} onChange={e=>{setCode(e.target.value);setErr("");}} onKeyDown={e=>{if(e.key==="Enter")confirmEnable();}} style={{...inputStyle,maxWidth:140,textAlign:"center",letterSpacing:"0.25em"}}/>
                <button onClick={confirmEnable} disabled={busyMfa||!/^\d{6}$/.test(code.trim())} style={{...primary,opacity:busyMfa||!/^\d{6}$/.test(code.trim())?0.6:1}}>{busyMfa?"Checking…":"Turn on"}</button>
                <button onClick={()=>{setStage(null);setSetup(null);setCode("");setErr("");}} style={{...primary,background:"transparent",border:`1px solid ${S.border2}`,color:S.text3}}>Cancel</button>
              </div>
            </div>
          ) : stage === "recovery" ? (
            <div>
              <div style={{fontSize:11,color:S.text2,lineHeight:1.6,marginBottom:10}}><b>Save these recovery codes now</b> — they're shown only once. Each works one time if you lose your authenticator.</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:5,marginBottom:10}}>
                {recovery.map(c=> <code key={c} style={{fontSize:11,padding:"6px 8px",borderRadius:6,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text,textAlign:"center"}}>{c}</code>)}
              </div>
              <div style={{display:"flex",gap:6}}>
                <button onClick={()=>{try{navigator.clipboard.writeText(recovery.join("\n"));showToast("Recovery codes copied");}catch{}}} style={primary}>Copy all</button>
                <button onClick={()=>{setStage(null);setRecovery([]);}} style={{...primary,background:"transparent",border:`1px solid ${S.border2}`,color:S.text3}}>Done</button>
              </div>
            </div>
          ) : stage === "disable" ? (
            <div>
              <div style={{fontSize:11,color:S.text2,lineHeight:1.6,marginBottom:10}}>Enter a current authenticator code (or a recovery code) to turn off two-factor authentication.</div>
              <div style={{display:"flex",gap:6}}>
                <input placeholder="Code" value={code} onChange={e=>{setCode(e.target.value);setErr("");}} onKeyDown={e=>{if(e.key==="Enter")confirmDisable();}} style={{...inputStyle,maxWidth:160,textAlign:"center"}}/>
                <button onClick={confirmDisable} disabled={busyMfa||!code.trim()} style={{...primary,borderColor:S.danger+"40",background:S.danger+"12",color:S.danger,opacity:busyMfa||!code.trim()?0.6:1}}>{busyMfa?"Checking…":"Turn off"}</button>
                <button onClick={()=>{setStage(null);setCode("");setErr("");}} style={{...primary,background:"transparent",border:`1px solid ${S.border2}`,color:S.text3}}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{flex:1,fontSize:11,color:S.text3,lineHeight:1.5}}>{on?"Your account requires an authenticator code at sign-in.":"Protect your account with an authenticator-app code at sign-in."}</div>
              {on
                ? <button onClick={()=>{setStage("disable");setCode("");setErr("");}} style={{...primary,borderColor:S.danger+"40",background:"transparent",color:S.danger}}>Turn off</button>
                : <button onClick={beginSetup} disabled={busyMfa} style={primary}>{busyMfa?"Preparing…":"Turn on"}</button>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Settings → API Access (admin). List/create/revoke org API keys for the
  // OpenAI-compatible /v1 endpoint. The plaintext key is shown exactly once
  // at creation. Non-Business orgs see the upgrade path instead.
  // Settings → Prompt Library (admin): the team's reusable prompts,
  // insertable from Cmd+K by every member.
  function PromptLibraryCard() {
    const [form, setForm] = useState({ title: "", text: "" });
    const [busyP, setBusyP] = useState(false);
    if (!serverMode || !authToken) return null;
    const addPrompt = async () => {
      if (!form.title.trim() || !form.text.trim()) return;
      setBusyP(true);
      try {
        const r = await fetch("/api/org/prompts", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` }, body: JSON.stringify(form) });
        const d = await r.json().catch(()=>({}));
        if (!r.ok) showToast(d.error || "Couldn't save prompt");
        else { setOrgPrompts(p => [...p, d.prompt]); setForm({ title: "", text: "" }); showToast("Prompt saved — find it in Cmd+K"); }
      } catch (e) { showToast("Couldn't save prompt: " + e.message); }
      setBusyP(false);
    };
    const removePrompt = async (id) => {
      try {
        const r = await fetch(`/api/org/prompts/${id}`, { method: "DELETE", headers: { "Authorization": `Bearer ${authToken}` } });
        if (r.ok) { setOrgPrompts(p => p.filter(x => x.id !== id)); showToast("Prompt removed"); }
      } catch {}
    };
    return (
      <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden",marginBottom:14}}>
        <div style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2,display:"flex",alignItems:"center",gap:8}}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
          Prompt Library
        </div>
        <div style={{padding:16}}>
          <div style={{fontSize:11,color:S.text3,lineHeight:1.6,marginBottom:12}}>Save the prompts that work — RFP responses, bug triage, weekly reports. Every member can insert them from <b>Cmd+K</b>.</div>
          {orgPrompts.length > 0 && (
            <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:12}}>
              {orgPrompts.map(p => (
                <div key={p.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`}}>
                  <span style={{fontSize:11,fontWeight:600,color:S.text,flexShrink:0}}>{p.title}</span>
                  <span style={{flex:1,fontSize:10,color:S.text3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.text}</span>
                  <button onClick={()=>removePrompt(p.id)} style={{padding:"3px 10px",borderRadius:6,background:"transparent",border:`1px solid ${S.danger}30`,color:S.danger,fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Remove</button>
                </div>
              ))}
            </div>
          )}
          <input value={form.title} placeholder="Prompt name (e.g. RFP response draft)" onChange={e=>setForm(p=>({...p,title:e.target.value}))}
            style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"8px 12px",color:S.text,fontSize:12,outline:"none",fontFamily:"inherit",boxSizing:"border-box",marginBottom:6}}/>
          <textarea value={form.text} placeholder="The prompt text…" rows={3} onChange={e=>setForm(p=>({...p,text:e.target.value}))}
            style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"8px 12px",color:S.text,fontSize:12,outline:"none",fontFamily:"inherit",resize:"vertical",boxSizing:"border-box",marginBottom:8}}/>
          <button onClick={addPrompt} disabled={busyP||!form.title.trim()||!form.text.trim()}
            style={{padding:"8px 14px",borderRadius:8,background:S.accentDim,border:`1px solid ${S.accent}40`,color:S.accent,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",opacity:busyP||!form.title.trim()||!form.text.trim()?0.6:1}}>
            {busyP?"Saving…":"Save prompt"}
          </button>
        </div>
      </div>
    );
  }

  function ApiKeysCard() {
    const [data, setData] = useState(null); // { keys, apiAccess, plan }
    const [newName, setNewName] = useState("");
    const [created, setCreated] = useState(null); // { key, record }
    const [busyKeys, setBusyKeys] = useState(false);
    const loadKeys = async () => {
      try {
        const r = await fetch("/api/org/api-keys", { headers: { "Authorization": `Bearer ${authToken}` } });
        if (r.ok) setData(await r.json());
      } catch {}
    };
    useEffect(() => { if (serverMode && authToken) loadKeys(); }, []);
    if (!serverMode || !authToken) return null;
    const createKey = async () => {
      setBusyKeys(true);
      try {
        const r = await fetch("/api/org/api-keys", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` }, body: JSON.stringify({ name: newName.trim() || "API key" }) });
        const d = await r.json().catch(()=>({}));
        if (!r.ok) { showToast(d.error || "Couldn't create key"); }
        else { setCreated(d); setNewName(""); loadKeys(); }
      } catch (e) { showToast("Couldn't create key: " + e.message); }
      setBusyKeys(false);
    };
    const revokeKey = async (id) => {
      if (!window.confirm("Revoke this key? Anything using it stops working immediately.")) return;
      try {
        const r = await fetch(`/api/org/api-keys/${id}`, { method: "DELETE", headers: { "Authorization": `Bearer ${authToken}` } });
        if (r.ok) { showToast("Key revoked"); if (created?.record?.id === id) setCreated(null); loadKeys(); }
      } catch {}
    };
    const curl = `curl ${window.location.origin}/v1/chat/completions \\\n  -H "Authorization: Bearer YOUR_KEY" -H "Content-Type: application/json" \\\n  -d '{"model":"claude-sonnet-4-6","messages":[{"role":"user","content":"Hello"}]}'`;
    return (
      <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden",marginBottom:14}}>
        <div style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2,display:"flex",alignItems:"center",gap:8}}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>API Access</div>
        <div style={{padding:16}}>
          {!data ? (
            <div style={{fontSize:11,color:S.text3}}>Loading…</div>
          ) : !data.apiAccess ? (
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{flex:1,fontSize:11,color:S.text3,lineHeight:1.6}}>Programmatic access to your workspace's models via an OpenAI-compatible endpoint is included from the <b>Business</b> plan up.</div>
              <button onClick={()=>setSection("billing")} style={{padding:"7px 14px",borderRadius:8,background:S.accentDim,border:`1px solid ${S.accent}40`,color:S.accent,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Upgrade</button>
            </div>
          ) : (
            <div>
              <div style={{fontSize:11,color:S.text3,lineHeight:1.6,marginBottom:12}}>Org-scoped keys for the OpenAI-compatible endpoint at <code style={{fontSize:10,background:S.inputBg,padding:"1px 6px",borderRadius:4,border:`1px solid ${S.border2}`}}>POST /v1/chat/completions</code>. Requests draw the same plan allowance and model tiers as in-app chat.</div>
              {created && (
                <div style={{background:(S.success||"#10b981")+"10",border:`1px solid ${S.success||"#10b981"}35`,borderRadius:10,padding:"10px 12px",marginBottom:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:S.success||"#10b981",marginBottom:6}}>Copy this key now — it won't be shown again.</div>
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    <code style={{flex:1,fontSize:10.5,padding:"7px 9px",borderRadius:7,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text,overflowWrap:"anywhere"}}>{created.key}</code>
                    <button onClick={()=>{try{navigator.clipboard.writeText(created.key);showToast("Key copied");}catch{}}} style={{padding:"6px 12px",borderRadius:7,background:S.accentDim,border:`1px solid ${S.accent}40`,color:S.accent,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Copy</button>
                    <button onClick={()=>setCreated(null)} style={{padding:"6px 10px",borderRadius:7,background:"transparent",border:`1px solid ${S.border2}`,color:S.text3,fontSize:11,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Done</button>
                  </div>
                </div>
              )}
              {(data.keys || []).length > 0 && (
                <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:12}}>
                  {data.keys.map(k => (
                    <div key={k.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`}}>
                      <span style={{fontSize:11,fontWeight:600,color:S.text,flexShrink:0}}>{k.name}</span>
                      <code style={{fontSize:10,color:S.text3}}>{k.prefix}</code>
                      <span style={{marginLeft:"auto",fontSize:9,color:S.text3,fontFamily:"monospace",flexShrink:0}}>{k.lastUsedAt ? "used " + new Date(k.lastUsedAt).toLocaleDateString() : "never used"}</span>
                      <button onClick={()=>revokeKey(k.id)} style={{padding:"3px 10px",borderRadius:6,background:"transparent",border:`1px solid ${S.danger}30`,color:S.danger,fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Revoke</button>
                    </div>
                  ))}
                </div>
              )}
              <div style={{display:"flex",gap:6,marginBottom:12}}>
                <input value={newName} placeholder="Key name (e.g. CI bot)" onChange={e=>setNewName(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")createKey();}}
                  style={{flex:1,background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"8px 12px",color:S.text,fontSize:12,outline:"none",fontFamily:"inherit"}}/>
                <button onClick={createKey} disabled={busyKeys} style={{padding:"8px 14px",borderRadius:8,background:S.accentDim,border:`1px solid ${S.accent}40`,color:S.accent,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",opacity:busyKeys?0.6:1}}>{busyKeys?"Creating…":"Create key"}</button>
              </div>
              <details>
                <summary style={{fontSize:10,color:S.text3,cursor:"pointer"}}>curl example</summary>
                <pre style={{fontSize:9.5,padding:"8px 10px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text2,overflowX:"auto",marginTop:6,whiteSpace:"pre-wrap"}}>{curl}</pre>
              </details>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Settings → Sessions: recent sign-ins + log-out-everywhere.
  // Settings → Notifications: web push for agent-run completion.
  function PushCard() {
    const [pushState, setPushState] = useState("idle"); // idle|on|busy|unsupported
    useEffect(() => {
      (async () => {
        try {
          if (!("serviceWorker" in navigator) || !("PushManager" in window)) { setPushState("unsupported"); return; }
          const reg = await navigator.serviceWorker.ready;
          const sub = await reg.pushManager.getSubscription();
          setPushState(sub ? "on" : "idle");
        } catch { setPushState("idle"); }
      })();
    }, []);
    if (!serverMode || !authToken || pushState === "unsupported") return null;
    const b64ToU8 = (s) => {
      const pad = "=".repeat((4 - (s.length % 4)) % 4);
      const raw = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
      return Uint8Array.from(raw, c => c.charCodeAt(0));
    };
    const enable = async () => {
      setPushState("busy");
      try {
        const kr = await fetch("/api/push/key").then(r => r.ok ? r.json() : null).catch(() => null);
        if (!kr || !kr.key) { showToast("Push isn't configured on the server yet"); setPushState("idle"); return; }
        const perm = await Notification.requestPermission();
        if (perm !== "granted") { showToast("Notifications were blocked in the browser"); setPushState("idle"); return; }
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToU8(kr.key) });
        const r = await fetch("/api/push/subscribe", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` }, body: JSON.stringify({ subscription: sub.toJSON() }) });
        if (!r.ok) { const d = await r.json().catch(()=>({})); showToast(d.error || "Couldn't enable push"); setPushState("idle"); return; }
        setPushState("on");
        showToast("Push notifications on ✓");
      } catch (e) { showToast("Couldn't enable push: " + e.message); setPushState("idle"); }
    };
    const disable = async () => {
      setPushState("busy");
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          try { await fetch("/api/push/unsubscribe", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` }, body: JSON.stringify({ endpoint: sub.endpoint }) }); } catch {}
          await sub.unsubscribe();
        }
        setPushState("idle");
        showToast("Push notifications off");
      } catch { setPushState("idle"); }
    };
    return (
      <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden",marginBottom:14}}>
        <div style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2,display:"flex",alignItems:"center",gap:8}}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          Notifications
        </div>
        <div style={{padding:16,display:"flex",alignItems:"center",gap:10}}>
          <div style={{flex:1,fontSize:11,color:S.text3,lineHeight:1.5}}>Get a device notification when an agent run finishes — even with the app closed. Works on desktop browsers and installed mobile PWAs (iOS 16.4+).</div>
          {pushState === "on"
            ? <button onClick={disable} style={{padding:"7px 14px",borderRadius:8,background:"transparent",border:`1px solid ${S.border2}`,color:S.text2,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Turn off</button>
            : <button onClick={enable} disabled={pushState === "busy"} style={{padding:"7px 14px",borderRadius:8,background:S.accentDim,border:`1px solid ${S.accent}40`,color:S.accent,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",flexShrink:0,opacity:pushState==="busy"?0.6:1}}>{pushState === "busy" ? "Working…" : "Turn on"}</button>}
        </div>
      </div>
    );
  }

  function SessionsCard() {
    const [info, setInfo] = useState(null);
    const [busyS, setBusyS] = useState(false);
    useEffect(() => {
      if (!serverMode || !authToken) return;
      fetch("/api/auth/sessions", { headers: { "Authorization": `Bearer ${authToken}` } })
        .then(r => r.ok ? r.json() : null).then(d => { if (d) setInfo(d); }).catch(()=>{});
    }, []);
    if (!serverMode || !authToken) return null;
    const logoutEverywhere = async () => {
      if (!window.confirm("Sign out of all other devices? You'll stay signed in here.")) return;
      setBusyS(true);
      try {
        const r = await fetch("/api/auth/logout-everywhere", { method: "POST", headers: { "Authorization": `Bearer ${authToken}` } });
        const d = await r.json().catch(()=>({}));
        if (r.ok && d.token) {
          localStorage.setItem("cf_auth_token", d.token);
          setAuthToken(d.token);
          showToast("All other sessions signed out");
        } else showToast(d.error || "Couldn't sign out other sessions");
      } catch { showToast("Couldn't sign out other sessions"); }
      setBusyS(false);
    };
    const uaShort = (ua) => {
      const s = String(ua || "");
      if (/iPhone|iPad/.test(s)) return "iOS";
      if (/Android/.test(s)) return "Android";
      if (/Macintosh/.test(s)) return "Mac";
      if (/Windows/.test(s)) return "Windows";
      if (/Linux/.test(s)) return "Linux";
      return "Unknown device";
    };
    return (
      <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden",marginBottom:14}}>
        <div style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2,display:"flex",alignItems:"center",gap:8}}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
          Sessions
        </div>
        <div style={{padding:16}}>
          {(info?.recentLogins || []).length > 0 && (
            <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:12}}>
              {info.recentLogins.map((l, i) => (
                <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`,fontSize:11}}>
                  <span style={{fontWeight:600,color:S.text,flexShrink:0}}>{uaShort(l.ua)}</span>
                  <span style={{fontFamily:"monospace",color:S.text3,fontSize:10}}>{l.ip}</span>
                  <span style={{marginLeft:"auto",color:S.text3,fontSize:10,flexShrink:0}}>{l.ts ? new Date(l.ts).toLocaleString() : ""}</span>
                  {i === 0 && <span style={{fontSize:9,fontFamily:"monospace",padding:"1px 6px",borderRadius:8,background:(S.success||"#10b981")+"18",color:S.success||"#10b981",flexShrink:0}}>latest</span>}
                </div>
              ))}
            </div>
          )}
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{flex:1,fontSize:11,color:S.text3,lineHeight:1.5}}>Lost a laptop or signed in on a shared machine? Revoke every other session — this one stays signed in.</div>
            <button onClick={logoutEverywhere} disabled={busyS}
              style={{padding:"7px 14px",borderRadius:8,background:"transparent",border:`1px solid ${S.danger}40`,color:S.danger,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",flexShrink:0,opacity:busyS?0.6:1}}>
              {busyS ? "Signing out…" : "Sign out everywhere"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  function ProfileCard() {
    const me = authUser || curUser;
    const [busy, setBusy] = useState(false);
    if (!me) return null;
    const updateProfile = async (patch) => {
      if (!serverMode || !authToken) return;
      setBusy(true);
      try {
        const r = await fetch("/api/auth/profile", {
          method: "PUT",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
          body: JSON.stringify(patch),
        });
        const d = await r.json();
        if (!r.ok) { showToast(d.error || "Save failed"); setBusy(false); return; }
        setAuthUser(d);
        showToast("Profile updated ✓");
      } catch (e) { showToast("Save failed: " + e.message); }
      setBusy(false);
    };
    const onPickAvatar = (e) => {
      const f = e.target.files?.[0]; if (!f) return;
      e.target.value = "";
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          // Crop center square, downscale to 256x256, JPEG @ 0.85 quality.
          const side = Math.min(img.width, img.height);
          const sx = (img.width - side) / 2, sy = (img.height - side) / 2;
          const target = 256;
          const c = document.createElement("canvas");
          c.width = target; c.height = target;
          const ctx = c.getContext("2d");
          ctx.drawImage(img, sx, sy, side, side, 0, 0, target, target);
          const data = c.toDataURL("image/jpeg", 0.85);
          updateProfile({ avatar: data });
        };
        img.onerror = () => showToast("Could not read image");
        img.src = ev.target.result;
      };
      reader.readAsDataURL(f);
    };
    return (
      <div style={{ background: S.cardBg, border: `1px solid ${S.border2}`, borderRadius: 14, overflow: "hidden", marginBottom: 14 }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${S.border}`, fontSize: 12, fontWeight: 700, color: S.text2 }}>👤 Your profile</div>
        <div style={{ padding: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 18 }}>
            <UserAvatar user={me} size={64} fontSize={22} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: S.text }}>{me.name}</div>
              <div style={{ fontSize: 11, color: S.text3, fontFamily: "monospace", marginTop: 2 }}>{me.email}</div>
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                <label style={{ padding: "5px 12px", borderRadius: 8, background: S.accentDim, border: `1px solid ${S.accent}40`, color: S.accent, fontSize: 11, fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
                  {me.avatar ? "Replace photo" : "Upload photo"}
                  <input type="file" accept="image/*" onChange={onPickAvatar} disabled={busy} style={{ display: "none" }} />
                </label>
                {me.avatar && (
                  <button onClick={() => updateProfile({ avatar: null })} disabled={busy}
                    style={{ padding: "5px 12px", borderRadius: 8, background: "transparent", border: `1px solid ${S.danger}40`, color: S.danger, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: S.text2, marginBottom: 5 }}>Display name</div>
            <input defaultValue={me.name}
              onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== me.name) updateProfile({ name: v }); }}
              style={{ width: "100%", background: S.inputBg, border: `1px solid ${S.border2}`, borderRadius: 8, padding: "8px 12px", color: S.text, fontSize: 13, outline: "none" }}/>
          </div>
          <div>
            <div style={{ fontSize: 11, color: S.text2, marginBottom: 6 }}>Accent color (used when you don't have a photo)</div>
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
              {COLORS.map(c => (
                <div key={c} onClick={() => updateProfile({ color: c })} title={c}
                  style={{ width: 26, height: 26, borderRadius: "50%", background: c, cursor: "pointer", border: me.color === c ? `2px solid ${S.text}` : "2px solid transparent", boxShadow: me.color === c ? `0 0 0 1px ${c}` : "none", transition: "all 0.15s", transform: me.color === c ? "scale(1.15)" : "scale(1)" }}/>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // "About you" persistent memory — free-text blob the user authors once
  // and that's prepended to every chat as system context. Distinct from
  // the entries-based Core Memory below (which the LLM populates via
  // [REMEMBER:...] tags). 8000-char hard cap matches the server.
  const ABOUT_YOU_MAX = 8000;
  function AboutYouCard() {
    const [value, setValue] = useState("");
    const [original, setOriginal] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
      if (!serverMode || !authToken) { setLoading(false); return; }
      let alive = true;
      fetch("/api/user/memory", { headers: { "Authorization": `Bearer ${authToken}` } })
        .then(r => r.ok ? r.json() : { memory: "" })
        .then(d => { if (!alive) return; setValue(d.memory || ""); setOriginal(d.memory || ""); setLoading(false); })
        .catch(() => { if (alive) setLoading(false); });
      return () => { alive = false; };
    }, []);
    const dirty = value !== original;
    const onSave = async () => {
      if (!dirty || saving) return;
      setSaving(true);
      try {
        const r = await fetch("/api/user/memory", {
          method: "PUT",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
          body: JSON.stringify({ memory: value.slice(0, ABOUT_YOU_MAX) }),
        });
        const d = await r.json();
        if (r.ok) {
          setOriginal(d.memory || "");
          setValue(d.memory || "");
          showToast("Memory saved \u2713");
        } else {
          showToast(d.error || "Save failed");
        }
      } catch (e) { showToast("Save failed: " + e.message); }
      setSaving(false);
    };
    return (
      <div style={{ background: S.cardBg, border: `1px solid ${S.border2}`, borderRadius: 14, overflow: "hidden", marginBottom: 14 }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${S.border}`, fontSize: 12, fontWeight: 700, color: S.text2 }}>🧠 About you</div>
        <div style={{ padding: 18 }}>
          <div style={{ fontSize: 11, color: S.text3, lineHeight: 1.55, marginBottom: 10 }}>
            What Claude should know about you across every chat. This is private and only used as context for your conversations.
          </div>
          <textarea
            value={value}
            disabled={loading}
            onChange={e => setValue(e.target.value)}
            maxLength={ABOUT_YOU_MAX}
            rows={6}
            placeholder={`e.g. I'm Josh, founder of ChatFort. I prefer concise answers and code examples in TypeScript. My team works on regulated industries (legal, healthcare).`}
            style={{
              width: "100%", background: S.inputBg, border: `1px solid ${S.border2}`,
              borderRadius: 8, padding: "10px 12px", color: S.text, fontSize: 13,
              fontFamily: "inherit", lineHeight: 1.6, resize: "vertical", minHeight: 120,
              outline: "none", boxSizing: "border-box",
              opacity: loading ? 0.5 : 1,
            }}
          />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
            <div style={{ fontSize: 10, color: value.length > ABOUT_YOU_MAX * 0.9 ? S.warning || "#f59e0b" : S.text3, fontFamily: "monospace" }}>
              {value.length} / {ABOUT_YOU_MAX}
            </div>
            <button
              onClick={onSave}
              disabled={!dirty || saving || loading}
              style={{
                padding: "7px 16px", borderRadius: 8,
                background: (!dirty || saving || loading) ? S.inputBg : S.accentDim,
                color: (!dirty || saving || loading) ? S.text3 : S.accent,
                border: `1px solid ${(!dirty || saving || loading) ? S.border2 : S.accent + "55"}`,
                fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                cursor: (!dirty || saving || loading) ? "default" : "pointer",
                opacity: (!dirty || saving || loading) ? 0.7 : 1,
              }}
            >
              {saving ? "Saving\u2026" : "Save"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  function SettingsPage(){if(!isAdmin)return(<div className="cf-page-fade-in" style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}><div style={{maxWidth:580,margin:"0 auto"}}><div style={{marginBottom:24}}><h2 className="cf-page-header" style={{fontSize:20,fontWeight:800,color:S.text,marginBottom:4}}>Settings</h2></div><ProfileCard/><MfaCard/><SessionsCard/><PushCard/><AboutYouCard/></div></div>);return(<div className="cf-page-fade-in" style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}><div style={{maxWidth:580,margin:"0 auto"}}><div style={{marginBottom:24}}><h2 className="cf-page-header" style={{fontSize:20,fontWeight:800,color:S.text,marginBottom:4}}>Settings</h2></div><div style={{position:"sticky",top:0,zIndex:5,display:"flex",gap:6,padding:"8px 0 12px",background:S.bg,flexWrap:"wrap"}}>{[["cf-set-account","Account"],["cf-set-security","Security"],["cf-set-workspace","Workspace"],["cf-set-developer","Developer"],["cf-set-danger","Danger"]].map(([id,label])=>(<button key={id} onClick={()=>{const el=document.getElementById(id);if(el)el.scrollIntoView({behavior:"smooth",block:"start"});}} style={{padding:"5px 12px",borderRadius:16,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}} onMouseEnter={e=>{e.currentTarget.style.borderColor=S.accent;e.currentTarget.style.color=S.accent;}} onMouseLeave={e=>{e.currentTarget.style.borderColor=S.border2;e.currentTarget.style.color=S.text2;}}>{label}</button>))}</div><span id="cf-set-account" style={{scrollMarginTop:60}}/><ProfileCard/><span id="cf-set-security" style={{scrollMarginTop:60}}/><MfaCard/><SessionsCard/><PushCard/><AboutYouCard/>{isAdmin && <ComplianceCard/>}{isAdmin && <RedactionCard/>}{isAdmin && <span id="cf-set-developer" style={{scrollMarginTop:60}}/>}{isAdmin && <ApiKeysCard/>}{isAdmin && <PromptLibraryCard/>}<div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderLeft:`3px solid ${S.success}`,borderRadius:14,overflow:"hidden",marginBottom:14}}><div style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.success}}>\uD83D\uDD12 Privacy Posture</div><div style={{padding:16,display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>{[["Data retention","Zero"],["Model training","Never"],["Hosting","Yours"],["Audit log","Local"]].map(([k,v])=><div key={k}><div style={{fontSize:10,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",marginBottom:3}}>{k}</div><div style={{fontSize:14,fontWeight:700,color:S.success,fontFamily:"monospace"}}>{v}</div></div>)}</div></div>{isAdmin && (<div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderLeft:`3px solid ${S.accent}`,borderRadius:14,overflow:"hidden",marginBottom:14}}><div style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.accent}}>📋 Compliance & Security Report</div><div style={{padding:16}}><div style={{fontSize:11,color:S.text3,lineHeight:1.6,marginBottom:12}}>Download an audit-ready snapshot of this organization's live security posture — data retention, BAA compliance routing, PII redaction, and access controls. Hand it to your security or compliance team.</div><div style={{display:"flex",gap:8,flexWrap:"wrap"}}><button onClick={()=>cfExportComplianceReport("pdf",{authToken,orgInfo,brandName,showToast})} className="cf-cta cf-btn-lift" style={{padding:"9px 16px",borderRadius:9,border:"none",background:`linear-gradient(135deg,${S.accent},${S.accent2||S.accent})`,color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>📄 PDF</button><button onClick={()=>cfExportComplianceReport("docx",{authToken,orgInfo,brandName,showToast})} className="cf-btn-lift" style={{padding:"9px 16px",borderRadius:9,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>📝 Word</button><button onClick={()=>cfExportComplianceReport("xlsx",{authToken,orgInfo,brandName,showToast})} className="cf-btn-lift" style={{padding:"9px 16px",borderRadius:9,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>📊 Excel</button><button onClick={()=>cfExportComplianceReport("md",{authToken,orgInfo,brandName,showToast})} className="cf-btn-lift" style={{padding:"9px 16px",borderRadius:9,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text3,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>⬇ Markdown</button></div></div></div>)}<span id="cf-set-workspace" style={{scrollMarginTop:60}}/><div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden",marginBottom:14}}><div style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>Color Mode</div><div style={{padding:16,display:"flex",gap:10}}>{[["light","\u2600\uFE0F","Light"],["dark","\uD83C\uDF19","Dark"]].map(([t,icon,label])=><div key={t} onClick={()=>setTheme(t)} style={{flex:1,padding:"10px 12px",borderRadius:10,border:`1px solid ${theme===t?S.accent:S.border2}`,background:theme===t?S.accentDim:S.inputBg,cursor:"pointer",display:"flex",alignItems:"center",gap:8,transition:"all 0.15s"}}><span style={{fontSize:16}}>{icon}</span><span style={{fontSize:13,fontWeight:theme===t?600:400,color:theme===t?S.accent:S.text2}}>{label}</span>{theme===t&&<div style={{marginLeft:"auto",width:6,height:6,borderRadius:"50%",background:S.accent}}/>}</div>)}</div></div><div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden",marginBottom:14}}><div style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>Model Defaults</div><div style={{padding:18}}><div style={{marginBottom:14}}><div style={{fontSize:11,color:S.text2,marginBottom:5,display:"flex",justifyContent:"space-between"}}>Temperature <em style={{fontStyle:"normal",color:S.accent,fontFamily:"monospace"}}>{cfg.temperature}</em></div><input type="range" min="0" max="2" step="0.1" value={cfg.temperature} onChange={e=>setCfg(p=>({...p,temperature:parseFloat(e.target.value)}))} style={{width:"100%",accentColor:S.accent}}/></div><div><div style={{fontSize:11,color:S.text2,marginBottom:5,display:"flex",justifyContent:"space-between"}}>Max Tokens <em style={{fontStyle:"normal",color:S.accent,fontFamily:"monospace"}}>{cfg.maxTokens}</em></div><input type="range" min="256" max="128000" step="256" value={cfg.maxTokens} onChange={e=>setCfg(p=>({...p,maxTokens:parseInt(e.target.value)}))} style={{width:"100%",accentColor:S.accent}}/></div></div></div><div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden",marginBottom:14}}><div style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>Interface</div><div style={{padding:16,display:"flex",flexDirection:"column",gap:14}}><div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}><span style={{fontSize:13,color:S.text2}}>Show timestamps</span><div onClick={()=>setCfg(p=>({...p,timestamps:!p.timestamps}))} style={{width:36,height:20,background:cfg.timestamps?S.accent:S.surface3,borderRadius:10,position:"relative",cursor:"pointer",border:`1px solid ${S.border2}`,transition:"background 0.18s"}}><div style={{position:"absolute",top:2,left:cfg.timestamps?16:2,width:14,height:14,background:"#fff",borderRadius:"50%",transition:"left 0.18s"}}/></div></div><div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}><div><div style={{fontSize:13,color:S.text2}}>Top-bar size</div><div style={{fontSize:10,color:S.text3,marginTop:2}}>Compact = original. Large = roomier model chip + taller bar.</div></div><div style={{display:"flex",gap:5}}>{[["compact","Compact"],["large","Large"]].map(([v,l])=>(<button key={v} onClick={()=>setCfg(p=>({...p,topbarSize:v}))} style={{padding:"5px 12px",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit",background:(cfg.topbarSize||"large")===v?S.accentDim:"transparent",border:`1px solid ${(cfg.topbarSize||"large")===v?S.accent:S.border2}`,color:(cfg.topbarSize||"large")===v?S.accent:S.text2}}>{l}</button>))}</div></div></div></div>{isAdmin && <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden",marginBottom:14}}><div style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>🎨 Branding</div><div style={{padding:16}}><div style={{display:"flex",alignItems:"center",gap:14,marginBottom:16}}><div style={{width:64,height:64,borderRadius:16,background:S.accentDim,border:`1px solid ${S.border2}`,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",flexShrink:0}}><AppLogo size={44}/></div><div style={{flex:1}}><div style={{fontSize:12,fontWeight:600,color:S.text,marginBottom:3}}>App Logo</div><div style={{fontSize:11,color:S.text2,marginBottom:8}}>JPEG or PNG · Replaces the default logo everywhere in the app</div><div style={{display:"flex",gap:6,flexWrap:"wrap"}}><label style={{padding:"5px 14px",borderRadius:8,background:S.accentDim,border:`1px solid ${S.border3}`,color:S.accent,fontSize:11,fontWeight:600,cursor:"pointer"}}>{brandLogo?"Replace logo":"Upload logo"}<input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;const r=new FileReader();r.onload=ev=>{const raw=ev.target.result;const img=new Image();img.onload=()=>{const side=Math.min(img.width,img.height);const sx=(img.width-side)/2,sy=(img.height-side)/2;const target=128;const c=document.createElement("canvas");c.width=target;c.height=target;const ctx=c.getContext("2d");ctx.drawImage(img,sx,sy,side,side,0,0,target,target);const d=c.toDataURL("image/jpeg",0.85);setBrandLogo(d);try{localStorage.setItem("cf_brand_logo",d);}catch{}saveBrandingToServer({logo:d});showToast("Logo updated ✓");};img.onerror=()=>{setBrandLogo(raw);try{localStorage.setItem("cf_brand_logo",raw);}catch{}saveBrandingToServer({logo:raw});showToast("Logo updated ✓");};img.src=raw;};r.readAsDataURL(f);}}/></label>{brandLogo&&<button onClick={()=>{setBrandLogo(null);localStorage.removeItem("cf_brand_logo");saveBrandingToServer({logo:null});showToast("Logo removed");}} style={{padding:"5px 14px",borderRadius:8,background:"transparent",border:`1px solid ${S.danger}30`,color:S.danger,fontSize:11,fontWeight:600,cursor:"pointer"}}>Remove</button>}</div></div></div><div style={{marginBottom:16}}><div style={{fontSize:11,color:S.text2,marginBottom:5}}>App Name</div><input value={brandName} onChange={e=>{setBrandName(e.target.value);sv("cf_brand_name",e.target.value);saveBrandingToServer({name:e.target.value});}} placeholder="ChatFort" style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"8px 12px",color:S.text,fontSize:13,outline:"none",fontFamily:"inherit"}}/></div><div style={{marginBottom:16}}><div style={{fontSize:11,color:S.text2,marginBottom:5}}>Platform Font</div><select value={brandFont} onChange={e=>{const v=e.target.value;if(!BRAND_FONT_KEYS.has(v))return;setBrandFont(v);sv("cf_brand_font",v);saveBrandingToServer({font:v});showToast("Font updated ✓");}} style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"8px 12px",color:S.text,fontSize:13,outline:"none",fontFamily:"inherit",cursor:"pointer"}}>{BRAND_FONTS.map(f=><option key={f.key} value={f.key}>{f.label}</option>)}</select><div style={{marginTop:8,padding:"10px 12px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border}`,fontSize:12,lineHeight:1.45,color:S.text2,fontFamily:brandFontStack}}>Preview: {BN} uses {brandFontOption(brandFont).label} across chats, projects, settings, and admin screens.</div></div><div><div style={{fontSize:11,color:S.text2,marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}><span>Accent Color</span>{brandAccent&&<button onClick={()=>{setBrandAccent(null);sv("cf_brand_color",null);saveBrandingToServer({color:null});showToast("Color reset");}} style={{padding:"2px 10px",borderRadius:6,background:"transparent",border:`1px solid ${S.border2}`,color:S.text3,fontSize:10,cursor:"pointer"}}>Reset to default</button>}</div><div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:10}}>{["#3b82f6","#8b5cf6","#ec4899","#10b981","#f59e0b","#ef4444","#06b6d4","#6366f1","#0ea5e9","#14b8a6","#f97316","#84cc16"].map(c=><div key={c} title={c} onClick={()=>{setBrandAccent(c);sv("cf_brand_color",c);saveBrandingToServer({color:c});showToast("Color updated ✓");}} style={{width:26,height:26,borderRadius:7,background:c,cursor:"pointer",border:brandAccent===c?`2px solid ${S.text}`:"2px solid transparent",boxShadow:brandAccent===c?`0 0 0 1px ${c}`:"none",transition:"all 0.15s",transform:brandAccent===c?"scale(1.15)":"scale(1)"}}/>)}</div><div style={{display:"flex",gap:8,alignItems:"center"}}><input type="color" value={brandAccent||(theme==="dark"?"#5ba3c9":"#3b82c4")} onChange={e=>{setBrandAccent(e.target.value);sv("cf_brand_color",e.target.value);saveBrandingToServer({color:e.target.value});}} style={{width:36,height:36,borderRadius:8,border:`1px solid ${S.border2}`,background:S.inputBg,cursor:"pointer",padding:3,flexShrink:0}}/><input value={brandAccent||""} onChange={e=>{const v=e.target.value;if(/^#[0-9a-fA-F]{6}$/.test(v)){setBrandAccent(v);sv("cf_brand_color",v);saveBrandingToServer({color:v});}}} placeholder={theme==="dark"?"#5ba3c9":"#3b82c4"} maxLength={7} style={{width:90,background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"7px 10px",color:S.text,fontSize:12,fontFamily:"monospace",outline:"none"}}/><div style={{flex:1,height:32,borderRadius:8,background:brandAccent||S.accent,border:`1px solid ${S.border}`,transition:"background 0.2s"}}/></div></div></div></div>}<div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden",marginBottom:14}}><div style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>🔊 Voice (Talk to Me)</div><div style={{padding:16}}><div style={{fontSize:11,color:S.text3,lineHeight:1.5,marginBottom:12}}>Pick the voice ChatFort uses when it reads replies aloud in Talk&nbsp;to&nbsp;Me. Preview each one below.</div><div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:8}}>{[["marin","Marin","Warm, natural"],["cedar","Cedar","Deep, calm"],["nova","Nova","Bright, upbeat"],["shimmer","Shimmer","Soft, gentle"],["onyx","Onyx","Deep, authoritative"]].map(([vid,vname,vdesc])=>{const active=(cfg.ttsVoice||"marin")===vid;return(<div key={vid} onClick={()=>setCfg(p=>({...p,ttsVoice:vid}))} style={{padding:"10px 12px",borderRadius:10,border:`1px solid ${active?S.accent:S.border2}`,background:active?S.accentDim:S.inputBg,cursor:"pointer",display:"flex",flexDirection:"column",gap:4,transition:"all .15s"}}><div style={{display:"flex",alignItems:"center",gap:6}}><span style={{fontSize:13,fontWeight:active?700:600,color:active?S.accent:S.text}}>{vname}</span>{active&&<span style={{marginLeft:"auto",width:7,height:7,borderRadius:"50%",background:S.accent}}/>}</div><div style={{fontSize:10,color:S.text3,lineHeight:1.4}}>{vdesc}</div><button onClick={e=>{e.stopPropagation();cfPreviewVoice(vid,showToast);}} style={{marginTop:4,padding:"4px 10px",borderRadius:6,background:"transparent",border:`1px solid ${S.border2}`,color:S.text2,fontSize:10,cursor:"pointer",fontFamily:"inherit",alignSelf:"flex-start"}}>▶ Preview</button></div>);})}</div></div></div><div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden",marginBottom:14}}><div style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>🧠 Core Memory</div><div style={{padding:16}}><div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}><div style={{flex:1}}><div style={{fontSize:12,fontWeight:600,color:S.text,marginBottom:2}}>Remember across conversations</div><div style={{fontSize:11,color:S.text3,lineHeight:1.5}}>When on, the AI sees your saved facts in every chat and can save new ones. Only you see your own memories.</div></div><div onClick={()=>toggleMemory(!memory.enabled)} style={{width:40,height:22,background:memory.enabled?S.success:S.surface3,borderRadius:12,position:"relative",cursor:"pointer",border:`1px solid ${S.border2}`,flexShrink:0,marginLeft:14,transition:"background 0.18s"}}><div style={{position:"absolute",top:2,left:memory.enabled?20:2,width:16,height:16,background:"#fff",borderRadius:"50%",transition:"left 0.18s"}}/></div></div>{memory.enabled&&(<div><div style={{display:"flex",gap:6,marginBottom:10}}><input id="cf-memory-input" placeholder="Add a fact manually (e.g., 'Works as a product manager')" style={{flex:1,background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,padding:"8px 12px",color:S.text,fontSize:12,outline:"none"}} onKeyDown={e=>{if(e.key==="Enter"&&e.target.value.trim()){addMemory(e.target.value.trim());e.target.value="";}}}/><button onClick={()=>{const el=document.getElementById("cf-memory-input");if(el?.value.trim()){addMemory(el.value.trim());el.value="";}}} style={{padding:"8px 14px",borderRadius:8,background:S.accentDim,border:`1px solid ${S.accent}40`,color:S.accent,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Add</button></div>{memory.entries.length===0?<div style={{padding:"16px 8px",textAlign:"center",color:S.text3,fontSize:12}}>No memories yet. Add one above or let the AI save them as you chat.</div>:<div style={{maxHeight:280,overflowY:"auto",display:"flex",flexDirection:"column",gap:5}}>{memory.entries.slice().reverse().map(e=>(<div key={e.id} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"8px 10px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`}}><span style={{fontSize:11,lineHeight:1.5,color:S.text2,flex:1}}>{e.text}</span><button onClick={()=>removeMemory(e.id)} title="Forget this" style={{background:"none",border:"none",color:S.text3,cursor:"pointer",fontSize:14,padding:0,lineHeight:1,flexShrink:0}}>×</button></div>))}</div>}{memory.entries.length>0&&<button onClick={()=>{if(window.confirm("Forget all "+memory.entries.length+" memories?"))clearMemory();}} style={{marginTop:10,padding:"5px 12px",borderRadius:7,background:"transparent",border:`1px solid ${S.danger}30`,color:S.danger,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Forget all</button>}</div>)}</div></div><span id="cf-set-danger" style={{scrollMarginTop:60}}/><div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden",marginBottom:14}}><div style={{padding:"14px 18px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.danger}}>Danger Zone</div><div style={{padding:16,display:"flex",flexDirection:"column",gap:8}}><Btn variant="danger" onClick={()=>{if(window.confirm("Delete all chats?")){setChats([]);setChatId(null);showToast("Chats cleared");}}}>Clear all chats</Btn><Btn variant="danger" onClick={()=>{if(window.confirm("Reset everything?")){localStorage.clear();window.location.reload();}}}>Reset everything</Btn></div></div></div></div>);}

  // "What did the agents do for us" — Hermes rollup card on Analytics.
  // Renders only when the org actually ran agents in the window.
  // Admin quality pulse: 👍/👎 totals + recent downvotes with agent context.
  function FeedbackPulseCard() {
    const [fb, setFb] = useState(null);
    useEffect(() => {
      if (!serverMode || !authToken) return;
      fetch("/api/org/feedback", { headers: { "Authorization": `Bearer ${authToken}` } })
        .then(r => r.ok ? r.json() : null).then(d => { if (d) setFb(d); }).catch(()=>{});
    }, []);
    if (!fb || (fb.up + fb.down) === 0) return null;
    const total = fb.up + fb.down;
    const pct = Math.round((fb.up / total) * 100);
    return (
      <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:18,marginBottom:20}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={S.accent} strokeWidth="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
          <span style={{fontSize:13,fontWeight:700,color:S.text}}>Response feedback</span>
          <span style={{fontSize:9,fontFamily:"monospace",color:S.text3}}>last {fb.days} days</span>
          <span style={{marginLeft:"auto",fontSize:12,fontWeight:800,color:pct >= 80 ? (S.success||"#10b981") : pct >= 60 ? (S.warning||"#f59e0b") : S.danger}}>{pct}% positive</span>
          <span style={{fontSize:10,color:S.text3,fontFamily:"monospace"}}>({fb.up}👍 {fb.down}👎)</span>
        </div>
        {(fb.recentDown || []).length > 0 && (
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.05em"}}>Recent downvotes</div>
            {fb.recentDown.map((d, i) => (
              <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`,fontSize:11}}>
                <span style={{color:S.danger,flexShrink:0}}>▾</span>
                <span style={{flex:1,color:S.text2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.preview || "(no preview)"}</span>
                {d.agentId && <span style={{fontSize:9,fontFamily:"monospace",color:S.text3,flexShrink:0}}>{d.agentId}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function AgentOutcomesCard() {
    const [data, setData] = useState(null);
    useEffect(() => {
      if (!serverMode || !authToken) return;
      fetch("/api/org/agent-outcomes", { headers: { "Authorization": `Bearer ${authToken}` } })
        .then(r => r.ok ? r.json() : null).then(d => { if (d) setData(d); }).catch(()=>{});
    }, []);
    if (!data || !data.totalRuns) return null;
    const stopped = data.totalRuns - (data.done || 0);
    const dur = data.avgSecs == null ? "—" : data.avgSecs >= 90 ? `${(data.avgSecs/60).toFixed(1)}m` : `${Math.round(data.avgSecs)}s`;
    return (
      <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:18,marginBottom:20}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={S.accent} strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
          <span style={{fontSize:13,fontWeight:700,color:S.text}}>Agent outcomes</span>
          <span style={{fontSize:9,fontFamily:"monospace",color:S.text3}}>last {data.days} days</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:(data.recent||[]).length?14:0}}>
          {[
            ["Runs", String(data.totalRuns), "started", S.text],
            ["Completed", String(data.done || 0), "reached done", S.success || "#10b981"],
            ["Stopped", String(stopped), "failed / capped / other", stopped > 0 ? (S.warning || "#f59e0b") : S.text3],
            ["Agent spend", "$" + ((data.costCents || 0) / 100).toFixed(2), `avg ${dur}/run`, S.accent],
          ].map(([label, val, sub, color]) => (
            <div key={label} style={{padding:"10px 12px",borderRadius:10,background:S.inputBg,border:`1px solid ${S.border2}`}}>
              <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:3}}>{label}</div>
              <div style={{fontSize:18,fontWeight:800,color}}>{val}</div>
              <div style={{fontSize:9,color:S.text3,marginTop:2}}>{sub}</div>
            </div>
          ))}
        </div>
        {(data.recent || []).length > 0 && (
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            {data.recent.map(r => (
              <div key={r.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",borderRadius:8,background:S.inputBg,border:`1px solid ${S.border2}`,fontSize:11}}>
                <span style={{color:S.success||"#10b981",flexShrink:0}}>✓</span>
                <span style={{flex:1,color:S.text2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.title || r.goal || r.id}</span>
                <span style={{fontSize:9,fontFamily:"monospace",color:S.text3,flexShrink:0}}>{r.model_id}</span>
                <span style={{fontSize:9,fontFamily:"monospace",color:S.accent,flexShrink:0}}>${((Number(r.cost_cents_used)||0)/100).toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function AnalyticsPage(){
    const [pin,setPin]=useState("");
    const [pinErr,setPinErr]=useState(false);
    // Drill-in + filter state lives in App now (see App-scope
    // 'analyticsTab' etc.) so it survives the 60s sync remount.
    // Local aliases keep the AnalyticsPage body identifiers unchanged.
    const tab = analyticsTab, setTab = setAnalyticsTab;
    const selE = analyticsSelE, setSelE = setAnalyticsSelE;
    // Coerce any value to a plain string for safe React rendering. Old
    // analytics entries sometimes have completion: {__text, __search} (raw
    // callAI sentinel that should have been unwrapped) — extract the text.
    const strify = (v) => {
      if (v == null) return "";
      if (typeof v === "string") return v;
      if (typeof v === "object") return String(v.__text || v.text || JSON.stringify(v));
      return String(v);
    };
    const filterUser = analyticsFilterUser, setFilterUser = setAnalyticsFilterUser;
    const filterModel = analyticsFilterModel, setFilterModel = setAnalyticsFilterModel;
    const filterWs = analyticsFilterWs, setFilterWs = setAnalyticsFilterWs;
    const filterStatus = analyticsFilterStatus, setFilterStatus = setAnalyticsFilterStatus;
    const dateRange = analyticsDateRange, setDateRange = setAnalyticsDateRange;
    const searchQ = analyticsSearchQ, setSearchQ = setAnalyticsSearchQ;
    const sortBy = analyticsSortBy, setSortBy = setAnalyticsSortBy;
    const [retEdit,setRetEdit]=useState({...retentionPolicy});
    const [exportFmt,setExportFmt]=useState("json");
    // Cost tab — server-computed so numbers reconcile with the owner console's
    // per-model pricing. Lazy-loaded on tab click.
    const [usage, setUsage] = useState(null);
    const [usageLoading, setUsageLoading] = useState(false);
    // Slice C1c: current-period billing usage so the By Model panel can
    // render a "billable" badge when the org has crossed its monthly cap.
    // Pulled once on mount; cap-null orgs (Enterprise) and zero-overage
    // orgs both render unbadged.
    const [billingUsage, setBillingUsage] = useState(null);
    useEffect(() => {
      if (!serverMode || !authToken) return;
      fetch("/api/billing/usage/current", { headers: { "Authorization": `Bearer ${authToken}` }})
        .then(r => r.ok ? r.json() : null)
        .then(d => setBillingUsage(d))
        .catch(() => {});
    }, []);
    useEffect(() => {
      if (tab !== "cost" || !serverMode || !authToken) return;
      setUsageLoading(true);
      fetch("/api/org/usage", { headers: { "Authorization": `Bearer ${authToken}` }})
        .then(r => r.ok ? r.json() : null)
        .then(d => { setUsage(d); setUsageLoading(false); })
        .catch(() => setUsageLoading(false));
    }, [tab]);

    useEffect(() => { if (serverMode && !adminOk) setAdminOk(true); }, []);
    if(!serverMode && !adminOk) return (
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:32,background:S.bg}}>
        <div style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:16,padding:32,width:"100%",maxWidth:360,textAlign:"center"}}>
          <div style={{fontSize:32,marginBottom:16}}>🔒</div>
          <div style={{fontSize:16,fontWeight:700,color:S.text,marginBottom:6}}>Admin Access Required</div>
          <div style={{fontSize:12,color:S.text2,marginBottom:20,lineHeight:1.6}}>Enter your admin PIN to view analytics.</div>
          <div style={{display:"flex",gap:8,justifyContent:"center",marginBottom:14}}>{[0,1,2,3].map(i=><div key={i} style={{width:14,height:14,borderRadius:"50%",background:pin.length>i?S.accent:S.surface3,border:"1px solid "+S.border2}}/>)}</div>
          <input type="password" inputMode="numeric" pattern="[0-9]*" maxLength={4} value={pin} autoFocus onChange={e=>{setPin(e.target.value);setPinErr(false);if(e.target.value.length===4){if(e.target.value===adminPin){setAdminOk(true);}else{setPinErr(true);setTimeout(()=>setPin(""),600);}}}} style={{...ISt(),fontSize:20,textAlign:"center",letterSpacing:10,marginBottom:12}}/>
          {pinErr&&<div style={{color:S.danger,fontSize:11,marginBottom:10}}>Incorrect PIN</div>}
          <Btn onClick={()=>{if(pin===adminPin)setAdminOk(true);else{setPinErr(true);setPin("");}}} style={{width:"100%",justifyContent:"center"}}>Unlock Analytics</Btn>
          <div style={{marginTop:12,fontSize:10,color:S.text3}}>Default PIN: 1234</div>
        </div>
      </div>
    );

    // Date range filter
    const rangeMs = {"24h":86400000,"7d":7*86400000,"30d":30*86400000,"90d":90*86400000,"1y":365*86400000,"all":Infinity};
    const cutoff = Date.now() - (rangeMs[dateRange]||Infinity);

    // Apply all filters
    const filtered = alog.filter(e => {
      if(e.ts < cutoff) return false;
      if(filterUser!=="all" && e.userId!==filterUser) return false;
      if(filterModel!=="all" && e.model!==filterModel) return false;
      if(filterWs!=="all" && e.workspace!==filterWs) return false;
      if(filterStatus!=="all" && e.status!==filterStatus) return false;
      if(searchQ) {
        const q = searchQ.toLowerCase();
        return (strify(e.prompt)||"").toLowerCase().includes(q) || (strify(e.completion)||"").toLowerCase().includes(q) || (e.userName||"").toLowerCase().includes(q);
      }
      return true;
    });

    const sorted = [...filtered].sort((a,b) => sortBy==="newest" ? b.ts-a.ts : sortBy==="oldest" ? a.ts-b.ts : sortBy==="tokens" ? (b.totalTokens||0)-(a.totalTokens||0) : (b.latencyMs||0)-(a.latencyMs||0));

    // Stats
    const totalTok = filtered.reduce((a,e)=>a+(e.totalTokens||0),0);
    const promptTok = filtered.reduce((a,e)=>a+(e.promptTokens||0),0);
    const completionTok = filtered.reduce((a,e)=>a+(e.completionTokens||0),0);
    const errors = filtered.filter(e=>e.status==="error").length;
    // Cap per-entry latency at 5 min (300000ms) to exclude corrupted entries
    // where latency was accidentally written as raw Date.now() timestamps
    const CAPMS = 300000;
    const latValid = filtered.filter(e => e.latencyMs && e.latencyMs > 0 && e.latencyMs < CAPMS);
    const avgLat = latValid.length ? Math.round(latValid.reduce((a,e)=>a+e.latencyMs,0)/latValid.length) : 0;
    const uniqueUsers = [...new Set(filtered.map(e=>e.userId).filter(Boolean))];

    // Per-user breakdown
    const byUser = {};
    filtered.forEach(e => {
      const uid = e.userId||"unknown";
      if(!byUser[uid]) byUser[uid]={id:uid,name:e.userName||"Unknown",color:e.userColor||S.text3,requests:0,promptTok:0,completionTok:0,totalTok:0,errors:0,lastSeen:0,folders:new Set(),models:new Set()};
      byUser[uid].requests++;
      byUser[uid].promptTok+=(e.promptTokens||0);
      byUser[uid].completionTok+=(e.completionTokens||0);
      byUser[uid].totalTok+=(e.totalTokens||0);
      if(e.status==="error") byUser[uid].errors++;
      if(e.ts>byUser[uid].lastSeen) byUser[uid].lastSeen=e.ts;
      if(e.workspace) byUser[uid].folders.add(e.workspace);
      if(e.model) byUser[uid].models.add(e.model);
    });
    const userRows = Object.values(byUser).sort((a,b)=>b.requests-a.requests);

    // By model
    const byModel = {};
    filtered.forEach(e=>{if(!byModel[e.model])byModel[e.model]={requests:0,tokens:0,errors:0};byModel[e.model].requests++;byModel[e.model].tokens+=(e.totalTokens||0);if(e.status==="error")byModel[e.model].errors++;});

    // Bar chart data
    const n7=Date.now();
    const days30=Array.from({length:30},(_,i)=>{const d=new Date(n7-(29-i)*86400000);const ds=new Date(d.getFullYear(),d.getMonth(),d.getDate()).getTime();return{label:i%5===0?d.toLocaleDateString([],{month:"short",day:"numeric"}):"",count:alog.filter(e=>e.ts>=ds&&e.ts<ds+86400000).length};});
    const maxD=Math.max(1,...days30.map(d=>d.count));

    // Unique filter values
    const allUsers = [...new Set(alog.map(e=>({id:e.userId,name:e.userName})).filter(u=>u.id).map(u=>JSON.stringify(u)))].map(s=>JSON.parse(s));
    const allModels = [...new Set(alog.map(e=>e.model).filter(Boolean))];
    const allWs = [...new Set(alog.map(e=>e.workspace).filter(Boolean))];

    // Retention info
    const retentionDays = retentionPolicy.years*365 + retentionPolicy.months*30 + retentionPolicy.days;
    const oldestEntry = alog.length ? new Date(Math.min(...alog.map(e=>e.ts))) : null;

    // Export
    function exportData() {
      const data = exportFmt==="json" ? JSON.stringify(sorted, null, 2) : sorted.map(e=>[e.ts,e.userName||"",e.model||"",e.workspace||"",e.promptTokens||0,e.completionTokens||0,e.totalTokens||0,e.latencyMs||0,e.status||"",'"'+((e.prompt||"").replace(/"/g,'""').slice(0,500))+'"','"'+((e.completion||"").replace(/"/g,'""').slice(0,500))+'"'].join(",")).join("\n");
      const header = exportFmt==="csv" ? "timestamp,user,model,folder,promptTokens,completionTokens,totalTokens,latencyMs,status,prompt,completion\n" : "";
      const blob = new Blob([header+data], {type:exportFmt==="json"?"application/json":"text/csv"});
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href=url; a.download="chatfort_analytics_"+Date.now()+"."+exportFmt; a.click(); URL.revokeObjectURL(url);
      showToast("Analytics exported!");
    }

    const tabSt=(t)=>({padding:"5px 12px",borderRadius:6,fontSize:11,fontFamily:"monospace",border:"1px solid "+(tab===t?S.accent:S.border2),background:tab===t?S.accentDim:"none",color:tab===t?S.accent:S.text2,cursor:"pointer"});
    const selBtnSt={fontSize:11,padding:"4px 9px",borderRadius:7,border:"1px solid "+S.border2,background:S.inputBg,color:S.text2,cursor:"pointer",fontFamily:"monospace"};
    const truncate=(s,n=120)=>s&&s.length>n?s.slice(0,n)+"…":s||"—";

    return (
      <div style={{flex:1,overflowY:"auto",padding:28,background:S.bg}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>

          {/* Header */}
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:20}}>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                <h2 style={{fontSize:20,fontWeight:800,color:S.text}}>Analytics</h2>
                <span style={{fontSize:9,padding:"2px 8px",borderRadius:10,fontFamily:"monospace",fontWeight:700,background:S.danger+"18",color:S.danger,border:"1px solid "+S.danger+"30"}}>ADMIN</span>
              </div>
              <p style={{fontSize:12,color:S.text2}}>
                {alog.length.toLocaleString()} total entries · {uniqueUsers.length} users · Retention: {retentionPolicy.customLabel}
                {analyticsLastSyncAt > 0 && (() => {
                  const ago = Math.floor((Date.now() - analyticsLastSyncAt) / 1000);
                  const label = ago < 60 ? `${ago}s ago` : ago < 3600 ? `${Math.floor(ago/60)}m ago` : `${Math.floor(ago/3600)}h ago`;
                  return <span title={`Last sync: ${new Date(analyticsLastSyncAt).toLocaleString()}`} style={{marginLeft:8,padding:"1px 8px",borderRadius:10,background:S.success+"15",color:S.success,fontSize:10,fontFamily:"monospace",border:`1px solid ${S.success}35`,fontWeight:700}}>● synced {label}</span>;
                })()}
              </p>
            </div>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <Btn variant="secondary" onClick={()=>refetchServerAnalytics()} title="Force sync from server (also auto-syncs every 60s)">↻ Sync</Btn>
              <select style={{...selBtnSt}} value={exportFmt} onChange={e=>setExportFmt(e.target.value)}><option value="json">JSON</option><option value="csv">CSV</option></select>
              <Btn variant="secondary" onClick={exportData}>⬇ Export</Btn>
              <Btn variant="secondary" onClick={()=>{setAdminOk(false);setSection("chat");}}>Lock</Btn>
              <Btn variant="danger" onClick={()=>{if(window.confirm("Clear ALL analytics? This cannot be undone."))setAlog([]);}}>Clear All</Btn>
            </div>
          </div>

          <AgentOutcomesCard/>
          <FeedbackPulseCard/>

          {/* Stat cards — clickable to drill into relevant view */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:8,marginBottom:20}}>
            {[
              ["Requests",filtered.length.toLocaleString(),"in range",S.text,()=>{console.log("[analytics] tile click: Requests");setTab("prompts");}],
              ["Total Tokens",totalTok.toLocaleString(),"prompt+completion",S.accent,()=>{console.log("[analytics] tile click: Total Tokens");setSortBy("tokens");setTab("prompts");}],
              ["Prompt Tokens",promptTok.toLocaleString(),"in",S.text2,()=>{console.log("[analytics] tile click: Prompt Tokens");setSortBy("tokens");setTab("prompts");}],
              ["Completion Tokens",completionTok.toLocaleString(),"out",S.text2,()=>{console.log("[analytics] tile click: Completion Tokens");setSortBy("tokens");setTab("prompts");}],
              ["Avg Latency",avgLat?((avgLat/1000).toFixed(1)+"s"):"—","per request",S.text,()=>{console.log("[analytics] tile click: Avg Latency");setSortBy("latency");setTab("prompts");}],
              ["Error Rate",filtered.length?Math.round(errors/filtered.length*100)+"%":"0%",errors+" errors",errors>0?S.danger:S.success,()=>{console.log("[analytics] tile click: Error Rate");setFilterStatus("error");setTab("prompts");}],
            ].map(([label,val,sub,color,onClick])=>(
              <button key={label} type="button" onClick={onClick}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=S.accent;e.currentTarget.style.boxShadow=`0 4px 12px ${S.accentDim}`;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=S.border2;e.currentTarget.style.boxShadow="none";}}
                style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:12,padding:"12px 14px",cursor:"pointer",transition:"border-color 0.15s, box-shadow 0.15s",textAlign:"left",fontFamily:"inherit",display:"block",width:"100%"}}>
                <div style={{fontSize:9,fontFamily:"monospace",color:S.text3,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{label}</div>
                <div style={{fontSize:18,fontWeight:700,color,fontFamily:"monospace",marginBottom:1}}>{val}</div>
                <div style={{fontSize:9,color:S.text3}}>{sub}</div>
              </button>
            ))}
          </div>

          {/* Tabs */}
          <div style={{display:"flex",gap:6,marginBottom:18,flexWrap:"wrap"}}>
            {[["overview","Overview"],["cost","Cost"],["users","Per User"],["prompts","Prompt Log"],["models","By Model"],["retention","Retention Policy"]].map(([t,l])=>(
              <button key={t} onClick={()=>setTab(t)} style={tabSt(t)}>{l}</button>
            ))}
          </div>

          {/* ── OVERVIEW TAB ── */}
          {tab==="overview" && (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {/* Date range filter */}
              <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                {[["24h","24h"],["7d","7 days"],["30d","30 days"],["90d","90 days"],["1y","1 year"],["all","All time"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setDateRange(v)} style={{...selBtnSt,background:dateRange===v?S.accentDim:"none",border:"1px solid "+(dateRange===v?S.accent:S.border2),color:dateRange===v?S.accent:S.text2,fontWeight:dateRange===v?600:400}}>{l}</button>
                ))}
              </div>
              {/* 30-day chart */}
              <div style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:14,padding:"16px 18px"}}>
                <div style={{fontSize:12,fontWeight:600,color:S.text2,marginBottom:16}}>Requests — Last 30 Days</div>
                <div style={{display:"flex",alignItems:"flex-end",gap:3,height:80}}>
                  {days30.map((d,i)=>(
                    <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                      <div style={{width:"100%",background:d.count?S.accent:S.surface3,borderRadius:"2px 2px 0 0",height:Math.max(2,(d.count/maxD)*70)+"px",transition:"height 0.3s"}}/>
                      {d.label&&<div style={{fontSize:7,fontFamily:"monospace",color:S.text3,whiteSpace:"nowrap"}}>{d.label}</div>}
                    </div>
                  ))}
                </div>
              </div>
              {/* Top users mini */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                <div style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:14,overflow:"hidden"}}>
                  <div style={{padding:"12px 16px",borderBottom:"1px solid "+S.border,fontSize:12,fontWeight:700,color:S.text2}}>Top Users</div>
                  {userRows.slice(0,5).map(u=>(
                    <div key={u.id} onClick={()=>{setFilterUser(u.id);setTab("users");}} style={{padding:"9px 16px",borderBottom:"1px solid "+S.border,display:"flex",alignItems:"center",gap:9,cursor:"pointer"}} onMouseEnter={e=>e.currentTarget.style.background=S.accentDim} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                      <div style={{width:26,height:26,borderRadius:"50%",background:u.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"#fff",flexShrink:0}}>{u.name.slice(0,2).toUpperCase()}</div>
                      <span style={{flex:1,fontSize:12,color:S.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.name}</span>
                      <span style={{fontSize:11,fontFamily:"monospace",color:S.accent}}>{u.requests}</span>
                      <span style={{fontSize:9,color:S.text3,fontFamily:"monospace"}}>{u.totalTok.toLocaleString()} tok</span>
                    </div>
                  ))}
                  {userRows.length===0&&<div style={{padding:"20px",textAlign:"center",color:S.text3,fontSize:12}}>No data yet</div>}
                </div>
                <div style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:14,overflow:"hidden"}}>
                  <div style={{padding:"12px 16px",borderBottom:"1px solid "+S.border,fontSize:12,fontWeight:700,color:S.text2}}>Top Models</div>
                  {Object.entries(byModel).sort((a,b)=>b[1].requests-a[1].requests).slice(0,5).map(([model,v])=>(
                    <div key={model} onClick={()=>{setFilterModel(model);setTab("prompts");}}
                      onMouseEnter={e=>e.currentTarget.style.background=S.accentDim}
                      onMouseLeave={e=>e.currentTarget.style.background="none"}
                      style={{padding:"9px 16px",borderBottom:"1px solid "+S.border,cursor:"pointer",transition:"background 0.12s"}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                        <span style={{fontSize:11,fontFamily:"monospace",color:S.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:180}}>{model.split("/").pop()}</span>
                        <span style={{fontSize:11,fontFamily:"monospace",color:S.accent,flexShrink:0}}>{v.requests} req</span>
                      </div>
                      <div style={{height:4,background:S.surface3,borderRadius:2}}><div style={{height:"100%",width:(v.requests/Math.max(1,...Object.values(byModel).map(x=>x.requests))*100)+"%",background:S.accent,borderRadius:2}}/></div>
                    </div>
                  ))}
                  {Object.keys(byModel).length===0&&<div style={{padding:"20px",textAlign:"center",color:S.text3,fontSize:12}}>No data yet</div>}
                </div>
              </div>
            </div>
          )}

          {/* ── PER USER TAB ── */}
          {tab==="users" && (
            <div>
              {/* Filter bar */}
              <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
                <select style={selBtnSt} value={dateRange} onChange={e=>setDateRange(e.target.value)}>
                  {[["24h","Last 24h"],["7d","Last 7 days"],["30d","Last 30 days"],["90d","Last 90 days"],["1y","Last year"],["all","All time"]].map(([v,l])=><option key={v} value={v}>{l}</option>)}
                </select>
                <select style={selBtnSt} value={filterUser} onChange={e=>setFilterUser(e.target.value)}>
                  <option value="all">All users</option>
                  {allUsers.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              {userRows.length===0 ? (
                <div style={{textAlign:"center",padding:"40px",color:S.text3,border:"1px dashed "+S.border2,borderRadius:14}}>No data for this period.</div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {userRows.map(u => (
                    <div key={u.id} style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:14,overflow:"hidden"}}>
                      {/* User header */}
                      <div style={{padding:"14px 18px",borderBottom:"1px solid "+S.border,display:"flex",alignItems:"center",gap:12}}>
                        <div style={{width:40,height:40,borderRadius:"50%",background:u.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700,color:"#fff",flexShrink:0}}>{u.name.slice(0,2).toUpperCase()}</div>
                        <div style={{flex:1}}>
                          <div style={{fontSize:14,fontWeight:700,color:S.text,marginBottom:2}}>{u.name}</div>
                          <div style={{fontSize:10,color:S.text3,fontFamily:"monospace"}}>Last active: {u.lastSeen?new Date(u.lastSeen).toLocaleString():"—"}</div>
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12,textAlign:"center"}}>
                          {[["Requests",u.requests,S.accent],["Prompt tok",u.promptTok.toLocaleString(),S.text2],["Completion tok",u.completionTok.toLocaleString(),S.text2],["Total tok",u.totalTok.toLocaleString(),S.text],["Errors",u.errors,u.errors>0?S.danger:S.success]].map(([l,v,c])=>(
                            <div key={l}><div style={{fontSize:15,fontWeight:700,color:c,fontFamily:"monospace"}}>{v}</div><div style={{fontSize:9,color:S.text3,textTransform:"uppercase",fontFamily:"monospace"}}>{l}</div></div>
                          ))}
                        </div>
                        <button onClick={()=>{setFilterUser(u.id);setTab("prompts");}} style={{...selBtnSt,whiteSpace:"nowrap",color:S.accent,border:"1px solid "+S.accent+"44"}}>View prompts →</button>
                      </div>
                      {/* User prompt sample */}
                      <div style={{padding:"10px 18px",background:S.surface2}}>
                        <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>Folders used: {[...u.folders].join(", ")||"—"} · Models: {[...u.models].map(m=>m.split("/").pop()).join(", ")||"—"}</div>
                        {alog.filter(e=>e.userId===u.id).slice(-2).reverse().map(e=>(
                          <div key={e.id} style={{marginBottom:4,fontSize:11,color:S.text2}}>
                            <span style={{color:S.text3,fontSize:9}}>{new Date(e.ts).toLocaleString()} · </span>
                            <span style={{color:S.accent}}>▸ </span>{truncate(e.prompt,100)}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── PROMPT LOG TAB ── */}
          {tab==="prompts" && (
            <div>
              {/* Filters */}
              <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
                <input value={searchQ} onChange={e=>setSearchQ(e.target.value)} placeholder="Search prompts & completions…" style={{...ISt(),maxWidth:280,fontSize:12}}/>
                <select style={selBtnSt} value={filterUser} onChange={e=>setFilterUser(e.target.value)}>
                  <option value="all">All users</option>
                  {allUsers.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
                <select style={selBtnSt} value={filterModel} onChange={e=>setFilterModel(e.target.value)}>
                  <option value="all">All models</option>
                  {allModels.map(m=><option key={m} value={m}>{m.split("/").pop()}</option>)}
                </select>
                <select style={selBtnSt} value={filterWs} onChange={e=>setFilterWs(e.target.value)}>
                  <option value="all">All folders</option>
                  {allWs.map(w=><option key={w} value={w}>{w}</option>)}
                </select>
                <select style={selBtnSt} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
                  <option value="all">All status</option>
                  <option value="ok">Success</option>
                  <option value="error">Errors only</option>
                </select>
                <select style={selBtnSt} value={sortBy} onChange={e=>setSortBy(e.target.value)}>
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="tokens">Most tokens</option>
                  <option value="latency">Slowest</option>
                </select>
                <span style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginLeft:"auto"}}>{sorted.length} of {alog.length} entries</span>
              </div>

              {sorted.length===0 ? (
                <div style={{textAlign:"center",padding:"40px",color:S.text3,border:"1px dashed "+S.border2,borderRadius:14}}>No entries match your filters.</div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {sorted.map(e => {
                    const isOpen = selE?.id === e.id;
                    const statusColor = e.status==="error" ? S.danger : S.success;
                    return (
                      <div key={e.id} onClick={()=>setSelE(isOpen?null:e)}
                        style={{background:S.cardBg,border:"1px solid "+(isOpen?S.accent:S.border2),borderLeft:"3px solid "+statusColor,borderRadius:12,overflow:"hidden",cursor:"pointer",transition:"border-color 0.15s"}}>
                        {/* Entry header */}
                        <div style={{padding:"10px 14px",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                          <span style={{fontSize:9,fontFamily:"monospace",color:S.text3,flexShrink:0}}>{new Date(e.ts).toLocaleString()}</span>
                          {e.userColor&&<div style={{width:16,height:16,borderRadius:"50%",background:e.userColor,display:"flex",alignItems:"center",justifyContent:"center",fontSize:7,fontWeight:700,color:"#fff",flexShrink:0}}>{(e.userName||"?").slice(0,1).toUpperCase()}</div>}
                          {e.userName&&<span style={{fontSize:10,fontWeight:600,color:e.userColor||S.accent,fontFamily:"monospace"}}>{e.userName}</span>}
                          <span style={{fontSize:9,padding:"1px 6px",borderRadius:4,fontFamily:"monospace",background:S.accentDim,color:S.accent}}>{(e.model||"?").split("/").pop().split("-").slice(0,2).join("-")}</span>
                          {e.workspace&&<span style={{fontSize:9,padding:"1px 6px",borderRadius:4,fontFamily:"monospace",background:S.surface2,color:S.text3,border:"1px solid "+S.border}}>{e.workspace}</span>}
                          <div style={{marginLeft:"auto",display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
                            <span style={{fontSize:9,fontFamily:"monospace",color:S.text3}}>↑{(e.promptTokens||0).toLocaleString()} ↓{(e.completionTokens||0).toLocaleString()} tok</span>
                            <span style={{fontSize:9,fontFamily:"monospace",color:S.text3}}>{e.latencyMs?((e.latencyMs/1000).toFixed(1)+"s"):"?"}</span>
                            <span style={{width:7,height:7,borderRadius:"50%",background:statusColor,display:"inline-block"}}/>
                          </div>
                        </div>
                        {/* Prompt preview */}
                        <div style={{padding:"0 14px 10px",borderTop:"1px solid "+S.border}}>
                          <div style={{display:"grid",gridTemplateColumns:isOpen?"1fr":"1fr 1fr",gap:10,marginTop:8}}>
                            <div>
                              <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:4}}>▸ Prompt ({(e.promptTokens||0).toLocaleString()} tokens)</div>
                              <div style={{color:S.text2,lineHeight:1.65,whiteSpace:isOpen?"pre-wrap":"nowrap",overflow:isOpen?"visible":"hidden",textOverflow:"ellipsis",background:S.surface2,borderRadius:7,padding:"7px 10px",fontFamily:isOpen?"monospace":"inherit",fontSize:isOpen?11:12}}>{strify(e.prompt)||"—"}</div>
                            </div>
                            {(isOpen || true) && (
                              <div>
                                <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:4}}>◂ Completion ({(e.completionTokens||0).toLocaleString()} tokens)</div>
                                <div style={{color:isOpen?S.text:S.text2,lineHeight:1.65,whiteSpace:isOpen?"pre-wrap":"nowrap",overflow:isOpen?"visible":"hidden",textOverflow:"ellipsis",background:isOpen?S.accentDim:S.surface2,borderRadius:7,padding:"7px 10px",fontFamily:isOpen?"monospace":"inherit",fontSize:isOpen?11:12}}>{e.status==="error"?"⚠ "+strify(e.completion):(strify(e.completion)||"—")}</div>
                              </div>
                            )}
                          </div>
                          {isOpen && (
                            <div style={{marginTop:10,display:"flex",gap:6}}>
                              <button onClick={ev=>{ev.stopPropagation();navigator.clipboard?.writeText(e.prompt||"");showToast("Prompt copied!");}} style={{...selBtnSt,fontSize:10}}>Copy prompt</button>
                              <button onClick={ev=>{ev.stopPropagation();navigator.clipboard?.writeText(e.completion||"");showToast("Completion copied!");}} style={{...selBtnSt,fontSize:10}}>Copy completion</button>
                              <button onClick={ev=>{ev.stopPropagation();navigator.clipboard?.writeText(JSON.stringify(e,null,2));showToast("Full entry copied!");}} style={{...selBtnSt,fontSize:10}}>Copy full JSON</button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── BY MODEL TAB ── */}
          {tab==="models" && (
            <div style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:14,overflow:"hidden"}}>
              <div style={{padding:"13px 18px",borderBottom:"1px solid "+S.border,fontSize:12,fontWeight:700,color:S.text2,display:"flex",alignItems:"center",gap:8}}>
                <span>Usage by Model</span>
                {billingUsage && billingUsage.overageTokens > 0 && (
                  <span title={`${billingUsage.overageTokens.toLocaleString()} tokens over the ${(billingUsage.cap||0).toLocaleString()} cap this period`}
                    style={{fontSize:9,padding:"2px 7px",borderRadius:10,background:(S.warning||"#f59e0b")+"22",color:(S.warning||"#f59e0b"),border:`1px solid ${S.warning||"#f59e0b"}55`,fontFamily:"monospace",fontWeight:700,letterSpacing:"0.04em"}}>
                    BILLABLE
                  </span>
                )}
              </div>
              {Object.keys(byModel).length===0 ? <div style={{padding:"40px",textAlign:"center",color:S.text3,fontSize:12}}>No data yet.</div> : (
                Object.entries(byModel).sort((a,b)=>b[1].requests-a[1].requests).map(([model,v])=>{
                  const maxR = Math.max(1,...Object.values(byModel).map(x=>x.requests));
                  return (
                    <div key={model} style={{padding:"12px 18px",borderBottom:"1px solid "+S.border}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:6,alignItems:"center"}}>
                        <span style={{fontSize:12,fontFamily:"monospace",color:S.text,fontWeight:600}}>{model}</span>
                        <div style={{display:"flex",gap:14,fontSize:11,fontFamily:"monospace"}}>
                          <span style={{color:S.accent}}>{v.requests} req</span>
                          <span style={{color:S.text3}}>{v.tokens.toLocaleString()} tok</span>
                          {v.errors>0&&<span style={{color:S.danger}}>{v.errors} err</span>}
                        </div>
                      </div>
                      <div style={{height:6,background:S.surface3,borderRadius:3}}><div style={{height:"100%",width:(v.requests/maxR*100)+"%",background:S.accent,borderRadius:3,transition:"width 0.4s"}}/></div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ── COST TAB ── */}
          {tab==="cost" && (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {usageLoading && !usage && (
                <div style={{padding:30,display:"flex",flexDirection:"column",alignItems:"center",gap:10,color:S.text3,fontSize:12}}>
                <span className="cf-auth-spinner" style={{display:"inline-flex"}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={S.border2} strokeWidth="2.5"/><path d="M21 12a9 9 0 0 0-9-9" stroke={S.accent} strokeWidth="2.5" strokeLinecap="round"/></svg>
                </span>
                <span>Loading…</span>
              </div>
              )}
	              {usage && (() => {
	                const s = usage.summary || {};
	                const maxDaily = Math.max(1, ...(usage.daily || []).map(d => d.cost));
	                const planName = usage.planDetails?.name || usage.plan || "—";
	                const trialQuota = s.trialUserQuota?.active ? s.trialUserQuota : null;
	                const trialPct = trialQuota?.limit ? Math.min(100, Math.round((trialQuota.used / trialQuota.limit) * 100)) : null;
	                const costColor = (trialPct != null ? trialPct : s.tokensPct) >= 85 ? S.danger : "#d4a017";
	                const pricedNote = s.pricedTokensPct != null && s.pricedTokensPct < 100
	                  ? ` · ${s.pricedTokensPct}% priced from table, ${100 - s.pricedTokensPct}% fallback`
	                  : s.pricedTokensPct != null ? ` · priced from per-model table` : "";
	                const usageCards = [
	                  ["Cost this month", s.isByok ? "BYOK" : `$${(s.costMonth||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`, s.isByok ? "You pay your provider directly" : `Billed to ChatFortAI${pricedNote}`, costColor],
	                  ["Tokens this month", (s.tokensMonth||0).toLocaleString(), s.tokensLimit ? `${s.tokensPct}% of ${(s.tokensLimit).toLocaleString()} cap` : "No org cap on your plan", S.accent],
	                  trialQuota ? ["Trial user allowance", `${(trialQuota.used||0).toLocaleString()} / ${(trialQuota.limit||0).toLocaleString()}`, `${trialPct}% used · resets when trial ends`, trialPct >= 85 ? S.danger : S.success] : null,
	                  ["Requests", (s.requestsMonth||0).toLocaleString(), s.errorsMonth ? `${s.errorsMonth} errors` : "all successful", s.errorsMonth ? S.danger : S.text],
	                  ["Plan", planName, `Seats: ${usage.seatsUsed}${usage.planDetails?.seats ? " / " + usage.planDetails.seats : ""}`, S.text2],
	                ].filter(Boolean);
	                return <>
	                  {/* Summary cards */}
	                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10}}>
	                    {usageCards.map(([l, v, sub, c]) => (
                      <div key={l} style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:12,padding:"12px 14px"}}>
                        <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{l}</div>
                        <div style={{fontSize:20,fontWeight:800,color:c,fontFamily:"monospace"}}>{v}</div>
                        <div style={{fontSize:10,color:S.text3,marginTop:4}}>{sub}</div>
                      </div>
                    ))}
                  </div>

                  {/* Plan cap bar, only when the plan has a cap */}
                  {s.tokensLimit && !s.isByok && (
                    <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:"14px 18px"}}>
                      <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:8}}>
                        <span style={{fontSize:12,fontWeight:700,color:S.text2}}>Monthly token cap</span>
                        <span style={{fontFamily:"monospace",fontSize:12,color:S.text3}}>{(s.tokensMonth||0).toLocaleString()} / {(s.tokensLimit).toLocaleString()}</span>
                      </div>
                      <div style={{height:8,borderRadius:4,background:S.inputBg,overflow:"hidden",border:`1px solid ${S.border2}`}}>
                        <div style={{width:`${Math.min(100, s.tokensPct||0)}%`,height:"100%",background:s.tokensPct >= 85 ? S.danger : s.tokensPct >= 60 ? (S.warning || "#f59e0b") : S.success,transition:"width 0.4s"}}/>
                      </div>
	                    </div>
	                  )}
	                  {trialQuota && !s.isByok && (
	                    <div style={{background:S.cardBg,border:`1px solid ${trialPct >= 85 ? S.danger : S.border2}`,borderRadius:14,padding:"14px 18px"}}>
	                      <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:8}}>
	                        <span style={{fontSize:12,fontWeight:700,color:S.text2}}>14-day trial user allowance</span>
	                        <span style={{fontFamily:"monospace",fontSize:12,color:S.text3}}>{(trialQuota.used||0).toLocaleString()} / {(trialQuota.limit||0).toLocaleString()}</span>
	                      </div>
	                      <div style={{height:8,borderRadius:4,background:S.inputBg,overflow:"hidden",border:`1px solid ${S.border2}`}}>
	                        <div style={{width:`${Math.min(100, trialPct||0)}%`,height:"100%",background:trialPct >= 85 ? S.danger : trialPct >= 60 ? (S.warning || "#f59e0b") : S.success,transition:"width 0.4s"}}/>
	                      </div>
	                    </div>
	                  )}

	                  {/* Daily cost chart (last 30 days) */}
                  <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:"16px 18px"}}>
                    <div style={{fontSize:12,fontWeight:600,color:S.text2,marginBottom:14}}>Daily cost — last 30 days</div>
                    <div style={{display:"flex",alignItems:"flex-end",gap:3,height:90}}>
                      {(usage.daily || []).map((d, i) => (
                        <div key={d.date} title={`${d.date}: $${d.cost.toFixed(2)} · ${d.tokens.toLocaleString()} tok · ${d.requests} req`}
                          style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                          <div style={{width:"100%",background:d.cost>0?S.accent:S.surface3,borderRadius:"2px 2px 0 0",height:Math.max(2,(d.cost/maxDaily)*80)+"px",transition:"height 0.3s"}}/>
                          {i % 5 === 0 && <div style={{fontSize:7,fontFamily:"monospace",color:S.text3,whiteSpace:"nowrap"}}>{d.date.slice(5)}</div>}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* By model + by user */}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                    <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
                      <div style={{padding:"12px 16px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>Cost by model</div>
                      {(usage.byModel || []).length === 0 ? (
                        <div style={{padding:20,textAlign:"center",color:S.text3,fontSize:12}}>No spend this month.</div>
                      ) : usage.byModel.slice(0, 10).map(m => (
                        <div key={m.model} style={{padding:"10px 16px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:10}}>
                          <div style={{flex:1,minWidth:0,overflow:"hidden"}}>
                            <div style={{fontSize:11,fontFamily:"monospace",color:S.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.model}</div>
                            <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginTop:2}}>{m.tokens.toLocaleString()} tok · {m.requests} req</div>
                          </div>
                          <span style={{fontSize:13,fontFamily:"monospace",fontWeight:700,color:costColor,flexShrink:0}}>${m.cost.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,overflow:"hidden"}}>
                      <div style={{padding:"12px 16px",borderBottom:`1px solid ${S.border}`,fontSize:12,fontWeight:700,color:S.text2}}>Cost by user</div>
                      {(usage.byUser || []).length === 0 ? (
                        <div style={{padding:20,textAlign:"center",color:S.text3,fontSize:12}}>No spend this month.</div>
                      ) : usage.byUser.slice(0, 10).map(u => (
                        <div key={u.userId} style={{padding:"10px 16px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:10}}>
                          <div style={{width:24,height:24,borderRadius:"50%",background:u.color||S.text3,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,color:"#fff",flexShrink:0}}>{(u.name||"?").slice(0,2).toUpperCase()}</div>
                          <div style={{flex:1,minWidth:0,overflow:"hidden"}}>
                            <div style={{fontSize:12,color:S.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.name}</div>
                            <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginTop:2}}>{u.tokens.toLocaleString()} tok · {u.requests} req</div>
                          </div>
                          <span style={{fontSize:13,fontFamily:"monospace",fontWeight:700,color:costColor,flexShrink:0}}>${u.cost.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>;
              })()}
            </div>
          )}

          {/* ── RETENTION POLICY TAB ── */}
          {tab==="retention" && (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {/* Current policy */}
              <div style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:14,overflow:"hidden"}}>
                <div style={{padding:"13px 18px",borderBottom:"1px solid "+S.border,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <span style={{fontSize:12,fontWeight:700,color:S.text2}}>Retention Policy</span>
                  <span style={{fontSize:9,padding:"2px 8px",borderRadius:10,fontFamily:"monospace",background:S.success+"18",color:S.success,border:"1px solid "+S.success+"30"}}>Active: {retentionPolicy.customLabel}</span>
                </div>
                <div style={{padding:18}}>
                  <div style={{marginBottom:14,fontSize:12,color:S.text2,lineHeight:1.7}}>Set how long analytics data is retained. Data older than the retention period is automatically purged on next save. Default is 7 years to match common regulatory requirements (HIPAA, SOX, FINRA).</div>

                  {/* Presets */}
                  <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Quick Presets</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:7,marginBottom:16}}>
                    {[
                      {label:"90 Days",y:0,m:3,d:0,badge:"GDPR minimum"},
                      {label:"1 Year",y:1,m:0,d:0,badge:""},
                      {label:"3 Years",y:3,m:0,d:0,badge:""},
                      {label:"7 Years",y:7,m:0,d:0,badge:"HIPAA · SOX · FINRA"},
                      {label:"10 Years",y:10,m:0,d:0,badge:""},
                      {label:"Forever",y:0,m:0,d:0,badge:"No auto-delete"},
                    ].map(p=>{
                      const isActive = retEdit.years===p.y&&retEdit.months===p.m&&retEdit.days===p.d&&(p.y+p.m+p.d===0?!retEdit.autoDelete:retEdit.autoDelete);
                      return (
                        <div key={p.label} onClick={()=>setRetEdit({...retEdit,years:p.y,months:p.m,days:p.d,autoDelete:p.y+p.m+p.d>0,customLabel:p.label+(p.badge?" ("+p.badge+")":"")})}
                          style={{padding:"10px 12px",borderRadius:9,border:"1px solid "+(isActive?S.accent:S.border2),background:isActive?S.accentDim:S.inputBg,cursor:"pointer",transition:"all 0.15s"}}>
                          <div style={{fontSize:13,fontWeight:isActive?700:500,color:isActive?S.accent:S.text,marginBottom:3}}>{p.label}</div>
                          {p.badge&&<div style={{fontSize:9,color:S.text3,fontFamily:"monospace"}}>{p.badge}</div>}
                        </div>
                      );
                    })}
                  </div>

                  {/* Custom */}
                  <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Custom Period</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:12}}>
                    {[["Years",0,50,"years"],["Months",0,11,"months"],["Days",0,31,"days"]].map(([label,min,max,key])=>(
                      <div key={key}>
                        <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>{label.toUpperCase()}</div>
                        <input type="number" min={min} max={max} value={retEdit[key]||0} onChange={e=>setRetEdit(p=>({...p,[key]:parseInt(e.target.value)||0,customLabel:"Custom"}))} style={{...ISt(),fontFamily:"monospace",fontSize:14,textAlign:"center"}}/>
                      </div>
                    ))}
                  </div>

                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 13px",background:S.surface2,borderRadius:8,marginBottom:14}}>
                    <div onClick={()=>setRetEdit(p=>({...p,autoDelete:!p.autoDelete}))} style={{width:36,height:20,background:retEdit.autoDelete?S.success:S.surface3,borderRadius:10,position:"relative",cursor:"pointer",border:"1px solid "+S.border2,transition:"background 0.2s",flexShrink:0}}>
                      <div style={{position:"absolute",top:2,left:retEdit.autoDelete?16:2,width:14,height:14,background:"#fff",borderRadius:"50%",transition:"left 0.18s"}}/>
                    </div>
                    <div>
                      <div style={{fontSize:12,fontWeight:600,color:S.text}}>Auto-delete expired entries</div>
                      <div style={{fontSize:10,color:S.text3}}>When off, data is kept indefinitely regardless of the period above</div>
                    </div>
                  </div>

                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <Btn onClick={()=>{setRetentionPolicy({...retEdit});showToast("Retention policy saved!");}}>Save Policy</Btn>
                    {retentionDays>0 && (
                      <span style={{fontSize:11,color:S.text3}}>
                        Oldest entry: {oldestEntry?oldestEntry.toLocaleDateString():"none"} · 
                        {" "}Entries expire: {new Date(Date.now()-retentionDays*86400000).toLocaleDateString()} and older
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Compliance info */}
              <div style={{background:S.cardBg,border:"1px solid "+S.border2,borderRadius:14,overflow:"hidden"}}>
                <div style={{padding:"13px 18px",borderBottom:"1px solid "+S.border,fontSize:12,fontWeight:700,color:S.text2}}>Regulatory Reference</div>
                <div style={{padding:16,display:"flex",flexDirection:"column",gap:8}}>
                  {[
                    ["HIPAA","6 years from creation or last effective date","Health data, covered entities"],
                    ["SOX","7 years for financial records","Public companies, audit trails"],
                    ["FINRA","6 years for most records, 3 years accessible","Broker-dealers, communications"],
                    ["GDPR","Only as long as necessary · Right to erasure","EU personal data"],
                    ["SEC Rule 17a-4","6 years (3 years easily accessible)","Broker-dealer records"],
                    ["ABA Model Rule 1.6","Varies by state, often 5-7 years","Attorney-client communications"],
                  ].map(([reg,period,scope])=>(
                    <div key={reg} style={{display:"flex",gap:12,padding:"9px 12px",background:S.surface2,borderRadius:8}}>
                      <span style={{fontSize:11,fontWeight:700,color:S.accent,fontFamily:"monospace",minWidth:90,flexShrink:0}}>{reg}</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:11,color:S.text,marginBottom:1}}>{period}</div>
                        <div style={{fontSize:9,color:S.text3,fontFamily:"monospace"}}>{scope}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }


  function WsModal(){const ed=wsModal?.edit;const [name,setName]=useState(ed?.name||"");const [desc,setDesc]=useState(ed?.desc||"");const [icon,setIcon]=useState(ed?.icon||"\uD83C\uDFF0");const [color,setColor]=useState(ed?.color||"#5ba3c9");const [sp,setSp]=useState(ed?.sysprompt||"");const save=()=>{if(!name.trim()){showToast("Name required");return;}if(ed){setWorkspaces(p=>p.map(w=>w.id===ed.id?{...w,name,desc,icon,color,sysprompt:sp}:w));showToast("Updated!");}else{setWorkspaces(p=>[...p,{id:"ws-"+gid(),name,desc,icon,color,sysprompt:sp,created:Date.now()}]);showToast("Created!");}setWsModal(null);};return(<Modal open title={ed?"Edit Workspace":"New Workspace"} onClose={()=>setWsModal(null)} footer={<><Btn variant="secondary" onClick={()=>setWsModal(null)}>Cancel</Btn><Btn onClick={save}>Save</Btn></>}><FR label="Name"><input style={ISt()} value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Research Project"/></FR><FR label="Description"><input style={ISt()} value={desc} onChange={e=>setDesc(e.target.value)} placeholder="Optional"/></FR><FR label="Icon"><EP value={icon} onChange={setIcon}/></FR><FR label="Color"><CP value={color} onChange={setColor}/></FR><FR label="System Prompt"><textarea style={{...ISt(),resize:"vertical",minHeight:72,fontFamily:"monospace",fontSize:12}} value={sp} onChange={e=>setSp(e.target.value)} placeholder="All chats use this prompt\u2026"/></FR></Modal>);}

  function ChatFolderModal(){const ed=chatFolderModal?.edit;const [name,setName]=useState(ed?.name||"");const save=()=>{const n=name.trim();if(!n){showToast("Folder name required");return;}if(ed){setChatFolders(p=>p.map(f=>f.id===ed.id?{...f,name:n}:f));showToast("Folder updated");}else{const id="folder-"+gid();setChatFolders(p=>[...p,{id,name:n,created:Date.now()}]);setChatFolderId(id);setRailChatOpen(true);showToast("Folder created");}setChatFolderModal(null);};return(<Modal open title={ed?"Edit Chat Folder":"New Folder"} onClose={()=>setChatFolderModal(null)} footer={<><Btn variant="secondary" onClick={()=>setChatFolderModal(null)}>Cancel</Btn><Btn onClick={save}>Save</Btn></>}><FR label="Folder name"><input style={ISt()} value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Client intake"/></FR></Modal>);}

  function AgModal() {
    const ed = agModal?.edit;
    const liveAgentCatalogModels = Array.isArray(liveModelCatalog?.models) ? liveModelCatalog.models : [];
    const liveAgentCatalogHasZdrData = liveAgentCatalogModels.some(m => m?.zdr);
    const liveAgentCatalogShouldFilterByZdr = liveModelCatalog?.zdrEnforced !== false && liveAgentCatalogHasZdrData;
    const liveAgentOpenRouterModels = liveAgentCatalogShouldFilterByZdr
      ? liveAgentCatalogModels.filter(m => m?.zdr?.verified)
      : liveAgentCatalogModels;
    const liveAgentOpenRouterById = new Map(liveAgentOpenRouterModels.map(m => [m.id, m]));
    const liveAgentOpenRouterAllowedIds = new Set(liveAgentOpenRouterById.keys());
    const agentCatalogIsAccessFiltered = liveAgentCatalogModels.length > 0 && liveModelCatalog?.accessFiltered !== false;
    const routedDefaultModel = OPENROUTER_FALLBACK_MODEL_MAP[DEFAULT_MODEL];
    const defaultAgentModelValue = routedDefaultModel && liveAgentOpenRouterById.has(routedDefaultModel)
      ? `${routedDefaultModel}|openrouter`
      : `${DEFAULT_MODEL}|${DEFAULT_PROVIDER}`;
    const [name,setName]     = useState(ed?.name||"");
    const [desc,setDesc]     = useState(ed?.desc||"");
    const [emoji,setEmoji]   = useState(ed?.emoji||"🤖");
    const [color,setColor]   = useState(ed?.color||S.accent);
    const [mv,setMv]         = useState(ed ? `${ed?.model || DEFAULT_MODEL}|${ed?.provider || DEFAULT_PROVIDER}` : defaultAgentModelValue);
    const [sp,setSp]         = useState(ed?.sysprompt||"");
    const [temp,setTemp]     = useState(ed?.temperature??0.7);
    const [starters,setStarters] = useState((ed?.starters||[]).join("\n"));
    const [agImage,setAgImage]   = useState(ed?.image||null);
    const [agId,setAgId]         = useState(ed?.agentId||"");
    const [tags,setTags]         = useState(ed?.tags||[]);
    const [tagInput,setTagInput] = useState("");
    const [showAdv,setShowAdv]   = useState(false);
    const [kbOpen,setKbOpen]     = useState(false);
    const [agentKbId,setAgentKbId] = useState(ed?.kbId||"");
    const [mvOpen,setMvOpen]     = useState(false); // base-model dropdown open
    const [mvQuery,setMvQuery]   = useState("");    // base-model search
    const [maxTok,setMaxTok]     = useState(ed?.maxTokens||1024);
    const [topP,setTopP]         = useState(ed?.topP||1.0);
    const [freqP,setFreqP]       = useState(ed?.freqPenalty||0);
    const [ttsVoice,setTtsVoice] = useState(ed?.ttsVoice||"");
    const [showJson,setShowJson] = useState(false);
    const [caps,setCaps] = useState(ed?.capabilities||{vision:true,fileUpload:true,fileContext:true,webSearch:true,imageGen:false,codeInterpreter:true,usage:false,citations:true,statusUpdates:true,builtinTools:true});
    const [defFeat,setDefFeat] = useState(ed?.defaultFeatures||{webSearch:true,imageGen:false,codeInterpreter:false});
    const [btTools,setBtTools] = useState(ed?.builtinTools||{timeCalc:true,memory:true,chatHistory:true,notes:true,knowledgeBase:true,channels:true,webSearch:true,imageGen:true,codeInterpreter:true});
    const [agSelTools,setAgSelTools] = useState(ed?.toolIds||[]); // custom tool IDs assigned to this agent
    const [agFilters,setAgFilters] = useState(ed?.filters||{securityGuardrail:false,pdfToText:true,globalTimestamp:true,chainOfThought:true,codeSyntax:false});
    const [agActions,setAgActions] = useState(ed?.actions||{chatBranching:true,downloadWord:true,pdfExport:true});

    const save = () => {
      if (!name.trim()) { showToast("Name required"); return; }
      const [model,provider] = mv.split("|");
      const data = {name,desc,emoji,color,model,provider,sysprompt:sp,temperature:parseFloat(temp),
        starters:starters.split("\n").map(s=>s.trim()).filter(Boolean),
        image:agImage, agentId:agId||name.toLowerCase().replace(/\s+/g,"-").replace(/[^a-z0-9-]/g,""),
        tags, kbId:agentKbId||null, maxTokens:maxTok, topP, freqPenalty:freqP, ttsVoice,
        capabilities:caps, defaultFeatures:defFeat, builtinTools:btTools, toolIds:agSelTools, filters:agFilters, actions:agActions};
      if (ed) { setAgents(p=>p.map(a=>a.id===ed.id?{...a,...data}:a)); showToast("Updated!"); }
      else { setAgents(p=>[...p,{id:"ag-"+gid(),...data}]); showToast("Agent created!"); }
      setAgModal(null);
    };

    const avail = [];
    const seenAvail = new Set();
    const pushAvail = (value, label, pk, color) => {
      if (!value || seenAvail.has(value)) return;
      seenAvail.add(value);
      avail.push({ value, label, pk, color });
    };
    const labelForOpenRouterModel = (m) => {
      const prefix = String(m.id || "").split("/")[0];
      const real = OR_REAL_PROVIDER[prefix];
      const providerName = real?.label || PROVIDERS.openrouter.name;
      const icon = PROVIDERS[real?.key]?.icon || PROVIDERS.openrouter.icon;
      const rawName = m.name || String(m.id || "").split("/").pop();
      const labelPrefix = `${providerName}:`;
      const modelName = String(rawName).toLowerCase().startsWith(labelPrefix.toLowerCase())
        ? String(rawName).slice(labelPrefix.length).trim()
        : rawName;
      const suffix = m?.zdr?.verified ? " · ZDR" : m?.source === "openrouter" ? " · Live" : "";
      return `${providerName} — ${modelName}${suffix}`;
    };
    const openRouterRows = liveAgentOpenRouterModels.length
      ? liveAgentOpenRouterModels
      : (PROVIDERS.openrouter.models || []);
    for (const m of openRouterRows) {
      if (agentCatalogIsAccessFiltered && !liveAgentOpenRouterAllowedIds.has(m.id)) continue;
      if (!modelVisibleToNonOwners(m.id)) continue;
      const _orPrefix = String(m.id || "").split("/")[0];
      const _orReal = OR_REAL_PROVIDER[_orPrefix];
      pushAvail(`${m.id}|openrouter`, labelForOpenRouterModel(m), _orReal?.key || "openrouter", _orReal?.color || PROVIDERS.openrouter?.color || S.accent);
    }
    for (const [pid,p] of Object.entries(PROVIDERS)) {
      if (pid === "openrouter" || !p.models?.length) continue;
      if (pid === "anthropic" && liveAgentOpenRouterModels.length && !integrations.anthropic?.key) continue;
      if (!p.builtin && !integrations[pid]?.key) continue;
      for (const m of p.models) {
        if (agentCatalogIsAccessFiltered && pid === "anthropic" && integrations.openrouter?.key && !integrations.anthropic?.key) {
          const routedId = OPENROUTER_FALLBACK_MODEL_MAP[m.id];
          if (routedId && !liveAgentOpenRouterAllowedIds.has(routedId)) continue;
        }
        if (!modelVisibleToNonOwners(m.id)) continue;
        pushAvail(`${m.id}|${pid}`, `${p.name} — ${m.name}`, realProviderKey(pid, m.id), p.color || S.accent);
      }
    }
    if (!seenAvail.has(mv)) {
      const [curModel, curProvider] = mv.split("|");
      const pv = PROVIDERS[curProvider] || {};
      const known = pv.models?.find(m => m.id === curModel) || liveAgentOpenRouterById.get(curModel);
      pushAvail(mv, `Current — ${known?.name || curModel}`, realProviderKey(curProvider, curModel), pv.color || S.accent);
    }

    const Chk = ({k, val, setter, label}) => (
      <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",userSelect:"none",padding:"4px 0"}}>
        <input type="checkbox" checked={val} onChange={e=>setter(p=>({...p,[k]:e.target.checked}))}
          style={{accentColor:S.accent,width:14,height:14,flexShrink:0}}/>
        <span style={{fontSize:12,color:S.text2}}>{label}</span>
      </label>
    );

    const derivedId = agId || name.toLowerCase().replace(/\s+/g,"-").replace(/[^a-z0-9-]/g,"") || "agent-id";
    const selectedAgentKb = knowledgeBases.find(kb => kb.id === agentKbId);
    const jsonData = {name,id:derivedId,description:desc,base_model:mv.split("|")[0],provider:mv.split("|")[1],
      system_prompt:sp,temperature:parseFloat(temp),max_tokens:maxTok,top_p:topP,freq_penalty:freqP,
      tags,kb_id:agentKbId||undefined,tts_voice:ttsVoice||undefined,capabilities:caps,default_features:defFeat,builtin_tools:btTools,filters:agFilters,actions:agActions};

    return (
      <div style={{position:"fixed",inset:0,background:S.bg,zIndex:500,display:"flex",flexDirection:"column",overflow:"hidden"}}>

        {/* ── Top bar ── */}
        <div style={{padding:"10px 20px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",background:S.surface,flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <button onClick={()=>setAgModal(null)} style={{width:30,height:30,border:`1px solid ${S.border2}`,borderRadius:8,background:S.inputBg,color:S.text2,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>←</button>
            <span style={{fontSize:15,fontWeight:700,color:S.text}}>{ed?"Edit Agent":"New Agent"}</span>
          </div>
          <button onClick={save} style={{padding:"7px 22px",borderRadius:8,border:"none",background:S.text,color:S.bg,fontSize:13,fontWeight:700,cursor:"pointer"}}>
            {ed?"Save Changes":"Save & Create"}
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{flex:1,overflowY:"auto",padding:"20px 24px"}}>
          <div style={{maxWidth:1120,margin:"0 auto",display:"grid",gridTemplateColumns:isMobile?"1fr":"minmax(0,700px) 360px",gap:20,alignItems:"start"}}>

            {/* Image + core fields */}
            <div style={{gridColumn:isMobile?"1":"2",gridRow:isMobile?"auto":"1 / span 12",display:"grid",gridTemplateColumns:"1fr",gap:16,alignItems:"start",alignSelf:"start",position:isMobile?"relative":"sticky",top:isMobile?"auto":16}}>

              {/* Avatar */}
              <div style={{display:"flex",flexDirection:"column",alignItems:"stretch",gap:8,flexShrink:0,maxWidth:150,justifySelf:"center",width:"100%"}}>
                <div style={{width:"100%",aspectRatio:"1 / 1",borderRadius:16,background:`linear-gradient(180deg,${S.surface2},${S.inputBg})`,border:`1px solid ${color}55`,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",position:"relative",boxShadow:`0 18px 38px ${color}14`}}>
                  {agImage
                    ? <img src={agImage} style={{width:"100%",height:"100%",objectFit:"cover"}} alt="Agent"/>
                    : <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                        <span style={{fontSize:42,lineHeight:1}}>{emoji}</span>
                        <span style={{fontSize:9,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.06em"}}>No image</span>
                      </div>
                  }
                  <label title="Upload agent image" style={{position:"absolute",bottom:8,right:8,width:28,height:28,background:S.surface,border:`1px solid ${S.border2}`,borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:12,boxShadow:"0 8px 22px rgba(0,0,0,0.18)"}}>
                    📷<input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;const r=new FileReader();r.onload=ev=>setAgImage(ev.target.result);r.readAsDataURL(f);}}/>
                  </label>
                </div>
                {agImage && <button onClick={()=>setAgImage(null)} style={{fontSize:10,color:S.text3,background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,cursor:"pointer",padding:"6px 8px",fontFamily:"inherit"}}>Reset Image</button>}
              </div>

              {/* Fields */}
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <div>
                  <div style={{fontSize:11,color:S.text2,marginBottom:4}}>Model Name</div>
                  <input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Legal Assistant" style={{...ISt(),width:"100%"}}/>
                </div>
                <div>
                  <div style={{fontSize:11,color:S.text2,marginBottom:4}}>Model ID</div>
                  <input value={agId} onChange={e=>setAgId(e.target.value)} placeholder={derivedId} style={{...ISt(),width:"100%",fontFamily:"monospace",fontSize:12}}/>
                </div>
                <div>
                  <div style={{fontSize:11,color:S.text2,marginBottom:4}}>Base Model (From)</div>
                  <div style={{position:"relative"}}>
                    {(() => {
                      const cur = avail.find(a => a.value === mv) || avail[0];
                      return (
                        <button type="button" onClick={()=>{setMvOpen(o=>!o);setMvQuery("");}} style={{...ISt(),width:"100%",display:"flex",alignItems:"center",gap:8,cursor:"pointer",textAlign:"left",paddingRight:34,fontFamily:"monospace",fontSize:12}}>
                          {cur && <ProviderLogo providerKey={cur.pk} color={cur.color} size={14} />}
                          <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cur?.label || mv}</span>
                        </button>
                      );
                    })()}
                    <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2.5" style={{position:"absolute",right:11,top:"50%",transform:"translateY(-50%)",pointerEvents:"none",transition:"transform .15s ease",...(mvOpen?{transform:"translateY(-50%) rotate(180deg)"}:{})}}><polyline points="6 9 12 15 18 9"/></svg>
                    {mvOpen && (<>
                      <div onClick={()=>setMvOpen(false)} style={{position:"fixed",inset:0,zIndex:60}}/>
                      <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:S.modalBg||S.cardBg,border:`1px solid ${S.border2}`,borderRadius:10,maxHeight:300,overflowY:"auto",zIndex:61,boxShadow:"0 14px 44px rgba(0,0,0,0.45)"}}>
                        <div style={{padding:"8px 10px",borderBottom:`1px solid ${S.border}`,position:"sticky",top:0,background:S.modalBg||S.cardBg,zIndex:1}}>
                          <input autoFocus value={mvQuery} onChange={e=>setMvQuery(e.target.value)} placeholder="Search models…" style={{...ISt(),width:"100%",fontSize:12,fontFamily:"inherit"}} />
                        </div>
                        {avail.filter(a => !mvQuery || a.label.toLowerCase().includes(mvQuery.toLowerCase()) || a.value.toLowerCase().includes(mvQuery.toLowerCase())).map(a => (
                          <div key={a.value} onClick={()=>{setMv(a.value);setMvOpen(false);setMvQuery("");}} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",cursor:"pointer",background:a.value===mv?S.accentDim:"none",fontSize:12,fontFamily:"monospace",borderBottom:`1px solid ${S.border}55`}}>
                            <ProviderLogo providerKey={a.pk} color={a.color} size={13} />
                            <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:a.value===mv?S.accent:S.text}}>{a.label}</span>
                            {a.value===mv && <span style={{color:S.accent,fontSize:10}}>✓</span>}
                          </div>
                        ))}
                        {avail.filter(a => !mvQuery || a.label.toLowerCase().includes(mvQuery.toLowerCase())).length === 0 && (
                          <div style={{padding:"14px 12px",fontSize:11,color:S.text3,textAlign:"center"}}>No models match.</div>
                        )}
                      </div>
                    </>)}
                  </div>
                  <div style={{fontSize:9,color:S.text3,fontFamily:"monospace",marginTop:4,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                    {liveAgentOpenRouterModels.length
                      ? `${liveAgentOpenRouterModels.length} ${liveAgentCatalogShouldFilterByZdr ? "ZDR-verified" : "live"} OpenRouter model${liveAgentOpenRouterModels.length===1?"":"s"} available`
                      : "Using configured provider models"}
                  </div>
                </div>
                <div>
                  <div style={{fontSize:11,color:S.text2,marginBottom:4}}>Description</div>
                  <input value={desc} onChange={e=>setDesc(e.target.value)} placeholder="Add a short description about what this agent does" style={{...ISt(),width:"100%"}}/>
                </div>
                <div>
                  <div style={{fontSize:11,color:S.text2,marginBottom:4}}>Tags</div>
                  <div style={{display:"flex",gap:4,flexWrap:"wrap",alignItems:"center",padding:"6px 8px",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,minHeight:36}}>
                    {tags.map((t,i)=>(
                      <span key={i} style={{padding:"2px 8px",borderRadius:20,background:S.accentDim,border:`1px solid ${S.border3}`,fontSize:11,color:S.accent,display:"flex",alignItems:"center",gap:3}}>
                        {t}<button onClick={()=>setTags(p=>p.filter((_,j)=>j!==i))} style={{background:"none",border:"none",cursor:"pointer",color:S.accent,fontSize:12,padding:0,lineHeight:1}}>×</button>
                      </span>
                    ))}
                    <input value={tagInput} onChange={e=>setTagInput(e.target.value)}
                      onKeyDown={e=>{if((e.key==="Enter"||e.key===",")&&tagInput.trim()){setTags(p=>[...p,tagInput.trim()]);setTagInput("");e.preventDefault();}}}
                      placeholder="Add a tag…" style={{background:"none",border:"none",outline:"none",color:S.text,fontSize:12,minWidth:80}}/>
                  </div>
                </div>
                <div style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:12,overflow:"hidden",marginTop:2}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,padding:"10px 12px",borderBottom:`1px solid ${S.border}`}}>
                    <div>
                      <div style={{fontSize:10,fontWeight:800,color:S.text3,textTransform:"uppercase",letterSpacing:"0.08em",fontFamily:"monospace"}}>Appearance</div>
                      <div style={{fontSize:11,color:S.text2,marginTop:2}}>Choose an icon and accent used on agent cards and chat context.</div>
                    </div>
                    <div style={{width:34,height:34,borderRadius:10,background:`${color}18`,border:`1px solid ${color}45`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{emoji}</div>
                  </div>
                  <div style={{padding:12,display:"grid",gap:12}}>
                    <div>
                      <div style={{fontSize:10,fontWeight:700,color:S.text3,marginBottom:7,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.06em"}}>Icon</div>
                      <EP value={emoji} onChange={setEmoji}/>
                    </div>
                    <div>
                      <div style={{fontSize:10,fontWeight:700,color:S.text3,marginBottom:7,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.06em"}}>Accent Color</div>
                      <CP value={color} onChange={setColor}/>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Model Params */}
            <Sec title="Model Params">
              <div style={{marginBottom:10}}>
                <div style={{fontSize:11,color:S.text2,marginBottom:4}}>System Prompt</div>
                <textarea value={sp} onChange={e=>setSp(e.target.value)}
                  placeholder={"Write your model system prompt content here\ne.g. You are a legal expert specialising in contract review…"}
                  style={{...ISt(),width:"100%",resize:"vertical",minHeight:110,fontFamily:"monospace",fontSize:12}}/>
              </div>
              <div onClick={()=>setShowAdv(p=>!p)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",padding:"8px 0",borderTop:`1px solid ${S.border}`}}>
                <span style={{fontSize:12,fontWeight:600,color:S.accent}}>Advanced Params</span>
                <span style={{fontSize:11,color:S.text3}}>{showAdv?"Hide":"Show"}</span>
              </div>
              {showAdv && (
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,paddingTop:10}}>
                  {[["Temperature",temp,setTemp,0,2,0.1],["Max Tokens",maxTok,setMaxTok,256,128000,256],["Top P",topP,setTopP,0,1,0.05],["Frequency Penalty",freqP,setFreqP,-2,2,0.1]].map(([lbl,val,setter,mn,mx,step])=>(
                    <div key={lbl}>
                      <div style={{fontSize:11,color:S.text2,marginBottom:4,display:"flex",justifyContent:"space-between"}}>
                        {lbl}<em style={{fontStyle:"normal",color:S.accent,fontFamily:"monospace"}}>{typeof val==="number"?val.toFixed(step<1?1:0):val}</em>
                      </div>
                      <input type="range" min={mn} max={mx} step={step} value={val} onChange={e=>setter(parseFloat(e.target.value))} style={{width:"100%",accentColor:S.accent}}/>
                    </div>
                  ))}
                </div>
              )}
            </Sec>

            {/* Starter Prompts */}
            <Sec title="Starter Prompts">
              <textarea value={starters} onChange={e=>setStarters(e.target.value)}
                placeholder={"Summarize this document\nExplain this clause\nDraft a response\nFind the key risks"}
                style={{...ISt(),width:"100%",resize:"vertical",minHeight:80,fontFamily:"monospace",fontSize:12}}/>
              <div style={{fontSize:10,color:S.text3,marginTop:6}}>One per line · shown as quick-start buttons when the user opens this agent</div>
            </Sec>

            {/* Knowledge */}
            <Sec title="Knowledge">
              <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
                <button type="button" onClick={()=>setKbOpen(p=>!p)} style={{padding:"8px 13px",borderRadius:9,background:agentKbId?S.accentDim:S.inputBg,border:`1px solid ${agentKbId?S.accent:S.border2}`,color:agentKbId?S.accent:S.text2,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:7}}>
                  📚 {selectedAgentKb ? selectedAgentKb.name : "Select Knowledge"}
                  <span style={{fontSize:10,color:agentKbId?S.accent:S.text3,fontFamily:"monospace"}}>{knowledgeBases.length}</span>
                </button>
                <button type="button" onClick={()=>{setAgModal(null);setSection("knowledge");showToast("Create or open a knowledge base, then upload files there.");}} style={{padding:"8px 13px",borderRadius:9,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text2,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:7}}>📎 Upload Files</button>
              </div>
              {kbOpen && (
                <div style={{background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:10,padding:6,marginBottom:10}}>
                  {knowledgeBases.length === 0 ? (
                    <div style={{padding:12}}>
                      <div style={{fontSize:12,color:S.text,fontWeight:700,marginBottom:4}}>No knowledge bases yet</div>
                      <div style={{fontSize:11,color:S.text2,lineHeight:1.5,marginBottom:10}}>Knowledge bases are created in the Knowledge section, then documents are uploaded into them for RAG retrieval.</div>
                      <button type="button" onClick={()=>{setAgModal(null);setSection("knowledge");}} style={{padding:"7px 11px",borderRadius:8,background:S.accentDim,border:`1px solid ${S.accent}40`,color:S.accent,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Create Knowledge Base</button>
                    </div>
                  ) : (
                    <>
                      <div onClick={()=>{setAgentKbId("");setKbOpen(false);}} style={{padding:"8px 10px",borderRadius:8,cursor:"pointer",display:"flex",alignItems:"center",gap:8,color:S.text,fontSize:12,background:!agentKbId?S.accentDim:"transparent"}}>
                        <span>⊘</span><span style={{flex:1}}>No default knowledge base</span>{!agentKbId && <span style={{color:S.accent}}>✓</span>}
                      </div>
                      {knowledgeBases.map(kb => (
                        <div key={kb.id} onClick={()=>{setAgentKbId(kb.id);setKbOpen(false);}} style={{padding:"8px 10px",borderRadius:8,cursor:"pointer",display:"flex",alignItems:"center",gap:8,color:S.text,fontSize:12,background:agentKbId===kb.id?S.accentDim:"transparent"}}>
                          <span>📚</span>
                          <span style={{flex:1,minWidth:0}}>
                            <span style={{display:"block",fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{kb.name}</span>
                            <span style={{display:"block",fontSize:10,color:S.text3,fontFamily:"monospace",marginTop:1}}>{kb.docCount||0} docs · {kb.chunkCount||0} chunks</span>
                          </span>
                          {agentKbId===kb.id && <span style={{color:S.accent}}>✓</span>}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
              <div style={{fontSize:11,color:S.text2,lineHeight:1.6}}>
                {selectedAgentKb
                  ? `This agent will use "${selectedAgentKb.name}" by default when it chats. Users can still override it from the chat tools menu.`
                  : "Knowledge bases come from the Knowledge section. Create one there, upload documents into it, then select it here to make it this agent's default context."}
              </div>
            </Sec>

            {/* Tools */}
            <Sec title="Tools">
              {tools.length === 0 ? (
                <div style={{fontSize:12,color:S.text3,lineHeight:1.6}}>
                  No tools created yet.{" "}
                  <button onClick={()=>{setAgModal(null);setSection("tools");}} style={{background:"none",border:"none",color:S.accent,cursor:"pointer",fontSize:12,padding:0,textDecoration:"underline"}}>Create a tool →</button>
                </div>
              ) : (
                <>
                  <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:10}}>
                    {tools.map(t => {
                      const sel = agSelTools.includes(t.id);
                      return (
                        <label key={t.id} style={{display:"flex",alignItems:"center",gap:7,padding:"6px 12px",borderRadius:8,border:`1px solid ${sel?S.accent:S.border2}`,background:sel?S.accentDim:S.inputBg,cursor:"pointer",fontSize:12,color:sel?S.accent:S.text2,transition:"all 0.15s"}}>
                          <input type="checkbox" checked={sel} onChange={e=>{const v=e.target.checked;setAgSelTools(p=>v?[...p,t.id]:p.filter(id=>id!==t.id));}} style={{accentColor:S.accent}}/>
                          <span style={{fontWeight:sel?700:400}}>🔧 {t.name}</span>
                          {t.version && <span style={{fontSize:9,fontFamily:"monospace",color:S.text3}}>v{t.version}</span>}
                        </label>
                      );
                    })}
                  </div>
                  <div style={{fontSize:11,color:S.text3}}>Enabled tools are passed to the AI as callable functions. The model decides when to call them.</div>
                </>
              )}
            </Sec>

            {/* Filters */}
            <Sec title="Filters">
              <div style={{display:"flex",flexWrap:"wrap",columnGap:24,rowGap:2}}>
                {[["securityGuardrail","🛡 Security Guardrail"],["pdfToText","📄 PDF to Text"],["globalTimestamp","🕐 Global Timestamp Injector"],["chainOfThought","🔗 Depth & Chain-of-Thought Enforcer"],["codeSyntax","🎨 Code Syntax Highlighter"]].map(([k,lbl])=>(
                  <Chk key={k} k={k} val={agFilters[k]} setter={setAgFilters} label={lbl}/>
                ))}
              </div>
            </Sec>

            {/* Actions */}
            <Sec title="Actions">
              <div style={{display:"flex",flexWrap:"wrap",columnGap:24,rowGap:2}}>
                {[["chatBranching","🔀 Chat Branching"],["downloadWord","📝 Download Chat as Word Document"],["pdfExport","📑 PDF Export"]].map(([k,lbl])=>(
                  <Chk key={k} k={k} val={agActions[k]} setter={setAgActions} label={lbl}/>
                ))}
              </div>
            </Sec>

            {/* Capabilities */}
            <Sec title="Capabilities">
              <div style={{display:"flex",flexWrap:"wrap",columnGap:24,rowGap:2}}>
                {[["vision","👁 Vision"],["fileUpload","📎 File Upload"],["fileContext","📂 File Context"],["webSearch","🔍 Web Search"],["imageGen","🎨 Image Generation"],["codeInterpreter","💻 Code Interpreter"],["usage","📊 Usage"],["citations","📌 Citations"],["statusUpdates","🔔 Status Updates"],["builtinTools","🔧 Builtin Tools"]].map(([k,lbl])=>(
                  <Chk key={k} k={k} val={caps[k]} setter={setCaps} label={lbl}/>
                ))}
              </div>
            </Sec>

            {/* Default Features */}
            <Sec title="Default Features">
              <div style={{display:"flex",flexWrap:"wrap",columnGap:24,rowGap:2}}>
                {[["webSearch","🔍 Web Search"],["imageGen","🎨 Image Generation"],["codeInterpreter","💻 Code Interpreter"]].map(([k,lbl])=>(
                  <Chk key={k} k={k} val={defFeat[k]} setter={setDefFeat} label={lbl}/>
                ))}
              </div>
            </Sec>

            {/* Builtin Tools */}
            <Sec title="Builtin Tools">
              <div style={{display:"flex",flexWrap:"wrap",columnGap:24,rowGap:2}}>
                {[["timeCalc","⏱ Time & Calculation"],["memory","🧠 Memory"],["chatHistory","💬 Chat History"],["notes","📝 Notes"],["knowledgeBase","📚 Knowledge Base"],["channels","📡 Channels"],["webSearch","🔍 Web Search"],["imageGen","🎨 Image Generation"],["codeInterpreter","💻 Code Interpreter"]].map(([k,lbl])=>(
                  <Chk key={k} k={k} val={btTools[k]} setter={setBtTools} label={lbl}/>
                ))}
              </div>
            </Sec>

            {/* TTS Voice */}
            <Sec title="TTS Voice">
              <input value={ttsVoice} onChange={e=>setTtsVoice(e.target.value)}
                placeholder="e.g. alloy, echo, shimmer" style={{...ISt(),width:"100%"}}/>
            </Sec>

            {/* Save button */}
            <button onClick={save} className="cf-cta cf-btn-lift" style={{width:"100%",padding:"14px",borderRadius:10,border:"none",background:`linear-gradient(135deg,${S.accent},${S.accent2||S.accent})`,color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",marginBottom:10,letterSpacing:"-0.01em"}}>
              {ed?"Save Changes":"Save & Create"}
            </button>

            {/* JSON Preview */}
            <div style={{marginBottom:24}}>
              <div onClick={()=>setShowJson(p=>!p)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",padding:"8px 0",borderTop:`1px solid ${S.border}`}}>
                <span style={{fontSize:12,color:S.text2,fontWeight:500}}>JSON Preview</span>
                <span style={{fontSize:11,color:S.text3}}>{showJson?"Hide":"Show"}</span>
              </div>
              {showJson && (
                <pre style={{background:S.codeBlockBg,borderRadius:10,padding:14,fontSize:11,color:S.text2,overflowX:"auto",lineHeight:1.6,marginTop:8}}>
                  {JSON.stringify(jsonData,null,2)}
                </pre>
              )}
            </div>

          </div>
        </div>
      </div>
    );
  }

  const chatFoldersSafe = (Array.isArray(chatFolders) && chatFolders.length) ? chatFolders : DCF;
  const activeChatFolders = chatFoldersSafe.filter(f=>!archivedChatFolderIdSet.has(f.id));
  const archivedChatFolders = chatFoldersSafe.filter(f=>archivedChatFolderIdSet.has(f.id));
  const chatFolderIdSet = new Set(chatFoldersSafe.map(f=>f.id));
  const effectiveChatFolderId = (chat) => chatFolderIdSet.has(chat?.folderId) ? chat.folderId : "folder-general";
  const activeChatFolderId = chatFolderId==="folder-all" || activeChatFolders.some(f=>f.id===chatFolderId) ? chatFolderId : "folder-all";
  const visibleChatHistory = activeChatFolderId==="folder-all" ? chats : chats.filter(c=>effectiveChatFolderId(c)===activeChatFolderId);
  const curWs=workspaces.find(w=>w.id===DEFAULT_CHAT_WS_ID)||workspaces[0];
  const wsChats=visibleChatHistory;
  const now2=Date.now();
  const grp={Today:[],Week:[],Older:[]};
  visibleChatHistory.forEach(c=>{const a=now2-c.created;if(a<86400000)grp.Today.push(c);else if(a<604800000)grp.Week.push(c);else grp.Older.push(c);});
  // Search runs over the global chat history plus the active folder filter — keeps results consistent
  // with what the date-grouped list shows. Memoized so unrelated re-renders
  // (theme toggle, streaming commit) don't re-scan every message.
  const chatSearchResults = useMemo(() => {
    const q = (chatSearchDebounced || "").trim();
    if (!q) return null;
    const t0 = (typeof performance !== "undefined") ? performance.now() : 0;
    const r = searchChats(visibleChatHistory, q);
    if (typeof console !== "undefined" && t0) {
      try { console.log(`[chat-search] "${q}" → ${r?.length || 0} hits in ${(performance.now() - t0).toFixed(2)}ms`); } catch {}
    }
    return r;
  }, [visibleChatHistory, chatSearchDebounced]);
  const chatSearchActive = chatSearchResults !== null;
  const chatSearchVisible = chatSearchActive ? (chatSearchShowAll ? chatSearchResults : chatSearchResults.slice(0, 50)) : null;
  const chatSearchKey = (e) => {
    if (e.key === "Escape") { setChatSearch(""); e.currentTarget.blur(); return; }
    if (!chatSearchVisible || !chatSearchVisible.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setChatSearchFocusIdx(i => Math.min(i + 1, chatSearchVisible.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setChatSearchFocusIdx(i => Math.max(0, i - 1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const r = chatSearchVisible[Math.min(chatSearchFocusIdx, chatSearchVisible.length - 1)];
      if (r) onPickChat(r.chat.id, r.chat.agentId, { scrollToMessageId: r.snippetMsgId });
    }
  };
  const CHAT_RAIL_VISIBLE_ROWS = 5;
  const CHAT_RAIL_ROW_HEIGHT = 31;
  const CHAT_RAIL_GROUP_HEADER_HEIGHT = 18;
  const chatRailHistoryMaxHeight = CHAT_RAIL_GROUP_HEADER_HEIGHT + (CHAT_RAIL_VISIBLE_ROWS * CHAT_RAIL_ROW_HEIGHT);

  // In server mode, admin privileges come from the JWT role. In local/standalone
  // mode we don't have real multi-user auth, so everyone is treated as admin.
  const isAdmin = serverMode ? (authUser?.role === "admin" || authUser?.role === "superadmin") : true;
  const isSuperAdmin = serverMode ? (authUser?.role === "superadmin") : true;
  // Plan section gating: server-computed map of which feature surfaces
  // this org unlocks (authUser.sectionAccess). Fail-open: local mode,
  // old servers, or unconfigured billing leave everything available.
  const secAccess = serverMode ? (authUser?.sectionAccess || null) : null;
  const secOk = (k) => !secAccess || secAccess[k] !== false;
  const renderPlanLock = (label, need) => (
    <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:32,background:S.bg}}>
      <div className="cf-page-card" style={{maxWidth:460,textAlign:"center",background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:16,padding:"38px 34px"}}>
        <div style={{fontSize:36,marginBottom:12}}>{"\u{1F512}"}</div>
        <div style={{fontSize:17,fontWeight:800,color:S.text,marginBottom:8}}>{label} is available on the {need==="pro"?"Professional":"Business"} plan</div>
        <div style={{fontSize:12.5,color:S.text2,lineHeight:1.65,marginBottom:20}}>
          Your workspace is on the {String(secAccess?.plan||"current").toUpperCase()} plan.
          {isAdmin ? " Upgrade to unlock it instantly \u2014 plan changes prorate automatically." : " Ask your workspace admin to upgrade in Billing."}
        </div>
        {isAdmin && (
          <button onClick={()=>setSection("billing")} style={{padding:"11px 22px",borderRadius:10,border:"none",background:`linear-gradient(135deg,${S.accent},${S.accent2})`,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            Upgrade in Billing
          </button>
        )}
      </div>
    </div>
  );
  // Diagnostic: log role state so admins can check jlo etc. in console
  console.log("[access] serverMode=", serverMode, "role=", authUser?.role, "isAdmin=", isAdmin);

  const railChat={id:"chat",icon:IC.chat,label:"Chat",desc:"Your conversations"};
  // Projects now lives inside the "More" group (see railMore below),
  // so railPrimary is empty. Its .map() renders nothing -- no gap.
  const railPrimary=[];
  const railMore=[
    // Slice H34a: Projects -- shared docs + chat. Hash route #projects
    // (+ optional /<id>/doc/<docId>) handled by ProjectsPanel internally.
    {id:"projects",icon:IC.ws,label:"Projects",desc:"Shared docs + chat with your team"},
    {id:"agents",icon:IC.agent,label:"Agents",desc:"Custom AI assistants"},
    {id:"knowledge",icon:IC.knowledge,label:"Knowledge",desc:"Docs for RAG retrieval"},
    {id:"guide",icon:IC.guide,label:"Guide",desc:"How to use ChatFortAI"},
    {id:"automations",icon:IC.bolt,label:"Automations"+(secOk("automations")?"":" \u{1F512}"),desc:"Recurring AI tasks on a schedule"},
    {id:"cowork",icon:IC.cowork,label:"Hermes Co-work"+(secOk("cowork")?"":" \u{1F512}"),desc:"Everyday work from the Hermes orb"},
    {id:"tools",icon:IC.tools,label:"Tools",desc:"Custom functions for agents"},
    {id:"mcp",icon:IC.mcp,label:"MCP Servers",desc:"Tool & data connectors"},
    {id:"workflows",icon:IC.workflows,label:"Workflows"+(secOk("workflows")?"":" \u{1F512}"),desc:"Automate with AI agents"},
    ...(isAdmin?[{id:"bots",icon:IC.bots,label:"Bots"+(secOk("bots")?"":" \u{1F512}"),desc:"Public chat widgets for your website"}]:[]),
  ];
  const railSettings={id:"settings",icon:IC.settings,label:"Settings",desc:"App preferences & config"};
  const railAdmin=[
    {id:"integrations",icon:IC.integ,label:"AI Providers",desc:"API keys for models",admin:true},
    {id:"marketplace",icon:IC.marketplace,label:"Marketplace",desc:"Slack, Teams, Drive, CRM, more",admin:true},
    {id:"models",icon:IC.eye,label:"Model visibility",desc:"Choose which models appear",owner:true},
    {id:"users",icon:IC.users,label:"Users",desc:"Manage access & roles",admin:true},
    {id:"analytics",icon:IC.analytics,label:"Analytics"+(secOk("analytics")?"":" \u{1F512}"),desc:"Usage metrics",admin:true},
    {id:"audit",icon:IC.audit,label:"Audit log",desc:"Who did what, when",admin:true},
    {id:"sso",icon:IC.sso,label:"SSO",desc:"Single sign-on (OIDC)",admin:true},
    {id:"billing",icon:IC.billing,label:"Billing",desc:"Plan & subscription",admin:true},
    {id:"owner",icon:IC.crown,label:"Owner console",desc:"All tenants · operator view",owner:true}
  ].filter(item => item.owner ? isSuperAdmin : isAdmin);
  const railMoreSections=railMore.map(item=>item.id);
  const railAdminSections=railAdmin.map(item=>item.id);
  const [railMoreOpen,setRailMoreOpen]=useState(()=>ld("cf_rail_more_open", false));
  const [railAdminOpen,setRailAdminOpen]=useState(()=>ld("cf_rail_admin_open", false));
  useEffect(()=>{sv("cf_rail_more_open", railMoreOpen);},[railMoreOpen]);
  useEffect(()=>{sv("cf_rail_admin_open", railAdminOpen);},[railAdminOpen]);
  useEffect(()=>{ if(section==="chat") setRailChatOpen(true); if(railMoreSections.includes(section)) setRailMoreOpen(true); if(railAdminSections.includes(section)) setRailAdminOpen(true); },[section]);
  useEffect(()=>{ if(section==="workspaces") setSection("chat"); },[section]);
  // Refresh the user list when the Users page is opened so other members'
  // profile updates (e.g. a new avatar) show without a full reload.
  useEffect(()=>{
    if(section!=="users" || !(isAdmin||isSuperAdmin)) return;
    const t = authToken || (()=>{ try { return localStorage.getItem("cf_auth_token"); } catch { return ""; } })();
    if(!t) return;
    fetch("/api/admin/users",{ headers:{ Authorization:"Bearer "+t } })
      .then(r=>r.ok?r.json():null)
      .then(d=>{ if(Array.isArray(d)) setUsers(d); })
      .catch(()=>{});
  },[section]);

  // If a non-admin somehow landed on an admin section (via old state, deep link,
  // or admin role change), bounce them to chat.
  useEffect(() => {
    if ((!isAdmin && (section === "users" || section === "analytics" || section === "integrations" || section === "billing")) || (!isSuperAdmin && section === "models")) {
      setSection("chat");
    }
  }, [isAdmin, isSuperAdmin, section]);

  // ── Public pricing page (accessible pre-auth at /pricing or via login link)
  function PricingPage({ onBack }) {
    const [plans, setPlans] = useState(null);
    const [annual, setAnnual] = useState(true);
    const [variant, setVariant] = useState("managed"); // "managed" | "byok"
    useEffect(() => {
      fetch("/api/plans").then(r => r.ok ? r.json() : null).then(d => { if (d) setPlans(d); }).catch(()=>{});
    }, []);
    const order = ["trial", "starter", "pro", "business", "premium", "enterprise"];
    const fmt = (n) => n == null ? "Unlimited" : (n >= 1_000_000 ? (n/1_000_000).toFixed(0)+"M" : n >= 1000 ? (n/1000).toFixed(0)+"k" : n.toLocaleString());
    const planColor = (id) => id === "enterprise" ? "#d4a017" : id === "premium" ? "#f59e0b" : id === "business" ? "#8b5cf6" : id === "pro" ? S.accent : id === "starter" ? "#4caf87" : S.text3;
    const priceOf = (p) => {
      const v = variant === "byok" ? p.byok : p.managed;
      return annual ? v?.annualPrice : v?.price;
    };
    return (
      <div className="cf-page-fade-in" style={{minHeight:"100vh",background:S.bg,fontFamily:brandFontStack,padding:"40px 20px",overflowY:"auto"}}>
        <style>{`@import url('${BRAND_FONT_IMPORT}');:root{--cf-brand-font:${brandFontStack};}body,#root{font-family:var(--cf-brand-font);}*{box-sizing:border-box;margin:0;padding:0;}`}</style>
        <div style={{maxWidth:1200,margin:"0 auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:32}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:36,height:36,borderRadius:10,background:brandLogoActive?"transparent":`linear-gradient(135deg,${S.accent},${S.accent2})`,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
                {brandLogoActive ? <img src={brandLogoActiveUrl} alt={BN} style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <CFLogo size={22}/>}
              </div>
              <span style={{fontSize:16,fontWeight:800,color:S.text}}>{BN}</span>
            </div>
            <button onClick={onBack} style={{padding:"8px 14px",borderRadius:9,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>← Back to sign in</button>
          </div>

          <div className="cf-pricing-header" style={{textAlign:"center",marginBottom:28}}>
            <h1 style={{fontSize:32,fontWeight:800,color:S.text,letterSpacing:"-0.02em",marginBottom:8}}>Simple pricing, no per-message billing</h1>
            <p style={{fontSize:14,color:S.text2,maxWidth:620,margin:"0 auto",lineHeight:1.6}}>Every plan includes a generous monthly token allowance. Only the overage is billed — and we alert you long before you get there.</p>
          </div>

          {/* Variant + billing toggles */}
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:14,marginBottom:28}}>
            <div style={{display:"flex",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:10,padding:4}}>
              <button className="cf-pricing-toggle" data-active={variant==="managed"} onClick={()=>setVariant("managed")} style={{padding:"8px 18px",borderRadius:7,border:"none",background:variant==="managed"?S.accentDim:"transparent",color:variant==="managed"?S.accent:S.text3,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}} title="We provide the AI. Simplest to set up.">
                Managed <span style={{fontSize:10,fontWeight:500,opacity:0.8,marginLeft:4}}>· we supply AI</span>
              </button>
              <button className="cf-pricing-toggle" data-active={variant==="byok"} onClick={()=>setVariant("byok")} style={{padding:"8px 18px",borderRadius:7,border:"none",background:variant==="byok"?S.accentDim:"transparent",color:variant==="byok"?S.accent:S.text3,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}} title="You bring your own Anthropic / OpenAI / Azure keys. ~40% cheaper.">
                BYOK <span style={{fontSize:10,fontWeight:500,opacity:0.8,marginLeft:4}}>· your API keys</span>
              </button>
            </div>
            <div style={{display:"flex",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:10,padding:4}}>
              <button className="cf-pricing-toggle" data-active={!annual} onClick={()=>setAnnual(false)} style={{padding:"7px 16px",borderRadius:7,border:"none",background:!annual?S.accentDim:"transparent",color:!annual?S.accent:S.text3,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Monthly</button>
              <button className="cf-pricing-toggle" data-active={annual} onClick={()=>setAnnual(true)} style={{padding:"7px 16px",borderRadius:7,border:"none",background:annual?S.accentDim:"transparent",color:annual?S.accent:S.text3,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Annual <span style={{color:S.success,fontSize:10,marginLeft:4}}>· save 20%</span></button>
            </div>
            {variant === "byok" && (
              <div style={{fontSize:11,color:S.text3,maxWidth:500,textAlign:"center",lineHeight:1.5}}>
                BYOK: you supply Anthropic, OpenAI, Azure, or Bedrock API keys. You pay your providers directly for usage; we just run the platform. No monthly token cap from us.
              </div>
            )}
          </div>

          <div style={{ maxWidth: 660, margin: "0 auto 26px", background: S.cardBg, border: `1px solid ${S.success}40`, borderLeft: `3px solid ${S.success}`, borderRadius: 12, padding: "12px 18px", fontSize: 12.5, color: S.text2, lineHeight: 1.6, textAlign: "center" }}>
            <span style={{ fontWeight: 700, color: S.text }}>✓ Unlimited base models</span> — the everyday fast &amp; balanced models are free to use without per-message limits on every paid plan, under fair use (~20M tokens per seat / mo). Your per-seat token allowance applies to <span style={{ fontWeight: 600, color: S.text }}>premium frontier models</span> only{variant === "byok" ? "; on BYOK there are no model limits at all — you pay your provider directly." : "."}
          </div>
          {plans ? (
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))",gap:14,alignItems:"stretch"}}>
              {order.filter(k => plans[k]).map(k => {
                const p = plans[k];
                const pc = planColor(k);
                const price = priceOf(p);
                const isFeatured = k === "pro";
                return (
                  <div key={k} className="cf-pricing-card" style={{background:S.cardBg,border:`1px solid ${isFeatured?pc:S.border2}`,borderRadius:16,padding:"22px 20px",display:"flex",flexDirection:"column",gap:12,position:"relative",boxShadow:isFeatured?`0 12px 32px ${pc}18`:"none"}}>
                    {isFeatured && <div style={{position:"absolute",top:-11,right:18,background:`linear-gradient(135deg,${pc},${pc}cc)`,color:"#fff",fontSize:10,fontWeight:700,letterSpacing:"0.07em",textTransform:"uppercase",fontFamily:"monospace",padding:"4px 11px",borderRadius:7,boxShadow:`0 4px 12px ${pc}55`}}>★ Most popular</div>}
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:pc,fontFamily:"monospace",letterSpacing:"0.06em",textTransform:"uppercase"}}>{p.name}</div>
                      <div style={{fontSize:11,color:S.text3,marginTop:3,lineHeight:1.5,minHeight:32}}>{p.tagline}</div>
                    </div>
                    <div style={{display:"flex",alignItems:"baseline",gap:4,margin:"4px 0 2px"}}>
                      {price != null ? (
                        <>
                          <span style={{fontSize:32,fontWeight:800,color:S.text,letterSpacing:"-0.02em"}}>${price}</span>
                          <span style={{fontSize:12,color:S.text3}}>/ user / mo</span>
                        </>
                      ) : (
                        <span style={{fontSize:22,fontWeight:700,color:S.text}}>Contact sales</span>
                      )}
                    </div>
                    {annual && price != null && price > 0 && <div style={{fontSize:10,color:S.text3,fontFamily:"monospace"}}>billed annually</div>}
                    <div style={{height:1,background:S.border,margin:"6px 0"}}/>
                    {(() => {
                      // Paid plans get the full feature matrix (parity with the
                      // in-app Billing page); trial + enterprise keep their
                      // hand-tuned highlights (they're already specific).
                      const paid = k === "starter" || k === "pro" || k === "business" || k === "premium";
                      if (!paid) {
                        return (
                          <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 7, flex: 1 }}>
                            {(p.highlights || []).map((h, i) => (
                              <li key={i} style={{ display: "flex", gap: 7, fontSize: 12, color: S.text2, lineHeight: 1.5 }}>
                                <span style={{ color: pc, flexShrink: 0, marginTop: 1 }}>✓</span><span>{h}</span>
                              </li>
                            ))}
                          </ul>
                        );
                      }
                      const f = p.features || {};
                      const rank = ({ starter: 0, pro: 1, business: 2, premium: 3 })[k] ?? 0;
                      const tokM = Math.round((p.monthlyTokens || 0) / 1e6 * 10) / 10;
                      const rows = [
                        ["Up to " + (p.seats ?? "—") + " users", true],
                        [tokM + "M premium tokens / seat / mo", true],
                        ["Unlimited base models (fair use)", true],
                        ["Chat, Projects, Agents, Knowledge, Tools, MCP", true],
                        ["Automations & Workflows", rank >= 1],
                        ["CoWork agent", rank >= 1],
                        ["Hermes auto-runs", rank >= 2],
                        ["Terminal (code agent)", rank >= 2],
                        ["Analytics dashboard", rank >= 2],
                        ["Embeddable Bots", rank >= 2],
                        ["Multi-model compare", !!f.multiModel],
                        ["Bring your own keys (BYOK)", !!f.byok],
                        ["API access + /v1", !!f.apiAccess],
                        ["SSO (Okta / Entra / Google)", !!f.ssoOidc],
                        ["Audit log + CSV export", !!f.auditLog],
                        ["HIPAA BAA", !!f.baa],
                        ["White-label", !!f.whiteLabel],
                      ];
                      return (
                        <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                          {rows.map(([label, on]) => (
                            <li key={label} style={{ display: "flex", gap: 7, fontSize: 11.5, lineHeight: 1.4, color: on ? S.text2 : S.text3, opacity: on ? 1 : 0.5 }}>
                              <span style={{ color: on ? S.success : S.text3, flexShrink: 0, marginTop: 1, fontWeight: 700 }}>{on ? "✓" : "·"}</span>
                              <span>{label}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    })()}
                    <button onClick={onBack} className="cf-pricing-cta" style={{padding:"10px",borderRadius:10,border:isFeatured?"none":`1px solid ${pc}44`,background:isFeatured?`linear-gradient(135deg,${pc},${pc}cc)`:"transparent",color:isFeatured?"#fff":pc,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginTop:6}}>
                      {k === "trial" ? "Start free trial" : k === "enterprise" ? "Contact sales" : "Start with " + p.name}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : <div style={{textAlign:"center",padding:40,color:S.text3}}>Loading plans…</div>}

          {/* Add-ons */}
          <div style={{marginTop:40,background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:14,padding:22}}>
            <div style={{fontSize:12,fontWeight:700,color:S.text3,fontFamily:"monospace",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:12}}>Add-ons</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:14}}>
              {[
                ["Extra tokens", "$0.50 per 100k over allowance", "Soft overage — alerted at 85%, 100%"],
                ["HIPAA BAA (Pro)", "$500 / month", "Bundled free on Business and Enterprise"],
                ["White-label (Business)", "$1,000 / month", "Custom domain, no ChatFortAI branding"],
                ["Implementation (Enterprise)", "$10k – $50k one-time", "SSO + AD + KB seeding + training"],
              ].map(([name, price, sub]) => (
                <div key={name} className="cf-pricing-addon" style={{padding:"14px 16px",borderRadius:10,border:`1px solid ${S.border2}`,background:S.inputBg}}>
                  <div style={{fontSize:13,fontWeight:700,color:S.text,marginBottom:4}}>{name}</div>
                  <div style={{fontSize:13,color:S.accent,fontFamily:"monospace",marginBottom:3}}>{price}</div>
                  <div style={{fontSize:11,color:S.text3,lineHeight:1.5}}>{sub}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{textAlign:"center",marginTop:24,fontSize:11,color:S.text3}}>
            Prices in USD. Cancel anytime. {annual ? "Annual commits" : "Monthly plans"} auto-renew until cancelled.
          </div>
        </div>
      </div>
    );
  }

  if (showPricing) {
    return <PricingPage onBack={()=>{ setShowPricing(false); if (window.location.pathname === "/pricing") history.replaceState(null, "", "/"); }} />;
  }

  const renderRailButton=(item,{nested=false,adminGroup=false}={})=>{
    const active=section===item.id;
    const sensitive=item.admin||item.owner||adminGroup;
    const railButtonMargin=railCollapsed?"0 6px":nested?"0 12px 0 14px":"0 10px 0 6px";
    const railButtonWidth=railCollapsed?"calc(100% - 12px)":nested?"calc(100% - 26px)":"calc(100% - 16px)";
    return <button key={item.id} className="cf-tile-hover" onClick={()=>{setSection(item.id);if(isMobile)setNavOpen(false);}}
      title={railCollapsed ? `${item.label} — ${item.desc}` : undefined}
      style={{
        padding:railCollapsed?"6px 0":nested?"6px 10px 6px 22px":"8px 12px",
        margin:railButtonMargin,
        borderRadius:10,
        background:active?S.accentDim:"none",
        border:active?`1px solid ${S.border3}`:"1px solid transparent",
        color:active?S.accent:sensitive?S.danger+"cc":S.text2,
        cursor:"pointer",display:"flex",alignItems:"center",gap:railCollapsed?0:nested?8:10,
        justifyContent:railCollapsed?"center":"flex-start",
        position:"relative",transition:"all 0.15s",textAlign:"left",width:railButtonWidth,flexShrink:0
      }}
      onMouseEnter={e=>{if(!active)e.currentTarget.style.background=S.hoverBg;}}
      onMouseLeave={e=>{if(!active)e.currentTarget.style.background="none";}}>
      {active&&<div style={{position:"absolute",left:-1,top:"50%",transform:"translateY(-50%)",width:3,height:nested?18:22,background:S.grad2,borderRadius:"0 3px 3px 0"}}/>}
      <div style={{width:nested?26:30,height:nested?26:30,borderRadius:8,background:active?`${S.accent}20`:S.inputBg,border:`1px solid ${active?S.border3:S.border2}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:active?S.accent:sensitive?S.danger+"cc":S.text3,transition:"all 0.15s"}}>
        {item.icon}
        {sensitive&&<div style={{position:"absolute",top:nested?5:6,right:nested?8:10,width:6,height:6,borderRadius:"50%",background:adminOk?S.success:S.danger,border:`1px solid ${S.navBg}`}}/>}
      </div>
      {!railCollapsed && <div style={{flex:1,overflow:"hidden"}}>
        <div style={{fontSize:nested?11:12,fontWeight:active?700:500,color:active?S.accent:sensitive?S.danger+"cc":S.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.label}</div>
        <div style={{fontSize:10,color:active?S.accent+"99":S.text3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",marginTop:1}}>{item.desc}</div>
      </div>}
    </button>;
  };

  const renderRailDisclosure=({id,label,desc,icon,open,onToggle,active,childrenCount,sensitive=false})=>{
    const railDisclosureMargin=railCollapsed?"0 6px":"0 10px 0 6px";
    const railDisclosureWidth=railCollapsed?"calc(100% - 12px)":"calc(100% - 16px)";
    return (
    <button key={id} className="cf-tile-hover" onClick={onToggle}
      title={railCollapsed ? `${label} — ${desc}` : undefined}
      style={{
        padding:railCollapsed?"6px 0":"8px 12px",margin:railDisclosureMargin,borderRadius:10,
        background:active?S.accentDim:"none",
        border:active?`1px solid ${S.border3}`:"1px solid transparent",
        color:active?S.accent:sensitive?S.danger+"cc":S.text2,
        cursor:"pointer",display:"flex",alignItems:"center",gap:railCollapsed?0:10,
        justifyContent:railCollapsed?"center":"flex-start",
        position:"relative",transition:"all 0.15s",textAlign:"left",width:railDisclosureWidth,flexShrink:0
      }}
      onMouseEnter={e=>{if(!active)e.currentTarget.style.background=S.hoverBg;}}
      onMouseLeave={e=>{if(!active)e.currentTarget.style.background="none";}}>
      {active&&<div style={{position:"absolute",left:-1,top:"50%",transform:"translateY(-50%)",width:3,height:22,background:S.grad2,borderRadius:"0 3px 3px 0"}}/>}
      <div style={{width:30,height:30,borderRadius:8,background:active?`${S.accent}20`:S.inputBg,border:`1px solid ${active?S.border3:S.border2}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:active?S.accent:sensitive?S.danger+"cc":S.text3,transition:"all 0.15s"}}>
        {icon}
      </div>
      {!railCollapsed && <div style={{flex:1,overflow:"hidden"}}>
        <div style={{fontSize:12,fontWeight:active?700:500,color:active?S.accent:sensitive?S.danger+"cc":S.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{label}</div>
        <div style={{fontSize:10,color:active?S.accent+"99":S.text3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",marginTop:1}}>{desc}</div>
      </div>}
      {!railCollapsed && <span style={{fontSize:10,color:active?S.accent:S.text3,fontFamily:"monospace",display:"flex",alignItems:"center",gap:6}}>
        <span>{childrenCount}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{transform:open?"rotate(180deg)":"rotate(0deg)",transition:"transform 0.15s"}}><polyline points="6 9 12 15 18 9"/></svg>
      </span>}
    </button>
    );
  };

  const renderChatRailPanel=()=>{
    if (railCollapsed) return null;
    const countForFolder=(id)=>id==="folder-all"
      ? chats.length
      : chats.filter(c=>effectiveChatFolderId(c)===id).length;
    const moveFolderId=effectiveChatFolderId(curChat);
    const moveFolderOptions=activeChatFolders.some(f=>f.id===moveFolderId)
      ? activeChatFolders
      : [...activeChatFolders, chatFoldersSafe.find(f=>f.id===moveFolderId)].filter(Boolean);
    const hasExtraFolders=activeChatFolders.length>CHAT_FOLDER_COLLAPSED_LIMIT;
    let visibleChatFolders=railFoldersExpanded
      ? activeChatFolders
      : activeChatFolders.slice(0, CHAT_FOLDER_COLLAPSED_LIMIT);
    if(!railFoldersExpanded && activeChatFolderId!=="folder-all" && !visibleChatFolders.some(f=>f.id===activeChatFolderId)){
      const activeFolder=activeChatFolders.find(f=>f.id===activeChatFolderId);
      if(activeFolder) visibleChatFolders=[...visibleChatFolders.slice(0, Math.max(0, CHAT_FOLDER_COLLAPSED_LIMIT-1)), activeFolder];
    }
    const hiddenFolderCount=Math.max(0, activeChatFolders.length-CHAT_FOLDER_COLLAPSED_LIMIT);
    const canArchiveFolder=(f)=>!!(f&&f.id&&f.id!=="folder-all"&&f.id!=="folder-general");
    const restoreChatFolder=(folder)=>{
      if(!folder?.id) return;
      setArchivedChatFolderIds(prev=>prev.filter(id=>id!==folder.id));
      showToast("Folder restored");
    };
    const archiveChatFolder=(folder)=>{
      if(!canArchiveFolder(folder)) return;
      setArchivedChatFolderIds(prev=>prev.includes(folder.id)?prev:[...prev,folder.id]);
      if(chatFolderId===folder.id) setChatFolderId("folder-all");
      showToast({text:`${folder.name} archived`,actionLabel:"Undo",onAction:()=>restoreChatFolder(folder)});
    };
    const renderFolderOption=(f)=>{
      const active=activeChatFolderId===f.id;
      const canArchive=canArchiveFolder(f);
      return (
        <div key={f.id} data-chat-folder-row style={{width:"100%",display:"flex",alignItems:"center",gap:3}}>
          <button
            type="button"
            onClick={()=>{setChatFolderId(f.id);setSection("chat");setRailChatOpen(true);}}
            style={{flex:1,minWidth:0,padding:"6px 7px",borderRadius:8,border:`1px solid ${active?S.border3:"transparent"}`,background:active?S.accentDim:"transparent",color:active?S.accent:S.text2,fontFamily:"inherit",fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",gap:6,textAlign:"left"}}
            onMouseEnter={e=>{if(!active)e.currentTarget.style.background=S.hoverBg;}}
            onMouseLeave={e=>{if(!active)e.currentTarget.style.background="transparent";}}
          >
            <span style={{width:16,height:16,borderRadius:5,display:"flex",alignItems:"center",justifyContent:"center",background:active?`${S.accent}18`:S.inputBg,border:`1px solid ${active?S.border3:S.border2}`,flexShrink:0,color:active?S.accent:S.text3}}>{IC.ws}</span>
            <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</span>
            <span style={{fontSize:9,fontFamily:"monospace",color:active?S.accent:S.text3}}>{countForFolder(f.id)}</span>
          </button>
          {canArchive&&(
            <button
              type="button"
              onClick={()=>archiveChatFolder(f)}
              aria-label={`Archive ${f.name}`}
              title={`Archive ${f.name}`}
              style={{width:22,height:22,borderRadius:7,border:`1px solid ${S.border2}`,background:S.cardBg,color:S.text3,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,flexShrink:0}}
            >{IC.archive}</button>
          )}
        </div>
      );
    };
    const renderArchivedFolderOption=(f)=>(
      <div key={f.id} data-chat-folder-archived-row style={{width:"100%",display:"flex",alignItems:"center",gap:6,padding:"6px 7px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.cardBg,color:S.text2,fontFamily:"inherit",fontSize:11}}>
        <span style={{width:16,height:16,borderRadius:5,display:"flex",alignItems:"center",justifyContent:"center",background:S.inputBg,border:`1px solid ${S.border2}`,flexShrink:0,color:S.text3}}>{IC.archive}</span>
        <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</span>
        <span style={{fontSize:9,fontFamily:"monospace",color:S.text3}}>{countForFolder(f.id)}</span>
        <button
          type="button"
          onClick={()=>restoreChatFolder(f)}
          aria-label={`Restore ${f.name}`}
          title={`Restore ${f.name}`}
          style={{width:22,height:22,borderRadius:7,border:`1px solid ${S.border3}`,background:S.accentDim,color:S.accent,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,flexShrink:0}}
        >{IC.unarchive}</button>
      </div>
    );
    return (
      <div className="cf-chat-rail-panel" style={{margin:"0 6px 7px 14px",padding:"8px",borderRadius:12,background:S.inputBg,border:`1px solid ${S.border2}`,display:"flex",flexDirection:"column",gap:8,flexShrink:0}}>
        <button
          className="cf-newchat-btn"
          onClick={()=>{newChat();if(isMobile)setNavOpen(false);}}
          style={{width:"100%",padding:"7px 9px",borderRadius:8,background:S.accentDim,border:`1px solid ${S.border3}`,color:S.accent,fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:7}}
        >{IC.plus} New Chat</button>

        <div style={{position:"relative"}}>
          <input
            ref={chatSearchInputRef}
            type="search"
            placeholder="Search chats..."
            value={chatSearch}
            onChange={e=>setChatSearch(e.target.value)}
            onKeyDown={chatSearchKey}
            aria-label="Search chats"
            style={{width:"100%",padding:"7px 24px 7px 8px",background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:8,color:S.text,fontFamily:"inherit",fontSize:11,outline:"none"}}
          />
          {chatSearch && (
            <button
              onClick={()=>{setChatSearch("");setTimeout(()=>chatSearchInputRef.current?.focus(),0);}}
              aria-label="Clear search"
              title="Clear"
              style={{position:"absolute",right:3,top:3,bottom:3,width:20,padding:0,background:"none",border:"none",color:S.text3,cursor:"pointer",fontSize:13,lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center"}}
            >×</button>
          )}
          {chatSearchActive && (
            <div style={{fontSize:9,marginTop:4,fontFamily:"monospace",color:S.text3}}>
              {chatSearchResults.length===0?"No matches":`${chatSearchResults.length} result${chatSearchResults.length===1?"":"s"}`}
            </div>
          )}
        </div>

        {!chatSearchActive && (
          <div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:6,margin:"1px 0 5px"}}>
              <div style={{fontSize:9,fontWeight:800,letterSpacing:"0.08em",textTransform:"uppercase",fontFamily:"monospace",color:S.text3}}>{railArchivedFoldersOpen?"Archived":"Folders"}</div>
              <div style={{display:"flex",alignItems:"center",gap:4}}>
                <button
                  type="button"
                  onClick={()=>setRailArchivedFoldersOpen(v=>!v)}
                  aria-pressed={railArchivedFoldersOpen}
                  title={railArchivedFoldersOpen?"Back to active folders":"View archived folders"}
                  aria-label={railArchivedFoldersOpen?"Back to active folders":"View archived folders"}
                  style={{height:22,minWidth:22,padding:"0 5px",borderRadius:7,border:`1px solid ${railArchivedFoldersOpen?S.border3:S.border2}`,background:railArchivedFoldersOpen?S.accentDim:S.cardBg,color:railArchivedFoldersOpen?S.accent:S.text3,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:3,fontSize:9,fontFamily:"monospace"}}
                >{IC.archive}{archivedChatFolders.length>0&&<span>{archivedChatFolders.length}</span>}</button>
                {!railArchivedFoldersOpen&&(
                  <button
                    type="button"
                    onClick={()=>setChatFolderModal({edit:null})}
                    title="New folder"
                    aria-label="New folder"
                    data-tooltip="Create a new folder for organizing chats."
                    style={{width:22,height:22,borderRadius:7,border:`1px solid ${S.border2}`,background:S.cardBg,color:S.accent,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0}}
                  >{IC.plus}</button>
                )}
              </div>
            </div>
            {railArchivedFoldersOpen ? (
              <div style={{display:"flex",flexDirection:"column",gap:2}}>
                {archivedChatFolders.length?archivedChatFolders.map(renderArchivedFolderOption):(
                  <div className="cf-empty-state" style={{padding:"12px 6px",textAlign:"center",color:S.text3,fontSize:11}}>No archived folders</div>
                )}
              </div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:2}}>
                {renderFolderOption({id:"folder-all",name:"All chats"})}
                <div style={{display:"flex",flexDirection:"column",gap:2,maxHeight:railFoldersExpanded?150:"none",overflowY:railFoldersExpanded&&hasExtraFolders?"auto":"visible",paddingRight:railFoldersExpanded&&hasExtraFolders?2:0}}>
                  {visibleChatFolders.map(renderFolderOption)}
                </div>
                {hasExtraFolders && (
                  <button
                    onClick={()=>setRailFoldersExpanded(v=>!v)}
                    aria-expanded={railFoldersExpanded}
                    title={railFoldersExpanded?"Collapse chat folders":"Show more chat folders"}
                    style={{width:"100%",marginTop:2,padding:"5px 7px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.cardBg,color:S.text3,fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,textAlign:"left"}}
                  >
                    <span>{railFoldersExpanded?"Show fewer folders":`Show ${hiddenFolderCount} more folder${hiddenFolderCount===1?"":"s"}`}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{transform:railFoldersExpanded?"rotate(180deg)":"rotate(0deg)",transition:"transform 0.15s",flexShrink:0}}><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                )}
              </div>
            )}
            {curChat && !railArchivedFoldersOpen && (
              <div style={{marginTop:7,paddingTop:7,borderTop:`1px solid ${S.border}`}}>
                <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.07em",textTransform:"uppercase",fontFamily:"monospace",color:S.text3,marginBottom:4}}>Move current chat</div>
                <select
                  value={moveFolderId}
                  onChange={e=>{const next=e.target.value;setChats(p=>p.map(c=>c.id===curChat.id?{...c,folderId:next}:c));setChatFolderId(next);showToast("Chat moved");}}
                  style={{width:"100%",padding:"6px 7px",borderRadius:8,border:`1px solid ${S.border2}`,background:S.cardBg,color:S.text2,fontFamily:"inherit",fontSize:11}}
                >
                  {moveFolderOptions.map(f=><option key={f.id} value={f.id}>{f.name}{archivedChatFolderIdSet.has(f.id)?" (archived)":""}</option>)}
                </select>
              </div>
            )}
          </div>
        )}

        <div>
          <div style={{fontSize:9,fontWeight:800,letterSpacing:"0.08em",textTransform:"uppercase",fontFamily:"monospace",color:S.text3,margin:"1px 0 5px"}}>
            {chatSearchActive?"Results":"Chats"}
          </div>
          <div style={{maxHeight:chatRailHistoryMaxHeight,overflowY:"auto",paddingRight:2}}>
            {chatSearchActive ? (
              chatSearchResults.length===0 ? (
                <div style={{padding:"14px 6px",textAlign:"center",color:S.text3,fontSize:11,lineHeight:1.45}}>No chats match "<strong style={{color:S.text2}}>{chatSearchDebounced}</strong>"</div>
              ) : (
                <>
                  {chatSearchVisible.map((r,i)=>{
                    const isFocus=i===Math.min(chatSearchFocusIdx,chatSearchVisible.length-1);
                    const isActive=r.chat.id===chatId;
                    return (
                      <div
                        key={r.chat.id}
                        onClick={()=>onPickChat(r.chat.id,r.chat.agentId,{scrollToMessageId:r.snippetMsgId})}
                        onMouseEnter={()=>setChatSearchFocusIdx(i)}
                        style={{padding:"6px 7px",borderRadius:8,cursor:"pointer",marginBottom:2,background:isActive?S.accentDim:(isFocus?S.hoverBg:"transparent"),border:`1px solid ${isActive?S.border3:(isFocus?S.border2:"transparent")}`}}
                      >
                        <div style={{fontSize:11,fontWeight:600,color:S.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginBottom:r.snippet?2:0}}>
                          <HighlightText text={r.chat.title||"Untitled"} needle={chatSearchDebounced} accent={S.accent}/>
                        </div>
                        {r.snippet && <div style={{fontSize:10,color:S.text3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}><HighlightText text={r.snippet} needle={chatSearchDebounced} accent={S.accent}/></div>}
                      </div>
                    );
                  })}
                  {chatSearchResults.length>50 && !chatSearchShowAll && (
                    <button onClick={()=>setChatSearchShowAll(true)} style={{width:"100%",padding:"6px 7px",marginTop:5,background:"transparent",border:`1px dashed ${S.border2}`,borderRadius:8,color:S.text2,fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>
                      Show {chatSearchResults.length-50} more
                    </button>
                  )}
                </>
              )
            ) : (
              <>
                {Object.entries(grp).map(([label,items])=>!items.length?null:(
                  <div key={label}>
                    <div style={{padding:"6px 3px 2px",fontSize:8,fontWeight:800,letterSpacing:"0.08em",textTransform:"uppercase",color:S.text3,fontFamily:"monospace"}}>{label==="Week"?"This Week":label}</div>
                    {items.map((c,i)=>{
                      const ag=agents.find(a=>a.id===c.agentId);
                      const isA=c.id===chatId;
                      const isGeneric=!!brandLogoActive&&(!ag?.emoji||ag?.emoji==="\uD83C\uDFF0"||ag?.id==="ag-default");
                      return <ChatSideRow key={c.id} index={i} chatId={c.id} agentId={c.agentId||"ag-default"} title={c.title} active={isA} isGenericAgent={isGeneric} agentEmoji={ag?.emoji} agentColor={ag?.color} brandLogoUrl={brandLogoActiveUrl} text={S.text} text2={S.text2} accent={S.accent} accentDim={S.accentDim} border3={S.border3} danger={S.danger} hoverBg={S.hoverBg} xIcon={IC.x} onPick={onPickChat} onDelete={onDeleteChat}/>;
                    })}
                  </div>
                ))}
                {!visibleChatHistory.length && <div className="cf-empty-state" style={{padding:"14px 6px",textAlign:"center",color:S.text3,fontSize:11}}>{activeChatFolderId==="folder-all"?"No chats yet":"No chats in this folder"}</div>}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Org-invite accept panel (from the invite email link) ─────────────────
  if (serverMode && !authUser && orgInviteTok) {
    const info = orgInviteInfo;
    const ready = inviteForm.name.trim() && inviteForm.p1.length >= 8 && inviteForm.p1 === inviteForm.p2 && !inviteForm.loading;
    const submitAccept = async () => {
      if (!ready) return;
      setInviteForm(p=>({...p,loading:true,err:""}));
      try {
        const r = await fetch("/api/auth/accept-org-invite", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ token: orgInviteTok, name: inviteForm.name.trim(), password: inviteForm.p1 }) });
        const d = await r.json().catch(()=>({}));
        if (!r.ok) { setInviteForm(p=>({...p,loading:false,err:d.error||"Couldn't accept the invite"})); return; }
        localStorage.setItem("cf_auth_token", d.token);
        setAuthToken(d.token);
        setAuthUser(d.user);
        setOrgInviteTok(null); setOrgInviteInfo(null); setInviteForm({name:"",p1:"",p2:"",err:"",loading:false});
        showToast(`Welcome to ${(info && info.orgName) || BN}!`);
      } catch { setInviteForm(p=>({...p,loading:false,err:"Network error — try again"})); }
    };
    const inputStyle = {width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"11px 14px",color:S.text,fontFamily:"inherit",fontSize:13};
    return (
      <div style={{height:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:S.bg,fontFamily:brandFontStack}}>
        <style>{`@import url('${BRAND_FONT_IMPORT}');:root{--cf-brand-font:${brandFontStack};}body,#root{font-family:var(--cf-brand-font);}*{box-sizing:border-box;margin:0;padding:0}input:focus{outline:none;border-color:${S.accent}!important;box-shadow:0 0 0 3px ${S.accentDim}!important;}`}</style>
        <div className="cf-auth-panel" style={{width:"100%",maxWidth:400,padding:"36px 36px 28px",borderRadius:20,background:S.surface,border:`1px solid ${S.border2}`,boxShadow:"0 18px 50px rgba(15,23,42,0.30)",margin:"0 16px"}}>
          <div style={{textAlign:"center",marginBottom:18}}>
            <div style={{fontSize:28,marginBottom:8}}>🎟️</div>
            <div style={{fontSize:16,fontWeight:800,color:S.text}}>You're invited</div>
            {info === null
              ? <div style={{fontSize:11,color:S.text3,marginTop:6}}>Checking your invite…</div>
              : info.valid
                ? <div style={{fontSize:11,color:S.text3,marginTop:6,lineHeight:1.5}}>Join <b style={{color:S.text2}}>{info.orgName || BN}</b> as <b style={{color:S.text2}}>{info.role}</b><br/>({info.email})</div>
                : <div style={{fontSize:11,color:S.danger,marginTop:6,lineHeight:1.5}}>This invite has expired or was revoked.<br/>Ask your admin for a new one.</div>}
          </div>
          {info && info.valid && (
            <>
              {inviteForm.err && <div style={{background:S.danger+"18",border:`1px solid ${S.danger}40`,borderRadius:8,padding:"8px 14px",marginBottom:12,fontSize:12,color:S.danger,textAlign:"center"}}>{inviteForm.err}</div>}
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <input placeholder="Your name" value={inviteForm.name} autoFocus
                  onChange={e=>setInviteForm(p=>({...p,name:e.target.value,err:""}))}
                  onKeyDown={e=>{if(e.key==="Enter")submitAccept();}} style={inputStyle}/>
                <input type="password" placeholder="Choose a password (8+ characters)" value={inviteForm.p1}
                  onChange={e=>setInviteForm(p=>({...p,p1:e.target.value,err:""}))}
                  onKeyDown={e=>{if(e.key==="Enter")submitAccept();}} style={inputStyle}/>
                <input type="password" placeholder="Repeat password" value={inviteForm.p2}
                  onChange={e=>setInviteForm(p=>({...p,p2:e.target.value,err:""}))}
                  onKeyDown={e=>{if(e.key==="Enter")submitAccept();}} style={inputStyle}/>
                {inviteForm.p2 && inviteForm.p1 !== inviteForm.p2 && <div style={{fontSize:11,color:S.danger}}>Passwords don't match</div>}
                <button onClick={submitAccept} disabled={!ready}
                  style={{padding:"12px",borderRadius:10,border:"none",background:!ready?S.border3:`linear-gradient(135deg,${S.accent},${S.accent2})`,color:"#fff",fontSize:13,fontWeight:700,cursor:!ready?"default":"pointer",fontFamily:"inherit"}}>
                  {inviteForm.loading?"Creating your account…":"Accept & join"}
                </button>
              </div>
            </>
          )}
          <div style={{textAlign:"center",marginTop:14,fontSize:11}}>
            <button onClick={()=>{setOrgInviteTok(null);setOrgInviteInfo(null);}} style={{background:"none",border:"none",color:S.text3,fontSize:11,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>Back to sign in</button>
          </div>
        </div>
      </div>
    );
  }

  // ── MFA code panel (password accepted, second factor pending) ────────────
  if (serverMode && !authUser && mfaChallenge) {
    const ready = /^[0-9A-Za-z-]{6,}$/.test(mfaForm.code.trim()) && !mfaForm.loading;
    const submitMfa = async () => {
      if (!ready) return;
      setMfaForm(p=>({...p,loading:true,err:""}));
      try {
        const r = await fetch("/api/auth/mfa/verify", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ mfaToken: mfaChallenge, code: mfaForm.code.trim() }) });
        const d = await r.json().catch(()=>({}));
        if (!r.ok) {
          if (r.status === 401 && /expired/i.test(d.error || "")) { setMfaChallenge(null); setMfaForm({code:"",err:"",loading:false}); showToast(d.error); return; }
          setMfaForm(p=>({...p,loading:false,err:d.error||"That code didn't work"})); return;
        }
        localStorage.setItem("cf_auth_token", d.token);
        setAuthToken(d.token);
        setAuthUser(d.user);
        setMfaChallenge(null); setMfaForm({code:"",err:"",loading:false});
        fetch("/api/branding", { headers: { "Authorization": `Bearer ${d.token}` }})
          .then(r2 => r2.ok ? r2.json() : null).then(applyBrandingFromServer).catch(()=>{});
      } catch (e) { setMfaForm(p=>({...p,loading:false,err:"Network error — try again"})); }
    };
    return (
      <div style={{height:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:S.bg,fontFamily:brandFontStack}}>
        <style>{`@import url('${BRAND_FONT_IMPORT}');:root{--cf-brand-font:${brandFontStack};}body,#root{font-family:var(--cf-brand-font);}*{box-sizing:border-box;margin:0;padding:0;}input:focus{outline:none;border-color:${S.accent}!important;box-shadow:0 0 0 3px ${S.accentDim}!important;}`}</style>
        <div className="cf-auth-panel" style={{width:"100%",maxWidth:400,padding:"36px 36px 28px",borderRadius:20,background:S.surface,border:`1px solid ${S.border2}`,boxShadow:"0 24px 80px rgba(0,0,0,0.3)",margin:"0 16px"}}>
          <div style={{textAlign:"center",marginBottom:18}}>
            <div style={{fontSize:28,marginBottom:8}}>🔐</div>
            <div style={{fontSize:16,fontWeight:800,color:S.text}}>Two-factor authentication</div>
            <div style={{fontSize:11,color:S.text3,marginTop:5,lineHeight:1.5}}>Enter the 6-digit code from your authenticator app, or one of your recovery codes.</div>
          </div>
          {mfaForm.err && <div style={{background:S.danger+"18",border:`1px solid ${S.danger}40`,borderRadius:8,padding:"8px 14px",marginBottom:12,fontSize:12,color:S.danger,textAlign:"center"}}>{mfaForm.err}</div>}
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <input inputMode="numeric" autoComplete="one-time-code" placeholder="123456" value={mfaForm.code} autoFocus
              onChange={e=>setMfaForm(p=>({...p,code:e.target.value,err:""}))}
              onKeyDown={e=>{if(e.key==="Enter")submitMfa();}}
              style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"12px 14px",color:S.text,fontFamily:"monospace",fontSize:18,textAlign:"center",letterSpacing:"0.3em"}}/>
            <button onClick={submitMfa} disabled={!ready}
              style={{padding:"12px",borderRadius:10,border:"none",background:!ready?S.border3:`linear-gradient(135deg,${S.accent},${S.accent2})`,color:"#fff",fontSize:13,fontWeight:700,cursor:!ready?"default":"pointer",fontFamily:"inherit"}}>
              {mfaForm.loading?"Verifying…":"Verify"}
            </button>
          </div>
          <div style={{textAlign:"center",marginTop:14,fontSize:11}}>
            <button onClick={()=>{setMfaChallenge(null);setMfaForm({code:"",err:"",loading:false});}} style={{background:"none",border:"none",color:S.text3,fontSize:11,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>Back to sign in</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Forgot / reset password panels (server mode, not yet authenticated) ──
  if (serverMode && !authUser && (resetTok || forgotView)) {
    const panel = (children) => (
      <div style={{height:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:S.bg,fontFamily:brandFontStack}}>
        <style>{`@import url('${BRAND_FONT_IMPORT}');:root{--cf-brand-font:${brandFontStack};}body,#root{font-family:var(--cf-brand-font);}*{box-sizing:border-box;margin:0;padding:0;}input:focus{outline:none;border-color:${S.accent}!important;box-shadow:0 0 0 3px ${S.accentDim}!important;}`}</style>
        <div className="cf-auth-panel" style={{width:"100%",maxWidth:400,padding:"36px 36px 28px",borderRadius:20,background:S.surface,border:`1px solid ${S.border2}`,boxShadow:"0 24px 80px rgba(0,0,0,0.3)",margin:"0 16px"}}>
          <div style={{textAlign:"center",marginBottom:20}}>
            <div style={{fontSize:22,fontWeight:800,color:S.text,letterSpacing:"-0.02em"}}>{BN}</div>
          </div>
          {children}
        </div>
      </div>
    );
    const inputStyle = {width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"11px 14px",color:S.text,fontFamily:"inherit",fontSize:13};
    const primaryBtn = (disabled) => ({padding:"12px",borderRadius:10,border:"none",background:disabled?S.border3:`linear-gradient(135deg,${S.accent},${S.accent2})`,color:"#fff",fontSize:13,fontWeight:700,cursor:disabled?"default":"pointer",fontFamily:"inherit",marginTop:4});
    const backLink = (
      <div style={{textAlign:"center",marginTop:14,fontSize:11}}>
        <button onClick={()=>{setForgotView(null);setResetTok(null);setResetForm({p1:"",p2:"",err:"",loading:false});}} style={{background:"none",border:"none",color:S.text3,fontSize:11,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>Back to sign in</button>
      </div>
    );
    if (resetTok) {
      const ready = resetForm.p1.length >= 8 && resetForm.p1 === resetForm.p2 && !resetForm.loading;
      const submitReset = async () => {
        if (!ready) return;
        setResetForm(p=>({...p,loading:true,err:""}));
        try {
          const r = await fetch("/api/auth/reset", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ token: resetTok, password: resetForm.p1 }) });
          const d = await r.json().catch(()=>({}));
          if (!r.ok) { setResetForm(p=>({...p,loading:false,err:d.error||"Could not reset password"})); return; }
          setResetTok(null); setResetForm({p1:"",p2:"",err:"",loading:false}); setForgotView(null);
          showToast("Password updated — sign in with your new password");
        } catch (e) { setResetForm(p=>({...p,loading:false,err:"Network error — try again"})); }
      };
      return panel(
        <>
          <div style={{fontSize:13,fontWeight:700,color:S.text,marginBottom:4,textAlign:"center"}}>Set a new password</div>
          <div style={{fontSize:11,color:S.text3,marginBottom:14,textAlign:"center"}}>Minimum 8 characters.</div>
          {resetForm.err && <div style={{background:S.danger+"18",border:`1px solid ${S.danger}40`,borderRadius:8,padding:"8px 14px",marginBottom:12,fontSize:12,color:S.danger,textAlign:"center"}}>{resetForm.err}</div>}
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <input type="password" placeholder="New password" value={resetForm.p1} autoFocus
              onChange={e=>setResetForm(p=>({...p,p1:e.target.value,err:""}))}
              onKeyDown={e=>{if(e.key==="Enter")submitReset();}} style={inputStyle}/>
            <input type="password" placeholder="Repeat new password" value={resetForm.p2}
              onChange={e=>setResetForm(p=>({...p,p2:e.target.value,err:""}))}
              onKeyDown={e=>{if(e.key==="Enter")submitReset();}} style={inputStyle}/>
            {resetForm.p2 && resetForm.p1 !== resetForm.p2 && <div style={{fontSize:11,color:S.danger}}>Passwords don't match</div>}
            <button onClick={submitReset} disabled={!ready} style={primaryBtn(!ready)}>{resetForm.loading?"Saving…":"Save new password"}</button>
          </div>
          {backLink}
        </>
      );
    }
    if (forgotView === "sent") {
      return panel(
        <>
          <div style={{fontSize:28,textAlign:"center",marginBottom:10}}>📬</div>
          <div style={{fontSize:13,fontWeight:700,color:S.text,marginBottom:6,textAlign:"center"}}>Check your email</div>
          <div style={{fontSize:12,color:S.text2,lineHeight:1.6,textAlign:"center"}}>If an account exists for <b>{forgotEmail}</b>, a reset link is on its way. It expires in 1 hour.</div>
          {backLink}
        </>
      );
    }
    const submitForgot = async () => {
      const em = forgotEmail.trim();
      if (!em) return;
      try { await fetch("/api/auth/forgot", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ email: em }) }); } catch {}
      setForgotView("sent");
    };
    return panel(
      <>
        <div style={{fontSize:13,fontWeight:700,color:S.text,marginBottom:4,textAlign:"center"}}>Reset your password</div>
        <div style={{fontSize:11,color:S.text3,marginBottom:14,textAlign:"center"}}>Enter your account email and we'll send a reset link.</div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <input type="email" placeholder="Email address" value={forgotEmail} autoFocus
            onChange={e=>setForgotEmail(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter")submitForgot();}} style={inputStyle}/>
          <button onClick={submitForgot} disabled={!forgotEmail.trim()} style={primaryBtn(!forgotEmail.trim())}>Send reset link</button>
        </div>
        {backLink}
      </>
    );
  }

  // ── Login / signup screen (server mode, not yet authenticated) ───────────
  if (serverMode && !authUser) {
    const handleLoginKey = (e) => { if (e.key === "Enter") doLogin(loginForm.email, loginForm.password); };
    const handleSignupKey = (e) => { if (e.key === "Enter") doSignup(signupForm.orgName, signupForm.name, signupForm.email, signupForm.password); };
    const tabBtn = (active) => ({
      flex:1, padding:"10px", borderRadius:0, border:"none",
      background: active ? S.accentDim : "transparent",
      color: active ? S.accent : S.text3,
      fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
      borderBottom: active ? `2px solid ${S.accent}` : `2px solid transparent`,
      transition: "background 160ms ease, color 160ms ease, border-color 240ms ease",
    });
    const signupReady = signupForm.orgName.trim() && signupForm.name.trim() && signupForm.email.trim() && signupForm.password.length >= 8;
    return (
      <div style={{height:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:S.bg,fontFamily:brandFontStack}}>
        <style>{`@import url('${BRAND_FONT_IMPORT}');:root{--cf-brand-font:${brandFontStack};}body,#root{font-family:var(--cf-brand-font);}*{box-sizing:border-box;margin:0;padding:0;}input:focus{outline:none;border-color:${S.accent}!important;box-shadow:0 0 0 3px ${S.accentDim}!important;}`}</style>
        <div className="cf-auth-panel" style={{width:"100%",maxWidth:400,padding:"36px 36px 28px",borderRadius:20,background:S.surface,border:`1px solid ${S.border2}`,boxShadow:"0 24px 80px rgba(0,0,0,0.3)",margin:"0 16px"}}>
          {/* Logo + title */}
          <div style={{textAlign:"center",marginBottom:24}}>
            <div style={{width:60,height:60,borderRadius:18,background:brandLogoActive?"transparent":`linear-gradient(135deg,${S.accent},${S.accent2})`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px",boxShadow:`0 8px 24px ${S.accentDim}`,overflow:"hidden"}}>
              {brandLogoActive ? <img src={brandLogoActiveUrl} alt={BN} style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <CFLogo size={36}/>}
            </div>
            <div style={{fontSize:22,fontWeight:800,color:S.text,letterSpacing:"-0.02em"}}>{BN}</div>
            <div style={{fontSize:12,color:S.text3,marginTop:5}}>{authMode==="login" ? "Sign in to your account" : "Create a new organization"}</div>
          </div>
          {/* Tabs */}
          <div style={{display:"flex",marginBottom:18,borderBottom:`1px solid ${S.border2}`}}>
            <button className="cf-auth-tab" onClick={()=>setAuthMode("login")} style={tabBtn(authMode==="login")}>Log in</button>
            <button className="cf-auth-tab" onClick={()=>setAuthMode("signup")} style={tabBtn(authMode==="signup")}>Sign up</button>
          </div>
          {authMode === "login" ? (
            <>
              {loginForm.err && <div style={{background:S.danger+"18",border:`1px solid ${S.danger}40`,borderRadius:8,padding:"8px 14px",marginBottom:14,fontSize:12,color:S.danger,textAlign:"center"}}>{loginForm.err}</div>}
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <input type="email" placeholder="Email address" value={loginForm.email}
                  onChange={e=>setLoginForm(p=>({...p,email:e.target.value,err:""}))} onKeyDown={handleLoginKey}
                  autoFocus style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"11px 14px",color:S.text,fontFamily:"inherit",fontSize:13}}/>
                <input type="password" placeholder="Password" value={loginForm.password}
                  onChange={e=>setLoginForm(p=>({...p,password:e.target.value,err:""}))} onKeyDown={handleLoginKey}
                  style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"11px 14px",color:S.text,fontFamily:"inherit",fontSize:13}}/>
                <button className="cf-auth-submit" onClick={()=>doLogin(loginForm.email,loginForm.password)}
                  disabled={loginForm.loading||!loginForm.email||!loginForm.password}
                  style={{padding:"12px",borderRadius:10,border:"none",background:loginForm.loading||!loginForm.email||!loginForm.password?S.border3:`linear-gradient(135deg,${S.accent},${S.accent2})`,color:"#fff",fontSize:13,fontWeight:700,cursor:loginForm.loading||!loginForm.email||!loginForm.password?"default":"pointer",fontFamily:"inherit",marginTop:4,display:"inline-flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  {loginForm.loading && (
                    <span className="cf-auth-spinner" style={{display:"inline-flex"}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,0.3)" strokeWidth="3"/><path d="M21 12a9 9 0 0 0-9-9" stroke="#fff" strokeWidth="3" strokeLinecap="round"/></svg>
                    </span>
                  )}
                  {loginForm.loading?"Signing in…":"Sign in"}
                </button>
              </div>
            </>
          ) : (
            <>
              {signupForm.err && <div style={{background:S.danger+"18",border:`1px solid ${S.danger}40`,borderRadius:8,padding:"8px 14px",marginBottom:14,fontSize:12,color:S.danger,textAlign:"center"}}>{signupForm.err}</div>}
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <input type="text" placeholder="Company / team name" value={signupForm.orgName}
                  onChange={e=>setSignupForm(p=>({...p,orgName:e.target.value,err:""}))} onKeyDown={handleSignupKey}
                  autoFocus style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"11px 14px",color:S.text,fontFamily:"inherit",fontSize:13}}/>
                <input type="text" placeholder="Your name" value={signupForm.name}
                  onChange={e=>setSignupForm(p=>({...p,name:e.target.value,err:""}))} onKeyDown={handleSignupKey}
                  style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"11px 14px",color:S.text,fontFamily:"inherit",fontSize:13}}/>
                <input type="email" placeholder="Work email" value={signupForm.email}
                  onChange={e=>setSignupForm(p=>({...p,email:e.target.value,err:""}))} onKeyDown={handleSignupKey}
                  style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"11px 14px",color:S.text,fontFamily:"inherit",fontSize:13}}/>
                <input type="password" placeholder="Password (8+ characters)" value={signupForm.password}
                  onChange={e=>setSignupForm(p=>({...p,password:e.target.value,err:""}))} onKeyDown={handleSignupKey}
                  style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"11px 14px",color:S.text,fontFamily:"inherit",fontSize:13}}/>
                <button className="cf-auth-submit" onClick={()=>doSignup(signupForm.orgName,signupForm.name,signupForm.email,signupForm.password)}
                  disabled={signupForm.loading||!signupReady}
                  style={{padding:"12px",borderRadius:10,border:"none",background:signupForm.loading||!signupReady?S.border3:`linear-gradient(135deg,${S.accent},${S.accent2})`,color:"#fff",fontSize:13,fontWeight:700,cursor:signupForm.loading||!signupReady?"default":"pointer",fontFamily:"inherit",marginTop:4,display:"inline-flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  {signupForm.loading && (
                    <span className="cf-auth-spinner" style={{display:"inline-flex"}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,0.3)" strokeWidth="3"/><path d="M21 12a9 9 0 0 0-9-9" stroke="#fff" strokeWidth="3" strokeLinecap="round"/></svg>
                    </span>
                  )}
                  {signupForm.loading?"Creating your workspace…":"Create workspace"}
                </button>
                <div style={{fontSize:10,color:S.text3,textAlign:"center",marginTop:2}}>You'll be the admin for your org. Starts on a free trial.</div>
              </div>
            </>
          )}
          {authMode === "login" && (
            <div style={{textAlign:"center",marginTop:14}}>
              <button className="cf-auth-sso" onClick={()=>{
                const orgId = window.prompt("Enter your organization ID (given by your admin)");
                if (orgId && orgId.trim()) window.location.href = "/api/auth/oidc/start?org=" + encodeURIComponent(orgId.trim()) + "&next=" + encodeURIComponent(window.location.pathname);
              }} style={{display:"inline-flex",alignItems:"center",gap:7,padding:"7px 14px",borderRadius:9,border:`1px solid ${S.border2}`,background:S.inputBg,color:S.text2,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                <span style={{fontSize:13}}>🔑</span> Sign in with SSO
              </button>
              <div style={{marginTop:10,fontSize:11}}>
                <button onClick={()=>{setForgotEmail(loginForm.email||"");setForgotView("request");}} style={{background:"none",border:"none",color:S.text3,fontSize:11,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>Forgot password?</button>
              </div>
            </div>
          )}
          <div style={{textAlign:"center",marginTop:16,fontSize:11,color:S.text3}}>
            <button onClick={()=>setShowPricing(true)} style={{background:"none",border:"none",color:S.text3,fontSize:11,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>View pricing</button>
          </div>
          <div style={{textAlign:"center",marginTop:8,fontSize:11,color:S.text3}}>🔒 Your session is encrypted · Zero retention</div>
          <div style={{textAlign:"center",marginTop:18,paddingTop:14,borderTop:`1px solid ${S.border2}`,fontSize:10,color:S.text3,fontFamily:"monospace",letterSpacing:"0.04em"}}>
            <span style={{opacity:0.85}}>Built by</span>{" "}
            <a href="https://chatfortai.com" target="_blank" rel="noreferrer" style={{color:S.text2,textDecoration:"none",fontWeight:600}}>ChatFortAI</a>
            <span style={{opacity:0.5}}> · </span>
            <span style={{opacity:0.85}}>Zero-retention AI · SOC 2 in progress</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Forced password change (temp password flow) ──────────────────────────
  if (serverMode && authUser && authUser.mustChangePassword) {
    const handlePwKey = (e) => { if (e.key === "Enter") submitPwChange(); };
    return (
      <div style={{height:"100dvh",display:"flex",alignItems:"center",justifyContent:"center",background:S.bg,fontFamily:brandFontStack}}>
        <style>{`@import url('${BRAND_FONT_IMPORT}');:root{--cf-brand-font:${brandFontStack};}body,#root{font-family:var(--cf-brand-font);}*{box-sizing:border-box;margin:0;padding:0;}input:focus{outline:none;border-color:${S.accent}!important;box-shadow:0 0 0 3px ${S.accentDim}!important;}`}</style>
        <div style={{width:"100%",maxWidth:400,padding:36,borderRadius:20,background:S.surface,border:`1px solid ${S.border2}`,boxShadow:"0 24px 80px rgba(0,0,0,0.3)",margin:"0 16px"}}>
          <div style={{textAlign:"center",marginBottom:24}}>
            <div style={{width:60,height:60,borderRadius:18,background:brandLogoActive?"transparent":`linear-gradient(135deg,${S.accent},${S.accent2})`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px",boxShadow:`0 8px 24px ${S.accentDim}`,fontSize:28,overflow:"hidden",position:"relative"}}>
              {brandLogoActive ? <img src={brandLogoActiveUrl} alt={BN} style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <span>🔐</span>}
              {brandLogoActive && <span style={{position:"absolute",bottom:-2,right:-2,fontSize:20,background:S.surface,borderRadius:"50%",width:24,height:24,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 6px rgba(0,0,0,0.2)"}}>🔐</span>}
            </div>
            <div style={{fontSize:20,fontWeight:800,color:S.text,letterSpacing:"-0.02em"}}>Set a new password</div>
            <div style={{fontSize:12,color:S.text3,marginTop:6,lineHeight:1.5}}>Welcome to <strong style={{color:S.text2}}>{BN}</strong>, <strong style={{color:S.text2}}>{authUser.name}</strong>. You were given a temporary password. Please choose your own to continue.</div>
          </div>
          {pwChange.err && (
            <div style={{background:S.danger+"18",border:`1px solid ${S.danger}40`,borderRadius:8,padding:"8px 14px",marginBottom:14,fontSize:12,color:S.danger,textAlign:"center"}}>{pwChange.err}</div>
          )}
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div>
              <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>CURRENT (TEMP) PASSWORD</div>
              <input type="password" value={pwChange.current} onChange={e=>setPwChange(p=>({...p,current:e.target.value,err:""}))} onKeyDown={handlePwKey} autoFocus
                style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"11px 14px",color:S.text,fontFamily:"inherit",fontSize:13}}/>
            </div>
            <div>
              <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>NEW PASSWORD <span style={{color:S.text3+"99"}}>(min 8 chars)</span></div>
              <input type="password" value={pwChange.next} onChange={e=>setPwChange(p=>({...p,next:e.target.value,err:""}))} onKeyDown={handlePwKey}
                style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"11px 14px",color:S.text,fontFamily:"inherit",fontSize:13}}/>
            </div>
            <div>
              <div style={{fontSize:10,color:S.text3,fontFamily:"monospace",marginBottom:4}}>CONFIRM NEW PASSWORD</div>
              <input type="password" value={pwChange.confirm} onChange={e=>setPwChange(p=>({...p,confirm:e.target.value,err:""}))} onKeyDown={handlePwKey}
                style={{width:"100%",background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:9,padding:"11px 14px",color:S.text,fontFamily:"inherit",fontSize:13}}/>
            </div>
            <button onClick={submitPwChange} disabled={pwChange.loading}
              style={{padding:"12px",borderRadius:10,border:"none",background:pwChange.loading?S.border3:`linear-gradient(135deg,${S.accent},${S.accent2})`,color:"#fff",fontSize:13,fontWeight:700,cursor:pwChange.loading?"default":"pointer",fontFamily:"inherit",marginTop:6}}>
              {pwChange.loading?"Saving…":"Update password & continue"}
            </button>
            <button onClick={doLogout} style={{padding:"8px",borderRadius:8,border:"none",background:"transparent",color:S.text3,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Sign out instead</button>
          </div>
        </div>
      </div>
    );
  }

  return(
    <div className="cf-brand-font-scope" style={{display:"flex",height:"100dvh",maxHeight:"100dvh",width:"100vw",maxWidth:"100vw",background:S.bg,color:S.text,fontFamily:brandFontStack,fontSize:14,overflow:"hidden",position:"relative"}}>
      <style>{`@import url('${BRAND_FONT_IMPORT}');:root{--cf-brand-font:${brandFontStack};}html,body,#root,.cf-brand-font-scope{font-family:var(--cf-brand-font)!important;}.cf-brand-font-scope,.cf-brand-font-scope :where(button,input,textarea,select,optgroup,option,a,p,span,div,label,strong,em,h1,h2,h3,h4,h5,h6,li,td,th,small){font-family:var(--cf-brand-font)!important;}.cf-brand-font-scope :where(pre,code,kbd,samp,.cf-code-block,.cf-code-block *,.cf-terminal,.cf-terminal *){font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace!important;}@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}@keyframes blink{0%,100%{opacity:1}50%{opacity:0}}@keyframes cfPreviewIn{from{opacity:0;transform:translateX(24px)}to{opacity:1;transform:translateX(0)}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}@keyframes voicePulse{0%{transform:scale(1);opacity:0.6}100%{transform:scale(1.15);opacity:0}}@keyframes voiceBar{from{transform:scaleY(0.4)}to{transform:scaleY(1)}}@keyframes searchFlash{0%{background:${S.accent}55}40%{background:${S.accent}33}100%{background:transparent}}.searchHighlightFlash{animation:searchFlash 1s ease-out;border-radius:14px;}*{box-sizing:border-box;margin:0;padding:0;}::-webkit-scrollbar{width:4px;}::-webkit-scrollbar-thumb{background:${S.border2};border-radius:2px;}input:focus,textarea:focus,select:focus{outline:none;border-color:${S.accent}!important;box-shadow:0 0 0 3px ${S.accentDim}!important;}select option{background:${S.surface};color:${S.text};}.cf-rail-resizer::after{content:"";position:absolute;top:10px;bottom:10px;right:3px;width:2px;border-radius:2px;background:${S.accent};opacity:0;transition:opacity .12s ease,background .12s ease;}.cf-rail-resizer:hover::after,.cf-rail-resizer[data-active="true"]::after{opacity:.75;}
/* Polish round 1 -- foundation. Layout-neutral CSS only. */
*:focus-visible{outline:2px solid ${S.accent}!important;outline-offset:2px;border-radius:4px;}
button:focus-visible,a:focus-visible{outline-offset:3px;}
::-webkit-scrollbar:hover{width:8px;}
::-webkit-scrollbar-thumb:hover{background:${S.accent};}
::selection{background:${S.accent}40;color:${S.text};}
.cf-card-hover{position:relative;overflow:hidden;transition:transform .18s cubic-bezier(0.16,1,0.3,1),box-shadow .18s ease,border-color .18s ease;}
.cf-card-hover:hover{transform:translateY(-2px);border-color:${S.accent}!important;box-shadow:0 8px 24px rgba(0,0,0,0.10),0 2px 4px rgba(0,0,0,0.06);}
.cf-card-hover::after{content:"";position:absolute;inset:0;background:linear-gradient(120deg,transparent 30%,${S.accent}10 50%,transparent 70%);transform:translateX(-100%);transition:transform .55s ease;pointer-events:none;}
.cf-card-hover:hover::after{transform:translateX(100%);}
@keyframes cfShimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}
.cf-shimmer{background:linear-gradient(90deg,${S.surface2} 0%,${S.border} 50%,${S.surface2} 100%);background-size:800px 100%;animation:cfShimmer 1.4s linear infinite;border-radius:8px;}
@keyframes cfPulseSlow{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.55;transform:scale(0.92)}}
.cf-pulse-slow{animation:cfPulseSlow 2.2s ease-in-out infinite;}
.cf-press{transition:transform .08s ease;}
.cf-press:active{transform:scale(0.97);}
.cf-link{color:${S.accent};text-decoration:none;transition:color .12s ease;}
.cf-link:hover{text-decoration:underline;}
button:disabled,button[aria-disabled="true"]{cursor:not-allowed!important;opacity:0.45;}
.cf-setup-card{overflow:visible;}
.cf-setup-label{min-width:0;}
@media (max-width:640px),(max-height:720px){
  .cf-setup-card{margin-top:clamp(58px,12dvh,96px)!important;max-width:calc(100vw - 20px)!important;padding:7px 8px!important;}
  .cf-setup-brand{display:none!important;}
  .cf-setup-step-done,.cf-setup-step-skip{display:none!important;}
  .cf-setup-step{font-size:10px!important;gap:5px!important;}
  .cf-setup-action{padding:1px 6px!important;font-size:9.5px!important;}
}
@media (max-width:420px){
  .cf-setup-card{max-width:calc(100vw - 12px)!important;}
  .cf-setup-head{margin-bottom:4px!important;}
  .cf-setup-label{max-width:100%;}
}
@media (prefers-reduced-motion: reduce){
  *,*::before,*::after{animation-duration:.001ms!important;animation-iteration-count:1!important;transition-duration:.001ms!important;}
}
.cf-skip-link{position:fixed;top:-40px;left:8px;background:${S.accent};color:#fff;padding:8px 14px;border-radius:6px;text-decoration:none;font-size:13px;font-weight:600;z-index:9999;transition:top .15s ease;}
.cf-skip-link:focus{top:8px;}
/* Polish round 2 -- class-targeted hovers + animations. */
.cf-code-block{transition:box-shadow .15s ease,border-color .15s ease;}
.cf-code-block:hover{box-shadow:0 4px 12px rgba(0,0,0,0.10);border-color:${S.border3}!important;}
.cf-code-block:hover .cf-code-actions{opacity:1;}
.cf-code-actions{opacity:0.6;transition:opacity .15s ease;}
.cf-empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;animation:cfFadeIn .35s ease-out;}
@keyframes cfFadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
.cf-page-card{position:relative;overflow:hidden;transition:transform .18s cubic-bezier(0.16,1,0.3,1),border-color .18s ease,box-shadow .18s ease;}
.cf-page-card:hover{transform:translateY(-2px);border-color:${S.accent}!important;box-shadow:0 6px 18px rgba(0,0,0,0.08);}
.cf-page-card::after{content:"";position:absolute;inset:0;background:linear-gradient(120deg,transparent 30%,${S.accent}10 50%,transparent 70%);transform:translateX(-100%);transition:transform .55s ease;pointer-events:none;}
.cf-page-card:hover::after{transform:translateX(100%);}
.cf-pricing-card{position:relative;overflow:hidden;transition:transform .2s cubic-bezier(0.16,1,0.3,1),box-shadow .2s ease,border-color .2s ease;}
.cf-pricing-card:hover{transform:translateY(-3px);box-shadow:0 12px 32px rgba(0,0,0,0.10);border-color:${S.accent}80!important;}
.cf-pricing-card::after{content:"";position:absolute;inset:0;background:linear-gradient(120deg,transparent 30%,${S.accent}10 50%,transparent 70%);transform:translateX(-100%);transition:transform .55s ease;pointer-events:none;}
.cf-pricing-card:hover::after{transform:translateX(100%);}
.cf-attach-chip{animation:cfChipIn .22s cubic-bezier(0.16,1,0.3,1);}
@keyframes cfChipIn{from{opacity:0;transform:scale(0.85)}to{opacity:1;transform:scale(1)}}
.cf-send-btn{transition:transform .12s ease,box-shadow .15s ease;}
.cf-send-btn:not([data-busy="true"]):hover{transform:scale(1.05);}
.cf-send-btn[data-busy="true"]{box-shadow:0 0 0 0 ${S.accent}66;animation:cfSendBusy 1.4s ease-in-out infinite;}
@keyframes cfSendBusy{0%,100%{box-shadow:0 0 0 0 ${S.accent}66}50%{box-shadow:0 0 0 8px ${S.accent}00}}
.cf-jump-pill[data-visible="true"]{animation:cfJumpBob 2.4s ease-in-out infinite;}
@keyframes cfJumpBob{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}
.cf-hero-starter{position:relative;overflow:hidden;transition:transform .18s cubic-bezier(0.16,1,0.3,1),border-color .18s ease;}
.cf-hero-starter:hover{transform:translateY(-2px);border-color:${S.accent}!important;}
.cf-hero-starter::after{content:"";position:absolute;inset:0;background:linear-gradient(120deg,transparent 30%,${S.accent}10 50%,transparent 70%);transform:translateX(-100%);transition:transform .55s ease;pointer-events:none;}
.cf-hero-starter:hover::after{transform:translateX(100%);}
.cf-mic-btn[data-dictating="true"]{box-shadow:0 0 0 0 ${S.danger}66;animation:cfMicPulse 1s ease-in-out infinite;}
@keyframes cfMicPulse{0%,100%{box-shadow:0 0 0 0 ${S.danger}66}50%{box-shadow:0 0 0 8px ${S.danger}00}}
.cf-section-enter{animation:cfSectionUp .28s cubic-bezier(0.16,1,0.3,1);}
@keyframes cfSectionUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
/* Polish round 3 -- sidebar / toast / pickers / chat polish. */
.cf-provider-badge{transition:transform .12s ease,filter .12s ease;}
.cf-provider-badge:hover{transform:scale(1.06);filter:brightness(1.1);}
.cf-sidebar-row{position:relative;overflow:hidden;transition:background .12s ease,padding-left .12s ease;}
.cf-sidebar-row:hover{padding-left:calc(var(--cf-sb-pad,12px) + 2px);}
.cf-sidebar-row::after{content:"";position:absolute;inset:0;background:linear-gradient(120deg,transparent 30%,${S.accent}10 50%,transparent 70%);transform:translateX(-100%);transition:transform .55s ease;pointer-events:none;}
.cf-sidebar-row:hover::after{transform:translateX(100%);}
/* Topbar pills (model dropdown, user pill) get the same sweep + lift. */
.cf-topbar-pill{position:relative;overflow:hidden;transition:transform .14s cubic-bezier(0.16,1,0.3,1),border-color .14s ease,box-shadow .14s ease;}
.cf-topbar-pill:hover{transform:translateY(-1px);border-color:${S.accent}!important;box-shadow:0 4px 12px rgba(0,0,0,0.10);}
.cf-topbar-pill::after{content:"";position:absolute;inset:0;background:linear-gradient(120deg,transparent 30%,${S.accent}10 50%,transparent 70%);transform:translateX(-100%);transition:transform .55s ease;pointer-events:none;}
.cf-topbar-pill:hover::after{transform:translateX(100%);}
/* Marketplace + admin integration cards opt in via .cf-tile-hover --
   same treatment as .cf-page-card. */
.cf-tile-hover{position:relative;overflow:hidden;transition:transform .18s cubic-bezier(0.16,1,0.3,1),border-color .18s ease,box-shadow .18s ease;}
.cf-tile-hover:hover{transform:translateY(-2px);border-color:${S.accent}!important;box-shadow:0 6px 18px rgba(0,0,0,0.08);}
.cf-tile-hover::after{content:"";position:absolute;inset:0;background:linear-gradient(120deg,transparent 30%,${S.accent}10 50%,transparent 70%);transform:translateX(-100%);transition:transform .55s ease;pointer-events:none;}
.cf-tile-hover:hover::after{transform:translateX(100%);}
/* Rail nav rows: keep the shimmer + accent border, drop the lift +
   shadow so a column of rows doesn't bounce on hover. */
.cf-rail .cf-tile-hover:hover{transform:none;box-shadow:none;}
.cf-toast,[class*="cf-toast-"]{animation:cfToastIn .28s cubic-bezier(0.16,1,0.3,1);}
@keyframes cfToastIn{from{opacity:0;transform:translateY(-8px) scale(0.96)}to{opacity:1;transform:translateY(0) scale(1)}}
.cf-audit-row:nth-child(even),.cf-owner-row:nth-child(even){background:${S.surface}80;}
.cf-audit-row:hover,.cf-owner-row:hover{background:${S.accentDim}!important;}
.cf-audit-row,.cf-owner-row{transition:background .12s ease;position:relative;overflow:hidden;}
.cf-audit-row::after,.cf-owner-row::after{content:"";position:absolute;inset:0;background:linear-gradient(120deg,transparent 30%,${S.accent}10 50%,transparent 70%);transform:translateX(-100%);transition:transform .55s ease;pointer-events:none;}
.cf-audit-row:hover::after,.cf-owner-row:hover::after{transform:translateX(100%);}
.cf-color-swatch{transition:transform .14s cubic-bezier(0.16,1,0.3,1),box-shadow .14s ease;}
.cf-color-swatch:hover{transform:scale(1.15);}
.cf-color-swatch:focus-visible{box-shadow:0 0 0 3px ${S.accentDim};outline:none!important;}
.cf-emoji-tile{transition:transform .12s ease,background .12s ease;}
.cf-emoji-tile:hover{transform:scale(1.18);}
.cf-lang-dot{display:inline-block;width:6px;height:6px;border-radius:50%;background:${S.accent};margin-right:5px;flex-shrink:0;}
.cf-cta{position:relative;overflow:hidden;}
.cf-cta::before{content:"";position:absolute;inset:0;background:linear-gradient(120deg,transparent 30%,rgba(255,255,255,0.18) 50%,transparent 70%);transform:translateX(-100%);transition:transform .55s ease;pointer-events:none;}
.cf-cta:hover::before{transform:translateX(100%);}
.cf-user-avatar{transition:box-shadow .15s ease;}
.cf-user-avatar:hover{box-shadow:0 0 0 2px ${S.accentDim};}
.cf-chat-scroller{scroll-behavior:smooth;}
.cf-stream-accent::after{content:"▌";display:inline-block;color:${S.accent};font-weight:700;animation:blink 0.9s step-end infinite;margin-left:1px;}
/* Polish round 4 -- modal/rail/input + grid stagger.
   NOTE: .cf-main{contain:layout style;} was the cause of the sidebar
   bug; intentionally excluded. Rail transitions only animate
   non-layout properties so width changes never race the first paint. */
.cf-modal-backdrop{animation:cfBackdropIn .2s ease-out;}
@keyframes cfBackdropIn{from{opacity:0}to{opacity:1}}
.cf-rail,.cf-side{transition:box-shadow .18s ease,border-color .18s ease;}
.cf-input-shell{transition:border-color .15s ease,box-shadow .15s ease;}
.cf-input-shell:focus-within{border-color:${S.accent}!important;box-shadow:0 0 0 3px ${S.accentDim};}
.cf-topbar{transition:box-shadow .18s ease;}
.cf-topbar[data-scrolled="true"]{box-shadow:0 4px 18px -8px rgba(0,0,0,0.2);}
.cf-btn-lift{transition:transform .14s cubic-bezier(0.16,1,0.3,1),box-shadow .14s ease;}
.cf-btn-lift:hover{transform:translateY(-1px);box-shadow:0 4px 10px rgba(0,0,0,0.10);}
.cf-btn-lift:active{transform:translateY(0);box-shadow:0 1px 2px rgba(0,0,0,0.10);}
.cf-grid-stagger > *{animation:cfGridIn .32s cubic-bezier(0.16,1,0.3,1) both;position:relative;overflow:hidden;}
.cf-grid-stagger > *:hover{border-color:${S.accent}!important;}
.cf-grid-stagger > *::after{content:"";position:absolute;inset:0;background:linear-gradient(120deg,transparent 30%,${S.accent}10 50%,transparent 70%);transform:translateX(-100%);transition:transform .55s ease;pointer-events:none;}
.cf-grid-stagger > *:hover::after{transform:translateX(100%);}
.cf-grid-stagger > *:nth-child(1){animation-delay:.02s}
.cf-grid-stagger > *:nth-child(2){animation-delay:.06s}
.cf-grid-stagger > *:nth-child(3){animation-delay:.10s}
.cf-grid-stagger > *:nth-child(4){animation-delay:.14s}
.cf-grid-stagger > *:nth-child(5){animation-delay:.18s}
.cf-grid-stagger > *:nth-child(6){animation-delay:.22s}
.cf-grid-stagger > *:nth-child(7){animation-delay:.26s}
.cf-grid-stagger > *:nth-child(8){animation-delay:.30s}
@keyframes cfGridIn{from{opacity:0;transform:translateY(8px) scale(0.98)}to{opacity:1;transform:translateY(0) scale(1)}}
.cf-hero-logo{transition:transform .25s cubic-bezier(0.16,1,0.3,1);}
.cf-hero-logo:hover{transform:scale(1.04);}
img{transition:opacity .25s ease;}
img[loading="lazy"]:not([src=""]){opacity:1;}
input[type="range"]{accent-color:${S.accent};}
input[type="checkbox"],input[type="radio"]{accent-color:${S.accent};cursor:pointer;}
/* Polish round 5 -- final batch (markdown / print / a11y / utilities). */
*{scrollbar-width:thin;scrollbar-color:${S.border2} transparent;}
kbd{display:inline-flex;align-items:center;padding:1px 6px;border-radius:5px;background:${S.surface2};border:1px solid ${S.border2};border-bottom-width:2px;font-family:'Outfit','Segoe UI',monospace;font-size:11px;color:${S.text2};font-weight:600;line-height:1.5;min-width:18px;justify-content:center;}
blockquote{margin:8px 0;padding:6px 14px;border-left:3px solid ${S.accent};background:${S.accent}08;color:${S.text2};border-radius:0 8px 8px 0;font-style:italic;}
table{border-collapse:collapse;width:100%;margin:10px 0;font-size:13px;}
table th,table td{padding:6px 10px;border:1px solid ${S.border};text-align:left;}
table th{background:${S.surface2};font-weight:700;color:${S.text};}
table tr:nth-child(even) td{background:${S.surface}66;}
hr{border:none;height:1px;background:linear-gradient(90deg,transparent,${S.border2},transparent);margin:14px 0;}
@media print{
  .cf-rail,.cf-side,.cf-topbar,.cf-input-shell,.cf-jump-pill,.cf-toast,[class*="cf-toast-"]{display:none!important;}
  .cf-main{flex:1 1 100%!important;width:100%!important;padding:0!important;}
  .cf-chat-scroller{overflow:visible!important;height:auto!important;}
  body,html{background:#fff!important;color:#000!important;}
  pre,code,blockquote,table,figure{page-break-inside:avoid;}
}
details{margin:8px 0;}
summary{cursor:pointer;padding:6px 10px;border-radius:8px;background:${S.surface2};border:1px solid ${S.border};color:${S.text2};font-weight:600;list-style:none;transition:background .12s ease;}
summary::-webkit-details-marker{display:none}
summary::before{content:"▶";display:inline-block;margin-right:6px;transition:transform .15s ease;color:${S.accent};font-size:9px;}
details[open] > summary::before{transform:rotate(90deg);}
summary:hover{background:${S.accentDim};}
[draggable="true"]{cursor:grab;}
[draggable="true"]:active{cursor:grabbing;}
button:not(:disabled){-webkit-tap-highlight-color:${S.accent}33;}
pre::selection,pre code::selection,.cf-code-block ::selection{background:${S.accent}55;color:${S.text};}
a{text-underline-offset:2px;}
html,body{transition:background-color .25s ease, color .25s ease;}
.cf-chat-scroller img{max-width:100%;height:auto;border-radius:8px;}
.cf-key{display:inline-flex;align-items:center;padding:1px 6px;border-radius:5px;background:${S.surface2};border:1px solid ${S.border2};border-bottom-width:2px;font-family:'Outfit','Segoe UI',monospace;font-size:11px;color:${S.text2};font-weight:600;line-height:1.5;min-width:18px;justify-content:center;}
.cf-cta:disabled,.cf-cta[aria-disabled="true"]{opacity:0.5;cursor:not-allowed;}
.cf-cta:disabled::before,.cf-cta[aria-disabled="true"]::before{animation:none!important;}
.cf-danger-btn,button[data-danger="true"]{box-shadow:inset 0 0 0 1px ${S.danger}55;}
.cf-danger-btn:hover,button[data-danger="true"]:hover{background:${S.danger}18!important;color:${S.danger}!important;}
.cf-badge-pulse{animation:cfPulseSlow 1.8s ease-in-out infinite;}
.cf-brand-logo{transition:transform .2s cubic-bezier(0.16,1,0.3,1);}
.cf-brand-logo:hover{transform:scale(1.08);}
.cf-wf-progress-bar{transition:width .25s cubic-bezier(0.16,1,0.3,1);}
.cf-wf-star{transition:transform .14s ease,color .14s ease;}
.cf-wf-star:hover{transform:scale(1.18) rotate(8deg);}
.cf-empty-icon-pop{animation:cfEmptyPop .55s cubic-bezier(0.16,1,0.3,1);}
@keyframes cfEmptyPop{0%{opacity:0;transform:scale(0.6) rotate(-8deg)}60%{transform:scale(1.1) rotate(4deg)}100%{opacity:1;transform:scale(1) rotate(0)}}
.sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;}
input,textarea{text-rendering:optimizeLegibility;}
input[type="number"],.cf-tabular{font-variant-numeric:tabular-nums;}
.cf-code-block pre,pre code{font-feature-settings:"liga","calt";font-variant-ligatures:contextual;}
svg{vertical-align:middle;}
.cf-icon{stroke:currentColor;fill:none;}
.cf-chev{transition:transform .14s ease;display:inline-block;}
[data-expanded="true"] > .cf-chev,[aria-expanded="true"] > .cf-chev{transform:rotate(90deg);}
/* Desktop sidebar guard -- defensive only. Forces the rail + chat
   side panels to flex-render on viewports >= 769px regardless of
   any later polish or override. */
@media (min-width: 769px){
  .cf-rail{display:flex!important;}
  .cf-side{display:flex!important;}
}
@media (max-width:768px){
  html,body,#root{height:100dvh!important;max-height:100dvh!important;width:100vw!important;max-width:100vw!important;overflow:hidden!important;}
  body{padding:0!important;}
  /* Mobile safety net for the visual-polish pass:
     - Reduce default page padding so content gets viewport width back.
     - Force per-page action header to wrap (was overflowing the
       'Workflows' / 'Agents' / 'Models' admin pages).
     - Constrain hard-coded long grid templateColumns to scroll
       horizontally instead of clipping. Apply per-cell so headers and
       rows scroll together.
     - Inputs and selects respect 16px minimum font-size to defeat
       iOS Safari's auto-zoom on focus.
     - Modal backdrops + slide-overs go edge-to-edge.
     - Multi-model chip row scrolls horizontally instead of wrapping
       into 5 lines on a phone. */
  .cf-page-card{padding:14px!important;}
  .cf-page-header{padding-right:0!important;}
  .cf-page-header h2{font-size:18px!important;}
  .cf-page-header + *{flex-wrap:wrap!important;}
  /* Topbar can be cramped -- shrink the model pill text a touch. */
  .cf-topbar-pill{max-width:100%;}
  /* Defeat iOS auto-zoom on focus. */
  input,textarea,select{font-size:max(16px,1em)!important;}
  /* Modal panels go nearly edge-to-edge so dialogs are readable. */
  .cf-modal-panel{max-width:96vw!important;width:96vw!important;}
  .cf-slide-in-right,.cf-slide-in-top{max-width:100vw!important;width:100vw!important;}
  /* Generic horizontal scroll for wide tables. */
  .cf-mobile-hscroll{overflow-x:auto!important;-webkit-overflow-scrolling:touch;}
  .cf-mobile-hscroll > *{min-width:max-content;}
  /* Round 7 -- 10 more mobile tweaks */
  /* 61. Bigger tap targets on chip-style controls. */
  button,a[role="button"],.cf-topbar-pill{min-height:36px;}
  /* 62. Make the chat input area more thumb-friendly. */
  .cf-input-shell{padding-bottom:env(safe-area-inset-bottom,0)!important;}
  /* 63. Modal panels respect safe area for notched devices. */
  .cf-modal-panel{padding-top:env(safe-area-inset-top,0);padding-bottom:env(safe-area-inset-bottom,0);}
  /* 64. Comparison grid: single column on phones AND tablets. */
  /* (already done at 768; tighten for hand-held layouts on portrait tablets too) */
  /* 65. Code blocks: smaller font + horizontal scroll preserved. */
  .cf-code-block pre{font-size:11px!important;line-height:1.5!important;}
  /* 66. Make the multi-model chip row scroll instead of wrap. */
  .cf-mobile-hscroll-chips{display:flex!important;overflow-x:auto!important;flex-wrap:nowrap!important;-webkit-overflow-scrolling:touch;scrollbar-width:none;}
  .cf-mobile-hscroll-chips::-webkit-scrollbar{display:none;}
  /* 67. Toasts go full-width edge-to-edge on phones. */
  .cf-toast{left:8px!important;right:8px!important;max-width:none!important;width:auto!important;}
  /* 68. Topbar pill avatars stay round on phones (auto-square stretching fix). */
  .cf-topbar-pill{flex-shrink:1;min-width:0;}
  /* 69. File rail in Terminal scrolls horizontally if narrowed. */
  .cf-rail,.cf-side{-webkit-overflow-scrolling:touch;}
  /* 70. Form select chevron retains alignment after font-size bump. */
  select{background-position:right 8px center!important;}
  /* ── Mobile responsive pass (foundation) ──────────────────────────────
     Page content uses inline grid templates + big padding that media
     queries can't normally override -- but a CSS !important rule beats a
     (non-!important) inline style, and React serializes gridTemplateColumns
     to grid-template-columns. These selectors collapse the fixed-column
     content grids to a single column and reclaim page width on phones,
     with no per-component edits. Scoped to .cf-main so the rail/topbar
     chrome is unaffected. */
  .cf-main [style*="grid-template-columns: 1fr 1fr"]{grid-template-columns:1fr!important;}
  .cf-main [style*="grid-template-columns: repeat("]{grid-template-columns:1fr!important;}
  .cf-main [style*="grid-template-columns: 2fr 1fr"]{grid-template-columns:1fr!important;}
  .cf-main [style*="grid-template-columns: 1fr 2fr"]{grid-template-columns:1fr!important;}
  .cf-main [style*="grid-template-columns: 1.3fr"]{grid-template-columns:1fr!important;}
  /* Reclaim horizontal space: the page-scroll roots use padding:28 (some
     24). Trimming whitespace gives content the viewport width back. */
  .cf-main [style*="padding: 28px"]{padding:14px!important;}
  .cf-main [style*="padding: 24px"]{padding:16px!important;}
  /* Belt-and-suspenders: nothing in the content column should force the
     page wider than the viewport. */
  .cf-main{overflow-x:hidden!important;}
  /* Opt-in: tag any grid with cf-grid-collapse to force single column on
     phones (used by follow-up table/layout fixes). */
  .cf-grid-collapse{grid-template-columns:1fr!important;}
  /* ── Mobile round 2 ───────────────────────────────────────────────────
     Audit log is a px-based wide table that overflows on phones. Stack
     each entry's fields into a single-column card (rows = .cf-audit-row)
     and hide the column header (the grid header has no row class, so
     :not(.cf-audit-row) targets it). */
  .cf-audit-row{grid-template-columns:1fr!important;gap:3px!important;}
  .cf-main [style*="grid-template-columns: 160px 180px 1fr"]:not(.cf-audit-row){display:none!important;}
  /* Chat: reclaim the message column's 32px side gutters on phones. */
  .cf-main [style*="padding: 0 32px"],
  .cf-main [style*="padding: 0px 32px"]{padding-left:14px!important;padding-right:14px!important;}
}
/* Round 7 (desktop-side tweaks) */
@media (min-width: 1280px){
  /* 71. Wider chat content cap on big monitors. */
  .cf-chat-scroller{max-width:1100px;margin:0 auto;}
}
@media (min-width: 1600px){
  /* 72. Even wider on ultra-wide. */
  .cf-chat-scroller{max-width:1280px;}
}
/* 73. High-density displays sharpen 1px borders. */
@media (-webkit-min-device-pixel-ratio: 2),(min-resolution: 192dpi){
  *{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;}
}
/* 74. Prefer-color-scheme dark gets a gentler outline. */
@media (prefers-color-scheme: dark){
  *:focus-visible{outline-width:1.5px!important;}
}
/* 75. Touch-pointer devices skip hover effects that don't translate. */
@media (hover: none){
  .cf-card-hover:hover{transform:none!important;}
  .cf-btn-lift:hover{transform:none!important;}
}
/* Round 8 -- a11y + form polish */
/* 76. Required-field indicator (red dot before label). */
label[data-required="true"]::after{content:"*";color:${S.danger};margin-left:4px;}
/* 77. Invalid input gets a red ring. */
input[aria-invalid="true"],textarea[aria-invalid="true"],select[aria-invalid="true"]{border-color:${S.danger}!important;box-shadow:0 0 0 3px ${S.danger}22!important;}
/* 78. Disabled inputs visually muted. */
input:disabled,textarea:disabled,select:disabled{opacity:0.55;cursor:not-allowed;}
/* 79. Placeholder color matches the muted text token. */
::placeholder{color:${S.text3};opacity:0.7;}
/* 80. High-contrast Windows mode keeps borders. */
@media (forced-colors: active){
  *{forced-color-adjust:auto;}
  button,input,textarea,select{border:1px solid CanvasText!important;}
  *:focus-visible{outline:2px solid Highlight!important;}
}
/* 81. Print: only the chat content, no chrome. (extending earlier rule) */
@media print{
  .cf-page-header{margin-bottom:18px;}
  pre,code{page-break-inside:avoid;}
  blockquote,table,figure{page-break-inside:avoid;}
}
/* 82. Screen-reader-only utility for visually-hidden labels. */
.sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;}
/* 83. Generic "saving..." dot animation hook. */
.cf-saving::after{content:"...";display:inline-block;width:14px;text-align:left;animation:cfSavingDots 1.2s steps(4) infinite;}
@keyframes cfSavingDots{0%{content:""}25%{content:"."}50%{content:".."}75%{content:"..."}100%{content:""}}
/* 84. Live region for toast/announce (visually hidden, present in DOM). */
.cf-live{position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;}
/* 85. Loading skeleton row reusable. */
.cf-skel{display:inline-block;height:1em;border-radius:4px;background:linear-gradient(90deg,${S.surface2} 0%,${S.border} 50%,${S.surface2} 100%);background-size:800px 100%;animation:cfShimmer 1.4s linear infinite;}
.cf-skel.lg{height:1.5em;border-radius:6px;}
.cf-skel.sm{height:0.75em;border-radius:3px;}
/* Round 9 -- 10 more polish items */
/* 86. .cf-chat-rail-panel slide-in animation. */
.cf-chat-rail-panel{animation:cfRailIn .25s cubic-bezier(0.16,1,0.3,1);}
@keyframes cfRailIn{from{opacity:0;transform:translateX(-6px)}to{opacity:1;transform:translateX(0)}}
/* 87. Refined modal-backdrop blur tier. */
.cf-modal-backdrop{backdrop-filter:blur(10px) saturate(120%)!important;}
/* 88. .cf-toast-action button gets accent text on hover. */
.cf-toast-action:hover{color:${S.accent}!important;}
/* 89. Active sidebar row gets a thicker accent indicator. */
.cf-sidebar-row[aria-current="true"],.cf-sidebar-row[data-active="true"]{box-shadow:inset 3px 0 0 ${S.accent};}
/* 90. Chevron/triangle icons gentle rotate on disclosure expand. */
[data-expanded="true"] > .cf-chev,[aria-expanded="true"] > .cf-chev{transform:rotate(90deg);}
.cf-chev{transition:transform .14s ease;display:inline-block;}
/* 91. Smooth in-app navigation: prevent layout shift during route change. */
/* Removed: .cf-main{contain:layout style;} -- it interacted badly with
   the flex sibling rail and could collapse it to zero width on some
   browsers. Kept the rule slot to preserve numbering. */
/* 92. Better text rendering on forms. */
input,textarea{text-rendering:optimizeLegibility;}
/* 93. Numeric inputs use tabular figures (line up nicely). */
input[type="number"],.cf-tabular{font-variant-numeric:tabular-nums;}
/* 94. Code blocks gentle ligatures when JetBrains Mono is loaded. */
.cf-code-block pre,pre code{font-feature-settings:"liga","calt";font-variant-ligatures:contextual;}
/* 95. SVG icons inherit currentColor consistently. */
svg{vertical-align:middle;}
.cf-icon{stroke:currentColor;fill:none;}
/* Round 10 -- final 10 items closing the overnight run */
/* 96. Buttons get subtle press feedback on every interactive surface. */
button:not(:disabled){-webkit-tap-highlight-color:${S.accent}33;}
/* 97. Selection on code blocks uses a softer accent. */
pre::selection,pre code::selection,.cf-code-block ::selection{background:${S.accent}55;color:${S.text};}
/* 98. Anchor underline offset (nicer link rendering). */
a{text-underline-offset:2px;}
/* 99. Buttons with .cf-icon-only get a circular hit area. */
button.cf-icon-only{border-radius:50%;width:36px;height:36px;display:inline-flex;align-items:center;justify-content:center;padding:0;}
/* 100. Smooth transitions on color-scheme flip (light <-> dark). */
html,body{transition:background-color .25s ease, color .25s ease;}
/* 101. Limit very large markdown images so they don't blow up chat. */
.cf-chat-scroller img{max-width:100%;height:auto;border-radius:8px;}
/* 102. Inline kbd-like chip via .cf-key. */
.cf-key{display:inline-flex;align-items:center;padding:1px 6px;border-radius:5px;background:${S.surface2};border:1px solid ${S.border2};border-bottom-width:2px;font-family:'Outfit','Segoe UI',monospace;font-size:11px;color:${S.text2};font-weight:600;line-height:1.5;min-width:18px;justify-content:center;}
/* 103. Disabled .cf-cta state: muted but readable. */
.cf-cta:disabled,.cf-cta[aria-disabled="true"]{opacity:0.5;cursor:not-allowed;}
.cf-cta:disabled::before,.cf-cta[aria-disabled="true"]::before{animation:none!important;}
/* 104. Add gentle outline on important danger buttons (prevents accidental clicks). */
.cf-danger-btn,button[data-danger="true"]{box-shadow:inset 0 0 0 1px ${S.danger}55;}
.cf-danger-btn:hover,button[data-danger="true"]:hover{background:${S.danger}18!important;color:${S.danger}!important;}
/* 105. Final touch: smoother provider chip wrap on mobile (no big jumps). */
.cf-provider-badge{transition:transform .12s ease,filter .12s ease,background-color .12s ease;}
/* Desktop sidebar guard -- defensive: the rail and chat-side panels
   must remain flex on viewports >= 769px regardless of how any future
   polish round may evolve. Wins over a stray !important display:none. */
@media (min-width: 769px){
  .cf-rail{display:flex!important;}
  .cf-side{display:flex!important;}
}
@media (max-width:768px){
  /* Hide desktop nav rail completely on mobile unless explicitly opened */
  .cf-rail{display:none!important;}
  .cf-rail[data-open="true"]{display:flex!important;position:fixed!important;top:0!important;left:0!important;height:100dvh!important;width:240px!important;min-width:240px!important;z-index:50!important;box-shadow:2px 0 32px rgba(0,0,0,0.5)!important;}
  .cf-rail-resizer{display:none!important;}
  /* Hide chat sidebar on mobile unless explicitly opened */
  .cf-side{display:none!important;}
  .cf-side[data-open="true"]{display:flex!important;position:fixed!important;top:0!important;left:0!important;height:100dvh!important;width:280px!important;min-width:280px!important;z-index:45!important;box-shadow:2px 0 32px rgba(0,0,0,0.5)!important;}
  /* Main content gets full width */
  .cf-main{flex:1!important;min-width:0!important;width:100vw!important;}
  /* Preview goes fullscreen on mobile */
  .cf-preview{position:fixed!important;inset:0!important;z-index:40!important;flex:1!important;min-width:0!important;max-width:none!important;border-left:none!important;}
  /* Backdrop — shown on mobile only when drawer is open */
  .cf-backdrop{display:block!important;position:fixed!important;inset:0!important;background:rgba(0,0,0,0.55)!important;z-index:44!important;backdrop-filter:blur(2px);}
  .cf-backdrop.nav{z-index:49!important;}
  /* Compact topbar elements */
  .cf-topbar{padding:0 8px!important;}
}`}</style>
      {/* Mobile backdrop for nav drawer */}
      {navOpen && (
        <div className="cf-backdrop nav" onClick={()=>setNavOpen(false)} style={{display:"none"}}/>
      )}
      <nav className="cf-rail" data-open={navOpen?"true":"false"} style={{
        width:railCollapsed?RAIL_COLLAPSED_WIDTH:railWidth,minWidth:railCollapsed?RAIL_COLLAPSED_WIDTH:railWidth,transition:railResizing?"none":"width 0.18s ease, min-width 0.18s ease",
        background:S.navBg,backdropFilter:"blur(20px)",borderRight:`1px solid ${S.border}`,
        display:"flex",flexDirection:"column",padding:"10px 0",gap:1,
        zIndex:20,overflowY:"auto",overflowX:"hidden",position:"relative"
      }}>
        {/* Nav logo + collapse toggle */}
        <div style={{padding:railCollapsed?"6px 10px 10px":"6px 14px 10px",display:"flex",alignItems:"center",gap:9,flexShrink:0,justifyContent:railCollapsed?"center":"flex-start"}}>
          <div style={{width:28,height:28,borderRadius:8,background:S.accentDim,border:`1px solid ${S.border3}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}><AppLogo size={18}/></div>
          {!railCollapsed && <div style={{flex:1,minWidth:0}}><div style={{fontSize:13,fontWeight:800,color:S.text,letterSpacing:"-0.02em"}}>{BN}</div><div style={{fontSize:9,color:S.accent,fontFamily:"monospace",letterSpacing:"0.08em"}}>PRIVATE AI</div></div>}
          {!railCollapsed && (
            <button onClick={()=>{setRailResizing(false);setRailCollapsed(true);}} title="Collapse sidebar" aria-label="Collapse sidebar"
              style={{width:22,height:22,borderRadius:6,background:"transparent",border:`1px solid ${S.border2}`,color:S.text3,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,padding:0}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
          )}
        </div>
        {/* Collapsed-mode expand button sits under the logo */}
        {railCollapsed && (
          <button onClick={()=>setRailCollapsed(false)} title="Expand sidebar" aria-label="Expand sidebar"
            style={{margin:"0 10px 6px",padding:"4px 0",borderRadius:6,background:"transparent",border:`1px solid ${S.border2}`,color:S.text3,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        )}
        <div style={{height:1,background:S.border,margin:"0 10px 6px"}}/>
        {renderRailDisclosure({
          id:"rail-chat",
          label:railChat.label,
          desc:railChat.desc,
          icon:railChat.icon,
          open:railChatOpen,
          onToggle:()=>{setSection("chat");setRailCollapsed(false);setRailChatOpen(p=>section==="chat"?!p:true);},
          active:section==="chat",
          childrenCount:visibleChatHistory.length
        })}
        {railChatOpen && renderChatRailPanel()}
        {railPrimary.map(item=>renderRailButton(item))}
        {renderRailDisclosure({
          id:"rail-more",
          label:"More",
          desc:"Projects, agents, workflows",
          icon:IC.tools,
          open:railMoreOpen,
          onToggle:()=>setRailMoreOpen(p=>!p),
          active:railMoreSections.includes(section),
          childrenCount:railMore.length
        })}
        {railMoreOpen && railMore.map(item=>renderRailButton(item,{nested:true}))}
        {renderRailButton(railSettings)}
        {railAdmin.length>0 && renderRailDisclosure({
          id:"rail-admin",
          label:"Admin settings",
          desc:"Providers, users, billing",
          icon:IC.lock,
          open:railAdminOpen,
          onToggle:()=>setRailAdminOpen(p=>!p),
          active:railAdminSections.includes(section),
          childrenCount:railAdmin.length,
          sensitive:true
        })}
        {railAdminOpen && railAdmin.map(item=>renderRailButton(item,{nested:true,adminGroup:true}))}
      </nav>
      {!railCollapsed && !isMobile && (
        <div
          className="cf-rail-resizer"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize navigation"
          title="Drag to resize navigation. Double-click to reset."
          data-active={railResizing?"true":"false"}
          onMouseDown={(e)=>{e.preventDefault();setRailResizing(true);}}
          onDoubleClick={()=>setRailWidth(RAIL_WIDTH_DEFAULT)}
          style={{
            position:"absolute",top:0,bottom:0,left:(railCollapsed?RAIL_COLLAPSED_WIDTH:railWidth)-4,width:8,height:"100dvh",
            cursor:"col-resize",zIndex:35,touchAction:"none",
            background:railResizing?S.accentDim:"transparent"
          }}
        />
      )}
      <main
        className="cf-main"
        style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minWidth:0,position:"relative"}}
        onDragEnter={e => {
          // Only react when actual files are being dragged in, not text /
          // links / DOM nodes. dataTransfer.types is a DOMStringList that
          // includes "Files" only on real file drags.
          if (section !== "chat") return;
          if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes("Files")) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragOver={e => {
          if (section !== "chat") return;
          if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes("Files")) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
        }}
        onDragLeave={e => {
          // dragleave fires for every child crossing — only clear when leaving
          // the main pane itself (relatedTarget outside our subtree).
          if (e.currentTarget.contains(e.relatedTarget)) return;
          setDragOver(false);
        }}
        onDrop={e => {
          if (section !== "chat") { setDragOver(false); return; }
          if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes("Files")) { setDragOver(false); return; }
          e.preventDefault();
          setDragOver(false);
          const files = Array.from(e.dataTransfer.files || []);
          if (files.length) intakeFiles(files);
        }}
      >
        {/* Slice 4: drag-and-drop overlay. Pointer-events: none lets every
            sub-element keep its own drag handlers; the parent main is what
            actually catches the drop. */}
        {dragOver && section === "chat" && (
          <div style={{position:"absolute",inset:0,zIndex:60,background:`${S.bg}cc`,backdropFilter:"blur(2px)",display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none"}}>
            <div style={{padding:"30px 40px",border:`2px dashed ${S.accent}`,borderRadius:18,background:S.accentDim,color:S.accent,fontWeight:600,fontSize:15,fontFamily:"inherit",textAlign:"center",boxShadow:"0 12px 36px rgba(0,0,0,0.18)"}}>
              <div style={{fontSize:24,marginBottom:6}}>⤓</div>
              Drop to attach
              <div style={{fontSize:11,fontWeight:400,opacity:0.75,marginTop:4}}>Images, PDFs, or text files</div>
            </div>
          </div>
        )}
        <div className="cf-topbar" style={{height:cfg.topbarSize==="large"?68:52,borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:isMobile?6:(cfg.topbarSize==="large"?12:8),padding:isMobile?"0 8px":(cfg.topbarSize==="large"?"0 20px":"0 16px"),background:S.topbarBg,backdropFilter:"blur(20px)",flexShrink:0}}>
          {isMobile && <button className="cf-topbar-pill cf-btn-lift" onClick={()=>setNavOpen(p=>!p)} style={{width:34,height:34,background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:8,color:S.text2,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{IC.menu}</button>}
          {!sidebar&&!isMobile&&<div style={{display:"flex",alignItems:"center",gap:8}}><div className="cf-brand-logo" style={{width:24,height:24,borderRadius:7,background:S.accentDim,border:`1px solid ${S.border3}`,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}><AppLogo size={16}/></div><span style={{fontSize:14,fontWeight:800,color:S.text}}>{BN}</span></div>}
	          {!isMobile && (() => {
	            const label = orgInfo ? (orgInfo.plan_details?.name || orgInfo.plan || "trial").toUpperCase() : null;
	            const seats = orgInfo?.plan_details?.seats;
	            const seatsUsed = orgInfo?.seatsUsed ?? 0;
	            const tokLimit = orgInfo?.plan_details?.monthlyTokens;
	            const tokUsed = orgInfo?.tokensUsedThisMonth || 0;
	            const tokPct = (tokLimit != null && tokLimit > 0) ? Math.min(100, Math.round((tokUsed / tokLimit) * 100)) : null;
	            const trialQuota = orgInfo?.trialUserQuota?.active ? orgInfo.trialUserQuota : null;
	            const quotaLimit = trialQuota ? trialQuota.limit : tokLimit;
	            const quotaUsed = trialQuota ? trialQuota.used : tokUsed;
	            const quotaPct = (quotaLimit != null && quotaLimit > 0) ? Math.min(100, Math.round((quotaUsed / quotaLimit) * 100)) : null;
	            const warn = quotaPct != null && quotaPct >= 85;
	            const effColor = warn ? S.danger : S.success;
	            const statusParts = ["ZERO RETENTION"];
	            if (label) statusParts.push(warn && quotaPct != null ? `${label}${trialQuota ? " TRIAL USER" : ""} ${quotaPct}%` : `${label}${trialQuota ? " TRIAL USER" : ""}`);
	            const tip = [
	              "Zero retention enabled",
	              orgInfo ? `Plan: ${label}` : null,
	              orgInfo ? (seats != null ? `Seats: ${seatsUsed} / ${seats}` : "Seats: unlimited") : null,
	              orgInfo && trialQuota ? `Trial user tokens: ${trialQuota.used.toLocaleString()} / ${trialQuota.limit.toLocaleString()} (${quotaPct}%)` : null,
	              orgInfo ? (tokLimit != null ? `Org tokens this month: ${tokUsed.toLocaleString()} / ${tokLimit.toLocaleString()} (${tokPct}%)` : `Org tokens this month: ${tokUsed.toLocaleString()}${trialQuota ? "" : " (unlimited)"}`) : null,
	              trialQuota?.resetsAt ? `Trial ends: ${new Date(trialQuota.resetsAt).toLocaleString()}` : null,
	              serverMode ? "Backend connection active" : null
	            ].filter(Boolean).join("\n");
            return <span title={tip} className={"cf-badge-hover cf-plan-chip" + (warn ? " cf-wf-card-pulse" : "")} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",borderRadius:20,background:S.inputBg,border:`1px solid ${S.border2}`,fontSize:9,fontFamily:"monospace",color:warn?effColor:S.text2,fontWeight:800,letterSpacing:0,whiteSpace:"nowrap",flexShrink:0}}><span style={{width:5,height:5,borderRadius:"50%",background:effColor,display:"inline-block",boxShadow:`0 0 0 3px ${effColor}16`}}/>{statusParts.join(" / ")}</span>;
          })()}
          {section==="chat"&&(<div ref={modelBtnRef} className="cf-topbar-pill" onClick={()=>setMdOpen(p=>!p)} style={{display:"flex",alignItems:"center",gap:cfg.topbarSize==="large"?10:7,background:S.inputBg,border:`1px solid ${S.border2}`,borderRadius:cfg.topbarSize==="large"?24:20,padding:cfg.topbarSize==="large"?"10px 18px 10px 14px":"5px 12px 5px 9px",cursor:"pointer",userSelect:"none",flexShrink:0,overflow:"hidden"}}>{(() => { const rk = realProviderKey(activeProv, activeModel); const rc = (OR_REAL_PROVIDER[String(activeModel||"").split("/")[0]] && OR_REAL_PROVIDER[String(activeModel||"").split("/")[0]].color) || PROVIDERS[rk]?.color || PROVIDERS[activeProv]?.color || S.accent; return <ProviderLogo providerKey={rk} color={rc} size={cfg.topbarSize==="large"?14:11} />; })()}{!isMobile && <span style={{fontSize:cfg.topbarSize==="large"?12:10,color:S.text3,fontFamily:"monospace",fontWeight:500}}>Model:</span>}<span style={{fontSize:cfg.topbarSize==="large"?16:12,fontWeight:700,fontFamily:"monospace",color:S.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:isMobile?90:"none"}}>{curMod?.name||activeModel}</span>{baaModels.includes(activeModel) && <span title="This model routes through Azure OpenAI under your BAA when compliance mode is on" style={{fontSize:9,padding:"1px 6px",borderRadius:4,background:S.success+"22",color:S.success,border:`1px solid ${S.success}40`,fontFamily:"monospace",fontWeight:700,flexShrink:0}}>🛡 BAA</span>}<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg></div>)}
          <div style={{marginLeft:"auto",display:"flex",gap:6,alignItems:"center"}}>
            <div className="cf-topbar-pill" onClick={()=>{ if (isAdmin) setSection("analytics"); else setSection("settings"); }} style={{display:"flex",alignItems:"center",gap:7,padding:isMobile?"4px":"4px 10px 4px 5px",borderRadius:isMobile?"50%":20,background:S.inputBg,border:`1px solid ${S.border2}`,cursor:"pointer"}}>
              <UserAvatar user={authUser||curUser} size={20} fontSize={9} />
              {!isMobile && <span style={{fontSize:10,fontFamily:"monospace",color:S.text2}}>{(authUser||curUser)?.name||"User"}</span>}
              {!isMobile && serverMode && authUser?.role && (() => {
                const isOwner = authUser.role === "superadmin";
                const isAdminRole = authUser.role === "admin";
                const clr = isOwner ? "#d4a017" : isAdminRole ? S.danger : S.accent;
                const label = isOwner ? "OWNER" : authUser.role;
                return <span style={{fontSize:8,padding:"1px 6px",borderRadius:10,fontFamily:"monospace",fontWeight:700,background:clr+"18",color:clr,border:`1px solid ${clr}30`,marginLeft:4,textTransform:"uppercase"}}>{label}</span>;
              })()}
            </div>
            {!isMobile && <div style={{position:"relative"}}>
              <button className="cf-topbar-pill" onClick={canInstall ? installApp : ()=>setInstallTip(p=>!p)}
                title="Install App"
                style={{height:32,padding:"0 12px",width:"auto",borderRadius:9,border:`1px solid ${S.accent}40`,background:S.accentDim,color:S.accent,fontSize:11,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6,fontFamily:"inherit",whiteSpace:"nowrap"}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                {!isMobile && "Install App"}
              </button>
              {installTip && !canInstall && (
                <div style={{position:"absolute",top:38,right:0,width:220,padding:"10px 12px",borderRadius:10,background:S.modalBg,border:`1px solid ${S.border2}`,boxShadow:"0 8px 32px rgba(0,0,0,0.2)",fontSize:11,color:S.text2,lineHeight:1.7,zIndex:999}}>
                  Click the <strong style={{color:S.accent}}>⊕ install</strong> icon in your browser's address bar and select <em>Install</em>.<br/>
                  <span style={{color:S.text3,fontSize:10}}>Or: Chrome menu → Save and share → Install ChatFort AI</span>
                </div>
              )}
            </div>}
            {serverMode && authUser && (
              <button className="cf-topbar-pill" onClick={doLogout} title="Sign out" aria-label="Sign out" data-tooltip="Sign out of this local session." style={{width:32,height:32,borderRadius:9,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text3,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              </button>
            )}
            <button className="cf-topbar-pill" onClick={()=>setTheme(t=>t==="dark"?"light":"dark")} title="Toggle theme" aria-label="Toggle theme" style={{width:32,height:32,borderRadius:9,background:S.inputBg,border:`1px solid ${S.border2}`,color:S.text2,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>{theme==="dark"?IC.sun:IC.moon}</button>
            {/* What's New — grouped with the other compact utility buttons. */}
            <button
              type="button"
              onClick={() => setWhatsNewOpen(true)}
              title="What's new"
              aria-label="What's new"
              className="cf-topbar-pill cf-btn-lift"
              style={{
                position:"relative", width:32, height:32, borderRadius:9,
                background:S.inputBg, border:`1px solid ${S.border2}`,
                color:S.text2, cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center",
                padding:0,
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 11l18-8v18l-18-8z" />
                <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
              </svg>
              {whatsNewUnread && (
                <span
                  aria-hidden="true"
                  className="cf-badge-pulse"
                  style={{
                    position:"absolute", top:6, right:6,
                    width:6, height:6, borderRadius:"50%",
                    background:S.danger || "#ef4444",
                    boxShadow:`0 0 0 2px ${S.inputBg}`,
                  }}
                />
              )}
            </button>
          </div>
        </div>
        {serverMode && authUser && authUser.emailVerified === false && (
          <div style={{padding:"8px 16px",background:`${S.warning||"#f59e0b"}15`,borderBottom:`1px solid ${S.warning||"#f59e0b"}40`,color:S.text,fontSize:12,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <span style={{fontSize:14}}>✉️</span>
            <span style={{flex:1,minWidth:200}}>Verify your email — we sent a link to <strong>{authUser.email}</strong>. Some features may be limited until you confirm.</span>
            <button onClick={async () => {
              try {
                const r = await fetch("/api/auth/resend-verification", { method:"POST", headers:{ "Authorization":`Bearer ${authToken}` }});
                const d = await r.json().catch(()=>({}));
                if (r.ok) showToast(d.alreadyVerified ? "Already verified" : "Verification email sent");
                else showToast(d.error || "Could not resend");
              } catch { showToast("Could not resend"); }
            }} style={{padding:"4px 12px",borderRadius:8,background:"none",border:`1px solid ${S.warning||"#f59e0b"}60`,color:S.warning||"#f59e0b",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Resend</button>
          </div>
        )}
        {section==="chat"&&(<div style={{flex:1,display:"flex",flexDirection:"row",overflow:"hidden"}}><div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minWidth:320}}><StableChatPage/>{(chatId&&(chats.find(c=>c.id===chatId)?.messages||[]).length>0)&&<div style={{flexShrink:0,padding:"12px 20px 16px",background:S.topbarBg,backdropFilter:"blur(20px)",borderTop:`1px solid ${S.border}`}}><div style={{maxWidth:760,margin:"0 auto",display:"flex",justifyContent:"center"}}>{InputBar({controlled:true})}</div></div>}</div>{previewContent&&<PreviewPanel/>}</div>)}
      {/* Agent library modal */}
      {libraryOpen && (() => {
        const installedNames = new Set(agents.map(a => a.name));
        const categories = {};
        for (const t of AGENT_LIBRARY) { (categories[t.cat] = categories[t.cat] || []).push(t); }
        const install = (tpl) => {
          const newAg = {
            id: "ag-" + gid(),
            name: tpl.name, emoji: tpl.emoji, color: tpl.color, desc: tpl.desc,
            sysprompt: tpl.sysprompt, temperature: tpl.temperature, starters: tpl.starters,
            model: "claude-sonnet-4-20250514", provider: "anthropic",
          };
          setAgents(p => [...p, newAg]);
          showToast(`${tpl.name} installed`);
        };
        return (
          <div onClick={()=>setLibraryOpen(false)} style={{position:"fixed",inset:0,zIndex:300,background:"rgba(0,0,0,0.6)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
            <div onClick={e=>e.stopPropagation()} style={{background:S.surface,border:`1px solid ${S.border2}`,borderRadius:18,width:"min(880px, 100%)",maxHeight:"85vh",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 80px rgba(0,0,0,0.4)"}}>
              <div style={{padding:"20px 24px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontSize:18,fontWeight:800,color:S.text,letterSpacing:"-0.02em"}}>📚 Agent Library</div>
                  <div style={{fontSize:12,color:S.text3,marginTop:4}}>Install pre-built agents for regulated industries. One click, tune later.</div>
                </div>
                <button onClick={()=>setLibraryOpen(false)} style={{background:"none",border:`1px solid ${S.border2}`,borderRadius:8,padding:"6px 10px",color:S.text2,cursor:"pointer",fontFamily:"inherit",fontSize:12}}>Close</button>
              </div>
              <div style={{flex:1,overflowY:"auto",padding:20}}>
                {Object.entries(categories).map(([cat, items]) => (
                  <div key={cat} style={{marginBottom:22}}>
                    <div style={{fontSize:10,fontWeight:700,color:S.text3,textTransform:"uppercase",fontFamily:"monospace",letterSpacing:"0.08em",marginBottom:10}}>{cat}</div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:10}}>
                      {items.map((t,i) => {
                        const already = installedNames.has(t.name);
                        return (
                          <div key={cat+i} style={{background:S.cardBg,border:`1px solid ${S.border2}`,borderRadius:12,padding:14,display:"flex",flexDirection:"column",gap:10}}>
                            <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                              <div style={{width:36,height:36,borderRadius:10,background:`${t.color}18`,border:`1px solid ${t.color}30`,fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{t.emoji}</div>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:13,fontWeight:700,color:S.text,marginBottom:2}}>{t.name}</div>
                                <div style={{fontSize:11,color:S.text2,lineHeight:1.45}}>{t.desc}</div>
                              </div>
                            </div>
                            <button disabled={already} onClick={()=>install(t)}
                              style={{marginTop:"auto",padding:"7px 12px",borderRadius:8,border:"none",background:already?S.border3:`linear-gradient(135deg,${t.color},${t.color}cc)`,color:"#fff",fontSize:11,fontWeight:700,cursor:already?"default":"pointer",fontFamily:"inherit",opacity:already?0.6:1}}>
                              {already ? "✓ Installed" : "Install"}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}
              {/* Voice Mode Overlay */}

      {voiceMode&&(
        <div style={voiceDocked?{position:"fixed",right:76,bottom:18,zIndex:200,display:"flex",flexDirection:"column",alignItems:"center",gap:8,background:"rgba(10,12,20,0.95)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:18,padding:"12px 14px",boxShadow:"0 14px 44px rgba(0,0,0,0.55)",width:176}:{position:"fixed",inset:0,zIndex:200,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(20px)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"safe center",gap:isMobile?16:24,overflowY:"auto",WebkitOverflowScrolling:"touch",boxSizing:"border-box",padding:"calc(env(safe-area-inset-top, 0px) + 16px) 16px calc(env(safe-area-inset-bottom, 0px) + 16px)"}}>
          {!voiceDocked && <div style={{fontSize:13,fontWeight:600,color:"rgba(255,255,255,0.5)",fontFamily:"monospace",letterSpacing:"0.08em",textTransform:"uppercase"}}>Talk to Me</div>}
          {/* Talk to Me character */}
          <style>{"@keyframes ttmTalk{0%,100%{transform:scaleY(0.3)}50%{transform:scaleY(1)}}@keyframes ttmBlink{0%,90%,100%{transform:scaleY(1)}95%{transform:scaleY(0.08)}}@keyframes ttmFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}"}</style>
          <div onClick={()=>{if(voiceSpeaking){interruptVoice();return;}if(voiceListening){stopListening();return;}if(recogRef.current){recogRef.current.stop();recogRef.current=null;}listenForVoice();}} style={{position:"relative",width:voiceDocked?86:(isMobile?132:180),height:voiceDocked?86:(isMobile?132:180),display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
            <div style={{position:"absolute",inset:0,borderRadius:"50%",border:"2px solid "+(voiceListening?"#5ba3c9":voiceSpeaking?"#a78bfa":voiceThinking?"#f59e0b":"rgba(255,255,255,0.10)"),animation:(voiceListening||voiceSpeaking||voiceThinking)?"voicePulse 1.6s ease-in-out infinite":"none"}}/>
            <div style={{position:"absolute",inset:16,borderRadius:"50%",border:"2px solid "+(voiceListening?"#5ba3c9":voiceSpeaking?"#a78bfa":voiceThinking?"#f59e0b":"rgba(255,255,255,0.07)"),animation:(voiceListening||voiceSpeaking||voiceThinking)?"voicePulse 1.6s ease-in-out 0.3s infinite":"none"}}/>
            <svg width={voiceDocked?60:(isMobile?88:116)} height={voiceDocked?60:(isMobile?88:116)} viewBox="0 0 120 120" style={{animation:"ttmFloat 4s ease-in-out infinite",filter:voiceSpeaking?"drop-shadow(0 0 26px rgba(167,139,250,0.55))":voiceListening?"drop-shadow(0 0 26px rgba(91,163,201,0.55))":"drop-shadow(0 0 14px rgba(255,255,255,0.12))",transition:"filter 0.3s"}}>
              <defs><linearGradient id="ttmFace" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#5ba3c9"/><stop offset="1" stopColor="#7c5cff"/></linearGradient></defs>
              <rect x="16" y="16" width="88" height="88" rx="28" fill="url(#ttmFace)"/>
              <g style={{transformOrigin:"60px 50px",animation:"ttmBlink 4.5s infinite"}}>
                <ellipse cx="45" cy="50" rx="7.5" ry={voiceListening?10:8} fill="#fff"/>
                <ellipse cx="75" cy="50" rx="7.5" ry={voiceListening?10:8} fill="#fff"/>
                <circle cx="45" cy={voiceListening?52:51} r="3.6" fill="#0f172a"/>
                <circle cx="75" cy={voiceListening?52:51} r="3.6" fill="#0f172a"/>
              </g>
              {voiceSpeaking
                ? <ellipse ref={mouthRef} cx="60" cy="80" rx="11" ry="3" fill="#0f172a"/>
                : <path d={voiceListening?"M46 78 q14 12 28 0":"M48 80 q12 7 24 0"} stroke="#0f172a" strokeWidth="4.5" fill="none" strokeLinecap="round"/>}
            </svg>
          </div>
          {/* Status */}
          <div style={{display:voiceDocked?"none":"block",fontSize:16,color:"rgba(255,255,255,0.85)",fontWeight:500,minHeight:28,textAlign:"center",maxWidth:400,padding:"0 24px"}}>
            {(busy||voiceThinking)?"Thinking…":voiceSpeaking?"Speaking… · tap to interrupt":voiceListening?"Listening… · tap the circle to send":voiceTranscript?`"${voiceTranscript}"`:"Tap “Speak again” or just talk"}
          </div>
          {/* Waveform bars when listening */}
          {voiceListening&&!voiceDocked&&<div style={{display:"flex",gap:4,alignItems:"center",height:32}}>{[0.4,0.7,1,0.8,0.5,0.9,0.6,1,0.7,0.4].map((h,i)=><div key={i} style={{width:3,borderRadius:2,background:"#5ba3c9",opacity:0.8,height:`${h*28}px`,animation:`voiceBar 0.6s ${i*0.06}s ease-in-out infinite alternate`}}/>)}</div>}
          {/* Controls */}
          {voiceDocked ? (
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <button onClick={()=>setVoiceDocked(false)} title="Back to full screen" style={{padding:"7px 13px",borderRadius:18,background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",color:"rgba(255,255,255,0.78)",fontSize:11,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>Expand</button>
            <button onClick={exitVoiceMode} style={{padding:"7px 13px",borderRadius:18,background:"rgba(220,38,38,0.15)",border:"1px solid rgba(220,38,38,0.3)",color:"#f87171",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Exit</button>
          </div>
          ) : (
          <div style={{display:"flex",gap:12,alignItems:"center",marginTop:8,flexWrap:"wrap",justifyContent:"center"}}>
            <button onClick={()=>{if(voiceSpeaking){interruptVoice();return;}if(voiceListening){stopListening();return;}if(recogRef.current){recogRef.current.stop();recogRef.current=null;}listenForVoice();}} disabled={busy&&!voiceSpeaking&&!voiceListening} style={{padding:"10px 20px",borderRadius:24,background:voiceListening?"rgba(91,163,201,0.22)":"rgba(255,255,255,0.08)",border:"1px solid "+(voiceListening?"rgba(91,163,201,0.5)":"rgba(255,255,255,0.15)"),color:voiceListening?"#bfe0f0":"rgba(255,255,255,0.7)",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>{voiceSpeaking?"Interrupt":voiceListening?"Done":"Speak again"}</button>
            <button onClick={turnIntoAction} style={{padding:"10px 22px",borderRadius:24,background:"linear-gradient(135deg,#5ba3c9,#7c5cff)",border:"none",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{"\u2726"} Turn into action</button>
            <button onClick={()=>{setSection("chat");setVoiceDocked(true);}} title="Dock the avatar and watch it work" style={{padding:"10px 18px",borderRadius:24,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.15)",color:"rgba(255,255,255,0.82)",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Show me what you're doing</button>
            <button onClick={exitVoiceMode} style={{padding:"10px 20px",borderRadius:24,background:"rgba(220,38,38,0.15)",border:"1px solid rgba(220,38,38,0.3)",color:"#f87171",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Exit</button>
          </div>
          )}
          {!voiceDocked && <div style={{fontSize:10,color:"rgba(255,255,255,0.25)",fontFamily:"monospace",marginTop:4}}>Natural voice · just speak and it talks back</div>}
        </div>
      )}
        {section==="agents"&&<AgentsPage/>}
        {section==="tools"&&<ToolsPage/>}
        {section==="integrations"&&<IntegrationsPage/>}
        {section==="mcp"&&<MCPPage mcpServers={mcpServers} setMcpServers={setMcpServers} showToast={showToast}/>}
        {section==="cli"&&(secOk("terminal")?<TerminalPage showToast={showToast} setSection={setSection}/>:renderPlanLock("Code mode","business"))}
        {section==="workflows"&&(secOk("workflows")?<WorkflowsPage key={workflowsReloadKey} showToast={showToast}/>:renderPlanLock("Workflows","pro"))}
        {section==="settings"&&SettingsPage()}
        {section==="bots"&&isAdmin&&(secOk("bots")?<BotsPage/>:renderPlanLock("Bots","business"))}
        {section==="users"&&isAdmin&&<UserManagementPage users={users} setUsers={setUsers} invites={invites} setInvites={setInvites} ssoConfig={ssoConfig} setSsoConfig={setSsoConfig} adminOk={adminOk} setAdminOk={setAdminOk} adminPin={adminPin} showToast={showToast} gid={gid} rolePerms={rolePerms} setRolePerms={setRolePerms} allAgents={agents} allTools={tools} mcpServers={mcpServers} serverMode={serverMode} authToken={authToken} isSuperAdmin={isSuperAdmin}/>}
        {section==="models"&&isSuperAdmin&&<ModelsPage/>}
        {section==="knowledge"&&<KnowledgePage/>}
        {section==="guide"&&(
          <ErrorBoundary label="guide" compact>
            <Suspense fallback={
              <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:32,color:S.text3,fontSize:13,fontFamily:"var(--cf-brand-font, inherit)"}}>
                Loading Platform Guide...
              </div>
            }>
              <PlatformGuide onNavigate={setSection}/>
            </Suspense>
          </ErrorBoundary>
        )}
        {section==="analytics"&&isAdmin&&(secOk("analytics")?<AnalyticsPage/>:renderPlanLock("Analytics","business"))}
        {section==="audit"&&isAdmin&&<AuditPage/>}
        {section==="sso"&&isAdmin&&<SsoPage/>}
        {section==="billing"&&isAdmin&&<BillingPage/>}
        {section==="automations"&&(secOk("automations")?<AutomationsPage/>:renderPlanLock("Automations","pro"))}
        {section==="hermes"&&!secOk("hermes")&&renderPlanLock("Hermes","business")}
        {section==="hermes"&&secOk("hermes")&&(
          // perf/code-split: HermesPanel is lazy-loaded. The Suspense
          // fallback renders for the few hundred ms it takes to fetch +
          // parse the Hermes chunk on first navigation. Subsequent
          // navigations are instant (chunk cached by the SW + browser).
          // fix/pwa-error-boundary: per-route boundary so a crashing
          // lazy-loaded Skills/Audit/etc. only blanks the Hermes pane,
          // not the whole shell.
          <ErrorBoundary label="hermes" compact>
            <Suspense fallback={
              <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:32,color:S.text3,fontSize:13,fontFamily:"var(--cf-brand-font, inherit)"}}>
                Loading Hermes...
              </div>
            }>
              <HermesPanel showToast={showToast} isAdmin={isAdmin}/>
            </Suspense>
          </ErrorBoundary>
        )}
        {section==="cowork"&&!secOk("cowork")&&renderPlanLock("Hermes Co-work","pro")}
        {section==="cowork"&&secOk("cowork")&&(
          <ErrorBoundary label="cowork" compact>
            <Suspense fallback={
              <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:32,color:S.text3,fontSize:13,fontFamily:"var(--cf-brand-font, inherit)"}}>
                Loading Hermes Co-work...
              </div>
            }>
              <CoWorkWorkspace showToast={showToast} isAdmin={isAdmin} setSection={setSection}/>
            </Suspense>
          </ErrorBoundary>
        )}
        {section==="projects"&&(
          // Slice H34a: Projects (collaboration workspaces) -- lazy-loaded
          // so the chunk only ships when this section is opened.
          // fix/pwa-error-boundary: per-route boundary so a crashing
          // Projects chunk only blanks the Projects pane.
          <ErrorBoundary label="projects" compact>
            <Suspense fallback={
              <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:32,color:S.text3,fontSize:13,fontFamily:"var(--cf-brand-font, inherit)"}}>
                Loading Projects...
              </div>
            }>
              <ProjectsPanel
                key={projectsReloadKey}
                showToast={showToast}
                authUser={authUser}
                onHermesSurfaceContext={onProjectsHermesSurfaceContext}
              />
            </Suspense>
          </ErrorBoundary>
        )}
        {section==="marketplace"&&isAdmin&&<MarketplacePage/>}
        {section==="owner"&&isSuperAdmin&&<OwnerConsolePage/>}
      </main>
      <HermesResidentTray
        section={section}
        authUser={authUser}
        showToast={showToast}
        onOpenHermes={() => setSection("hermes")}
        onOpenCode={() => setSection("cli")}
        getSurfaceContext={getHermesSurfaceContext}
        onTalkToMe={startVoiceMode}
      />
      {chatFolderModal&&<ChatFolderModal/>}
      {agModal&&<AgModal/>}
      {/* Slice H34b: accept-invite overlay. Mounts on top of everything
          when ?accept_invite=<token> is in the URL -- whether the user
          is authed or not. The component clears the URL param itself
          on completion and calls onDone(projectId) to route into the
          newly-joined project. */}
      {inviteToken && (
        <AcceptInvitePage
          token={inviteToken}
          onDone={(projectId) => {
            setInviteToken(null);
            if (projectId) {
              try { window.location.hash = "#projects/" + encodeURIComponent(projectId); } catch {}
              setSection("projects");
            }
          }}
          onAuthChanged={(newToken) => {
            // signup-via-invite already wrote cf_auth_token; mirror it
            // into app state so the rest of the shell sees the new auth
            // without a page reload.
            setAuthToken(newToken);
          }}
        />
      )}
      {toast && (() => {
        // Slice 7-E: when toast is a {text, actionLabel, onAction} object,
        // render the action as a clickable inline button and flip the
        // toast container to receive pointer events. Plain-string toasts
        // keep the original pointerEvents:none so they don't block clicks
        // on whatever's underneath.
        const isAction = typeof toast === "object" && !!toast.onAction;
        const text = isAction ? toast.text : toast;
        // Polish: detect toast type from leading symbol/word so the
        // dot color matches. ✓ / ✗ / ⚠ / 🔒 / ❌ are all common; we
        // also fall back to keyword sniffing for 'fail' / 'error'.
        const _tt = (text || "").toString();
        const _tType = (_tt.startsWith("✗") || _tt.startsWith("❌") || /fail(ed)?$|error/i.test(_tt)) ? "error"
                    : (_tt.startsWith("⚠") || /warn/i.test(_tt)) ? "warn"
                    : (_tt.startsWith("✓") || _tt.startsWith("✅")) ? "success"
                    : "info";
        const _tDotColor = _tType === "error" ? S.danger
                       : _tType === "warn" ? (S.warning || "#f59e0b")
                       : _tType === "success" ? S.success
                       : S.accent;
        return (
          <div className="cf-toast" data-action={isAction ? "true" : "false"} key={typeof toast === "object" ? (toast.text || "") : toast} style={{position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",background:S.toastBg,backdropFilter:"blur(16px)",border:`1px solid ${S.border2}`,color:"#fff",padding:"10px 20px",borderRadius:24,fontSize:12,zIndex:999,pointerEvents:"auto",display:"flex",alignItems:"center",gap:10}}>
            <span className="cf-toast-dot" style={{width:6,height:6,borderRadius:"50%",background:_tDotColor,display:"inline-block"}}/>
            <span className="cf-toast-text">{text}</span>
            {isAction && (
              <button className="cf-toast-action" onClick={()=>{ try{ toast.onAction(); }catch{} setToast(""); }}
                style={{background:"none",border:"none",color:S.accent,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",padding:"0 2px"}}>
                {toast.actionLabel || "Open"} →
              </button>
            )}
            <button onClick={()=>setToast("")} className="cf-toast-dismiss" title="Dismiss (Esc)" aria-label="Dismiss" style={{background:"none",border:"none",color:"#fff",fontSize:14,cursor:"pointer",fontFamily:"inherit",padding:"0 2px",lineHeight:1}}>×</button>
          </div>
        );
      })()}
      {isOffline && (typeof navigator === "undefined" || navigator.onLine === false) && (
        <div role="status" style={{position:"fixed",top:10,left:"50%",transform:"translateX(-50%)",zIndex:700,display:"flex",alignItems:"center",gap:8,padding:"6px 14px",borderRadius:20,background:"#f59e0b",color:"#1e293b",fontSize:11,fontWeight:700,boxShadow:"0 6px 24px rgba(0,0,0,0.35)"}}>
          <span style={{width:7,height:7,borderRadius:"50%",background:"#1e293b"}}/>
          Offline — messages will send when you reconnect
        </div>
      )}
      {traceModalMsg && <TraceModal msg={traceModalMsg} onClose={() => setTraceModalMsg(null)} />}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} chats={chats} isAdmin={isAdmin}
        onNewChat={() => newChat()}
        onGo={(s) => setSection(s)}
        onPickChat={(id) => { setChatId(id); setSection("chat"); }}
        prompts={orgPrompts}
        onPickPrompt={(p) => {
          setSection("chat");
          setTimeout(() => {
            if (textareaRef.current) { textareaRef.current.value = p.text; textareaRef.current.focus(); }
            else setInput(p.text);
          }, 60);
        }} />
      <WhatsNewPanel open={whatsNewOpen} onClose={closeWhatsNew} theme={S} />
      <GlobalTooltip theme={S} />
      {multiPickerIdx!==null&&<>
        <div onClick={()=>setMultiPickerIdx(null)} style={{position:"fixed",inset:0,zIndex:499}}/>
        <MultiModelPicker
          idx={multiPickerIdx}
          multiModels={multiModels}
          setMultiModels={setMultiModels}
          setMultiPickerIdx={setMultiPickerIdx}
          integrations={integrations}
          chipRefs={multiChipRefs}
          modelOverrides={modelOverrides}
          orgAllowedModels={orgAllowedModels}
          liveModelCatalog={liveModelCatalog}
          orgCustomModels={orgCustomModels}
          authUser={authUser}
        />
      </>}
      {mdOpen&&<>
        <div onClick={()=>setMdOpen(false)} style={{position:"fixed",inset:0,zIndex:499,background:isMobile?"rgba(0,0,0,0.45)":"transparent"}}/>
        <div style={isMobile
          ? {position:"fixed",zIndex:500,top:"10dvh",left:"3vw",right:"3vw",bottom:"auto",background:S.modalBg,border:`1px solid ${S.border2}`,borderRadius:14,maxHeight:"80dvh",display:"flex",flexDirection:"column",boxShadow:"0 20px 60px rgba(0,0,0,0.45)",overflow:"hidden"}
          : {position:"fixed",zIndex:500,top:(()=>{const r=modelBtnRef.current?.getBoundingClientRect();return r?r.bottom+6:60})(),left:(()=>{const r=modelBtnRef.current?.getBoundingClientRect();return r?Math.max(8,Math.min(r.left,window.innerWidth-388)):100})(),background:S.modalBg,border:`1px solid ${S.border2}`,borderRadius:14,width:380,maxWidth:"94vw",maxHeight:"min(70vh, 560px)",display:"flex",flexDirection:"column",boxShadow:"0 20px 60px rgba(0,0,0,0.3)",overflow:"hidden"}
        }>
          {/* Search */}
          <div style={{padding:"10px 12px",borderBottom:`1px solid ${S.border}`,display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input value={mdQuery} onChange={e=>setMdQuery(e.target.value)} autoFocus placeholder="Search a model" style={{flex:1,background:"none",border:"none",outline:"none",color:S.text,fontSize:13,fontFamily:"inherit"}}/>
            {mdQuery && <button onClick={()=>setMdQuery("")} style={{background:"none",border:"none",color:S.text3,cursor:"pointer",fontSize:14,padding:0,lineHeight:1}}>×</button>}
          </div>
          {/* Flat scrollable model list */}
          <div style={{flex:1,overflowY:"auto",padding:"4px 0"}}>
            {(()=>{
              const showProvider = !!(authUser?.byok || isSuperAdmin || cfg.showProviderBadges);
              const liveOpenRouterModels = Array.isArray(liveModelCatalog?.models) ? liveModelCatalog.models : [];
              // Free/unlimited-tier models (server _modelTier): live-catalog rows
              // tagged gatingTier + the bundled-model tiers map. Used to badge
              // the picker so users see which models don't count toward the cap.
              const unlimitedIds = (() => {
                const s = new Set();
                for (const m of liveOpenRouterModels) { if (m?.gatingTier === "unlimited") s.add(m.id); }
                const _t = liveModelCatalog?.tiers || {};
                for (const _id of Object.keys(_t)) { if (_t[_id] === "unlimited") s.add(_id); }
                return s;
              })();
              const catalogHasZdrData = liveOpenRouterModels.some(m => m?.zdr);
              const shouldFilterByZdr = liveModelCatalog?.zdrEnforced !== false && catalogHasZdrData;
              const selectableOpenRouterModels = shouldFilterByZdr
                ? liveOpenRouterModels.filter(m => m?.zdr?.verified)
                : liveOpenRouterModels;
              const liveOpenRouterById = new Map(selectableOpenRouterModels.map(m => [m.id, m]));
              const liveOpenRouterAllowedIds = new Set(liveOpenRouterById.keys());
              const catalogIsAccessFiltered = liveOpenRouterModels.length > 0 && liveModelCatalog?.accessFiltered !== false;
              const flatPlatform = Object.entries(PROVIDERS).filter(([pid,p])=>p.builtin||!!integrations[pid]?.key).flatMap(([pid,p])=>{
                const orPinned = pid==="openrouter"&&integrations.openrouter?.pinned?.length ? integrations.openrouter.pinned.map(id=>{
                  const known = liveOpenRouterById.get(id) || p.models.find(mm=>mm.id===id);
                  return known
                    ? { ...known, isPinned: true }
                    : { id, name:id.split("/").pop(), desc:showProvider?id.split("/")[0]+" via OR":"", tier:"balanced", isPinned:true };
                }).filter(m => !catalogIsAccessFiltered || liveOpenRouterAllowedIds.has(m.id)) : [];
                const models = pid==="openrouter"
                  ? [...orPinned, ...selectableOpenRouterModels, ...p.models.filter(m => !catalogIsAccessFiltered || liveOpenRouterAllowedIds.has(m.id))].reduce((acc, m) => {
                      if (!m?.id || acc.some(x => x.id === m.id)) return acc;
                      acc.push(m.source === "openrouter" ? { ...m, isLiveCatalog: true } : m);
                      return acc;
                    }, [])
                  : p.models.filter(m => {
                      if (!catalogIsAccessFiltered || pid !== "anthropic" || integrations.anthropic?.key || !integrations.openrouter?.key) return true;
                      const routedId = OPENROUTER_FALLBACK_MODEL_MAP[m.id];
                      return routedId ? liveOpenRouterAllowedIds.has(routedId) : true;
                    });
                return models.map(m=>({...m,provider:pid,providerName:p.name,providerColor:p.color}));
              });
              // Append per-org custom models — routed through whichever provider
              // the superadmin specified. They bypass org.allowed_models (if the
              // admin added it, it's allowed by definition).
              const customRows = (orgCustomModels || []).map(m => {
                const pv = PROVIDERS[m.provider] || {};
                return { ...m, providerName: pv.name || m.provider, providerColor: pv.color || S.accent, isCustom: true };
              });
              const flat = [...flatPlatform, ...customRows].filter(m=>{
                if (modelOverrides[m.id]?.enabled===false) return false;
                // Org-level allowlist — applies to platform models only.
                // Custom models are always allowed (the superadmin explicitly added them).
                if (!m.isCustom && Array.isArray(orgAllowedModels) && orgAllowedModels.length > 0 && !orgAllowedModels.includes(m.id)) return false;
                if (authUser && !(authUser.role === "admin" || authUser.role === "superadmin") && Array.isArray(authUser.allowedModels) && authUser.allowedModels.length > 0 && !authUser.allowedModels.includes(m.id)) return false;
                if (mdQuery) { const q=mdQuery.toLowerCase(); const ov=modelOverrides[m.id]||{}; const hay=(m.providerName+" "+(ov.name||m.name)+" "+(ov.description||m.desc||"")+" "+m.id).toLowerCase(); if (!hay.includes(q)) return false; }
                return true;
              });
              if (flat.length===0) return <div style={{padding:"24px 16px",textAlign:"center",color:S.text3,fontSize:12}}>No models match</div>;
              // Group by provider. Pinned OR models collapse into a dedicated
              // "Pinned" group at the very top; custom-model rows get their own
              // "Custom" group at the bottom. Within each group, sort by tier
              // (frontier → balanced → fast). Provider order mirrors the order
              // keys appear in the PROVIDERS object — Anthropic first, since
              // that's where the DEFAULT_MODEL lives.
              const providerOrder = Object.keys(PROVIDERS);
              const groups = new Map();
              const getGroup = (key, label, color) => {
                if (!groups.has(key)) groups.set(key, { key, label, color, rows: [] });
                return groups.get(key);
              };
              // In the chat picker, OpenRouter is the delivery pipe rather
              // than the model family. Always re-bucket OR rows into their
              // real upstream (Anthropic / OpenAI / DeepSeek / ...), including
              // owner/admin users, so each provider group stays current.
              const hideOpenRouter = true;
              for (const m of flat) {
                if (m.isPinned) { getGroup("__pinned", "Pinned", S.accent).rows.push(m); continue; }
                if (m.isCustom) { getGroup("__custom", "Custom for your org", S.accent).rows.push(m); continue; }
                if (hideOpenRouter && m.provider === "openrouter") {
                  const prefix = String(m.id).split("/")[0];
                  const real = OR_REAL_PROVIDER[prefix];
                  if (real) { getGroup(real.key, real.label, real.color).rows.push(m); continue; }
                  // Unknown prefix — fold into a neutral bucket rather than
                  // exposing "OpenRouter" by name.
                  getGroup("__more", "Other models", S.accent).rows.push(m);
                  continue;
                }
                const p = PROVIDERS[m.provider];
                getGroup(m.provider, p?.name || m.providerName || m.provider, p?.color || m.providerColor || S.accent).rows.push(m);
              }
              // Sort within each group by tier.
              for (const g of groups.values()) {
                g.rows.sort((a, b) =>
                  (TIER_RANK[a.tier] ?? 3) - (TIER_RANK[b.tier] ?? 3)
                  || (b.created || 0) - (a.created || 0)
                  || String(a.name || a.id).localeCompare(String(b.name || b.id))
                );
              }
              // Order groups: pinned → known providers (direct + rebucketed)
              // → any leftover groups (unknown OR prefixes) → custom. The
              // rebucketed-provider order puts Anthropic/OpenAI first (our
              // default-model upstream and the most common paid tier), then
              // everyone else by popularity.
              const orderedGroups = [];
              if (groups.has("__pinned")) orderedGroups.push(groups.get("__pinned"));
              const knownOrder = [
                ...providerOrder,                                         // anthropic, openai, openrouter, hermes, groq, mistral
                "google","meta","deepseek","xai","qwen",                  // OR rebucket keys
              ];
              const seen = new Set();
              for (const k of knownOrder) {
                if (groups.has(k) && !seen.has(k)) { orderedGroups.push(groups.get(k)); seen.add(k); }
              }
              // Any remaining groups (e.g. __more for unknown OR prefixes).
              for (const [k, g] of groups) {
                if (!seen.has(k) && k !== "__pinned" && k !== "__custom") { orderedGroups.push(g); seen.add(k); }
              }
              if (groups.has("__custom")) orderedGroups.push(groups.get("__custom"));

              const renderRow = (m) => {
                const ov=modelOverrides[m.id]||{};
                const displayName=ov.name||m.name;
                const isSelected=(selModel?.model||curAg?.model)===m.id&&(selModel?.provider||curAg?.provider)===m.provider;
                const pinned=integrations.openrouter?.pinned?.includes(m.id);
                const liveMeta = m.provider === "openrouter" ? (liveOpenRouterById.get(m.id) || m) : m;
                const zdrProviders = Array.isArray(liveMeta?.zdr?.providers) ? liveMeta.zdr.providers : [];
                const isZdrVerified = !!liveMeta?.zdr?.verified;
                const descText = ov.description || m.desc || "";
                const zdrPrefix = isZdrVerified
                  ? `ZDR via ${zdrProviders.slice(0, 2).join(", ") || "verified endpoint"}`
                  : "";
                const rowDesc = [zdrPrefix, descText].filter(Boolean).join(" · ");
                return (
                  <div key={m.provider+":"+m.id} onClick={()=>{setSelModel({model:m.id,provider:m.provider});setMdOpen(false);setMdQuery("");}}
                    onMouseEnter={e=>{if(!isSelected)e.currentTarget.style.background=S.hoverBg;}}
                    onMouseLeave={e=>{if(!isSelected)e.currentTarget.style.background="none";}}
                    style={{padding:"10px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:10,background:isSelected?S.accentDim:"none",borderLeft:`3px solid ${isSelected?m.providerColor||S.accent:"transparent"}`,transition:"background 0.12s"}}>
                    {(() => { const rk = realProviderKey(m.provider, m.id); const rc = (m.provider === "openrouter" && OR_REAL_PROVIDER[String(m.id).split("/")[0]]) ? OR_REAL_PROVIDER[String(m.id).split("/")[0]].color : (m.providerColor || S.accent); return <ProviderLogo providerKey={rk} color={rc} size={14} />; })()}
                    <div style={{flex:1,minWidth:0,overflow:"hidden"}}>
                      <div style={{fontSize:13,color:S.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:6}}>
                        <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:600}}>{displayName}</span>
                        {m.id === DEFAULT_MODEL && m.provider === DEFAULT_PROVIDER && (
                          <span title="Default model for new users and agents" style={{fontSize:9,padding:"1px 6px",borderRadius:4,background:"#d4a01722",color:"#d4a017",border:`1px solid #d4a01740`,fontFamily:"monospace",fontWeight:700,flexShrink:0}}>DEFAULT</span>
                        )}
                        {baaModels.includes(m.id) && (
                          <span title="Routable through Azure OpenAI under BAA" style={{fontSize:9,padding:"1px 6px",borderRadius:4,background:S.success+"22",color:S.success,border:`1px solid ${S.success}40`,fontFamily:"monospace",fontWeight:700,flexShrink:0}}>🛡 BAA</span>
                        )}
                        {m.isLiveCatalog && (
                          <span title="Discovered from the live OpenRouter model catalog" style={{fontSize:9,padding:"1px 6px",borderRadius:4,background:S.accentDim,color:S.accent,border:`1px solid ${S.accent}40`,fontFamily:"monospace",fontWeight:700,flexShrink:0}}>LIVE</span>
                        )}
                        {unlimitedIds.has(m.id) && (
                          <span title="Unlimited base model — free to use without per-message limits on every paid plan, under fair use (~20M tokens/seat/mo). Doesn't count toward your premium-token allowance." style={{fontSize:9,padding:"1px 6px",borderRadius:4,background:S.success+"22",color:S.success,border:`1px solid ${S.success}40`,fontFamily:"monospace",fontWeight:700,flexShrink:0}}>∞ FREE</span>
                        )}
                        {isZdrVerified && (
                          <span title={`Zero retention endpoint verified by OpenRouter${zdrProviders.length ? ` via ${zdrProviders.join(", ")}` : ""}`} style={{fontSize:9,padding:"1px 6px",borderRadius:4,background:S.success+"22",color:S.success,border:`1px solid ${S.success}40`,fontFamily:"monospace",fontWeight:700,flexShrink:0}}>ZDR</span>
                        )}
                      </div>
                      {rowDesc&&<div style={{fontSize:10,color:S.text3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginTop:2}}>{rowDesc}</div>}
                    </div>
                    {m.provider==="openrouter" && (
                      <button onClick={e=>{
                        e.stopPropagation();
                        const cur=integrations.openrouter?.pinned||[];
                        const next=cur.includes(m.id)?cur.filter(x=>x!==m.id):[...cur,m.id];
                        setIntegrations(p=>({...p,openrouter:{...(p.openrouter||{}),pinned:next}}));
                      }} title={pinned?"Unpin":"Pin to top"}
                      style={{width:24,height:24,padding:0,border:`1px solid ${pinned?S.accent:S.border2}`,borderRadius:6,background:pinned?S.accentDim:"transparent",color:pinned?S.accent:S.text3,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700,lineHeight:1,flexShrink:0,fontFamily:"inherit"}}>
                        {pinned?"−":"+"}
                      </button>
                    )}
                    {isSelected && <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={S.accent} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
                  </div>
                );
              };
              return orderedGroups.map((g, gi) => (
                <div key={g.key}>
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:gi===0?"8px 14px 4px":"14px 14px 4px",fontSize:10,fontWeight:700,color:S.text3,fontFamily:"monospace",textTransform:"uppercase",letterSpacing:"0.08em"}}>
                    <ProviderLogo providerKey={g.key} color={g.color} size={12} />
                    <span>{g.label}</span>
                    <span style={{marginLeft:"auto",color:S.text3,fontWeight:500,fontSize:9}}>{g.rows.length}</span>
                  </div>
                  {g.rows.map(renderRow)}
                </div>
              ));
            })()}
          </div>
          {(liveModelCatalog?.count > 0 || liveModelCatalogError) && (
            <div style={{padding:"7px 14px",borderTop:`1px solid ${S.border}`,background:S.surface2||S.inputBg,color:liveModelCatalogError?S.text3:S.text2,fontSize:10,fontFamily:"monospace",display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
              <span style={{width:6,height:6,borderRadius:"50%",background:liveModelCatalogError?S.text3:S.success,display:"inline-block"}}/>
              {liveModelCatalog?.count > 0
                ? (() => {
                    const models = Array.isArray(liveModelCatalog.models) ? liveModelCatalog.models : [];
                    const zdrCount = models.filter(m => m?.zdr?.verified).length;
                    const prefix = liveModelCatalog.zdrEnforced !== false && zdrCount
                      ? `${zdrCount} ZDR-verified / ${liveModelCatalog.count} key-allowed`
                      : `${liveModelCatalog.count} key-allowed`;
                    return `${prefix} OpenRouter models · ${liveModelCatalog.cache === "hit" ? "cached" : liveModelCatalog.stale ? "stale cache" : "refreshed"}`;
                  })()
                : "Live model catalog unavailable"}
            </div>
          )}
          {/* Footer: set as default (only shown if user picked a non-default model) */}
          {selModel && curAg && (selModel.model !== curAg.model) && (
            <div style={{padding:"8px 14px",borderTop:`1px solid ${S.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",background:S.surface2||S.inputBg,flexShrink:0}}>
              <span style={{fontSize:11,color:S.text3}}>Current selection</span>
              <button onClick={()=>{
                setAgents(p=>p.map(a=>a.id===curAg.id?{...a,model:selModel.model,provider:selModel.provider}:a));
                showToast("Set as default");
              }} style={{padding:"4px 12px",borderRadius:7,background:S.accentDim,border:`1px solid ${S.accent}40`,color:S.accent,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                Set as default
              </button>
            </div>
          )}
          <button onClick={()=>{setSection("integrations");setMdOpen(false);}} style={{padding:"10px 16px",cursor:"pointer",border:"none",background:S.surface2||S.inputBg,borderTop:`1px solid ${S.border}`,width:"100%",textAlign:"left",color:S.accent,fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
            <span style={{fontSize:16,lineHeight:1}}>+</span> Add provider
          </button>
        </div>
      </>}
    </div>
  );
}

// ── Entry mount ──
// Previously the app was loaded via Babel-Standalone (fetch + regex-strip
// imports + Babel.transform + inject <script>). Under Vite this module IS
// the entry — it imports React/ReactDOM directly and mounts on load. The
// splash-hide animation used to fire from index.html's loadApp() callback;
// we replicate that here so the transition feels identical.
if (typeof document !== "undefined") {
  // (The previous cfThinkPulse keyframes injection was retired with the
  // imperative thinking dots — see ThinkingIndicator.jsx, which now owns
  // its own scoped CSS via a stable <style id>.)
  const rootEl = document.getElementById("root");
  if (rootEl) {
    // fix/pwa-error-boundary: top-level ErrorBoundary catches anything
    // that escapes the per-route boundaries (e.g. a render-time bug
    // in the top nav/sidebar). ChunkLoadBanner lives OUTSIDE the
    // boundary so that even if <App /> entirely fails to mount, the
    // user still gets a Reload button when the underlying cause is a
    // stale chunk reference from a prior deploy.
    createRoot(rootEl).render(
      <>
        <ChunkLoadBanner />
        <ErrorBoundary label="app-root">
          <App />
        </ErrorBoundary>
      </>
    );
    setTimeout(() => {
      const splash = document.getElementById("splash");
      if (splash) {
        splash.classList.add("hidden");
        setTimeout(() => splash.remove(), 600);
      }
    }, 400);
  }
}
